import { merge, cloneDeep, get, set, forEachRight } from 'lodash';
import axios from 'axios';
import { formatLocale } from 'd3-format';
import { select } from 'd3-selection';
import { timeFormatDefaultLocale } from 'd3-time-format';
import EventEmitter from 'events';
import { scalePow, scaleLinear } from 'd3-scale';
import { min, max } from 'd3-array';
import { zoomIdentity, zoom, zoomTransform } from 'd3-zoom';
import { StreamManager } from '@markit/stream-utils';

class Annotation {
	constructor(panel, params = {}) {
		this.core = panel.core;
		this.panel = panel;
		this.params = merge(this._getDefaultParams(), params);
	}
}

/* eslint-disable no-confusing-arrow */
/** Utility function to add pixel adjustment to canvas fillRect method */
const fillRect = (ctx, x, y, w, h) => {
	ctx.fillRect(
		Math.round(x),
		Math.round(y),
		Math.round(w) + 1,
		Math.round(h) + 1
	);
};

const roundRect = (ctx, x, y, w, h, radii) => {
	if (!ctx.roundRect) return fillRect(ctx, x, y, w, h); // fallback to fillRect if roundRect is not supported
	ctx.roundRect(
		Math.round(x),
		Math.round(y),
		Math.round(w) + 1,
		Math.round(h) + 1,
		radii
	);
};

/**
 * Method to adjust value for canvas subpixel offset
 * @param {number} val
 * @param {Boolean} roundUp - Whether or not to round the value up. True rounds up, false rounds down.
 * @return {number}
 */
const px = (val, roundUp = true) => roundUp ? Math.round(val) + 0.5 : Math.round(val) - 0.5;

/** Utility function to add pixel adjustment to canvas rect method for hollow candles */
function hollowRect(ctx, x, y, w, h) {
	const h2 = h < 0 ? Math.min(-1, h) : Math.max(1, h); // enforce 1px height for IE11 (NXC-766)
	ctx.rect(px(x), px(y), Math.round(w), h2);
}

/** Utility method to draw a canvas line with pixel offset applied */
const lineTo = (ctx, x, y) => ctx.lineTo(px(x), px(y));

/** Utility method to move the pointer with pixel offset applied */
const moveTo = (ctx, x, y) => ctx.moveTo(px(x), px(y));

/**
 * Canvas rectangle utility function
 * @param {CanvasRenderingContext2D} ctx
 * @param {int} x
 * @param {int} y
 * @param {int} w - rectangle width
 * @param {int} h - rectangle height
 */
const rect = (ctx, x, y, w, h) => ctx.rect(px(x), px(y), Math.round(w), Math.round(h));

/** Utility function to add pixel adjustment to canvas strokeRect method */
const strokeRect = (ctx, x, y, w, h) => ctx.strokeRect(px(x), px(y), Math.round(w), Math.round(h));

var subpixel = {
	fillRect,
	hollowRect,
	roundRect,
	lineTo,
	moveTo,
	px,
	rect,
	strokeRect
};

/** Line Annotation */
class LineAnnotation extends Annotation {
	_getDefaultParams() {
		return {
			start: { date: null, value: null, x: null, y: null },
			stop: { date: null, value: null, x: null, y: null },
			style: this.core.getStyle('annotation.line')
		};
	}

	render() {
		const ctx = this.panel.rootContext;
		const panel = this.panel;
		const params = this.params;
		const xScale = panel.xAxis.primaryScale;
		const yScale = panel.yAxis.primaryScale;
		const x0 = typeof params.start.x === 'number' ? params.start.x : xScale(this.core.closestExchangeIndex(new Date(params.start.date)));
		const x1 = typeof params.stop.x === 'number' ? params.stop.x : xScale(this.core.closestExchangeIndex(new Date(params.stop.date)));
		const y0 = typeof params.start.y === 'number' ? params.start.y : yScale(params.start.value);
		const y1 = typeof params.stop.y === 'number' ? params.stop.y : yScale(params.stop.value);
		ctx.beginPath();
		ctx.setLineDash(params.style.lineDash.split(/\s*,\s*/));
		ctx.lineWidth = params.style.width;
		ctx.strokeStyle = params.style.color;
		ctx.moveTo(subpixel.px(x0), subpixel.px(y0));
		ctx.lineTo(subpixel.px(x1), subpixel.px(y1));
		ctx.stroke();
		ctx.setLineDash([]);
		ctx.closePath();
	}
}

/** Rectangle Annotation */
class RectangleAnnotation extends Annotation {
	_getDefaultParams() {
		return {
			start: { date: null, value: null, x: null, y: null },
			stop: { date: null, value: null, x: null, y: null },
			style: this.core.getStyle('annotation.rectangle')
		};
	}

	render() {
		const ctx = this.panel.rootContext;
		const panel = this.panel;
		const params = this.params;
		const style = params.style;
		const xScale = panel.xAxis.primaryScale;
		const x0 = typeof params.start.x === 'number' ? params.start.x : xScale(this.core.closestExchangeIndex(new Date(params.start.date)));
		const x1 = typeof params.stop.x === 'number' ? params.stop.x : xScale(this.core.closestExchangeIndex(new Date(params.stop.date)));
		const y0 = params.start.y;
		const y1 = params.stop.y;
		ctx.setLineDash(style.lineDash.split(/\s*,\s*/));
		ctx.lineWidth = style.width;
		ctx.beginPath();

		// rectangle path
		subpixel.rect(ctx, x0, y0, x1 - x0, y1 - y0);

		// fill
		if (style.background.color) {
			ctx.fillStyle = style.background.color;
			ctx.fill();
		}

		// overall border
		if (style.color) {
			ctx.strokeStyle = style.color;
			ctx.stroke();
		}

		// individual border sides
		if (style.top.color) {
			this._renderSide(
				[x0, y0],
				[x1, y0],
				style.top.color
			);
		}

		if (style.right.color) {
			this._renderSide(
				[x1, y0],
				[x1, y1],
				style.right.color
			);
		}

		if (style.bottom.color) {
			this._renderSide(
				[x0, y1],
				[x1, y1],
				style.bottom.color
			);
		}

		if (style.left.color) {
			this._renderSide(
				[x0, y0],
				[x0, y1],
				style.left.color
			);
		}

		ctx.setLineDash([]);
	}

	_renderSide(start, stop, color) {
		const ctx = this.panel.rootContext;
		ctx.strokeStyle = color;
		ctx.beginPath();
		ctx.moveTo(subpixel.px(start[0]), subpixel.px(start[1]));
		ctx.lineTo(subpixel.px(stop[0]), subpixel.px(stop[1]));
		ctx.stroke();
		ctx.closePath();
	}
}

/** Text Annotation */
class TextAnnotation extends Annotation {
	_getDefaultParams() {
		return {
			date: null,
			value: null,
			text: 'Text',
			x: null,
			y: null,
			style: this.core.getStyle('annotation.text')
		};
	}

	render() {
		const { panel, params } = this;
		const ctx = panel.rootContext;
		const xScale = panel.xAxis.primaryScale;
		const yScale = panel.yAxis.primaryScale;
		let x0 = typeof params.x === 'number' ? params.x : xScale(this.core.closestExchangeIndex(new Date(params.date)));
		let y0 = typeof params.y === 'number' ? params.y : yScale(params.value);
		x0 += params.style.padding.left;
		x0 -= params.style.padding.right;
		y0 += params.style.padding.top;
		y0 -= params.style.padding.bottom;
		ctx.textAlign = params.style.textAlign;
		ctx.textBaseline = params.style.textBaseline;
		ctx.fillStyle = params.style.color;
		ctx.font = panel.getFontStyle(params.style.font.weight, params.style.font.size, params.style.font.family);
		panel.glowText(
			ctx,
			params.text,
			x0,
			y0,
			params.style.color,
			params.style.glow.color,
			params.style.glow.width
		);
	}
}

const drawLine = (chart, params = {}) => {
	const annotation = new LineAnnotation(
		chart.panels[0],
		params
	);
	annotation.render();
};

const drawRectangle = (chart, params = {}) => {
	const annotation = new RectangleAnnotation(
		chart.panels[0],
		params
	);
	annotation.render();
};

const drawText = (chart, params = {}) => {
	const annotation = new TextAnnotation(
		chart.panels[0],
		params
	);
	annotation.render();
};

var annotation = {
	drawLine,
	drawRectangle,
	drawText
};

/**
 * Enum for cursor type
 * @readonly
 * @enum {string}
 */
const types$1 = Object.freeze({
	DEFAULT: 'default',
	POINTER: 'pointer',
	NS_RESIZE: 'ns-resize',
	CROSSHAIR: 'cross',
	CLOSED_HAND: 'closed_hand'
});

/**
 * Enums associated with the cursor
 * @readonly
 * @enum {object}
 */
var cursor = {
	types: types$1
};

/**
 * Enum for event type
 * @readonly
 * @enum {string}
 */
const types = Object.freeze({
	BOUNDARY_STATUS: 'BOUNDARY_STATUS',
	CHART_NOT_AVAILABLE: 'CHART_NOT_AVAILABLE',
	CLICK_RIGHT: 'CLICK_RIGHT',
	CLICK_START: 'CLICK_START',
	CLICK: 'CLICK',
	COMPARISON_ADD: 'COMPARISON_ADD',
	CROSSHAIR_HIDE: 'CROSSHAIR_HIDE',
	CROSSHAIR_MOVE: 'CROSSHAIR_MOVE',
	CROSSHAIR_SHOW: 'CROSSHAIR_SHOW',
	CURSOR_CHANGE: 'CURSOR_CHANGE',
	DATA_DENSITY: 'DATA_DENSITY',
	DATA_ELEMENT_ERROR: 'DATA_ELEMENT_ERROR',
	DATA_FRAME_UPDATE: 'DATA_FRAME_UPDATE',
	DATA_LOAD_COMPLETE: 'DATA_LOAD_COMPLETE',
	DATA_LOAD_ERROR: 'DATA_LOAD_ERROR',
	DATA_LOAD_START: 'DATA_LOAD_START',
	DATA_LOAD_STOP: 'DATA_LOAD_STOP',
	DATA_POLL_ERROR: 'DATA_POLL_ERROR',
	DATA_PREPEND_ERROR: 'DATA_PREPEND_ERROR',
	DATA_PRESENT: 'DATA_PRESENT',
	DOUBLE_CLICK: 'DOUBLE_CLICK',
	EVENT_ADD: 'EVENT_ADD',
	EVENT_MOUSE_ENTER: 'EVENT_MOUSE_ENTER',
	EVENT_MOUSE_LEAVE: 'EVENT_MOUSE_LEAVE',
	EVENT_REMOVE: 'EVENT_REMOVE',
	EVENT_RENDER: 'EVENT_RENDER',
	EXPORT_CANVAS_END: 'EXPORT_CANVAS_END',
	EXPORT_CANVAS_START: 'EXPORT_CANVAS_START',
	FLAG_HIDE_CROSSHAIR: 'FLAG_HIDE_CROSSHAIR',
	FLAG_HIDE_HORIZ_ANNOTATION: 'FLAG_HIDE_HORIZ_ANNOTATION',
	FLAG_HIDE_INDICATOR: 'FLAG_HIDE_INDICATOR',
	FLAG_HIDE_LASTCLOSE: 'FLAG_HIDE_LASTCLOSE',
	FLAG_HIDE_XAXIS: 'FLAG_HIDE_XAXIS',
	FLAG_RENDER_CROSSHAIR: 'FLAG_RENDER_CROSSHAIR',
	FLAG_RENDER_HORIZ_ANNOTATION: 'FLAG_RENDER_HORIZ_ANNOTATION',
	FLAG_RENDER_INDICATOR: 'FLAG_RENDER_INDICATOR',
	FLAG_RENDER_LASTCLOSE: 'FLAG_RENDER_LASTCLOSE',
	FLAG_RENDER_XAXIS: 'FLAG_RENDER_XAXIS',
	FLAG_SHOW_CROSSHAIR: 'FLAG_SHOW_CROSSHAIR',
	FLAG_SHOW_HORIZ_ANNOTATION: 'FLAG_SHOW_HORIZ_ANNOTATION',
	FLAG_SHOW_INDICATOR: 'FLAG_SHOW_INDICATOR',
	FLAG_SHOW_LASTCLOSE: 'FLAG_SHOW_LASTCLOSE',
	FLAG_SHOW_XAXIS: 'FLAG_SHOW_XAXIS',
	IMAGE_SAVE_ERROR: 'IMAGE_SAVE_ERROR',
	IMAGE_SAVE_SUCCESS: 'IMAGE_SAVE_SUCCESS',
	INDICATOR_BESTWORST_UPDATE: 'INDICATOR_BESTWORST_UPDATE',
	INDICATOR_HIGHLOW_RENDER: 'INDICATOR_HIGHLOW_RENDER',
	INDICATOR_INPUT_UPDATE: 'INDICATOR_INPUT_UPDATE',
	INDICATOR_LOWER_ADD: 'INDICATOR_LOWER_ADD',
	INDICATOR_LOWER_REMOVE: 'INDICATOR_LOWER_REMOVE',
	INDICATOR_MOUSE_MOVE: 'INDICATOR_MOUSE_MOVE',
	INDICATOR_MOUSE_OUT: 'INDICATOR_MOUSE_OUT',
	INDICATOR_MOUSE_OVER: 'INDICATOR_MOUSE_OVER',
	INDICATOR_PARAM_UPDATE: 'INDICATOR_PARAM_UPDATE',
	INDICATOR_REGIONSERIES_MOUSE_OUT: 'INDICATOR_REGIONSERIES_MOUSE_OUT',
	INDICATOR_REGIONSERIES_MOUSE_OVER: 'INDICATOR_REGIONSERIES_MOUSE_OVER',
	INDICATOR_STYLE_UPDATE: 'INDICATOR_STYLE_UPDATE',
	INDICATOR_UPPER_ADD: 'INDICATOR_UPPER_ADD',
	INDICATOR_UPPER_REMOVE: 'INDICATOR_UPPER_REMOVE',
	KEY_DOWN: 'KEY_DOWN',
	KEY_UP: 'KEY_UP',
	LONG_DOUBLE_PRESS: 'LONG_DOUBLE_PRESS',
	LONG_PRESS: 'LONG_PRESS',
	MOUSE_DOWN: 'MOUSE_DOWN',
	MOUSE_ENTER: 'MOUSE_ENTER',
	MOUSE_LEAVE: 'MOUSE_LEAVE',
	MOUSE_MOVE: 'MOUSE_MOVE',
	MOUSE_UP: 'MOUSE_UP',
	PANEL_LEGEND_UPDATE: 'PANEL_LEGEND_UPDATE',
	PANEL_MOUSE_MOVE: 'PANEL_MOUSE_MOVE',
	PANEL_SCALE_CHANGE: 'PANEL_SCALE_CHANGE',
	PRICE_MARKER_TYPE_SET: 'PRICE_MARKER_TYPE_SET',
	RENDER: 'RENDER',
	RESIZE_HANDLE_HOVER: 'RESIZE_HANDLE_HOVER',
	RESIZE: 'RESIZE',
	SAVE_COMPLETE: 'SAVE_COMPLETE',
	SESSION_RENDER: 'SESSION_RENDER',
	SYMBOL_UPDATE: 'SYMBOL_UPDATE',
	TEMPLATE_APPLY: 'TEMPLATE_APPLY',
	TEMPLATE_LOAD_ERROR: 'TEMPLATE_LOAD_ERROR',
	TEMPLATE_LOAD: 'TEMPLATE_LOAD',
	TEMPLATE_SAVE_COMPLETE: 'TEMPLATE_SAVE_COMPLETE',
	TIME_SERIES_SET: 'TIME_SERIES_SET',
	TOOL_ADD: 'TOOL_ADD',
	TOOL_DOUBLE_CLICK: 'TOOL_DOUBLE_CLICK',
	TOOL_HANDLE_MOUSE_ENTER: 'TOOL_HANDLE_MOUSE_ENTER',
	TOOL_HANDLE_MOUSE_LEAVE: 'TOOL_HANDLE_MOUSE_LEAVE',
	TOOL_MODE_CHANGE: 'TOOL_MODE_CHANGE',
	TOOL_MOUSE_DOWN: 'TOOL_MOUSE_DOWN',
	TOOL_MOUSE_ENTER: 'TOOL_MOUSE_ENTER',
	TOOL_MOUSE_LEAVE: 'TOOL_MOUSE_LEAVE',
	TOOL_MOUSE_MOVE: 'TOOL_MOUSE_MOVE',
	TOOL_MOUSE_UP: 'TOOL_MOUSE_UP',
	TOOL_OHLCCROSSHAIR_RENDER: 'TOOL_OHLCCROSSHAIR_RENDER',
	TOOL_REMOVE: 'TOOL_REMOVE',
	TOOL_RENDER: 'TOOL_RENDER',
	TOOL_SELECT: 'TOOL_SELECT',
	TOUCH_END: 'TOUCH_END',
	TOUCH_MOVE: 'TOUCH_MOVE',
	TOUCH_START: 'TOUCH_START',
	WINDOW_RESIZE: 'WINDOW_RESIZE',
	ZOOM_END: 'ZOOM_END',
	ZOOM: 'ZOOM',
	ZOOM_START: 'ZOOM_START'
});

/**
 * Enums associated with events
 * @readonly
 * @enum {object}
 */
var event$1 = {
	types
};

/** Flags show up in a panel's yaxis gutter and are generated automatically (not saved) */
class Flag {
	/**
	 * @param {object} args Developer-defined custom args
	 * @property {object} style Flag style customization options
	 */
	constructor(args = {}) {
		/** @type {string} */
		this.id = args.id;
		/** @type {Panel} */
		this.panel = args.panel;
		/** @type {Modcharts} */
		this.core = args.panel.core;
		/** @type {Indicator} */
		this.indicator = args.indicator;
		/** @type {object} */
		this.el = {};
		/** @type {object} */
		this.style = this._getStyle(args);
		/** @type {string} */
		this.seriesId = args.seriesId;
	}

	/**
	 * Merge developer inputs with this default style
	 * @param {object} args Constructor args
	 * @returns {object} Default style for this type of flag
	 * @abstract
	 */
	_getStyle() {
		this.panel.core.warn("Required Flag method '_getStyle' not found");
	}

	/** Set the flag element's visibility to hidden */
	hide() {
		this.eventEmitter.emit(types[this.eventType.hide], { id: this.id });
		if (!this.isCustomRender) {
			Object.values(this.el).forEach(el => {
				el.style('visibility', 'hidden');
			});
		}
	}

	/** Show the flag element */
	show() {
		if (!this.panel.core.params.showFlags) return;
		if (this.id === 'crosshair.xAxis' && !this.panel.core.params.showXAxisFlag) return;
		this.eventEmitter.emit(types[this.eventType.show], { id: this.id });
		if (!this.isCustomRender) {
			Object.values(this.el).forEach(el => {
				el.style('visibility', null);
			});
		}
	}

	/**
	 * Abstract method to return label value. Must be overridden
	 * @abstract
	 * @private
	 */
	getValue() {
		return;
	}

	/** Remove the flag element from the DOM */
	remove() {
		if (this.el.flagBody) {
			const el = this.el.flagBody.node().parentNode;
			if (el) el.parentNode.removeChild(el);
		}
	}

	/**
	 * Create svg group ('<g>') for the flag and give it a classname
	 * @returns SVG flag group element
	 * @private
	 */
	getFlagGroupSVG() {
		const group = this.panel.rootOverlay.append('g');
		// add some classnames for potential developer customization (they are not needed by default)
		if (this.id) {
			group.node().classList.add(this.getClassName(`modcharts-flag-${this.id}`));
		}

		// Where appropriate, add a slightly more specific className.  One example of this is indicator flags, e.g. modcharts-flag-mae-maehigh / modcharts-flag-mae-maelow
		if (this.seriesId) {
			group.node().classList.add(this.getClassName(`modcharts-flag-${this.indicator.params.id}-${this.seriesId}`));
		}

		return group;
	}

	// convert id to a safe CSS className
	getClassName(id) {
		return id.toLowerCase().replace(/\./g, '-');
	}

	/**
	 * Create flag body inside flag svg group
	 * Group is passed in here because svg/d3 are a little awkward in creating elements with no parent
	 * @param {object} group A flag body SVG group - d3 selection
	 * @returns d3 svg polygon selection
	 * @private
	 */
	getFlagBody(group) {
		let body;
		if (this.style.shapeType === 'polygon') {
			body = group.append('polygon');
		} else if (this.style.shapeType === 'rectangle') {
			body = group.append('rect');
		} else {
			// Default to polygon if shapeType is not defined or is invalid
			body = group.append('polygon');
		}

		// background color
		body.style('fill', this.style.background.color);

		return body;
	}

	/**
	 * Create a label for the flag svg group.
	 * Group is passed in here because svg/d3 are a little awkward in creating
	 * elements with no parent
	 * @param {object} group A flag body SVG group - d3 selection
	 * @returns d3 svg text selection that will receive repeated updates to its
	 * value during crosshair updates
	 * @private
	 */
	getFlagLabel(group) {
		return group
			.append('text')
			.style('fill', this.style.color)
			.style('font-family', this.style.font.family)
			.style('font-size', `${this.style.font.size}px`)
			.attr('dy', '0.35em'); // dominant-baseline doesn't seem to work right in IE
	}

	/**
	 * Utility to get the appropriate padding value
	 * @param {string} side Which side's padding to get ('top', 'right', 'bottom', 'left')
	 * @returns {number} Calculated padding value
	 */
	_getPadding(side) {
		const { shapeType, polygon, rectangle, padding } = this.style;
		const yAxisSide = this.panel.core.params.yAxisSide;

		let shapePadding;

		if (shapeType === 'polygon') {
			shapePadding = polygon.yAxis[yAxisSide].label.padding[side];
		} else if (shapeType === 'rectangle') {
			shapePadding = rectangle.yAxis[yAxisSide].label.padding[side];
		}

		// Check if the padding value is valid (including 0), if not, use the default padding value
		return typeof shapePadding === 'number' ? shapePadding : padding;
	}

	/**
	 * Update flag body position, color
	 * @param {Panel} panel Panel to which this flag belongs
	 * @param {number} x New x-axis location for this flag
	 * @param {number} y New y-axis location for this flag
	 * @returns d3 svg polygon selection
	 */
	_updateFlagBody(panel, x, y) {
		if (!this.el.flagBody || window.isNaN(x) || window.isNaN(y)) return;

		const paddingTop = this._getPadding('top');
		const paddingBottom = this._getPadding('bottom');
		this._getPadding('left');
		this._getPadding('right');
		const fontHeight = this.panel.params.style.yAxis.font.size;

		if (this.style.shapeType === 'polygon') {
			let points;
			let x2;
			const pointer = this.style.polygon.pointer;
			const yFlagBodyTop = y - paddingTop - fontHeight / 2;
			const yFlagBodyBottom = y + paddingBottom + fontHeight / 2;
			const yPointerTop = typeof pointer.height === 'number' ? y - pointer.height / 2 : yFlagBodyTop;
			const yPointerBottom = typeof pointer.height === 'number' ? y + pointer.height / 2 : yFlagBodyBottom;

			if (this.panel.core.params.yAxisSide === 'left') {
				x2 = x + panel.size.padding.left; // right edge of xaxis
				points = [
					[x2, y], // tip of the pointer
					[x2 - pointer.width, yPointerTop], // top right of pointer
					[x2 - pointer.width, yFlagBodyTop], // top left of flag body
					[x, yFlagBodyTop], // top right
					[x, yFlagBodyBottom], // bottom right
					[x2 - pointer.width, yFlagBodyBottom], // bottom left of flag body
					[x2 - pointer.width, yPointerBottom] // bottom right of pointer
				];
			} else {
				x2 = x + panel.size.padding.right; // right edge of xaxis
				points = [
					[x, y], // tip of the pointer
					[x + pointer.width, yPointerTop], // top right of pointer
					[x + pointer.width, yFlagBodyTop], // top left of flag body
					[x2, yFlagBodyTop], // top right
					[x2, yFlagBodyBottom], // bottom right
					[x + pointer.width, yFlagBodyBottom], // bottom left of flag body
					[x + pointer.width, yPointerBottom] // bottom right of pointer
				];
			}

			// Join points into a string for the 'points' SVG attribute
			points = points.map(point => point.join(' ')).join(',');
			this.el.flagBody.attr('points', points);
		} else if (this.style.shapeType === 'rectangle') {
			const width = Math.abs(panel.size.padding.right - panel.size.padding.left);
			const height = paddingTop + paddingBottom + fontHeight;
			this.el.flagBody
				.attr('x', x)
				.attr('y', y - paddingTop - fontHeight / 2)
				.attr('width', width)
				.attr('height', height)
				.attr('rx', this.style.rectangle.radius.x) // Rounded corners
				.attr('ry', this.style.rectangle.radius.y); // Rounded corners
		}

		// Update background color
		if (this.el.flagBody.style('fill') !== this.style.background.color) {
			this.el.flagBody.style('fill', this.style.background.color);
		}
	}

	/**
	 * Update flag label position, value, color
	 * @param {Panel} panel The panel to which this flag belongs
	 * @param {string} val The new flag label value
	 * @param {number} x The label's new x-axis location
	 * @param {number} y The label's new y-axis location
	 * @returns d3 svg polygon selection
	 */
	_updateFlagLabel(panel, val, x, y) {
		if (!this.el.flagLabel || window.isNaN(x) || window.isNaN(y)) return;

		// Update label text and position
		const formatString = this._getFlagFormatString(val);
		const formatValue = panel.yAxis.getFormatValue(val, formatString);
		const paddingLeft = this._getPadding('left');
		const paddingRight = this._getPadding('right');
		const leftYAxis = panel.core.params.yAxisSide === 'left';

		if (this.style.shapeType === 'polygon') {
			const pointerWidth = this.style.polygon.pointer.width;
			x = leftYAxis ? panel.size.padding.left - pointerWidth - paddingRight : x + pointerWidth + paddingLeft;
		} else { // rectangle
			x = leftYAxis ? panel.size.padding.left - paddingRight : x + paddingLeft;
		}
		this.el.flagLabel.attr('x', x);
		this.el.flagLabel.attr('y', y);
		this.el.flagLabel.attr('text-anchor', leftYAxis ? 'end' : 'start');

		// Update label value
		this.el.flagLabel.text(formatValue);

		// Update label font size
		this.el.flagLabel.style('font-size', `${this.panel.params.style.yAxis.font.size}px`);

		// Update label color
		if (this.style.background.color === this.style.color) {
			const fallbackColor = '#000000';
			this.panel.core.warn(`Flag label color is same as flag body. Using fallback color of ${fallbackColor}.`);
			this.el.flagLabel.style('fill', fallbackColor);
		} else if (this.el.flagLabel.style('fill') !== this.style.color) {
			this.el.flagLabel.style('fill', this.style.color);
		}
	}

	/** Update flag body color if something has changed */
	_resetFlagBackground() {
		// Update background color - this may have changed if the parent indicator's styles were modified
		const origBgColor = this.style.background.color;

		// If indicator has multiple flags, use default style, else use the indicator color
		if (this.indicator.params.componentSeries) {
			// Recalculate style
			this.style = this._getStyle({
				id: this.id,
				indicator: this.indicator,
				panel: this.panel,
				seriesId: this.seriesId
			});
		} else {
			const indicatorStyle = this.indicator
				? this.indicator.params.style
				: {};

			if (indicatorStyle.color) this.style.background.color = indicatorStyle.color;
		}

		// Update flag body color to match indicator color
		if (origBgColor !== this.style.background.color) {
			this.el.flagBody.style('fill', this.style.background.color);
		}
	}

	/**
	 * Update flag html and render the changes
	 * @param {number} val New value for the flag label
	 * @return {object}
	 * @property {number} val Flag label value
	 * @property {number} x X-axis location for flag
	 * @property {number} y Y-axis location for flag
	 * @property {number} top Flag panel's top location
	 * @property {number} bottom Flag panel's bottom location
	 */
	render(val = this.getValue()) {
		const { panel } = this;

		if (typeof val === 'number') {
			const height = this.panel.params.style.yAxis.font.size + this._getPadding('top') + this._getPadding('bottom');
			const halfHeight = height / 2;
			let x;
			if (panel.core.params.yAxisSide === 'left') {
				x = 0;
			} else {
				x = panel.size.width + panel.size.padding.left;
			}
			const y = panel.yAxis.primaryScale(val);
			const panelTop = panel.size.top;
			const yEdgeTest = panelTop + y;
			const panelBottom = panelTop + panel.size.height;

			// Hide if near top/bottom edges of panel. yEdgeTest takes into account top margin, since y is relative to zero value of scale.
			if (yEdgeTest + halfHeight > panelBottom || yEdgeTest - halfHeight < panelTop) {
				return this.hide();
			}
			// default render
			if (!this.isCustomRender) {
				// Create body and label once
				if (!this.el.flagBody) {
					const group = this.getFlagGroupSVG();
					this.el.flagBody = this.getFlagBody(group);
					this.el.flagLabel = this.getFlagLabel(group);
				}
				// Check if color params have changed, if so update SVG components
				if (this.indicator) this._resetFlagBackground();

				this._updateFlagBody(panel, x, y);
				this._updateFlagLabel(panel, val, x, y);
			} else {
				// Custom flag render
				this.eventEmitter.emit(types[this.eventType.render], {
					val,
					x,
					y,
					panelTop,
					panelBottom,
					id: this.id,
					panel,
					indicator: this.indicator,
					style: this.style
				});
			}

			this.show();
			return { val, x, y, top: panelTop, bottom: panelBottom };
		}
		return {};
	}

	/**
	 * Utility method to calculate the width of a specific flag's label
	 * @param {object} options Additional options such as padding
	 * @returns {number} The total width of the flag's label including padding
	 */
	_calculateFlagLabelWidth(options = {}) {
		const paddingLeft = this._getPadding('left');
		const paddingRight = this._getPadding('right');
		const pointerWidth = this.style.shapeType === 'polygon' ? this.style.polygon.pointer.width : 0;

		// Use the previously set text, font family, size, and weight from the flag instance
		const textElement = this.el.flagLabel;

		if (!textElement) return 0;

		const previousText = textElement.text();
		const fontFamily = this.style.font.family;
		const fontSize = this.style.font.size;

		// Apply the text and styles to the element (if not already applied)
		textElement.text(previousText)
			.style('font-family', fontFamily)
			.style('font-size', fontSize); // no weight supported yet

		// Get the bounding box of just the text element itself
		const bbox = textElement.node().getBBox();

		// Calculate the total width including any padding and pointer width (if applicable)
		const totalWidth = bbox.width + paddingLeft + paddingRight + pointerWidth;
		return totalWidth;
	}

	/**
	 * Return id of the appropriate label bucket (e.g. 'small', 'default', 'large')
	 * @returns {string} label bucket id
	 * @private
	 */
	_getFlagFormatType(val) {
		const { panel } = this;
		const { format: formatType } = panel.params.style.yAxis.label.flag;
		if (val >= formatType.large.threshold) {
			return 'large';
		} else if (val < formatType.small.threshold) {
			return 'small';
		}
		return 'default';
	}

	/**
	 * Return an appropriate d3-format string based on an input number (e.g. '0,.2f')
	 * @returns {string}
	 */
	_getFlagFormatString(val) {
		const { panel } = this;
		let formatString;
		const formatType = this._getFlagFormatType(val); // small, default, large
		const yLabelFormat = panel.params.style.yAxis.label.flag.format[formatType];
		if (panel.params.yAxis.format === 'percent' || panel.isNormalized) {
			// percent-change formatting
			formatString = yLabelFormat.formatPercent;
		} else {
			// default formatting
			formatString = yLabelFormat.format;
		}
		return formatString;
	}

	/**
	 * @returns The appropriate event emitter types
	 * @type {object}
	 * @abstract
	 */
	get eventType() {
		throw new Error('Required Flag eventType getter not found');
	}

	/**
	 * @returns True if a custom render listener is enabled
	 * @type {boolean}
	 */
	get isCustomRender() {
		return this.eventEmitter.listeners(this.eventType.render).length > 0;
	}

	/**
	 * @returns Core event emitter
	 * @type {object}
	 */
	get eventEmitter() {
		return this.panel.core.eventEmitter;
	}
}

/** Crosshair flag */
class CrosshairFlagY extends Flag {
	_getStyle(args = {}) {
		const returnStyle = this.panel.core.getStyle('flag.crosshair.yAxis');
		const panelStyle = this.panel.params.style;

		// Use fallback values if necessary
		if (!returnStyle.font.family) returnStyle.font.family = panelStyle.yAxis.font.family;
		if (!returnStyle.font.size) returnStyle.font.size = panelStyle.yAxis.font.size;

		return {
			...returnStyle,
			...args.style
		};
	}

	/**
	 * @returns The appropriate event emitter types
	 * @type {object}
	 */
	get eventType() {
		return {
			render: 'FLAG_RENDER_CROSSHAIR',
			hide: 'FLAG_HIDE_CROSSHAIR',
			show: 'FLAG_SHOW_CROSSHAIR'
		};
	}
}

/** XAxis flag */
class CrosshairFlagX extends Flag {
	_getStyle(args = {}) {
		const returnStyle = this.panel.core.getStyle('flag.crosshair.xAxis');
		const panelStyle = this.panel.params.style;

		// Use fallback values if necessary
		if (!returnStyle.font.family) returnStyle.font.family = panelStyle.yAxis.font.family;
		if (!returnStyle.font.size) returnStyle.font.size = panelStyle.yAxis.font.size;

		return {
			...returnStyle,
			...args.style
		};
	}

	remove() {
		if (this.el.rootBody) this.el.rootBody.remove();
	}

	render(x0, valDate) {
		// Only render with proper date value
		if (!valDate) return;

		const { size: panelSize } = this.panel;

		const panelBottom =
			panelSize.top + // Panel top is already past the top margin, so don't include the top margin
			panelSize.padding.top +
			panelSize.height;


		if (!this.isCustomRender) {
			if (!this.el.rootBody) {
				this.el.rootBody = this.panel.core.rootModchart
					.append('div')
					.attr('class', 'modcharts-flag-xaxis');
			}

			this.el.rootBody
				.html(this.getLabel(valDate))
				.style('top', `${panelBottom}px`)
				.style('left', `${this.getLeft(x0)}px`)
				.style('background-color', this.style.background.color)
				.style('color', this.style.color)
				.style('font-family', this.style.font.family)
				.style('font-size', `${this.style.font.size}px`);

		} else {

			this.eventEmitter.emit(types[this.eventType.render], {
				date: valDate,
				dateLabel: this.getLabel(valDate),
				id: this.id,
				indicator: this.indicator,
				panel: this.panel,
				panelBottom,
				style: this.style,
				x: x0
			});
		}
	}

	getLeft(x0) {
		const width = this.el.rootBody.node().offsetWidth;
		let left = Math.max(x0 - (width / 2), 0);
		if (left + width > this.panel.size.width) {
			left = this.panel.size.width - width;
		}
		return left;
	}

	getLabel(valDate) {
		let label = this.formatDate(valDate);
		if (this.panel.core.state.isIntraday) {
			label = `${label} ${this.formatTime(valDate)}`;
		}
		return label;
	}

	formatDate(date) {
		return this.panel.core.timeFormat('%x')(date);
	}

	formatTime(date) {
		const core = this.panel.core;
		return core.timeFormat(core.locale.time)(date);
	}

	/**
	 * @returns The appropriate event emitter types
	 * @type {object}
	 */
	get eventType() {
		return {
			render: 'FLAG_RENDER_XAXIS',
			hide: 'FLAG_HIDE_XAXIS',
			show: 'FLAG_SHOW_XAXIS'
		};
	}
}

/* eslint-disable max-depth */

/**
 * Creates object with the OHLCV or custom data to be emitted for the current crosshair position
 * @param {Panel} activePanel - Panel that is active (mouse is currently hovering over)
 * @param {Date} date - Date for relevant data point, used to get appropriate chart data
 * @param {Number} newX - Chart data point X coordinate closest to mouse X coordinate
 * @param {Number} newY - Distance from top of active panel to Mouse Y coordinate
 * @param {Number} dataY - Vertical location (in pixels) of the current chart data point under the crosshair
 */
function buildCrosshairData(activePanel, date, newX, newY, dataY) {
	const { core } = activePanel;
	const dataOut = {
		date,
		coords: {
			x: newX,
			y: newY,
			dataY
		},
		activePanel
	};
	const dataOHLC = core.getDataByType(core.params.symbol, 'price');
	const dataVolume = core.getDataByType(core.params.symbol, 'volume');
	let dataIndex = core._getIndexByDate(core.dataPrimary, date);
	let dp;

	// OHLC data
	if (dataOHLC.length) {
		dp = dataOHLC[0][dataIndex];
		dataOut.open = dp.open;
		dataOut.high = dp.high;
		dataOut.low = dp.low;
		dataOut.close = dp.close;
	}

	// Add volume data
	if (dataVolume.length && dataVolume[0][dataIndex]) {
		dataOut.volume = dataVolume[0][dataIndex].volume;
	}

	// Get indicator data from all panels.  This is used when the simple data above isn't sufficient for more complex crosshair usage, like showing lower panel information or comparison data.
	dataOut.panels = [];
	core.panels.forEach(panel => {
		const panelData = {
			uid: panel.params.uid
		};
		const indicators = [];
		for (const ind of panel.indicators) {
			// Don't add info for noClip indicators
			if (ind.params.noClip) continue;

			// Get indicator data for this panel
			const { id, uid } = ind.params;
			const indParams = { id, uid };

			// Get standard data (OHLC for primary symbol)
			if (core.data[core.params.symbol]) {
				// Get standard indicator data for this panel
				const standardSymbol = ind.params.symbol || core.params.symbol;
				const symbolData = core.data[standardSymbol];

				// Does data exist for this indicator ID and UID?
				if (
					symbolData[id] &&
					symbolData[id][uid]
				) {
					dataIndex = ind.params.markerType === 'stepped' ?
						core._getPrevIndexByDate(symbolData[id][uid], date) :
						core._getIndexByDate(symbolData[id][uid], date);
					// Does data exist for this index?
					if (symbolData[id][uid][dataIndex]) {
						const data = symbolData[id][uid][dataIndex];
						indParams.symbol = standardSymbol;

						// Get x/y coords for this indicator
						const y = {};
						for (const key in data) {
							const value = data[key];

							// Skip date/text values
							if (
								!/^date/.test(key) &&
								!isNaN(Number(value))
							) {
								y[key] = Number(ind.yScale(value));
							}
						}

						const newPanelIndicator = {
							coords: {
								x: newX,
								y
							},
							data,
							indicator: ind,
							panel: ind.panel,
							params: indParams
						};

						// Convert normalized values to actuals (CH-4821)
						if (
							ind.panel.isNormalized &&
							core.state.normalizeValues?.indicators &&
							core.state.normalizeValues.indicators[indParams.uid]
						) {
							const normalizedValues = core.state.normalizeValues.indicators[indParams.uid];
							newPanelIndicator.data.actual = {
								close: core.getActualFromNormalized(data.close, normalizedValues.close),
								high: core.getActualFromNormalized(data.high, normalizedValues.high),
								low: core.getActualFromNormalized(data.low, normalizedValues.low),
								open: core.getActualFromNormalized(data.open, normalizedValues.open)
							};
						}

						indicators.push(newPanelIndicator);
					}
				}
			} else if (core.params.customData) { // Get custom data
				const { data: customData } = core;
				const { datasetId } = ind.params;

				if (customData[datasetId]) {
					// CH-2060 Can't go by primary index, have to use date since custom indices could differ
					const customIndex = core.crosshair._getParam('returnClosestCustomData') // This param may need to be placed in a more global location
						? core._getIndexByDate(customData[datasetId], date)
						: core._getIndexByDate(customData[datasetId], date, true);
					const y = {};
					let data = {};
					indParams.datasetId = datasetId;

					if (customIndex > -1) {
						// Convert data values to coordinate values
						data = { ...customData[datasetId][customIndex] };
						for (const key in data) {
							const value = data[key];

							// Skip date/text values
							if (
								!/^date/.test(key) &&
								!isNaN(Number(value))
							) {
								y[key] = Number(ind.yScale(value));
							}
						}
					} else {
						data = { date, value: null };
						y.value = null;
					}

					indicators.push({
						coords: {
							x: newX,
							y
						},
						data,
						indicator: ind,
						panel: ind.panel,
						params: indParams
					});
				}
			}
		}

		// Add indicator info to panel object
		panelData.indicators = [...indicators];

		dataOut.panels.push(panelData);
	});

	// currency
	if (core.state.currency) dataOut.currency = core.state.currency;

	// timezone label
	if (core.state.timezoneLabel) dataOut.timezoneLabel = core.state.timezoneLabel;

	// utc offset
	const { symbol } = core.params;
	if (symbol) {
		dataOut.utcOffset = core.xref[symbol] ? core.xref[symbol].utcOffset : null;
		dataOut.issueType = core.xref[symbol] ? core.xref[symbol].issueType : null;
	}

	return dataOut;
}

/**
 * Adds crosshair flags to given panel
 * @param {Panel} panel - Panel to add flags to
 */
function addCrosshairFlags(panel) {
	// Create the flag and circle on the upper panel only
	if (panel.isUpper) {
		// Create flag if it doesn't exist
		if (!panel.flags['crosshair.yAxis']) {
			panel.flags['crosshair.yAxis'] = new CrosshairFlagY({
				panel,
				id: 'crosshair.yAxis'
			});
		}
	}

	// Create the optional XAxis flag
	if (
		!panel.flags['crosshair.xAxis'] &&
		panel.xAxis.hasAxis() &&
		panel.core.params.showXAxisFlag
	) {
		panel.flags['crosshair.xAxis'] = new CrosshairFlagX({
			panel,
			id: 'crosshair.xAxis'
		});
	}
}

/**
 * Conceals crosshair flags on given panel
 * @param {Panel} panel - Panel on which to hide flags
 */
function concealCrosshairFlags(panel) {
	// Conceal yAxis flag (will only exist on first panel)
	if (panel.flags['crosshair.yAxis']) panel.flags['crosshair.yAxis'].hide();

	// Conceal xAxis flag
	if (panel.flags['crosshair.xAxis']) panel.flags['crosshair.xAxis'].hide();
}

/**
 * Removes crosshair flags from given panel
 * @param {Panel} panel - Panel to remove flags from
 */
function removeCrosshairFlags(panel) {
	['crosshair.xAxis', 'crosshair.yAxis'].forEach(id => {
		if (panel.flags[id]) {
			panel.flags[id].remove();
			delete panel.flags[id];
		}
	});
}

/**
 * Renders crosshair flags on given panel
 * @param {Panel} panel - Panel on which to render flags
 * @param {object} params - Function params
 * @param {Boolean} params.xAxis - Should the xAxis flag be rendered
 * @param {Boolean} params.xDate - The date to be shown on the xAxis flag
 * @param {Boolean} params.xValue - The coordinate where the flag should be shown
 * @param {Boolean} params.yAxis - Should the yAxis flag be rendered
 * @param {Boolean} params.yValue - The coordinate where the flag should be shown
 */
function renderCrosshairFlags(panel, { xAxis = false, xDate, xValue, yAxis = false, yValue }) {
	// Render xAxis flag
	if (xAxis && panel.flags['crosshair.xAxis']) {
		// Update flag & label
		panel.flags['crosshair.xAxis'].render(xValue, xDate);
		panel.flags['crosshair.xAxis'].hide();
	}

	// Render yAxis flag
	if (yAxis && panel.flags['crosshair.yAxis']) {
		// Update flag & label
		panel.flags['crosshair.yAxis'].render(panel.yAxis.primaryScale.invert(yValue));
		panel.flags['crosshair.yAxis'].hide();
	}
}

/**
 * Reveals crosshair flags on given panel
 * @param {Panel} panel - Panel on which to reveal flags
 */
function revealCrosshairFlags(panel) {
	// Show yAxis flag (will only exist on first panel)
	if (panel.flags['crosshair.yAxis']) panel.flags['crosshair.yAxis'].show();

	// Show xAxis flag
	if (panel.flags['crosshair.xAxis']) panel.flags['crosshair.xAxis'].show();
}

/** Class used to render/reveal/conceal crosshairs on panels */
class Crosshair {
	/**
	 * @param {Object} core - Modcharts chart object
	 * @param {Object} settings - Parameters to configure crosshair
	 */
	constructor(core, settings = {}) {
		/** @type {Object} */
		this.core = core;
		// Replace existing crosshair if necessary
		if (core.crosshair && core.crosshair.constructor.name === 'Crosshair') {
			// Destroy existing crosshair
			core.crosshair.destroy();
		}
		// Save crosshair to chart
		core.crosshair = this;

		/** @type {Boolean} */
		this.isNearEdge = false;
		/**
		 * @type {Object}
		 * @property {Boolean} params.enabled - Enable display of the crosshair
		 * @property {Boolean} params.flagEnabled - Enable display of the crosshair flags on the X and Y axes
		 * @property {Boolean} params.persist - Continue to display the crosshair after the mouse leaves the chart
		 * @property {Boolean} params.primaryOnly - Restrict display of the crosshair to the primary panel only
		 * @property {Boolean} params.returnClosestCustomData - Return data point closest to crosshair position or data point at exact crosshair position. If no data point is found, null will be returned for the data and y coordinate values.
		 * @property {Boolean} params.showCircle - Enable display of the crosshair circle on the chart data point under the mouse
		 * @property {Boolean} params.showFlagCrosshairDisabled - Display crosshair flag even if crosshair is disabled (still follows `flagEnabled` param)
		 * @property {Boolean} params.snapHorizontalToData - Position the horizontal crosshair line at the y position of the current data point (disabled is under mouse)
		 */
		this.params = { ...settings };

		this.listenerEvents = [
			types.CURSOR_CHANGE,
			types.DATA_LOAD_START,
			types.MOUSE_DOWN,
			types.MOUSE_LEAVE,
			types.MOUSE_MOVE,
			types.PANEL_MOUSE_MOVE,
			types.RESIZE,
			types.RESIZE_HANDLE_HOVER,
			types.TOOL_MOUSE_MOVE,
			types.ZOOM
		];

		this._eventHandlers = {
			[types.CURSOR_CHANGE]: ({ cursor }) => { this._enforceChartCursor(cursor); },
			[types.DATA_LOAD_START]: () => { this._concealCrosshairs(); },
			[types.MOUSE_DOWN]: () => { if (!this.core.state.isTouchDevice) this._concealCrosshairs(); },
			[types.MOUSE_LEAVE]: () => { if (!this._getParam('persist')) this._concealCrosshairs(); },
			[types.MOUSE_MOVE]: () => {
				if (core.state.zooming || core.dataXHR || core.status === 0) {
					this._concealCrosshairs();
				}
			},
			[types.PANEL_MOUSE_MOVE]: ({ mouse, activePanel }) => {
				if (this.core.state.updateCrosshairTimeout) {
					clearTimeout(this.core.state.updateCrosshairTimeout);
				}

				this.core.state.updateCrosshairTimeout = setTimeout(() => {
					this._updateCrosshair(mouse, activePanel);
					this._revealCrosshairs(activePanel);
				}, 0);

			},
			[types.RESIZE]: () => this._concealCrosshairs(),
			[types.RESIZE_HANDLE_HOVER]: () => this._concealCrosshairs(),
			[types.TOOL_MOUSE_MOVE]: () => this._concealCrosshairs(),
			[types.ZOOM]: () => this._concealCrosshairs()
		};

		// Register listeners
		this.listenerEvents.forEach(e => core.eventEmitter.on(e, this._eventHandlers[e]));
	}

	/**
	 * Returns the param value from the proper collection: the crosshair params if the param
	 * exists there, or the chart params.
	 * @param {string} name - The name of the param to retrieve
	 * @returns {*} The param value
	 */
	_getParam(name) {
		if (Object.prototype.hasOwnProperty.call(this.params, name)) return this.params[name];
		return this.core.params.crosshair[name];
	}

	/** Completely remove the crosshair from the chart */
	destroy() {
		const { core } = this;

		// Unregister listeners
		this.listenerEvents.forEach(e => core.eventEmitter.off(e, this._eventHandlers[e]));

		for (const panel of this.core.panels) {
			// Remove flags
			removeCrosshairFlags(panel);

			// Remove SVG elements
			['crosshairCircle', 'crosshairHoriz', 'crosshairVert'].forEach(id => {
				if (panel[id]) {
					panel[id].remove();
					delete panel[id];
				}
			});
		}

		// Kill reference from core to this crosshair
		core.crosshair = null;
	}

	/**
	 * Create crosshairs if they don't exist, update crosshair information and call render methods
	 * @param {Object} mouse - Mouse position information
	 * @param {Object} activePanel - Panel that is active (mouse is currently hovering over)
	 */
	_updateCrosshair(mouse, activePanel) {
		if (this.core.status === 0) return;
		if (!activePanel || !this.core.exchangeDates) return;

		// Update cursor when crosshair is enabled
		if (this._getParam('enabled')) {
			this.core.setCursor(types$1.CROSSHAIR);
		} else {
			this.core.setCursor(types$1.DEFAULT);
		}

		const [valX, valY] = mouse;
		const valIdx = this.core.panels[0].xAxis.primaryScale.invert(valX);
		const leftLimit = activePanel.size.padding.left;
		const rightLimit = activePanel.size.width + activePanel.size.padding.left;
		let indicator;
		let valDate;
		let valueY;
		let newX;
		let newY;
		let dataY;
		let dataIndex;
		const dataSource = this.core.params.customData ? this.core.dataPrimary : this.core.dataFrame;

		if (
			this.core.data &&
			this.core.state.toolmode === null &&
			dataSource
		) {
			dataIndex = this.core._getIndexByDomain(valIdx, dataSource, 'dateIndex');

			// Don't select index that is off of the chart
			const [domainLowerBound, domainUpperBound] = activePanel.xAxis.primaryScale.domain();
			const { dateIndex } = dataSource[dataIndex];
			if (dateIndex > domainUpperBound) dataIndex--;
			else if (dateIndex < domainLowerBound) dataIndex++;

			// fix errant errors while chart is changing state
			if (!dataSource[dataIndex]) return;

			newX = subpixel.px(
				Number(
					this.core.panels[0].xAxis.primaryScale(dataSource[dataIndex].dateIndex)
				)
			);

			if (isNaN(newX)) return;

			for (const panel of this.core.panels) {
				// Create the horizontal crosshair (this gets created on every panel)
				if (!panel.crosshairHoriz) {
					panel.crosshairHoriz = panel.rootOverlay
						.append('g')
						.attr('class', 'modcharts-crosshair-horiz')
						.append('line')
						.style('visibility', 'hidden');
				}

				// Create the vertical crosshair (this gets created on every panel)
				if (!panel.crosshairVert) {
					panel.crosshairVert = panel.rootOverlay
						.append('g')
						.attr('class', 'modcharts-crosshair-vert')
						.append('line')
						.style('visibility', 'hidden');
				}

				// Create the flag and circle on the upper panel only
				addCrosshairFlags(panel);

				// Create the circle on the upper panel only
				if (panel.isUpper && !panel.crosshairCircle) {
					panel.crosshairCircle = panel.rootOverlay
						.append('circle')
						.attr('class', 'modcharts-crosshair-circle')
						.attr('r', panel.params.style.crosshair.circleRadius)
						.style('visibility', 'hidden');
				}

				indicator = panel.indicators[0];

				// indicators can sometimes be removed before mousemove handler is complete
				if (!indicator) break;

				// vertical crosshair and data callback
				if (
					this.core.exchangeDates[Math.round(valIdx)] &&
					valX < rightLimit &&
					valX > leftLimit
				) {
					// Emit crosshair data for active panel
					if (panel === activePanel) {
						newY = subpixel.px(valY);
						valueY = this.core.params.customData
							? dataSource[dataIndex].value
							: dataSource[dataIndex].close;

						if (
							typeof valueY === 'number' &&
							this._getParam('enabled') &&
							this.core.data
						) {
							valDate = new Date(dataSource[dataIndex].date);
							dataY = Number(this.core.panels[0].yAxis.primaryScale(valueY));
							this._emitCrosshairData(activePanel, valDate, newX, newY, dataY);
						}
					}

					// Vertical crosshair is rendered on every panel
					this._renderCrosshairVertical(panel, newX, valDate);
				} else {
					// hide invalid crosshairs
					panel.crosshairVert.style('visibility', 'hidden');
				}
			}

			// Horizontal crosshair elements are only rendered on the active panel
			this._renderCrosshairHorizontal(activePanel, mouse, newX, newY, dataY);
		}
	}

	_emitCrosshairData(panel, valDate, newX, newY, dataY) {
		// Use setTimeout so as not to block when there are repeated calls to update the crosshair
		if (this.core.state.crosshairDataTimeout) {
			clearTimeout(this.core.state.crosshairDataTimeout);
		}
		this.core.state.crosshairDataTimeout = setTimeout(
			() => {
				this.core.eventEmitter.emit(
					types.CROSSHAIR_MOVE,
					buildCrosshairData(panel, valDate, newX, newY, dataY)
				);
			},
			0
		);
	}

	/**
	 * Render the vertical crosshair for a given panel
	 * @param {Object} panel - Panel on which to render crosshair
	 * @param {Number} xDate - Date for chart data point X coordinate closest to mouse X coordinate
	 * @param {Number} xValue - Chart data point X coordinate closest to mouse X coordinate
	 */
	_renderCrosshairVertical(panel, xValue, xDate) {
		// Render xAxis flag
		renderCrosshairFlags(panel, { xAxis: true, xDate, xValue });

		// primary panel crosshair only (NXC-657)
		if (!panel.isUpper && this._getParam('primaryOnly')) return;

		// Update crosshair vertical
		panel.crosshairVert
			.attr('x1', xValue)
			.attr('y1', panel.size.padding.top)
			.attr('x2', xValue)
			.attr('y2', panel.size.padding.top + panel.size.height)
			.style('visibility', 'hidden');

		// reflow
		try {
			panel.crosshairVert.node().getBBox();
		} catch (error) {
			this.warn(error.message);
		}
	}

	/**
	 * Render the horizontal crosshair for a given panel
	 * @param {Object} activePanel - Panel that is active (mouse is currently hovering over)
	 * @param {Number[]} mouse - Mouse position information [x, y]
	 * @param {Number} newX - Chart data point X coordinate closest to mouse X coordinate
	 * @param {Number} newY - Distance from top of active panel to Mouse Y coordinate
	 * @param {Number} dataY - Vertical location of the current chart data point under the crosshair
	 */
	_renderCrosshairHorizontal(
		activePanel,
		mouse,
		newX,
		newY,
		dataY
	) {
		if (isNaN(newY) || !activePanel) return;

		// Align upper panel horizontal crosshair to mouse Y (newY) or data Y (dataY) depending on preference
		if (
			this._getParam('snapHorizontalToData') &&
			activePanel.isUpper &&
			typeof dataY === 'number'
		) {
			newY = dataY;
		} else {
			// Adjust Y coord to be relative to active panel
			newY -= activePanel.size.top;
		}

		// hide flag/horizontal crosshair if the mouse nears vertical edge of the panel
		if (
			newY - 6 < activePanel.size.padding.top ||
			newY + 6 > activePanel.size.padding.top + activePanel.size.height
		) {
			this.isNearEdge = true;
		} else {
			this.isNearEdge = false;
		}

		// Update horizontal crosshair
		activePanel.crosshairHoriz
			.attr('x1', activePanel.size.padding.left)
			.attr('y1', newY)
			.attr('x2',	activePanel.size.padding.left + activePanel.size.width)
			.attr('y2', newY)
			.style('visibility', 'hidden');

		// Render yAxis flag
		renderCrosshairFlags(activePanel, { yAxis: true, yValue: newY });

		// Locate the crosshair circle on the chart
		if (
			activePanel.crosshairCircle &&
			!isNaN(dataY) &&
			typeof dataY === 'number' &&
			activePanel.isWithin(mouse)
		) {
			activePanel.crosshairCircle
				.attr('cx', newX)
				.attr('cy', dataY)
				.style('visibility', 'hidden');
		}

	}

	/** Conceal crosshairs on all panels. This is done using the 'visibility' style attribute. */
	_concealCrosshairs() {
		this.core.panels.forEach(panel => {
			// conceal horiz line
			if (panel.crosshairHoriz) {
				panel.crosshairHoriz.style('visibility', 'hidden');
			}

			// conceal vert line
			if (panel.crosshairVert) {
				panel.crosshairVert.style('visibility', 'hidden');
			}

			// Conceal crosshair flags
			concealCrosshairFlags(panel);

			// conceal circle (only will exist on first panel)
			if (panel.crosshairCircle) {
				panel.crosshairCircle.style('visibility', 'hidden');
			}
		});
		this.core.eventEmitter.emit(types.CROSSHAIR_HIDE);
	}

	/**
	 * Reveal vertical crosshairs on all panels; horizontal crosshair, flag, and circle on active panel
	 * @param {Object} activePanel - Panel that is active (mouse is currently hovering over)
	 */
	_revealCrosshairs(activePanel) {
		for (const panel of this.core.panels) {
			// Don't reveal crosshair if panel is lower and primary only option is true (NXC-657)
			if (!panel.isUpper &&	this._getParam('primaryOnly')) break;

			// Reveal vertical crosshair for panel
			if (
				this._getParam('enabled') &&
				panel.params.enableCrosshair &&
				panel.crosshairVert
			) {
				panel.crosshairVert.style('visibility', null);
			}

			// Reveal horizontal line, flag, circle for active panel only (flag, circle will appear on first panel only)
			if (panel === activePanel) {
				// Only show horizontal, flag if not near edge
				if (!this.isNearEdge) {
					if (
						this._getParam('enabled') &&
						panel.params.enableCrosshair &&
						panel.crosshairHoriz
					) {
						panel.crosshairHoriz.style('visibility', null);
					}

					if (
						(
							(this._getParam('enabled') && panel.params.enableCrosshair) ||
							// Check if flag should be shown even if crosshair is disabled
							this._getParam('showFlagCrosshairDisabled')
						) &&
						this._getParam('flagEnabled')
					) {
						revealCrosshairFlags(panel);
					}
				}

				if (
					this._getParam('enabled') &&
					panel.params.enableCrosshair &&
					this._getParam('showCircle') &&
					panel.crosshairCircle
				) {
					panel.crosshairCircle.style('visibility', null);
				}
			}
		}

		this.core.eventEmitter.emit(types.CROSSHAIR_SHOW);
	}

	/** Ensures chart does not use crosshair cursor when disabled */
	_enforceChartCursor(cursor) {
		if (cursor === types$1.CROSSHAIR && !this._getParam('enabled')) {
			this.core.setCursor(types$1.DEFAULT);
		}
	}

	/**
	 * Method used to change crosshair parameters
	 * @param {String} param - Name of parameter to change
	 * @param {Boolean} isEnabled - Turn parameter on/off
	 */
	setParam(param, isEnabled) {
		isEnabled = typeof isEnabled === 'boolean' ? isEnabled : true;

		// Save new value of param. Always save in chart params; only save in crosshair params if that param already exists.
		this.core.params.crosshair[param] = isEnabled;
		if (Object.prototype.hasOwnProperty.call(this.params, param)) {
			this.params[param] = isEnabled;
		}

		if (param === 'showCircle') {
			this.core.panels.forEach(panel => {
				if (panel.crosshairCircle) {
					let visibility = 'hidden';

					// Show circle if check passes
					if (this._getParam('showCircle')) visibility = null;

					panel.crosshairCircle.style('visibility', visibility);
				}
			});
		}
	}
}

/**
 * Utility function to deep clone an object using structuredClone if available,
 * otherwise falling back to cloneDeep for compatibility.
 * @param {Object} obj - The object to deep clone
 * @returns {Object} - The deep cloned object
 */
function deepClone(obj) {
	try {
		if (typeof structuredClone === 'function') {
			return structuredClone(obj);
		}
	} catch {
		return cloneDeep(obj);
	}
	return cloneDeep(obj);
}

// Object containing common style properties that are used in multiple style objects
const commonStyleProperties = {
	color: {
		accent: {
			primary: '#777',
			secondary: '#CCC'
		},
		band: {
			background: 'rgba(0, 0, 0, 0.05)',
			line: 'rgba(200, 200, 200, 0.5)'
		},
		primary: 'steelblue',
		negative: 'firebrick',
		neutral: '#999999',
		positive: 'forestgreen'
	},
	line: {
		width: 1.5
	},
	panel: {
		heightRatio: 0.25,
		yAxisPadding: {
			bottom: 0,
			top: 0
		}
	},
	font: {
		family: 'Arial, Helvetica, sans-serif'
	},
	sessionBreak: {
		color: null,
		lineDash: '2',
		width: null,
		border: {
			lineDash: null,
			color: null,
			width: null
		}
	}
};

// Holds user-defined overrides for common style properties located in `commonStyleProperties`
let commonStyleOverrides = {};

/**
* Updates the common style overrides.
* @param {Object} overrides - Object containing style property overrides.
*/
const setCommonStyles = overrides => {
	commonStyleOverrides = {}; // reset for jest testing
	merge(commonStyleOverrides, overrides);
};

/**
 * Retrieves the current value of a style property, considering any overrides.
 * This function looks up a property in the `commonStyleOverrides` object first.
 * If the property is not found in the overrides, it falls back to the default common styles.
 *
 * @param {string} path - The dot-separated path to the property in the commonStyleProperties style object.
 * @returns The value of the style property, either from the overrides or default.
 *
 * Example Usage:
 *   getCommonStyle('line.width') - Retrieves the line width.
 */
const getCommonStyle = path => {
	// First, check for an override
	const override = get(commonStyleOverrides, path);
	if (override !== undefined) {
		// Return a deep copy for objects to avoid mutations
		return typeof override === 'object' ? deepClone(override) : override;
	}

	// If no override, fall back to the default property
	const property = get(commonStyleProperties, path);
	// Return a deep copy for objects
	return typeof property === 'object' ? deepClone(property) : property;
};

function getAnnotationStyles() {
	// pull from commonStyles if wanted here
	return {
		line: {
			color: '#000',
			lineDash: '1,0',
			width: 1.5
		},
		rectangle: {
			background: { color: null },
			color: null,
			top: { color: null },
			right: { color: null },
			bottom: { color: null },
			left: { color: null },
			lineDash: '1,0',
			width: 1.5
		},
		text: {
			color: '#777',
			font: {
				family: 'Arial',
				size: 12,
				weight: 'normal'
			},
			glow: {
				color: 'none',
				width: 2
			},
			padding: {
				bottom: 0,
				left: 0,
				right: 0,
				top: 0
			},
			textAlign: 'left',
			textBaseline: 'bottom'
		}
	};
}

// Set the buffer width for the area past the chart in which the event will still render
const bufferWidth = 1;

var event = {
	canvasBufferWidth: {
		left: bufferWidth,
		right: bufferWidth
	},
	diameter: 16,
	offsetY: 20,
	padding: 5,
	xAxis: {
		padding: 0
	},
	cluster: {
		distance: 15
	},
	stack: {
		distance: 17
	}
};

const font = {
	family: null,
	size: null
};
const flag = {
	background: {
		color: '#555'
	},
	color: '#FFF',
	font,
	padding: 3, // fallback padding if not specified elsewhere
	shapeType: 'polygon', // polygon, rectangle
	polygon: {
		yAxis: {
			left: { // style for left YAxis
				label: {
					padding: {
						top: null,
						right: 0, // the default YAxis font size (11px) looks better with 0 here (because the pointer effectively is the padding). Larger fonts may need more right padding.
						bottom: null,
						left: null
					}
				}
			},
			right: { // style for right YAxis
				label: {
					padding: {
						top: null,
						right: null,
						bottom: null,
						left: 0 // the default YAxis font size (11px) looks better with 0 here (because the pointer effectively is the padding). Larger fonts may need more left padding.
					}
				}
			}
		},
		pointer: {
			height: null, // if null, automatically draw to flag's body height (the rectangle portion)
			width: 8
		}
	},
	rectangle: {
		radius: {
			x: 2,
			y: 2
		},
		yAxis: {
			left: { // style for left YAxis
				label: {
					padding: {
						top: null,
						right: null,
						bottom: null,
						left: 5
					}
				}
			},
			right: { // style for right YAxis
				label: {
					padding: {
						top: null,
						right: 5,
						bottom: null,
						left: null
					}
				}
			}
		}
	}
};

var flag$1 = {
	crosshair: {
		xAxis: flag,
		yAxis: flag
	},
	horizontalAnnotation: flag,
	indicator: flag,
	lastClose: flag
};

/**
 * Enum for Canvas context textAlign property
 * @readonly
 * @enum {string}
 */
const textAlign = Object.freeze({
	CENTER: 'center',
	// END: 'end',
	LEFT: 'left',
	RIGHT: 'right'
	// START: 'start'
});

/**
 * Enum for Canvas context textBaseline property
 * @readonly
 * @enum {string}
 */
const textBaseline = Object.freeze({
	ALPHABETIC: 'alphabetic',
	// BOTTOM: 'bottom',
	HANGING: 'hanging',
	// IDEOGRAPHIC: 'ideographic',
	MIDDLE: 'middle'
	// TOP: 'top'
});

/**
 * Enums associated with Canvas context methods
 * @readonly
 * @enum {object}
 */
var canvasEnums = {
	textAlign,
	textBaseline
};

function getMarkerStyles() {

	const bandBackgroundColor = getCommonStyle('color.band.background');
	const bandLineColor = getCommonStyle('color.band.line');
	const fontFamily = getCommonStyle('font.family');

	return {
		band: {
			background: {
				color: bandBackgroundColor
			},
			color: bandLineColor,
			label: {
				color: '#888',
				font: {
					family: fontFamily,
					size: 11,
					weight: 'normal'
				},
				padding: {
					left: 5,
					right: 5
				},
				textAlign: canvasEnums.textAlign.LEFT
			},
			lineDash: '',
			width: 1
		},
		extendedHours: {
			color: 'rgba(255, 255, 255, 0.3)',
			preMarket: {
				background: {
					color: 'rgba(155, 155, 155, 0.15)'
				},
				// Middle line
				startLine: {
					color: null,
					width: null
				},
				// Right line
				stopLine: {
					color: null,
					width: null
				}
			},
			postMarket: {
				background: {
					color: 'rgba(155, 255, 255, 0.1)'
				},
				// Left line
				startLine: {
					color: null,
					width: null
				}
			},
			width: 1
		}
	};
}

function getCCIStyles() {

	const commonLineWidth = getCommonStyle('line.width');
	const commonSessionBreak = getCommonStyle('sessionBreak');
	const markerStyle = getMarkerStyles();

	return {
		color: '#FF5F20',
		lineDash: '',
		overbought: markerStyle.band,
		oversold: markerStyle.band,
		width: commonLineWidth,
		sessionBreak: commonSessionBreak
	};
}

function getHAStyles() {

	const commonFontFamily = getCommonStyle('font.family');

	return {
		color: '#aaa',
		height: 15,
		margin: 5,
		text: {
			background: {
				color: '#ccc'
			},
			color: '#000',
			font: {
				family: commonFontFamily,
				size: 12,
				weight: 'normal'
			},
			hover: {
				background: {
					color: '#333'
				},
				color: '#aaa',
				font: {
					family: commonFontFamily,
					size: 11,
					weight: 'normal'
				}
			}
		},
		width: 1
	};
}

function getIchimokuStyles() {
	return {
		chikou: {
			color: '#F34949',
			width: 1.25,
			lineDash: null
		},
		kijun: {
			color: '#2E8AAC',
			width: 1.25,
			lineDash: null
		},
		senkou: {
			a: {
				color: 'green',
				width: 1.25,
				lineDash: null
			},
			b: {
				color: 'red',
				width: 1.25,
				lineDash: null
			},
			negative: {
				color: 'rgba(255, 0, 0, 0.2)'
			},
			positive: {
				color: 'rgba(73, 184, 64, 0.3)'
			}
		},
		tenkan: {
			color: '#9A4AEC',
			width: 1.25,
			lineDash: null
		},
		sessionBreak: {}
	};
}

function getMFIStyles() {

	const commonLineWidth = getCommonStyle('line.width');
	const commonYAxisPaddingBottom = getCommonStyle('panel.yAxisPadding.bottom');
	const commonYAxisPaddingTop = getCommonStyle('panel.yAxisPadding.top');
	const commonSessionBreak = getCommonStyle('sessionBreak');
	const marker = getMarkerStyles();

	return {
		color: 'purple',
		lineDash: '',
		overbought: marker.band,
		oversold: marker.band,
		width: commonLineWidth,
		yAxisPadding: {
			bottom: commonYAxisPaddingBottom,
			top: commonYAxisPaddingTop
		},
		sessionBreak: commonSessionBreak
	};
}

function getPercentBStyles() {

	const commonPrimaryColor = getCommonStyle('color.primary');
	const commonLineWidth = getCommonStyle('line.width');
	const commonSessionBreak = getCommonStyle('sessionBreak');
	const markerStyle = getMarkerStyles();

	return {
		color: 'brown',
		lineDash: '',
		median: {
			color: commonPrimaryColor,
			lineDash: '',
			width: 3,
			sessionBreak: commonSessionBreak
		},
		overbought: markerStyle.band,
		oversold: markerStyle.band,
		width: commonLineWidth,
		sessionBreak: commonSessionBreak
	};
}

/**
 * Enum for marker type, which determines the marker to use when drawing the chart
 * @readonly
 * @enum {string}
 */
const markerType = Object.freeze({
	/** Draw indicator using bar markers */
	BAR: 'bar',
	/** Draw indicator using candlestick markers */
	CANDLESTICK: 'candlestick',
	/** Draw indicator using a line chart with a fill underneath the line */
	FILL: 'fill',
	/** Draw indicator using High-Low-Close markers */
	HLC: 'hlc',
	/** Draw indicator using lines connecting each data point */
	LINE: 'line',
	/** Draw indicator using lines connecting each data point, with a shape at each point on top of the line */
	LINEWITHSHAPES: 'lineWithShapes',
	/** Draw indicator using Open-High-Low-Close markers with colors specified by ohlcType */
	OHLC: 'ohlc',
	/** Draw indicator using positive/negative fill based on previous close */
	POSNEG: 'posneg',
	/** Draw indicator using shapes for each data point */
	SHAPE: 'shape',
	/** Draw indicator using stepped line markers */
	STEPPED: 'stepped'
});

/**
 * Enum for OHLC type, which determines how to render the OHLC marker
 * @readonly
 * @enum {string}
 */
const ohlcType = Object.freeze({
	/** Draw using one color */
	MONO: 'mono',
	/** Draw new highs with positive color */
	NEWHIGHS: 'newhighs',
	/** Draw new lows with negative color */
	NEWLOWS: 'newlows',
	/** Draw using posneg logic */
	POSNEG: 'posneg'
});

const shapeType = Object.freeze({
	CIRCLE: 'circle',
	CUSTOM: 'custom',
	DIAMOND: 'diamond',
	SQUARE: 'square',
	TRIANGLE: 'triangle'
});

var PRICE = {
	markerType,
	ohlcType,
	shapeType
};

function getPriceStyles() {

	const commonPrimaryColor = getCommonStyle('color.primary');
	const commonNegativeColor = getCommonStyle('color.negative');
	const commonNeutralColor = getCommonStyle('color.neutral');
	const commonPositiveColor = getCommonStyle('color.positive');
	const commonLineWidth = getCommonStyle('line.width');

	return {
		background: {
			color: commonPrimaryColor,
			colorStop: 'rgba(255, 255, 255, 0.4)'
		},
		bar: {
			color: commonPrimaryColor
		},
		candlestick: {
			negative: {
				color: getCommonStyle('color.negative')
			},
			neutral: {
				color: commonNeutralColor
			},
			positive: {
				color: getCommonStyle('color.positive')
			},
			line: {
				default: {
					width: 1
				},
				large: {
					width: 1.5
				}
			},
			size: {
				large: false
			}
		},
		color: commonPrimaryColor,
		lineDash: null,
		ohlc: {
			negative: {
				color: commonNegativeColor
			},
			neutral: {
				color: commonNeutralColor
			},
			positive: {
				color: commonPositiveColor
			},
			line: {
				default: {
					width: 1
				},
				large: {
					width: 1.5
				}
			},
			size: {
				large: false
			}
		},
		palette: [
			'#ea580c', // orange
			'#16a34a', // green
			'#a855f7', // purple
			'#3b82f6', // blue
			'#ec4899', // pink
			'#eab308' // dark yellow
		],
		posneg: {
			lineDash: null,
			negative: {
				background: {
					color: 'rgba(178, 0, 0, 0.1)',
					colorStop: null
				},
				color: commonNegativeColor
			},
			positive: {
				background: {
					color: 'rgba(17, 182, 0, 0.1)',
					colorStop: null
				},
				color: commonPositiveColor
			},
			width: commonLineWidth
		},
		shape: {
			background: {
				color: commonPrimaryColor
			},
			color: commonPrimaryColor,
			length: 7,
			offset: 2,
			type: shapeType.CIRCLE,
			width: 1.5
		},
		width: commonLineWidth
	};
}

function getRSIStyles() {

	const commonLineWidth = getCommonStyle('line.width');
	const commonYAxisPaddingBottom = getCommonStyle('panel.yAxisPadding.bottom');
	const commonYAxisPaddingTop = getCommonStyle('panel.yAxisPadding.top');
	const commonSessionBreak = getCommonStyle('sessionBreak');
	const marker = getMarkerStyles();

	return {
		color: '#17510A',
		lineDash: '',
		overbought: marker.band,
		oversold: marker.band,
		width: commonLineWidth,
		yAxisPadding: {
			bottom: commonYAxisPaddingBottom,
			top: commonYAxisPaddingTop
		},
		sessionBreak: commonSessionBreak
	};
}

function getStochasticsStyles() {

	const commonLineWidth = getCommonStyle('line.width');
	const commonYAxisPaddingBottom = getCommonStyle('panel.yAxisPadding.bottom');
	const commonYAxisPaddingTop = getCommonStyle('panel.yAxisPadding.top');
	const commonSessionBreak = getCommonStyle('sessionBreak');
	const markerStyle = getMarkerStyles();

	return {
		color: '#600',
		d: {
			color: '#0076C9',
			lineDash: '3,1',
			width: commonLineWidth,
			sessionBreak: commonSessionBreak
		},
		lineDash: '',
		overbought: markerStyle.band,
		oversold: markerStyle.band,
		width: commonLineWidth,
		yAxisPadding: {
			bottom: commonYAxisPaddingBottom,
			top: commonYAxisPaddingTop
		},
		sessionBreak: commonSessionBreak
	};
}

function getUOStyles() {

	const commonLineWidth = getCommonStyle('line.width');
	const commonYAxisPaddingBottom = getCommonStyle('panel.yAxisPadding.bottom');
	const commonYAxisPaddingTop = getCommonStyle('panel.yAxisPadding.top');
	const commonSessionBreak = getCommonStyle('sessionBreak');
	const marker = getMarkerStyles();

	return {
		color: '#555',
		lineDash: '',
		overbought: marker.band,
		oversold: marker.band,
		width: commonLineWidth,
		yAxisPadding: {
			bottom: commonYAxisPaddingBottom,
			top: commonYAxisPaddingTop
		},
		sessionBreak: commonSessionBreak
	};
}

function getWPRStyles() {

	const commonLineWidth = getCommonStyle('line.width');
	const commonYAxisPaddingBottom = getCommonStyle('panel.yAxisPadding.bottom');
	const commonYAxisPaddingTop = getCommonStyle('panel.yAxisPadding.top');
	const commonSessionBreak = getCommonStyle('sessionBreak');
	const marker = getMarkerStyles();

	return {
		color: '#029974',
		lineDash: '',
		overbought: marker.band,
		oversold: marker.band,
		width: commonLineWidth,
		yAxisPadding: {
			bottom: commonYAxisPaddingBottom,
			top: commonYAxisPaddingTop
		},
		sessionBreak: commonSessionBreak
	};
}

function getIndicatorStyles() {

	return {
		adl: {
			color: '#D1009D',
			lineDash: null,
			width: getCommonStyle('line.width'),
			sessionBreak: getCommonStyle('sessionBreak')
		},
		advancedecline: {
			color: '#49CF6B',
			lineDash: null,
			width: getCommonStyle('line.width'),
			sessionBreak: getCommonStyle('sessionBreak')
		},
		avgtruerange: {
			color: '#007CEF',
			lineDash: null,
			width: getCommonStyle('line.width'),
			sessionBreak: getCommonStyle('sessionBreak')
		},
		awesomeoscillator: {
			positive: {
				color: getCommonStyle('color.positive')
			},
			negative: {
				color: getCommonStyle('color.negative')
			}
		},
		bestworst: {
			best: {
				background: {
					color: 'rgba(17, 182, 0, 0.1)'
				},
				color: getCommonStyle('color.positive'),
				lineDash: '2,3'
			},
			worst: {
				background: {
					color: 'rgba(178, 0, 0, 0.1)'
				},
				color: getCommonStyle('color.negative'),
				lineDash: '2,3'
			},
			width: 1
		},
		bollinger: {
			background: {
				color: 'rgba(194, 235, 254, 0.15)'
			},
			color: 'rgba(136, 136, 136, 0.8)',
			lineDash: null,
			sma: {
				color: getCommonStyle('color.positive'),
				lineDash: null,
				width: getCommonStyle('line.width'),
				sessionBreak: getCommonStyle('sessionBreak')
			},
			width: getCommonStyle('line.width'),
			sessionBreak: getCommonStyle('sessionBreak')
		},
		bollingerbandwidth: {
			color: '#CE9B3C',
			lineDash: null,
			width: getCommonStyle('line.width'),
			sessionBreak: getCommonStyle('sessionBreak')
		},
		chaikins: {
			color: '#32868F',
			lineDash: null,
			width: getCommonStyle('line.width'),
			sessionBreak: getCommonStyle('sessionBreak')
		},
		commoditychannelindex: getCCIStyles(),
		crs: {
			color: '#269843',
			lineDash: null,
			width: getCommonStyle('line.width'),
			sessionBreak: getCommonStyle('sessionBreak')
		},
		defaults: {
			color: null,
			width: getCommonStyle('line.width'),
			sessionBreak: getCommonStyle('sessionBreak')
		},
		dividendyield: {
			color: '#0090FF',
			lineDash: null,
			width: getCommonStyle('line.width'),
			sessionBreak: getCommonStyle('sessionBreak')
		},
		dmi: {
			adx: {
				color: '#0074E8',
				lineDash: '2,1',
				width: getCommonStyle('line.width'),
				sessionBreak: getCommonStyle('sessionBreak')
			},
			adxr: {
				color: 'darkorange',
				lineDash: null,
				width: getCommonStyle('line.width'),
				sessionBreak: getCommonStyle('sessionBreak')
			},
			negative: {
				color: 'blue',
				lineDash: null,
				width: getCommonStyle('line.width'),
				sessionBreak: getCommonStyle('sessionBreak')
			},
			positive: {
				color: '#FF5F20',
				lineDash: null,
				width: getCommonStyle('line.width'),
				sessionBreak: getCommonStyle('sessionBreak')
			},
			sessionBreak: getCommonStyle('sessionBreak')
		},
		ema: {
			color: '#D16905',
			lineDash: null,
			width: getCommonStyle('line.width'),
			sessionBreak: getCommonStyle('sessionBreak')
		},
		gsrecommendation: {
			align: 'bottom',
			border: {
				left: {
					color: '#FFF'
				}
			},
			height: 7,
			margin: {
				bottom: 0,
				top: 0,
				hover: 0
			},
			notrated: {
				background: {
					color: 'rgba(0, 0, 0, 0.02)'
				}
			},
			positive: {
				background: {
					color: getCommonStyle('color.positive')
				}
			},
			negative: {
				background: {
					color: getCommonStyle('color.negative')
				}
			},
			neutral: {
				background: {
					color: getCommonStyle('color.neutral')
				}
			}
		},
		highlow: {
			min: {
				color: '#e70d18'
			},
			max: {
				color: '#077c07'
			},
			label: {
				glow: {
					color: null
				}
			}
		},
		horizontalannotation: getHAStyles(),
		ichimokucloud: getIchimokuStyles(),
		linearregression: {
			color: '#C05E00',
			lineDash: null,
			width: getCommonStyle('line.width')
		},
		macd: {
			color: '#046EE6',
			ema: {
				color: '#A82323',
				lineDash: null,
				width: getCommonStyle('line.width'),
				sessionBreak: getCommonStyle('sessionBreak')
			},
			lineDash: null,
			negative: {
				color: getCommonStyle('color.negative')
			},
			positive: {
				color: getCommonStyle('color.positive')
			},
			width: getCommonStyle('line.width'),
			zero: {
				color: '#888',
				lineDash: null,
				width: getCommonStyle('line.width')
			},
			sessionBreak: getCommonStyle('sessionBreak')
		},
		mae: {
			background: {
				color: 'rgba(194, 235, 254, 0.15)'
			},
			color: '#999',
			lineDash: null,
			width: getCommonStyle('line.width'),
			sessionBreak: getCommonStyle('sessionBreak')
		},
		massindex: {
			color: '#672D8A',
			lineDash: null,
			setup: {
				color: '#EA7125',
				lineDash: null,
				width: 1,
				sessionBreak: getCommonStyle('sessionBreak')
			},
			trigger: {
				color: '#00A0DF',
				lineDash: null,
				width: 1,
				sessionBreak: getCommonStyle('sessionBreak')
			},
			width: getCommonStyle('line.width'),
			sessionBreak: getCommonStyle('sessionBreak')
		},
		momentum: {
			background: {
				color: 'rgba(137, 56, 186, 0.2)'
			},
			color: '#8938BA',
			lineDash: null,
			width: getCommonStyle('line.width'),
			sessionBreak: getCommonStyle('sessionBreak')
		},
		moneyflow: {
			color: 'darkred',
			lineDash: null,
			width: getCommonStyle('line.width'),
			sessionBreak: getCommonStyle('sessionBreak')
		},
		moneyflowindex: getMFIStyles(),
		onbalancevolume: {
			color: '#E47600',
			lineDash: null,
			width: getCommonStyle('line.width'),
			sessionBreak: getCommonStyle('sessionBreak')
		},
		nrv: {
			color: getCommonStyle('color.primary'),
			negative: {
				color: getCommonStyle('color.negative')
			},
			neutral: {
				color: '#999'
			},
			panelHeightRatio: getCommonStyle('panel.heightRatio'),
			positive: {
				color: getCommonStyle('color.positive')
			},
			yAxisPadding: {
				bottom: getCommonStyle('panel.yAxisPadding.bottom')
			}
		},
		perange: {
			color: '#2E9DE4',
			lineDash: null,
			low: {
				color: 'green',
				lineDash: null,
				width: getCommonStyle('line.width'),
				sessionBreak: getCommonStyle('sessionBreak')
			},
			width: getCommonStyle('line.width'),
			sessionBreak: getCommonStyle('sessionBreak')
		},
		peratio: {
			color: '#E47600',
			lineDash: null,
			width: getCommonStyle('line.width'),
			sessionBreak: getCommonStyle('sessionBreak')
		},
		percentb: getPercentBStyles(),
		previousclose: {
			color: '#555',
			label: {
				color: '#333',
				font: {
					family: getCommonStyle('font.family'),
					size: 11,
					weight: 'normal'
				},
				glow: {
					color: null
				}
			},
			lineDash: null,
			margin: {
				top: 0,
				bottom: 0
			},
			width: getCommonStyle('line.width')
		},
		price: getPriceStyles(),
		pricechannel: {
			background: {
				color: 'rgba(194, 235, 254, 0.15)'
			},
			color: '#888',
			lineDash: null,
			width: getCommonStyle('line.width'),
			sessionBreak: getCommonStyle('sessionBreak')
		},
		proc: {
			color: 'green',
			lineDash: null,
			width: getCommonStyle('line.width'),
			zero: {
				color: '#000',
				lineDash: null,
				width: getCommonStyle('line.width')
			},
			sessionBreak: getCommonStyle('sessionBreak')
		},
		psar: {
			color: '#EA4D00',
			length: 4
		},
		revenues: {
			color: '#AB491F',
			lineDash: null,
			width: getCommonStyle('line.width'),
			sessionBreak: getCommonStyle('sessionBreak')
		},
		rollingdividend: {
			color: '#26A42D',
			lineDash: null,
			width: getCommonStyle('line.width'),
			sessionBreak: getCommonStyle('sessionBreak')
		},
		rollingeps: {
			color: '#216711',
			lineDash: null,
			width: getCommonStyle('line.width'),
			sessionBreak: getCommonStyle('sessionBreak')
		},
		rsi: getRSIStyles(),
		sectorindustry: {
			color: '#EA4D00',
			lineDash: null,
			width: getCommonStyle('line.width')
		},
		sma: {
			color: '#0060FF',
			lineDash: null,
			width: getCommonStyle('line.width'),
			sessionBreak: getCommonStyle('sessionBreak')
		},
		stochastics: getStochasticsStyles(),
		tsf: {
			color: '#0076C9',
			lineDash: null,
			width: getCommonStyle('line.width'),
			sessionBreak: getCommonStyle('sessionBreak')
		},
		ultimateoscillator: getUOStyles(),
		updown: {
			color: '#72D6FB',
			lineDash: null,
			width: getCommonStyle('line.width')
		},
		updownratio: {
			color: '#2E8AAC',
			lineDash: null,
			width: getCommonStyle('line.width')
		},
		volume: {
			color: getCommonStyle('color.primary'),
			negative: {
				color: getCommonStyle('color.negative')
			},
			neutral: {
				color: '#999'
			},
			panelHeightRatio: getCommonStyle('panel.heightRatio'),
			positive: {
				color: getCommonStyle('color.positive')
			},
			radii: null,
			upper: {
				color: null,
				negative: {
					color: null
				},
				neutral: {
					color: null
				},
				positive: {
					color: null
				}
			},
			yAxisPadding: {
				bottom: getCommonStyle('panel.yAxisPadding.bottom')
			}
		},
		volumebyprice: {
			up: {
				color: 'rgba(34, 139, 34, 0.5)'
			},
			down: {
				color: 'rgba(178, 34, 34, 0.5)'
			}
		},
		vroc: {
			color: '#D56B03',
			lineDash: null,
			width: getCommonStyle('line.width'),
			zero: {
				color: '#333',
				width: 1,
				lineDash: null,
				sessionBreak: getCommonStyle('sessionBreak')
			},
			sessionBreak: getCommonStyle('sessionBreak')
		},
		vwap: {
			color: 'purple',
			lineDash: null,
			width: getCommonStyle('line.width'),
			sessionBreak: getCommonStyle('sessionBreak')
		},
		williamspctr: getWPRStyles(),
		wma: {
			color: '#03907E',
			lineDash: null,
			width: getCommonStyle('line.width'),
			sessionBreak: getCommonStyle('sessionBreak')
		},
		yieldspread: {
			color: '#EA4D00',
			lineDash: null,
			width: getCommonStyle('line.width'),
			sessionBreak: getCommonStyle('sessionBreak')
		}
	};
}

var crosshair$1 = {
	default: {
		color: '#777',
		lineDash: '2,1',
		width: 1
	},
	horizontal: {
		color: null,
		lineDash: null,
		width: null
	},
	vertical: {
		color: null,
		lineDash: null,
		width: null
	}
};

const color = [
	'#000000',
	'#A92B21',
	'#699C1E',
	'#24B829',
	'#378A6E',
	'#5A5A5A',
	'#5A5A5A',
	'#378A6E',
	'#24B829',
	'#699C1E',
	'#A92B21'
];

const background = {
	color: color.map(c => `${c}4D`) // Add transparency to the colors from above
};

const label$1 = {
	color: null, // the label will use the section color if this is not defined.
	format: {
		default: '0,.2f',
		percent: '0,.2%'
	},
	font: {
		family: 'Arial, Helvetica, sans-serif',
		size: 12,
		weight: 'normal'
	}
};

const thresholds = [
	0,
	0.236,
	0.382,
	0.5,
	0.618,
	1,
	1.618,
	2.618,
	3.618,
	4.236
];

var fibonacciTools = {
	fibArc: {
		background: { color: background.color.slice(1, 6) },
		color: color.slice(1, 6),
		label: label$1,
		thresholds: thresholds.slice(1, 6),
		width: 1
	},
	fibCircle: {
		background: { color: background.color.slice(1, 6) },
		color: color.slice(1, 6),
		label: label$1,
		thresholds: thresholds.slice(1, 6),
		width: 1
	},
	fibFan: {
		background: { color: background.color.slice(0, 6) },
		color: color.slice(0, 6),
		label: label$1,
		thresholds: thresholds.slice(0, 6),
		width: 1
	},
	fibonacci: {
		background,
		color,
		label: label$1,
		thresholds,
		width: 1
	}
};

function getToolStyles() {

	const commonAccentPrimary = getCommonStyle('color.accent.primary');
	const commonLineWidth = getCommonStyle('line.width');
	const commonFontFamily = getCommonStyle('font.family');

	return {
		arrow: {
			arrow: {
				height: 15,
				width: 5
			},
			background: {
				color: 'steelblue'
			},
			color: 'steelblue',
			width: 2
		},
		edgeHandle: {
			background: {
				color: null
			},
			color: null,
			hover: {
				background: {
					color: null
				},
				color: null
			},
			width: 20
		},
		ellipse: {
			background: {
				color: '#0001'
			},
			boundingBox: {
				color: '#000',
				lineDash: '2,3'
			},
			color: '#000',
			width: 1
		},
		extendedHorizontal: {
			support: {
				color: 'limegreen',
				width: 2
			},
			resistance: {
				color: 'red',
				width: 2
			},
			default: {
				color: 'pink',
				width: 2
			}
		},
		extendedLine: {
			color: 'orange',
			width: 2
		},
		...fibonacciTools,
		gannFan: {
			color: [
				'#666',
				'darkred',
				'orange',
				'#A8A800',
				'darkgreen'
			],
			gannLabels: ['1/1', '1/2', '1/3', '1/4', '1/8'],
			thresholds: [1, 0.5, 0.333, 0.25, 0.125],
			width: 1,
			label: {
				color: null,
				font: {
					family: 'Arial, Helvetica, sans-serif',
					size: 12,
					weight: 'normal'
				}
			}
		},
		handle: {
			hover: { // when the tool is unselected and handle is hovered
				background: {
					color: '#EEE' // fill color
				},
				color: '#999', // border color
				length: 9.5, // circle radius
				width: 1.5 // stroke width
			},
			background: {
				color: '#EEE' // fill color
			},
			color: '#999', // border color
			length: 7.5, // circle radius
			width: 1.5, // stroke width
			shape: 'default' // Circle, no other option at the moment
		},
		horizontalLine: {
			color: commonAccentPrimary,
			width: 1
		},
		line: {
			color: '#80B9FF',
			width: commonLineWidth
		},
		measurementLine: {
			background: {
				color: '#0001'
			},
			color: '#2496F1',
			font: {
				family: commonFontFamily,
				size: 14,
				weight: 'normal'
			},
			label: {
				background: {
					border: {
						color: '#AAA',
						lineDash: '1,0',
						width: 1.5
					},
					color: '#FFF',
					radius: 3,
					padding: {
						left: 8,
						right: 8,
						top: 4,
						bottom: 2
					}
				},
				margin: 10,
				color: '#333'
			},
			width: commonLineWidth
		},
		crosshair: crosshair$1,
		ohlcCrosshair: {
			horizontal: {
				color: '#333333',
				width: 1
			},
			vertical: {
				color: '#333333',
				width: 1
			},
			shadow: {
				blur: 14,
				color: 'rgba(0, 0, 0, 0.5)'
			}
		},
		ray: {
			color: 'purple',
			width: 2
		},
		rectangle: {
			background: {
				color: 'transparent'
			},
			color: 'darkred',
			width: 2
		},
		returnsCalculator: {
			center: {
				background: {
					color: '#FFFFFF11'
				}
			},
			side: {
				background: {
					color: '#FFFFFFBF'
				},
				color: '#333333',
				width: 1
			}
		},
		snapHandle: {
			color: 'orange'
		},
		text: {
			background: {
				color: 'transparent'
			},
			boxHeight: 70,
			boxWidth: 200,
			color: '#000',
			font: {
				family: commonFontFamily,
				size: 14,
				weight: 'normal'
			},
			lineDash: '2,3',
			text: {
				color: '#333',
				lineHeight: 1.35
			},
			width: 1
		}
	};
}

function getAxesStyles() {

	const color = '#666';
	const background = { color: null };
	const commonFontFamily = getCommonStyle('font.family');
	const commonAccentSecondary = getCommonStyle('color.accent.secondary');
	const font = {
		family: commonFontFamily,
		size: 11,
		weight: 'normal'
	};

	return {
		xAxis: {
			background,
			font,
			bottomRow: {
				label: {
					color: null
				},
				font: {
					weight: null,
					size: null,
					family: null
				}
			},
			label: {
				color,
				lineHeight: null,
				overlap: {
					right: 8,
					left: 0 // Not supported yet
				},
				padding: {
					left: 3,
					right: 3,
					top: 5
				},
				spacing: {
					default: 50,
					minutes: 75
				},
				steps: {
					minutes: [1, 5, 10, 15, 30],
					hours: [1, 2, 4],
					days: [1, 2, 3],
					weeks: [1, 2, 4, 16],
					months: [1, 2, 3, 4, 6],
					years: [1, 2, 5, 10]
				},
				textAlign: canvasEnums.textAlign.LEFT
			},
			// Padding for XAxis domain (as opposed to padding for labels)
			padding: {
				left: 0,
				right: 0
			},
			tick: {
				alt: {
					color: commonAccentSecondary,
					length: 8,
					lineDash: null,
					width: null
				},
				color: commonAccentSecondary,
				length: 4,
				lineDash: '',
				width: 1
			}
		},
		yAxis: {
			background,
			font,
			label: {
				color,
				format: {
					default: { // any number that doesn't meet the other thresholds (large/small/micro) will use these formats
						format: '0,.2f', // e.g. 123.45
						formatPercent: '0,.2%' // e.g. 12.34%
					},
					large: {
						format: '0,.2s', // e.g. 600k
						formatPercent: '0,.0%', // e.g. 12%
						threshold: 5000 // steps greater or equal to this will use the large format
					},
					small: {
						format: '0.3f', // e.g. 0.123
						formatPercent: '0.2%', // e.g. 12.34%
						threshold: 0.01 // steps less than this will use the small format
					},
					micro: {
						format: '0.4f',
						formatPercent: '0.2%', // e.g. 12.34%
						threshold: 0.001 // steps less than this will use the micro format
					}
				},
				// Flag labels often need slightly different thresholds than axis labels.
				// For instance the axis labels may be round steps '600k, 500k, 400k' but the flag's format string should have
				// more granularity, like '524,010', so that the user knows the exact number the flag is pointing to.
				flag: {
					format: {
						default: { // any number that doesn't meet the "large" or "small" thresholds will use these formats
							format: '0,.2f', // e.g. 123.45
							formatPercent: '0,.2%' // e.g. 12.34%
						},
						large: {
							format: '0,.0f', // e.g. 12,345
							formatPercent: '0,.2%', // e.g. 12.34%
							threshold: 10000 // numbers greater or equal to this will use the large format
						},
						small: {
							format: '0.3f', // e.g. 0.123
							formatPercent: '0,.2%', // e.g. 12.34%
							threshold: 1 // numbers less than this will use the small format
						}
					}
				},
				// Padding for YAxis labels (as opposed to padding for domain)
				// i.e. this is the padding directly around the text of the label, like "25.6%"
				padding: {
					bottom: 3,
					left: 5,
					right: 5,
					top: 3
				},
				spacing: {
					default: 50,
					small: 30
				},
				textAlign: canvasEnums.textAlign.LEFT,
				textBaseline: canvasEnums.textBaseline.MIDDLE,
				threshold: {
					small: 170
				},
				vertical: {
					right: {
						font,
						offsetX: 0
					}
				}
			},
			// Padding for YAxis domain (as opposed to padding for labels)
			// This only applies vertically, and adds padding to the top and bottom of the entire YAxis.
			padding: {
				top: 15,
				bottom: 15
			},
			tick: {
				color: null,
				length: 4,
				lineDash: '',
				width: null
			}
		}
	};
}

var crosshair = {
	circleRadius: 3.5
};

function getGridStyles() {

	const commonAccentPrimary = getCommonStyle('color.accent.primary');
	const commonAccentSecondary = getCommonStyle('color.accent.secondary');

	return {
		background: { color: null },
		color: commonAccentSecondary,
		extension: 0,
		horizontal: {
			color: null,
			extension: null,
			lineDash: '1,1',
			normalize: {
				color: null,
				lineDash: null,
				width: null
			},
			width: null
		},
		lineDash: '',
		vertical: {
			alt: {
				color: commonAccentPrimary,
				extension: null,
				lineDash: null,
				width: null
			},
			color: null,
			extension: null,
			lineDash: '1,1',
			normalize: {
				color: null,
				lineDash: null,
				width: 1.5
			},
			width: null
		},
		width: 1
	};
}

var label = {
	closure: {
		color: 'rgba(255, 255, 255, 0.12)'
	}
};

function getBorderStyles() {

	const commonAccentSecondary = getCommonStyle('color.accent.secondary');

	return {
		bottom: {
			color: null,
			lineDash: null,
			padding: null,
			width: null
		},
		default: {
			color: commonAccentSecondary,
			lineDash: '',
			padding: 0,
			width: 1
		},
		left: {
			color: null,
			lineDash: null,
			padding: null,
			width: null
		},
		right: {
			color: null,
			lineDash: null,
			padding: null,
			width: null
		},
		top: {
			color: null,
			lineDash: null,
			padding: null,
			width: null
		}
	};
}

const commonSessionBreak = getCommonStyle('sessionBreak');

function getPanelStyles() {

	const axes = getAxesStyles();

	const sessionBreakStyle = merge({}, commonSessionBreak, {
		background: {
			color: 'rgba(0, 0, 0, 0.02)'
		}
	});

	return {
		...axes,
		border: getBorderStyles(),
		crosshair,
		grid: getGridStyles(),
		label,
		margin: {
			bottom: 0,
			top: 0
		},
		padding: {
			bottom: 35,
			left: 0,
			right: 55,
			top: 0
		},
		preMarket: {
			background: {
				color: null
			},
			border: {
				lineDash: null,
				color: null,
				width: null
			}
		},
		postMarket: {
			background: {
				color: null
			},
			border: {
				lineDash: null,
				color: null,
				width: null
			}
		},
		sessionBreak: sessionBreakStyle
	};
}

function getPanelLowerStyles() {
	// pull from commonStyles if wanted here
	return {
		yAxis: {
			padding: {
				top: 4,
				bottom: 4
			}
		}
	};
}

function getPanelYAxisLeftStyles() {
	return {
		yAxis: {
			label: {
				textAlign: canvasEnums.textAlign.RIGHT
			}
		},
		padding: {
			left: 50,
			right: 0
		}
	};
}

// Dynamically generate default styles
const generateDefaultStyles = (commonStyles = {}) => {
	// Apply any common style overrides to the `commonStyleOverrides` object.
	// Later, when calls such as `getIndicatorStyles()` or `getToolStyles` are made, they will
	// reference the updated `commonStyleOverrides` object.
	setCommonStyles(commonStyles);
	// Use 'merge' here to break references within these objects
	return merge({}, {
		annotation: getAnnotationStyles(),
		event,
		flag: flag$1,
		indicator: getIndicatorStyles(),
		marker: getMarkerStyles(),
		panel: getPanelStyles(),
		panelLower: getPanelLowerStyles(),
		panelYAxisLeft: getPanelYAxisLeftStyles(),
		tool: getToolStyles()
	});
};

/**
 * Enum for average type, which is used in calculating the MA envelope
 * @readonly
 * @enum {string}
 */
const averageTypes = Object.freeze({
	/** Use EMA in MAE calculations */
	EMA: 'ema',
	/** Use SMA in MAE calculations */
	SMA: 'sma',
	/** Use WMA in MAE calculations */
	WMA: 'wma'
});

var MAE = {
	averageTypes
};

/**
 * Enums associated with various indicators
 * @readonly
 * @enum {object}
 */
var indicators = Object.freeze({
	/** Enums associated with MAE indicator */
	MAE,
	/** Enums associated with Price indicator */
	PRICE
});

const HIGHEST = 0;
const HIGH = 1;
const MIDDLE = 2;
const LOW = Infinity;

/**
 * Enums associated with the labeler
 * @readonly
 * @enum {object}
 */
var labelerEnums = Object.freeze({
	labelPriorities: {
		LOW,
		MARKET_OPEN: HIGH,
		MARKET_CLOSE: HIGH,
		MIDDAY: MIDDLE,
		MIDDLE,
		HIGH,
		HIGHEST
	}
});

/**
 * Enums associated with adjusting the scale domain for an axis
 * @readonly
 * @enum {object}
 * @property {object} xAxis - Categories which would necessitate an augmentation to the X-axis domain
 */
const domainAdjustmentCategories = Object.freeze({
	xAxis: {
		BAR_MARKER: 'barMarker',
		EVENT: 'event',
		LINEWITHSHAPES: 'lineWithShapes',
		SHAPE: 'shape'
	}
});

/**
 * Enums associated with the renderDuplicateLabels param
 * @readonly
 * @enum {object}
 * @property {string} KEEP_ALL - Render all labels (skip the check for duplicates)
 * @property {string} KEEP_LEFT - Render the left-most duplicate label
 * @property {string} KEEP_RIGHT - Render the right-most duplicate label
 */
const renderDuplicateLabelsCategories = Object.freeze({
	KEEP_ALL: 'KEEP_ALL',
	KEEP_LEFT: 'KEEP_LEFT',
	KEEP_RIGHT: 'KEEP_RIGHT'
});

/**
 * Enums associated with the showMarketCloseLabels params
 * @readonly
 * @enum {string}
 * @property {string} ALL - For left aligned labels (the default), the market close labels will be drawn to the right of the market open labels. For center and right aligned labels, the market close label will only be drawn for the current day.
 * @property {string} NONE - No market close labels will be drawn
 * @property {string} ONCE - The label will only be drawn at the last drawable location on the XAxis
 */
const showMarketCloseLabelsCategories = Object.freeze({
	ALL: 'all',
	NONE: null,
	ONCE: 'once'
});

/**
 * Enums associated with the Modcharts class
 * @readonly
 * @enum {object}
 */
var modcharts = {
	domainAdjustmentCategories,
	renderDuplicateLabelsCategories,
	showMarketCloseLabelsCategories
};

/**
 * Enum for starting position in the Crosshair tool, which determines where the tool
 * will be initially rendered
 * @readonly
 * @enum {string}
 */
const startingPosition = {
	/** Initially render tool at the center data point */
	CENTER: 'center',
	/** Initially render tool at left-most data point */
	LEFT: 'left',
	/** Initially render tool at right-most data point */
	RIGHT: 'right'
};

var CROSSHAIR = {
	startingPosition
};

/**
 * Enum for label format in Fibonacci tools, which determines how the label is rendered
 * @readonly
 * @enum {string}
 */
const FibToolLabelFormat$4 = {
	/** Display the labels using a decimal representation */
	DECIMAL: 'decimal',
	/** Display the labels using a percentage representation */
	PERCENT: 'percent'
};

var FIBONACCI = {
	FibToolLabelFormat: FibToolLabelFormat$4
};

/**
 * Enums associated with various tools
 * @readonly
 * @enum {object}
 */
var toolEnums = Object.freeze({
	/** Enums associated with the Crosshair tool */
	CROSSHAIR,
	/** Enums associated with the Fibonacci Line tool */
	FIBONACCI
});

var enums = {
	cursor,
	event: event$1,
	indicators,
	labeler: labelerEnums,
	modcharts,
	tools: toolEnums
};

/* eslint-disable no-alert */

/** Combine all panel canvas elements into a single canvas for use in printing or saving */
const _getExportCanvas = (core, args = {}) => {
	core.eventEmitter.emit(types.EXPORT_CANVAS_START);

	const { size } = core.panels[0];
	const rootNode = core.rootModchart.node();
	const isStyled = !!getComputedStyle(rootNode)['width'];
	const bBox = rootNode.getBoundingClientRect();
	const { height: origHeight, width: origWidth } = bBox;
	const bgColor = _getVisibleBackgroundColor(rootNode);
	const ratio = core.getScreenRatio();

	args.height = args.height || 0;
	args.width = args.width || (size.width + size.padding.left + size.padding.right);

	if (args.height === 0) {
		// get total height
		core.panels.forEach(panel => {
			args.height +=
				panel.size.height +
				panel.size.padding.top +
				panel.size.padding.bottom;
		});
	}

	// set new dimensions
	rootNode.style.height = args.height;
	rootNode.style.width = args.width;

	core.resize();
	core.render();

	args.header = _getExportCanvasHeader();
	args.footer = _getExportCanvasFooter();

	const canvasHeight = args.height + args.header.height + args.footer.height + 1;

	// create canvas inside iframe
	const canvas = document.createElement('canvas');
	canvas.setAttribute('width', args.width * ratio);
	canvas.setAttribute('height', canvasHeight * ratio);
	canvas.style.width = args.width;
	canvas.style.height = canvasHeight;
	const ctx = canvas.getContext('2d');


	ctx.rect(0, 0, args.width * ratio, canvasHeight * ratio);
	ctx.fillStyle = bgColor;
	ctx.fill();

	// fill canvas with content
	_applyExportCanvasContent(core, ctx, args);

	// reset dimensions back to original
	rootNode.style.height = isStyled ? origHeight : '';
	rootNode.style.width = isStyled ? origWidth : '';

	core.resize();
	core.render();

	core.eventEmitter.emit(types.EXPORT_CANVAS_END);

	return canvas;
};

/**
 * Overridable method to add custom header content to an exported canvas
 * height = intended height of header section
 * applyContent = function reference that will receive a canvas context to draw into
 */
const _getExportCanvasHeader = () => {
	// default
	return {
		height: 0,
		applyContent: () => {
			return;
		}
	};
};

/**
 * Overridable method to add custom footer content to an exported canvas
 * height = intended height of footer section
 * applyContent = function reference that will receive a canvas context to draw into
 */
const _getExportCanvasFooter = () => {
	// default
	return {
		height: 0,
		applyContent: () => {
			return;
		}
	};
};

/** Draw each panel into the temporary canvas context */
const _applyExportCanvasContent = (core, context, args) => {
	let y = args.header
		? args.header.height
		: 0;
	const ratio = core.getScreenRatio();

	// optional header content
	if (args.header) args.header.applyContent(context, 0);

	// default font
	if (!args.font) args.font = '11px Arial';

	// init context
	context.font = args.font;
	context.textBaseline = 'top';
	context.textAlign = 'left';

	// add legend text (which is normally drawn using HTML) to each panel canvas
	core.panels.forEach(panel => {
		context.drawImage(panel.rootCanvas.node(), 0, y);
		context.drawImage(panel.rootTools.node(), 0, y);

		// draw legend content for this panel
		_applyExportCanvasLegendContent(core, context, panel, y);

		y +=
			(
				Math.round(panel.size.height) +
				panel.size.padding.top +
				Math.max(
					panel.size.padding.bottom,
					panel.size.margin.bottom
				)
			) *
			ratio;
	});

	// optional footer content
	if (args.footer) args.footer.applyContent(context, y);
};

/** Overridable method to draw a legend for a single panel into the export canvas */
const _applyExportCanvasLegendContent = (core, context, panel, top) => {
	let inputs = [];
	let xLeft = panel.size.padding.left + 5;
	let label = '';
	let symbol = '';

	for (const indicator of panel.indicators) {
		inputs = [];

		for (const input of indicator.params.inputs) {
			inputs.push(input.value);
		}

		// text label
		if (indicator.params.id === 'price') {
			// price labels should include company name
			symbol = indicator.params.symbol;

			label = symbol
				? core.xref[symbol].companyName
				: indicator.params.symbol;
		} else {
			// all other indicators
			label =
				(indicator.params.name || '') +
				(inputs.length !== 0 ? ` (${inputs.join(',')})` : '');
		}

		context.fillStyle =
			indicator.params.style.color ||
			(indicator.params.style.background && indicator.params.style.background.color) ||
			'#666';
		context.fillText(label, xLeft, top + 2);

		xLeft += context.measureText(label).width + 10;
	}
};

/** Get first non-transparent backgroundColor from DOM ancestors */
const _getVisibleBackgroundColor = el => {
	let bgColor = null;

	while (!bgColor && el.parentNode !== document) {
		// Get the color from the element attributes
		const color = el.style.backgroundColor;
		if (color.split(',').length !== 4 && color !== 'transparent') bgColor = color;

		el = el.parentNode;
	}

	return bgColor || '#FFFFFF';
};

/** Combine canvas panels and print hidden iframe */
const print = (core, args = {}) => {
	// unselect tools and exit tool mode
	core.selectTool(null);
	core.setToolMode(false);

	core.render();

	let printFrame = document.createElement('iframe');
	const marginTop = 80;
	const canvas = _getExportCanvas(core, args);
	printFrame.setAttribute('height', core.size.height);
	printFrame.setAttribute('width', core.size.width);
	printFrame.style.visibility = 'hidden';
	printFrame.style.zIndex = -1;
	printFrame.addEventListener('load', () => {
		const doc = printFrame.contentDocument;
		const body = doc.body;
		// append new canvas
		canvas.style.position = 'absolute';
		canvas.style.top = marginTop;

		body.appendChild(canvas);

		// firefox likes a setTimeout here
		window.setTimeout(() => {
			// invoke print from within iframe
			printFrame.contentWindow.focus();

			if (
				!printFrame.contentWindow.document.execCommand(
					'print',
					false,
					null
				)
			) {
				printFrame.contentWindow.print();
			}

			// remove hidden iframe
			printFrame.parentElement.removeChild(printFrame);
			printFrame = null;
		}, 0);
	});

	document.body.appendChild(printFrame);
};

/** Store down image on server */
const saveImage = async (core, args) => {
	// unselect tools and exit tool mode
	core.selectTool(null);
	core.setToolMode(false);

	args = args || {
		width: core.size.width,
		height: core.size.height
	};

	const canvas = _getExportCanvas(core, args);
	const canvasB64 = canvas.toDataURL();
	const inputs = {
		json: canvasB64,
		name: args.name
	};

	// optional id
	if (args.id && args.id.length) {
		inputs.id = args.id;
	}

	try {
		const response = await axios.post(
			`${core.params.apiPath}chartapi/saveimage`,
			inputs,
			{ headers: core._getXHRHeaders() }
		);

		const result = core.parseDebugInfo(response.request.responseText);

		if (args.onComplete) {
			args.onComplete(result);
		} else if (result.Id) {
			const url = `${core.params.apiPath}chartapi/loadimage/${result.Id}`;
			core.eventEmitter.emit(types.IMAGE_SAVE_SUCCESS, { result, url });
			return {
				args,
				id: result.Id,
				result,
				url
			};
		} else {
			core.eventEmitter.emit(types.IMAGE_SAVE_ERROR, { result });
			throw new Error(
				"Can't complete request to save image"
			);
		}
	} catch (error) {
		window.alert(`An error occurred.\n\n${error.message}`);
	}
	return true;
};

/** Temporarily store image, send down with custom filename, then delete image. */
const downloadImage = async (core, args = {}) => {
	// unselect tools and exit tool mode
	core.selectTool(null);
	core.setToolMode(false);

	const canvas = _getExportCanvas(core, args);
	const canvasB64 = canvas.toDataURL();

	try {
		const response = await axios.post(
			`${core.params.apiPath}chartapi/saveimage`,
			{ json: canvasB64 },
			{ headers: core._getXHRHeaders() }
		);

		const result = core.parseDebugInfo(response.request.responseText);

		if (result.Id) {
			let url = `${core.params.apiPath}chartapi/loadimage/${result.Id}?download=true&remove=true`;

			if (args.name) url += `&filename=${args.name}`;

			if (core.state.authToken) url += `&access_token=${core.state.authToken}`;

			// Set URL to initiate download
			window.location.href = url;

			return {
				args,
				id: result.Id,
				result
			};
		}
		throw new Error("Can't complete request to save image");
	} catch (error) {
		window.alert(`An error occurred.\n\n${error.message}`);
	}
	return true;
};

var _export = {
	downloadImage,
	print,
	saveImage
};

var defaultLabelTemplates = {
	xAxis: {
		doubleRow: {
			minutes: {
				default: '%X', // 4:00pm (Note: affected by the "time" setting in the locale definition)
				first: '%x %A' // 5/17/2024 Friday
			},
			hours: {
				default: '%X', // 4:00pm (Note: affected by the "time" setting in the locale definition)
				first: '%x %A' // 5/17/2024 Friday
			},
			days: {
				default: '%e', // 17
				first: '%b %Y', // May 2024
				firstAlt: '%x %A', // 5/17/2024 Friday
				intraday: '%x %A' // 5/17/2024 Friday
			},
			weeks: {
				default: '%-m/%-d', // 5/17
				first: '%b %Y', // May 2024
				firstAlt: '%b %Y' // May 2024
			},
			months: {
				default: '%b', // May
				first: '%Y', // 2024
				firstAlt: '%b %Y' // May 2024
			},
			years: {
				default: '%Y', // 2024
				first: '%Y' // 2024
			}
		},
		singleRow: {
			minutes: {
				default: '%X', // 4:00pm (Note: affected by the "time" setting in the locale definition)
				first: '%-m/%-d', // 5/17
				firstAlt: '%X' // 5/17
			},
			hours: {
				default: '%X', // 4:00pm (Note: affected by the "time" setting in the locale definition)
				first: '%-m/%-d', // 5/17
				firstAlt: '%X' // 5/17
			},
			days: {
				default: '%e', // 17
				first: '%-m/%-d' // 5/17
			},
			weeks: {
				default: '%-m/%-d', // 5/17
				first: '%b %Y', // May 2024
				firstAlt: '%b %Y' // May 2024
			},
			months: {
				default: '%b', // May
				first: '%Y', // 2024
				firstAlt: '%b %Y' // May 2024
			},
			years: {
				default: '%Y', // 2024
				first: '%Y' // 2024
			}
		}
	}
};

var defaultRowTemplateThresholds = [
	// Small charts
	{
		width: 150,
		intraday: [
			{
				timeSpan: Number.MAX_SAFE_INTEGER,
				upper: 'days',
				lower: 'months'
			}
		],
		interday: [
			{
				timeSpan: 15,
				upper: 'days',
				lower: 'months'
			},
			{
				timeSpan: 365,
				upper: 'weeks',
				lower: 'years'
			},
			{
				timeSpan: 365 * 10,
				upper: 'months',
				lower: 'years'
			},
			{
				timeSpan: Number.MAX_SAFE_INTEGER,
				upper: 'years',
				lower: null
			}
		]
	},
	// Medium charts
	{
		width: 900,
		intraday: [
			{
				timeSpan: 4,
				upper: 'minutes',
				lower: 'days'
			},
			{
				timeSpan: 24,
				upper: 'hours',
				lower: 'days'
			},
			{
				timeSpan: 60,
				upper: 'days',
				lower: 'months'
			},
			{
				timeSpan: Number.MAX_SAFE_INTEGER,
				upper: 'days',
				lower: 'months'
			}
		],
		interday: [
			{
				timeSpan: 17,
				upper: 'days',
				lower: 'months'
			},
			{
				timeSpan: 150,
				upper: 'weeks',
				lower: 'months'
			},
			{
				timeSpan: 365 * 3,
				upper: 'months',
				lower: 'years'
			},
			{
				timeSpan: Number.MAX_SAFE_INTEGER,
				upper: 'years',
				lower: null
			}
		]
	},
	// Large charts
	{
		width: Number.MAX_SAFE_INTEGER,
		intraday: [
			{
				timeSpan: 4,
				upper: 'minutes',
				lower: 'days'
			},
			{
				timeSpan: 50,
				upper: 'hours',
				lower: 'days'
			},
			{
				timeSpan: 130,
				upper: 'days',
				lower: 'months'
			},
			{
				timeSpan: Number.MAX_SAFE_INTEGER,
				upper: 'days',
				lower: 'months'
			}
		],
		interday: [
			{
				timeSpan: 30,
				upper: 'days',
				lower: 'months'
			},
			{
				timeSpan: 80,
				upper: 'weeks',
				lower: 'months'
			},
			{
				timeSpan: 365 * 5,
				upper: 'months',
				lower: 'years'
			},
			{
				timeSpan: Number.MAX_SAFE_INTEGER,
				upper: 'years',
				lower: null
			}
		]
	}
];

// Customize label templates for locale
const customLabelTemplates$1 = deepClone(defaultLabelTemplates);
customLabelTemplates$1.xAxis.doubleRow.weeks.default = '%-d/%-m';

var en_AU = {
	decimal: '.',
	thousands: ',',
	grouping: [3],
	currency: ['$', ''],
	dateTime: '%a %b %e %X %Y', // %c
	time: '%-I:%M%p', // 12-hour format
	periods: ['am', 'pm'], // %p
	date: '%-d/%-m/%Y', // %x
	days: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'], // %A
	shortDays: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'], // %a
	months: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'], // %B
	shortMonths: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'], // %b
	translations: {},
	labelTemplates: customLabelTemplates$1,
	rowTemplateThresholds: defaultRowTemplateThresholds
};

// Customize label templates for locale
const customLabelTemplates = deepClone(defaultLabelTemplates);
customLabelTemplates.xAxis.doubleRow.weeks.default = '%-d/%-m';

var en_GB = {
	decimal: '.',
	thousands: ',',
	grouping: [3],
	currency: ['$', ''],
	dateTime: '%a %b %e %X %Y', // %c
	time: '%-I:%M%p', // 12-hour format
	periods: ['am', 'pm'], // %p
	date: '%-d/%-m/%Y', // %x
	days: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'], // %A
	shortDays: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'], // %a
	months: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'], // %B
	shortMonths: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'], // %b
	translations: {},
	labelTemplates: customLabelTemplates,
	rowTemplateThresholds: defaultRowTemplateThresholds
};

var LocaleEnUs = {
	decimal: '.',
	thousands: ',',
	grouping: [3],
	currency: ['$', ''], // '$' stands for dynamic currency symbol (not Dollar). First position in array: currency symbol before value, second position: currency symbol after value
	minus: '-', // not all fonts support the unicode character "hyphen" (\u2212) or "hyphen-minus" (\u002D).
	dateTime: '%a %b %e %X %Y', // %c
	time: '%-I:%M%p', // 12-hour format
	periods: ['am', 'pm'], // %p
	date: '%-m/%-d/%Y', // %x
	days: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'], // %A
	shortDays: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'], // %a
	months: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'], // %B
	shortMonths: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'], // %b
	translations: {
		band: {
			overbought: 'Overbought',
			oversold: 'Oversold'
		},
		chart: {
			unavailable: 'Chart not available',
			unavailableIndicator: 'No data available for {0}'
		},
		indicator: {
			previousclose: {
				label: 'Previous Close {0}'
			}
		}
	},
	labelTemplates: defaultLabelTemplates,
	rowTemplateThresholds: defaultRowTemplateThresholds
};

var fr_CA = {
	decimal: '.',
	thousands: ',',
	grouping: [3],
	currency: ['$', ''],
	dateTime: '%a %b %e %X %Y', // %c
	time: '%H:%M', // 24-hour format
	periods: ['am', 'pm'], // %p
	date: '%-m/%-d/%Y', // %x
	days: ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'], // %A
	shortDays: ['dim', 'lun', 'mar', 'mer', 'jeu', 'ven', 'sam'], // %a
	months: ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'], // %B
	shortMonths: ['janv', 'fév', 'mars', 'avr', 'mai', 'juin', 'juill', 'août', 'sept', 'oct', 'nov', 'déc'], // %b
	translations: {
		band: {
			overbought: 'Sur-achat',
			oversold: 'sur-vente'
		},
		chart: {
			unavailable: 'Graphique non disponible'
		},
		indicator: {
			previousclose: {
				label: 'Prix ​​précédent {0}'
			},
			volume: {
				name: 'Volume',
				description: 'Un graphique à barres qui montre le nombre d’actions échangées pendant chaque période.'
			}
		}
	},
	labelTemplates: defaultLabelTemplates,
	rowTemplateThresholds: defaultRowTemplateThresholds
};

var it_IT = {
	decimal: '.',
	thousands: ',',
	grouping: [3],
	currency: ['$', ''],
	dateTime: '%a %b %e %X %Y', // %c
	time: '%-I:%M%p', // 12-hour format
	periods: ['am', 'pm'], // %p
	date: '%-m/%-d/%Y', // %x
	days: ['domenica', 'lunedì', 'martedì', 'mercoledì', 'giovedì', 'venerdì', 'sabato'], // %A
	shortDays: ['do.', 'lun.', 'mar.', 'mer.', 'gio.', 'ven.', 'sab.'], // %a
	months: ['gennaio', 'febbraio', 'marzo', 'aprile', 'maggio', 'giugno', 'luglio ', 'agosto', 'settembre', 'ottobre', 'novembre', 'dicembre'], // %B
	shortMonths: ['genn.', 'febbr.', 'mar.', 'apr.', 'magg.', 'giugno', 'luglio', 'ag.', 'sett.', 'ott.', 'nov', 'dic.'], // %b
	translations: {
		band: {
			overbought: 'Ipercomprato',
			oversold: 'Ipervenduto'
		},
		indicator: {
			previousclose: {
				label: 'Prezzo precedente {0}'
			},
			volume: {
				name: 'Volume',
				description: 'Un grafico a barre che mostra il numero di azioni scambiate in ogni periodo.'
			}
		}
	},
	labelTemplates: defaultLabelTemplates,
	rowTemplateThresholds: defaultRowTemplateThresholds
};

var ja_JP = {
	decimal: '.',
	thousands: ',',
	grouping: [3],
	currency: ['$', ''],
	dateTime: '%a %b %e %X %Y', // %c
	time: '%-I:%M%p', // 12-hour format
	periods: ['am', 'pm'], // %p
	date: '%Y年%-m月%-d日', // %x
	days: ['日曜日', '月曜', '火曜日', '水曜日', '木曜日', '金曜日', '土曜日'], // %A
	shortDays: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'], // %a
	months: ['1月', '2月', '3月', '4月', '5月', '6月', '7月', '8月', '9月', '10月', '11月', '12月'], // %B
	shortMonths: ['1月', '2月', '3月', '4月', '5月', '6月', '7月', '8月', '9月', '10月', '11月', '12月'], // %b
	translations: {
		band: {
			overbought: '買われすぎ',
			oversold: '売られすぎ'
		},
		chart: {
			unavailable: 'チャートを表示できません'
		},
		indicator: {
			previousclose: {
				label: '以前の価格 {0}',
				name: '前日クローズ'
			},
			volume: {
				name: 'ボリューム',
				description: '各期間中に取引される株式数を示すバーチャート。'
			}
		}
	},
	labelTemplates: defaultLabelTemplates,
	rowTemplateThresholds: defaultRowTemplateThresholds
};

/* eslint-disable camelcase */

var locales = {
	en_AU,
	en_GB,
	en_US: LocaleEnUs,
	fr_CA,
	it_IT,
	ja_JP
};

/**
 * The CallbackManager class manages event callbacks, allowing for registration,
 * deregistration, and emission of events
 */
class CallbackManager {
	constructor() {
		// Initialize an object to store callbacks. Each key (event ID) will map to an array of callback functions
		this.callbacks = {};
	}

	/**
	 * Registers a callback function for a given event ID
	 *
	 * @param {string} id - The event ID to listen for
	 * @param {function} callbackFunction - The callback function to register
	 */
	on(id, callbackFunction) {
		// If no callbacks are registered for the event ID, initialize an empty array
		if (!this.callbacks[id]) {
			this.callbacks[id] = [];
		}
		this.callbacks[id].push(callbackFunction);
	}

	/**
	 * Removes a callback function for a given event ID
	 *
	 * @param {string} id - The event ID to remove the callback from
	 * @param {function} callbackFunction - The callback function to remove
	 */
	off(id, callbackFunction) {
		// Check if any callbacks are registered for the event ID
		if (this.callbacks[id]) {
			this.callbacks[id] = this.callbacks[id].filter(cb => cb !== callbackFunction);
		}
	}

	/**
	 * Emits an event, causing all registered callbacks for this event ID to be called
	 *
	 * @param {string} id - The event ID to emit
	 * @param {...any} args - Arguments to pass to the callback functions
	 */
	emit(id, ...args) {
		if (this.callbacks[id] && this.callbacks[id].length > 0) {
			// Assuming there's only one callback per event ID
			try {
				return this.callbacks[id][0](...args);
			} catch (error) {
				// eslint-disable-next-line no-console
				return console.error(`Error in callback for ${id}:`, error);
			}
		}
	}

	/**
	 * Checks if any callbacks are registered for a given event ID
	 *
	 * @param {string} id - The event ID to check
	 * @returns {boolean} - True if callbacks are registered, false otherwise
	 */
	isRegistered(id) {
		return !!this.callbacks[id] && this.callbacks[id].length > 0;
	}
}

/* eslint-disable consistent-return */

/**
 * Handles loading and initializing charts, either from a JS object or a JSON string
 */
class ChartLoader {
	/**
	 * Creates an instance of ChartLoader
	 * @param {object} chart - The chart instance to be managed
	 */
	constructor(chart) {
		this.chart = chart;
	}

	/**
	 * Loads a chart using parameters or a saved state
	 * @param {string|object} newParams - The new parameters or a saved state string
	 * @param {Function} callback - Callback function to execute after loading
	 * @param {object} args - Additional arguments, defaults to { loadData: true }
	 * @returns The chart instance for chaining
	 */
	load(newParams, callback, args = { loadData: true }) {
		// if it's a string, it's a previously saved chart coming from the database
		// if it's a JS object, it's a new chart coming from the webcode
		const isStringInput = typeof newParams === 'string';
		const newChart = isStringInput ? JSON.parse(newParams) : newParams;

		if (!this._isValidChart(newChart)) {
			return this.chart.showMessageChartNotAvailable({
				description: 'Invalid chart inputs'
			});
		}

		this.chart.state.updateXAxisPresentScaleDomain = this._needsXAxisUpdate(newChart);

		this._prepareChartForLoading(newChart);
		this._buildPanels(newChart);

		if (args.loadData) {
			this._loadData(callback, args);
		}

		return this.chart;
	}

	/**
	 * Checks if the provided chart object is valid
	 * @param {object} chart - The chart object to validate
	 * @returns {boolean} True if the chart is valid, otherwise false
	 */
	_isValidChart(chart) {
		return !!chart?.params;
	}

	/**
	 * Determines if the X-axis needs updating based on the saved chart's parameters
	 * @param {object} newChart - The new chart object
	 * @returns {boolean} True if an update is needed, otherwise false
	 */
	_needsXAxisUpdate(newChart) {
		const params = newChart.params;
		return Object.prototype.hasOwnProperty.call(params, 'dateStart') &&
		Object.prototype.hasOwnProperty.call(params, 'dateStop');
	}

	/**
	 * Prepares the chart for loading by setting parameters, locale, and styles
	 * @param {object} newChart - The new chart object
	 */
	_prepareChartForLoading(newChart) {
		this.chart.removePanels();
		this.chart.params = this.chart.getDefaultParams(this.chart, newChart.params || {});
		this.chart.setLocale(merge({}, LocaleEnUs, this.chart.params.locale, this.chart.locale || {}));
		this.chart.styleMap = this._generateStyleMap(this.chart.params);
		this._convertDateParamsToObjects();
	}

	/**
	 * Generates a style map based on the given parameters
	 * @param {object} params - Chart parameters
	 * @returns {object} The generated style map
	 */
	_generateStyleMap(params) {
		const defaultStyles = generateDefaultStyles(params.styleCommon);
		return merge({}, defaultStyles, params.style);
	}

	/**
	 * Converts string date parameters to Date objects
	 */
	_convertDateParamsToObjects() {
		if (this.chart.params.dateStart && this.chart.params.dateStop) {
			this.chart.params.dateStart = this._convertToDate(this.chart.params.dateStart);
			this.chart.params.dateStop = this._convertToDate(this.chart.params.dateStop);
		}
	}

	/**
	 * Converts a string to a Date object, removing any unwanted characters
	 * @param {string} dateString - The date string to convert
	 * @returns {Date} The converted Date object
	 */
	_convertToDate(dateString) {
		if (typeof dateString === 'string') {
			dateString = dateString.replace(/"/g, '');
		}
		return new Date(dateString);
	}

	/**
	 * Build the panels from the new chart configuration
	 * @param {object} newChart - The new chart object
	 */
	_buildPanels(newChart) {
		this._validatePanelHeights(newChart);
		newChart.panels.forEach((savedPanel, index) => {
			const panel = this.chart.addPanel(savedPanel.params);
			if (index === 0 && this.chart.params.yAxisScale) {
				panel.setScale(this.chart.params.yAxisScale);
			}
			this._buildPanelIndicators(panel, savedPanel);
			this._buildPanelTools(panel, savedPanel);
			this._buildPanelEvents(panel, savedPanel);
		});
		this._updateChartSize();
		if (newChart.metaData) {
			this.chart.metaData = { ...newChart.metaData };
		}
		this._handleHiddenLowerPanels(newChart);
	}

	/**
	 * Validates the total height percentage of all panels
	 * Ensures the sum does not exceed 100%
	 * @param {object} newChart - The new chart object
	 */
	_validatePanelHeights(newChart) {
		let heightPctSum = 0;
		newChart.panels.forEach(p => {
			if (p.params?.size && typeof p.params.size.heightPct === 'number') {
				heightPctSum += p.params.size.heightPct;
			}
		});

		// this has happened in rare cases with saved charts where the sum is slightly greater than 1
		if (heightPctSum > 1) {
			newChart.panels.forEach(p => {
				p.params.size.heightPct = null;
			});
			this.chart.warn('Total panel height percentage is greater than 100%. Using fallback values.');
		}
	}

	/**
	 * Restores panel indicators from the saved panel data
	 * @param {object} panel - The panel to restore indicators to
	 * @param {object} savedPanel - The saved panel data
	 */
	_buildPanelIndicators(panel, savedPanel) {
		const indicators = savedPanel.indicators.map(indicator => ({ ...indicator }));
		panel.addIndicators(indicators);
	}

	/**
	 * Restores panel tools from the saved panel data
	 * @param {object} panel - The panel to restore tools to
	 * @param {object} savedPanel - The saved panel data
	 */
	_buildPanelTools(panel, savedPanel) {
		panel.tools = this.chart.getToolsFromJSON(panel, savedPanel.tools);
	}

	/**
	 * Restores panel events from the saved panel data
	 * @param {object} panel - The panel to restore events to
	 * @param {object} savedPanel - The saved panel data
	 */
	_buildPanelEvents(panel, savedPanel) {
		const events = savedPanel.events || [];
		events.forEach(event => {
			const eventParams = event.params;
			panel.addEvent(event.id || eventParams.id, eventParams || {});
		});
	}

	/**
	 * Updates the size of the chart based on the loaded panels
	 */
	_updateChartSize() {
		this.chart.setChartSize();
		if (!this.chart.params.size) {
			this.chart.rootMouse.style('width', `${this.chart.size.width}px`);
			this.chart.rootMouse.style('height', `${this.chart.size.height}px`);
		}
	}

	/**
	 * Handles hidden lower panels based on the saved chart configuration
	 * Note: currently only used on mobile.
	 * @param {object} newChart - The new chart object
	 */
	_handleHiddenLowerPanels(newChart) {
		if (newChart.params.hiddenLowers && this.chart.updateLowerPanels) {
			this.chart.updateLowerPanels(newChart.params.hiddenLowers);
		}
	}

	/**
	 * Loads data into the chart based on the provided parameters
	 * @param {Function} callback - Optional callback function to execute after data is loaded
	 */
	_loadData(callback, args = {}) {
		if (this.chart.params.customData) {
			this.chart.loadCustomData(callback, { loadSaved: true });
		} else {
			this.chart.loadData(callback, { loadSaved: true, ...args });
		}
	}
}

var name = "@markit/modcharts";
var version = "2.6.6";
var description = "HTML5 Charting Library";
var author = "Fincentric";
var license = "BSD-2-Clause";
var main = "dist/modcharts.cjs.js";
var module = "dist/modcharts.esm.js";
var browser = "dist/modcharts.esm.js";
var repository = {
	type: "git",
	url: "ssh://git@stash.mgmt.local/pd/modcharts.git"
};
var files = [
	"dist"
];
var scripts = {
	build: "rollup -c",
	postbuild: "npm run doc",
	buildIndicators: "node ./manual/buildIndicators.js",
	buildStyleReference: "node ./manual/buildStyleReference.js",
	buildStylesList: "node ./manual/buildStylesList.js",
	buildUserManualSearch: "node ./manual/buildUserManualSearch.js",
	changelog: "auto-changelog -p",
	concatMdFiles: "node ./manual/concatMdFiles.js",
	coverage: "jest --coverage --reporters=default --reporters=jest-junit",
	dev: "rollup -c -w",
	predoc: "npm run buildStyleReference && npm run buildIndicators && npm run buildStylesList && npm run concatMdFiles",
	doc: "esdoc",
	postdoc: "npm run buildUserManualSearch && cp ./static/favicon.ico ./docs",
	tags: "git tag --sort=taggerdate",
	test: "jest",
	"test:debug": "node --inspect-brk ./node_modules/jest/bin/jest.js --runInBand",
	version: "npm run changelog && git add .\\manual\\changelog.md"
};
var devDependencies = {
	"@babel/core": "^7.23.9",
	"@babel/plugin-transform-runtime": "^7.23.9",
	"@babel/preset-env": "^7.23.9",
	"@markit/eslint-config-markit": "^1.2.0",
	"@rollup/plugin-babel": "^6.0.3",
	"@rollup/plugin-commonjs": "^25.0.7",
	"@rollup/plugin-eslint": "^9.0.5",
	"@rollup/plugin-json": "^6.1.0",
	"@rollup/plugin-node-resolve": "^15.2.3",
	"auto-changelog": "^2.3.0",
	"babel-eslint": "^10.1.0",
	"concat-md": "^0.3.8",
	"core-js": "^3.35.1",
	esdoc: "^1.1.0",
	"esdoc-ecmascript-proposal-plugin": "^1.0.0",
	"esdoc-inject-style-plugin": "^1.0.0",
	"esdoc-publish-html-plugin": "^1.1.2",
	"esdoc-standard-plugin": "^1.0.0",
	eslint: "^8.6.0",
	"eslint-plugin-jest": "^25.3.4",
	jest: "^27.5.1",
	"jest-canvas-mock": "^2.2.0",
	"jest-junit": "^10.0.0",
	json5: "^2.2.0",
	mockdate: "^3.0.2",
	"regenerator-runtime": "^0.14.1",
	rimraf: "^3.0.2",
	rollup: "~2.78.0",
	"rollup-plugin-copy": "^3.3.0",
	"rollup-plugin-node-polyfills": "^0.2.1",
	"rollup-plugin-polyfill-node": "^0.13.0",
	"rollup-pluginutils": "^2.8.2",
	sinon: "^14.0.0"
};
var dependencies = {
	"@babel/runtime": "^7.23.9",
	"@markit/stream-utils": "^1.0.7",
	axios: "^0.21.1",
	"d3-array": "^2.11.0",
	"d3-format": "^2.0.0",
	"d3-scale": "^3.2.3",
	"d3-selection": "^2.0.0",
	"d3-time-format": "^3.0.0",
	"d3-zoom": "^2.0.0",
	"esdoc-inject-script-plugin": "^1.0.0",
	events: "^3.1.0",
	lodash: "^4.17.20"
};
var pkg = {
	name: name,
	version: version,
	description: description,
	author: author,
	license: license,
	main: main,
	module: module,
	browser: browser,
	repository: repository,
	files: files,
	scripts: scripts,
	devDependencies: devDependencies,
	"jest-junit": {
	outputDirectory: "./coverage"
},
	dependencies: dependencies
};

/** Grid handles chores related to rendering lines and ticks */
class Grid {
	/** @param {Panel} panel - the panel to which this grid belongs */
	constructor(panel) {
		/** @type {Panel} */
		this.panel = panel;
	}

	/**
	 * Render vertical grid lines and xaxis ticks
	 * @param {object} dateLabels
	 */
	renderVerticalGrid(dateLabels) {
		const { panel } = this;
		const ctx = panel.rootContext;
		const grid = this.getVerticalGridPositions(dateLabels);

		this.renderVerticalGridLines(ctx, grid.lines);
		this.renderVerticalAltLines(ctx, grid.altLines);
		this.renderVerticalTicks(ctx, grid.lines); // Regular ticks have the same x positions as regular lines
		this.renderVerticalAltTicks(ctx, grid.altTicks);
	}

	/**
	 * Draw regular vertical ticks
	 * @param {CanvasRenderingContext2D} ctx - Root context for this grid's parent panel
	 * @param {number[]} data - Collection of coordinates for starting points of lines
	 */
	renderVerticalTicks(ctx, data) {
		const { panel } = this;
		const { style } = panel.params;
		const y0 = panel.size.padding.top + panel.size.height;
		const { color, length, lineDash, width } = style.xAxis.tick;
		this.renderVerticalLines(ctx, data, y0, length, color, lineDash, width);
	}

	/**
	 * Draw regular vertical lines
	 * @param {CanvasRenderingContext2D} ctx - Root context for this grid's parent panel
	 * @param {number[]} data - Collection of coordinates for starting points of lines
	 */
	renderVerticalGridLines(ctx, data) {
		const { panel } = this;
		const { style } = panel.params;
		const y0 = panel.size.padding.top;
		const color =
			style.grid.vertical.color ||
			style.grid.color;
		const extension =
			style.grid.vertical.extension ||
			style.grid.extension;
		const lineDash =
			style.grid.vertical.lineDash ||
			style.grid.lineDash;
		const lineWidth =
			style.grid.vertical.width ||
			style.grid.width;
		// The extension will push the lines past the chart border
		const length = panel.size.height + extension;

		this.renderVerticalLines(ctx, data, y0, length, color, lineDash, lineWidth);
	}

	/**
	 * Draw alt vertical lines
	 * @param {CanvasRenderingContext2D} ctx - Root context for this grid's parent panel
	 * @param {number[]} data - Collection of coordinates for starting points of lines
	 */
	renderVerticalAltLines(ctx, data) {
		const { panel } = this;
		const { style } = panel.params;
		const y0 = panel.size.padding.top;
		const color =
			style.grid.vertical.alt.color ||
			style.grid.vertical.color ||
			style.grid.color;
		const extension =
			style.grid.vertical.alt.extension ||
			style.grid.vertical.extension ||
			style.grid.extension;
		const lineDash =
			style.grid.vertical.alt.lineDash ||
			style.grid.vertical.lineDash ||
			style.grid.lineDash;
		const lineWidth =
			style.grid.vertical.alt.width ||
			style.grid.vertical.width ||
			style.grid.width;
		// The extension will push the lines past the chart border
		const length = panel.size.height + extension;

		this.renderVerticalLines(ctx, data, y0, length, color, lineDash, lineWidth);
	}

	/**
	 * Draw alt vertical ticks
	 * @param {rootContext} ctx - the root context for this grid's parent panel
	 * @param {object} data - chart data
	 */
	renderVerticalAltTicks(ctx, data) {
		const { panel } = this;
		const { style } = panel.params;
		const y0 = panel.size.padding.top + panel.size.height;
		const color =
			style.xAxis.tick.alt.color ||
			style.xAxis.tick.color;
		const length =
			style.xAxis.tick.alt.length ||
			style.xAxis.tick.length;
		const lineDash =
			style.xAxis.tick.alt.lineDash ||
			style.xAxis.tick.lineDash;
		const lineWidth =
			style.xAxis.tick.alt.width ||
			style.xAxis.tick.width;

		this.renderVerticalLines(ctx, data, y0, length, color, lineDash, lineWidth);
	}

	/**
	 * Common render method for vertical lines
	 * @param {CanvasRenderingContext2D} ctx - Root context for this grid's parent panel
	 * @param {number[]} data - Collection of coordinates for starting points of lines
	 * @param {number} x0 - Location of 0 on the yAxis for this panel
	 * @param {number} length - Length of lines to render
	 * @param {string} color - Color to use for these lines
	 * @param {string[]} lineDash - Settings to turn on a dashed line
	 * @param {number} lineWidth - Line width to use for these lines
	 */
	renderVerticalLines(ctx, data, y0, length, color, lineDash, lineWidth) {
		if (
			lineWidth &&
			(
				color &&
				(
					color !== 'transparent' ||
					color !== 'none'
				)
			)
		) {
			ctx.save();
			ctx.beginPath();

			ctx.strokeStyle = color;
			ctx.lineWidth = lineWidth;
			ctx.setLineDash(lineDash.split(/\s*,\s*/));
			data.forEach(x0 => {
				ctx.moveTo(x0, y0);
				ctx.lineTo(x0, y0 + length);
			});
			ctx.stroke();

			ctx.restore();
		}
	}

	/**
	 * Return x positions of lines, alt lines, and alt ticks (which aren't always the same)
	 * @param {object} dateLabels
	 * @returns {object} Returns object with arrays of line, altLine, and altTick Xaxis locations
	 */
	getVerticalGridPositions(dateLabels) {
		const grid = {
			lines: [],
			altLines: [],
			altTicks: []
		};
		const self = this;
		const { panel } = this;
		let y = 0;
		let i = 0;
		let x0 = 0;
		let dayLabel = {};

		// collect lines, ticks, alt grid lines, and alt ticks.
		Object.keys(dateLabels).forEach(row => {
			const el = dateLabels[row];
			if (!el) return true;
			labelLoop: for (i = el.length - 1, x0 = 0; i >= 0; i--) {
				x0 = subpixel.px(panel.xAxis.primaryScale(el[i].index));

				if (row === 'months' && dateLabels.years) {
					// interday alt grid lines

					// check years for same index, if so add to alt collection and don't draw here.
					for (y = 0; y < dateLabels.years.length; y++) {
						if (dateLabels.years[y].index === el[i].index) {
							grid.altLines.push(x0);
							grid.altTicks.push(x0);
							continue labelLoop;
						}
					}
				} else if (/minutes|hours/.test(row) && dateLabels.days) {
					// intraday alt grid lines

					// check days for same index, if so add to alt collection and don't draw here.
					for (y = 0; y < dateLabels.days.length; y++) {
						dayLabel = dateLabels.days[y];

						if (el[i].index === dayLabel.index) {
							grid.altLines.push(x0);
							grid.altTicks.push(x0);
							continue labelLoop;
						} else if (
							self.hasAltLabelIntraday(el[i], el[i - 1], dayLabel)
						) {
							// use x position of the first dayLabel of each new day
							// when narrow collection's step doesn't line up with the day step.
							grid.altLines.push(
								subpixel.px(panel.xAxis.primaryScale(dayLabel.index))
							);
						}
					}
				}

				grid.lines.push(x0);
			}

			// only draw grids for the first applicable row of labels
			// (ex: months, not both months and years)
			if (el.length) return false;
		});
		// removes final alt line and tick within 25px of the right border to prevent appearance of double border lines (CH-3037)
		if (
			(grid.altLines[0] + 20) > (this.panel.size.width + this.panel.size.padding.left)
		) {
			grid.altLines.shift();
			grid.altTicks.shift();
		}

		return grid;
	}

	/**
	 * When the narrow collection's step doesn't align with the day collection's step, use an alternative
	 * way to determine if this qualifies as an alt label.  an example of this is when the narrow labels
	 * are "hours" and have a step of 2 on even-numbered hours, but the first day label is on an odd hour.
	 * @param {object} narrowLabel
	 * @param {object} prevNarrowLabel
	 * @param {object} dayLabel
	 * @returns {boolean}
	 */
	hasAltLabelIntraday(narrowLabel, prevNarrowLabel, dayLabel) {
		if (
			prevNarrowLabel &&
			dayLabel.date.getDate() === narrowLabel.date.getDate() &&
			dayLabel.date.getDate() !== prevNarrowLabel.date.getDate()
		) {
			return true;
		}

		return false;
	}

	/**
	 * Render the horizontal grid lines and optionally the horizontal normalize line
	 * @param {number[]} yTicks - Array of the yAxis tick locations
	 */
	renderHorizontalGrid(yTicks) {
		const { panel } = this;
		const { style } = panel.params;
		const ctx = panel.rootContext;

		// regular grid lines and ticks
		this.renderHorizontalGridLines(ctx, yTicks);
		this.renderYAxisTicks(ctx, yTicks);

		// optional horizontal normalization line (0%)
		if (
			panel.isUpper &&
			panel.isNormalized &&
			style.grid.horizontal.normalize.color !== null
		) {
			this.renderHorizontalNormalizationLine(ctx);
		}
	}

	/**
	 * Renders the yAxis ticks
	 * @param {CanvasRenderingContext2D} ctx - Root context for this grid's parent panel
	 * @param {number[]} data - Collection of coordinates for starting points of lines
	 */
	renderYAxisTicks(ctx, data) {
		const { panel } = this;
		const { style } = panel.params;
		const { color, length, lineDash, width } = style.yAxis.tick;
		let x0;
		if (panel.core.params.yAxisSide === 'left') {
			x0 = panel.size.padding.left - length;
		} else {
			x0 = panel.size.padding.left + panel.size.width;
		}
		this.renderHorizontalLines(ctx, data, x0, length, color, lineDash, width);
	}

	/**
	 * Renders the horizontal normalization line at the 0% mark
	 * This allows the 0% line to be styled differently than the other grid lines
	 * @param {CanvasRenderingContext2D} ctx - Root context for this grid's parent panel
	 */
	renderHorizontalNormalizationLine(ctx) {
		const { panel } = this;
		const { style } = panel.params;
		const x0 = panel.size.padding.left;
		const data = [
			panel.yAxis.primaryScale(0.0)
		];
		const color =
			style.grid.horizontal.normalize.color ||
			style.grid.horizontal.color ||
			style.grid.color;
		const extension =
			style.grid.horizontal.normalize.extension ||
			style.grid.horizontal.extension ||
			style.grid.extension;
		const lineWidth =
			style.grid.horizontal.normalize.width ||
			style.grid.horizontal.width ||
			style.grid.width;
		const lineDash =
			style.grid.horizontal.normalize.lineDash ||
			style.grid.horizontal.lineDash ||
			style.grid.lineDash;
		// The extension will push the lines past the chart border into the YAxis region
		const length = panel.size.width + extension;

		this.renderHorizontalLines(ctx, data, x0, length, color, lineDash, lineWidth);
	}

	/**
	 * Renders the horizontal grid lines
	 * @param {CanvasRenderingContext2D} ctx - Root context for this grid's parent panel
	 * @param {number[]} data - Collection of Y-coordinates for each line
	 */
	renderHorizontalGridLines(ctx, data) {
		const { panel } = this;
		const { style } = panel.params;
		const x0 = panel.size.padding.left;
		const color =
			style.grid.horizontal.color ||
			style.grid.color;
		const extension =
			style.grid.horizontal.extension ||
			style.grid.extension;
		const lineDash =
			style.grid.horizontal.lineDash ||
			style.grid.lineDash;
		const lineWidth =
			style.grid.horizontal.width ||
			style.grid.width;
		// The extension will push the lines past the chart border
		const length = panel.size.width + extension;

		this.renderHorizontalLines(ctx, data, x0, length, color, lineDash, lineWidth);
	}

	/**
	 * Common render method for horizontal lines
	 * @param {CanvasRenderingContext2D} ctx - Root context for this grid's parent panel
	 * @param {number[]} data - Collection of Y-coordinates, one per desired line
	 * @param {number} x0 - Location of left edge of the lines
	 * @param {number} length - Horizontal length of lines to render
	 * @param {string} color - Color to use for these lines
	 * @param {string[]} lineDash - Settings to turn on a dashed line
	 * @param {number} lineWidth - Line width (i.e. stroke width) to use for these lines
	 */
	renderHorizontalLines(ctx, data, x0, length, color, lineDash, lineWidth) {
		const { panel } = this;
		const yMin = 0;
		const yMax = panel.size.height;
		if (
			lineWidth &&
			(
				color &&
				(
					color !== 'transparent' ||
					color !== 'none'
				)
			)
		) {
			ctx.save();
			ctx.beginPath();

			ctx.strokeStyle = color;
			ctx.lineWidth = lineWidth;
			ctx.setLineDash(lineDash.split(/\s*,\s*/));
			data.forEach(y0 => {
				if (
					y0 >= yMin &&
					y0 < yMax
				) {
					ctx.moveTo(x0, y0);
					ctx.lineTo(x0 + length, y0);
				}
			});
			ctx.stroke();
			ctx.restore();
		}
	}
}

function createUid () {
	const uid = [];

	for (let x = 0; x < 8; x++) {
		uid.push(((Math.random() * 16) | 0).toString(16));
	}

	return uid.join('');
}

class Axis {
	/**
	 * @param {Panel} panel - A reference to the panel this Axis belongs to
	 * @param {Object} scaleParams - Optional params used to configure the primary scale
	 */
	constructor(panel = {}, scaleParams = {}) {
		/** @type {Panel} */
		this.panel = panel;
		/** @type {Modcharts} */
		this.core = panel.core;
		/** @type {Map} */
		this.scales = new Map();
		this.primaryScaleUid = createUid();
		this.scales.set(
			this.primaryScaleUid,
			this.createScale(
				scaleParams.domain,
				scaleParams.scaleType,
				scaleParams.range
			)
		);
	}

	/**
	 * Adds new scale to the axis scale Map
	 * @param {d3-scale} newScale - The new scale to add to this axis
	 * @return {string} UID for the new scale
	 */
	addScale(newScale = this.createScale()) {
		const newUid = createUid();
		this.scales.set(newUid, newScale);
		return newUid;
	}

	/**
	 * Create and return a scale per the given parameters
	 * @param {Number[]} domain - Number array containing the domain bounds for the scale
	 * @param {('linear'|'log')} scaleType - The type of scale, linear or log
     * @param {Number[]} range - Number array containing the range bounds for the scale
	 * @returns {d3-scale} The newly created scale
	 */
	createScale(domain = [0, 0], scaleType = 'linear', range = [0, 0]) {
		const scale = scaleType === 'log'
			? scalePow().exponent(0.00001)
			: scaleLinear();
		scale.domain(domain).range(range);

		return scale;
	}

	/**
	 * Gets the axis scale with the given UID
	 * @param {string} scaleUid - The UID for the scale to get
	 * @return {d3-scale} The requested scale
	 */
	getScale(scaleUid) {
		return this.scales.get(scaleUid);
	}

	set primaryScale(newScale) {
		this.scales.set(this.primaryScaleUid, newScale);
	}

	get primaryScale() {
		return this.scales.get(this.primaryScaleUid);
	}

	/** Draws axis tool on the canvas  */
	render() {
		throw new Error('Required Axis render method not found');
	}

	/**
	 * Sets the range (min and max) for the axis scale with the given UID.
	 * The YAxis starts at the max and stops at the min. The XAxis starts at the min and
	 * stops at the max.
	 * @param {number} start - Value where the range starts
	 * @param {number} stop - Value where the range stops
	 */
	setScaleRange({
		scaleUid = this.primaryScaleUid,
		start,
		stop
	}) {
		this.scales.get(scaleUid).range([start, stop]);
	}

	/**
	 * Sets the domain (start and stop) for the axis scale with the given UID.
	 * The YAxis starts at the max and stops at the min. The XAxis starts at the min and
	 * stops at the max.
	 * @param {number} start - Value where the domain starts
	 * @param {number} stop - Value where the domain stops
	 * @param {boolean} shouldSetNice - round the domain to ‘nice’ round values
	 */
	setScaleDomain({
		start,
		stop,
		scaleUid = this.primaryScaleUid,
		shouldSetNice = false
	}) {
		const scale = this.scales.get(scaleUid);
		if (shouldSetNice) scale.nice();
		scale.domain([start, stop]);
	}

}

/** A wrapper for the collection of d3 scales and related functions */
class AxisNumber extends Axis {
	/**
	 * Return y label density figure
	 * @param {object} size - panel size parameters
	 * @property {number} size.height - height of the panel
	 * @returns {number}
	 * @private
	 */
	getNumTicksY(size) {
		const { spacing, threshold } = this.panel.params.style.yAxis.label;
		return size.height < threshold.small
			? Math.ceil(size.height / spacing.small)
			: Math.ceil(size.height / spacing.default);
	}

	/**
	 * Return id of the appropriate label bucket (micro, small, default, large)
	 * @param {number} step - the step between two consecutive ticks
	 * @param {number} precisionMultiple - (example: 5) allows certain internal calls (Fibonacci Tool, Flags) to override the micro and small buckets logic
	 * @returns {string} label bucket id
	 * @private
	 */
	getFormatType(step = 100, precisionMultiple = 1, panel) {
		const { format: stepFormat } = panel.params.style.yAxis.label;
		if (step >= stepFormat.large.threshold) {
			return 'large';
		} else if (step < stepFormat.micro.threshold * precisionMultiple) {
			return 'micro';
		} else if (step < stepFormat.small.threshold * precisionMultiple) {
			return 'small';
		}

		return 'default';
	}

	/**
	 * Return a d3 format string for use in a format statement
	 * @param {d3-scale} yScale - Yaxis scale
	 * @param {number} [precisionMultiple] - optional precision multiplier. Defaults to 1
	 * @returns {string}
	 */
	getFormatString(yScale, precisionMultiple = 1, chart = this.core) {
		const { panel } = this;
		const { size } = panel;
		const domain = yScale.domain();
		const yTicks = yScale.ticks(this.getNumTicksY(size, chart)).reverse();
		let step;
		let formatString;

		// get the step between two consecutive ticks
		if (yTicks.length > 1) {
			step = yTicks[1] - yTicks[0];
		} else if (yTicks.length && yTicks[0] !== 0) {
			step = yTicks[0];
		} else {
			step = Math.abs(domain[1] - domain[0]);
		}
		const formatType = this.getFormatType(step, precisionMultiple, panel);
		const yLabelFormat = panel.params.style.yAxis.label.format[formatType];
		if (panel.params.yAxis.format === 'percent' || panel.isNormalized) {
			// percent-change formatting
			formatString = yLabelFormat.formatPercent;
		} else {
			// default formatting
			formatString = yLabelFormat.format;
		}

		// remove decimal places on large steps and values
		if (step > 2 && yTicks.length && yTicks[yTicks.length - 1] >= 1000 && formatType !== 'large') {
			formatString = formatString.replace(/\.\d/, '.0');
		}

		return formatString;
	}

	/**
	 * Formats the value per the format string passed in
	 * @param {number} val - Number to format
	 * @param {string} formatString - The d3-format specifier that will be used to format the number
	 * @param {boolean} isNormalized - Whether or not chart data is normalized
	 * @returns {string}
	 */
	getFormatValue(val, formatString = '', isNormalized = this.panel.isNormalized) {
		const { panel } = this;

		// Divide by 100 because the % modifier will multiply by 100 in d3-format
		if (
			panel.params.yAxis.format === 'percent' ||
			(/%$/.test(formatString) && isNormalized) // Don't change val if no % modifier is present
		) {
			val /= 100;
		}

		const valOut = formatLocale(this.core.locale).format(formatString)(val);

		// Use B for billion instead of G for giga
		return valOut.replace(/G/, 'B');
	}

	/**
	 * Calculate the labels that will be rendered and their positions.
	 * @param {d3-scale} yScale - Yaxis scale
	 * @param {object} chart - The chart object
	 * @returns {Array} - Array of objects with labels and their positions
	 */
	calculateRenderedLabels(yScale = this.primaryScale, chart = this.core) {
		const { panel } = this;
		const { size } = panel;
		const { style } = panel.params;
		const numTicksY = this.getNumTicksY(size, chart);
		const yScaleTicks = yScale.ticks(numTicksY).reverse();
		const yFormatString = this.getFormatString(yScale, 1, chart);
		const labels = [];
		const halfFontHeight = Math.ceil(style.yAxis.font.size / 2) + 2;
		let lastY = null;

		yScaleTicks.forEach((yTick, i) => {
			const y0 = subpixel.px(yScale(yTick));
			let yWithPadding;

			// Adjust y0 for text baseline setting
			switch (style.yAxis.label.textBaseline) {
				case canvasEnums.textBaseline.ALPHABETIC:
					yWithPadding = y0 - style.yAxis.label.padding.bottom;
					break;
				case canvasEnums.textBaseline.HANGING:
					yWithPadding = y0 + style.yAxis.label.padding.top;
					break;
				case canvasEnums.textBaseline.MIDDLE:
				default:
					yWithPadding = y0;
			}

			const bottomCollide = i === 0 && y0 > size.height - halfFontHeight;
			const topCollide = i === yScaleTicks.length - 1 && y0 < halfFontHeight;

			if (!bottomCollide && !topCollide) {
				if (!lastY || y0 + halfFontHeight < lastY) {
					const label = this.getFormatValue(yTick, yFormatString);
					labels.push({
						label,
						y0: yWithPadding
					});
					lastY = yWithPadding;
				}
			}
		});
		return labels;
	}

	/**
	 * Calculate label left position based on yAxisSide and padding
	 * @param {string} yAxisSide - The side of the Y-axis ('left' or 'right')
	 * @param {object} size - The size of the panel
	 * @param {object} style - The style parameters
	 * @returns {number} - The calculated x0 value
	 */
	getLabelXPosition(yAxisSide, size, style) {
		if (yAxisSide === 'left') {
			return size.padding.left - style.yAxis.label.padding.left;
		} else {
			return size.padding.left + size.width + style.yAxis.label.padding.left;
		}
	}

	/**
	 * Render the Y-axis
	 * @param {Array} labels - Array of precomputed Y-axis labels with positions
	 * @param {d3-scale} yScale - Yaxis scale
	 * @param {object} chart - The chart object
	 */
	render(chart = this.core, labels = this.calculateRenderedLabels(this.primaryScale)) {
		const { panel } = this;
		const { size } = panel;
		const { style } = panel.params;
		const ctx = panel.rootContext;
		this._renderedLabels = [];
		const x0 = this.getLabelXPosition(chart.params.yAxisSide, size, style);
		const yTicks = [];

		// Prepare context for labels
		ctx.beginPath();
		ctx.textAlign = style.yAxis.label.textAlign;
		ctx.textBaseline = style.yAxis.label.textBaseline;
		ctx.fillStyle = style.yAxis.label.color;
		ctx.font = panel.getFontStyle(
			style.yAxis.font.weight,
			style.yAxis.font.size,
			style.yAxis.font.family,
			style.yAxis.font.style
		);

		// Render Y labels
		labels.forEach(({ label, y0 }) => {
			ctx.fillText(label, x0, y0);
			yTicks.push(y0);
			this._renderedLabels.push(label); // Store for testing
		});

		// Draw horizontal grid lines
		panel.grid.renderHorizontalGrid(yTicks);

		// Draw vertical YAxis label
		if (chart.params.showVerticalPanelLegend) {
			this._renderVerticalLegend(ctx, style);
		}
	}

	/**
	 * Draws a vertical YAxis label if it fits
	 * @param {CanvasRenderingContext2D} ctx - The canvas context
	 */
	_renderVerticalLegend(ctx) {
		const { panel, core } = this;
		const { style } = panel.params;
		const { callbackManager } = core;
		const isLegendCallbackRegistered = callbackManager.isRegistered('PANEL_LEGEND_VERTICAL_LABEL');
		const legendText = isLegendCallbackRegistered
			? callbackManager.emit('PANEL_LEGEND_VERTICAL_LABEL', {
				indicatorParams: panel.indicators[0].params,
				primarySymbol: core.xref[core.params.symbol]
			})
			: panel.indicators[0].params.name;

		// Determine offsetX (distance from right edge of panel)
		const defaultFontSize = style.yAxis.label.vertical.right.font.size;
		const offsetX = style.yAxis.label.vertical.right.offsetX || defaultFontSize;
		const availableVerticalSpace = panel.size.height;

		// Set up canvas context for label
		this._setupVerticalLabelContext(ctx, style.yAxis.label.vertical.right);

		// Check if the label fits
		if (this._doesVerticalLabelFit(ctx, legendText, availableVerticalSpace)) {
			const legendX = panel.size.padding.left + panel.size.width + panel.size.padding.right - offsetX;
			const legendY = panel.size.height / 2; // Center vertically

			// Draw the label
			this._drawVerticalLabel(ctx, legendText, legendX, legendY);
		}
	}

	/**
	 * Set up the canvas context for drawing labels
	 * @param {CanvasRenderingContext2D} ctx - The canvas context
	 * @param {Object} style - Style settings for the label
	 */
	_setupVerticalLabelContext(ctx, style) {
		ctx.save(); // Save the current state
		ctx.fillStyle = style.color;
		ctx.font = this.panel.getFontStyle(style.font.weight, style.font.size, style.font.family, style.font.style);
		ctx.textAlign = 'left';
		ctx.textBaseline = 'middle';
	}

	/**
	 * Draws the label on the canvas
	 * @param {CanvasRenderingContext2D} ctx - The canvas context
	 * @param {string} text - The text of the label
	 * @param {number} x - X position for the label
	 * @param {number} y - Y position for the label
	 */
	_drawVerticalLabel(ctx, text, x, y) {
		const textWidth = ctx.measureText(text).width;
		ctx.translate(x, y);
		ctx.rotate(Math.PI / 2); // Rotate 90 degrees
		ctx.fillText(text, -textWidth / 2, 0); // Adjust position
		ctx.restore();
	}

	/**
	 * Checks if the label fits within the available space
	 * @param {CanvasRenderingContext2D} ctx - The canvas context
	 * @param {string} text - The text of the label
	 * @param {number} availableSpace - The available space to fit the label
	 * @returns {boolean} - True if the label fits, false otherwise
	 */
	_doesVerticalLabelFit(ctx, text, availableSpace) {
		const textWidth = ctx.measureText(text).width;
		return textWidth <= availableSpace;
	}
}

/* eslint-disable no-self-assign */

const { labelPriorities } = labelerEnums;

/** Performs these actions on a ruler: create labels, align labels, position labels, render labels */
class Labeler {
	constructor(axis, core) {
		this.axis = axis;
		this.core = core;
		this.labels = {};
		this.rowTemplate = {};
	}

	/** Returns a formatted string for a given date */
	format(date, labelType, formatType = 'default') {
		const { core } = this;
		let { labelTemplates } = core.params.locale;

		if (!labelTemplates) {
			// labelTemplates could be missing if loading a saved chart from before this feature was added
			core.warn("'labelTemplates' not found in locale. Using 'defaultLabelTemplates' instead.");
			core.params.locale.labelTemplates = defaultLabelTemplates;
			labelTemplates = defaultLabelTemplates;
		}

		// Determine the format key based on single-row mode and intraday state
		const isSingleRowMode = this.core.params.xAxisSingleRowMode;
		const isIntraday = core.state.isIntraday;
		const formatKey = (isSingleRowMode && isIntraday) ? 'singleRow' : 'doubleRow';

		// Ensure the labelType exists in the selected format key
		let labelTypeTemplates = labelTemplates.xAxis[formatKey][labelType];
		if (!labelTypeTemplates) {
			// core.warn(`'${labelType}' not found in '${formatKey}'. Using 'doubleRow' instead.`);
			labelTypeTemplates = labelTemplates.xAxis.doubleRow[labelType];
		}

		// Ensure the formatType exists within the labelTypeTemplates
		const dateFormat = labelTypeTemplates[formatType];
		if (!dateFormat) {
			// core.warn(`'${formatType}' not found in '${labelType}' for '${formatKey}'. Using 'default' instead.`);
			return core.timeFormat(labelTypeTemplates.default)(date);
		}

		return core.timeFormat(dateFormat)(date);
	}

	/**
	 * Returns a dynamic step based on the distance between labels of a certain type
	 * (weekly, monthly, etc.). This works by iterating through available label steps
	 * until the distance provided exceeds the spacing value. For example: Say we
	 * know the distance between the January and February labels is 48.5px, and we've
	 * defined a spacing value of 50 for "months" labels. It will begin by looking at
	 * the array of steps for months ([1, 2, 3, 4, 6]).  Since 48.5 is less than 50/1
	 * it will move to the next step and repeat.  48.5 is not less than 50/2, so it
	 * will stop iterating there and return a step of 2.
	 */
	_step(dist, spacing, steps) {
		return steps.find(step => dist >= spacing / step) || 1;
	}

	/**
	 * Generate a full collection of fresh labels for the entire XAxis. Each label
	 * will contain information about itself: its date, widths, formatted strings
	 * and priority. Only a subset of these labels will be chosen during XAxis
	 * renders, e.g. if the entire array has 8500 "days" labels, a given render
	 * may only be processing 19 labels. This method typically needs to be called on
	 * first load, or if the locale language was changed, or if switching between
	 * interday/intraday.
	 */
	_createLabels() {
		const { core } = this;
		const { exchangeDates } = core;
		const years = [];
		const months = [];
		const weeks = [];
		const days = [];
		const hours = [];
		const minutes = [];
		const isIntraday =
				typeof core.state.isIntraday === 'boolean'
					? core.state.isIntraday
					: core.params.days <= core.limitIntradayTrading;
		const denseLabelLimitLeft = new Date().setDate(
			new Date().getDate() - core.limitIntradayTrading - 10
		);
		const denseLabelLimitRight = new Date().setDate(new Date().getDate() + 4);
		let lastYear;
		let lastMonth;
		let lastWeek;
		let lastDay;
		let lastHour;
		let lastMin;
		let date;
		let dateLabel;
		let year;
		let month;
		let week;
		let day;
		let hour;
		let min;

		for (let i = 0; i < exchangeDates?.length; i++) {
			date = new Date(exchangeDates[i]);
			dateLabel = new Date(date);

			year = date.getFullYear();
			month = date.getMonth();
			week = this.getWeeks(date);
			day = date.getDate();
			hour = date.getHours();
			min = date.getMinutes();

			/**
			 * 'first' labels represent what will be displayed in the bottom row if the
			 * given label is in the first position in the top row (furthest to the left).
			 * This generally should match the default format of the next bigger timeframe.
			 * 'firstAlt' labels are used when additional information needs to be added
			 * to the label to specify the time period (eg. adding the year to January
			 * labels when the bottom row shows month names).
			 */
			if (year !== lastYear) {
				years.push({
					index: i,
					date,
					format: {
						default: this.format(date, 'years'),
						first: this.format(date, 'years', 'first')
					},
					stepCompare: date.getFullYear(),
					priority: labelPriorities.LOW
				});

				lastYear = year;
			}

			if (month !== lastMonth) {
				months.push({
					index: i,
					date,
					format: {
						default: this.format(date, 'months'),
						first: this.format(date, 'months', 'first'),
						firstAlt: this.format(date, 'months', 'firstAlt')
					},
					stepCompare: date.getMonth(),
					pairedFirst: 'years',
					priority: labelPriorities.LOW
				});

				lastMonth = month;
			}

			if (week !== lastWeek) {
				weeks.push({
					index: i,
					date,
					format: {
						default: this.format(date, 'weeks'),
						first: this.format(date, 'weeks', 'first'),
						firstAlt: this.format(date, 'weeks', 'firstAlt')
					},
					stepCompare: this.getWeeks(date),
					pairedFirst: 'months',
					priority: labelPriorities.LOW
				});

				lastWeek = week;
			}

			if (day !== lastDay) {
				days.push({
					index: i,
					date,
					format: {
						default: this.format(date, 'days'),
						first: this.format(date, 'days', 'first'),
						firstAlt: this.format(date, 'days', 'firstAlt'),
						intraday: this.format(date, 'days', 'intraday')
					},
					stepCompare: date.getDate(),
					pairedFirst: 'months',
					priority: labelPriorities.LOW
				});

				lastDay = day;
			}

			if (isIntraday) {
				const minutesFromStartOfDay = date.getHours() * 60 + date.getMinutes();

				// Get default market close/open times
				let previousClose = core.state.typicalSessions
					? core.state.typicalSessions.max
					: null;
				let currentOpen = core.state.typicalSessions
					? core.state.typicalSessions.min
					: null;

				// Get date from previous element or use today's date
				let previousTradingDate = new Date();
				if (i > 0) previousTradingDate = new Date(exchangeDates[i - 1]);

				// Function to get alternate session data, if the dates match
				const checkForAlternateSession = dateToCheck => {
					// Get the dates (which are the keys)
					const alternateSessionDates = Object.keys(core.timeService.alternateSessions);

					// Look for a matching date
					const matchingDate = alternateSessionDates.find(dateString => {
						const dateMatch = new Date(dateString);

						return (
							dateToCheck.getYear() === dateMatch.getYear() &&
							dateToCheck.getMonth() === dateMatch.getMonth() &&
							dateToCheck.getDate() === dateMatch.getDate()
						);
					});

					// Look for a matching exchange ID
					return (
						matchingDate &&
						core.timeService.alternateSessions[matchingDate]
							.find(session => session.exchangeId === core.xref[core.params.symbol].exchangeId)
					);
				};

				// Check for alternate sessions associated with PREVIOUS trading date
				if (
					core.timeService.alternateSessions && // Are there alternate sessions?
					checkForAlternateSession(previousTradingDate)
				) {
					// Get the open/close info for that session
					const session = checkForAlternateSession(previousTradingDate);
					previousClose = session.close;
				}

				// Check for alternate sessions associated with CURRENT trading date
				if (
					core.timeService.alternateSessions &&
					checkForAlternateSession(date)
				) {
					// Get the open/close info for that session
					const session = checkForAlternateSession(date);
					currentOpen = session.open;
				}

				// Check if time from current data point is for market open
				const isMarketOpen = currentOpen === minutesFromStartOfDay;

				/**
				 * If session start/end data exists, add market close label BEFORE next market
				 * open label (if flag is enabled)
				 * */
				if (
					core.params.showMarketCloseLabels &&
					isMarketOpen
				) {
					const marketClose = {
						hours: Math.floor(previousClose / 60),
						minutes: previousClose % 60
					};

					/**
					 * CH-1513
					 * If market close label is for current date, it gets a higher priority such that it draws
					 * instead of the (future) market open label. We have to look at the next day because the
					 * market close label lives at the same time as the market open label, which in this case
					 * is on the next day.
					*/
					const datePriorityCheck = new Date((new Date()).setDate(new Date().getDate() + 1));

					// Set date to use market close time
					previousTradingDate.setHours(marketClose.hours);
					previousTradingDate.setMinutes(marketClose.minutes);

					hours.push({
						index: i,
						date,
						format: {
							default: this.format(previousTradingDate, 'hours'),
							// Should this be date instead of previousTradingDate?
							first: this.format(date, 'hours', 'first')
						},
						stepCompare: date.getHours(),
						pairedFirst: 'days',
						// Use toLocaleDateString to avoid time check
						priority: date.toLocaleDateString() === datePriorityCheck.toLocaleDateString()
							? labelPriorities.HIGHEST
							: labelPriorities.MARKET_CLOSE,
						isMarketClose: true
					});

					minutes.push({
						index: i,
						date,
						format: {
							default: this.format(previousTradingDate, 'minutes'),
							// Should this be date instead of previousTradingDate?
							first: this.format(date, 'minutes', 'first')
						},
						stepCompare: date.getMinutes(),
						pairedFirst: 'days',
						// Use toLocaleDateString to avoid time check
						priority: date.toLocaleDateString() === datePriorityCheck.toLocaleDateString()
							? labelPriorities.HIGHEST
							: labelPriorities.MARKET_CLOSE,
						isMarketClose: true
					});
				}

				// If within dense label limits, add hour and minute
				if (
					date > denseLabelLimitLeft &&
					date < denseLabelLimitRight
				) {
					// Check time to add correct priority
					const priority = isMarketOpen
						? labelPriorities.MARKET_OPEN
						: date.getHours() === 12 && date.getMinutes() === 0
							? labelPriorities.MIDDAY
							: labelPriorities.LOW;

					if (hour !== lastHour) {
						hours.push({
							index: i,
							date,
							format: {
								default: this.format(dateLabel, 'hours'),
								first: this.format(dateLabel, 'hours', 'first')
							},
							stepCompare: date.getHours(),
							pairedFirst: 'days',
							priority,
							isMarketOpen
						});

						lastHour = hour;
					}

					if (min !== lastMin) {
						minutes.push({
							index: i,
							date,
							format: {
								default: this.format(dateLabel, 'minutes'),
								first: this.format(dateLabel, 'minutes', 'first')
							},
							stepCompare: date.getMinutes(),
							pairedFirst: 'days',
							priority,
							isMarketOpen
						});

						lastMin = min;
					}
				}
			}
		}

		this.labels = this.measureLabels({
			minutes,
			hours,
			days,
			weeks,
			months,
			years
		});
	}

	/**
	 * Measures and stores label widths for future collision detection logic
	 * @param {object} labels - Label object to use in calculations
	 * @return {object} Label object with measurements added
	 * */
	measureLabels(labels) {
		const { panel } = this.axis;
		const ctx = panel.rootContext;
		const { style } = panel.params;

		// before measuring label dimensions, set to axis label style
		ctx.font = panel.getFontStyle(
			style.xAxis.font.weight,
			style.xAxis.font.size,
			style.xAxis.font.family
		);
		Object.keys(labels).forEach(label => {
			const vals = labels[label];
			vals.forEach(value => {
				value.width = {};
				const { format, width } = value;

				Object.keys(format).forEach(formatType => {
					const formatValue = format[formatType];
					width[formatType] = ctx.measureText(formatValue).width;
				});
			});
		});

		return labels;
	}

	/**
	 * Returns the number of non-weekend days between two dates
	 * @param {Date} startDate
	 * @param {Date} endDate
	 * @return {number}
	 */
	getWeekdays(startDate, endDate) {
		let weekDay;
		let num = 0;
		const currentDate = new Date(startDate);

		while (currentDate <= endDate) {
			weekDay = currentDate.getDay();

			if (weekDay !== 0 && weekDay !== 6) {
				num++;
			}

			currentDate.setDate(currentDate.getDate() + 1);
		}

		return num;
	}

	/**
	 * Returns the numeric week of the year
	 * @param {Date} date - UTC date
	 * @return {number}
	 */
	getWeeks(date) {
		const jan = new Date(date.getFullYear(), 0, 1);
		return Math.ceil(((date - jan) / 86400000 + jan.getDay() + 1) / 7);
	}

	/**
	 * given current xScale domain, trim labels collection to just the appropriate labels
	 * @return {object}
	 */
	filterLabels() {
		const self = this;
		const { panel } = this.axis;
		const { core } = this;
		const ruler = core.exchangeDates;
		const domain = this.axis.primaryScale.domain();
		const domainLeft = Math.max(0, Math.floor(domain[0]));
		const domainRight = Math.min(ruler.length - 1, Math.floor(domain[1]));
		let i;
		let dist;
		let lastX;
		let rowLabel;
		const labels = {};
		let idxLeft;
		let idxRight;
		let step;
		let newLabels;
		const loopLabels = {};
		let count;
		let amt;
		const style = panel.params.style.xAxis;

		this.rowTemplate = this._getRowTemplate(domainLeft, domainRight);

		loopLabels[this.rowTemplate.upper] = self.labels[this.rowTemplate.upper];

		if (this.rowTemplate.lower) {
			loopLabels[this.rowTemplate.lower] = self.labels[this.rowTemplate.lower];
		}

		// trim all labels down to what's in current range
		Object.keys(loopLabels).forEach(rowId => {
			const rowLabels = loopLabels[rowId];
			if (!rowLabels || !rowLabels.length) return;

			const labelSteps = style.label.steps[rowId];
			const labelSpacing = style.label.spacing[rowId] || style.label.spacing.default;

			idxLeft = Math.max(
				0,
				core._getIndexByDomain(domainLeft, rowLabels) - 1
			);
			idxRight = Math.min(
				rowLabels.length - 1,
				core._getIndexByDomain(domainRight, rowLabels) + 1
			);

			labels[rowId] = rowLabels.slice(idxLeft, idxRight + 1);

			// calculate x positions of all rows and max dist
			count = 0;
			amt = 0;

			lastX = false;

			// loop backward so we can splice
			for (i = labels[rowId].length - 1; i >= 0; i--) {
				rowLabel = labels[rowId][i];
				rowLabel.x0 = panel.xAxis.primaryScale(rowLabel.index);

				// If label is market close, add negative subpixel offset
				if (
					rowLabel.isMarketClose &&
					rowLabel.x0 >= panel.size.padding.left + panel.size.width // Check right chart edge
				) {
					rowLabel.x0 = subpixel.px(rowLabel.x0, false);
				}

				// trim if outside of chart boundaries
				if (
					rowLabel.x0 < panel.size.padding.left || // Check left chart edge
					rowLabel.x0 > panel.size.padding.left + panel.size.width // Check right chart edge
				) {
					labels[rowId].splice(i, 1);
				} else if (
					lastX &&
					// Don't include label 0 since its dist is often much shorter and throws off the step calculation
					i > 0 &&
					// Don't add market close labels (because they're a special case and could throw off the calculation)
					!rowLabel.isMarketClose
				) {
					amt += lastX - rowLabel.x0;
					count++;
				}

				lastX = rowLabel.x0;
			}

			// find average distance between all distances deemed valid
			dist = amt / count;

			// trim out row if dist is too small
			if (dist < 0.1) {
				labels[rowId] = null;
				return true;
			}

			// now figure out a good step
			step = self._step(dist, labelSpacing, labelSteps);

			// Trim vals in each row that don't match the step, unless they're high priority
			newLabels = labels[rowId].filter(row => {
				return (
					row.priority <= labelPriorities.HIGH || // Don't filter out high priority labels
					row.stepCompare % step === 0
				);
			});

			if (rowId === 'hours') {
				newLabels.forEach(row => {
					row.step = step;
				});
			}

			labels[rowId] = newLabels;

			// other removals

			// trim out years if we have days and months
			if (
				rowId === 'years' &&
				labels.days &&
				labels.days.length &&
				labels.months &&
				labels.months.length
			) {
				labels.years = null;
				return true;
			}

			// trim out hours if we have days and minutes
			if (rowId === 'hours' && labels.minutes && labels.minutes.length) {
				labels.hours = null;
				return true;
			}

			// trim out weeks if we have days
			if (rowId === 'weeks' && labels.days && labels.days.length) {
				labels.weeks = null;
				return true;
			}
		});

		return labels;
	}

	/**
	 * Determines the appropriate upper/lower xAxis row template id's.
	 * Example: an interday chart may return "months" and "years", but an intraday chart may return
	 * "hours" and "days". Depending on the width of the chart and other factors, different templates
	 * will be returned.
	 * @param {number} rulerLeft - The index for the left side of the xAxis domain
	 * @param {number} rulerRight - The index for the right side of the xAxis domain
	 * @returns {object} The selected row template
	 */
	_getRowTemplate(rulerLeft, rulerRight) {
		const { core } = this;
		const { width } = this.axis.panel.size;

		let { rowTemplateThresholds } = core.params.locale;

		// rowTemplateThresholds could be missing or malformed if loading a saved chart from before this feature was added
		if (!rowTemplateThresholds || rowTemplateThresholds.find(row => row.width === null)) {
			core.warn("'rowTemplateThresholds' not found in locale. Using 'defaultRowTemplateThresholds' instead.");
			core.params.locale.rowTemplateThresholds = defaultRowTemplateThresholds;
			rowTemplateThresholds = defaultRowTemplateThresholds;
		}

		// Determine if the chart is intraday or interday
		const isIntraday = core.state.isIntraday;

		// Check if the required thresholds exist for the determined chart type
		const hasIntraday = rowTemplateThresholds[0].intraday;
		const hasInterday = rowTemplateThresholds[0].interday;

		const chartType = isIntraday && hasIntraday ? 'intraday' : 'interday';

		if (isIntraday && !hasIntraday) {
			// If intraday is missing and the chart is intraday, warn and fallback to a default
			core.warn('"intraday" thresholds not found, applying default intraday behavior.');
			return { lower: 'days', upper: 'hours' }; // Adjust based on your desired default intraday labels
		}

		if (!isIntraday && !hasInterday) {
			// If interday is missing and it's not intraday, warn and fallback to a default
			core.warn('"interday" thresholds not found, applying default interday behavior.');
			return { lower: null, upper: 'years' }; // Adjust based on your desired default interday labels
		}

		// Figure out what time span comparison should be (value in hours for intraday or days for interday)
		const ruler = core.exchangeDates;
		const timeSpan = chartType === 'intraday'
			? (rulerRight - rulerLeft) / 60 // Intraday: time span in hours
			: core.params.customData
				? Math.ceil(((ruler[rulerRight] - ruler[rulerLeft]) / (1000 * 60 * 60 * 24)) * 0.69) // Interday: time span in days
				: rulerRight - rulerLeft;

		// Find correct row template for the given chart type and time span
		const thresholds = rowTemplateThresholds.find(widthThreshold => width <= widthThreshold.width);

		if (!thresholds[chartType]) {
			// If the chart type thresholds are not found, provide a warning and fallback to a default
			core.warn(`"${chartType}" thresholds not found, applying default behavior.`);
			return { lower: null, upper: 'years' }; // Default for interday
		}

		const selectedThreshold = thresholds[chartType].find(
			timeSpanThreshold => timeSpan <= timeSpanThreshold.timeSpan
		);

		if (!selectedThreshold) {
			core.warn(
				`No matching timeSpan threshold found for timeSpan ${timeSpan}. Applying default behavior.`
			);
			return { lower: null, upper: 'years' }; // Default for interday
		}

		const { lower, upper } = selectedThreshold;
		return { lower, upper };
	}

	/**
	 * Renders each row (years, months, days, etc.) of labels on the X-axis.
	 *
	 * This method iterates through the provided labels, applying specific rendering rules
	 * based on whether the chart is intraday or interday. It handles right-alignment for the
	 * final visible label, ensures labels don't overlap, and applies optional styling for the
	 * bottom row. The method also manages label rendering based on whether they fall within
	 * the visible boundaries of the X-axis.
	 *
	 * Key functionalities:
	 * - Handles both intraday and interday charts differently for final label alignment.
	 * - Allows overlap of labels up to a specified limit (`overlap.right`).
	 * - Applies different styles for the bottom row of labels if specified.
	 * - Ensures labels are only drawn if they do not collide with neighboring labels or exceed boundaries.
	 *
	 * @param {Object} labels - An object containing the labels to be rendered, grouped by their respective rows (e.g., years, months).
	 */
	render(labels) {
		const { panel } = this.axis;
		const ctx = panel.rootContext;
		const { style } = panel.params;
		const labelsRendered = {};

		// Calculate the starting Y position for labels and set padding values
		const y0 = panel.size.height + panel.size.padding.top + (style.xAxis.label.padding.top || 0);
		const paddingLeft = style.xAxis.label.padding.left;
		const paddingRight = style.xAxis.label.padding.right;

		// Set the left and right limits based on the X-axis padding and width
		const limitLeft = panel.size.padding.left;
		const limitRight = panel.size.padding.left + panel.size.width;

		let label = {};
		let rowNum = 0; // Row counter for determining label positions vertically
		let newY = 0; // Y-coordinate for label rendering
		let i = 0; // General loop counter
		let left = 0; // Left boundary of the current label
		let right = 0; // Right boundary of the current label

		// Prepare the canvas for drawing
		ctx.beginPath();
		ctx.textBaseline = 'top';

		// Apply any specific rendering rules to the labels before starting the drawing process
		labels = this.applyRenderLabelRules(labels);

		// Set the initial text color and font style based on the configuration
		ctx.fillStyle = style.xAxis.label.color;
		ctx.font = panel.getFontStyle(
			style.xAxis.font.weight,
			style.xAxis.font.size,
			style.xAxis.font.family
		);

		const isIntraday = panel.core.state.isIntraday; // Determine if the chart is intraday
		let finalVisibleLabelIndex = -1; // Index of the last visible label on the X-axis

		for (const row in labels) {
			const rowLabels = labels[row];

			if (!rowLabels) continue;

			let previousLeft = limitRight; // Initialize the left boundary of the previous label

			// Apply different styles for the bottom row of labels if this is not the first row
			if (rowNum > 0 && style.xAxis.bottomRow) {
				ctx.fillStyle = style.xAxis.bottomRow.label.color || style.xAxis.label.color;
				ctx.font = panel.getFontStyle(
					style.xAxis.bottomRow.font.weight || style.xAxis.font.weight,
					style.xAxis.bottomRow.font.size || style.xAxis.font.size,
					style.xAxis.bottomRow.font.family || style.xAxis.font.family
				);
			}

			// Identify the final visible label within the right limit, ensuring it belongs to the current session
			for (i = rowLabels.length - 1; i >= 0; i--) {
				if (rowLabels[i].x0 <= limitRight) {
					if (isIntraday && rowLabels[i].isMarketClose) {
						finalVisibleLabelIndex = i;
						break;
					} else if (!isIntraday) {
						finalVisibleLabelIndex = i;
						break;
					}
				}
			}

			for (i = rowLabels.length - 1; i >= 0; i--) {
				label = rowLabels[i];
				let labelCoordX;
				let rightCollide = false; // Flag to check if the label collides with its right-hand neighbor
				const labelFormat = label.format;
				let textAlign = style.xAxis.label.textAlign || canvasEnums.textAlign.LEFT;

				const overlapRight = style.xAxis.label.overlap.right; // Pixel value
				if (
					isIntraday &&
					this.core.params.showMarketCloseLabels === 'once' &&
					i === finalVisibleLabelIndex
				) {
					// Right-align just the final market close label
					if ((label.x0 + label.width.default - limitRight) <= overlapRight) {
						// Allow the label to overlap slightly beyond the X-axis boundary
						labelCoordX = label.x0;
					} else {
						// Right-align just the final market close label if overlap is too large
						textAlign = canvasEnums.textAlign.RIGHT;
						labelCoordX = label.x0 - paddingRight;
					}
				} else if (
					isIntraday &&
					this.core.params.showMarketCloseLabels === 'all' &&
					label.isMarketClose
				) {
					// Right-align ALL market close labels
					textAlign = canvasEnums.textAlign.RIGHT;
					labelCoordX = label.x0 - paddingRight;
				} else if (
					!isIntraday &&
					i === finalVisibleLabelIndex &&
					label.x0 + label.width.default > limitRight
				) {
					// this section pertains to the final visible label on interday charts
					const previousLabel = rowLabels[i - 1];

					if (this.core.params.enableRightAlignFinalLabel) {
						if ((label.x0 + label.width.default - limitRight) <= overlapRight) {
							// Allow the label to overlap slightly beyond the X-axis boundary
							labelCoordX = label.x0;
						} else if (
							!previousLabel ||
								(previousLabel &&
								(previousLabel.x0 + previousLabel.width.default + paddingLeft) <
								(limitRight - paddingRight - label.width.default))
						) {
							// Right-align the label if the overlap is too large and it won't collide with the left-hand label
							textAlign = canvasEnums.textAlign.RIGHT;
							labelCoordX = label.x0;
						} else {
							// Skip rendering if it would collide with the left-hand label
							continue;
						}
					} else if ((label.x0 + label.width.default - limitRight) <= overlapRight) {
						// Allow the label to overlap slightly beyond the X-axis boundary
						labelCoordX = label.x0;
					} else {
						// Skip rendering if it exceeds the right limit and no special logic applies
						continue;
					}
				} else if (label.x0 + label.width.default > limitRight) {
					// Skip rendering if the label exceeds the X-axis width and no special logic applies
					continue;
				} else {
					// Default case: keep the label's original X position
					labelCoordX = label.x0;
				}

				ctx.textAlign = textAlign;

				// Determine the left and right edges of the label based on its alignment
				switch (textAlign) {
					case canvasEnums.textAlign.RIGHT:
						right = labelCoordX;
						left = right - label.width.default;
						break;
					case canvasEnums.textAlign.CENTER:
						left = label.x0 - (label.width.default / 2);
						right = left + label.width.default;
						labelCoordX = label.x0;
						break;
					case canvasEnums.textAlign.LEFT:
					default:
						left = label.x0 + paddingLeft;
						right = left + label.width.default;
						labelCoordX = left;
				}

				// Check if the current label collides with the right-hand neighbor
				rightCollide =
					i < rowLabels.length - 1 && // Only check for collision if there's a next label
					right >= previousLeft;

				// Draw the label if it doesn't collide with its neighbor and is within the left boundary
				if (
					!rightCollide &&
					left > limitLeft
				) {
					// Calculate the Y position for the label and draw it on the canvas
					newY = y0 + rowNum * (style.xAxis.label.lineHeight || style.xAxis.font.size + 4);
					ctx.fillText(labelFormat.default, labelCoordX, newY);

					if (!labelsRendered[row]) {
						labelsRendered[row] = [];
					}
					// save rendered labels for unit tests
					labelsRendered[row].push({
						text: labelFormat.default,
						x: labelCoordX,
						y: newY,
						textAlign,
						label: { ...label },
						isOverlap: textAlign === 'left' ? labelCoordX + label.width.default > limitRight : labelCoordX > limitRight
					});

					// Update the left boundary for the next label
					previousLeft = left;
				}
			}

			if (rowLabels.length) rowNum++;
		}
		return labelsRendered;
	}

	/**
	 * Checks if a label is a duplicate based on the given params.renderDuplicateLabels setting. (CH-1748)
	 * This helps in determining which labels to keep or discard when there are duplicate labels.
	 * Normally duplicates won't occur, but certain combinations of user-defined format templates can sometimes cause them to happen.
	 * Note: KEEP_RIGHT is wonky right now and will need further work to account for the dynamically inserted left labels.
	 *
	 * @param {Object} label - The current label being checked.
	 * @param {Array} rowLabels - The array of labels in the current row.
	 * @param {number} i - The index of the current label in the rowLabels array.
	 * @return {boolean} - Returns true if the label is a duplicate and should be discarded, false otherwise.
	 */
	isDuplicateLabel(label, rowLabels, i) {
		let isDuplicate = false;

		if (this.core.params.renderDuplicateLabels !== renderDuplicateLabelsCategories.KEEP_ALL) {
			const previousLabel = i < rowLabels.length - 1 ? rowLabels[i + 1] : null;
			const nextLabel = i > 0 ? rowLabels[i - 1] : null;

			switch (this.core.params.renderDuplicateLabels) {
				case renderDuplicateLabelsCategories.KEEP_LEFT:
					if (nextLabel && nextLabel.format.default === label.format.default) {
						isDuplicate = true;
					}
					break;
				case renderDuplicateLabelsCategories.KEEP_RIGHT:
					if (previousLabel && previousLabel.format.default === label.format.default) {
						isDuplicate = true;
					}
					break;
			}
		}

		return isDuplicate;
	}

	/** Just before rendering, modify specific labels based on rules */
	applyRenderLabelRules(labels) {
	// Check if the Labeler is in single-row mode
		if (this.core.params.xAxisSingleRowMode) {
			const lowerRow = this.rowTemplate.lower;
			const upperRow = this.rowTemplate.upper;

			if (labels[upperRow] && labels[lowerRow]) {
				const upperLabels = labels[upperRow];
				const lowerLabels = labels[lowerRow];

				// Merge the lower row values into the upper row values
				lowerLabels.forEach(lowerLabel => {
					const upperIndex = upperLabels.findIndex(label => label.index === lowerLabel.index);

					if (upperIndex !== -1) {
						// Replace the upper row label with the lower row label if it fits
						if (!this.replaceIfFits(upperLabels[upperIndex], lowerLabel, true)) {
							// If none of the formats fit, keep the original upper label
							upperLabels[upperIndex] = upperLabels[upperIndex];
						}
					} else {
						// If no corresponding upper label, add the lower label to the upper row
						upperLabels.push(lowerLabel);
					}
				});

				// Sort the upper row labels to maintain order
				upperLabels.sort((a, b) => a.index - b.index);

				// Delete the lower row
				delete labels[lowerRow];
			}
		}

		// Apply bottom row first label formatting (usually to ensure the year is always visible)
		for (const labelType in labels) {
			const labelTypeCollection = labels[labelType];
			if (
				labelTypeCollection &&
				labelTypeCollection.length &&
					labelTypeCollection[0].format &&
					labelType !== 'years'
			) {
				labels = this.updatePairedLabelRow(labelTypeCollection[0], labels, this.core.params.xAxisSingleRowMode ? this.rowTemplate.upper : this.rowTemplate.lower);
				break;
			}
		}

		// Apply special case rules by overwriting the default format and width properties for matching labels
		let rowNum = 0;
		for (const labelType in labels) {
			const labelTypeCollection = labels[labelType];

			if (!labelTypeCollection) continue;

			// Work from right to left
			for (let i = labelTypeCollection.length - 1; i >= 0; i--) {
				const label = labelTypeCollection[i];
				const labelFormat = label.format;

				// Rule: bottom row intraday labels use "intraday" formatting, only for when the top row is minutes or hours
				// CH-1615: This is a bit of an ugly rule and should probably be removed in the future. But it works for now.
				if (
					labelFormat.intraday &&
						i > 0 &&
						(
							(labels.hours && labels.hours.length) ||
								(labels.minutes && labels.minutes.length)
						)
				) {
					labelFormat.default = labelFormat.intraday;
					label.width.default = label.width.intraday;
				}

				// Rule: add year to January labels when bottom row is "months" (e.g., use the "firstAlt" formatting)
				if (
					labelType === 'months' &&
						rowNum > 0 &&
						label.date.getMonth() === 0 &&
						!this.core.params.xAxisSingleRowMode
				) {
					labelFormat.default = labelFormat.firstAlt;
					label.width.default = label.width.firstAlt;
				}
			}

			// Increment row counter to allow for format check on months row
			if (labelTypeCollection.length) rowNum++;
		}

		// Special case: Ensure consistency in intraday single-row mode
		if (this.core.params.xAxisSingleRowMode && this.core.state.isIntraday) {
			const upperRow = this.rowTemplate.upper;
			const upperLabels = labels[upperRow];

			// Ensure first label of the day uses the date format
			if (upperLabels && upperLabels.length > 1) {
				for (let i = 0; i < upperLabels.length; i++) {
					const label = upperLabels[i];
					const previousLabel = i > 0 ? upperLabels[i - 1] : null;
					const labelDate = new Date(label.date);

					// Check if it's the first label of the day
					if (i === 0 || (previousLabel && new Date(previousLabel.date).getDate() !== labelDate.getDate())) {
						label.format.default = label.format.first;
						label.width.default = label.width.first;
					}
				}
			}
		}

		return labels;
	}

	/**
	* Replace the target label with the new label if it fits within the available width.
	* Try different format options (first, default) if the initial choice does not fit.
	* @param {Object} targetLabel - The label to be replaced.
	* @param {Object} newLabel - The new label to use as a replacement.
	* @param {boolean} isSingleRowMode - Flag indicating if single row mode is enabled.
	* @return {boolean} - Returns true if a replacement was made, false otherwise.
	*/
	replaceIfFits(targetLabel, newLabel, isSingleRowMode) {
		const { panel } = this.axis;
		const { left: paddingLeft, right: paddingRight } = panel.params.style.xAxis.label.padding;
		const availableWidth = panel.size.width - paddingLeft - paddingRight;
		const formatOptions = isSingleRowMode ? ['first', 'default'] : ['firstAlt', 'first', 'default'];

		for (const formatType of formatOptions) {
			const newFormatWidth = newLabel.width[formatType];

			if (newFormatWidth <= availableWidth) {
				targetLabel.format.default = newLabel.format[formatType];
				targetLabel.width.default = newFormatWidth;

				// Ensure the chosen format type is recorded correctly
				newLabel.format.default = newLabel.format[formatType];
				newLabel.width.default = newLabel.width[formatType];

				return true;
			}
		}

		return false;
	}

	/**
	 * Insert a label into the appropriate row that has alternative formatting (usually to ensure the year is always visible).
	 * Take "first" formatting from the top label's formats and insert a new label into the paired row label collection.
	 */
	updatePairedLabelRow(label, labels, targetRow) {

		// Get the paired label collection so we can add the "first"-styled label to it
		const pairedId = label.pairedFirst;
		let paired = this.core.params.xAxisSingleRowMode ? labels[targetRow] : labels[pairedId];

		if (!paired || !paired.length) {
			// There are no paired labels, create a new one
			const newLabel = deepClone(label);

			if (this.core.params.xAxisSingleRowMode && labels[targetRow]) {
				paired = labels[targetRow] = [newLabel];
			} else if (!this.core.params.xAxisSingleRowMode && labels[pairedId]) {
				paired = labels[pairedId] = [newLabel];
			}

			if (paired?.length) {
				paired[0].format.default = paired[0].format.first;
				paired[0].width.default = paired[0].width.first;
			}
		} else {
			// Insert label at the beginning of the collection if it precedes the first paired label
			if (paired[0].date - label.date > 0) {
				const newLabel = deepClone(label);
				paired.splice(0, 0, newLabel);
			}

			paired[0].format.default = label.format.first;
			paired[0].width.default = label.width.first;
			paired[0].x0 = label.x0;

			const { style } = this.axis.panel.params;
			const { left: paddingLeft, right: paddingRight } = style.xAxis.label.padding;
			const left = paired[0].x0 + paddingLeft - paddingRight;
			const right = left + paired[0].width.default;

			// CH-1615: Could we avoid this step by using the label priority?

			// Remove neighboring paired label(s) if the new label is now overlapping.
			// We do this here to ensure this label always shows up since the render loop
			// prioritizes right-hand labels.
			for (let x = paired.length - 1; x >= 1; x--) {
				if (paired[x].x0 && paired[x].x0 <= right) paired.splice(1, 1);
			}
		}

		return labels;
	}
}

/** A wrapper for the collection of scales and label related functions */
class AxisDate extends Axis {
	constructor(panel, scaleParams) {
		super(panel, scaleParams);
		/** @type {Labeler} */
		this.labeler = new Labeler(this, this.core);
	}

	/**
	 * Trim time labels to frame, render labels and grid
	 * @param {object} dateLabels - object with years and months to display
	 */
	render(dateLabels) {
		const { core, panel } = this;
		const { params } = core;

		// Render labels if there is room
		if (panel.size.padding.bottom > 0) {
			// Deep clone dateLabels to prevent mutations in the original object
			this.labeler.render(deepClone(dateLabels) || {});
		}

		// Normalized charts can style the normalizedate line differently
		if (
			params.normalizeDate &&
			panel.isNormalized &&
			panel.params.style.grid.vertical.normalize.color &&
			!core.dataXHR
		) {
			let index = 0;
			const normalizeDate = new Date(params.normalizeDate);

			index = core.closestExchangeIndex(normalizeDate);

			let x0 = this.primaryScale(index);
			const padLeft = panel.size.padding.left;
			const ctx = panel.rootContext;

			// Render vertical normalize line
			if (x0 && x0 > padLeft && x0 < panel.size.width + padLeft) {
				const style = panel.params.style.grid.vertical.normalize;
				const lineDash = style.lineDash;
				x0 = subpixel.px(x0);
				ctx.beginPath();
				ctx.strokeStyle = style.color;
				ctx.lineWidth = style.width;
				if (lineDash) ctx.setLineDash(lineDash.split(/\s*,\s*/));
				ctx.moveTo(x0, panel.size.padding.top);
				ctx.lineTo(x0, panel.size.padding.top + panel.size.height);
				ctx.stroke();
				ctx.setLineDash([]);
			}
		}
	}

	/**
	 * Return true if this panel gets an XAxis based on various chart settings
	 * @returns {boolean}
	 */
	hasAxis() {
		const { core, panel } = this;
		const { panelXAxis } = core.params;
		const idx = panel.index;
		const lastIdx = core.panels.length - 1;

		if (typeof panel.params.hasXAxis === 'boolean') {
			// a per-panel param overrides the global rule
			return panel.params.hasXAxis;
		} else if (panelXAxis === 'none') {
			return false;
		} else if (panelXAxis === 'all') {
			return true;
		} else if (panelXAxis === 'first' && idx === 0) {
			return true;
		} else if (panelXAxis === 'last' && idx === lastIdx) {
			return true;
		}

		return false;
	}

	/**
	 * Generates an array of dates based on a custom dataset, similar to getExchangeRuler
	 * @returns {array}
	 */
	getCustomRuler(customData = this.core.getFirstCustomDataset(this.core.data)) {
		const rulerDates = [];
		// coerce dates in case the input data is not in date format
		customData.forEach((row, i) => {
			rulerDates[i] = new Date(customData[i].date);
		});

		return rulerDates;
	}

	/**
	 * Get array of dates for an exchange, based on information from the TimeService API call.
	 * @returns {array}
	 */
	getExchangeRuler() {
		const { core } = this;
		const { tradingDays } = core.timeService;
		let i = 0;
		let iLen = 0;
		const data = [];
		let exchangeId = '';

		if (!core.state.isIntraday) {
			// interday is simple - just use the tradingdays from the timeservice call

			// convert to utc date objects
			for (i = 0, iLen = tradingDays.length; i < iLen; i++) {
				data[i] = core.getUTCDate(new Date(tradingDays[i])).getTime();
			}
		} else if (core.timeService.typicalSessions) {
			// if intraday, loop over tradingdays, and within each trading day, loop over the
			// typical session(s) for that day of the week.
			// for each trading day, it starts at the minimum session date and counts up by one minute,
			// each time checking if that minute falls within one of that day's sessions.
			// it stops when the minutes exceed the maximum session date.
			// the maximum session date may be shorter than a typical day if part of the alternatesession collection (eg partial holiday).

			const typicalSessions = [...core.timeService.typicalSessions];
			let typicalDay = {};
			let tradingDate = new Date();
			let dateMin = new Date();
			let dateMax = new Date();
			let dateMinMs = 0;
			let dateMaxMs = 0;

			for (i = 0, iLen = tradingDays.length; i < iLen; i++) {
				tradingDate = core.getUTCDate(new Date(tradingDays[i]));

				// grab typical sessions for this day.
				typicalDay = typicalSessions[tradingDate.getDay()];

				if (!typicalDay.sessions.length) {
					continue;
				}

				// convert the open/close offsets into ms
				Object.keys(typicalDay.rulerSessions).forEach(exchange => {
					const exchangeSession = typicalDay.rulerSessions[exchange];
					exchangeSession.openMs = this.getSessionDateMS(
						tradingDate,
						exchangeSession.open
					);
					exchangeSession.closeMs = this.getSessionDateMS(
						tradingDate,
						exchangeSession.close
					);
				});

				// snap the loop start/end to min/max of all sessions on tradingDate
				dateMin = this.getMinSessionDate(tradingDate, typicalDay);
				dateMax = this.getMaxSessionDate(
					tradingDays[i],
					tradingDate,
					typicalDay
				);

				// convert dates to ints for better performance
				dateMinMs = dateMin.getTime();
				dateMaxMs = dateMax.getTime();

				// collect all valid trading minutes within range
				while (dateMinMs < dateMaxMs) {
					// check if this minute falls within any session
					for (exchangeId in typicalDay.rulerSessions) {
						if (
							Object.prototype.hasOwnProperty.call(typicalDay.rulerSessions, exchangeId)
						) {
							const exchangeSession =
								typicalDay.rulerSessions[exchangeId];

							if (
								dateMinMs >= exchangeSession.openMs &&
								dateMinMs < exchangeSession.closeMs &&
								(!data.length ||
									dateMinMs > data[data.length - 1])
							) {
								data[data.length] = dateMinMs;
								break;
							}
						}
					}

					// increment one minute
					dateMinMs += 60000;
				}
			}
		}

		return data;
	}

	/**
	 * Return a date object with specified minute offset from midnight
	 * @param {Date} date - the date to be offset
	 * @param {number} minutes - how many minutes to offset from midnight
	 * @returns {Date}
	 * @private
	 */
	getSessionDateMS(date, minutes) {
		return new Date(
			date.getFullYear(),
			date.getMonth(),
			date.getDate(),
			0,
			minutes
		).getTime();
	}

	/**
	 * Return a date object for a specific date with a minute offset for that day's typical session open
	 * @param {Date} tradingDate - the date to be offset
	 * @param {object} typicalDay - object with typical session open offset
	 * @property {number} typicalDay.min - number of minutes to offset open
	 * @private
	 */
	getMinSessionDate(tradingDate, typicalDay) {
		const dateMin = new Date(tradingDate);
		dateMin.setMinutes(typicalDay.min);

		return dateMin;
	}

	/**
	 * Return a date object for a specific date with a minute offset for that day's typical session close.
	 * Also accounts for alternate session information
	 * @param {Date} tradingDay
	 * @param {Date} tradingDate - the date to be offset
	 * @param {object} typicalDay - object with typical session close offset
	 * @property {number} typicalDay.max - number of minutes to offset close
	 * @returns {Date}
	 * @private
	 */
	getMaxSessionDate(tradingDay, tradingDate, typicalDay) {
		let dateMin = new Date(tradingDate);
		const { core } = this;

		// default to typical day's max
		dateMin.setMinutes(typicalDay.max);

		// adjust for alternate sessions
		if (core.params.collapseAlternateSessionGaps) {
			const alternateSessions = core.timeService.alternateSessions || {};

			// find alternate session on this trading date
			Object.keys(alternateSessions).forEach(key => {
				if (key === tradingDay.replace(/Z$/, '')) {
					// this day's date matches alternatesessions date

					// calculate new max
					let alternateMax = 0;

					// loop through typical day's rulersessions
					Object.keys(typicalDay.rulerSessions).forEach(exchangeId => {
						// alternate sessions for this exchange
						const matches = alternateSessions[key].filter(row => row.exchangeId === exchangeId);

						// if alternate session found, use alternate close date
						if (matches.length > 0) {
							// find max of all matches
							const matchesMax = Math.max(
								matches.map(o => o.close)
							);
							alternateMax = Math.max(alternateMax, matchesMax);
						} else {
							// if no match, use typical close date
							alternateMax = Math.max(
								alternateMax,
								typicalDay.rulerSessions[exchangeId].close
							);
						}
					});

					// set alternate dateMin for this day only
					if (alternateMax > 0) {
						dateMin = new Date(tradingDate);
						dateMin.setMinutes(alternateMax);
					}
				}
			});
		}

		return dateMin;
	}
}

/**
 * Marker objects can directly alter the DOM using primitive shapes and lines
 * and are intended to be managed via Indicator objects
 */
class Marker {
	constructor(params, panel) {
		this.panel = panel;
		this.params = this._getParams(params);
	}

	/**
	 * A wrapper for getDefaultParams
	 * @return {object}
	 */
	_getParams(params) {
		return merge({}, this.getDefaultParams(), params);
	}

	/**
	 * return the default params for this type of Marker
	 * @return {object}
	 */
	getDefaultParams() {
		throw new Error('Required Marker method not found');
	}

	/**
	 * calculate dynamic bar width based on data period and panel domain
	 * (method used by more than one marker subclass so it lives here)
	 * @param {Panel} panel
	 * @return {int}
	 */
	getBarWidth(panel, min = null, max = null) {
		if (!min && !max) [min, max] = panel.xAxis.primaryScale.domain();
		const { width } = panel.size;
		let len = max - min;

		switch (panel.core.params.dataPeriod) {
			case 'Year':
				len /= 365;
				break;
			case 'Month':
				len /= 15;
				break;
			case 'Week':
				len /= 3;
				break;
			case 'Day':
				break;
			case 'Hour':
				len /= 30;
				break;
			case 'Minute':
				len /= panel.core.params.dataInterval
					? panel.core.params.dataInterval
					: 1;
				break;
		}

		const w0 = Math.min(15, Math.max(0, width / len / 2 / 2));

		return w0 < 0.25 ? 0 : w0;
	}

	/** Update DOM elements using primitive shapes and lines */
	render() {
		throw new Error('Required Marker method not found');
	}
}

/** Line marker */
class LineMarker extends Marker {
	getDefaultParams() {
		return {
			lineWidth: 1,
			lineColor: '#000',
			fillColor: 'none',
			fillBetweenColor: '#aaa'
		};
	}

	render(
		ctx,
		data,
		xScale,
		yScale,
		color,
		lineWidth,
		lineDash,
		isStepped
	) {
		if (!data.length || !data[0].length) return;

		if (ctx.setLineDash && lineDash) {
			ctx.setLineDash(lineDash.split(/\s*,\s*/));
		}

		ctx.lineJoin = 'round';

		// lineWidth defaults
		if (lineWidth === null) lineWidth = this.params.lineWidth;

		// don't render if lineWidth is 0
		if (lineWidth === 0) {
			// reset dashed line
			if (ctx.setLineDash) ctx.setLineDash([]);
			return;
		}

		ctx.lineWidth = lineWidth;
		ctx.strokeStyle = color || this.params.lineColor;

		ctx.beginPath();

		ctx.moveTo(subpixel.px(xScale(data[0][0])), subpixel.px(yScale(data[0][1])));

		this.getLine(ctx, data, xScale, yScale, false, isStepped);

		ctx.stroke();

		// reset dashed line
		if (ctx.setLineDash) ctx.setLineDash([]);
	}

	getCoords(ctx, data, xScale, yScale) {
		if (!data.length || !data[0].length) return;

		const coords = [];

		coords.push([subpixel.px(xScale(data[0][0])), subpixel.px(yScale(data[0][1]))]);

		return [...coords, ...this.getLine(ctx, data, xScale, yScale, true, true)];
	}

	renderFill(ctx, data, height, xScale, yScale, fillStyle, fillToValue) {
		if (!data.length || data.length < 2 || !data[0].length) return;

		ctx.lineJoin = 'round';
		ctx.fillStyle = fillStyle || this.params.fillColor;
		ctx.beginPath();

		// get vertices for normal line
		const firstIndex = this.getLine(ctx, data, xScale, yScale);
		let y0;

		// complete the fill region with two extra vertices
		if (typeof fillToValue === 'number') {
			// fill to a specific value (commonly 0.0)
			y0 = subpixel.px(Math.min(yScale(fillToValue), height));
		} else {
			// fill to bottom of panel
			y0 = subpixel.px(height - 1);
		}

		// add a vertex at (last index, y0)
		if (data.length > 0) {
			ctx.lineTo(subpixel.px(xScale(data[data.length - 1][0])), y0);
		}

		// add a vertex at (first index, y0)
		if (
			typeof firstIndex === 'number' &&
			typeof data[firstIndex] !== 'undefined'
		) {
			ctx.lineTo(subpixel.px(xScale(data[firstIndex][0])), y0);
		}

		ctx.fill();
	}
	renderFillBetween(ctx, dataUp, dataDown, xScale, yScale, fillStyle) {
		if (!dataUp.length || !dataUp[0].length) return;

		ctx.lineJoin = 'round';
		ctx.fillStyle = fillStyle || this.params.fillBetweenColor;
		ctx.beginPath();

		ctx.moveTo(
			subpixel.px(xScale(dataUp[0][0])),
			subpixel.px(yScale(dataUp[0][1]))
		);

		this.getLine(ctx, dataUp, xScale, yScale);

		ctx.lineTo(
			subpixel.px(xScale(dataDown[dataDown.length - 1][0])),
			subpixel.px(yScale(dataUp[dataUp.length - 1][1]))
		);
		ctx.lineTo(
			subpixel.px(xScale(dataDown[dataDown.length - 1][0])),
			subpixel.px(yScale(dataDown[dataDown.length - 1][1]))
		);

		this.getLine(
			ctx,
			Array.prototype.slice.call(dataDown).reverse(),
			xScale,
			yScale
		);

		ctx.lineTo(
			subpixel.px(xScale(dataDown[0][0])),
			subpixel.px(yScale(dataDown[0][1]))
		);

		ctx.fill();
	}
	getLine(ctx, data, xScale, yScale, getData, isStepped) {
		let x0 = 0;
		let y0 = 0;
		let xLast = 0;
		let yLast = 0;
		let firstIndex;
		const coords = [];

		for (let x = 0, xLen = data.length; x < xLen; x++) {
			x0 = subpixel.px(xScale(data[x][0]));
			y0 = subpixel.px(yScale(data[x][1]));

			if (!isNaN(y0) && !isNaN(x0)) {
				if (typeof firstIndex !== 'number') {
					firstIndex = x;
				}

				if (x0 !== xLast || y0 !== yLast) {
					if (yLast && isStepped) {
						coords.push([x0, yLast]);
					}

					coords.push([x0, y0]);
				}

				xLast = x0;
				yLast = y0;
			}
		}

		// draw coords collection
		for (let x = 0, xLen = coords.length; x < xLen; x++) {
			ctx.lineTo(coords[x][0], coords[x][1]);
		}

		if (getData) {
			return coords;
		}
		return firstIndex;
	}

	// this is mostly like getLine(), but it just returns an array of coordinates for the isWithin check
	getLineCoords(data, xScale, yScale, isStepped) {
		if (!data || !xScale || !yScale) return [];
		let x0 = 0;
		let y0 = 0;
		let xLast = 0;
		let yLast = 0;
		const coords = [];

		for (let x = 0, xLen = data.length; x < xLen; x++) {
			x0 = xScale(data[x][0]);
			y0 = yScale(data[x][1]);

			if (!isNaN(y0) && !isNaN(x0)) {
				if (x0 !== xLast || y0 !== yLast) {
					if (yLast && isStepped) {
						coords.push([x0, yLast]);
					}

					coords.push([x0, y0]);
				}

				xLast = x0;
				yLast = y0;
			}
		}

		return coords;
	}
}

/**
 * Given an id, this function returns an entry from the locale definition's 'translations' object if it exists.
 * @param {object} locale - Modcharts locale object (e.g. chart.locale)
 * @param {string} id - Translation id (e.g. 'band.overbought')
 * @param {string[]} replacements - Strings to populate ${n} placeholders. e.g. ['100.00'] will replace '{0}' in the id with '100.00'
 * @param {string} defaultValue - optional fallback value. If not provided, the id will be returned.
 * @returns {string} - Translated string
 */
const translate = (locale = {}, id = '', replacements = [], defaultValue = null) => {
	let val = get(locale.translations, id) ||
		get(locales.en_US.translations, id) ||
		defaultValue ||
		id;
	replacements.forEach((row, index) => {
		val = val.replace(`{${index}}`, row);
	});
	return val;
};

/** Indicator objects are indicator parameters (period, smoothing) applied to one or more Markers */
class Indicator {
	/** @param {object} args - Custom args */
	constructor(args = {}) {
		/** @type {object} */
		this.state = {}; // State object for storing temporary data - a couple indicators use this
		/** @type {Panel} */
		this.panel = args.panel;
		/** @type {Modcharts} */
		this.core = this.panel.core;
		/** @type {number[]} */
		this.scaleUids = args.scaleUids || [this.panel.xAxis.primaryScaleUid, this.panel.yAxis.primaryScaleUid];
		/** @type {object} */
		this.params = this._getParams(args.params);
		/** @type {object} */
		this.markers = this._getMarkers();
		/** @type {object} */
		this.flags = {};
	}

	/**
	 * Returns a merged collection of default and custom params
	 * @param {object} args - Developer custom constructor args.
	 * @returns {object}
	 */
	_getParams(args) {
		const params = {
			extraInputs: [], // inputs not used by api (see mass index)
			id: 'indicator',
			inputs: [], // inputs used by chart api
			name: 'Chart Indicator',
			style: {},
			uid: createUid(),
			visible: true // Whether or not the indicator should be rendered
		};

		// merge derived indicator's default params into local params
		let paramsOut = { ...params, ...this._getDefaultParams() };

		// merge in styles from styleMap
		paramsOut.style = merge(paramsOut.style, this._getDefaultStyles(paramsOut.id));

		// merge in paramsCustom argument
		paramsOut = merge({}, paramsOut, args);

		// store off original params so we can revert to them if needed (translations uses this)
		this.state.originalParams = deepClone(paramsOut);

		return paramsOut;
	}

	/**
	 * Parse the styleMap for this indicator's styles
	 * @param {string} id - Indicator id (e.g. 'sma')
	 * @returns {object} A style object matching the styleMap for this indicator
	 */
	_getDefaultStyles(id = this.params.id) {
		return this.core.getStyle(`indicator.${id}`) || {};
	}

	/**
	 * Returns value of input name
	 * @param {string} name - Input name to search for.
	 */
	getInput(name) {
		for (const input of this.params.inputs) {
			if (input.name === name) {
				return input.value;
			}
		}

		for (const extraInput of this.params.extraInputs || []) {
			if (extraInput.name === name) {
				return extraInput.value;
			}
		}
	}

	/**
	 * Sets an input on this indicator
	 * @param {string} name - The input to set
	 * @param {string} value - The new input value
	 */
	setInput(name, value) {
		// Get indicator input with given name
		const indicatorInput = this.params.inputs.filter(row => row.name === name);

		// Set input to new value if that input was found above
		if (indicatorInput.length) {
			// Set input
			indicatorInput[0].value = value;

			// Fire update event
			this.core.eventEmitter.emit(
				types.INDICATOR_INPUT_UPDATE,
				{ indicator: this, name, value }
			);
		} else {
			// Figure out how to show error?
			this.core.warn(`Input ${name} not found`);
		}
	}

	/**
	 *  Sets a parameter on this indicator
	 * @param {string} name - The parameter to set
	 * @param {string} value - The new parameter value
	 */
	setParam(name, value) {
		// Get parameter with given name
		if (Object.prototype.hasOwnProperty.call(this.params, name)) {
			// Set param
			this.params[name] = value;

			// Fire update event
			this.core.eventEmitter.emit(
				types.INDICATOR_PARAM_UPDATE,
				{ indicator: this, name, value }
			);
		} else {
			this.core.warn(`Param ${name} not found`);
		}
	}

	/**
	 * Sets a style on this indicator
	 * @param {string} name - The style to set
	 * @param {string} value - The new style value
	 */
	setStyle(name, value) {
		if (this.getStyle(name) !== undefined) {
			let emitEvent = false;
			let updateStyle = false;

			// Make sure that numbers are saved as numbers, not strings
			if (typeof this.getStyle(name) === 'number') {
				// Only replace number type with new number
				if (typeof value !== 'number' && !isNaN(value)) {
					emitEvent = true;
					updateStyle = true;
					value = Number(value);
				} else {
					this.core.warn('Indicator.setStyle: Setting a non-numeric value where a numeric value is required is not allowed');
				}
			} else {
				emitEvent = true;
				updateStyle = true;
			}

			// Set the style
			if (updateStyle) set(this.params.style, name, value);

			// Fire update event
			if (emitEvent) {
				this.core.eventEmitter.emit(
					types.INDICATOR_STYLE_UPDATE,
					{ indicator: this, name, value }
				);
			}
		} else {
			this.core.warn(`Style ${name} not found`);
		}
	}

	/**
	 * Returns style info for the given selector from this indicator's params.style collection
	 * @param {string} selector - Selector to use when getting style (eg. 'panel.padding.right')
	 * @return {string|number} The style found in the styleMap
	 */
	getStyle(selector) {
		return get(this.params.style, selector);
	}

	/**
	 * Clips canvas context to panel size
	 * @param {CanvasRenderingContext2D} ctx - Canvas context
	 * @param {string} direction - Specify which area to clip - above or below value
	 * @param {int} value - The value limit of clip
	 * @return {CanvasRenderingContext2D}
	 */
	_clip(ctx, direction, value, dayLeft, dayRight) {
		ctx.save();
		ctx.beginPath();

		const top = this.panel.params.style.padding.top;
		const right = typeof dayRight === 'number' ? dayRight : this.panel.params.style.padding.left + this.panel.size.width;
		const bottom = this.panel.params.style.padding.top + this.panel.size.height;
		const left = typeof dayLeft === 'number' ? dayLeft : this.panel.params.style.padding.left;
		const y0 = this.yScale(value);

		if (direction === 'above') {
			ctx.moveTo(left, top);
			ctx.lineTo(right, top);
			ctx.lineTo(right, y0);
			ctx.lineTo(left, y0);
		} else {
			ctx.moveTo(left, y0);
			ctx.lineTo(right, y0);
			ctx.lineTo(right, bottom);
			ctx.lineTo(left, bottom);
		}

		ctx.closePath();

		ctx.clip();

		return ctx;
	}

	/**
	 * Restores context to preclip state
	 * @param {CanvasRenderingContext2D} ctx
	 * @return {CanvasRenderingContext2D}
	 */
	_clipEnd(ctx) {
		ctx.restore();
		return ctx;
	}

	/**
	 * Returns the default params for this type of indicator.
	 * @abstract
	 */
	_getDefaultParams() {
		console.warn('Required Indicator method _getDefaultParams not found');
	}

	/**
	 * Maps the current slice of indicator data to a one-dimensional array for
	 * use in generic marker rendering.
	 * @param {object[]} data - Data array
	 * @param {string} type - Key used to look up data in the array
	 * @returns {array[]} The resulting data array
	 */
	_getDataMap(data = [], type = this.params.id) {
		return data.map(d => [d.dateIndex, +d[type]]);
	}

	/**
	 * Returns indicator panel's xaxis d3-scale
	 * @returns {d3-scale}
	 */
	get xScale() {
		return this.panel.xAxis.getScale(this.scaleUids[0]);
	}

	/**
	 * Returns indicator panel's yaxis d3-scale
	 * @returns {d3-scale}
	 */
	get yScale() {
		return this.panel.yAxis.getScale(this.scaleUids[1]);
	}

	/**
	 * Returns the minimum value of all datasets for the indicator.
	 * @param {object[]} data - Data array
	 * @param {string} type - Key used to look up data in the array
	 * @returns {number} The minimum value found
	 */
	getRangeMin(data = [], type = this.params.id) {
		return min(data[type] || [], i => +i[type]);
	}

	/**
	 * Returns the maximum value of all datasets for the indicator.
	 * @param {object[]} data - Data array
	 * @param {string} type - Key used to look up data in the array
	 * @returns {number} The maximum value found
	 */
	getRangeMax(data = [], type = this.params.id) {
		return max(data[type] || [], i => +i[type]);
	}

	/**
	 * Helper function for getMinRange
	 * @return {number} The minimum value in the arguments
	 */
	_min() {
		let minVal = Infinity;

		for (const a of arguments) {
			minVal = Math.min(a, minVal);
		}

		return minVal;
	}

	/**
	 * Helper function for getMaxRange
	 * @return {number} The maximum value in the arguments
	 */
	_max() {
		let maxVal = -Infinity;

		for (const a of arguments) {
			maxVal = Math.max(a, maxVal);
		}

		return maxVal;
	}

	/**
	 * Return true if mouse coordinates intersect indicator.
	 * @return {Boolean} Whether or not the the mouse is within the indicator
	 * @abstract
	 */
	isWithin() {
		return false;
	}

	/**
	 * Checks for overlap between two boxes
	 * todo: performance improvement - convert to simple arrays instead of objects with x,y,width,height props
	 * @param {object} r1 - Rectangle 1
	 * @property {number} r1.x - Rectangle 1 x location
	 * @property {number} r1.y - Rectangle 1 y location
	 * @property {number} r1.width - Rectangle 1 width
	 * @property {number} r1.height - Rectangle 1 height
	 * @param {object} r2 - Rectangle 1
	 * @property {number} r2.x - Rectangle 1 x location
	 * @property {number} r2.y - Rectangle 1 y location
	 * @property {number} r2.width - Rectangle 1 width
	 * @property {number} r2.height - Rectangle 1 height
	 * @returns {boolean}
	 */
	_intersect(r1, r2) {
		if (
			r1.x < r2.x + r2.width &&
			r2.x < r1.x + r1.width &&
			r1.y < r2.y + r2.height
		) {
			return r2.y < r1.y + r1.height;
		} else {
			return false;
		}
	}

	/**
	 * Initialize and create references to this Indicator's Marker objects.
	 * @returns {object} Collection of indicator markers
	 */
	_getMarkers() {
		return { line: new LineMarker() };
	}

	/**
	 * Render this Indicator's Marker objects
	 * @abstract
	 */
	render() {
		console.warn('Required Indicator method render not found');
	}

	/**
	 * Cleanup before removing this indicator from panel's indicator collection
	 */
	remove() {
		// remove any associated flags
		Object.keys(this.flags).forEach(key => {
			const flag = this.flags[key];
			flag.remove();
			delete this.flags[key];
		});
	}

	/**
	 * Returns a gradient object given some colors and stops
	 * @param {CanvasRenderingContext2D} ctx - Canvas context
	 * @param {string} colorStart - CSS color value for the gradient start
	 * @param {string} colorStop - CSS color value for the gradient stop
	 * @param {number} x0 - X axis start location
	 * @param {number} y0 - Y axis start location
	 * @param {number} x1 - X axis end location
	 * @param {number} y1 - Y axis end location
	 * @returns {ICanvasGradient}
	 */
	_getLinearGradient(
		ctx,
		colorStart,
		colorStop,
		x0,
		y0,
		x1,
		y1
	) {
		const fill = ctx.createLinearGradient(x0, y0, x1, y1);
		fill.addColorStop(0, colorStart);
		fill.addColorStop(1, colorStop);
		return fill;
	}

	/**
	 * Gets a generic JSON object representation of this Indicator
	 * @returns {object} JSON representation of this indicator
	 */
	toJSON() {
		const json = {};
		const skipIndicatorParams = ['description']; // don't save these indicator params
		Object.keys(this.params).forEach(key => {
			const value = this.params[key];
			if (value === null) {
				return true;
			} else if (typeof value === 'object') {
				if (value.constructor === Array) {
					json[key] = [...[], ...value];
				} else {
					json[key] = { ...{}, ...value };
				}
			} else if (!skipIndicatorParams.includes(key)) {
				json[key] = value;
			}
		});
		return json;
	}

	/**
	 * Returns the first datapoint so that it can be passed back to ModchartsEngine for pan events.
	 * This prevents a jagged 'stairstep' jump when panning.  Only needed for a few indicators (OBV, A/D, ADL).
	 * @returns {object} Date and value of the first datapoint
	 */
	_getCumulativeAdjustment() {
		const { core } = this;
		const data = core.data[core.params.symbol][this.params.id][this.params.uid][0]; // this.data['36276'].adl['518b16a3'][0].adl
		// adjust the time offset so it is passed through as UTC (CH-352)
		const exchangeOffset = core.xref[core.params.symbol].utcOffset;
		const localOffset = data.date.getTimezoneOffset();
		const newDateIntraday = new Date(data.date.getTime() - ((localOffset + exchangeOffset) * 60000));
		const newDateInterday = new Date(data.date.getTime() - (localOffset * 60000));
		return {
			date: core.state.isIntraday ? newDateIntraday : newDateInterday,
			value: data[this.params.id]
		};
	}

	/**
	 * Returns a style value from either the style object, the style.sessionBreak object, or other applicable defaults.
	 * Fallback order for SessionBreak styles: bollinger.sma.sessionBreak > bollinger.sessionBreak > bollinger > defaults
	 * Fallback order for NormalSession styles: bollinger.sma > bollinger > defaults
	 * @returns {string} - Adjusted style value
	 * @property {string} key - Style key (eg. color, width, lineDash)
	 * @property {string} sessionType - Session type (eg. NormalSession, SessionBreak)
	 * @property {string} dataKey - Optional sub-series to use from data (eg. 'ema' in bollinger.ema)
	 */
	_getMarkerLineStyle(
		key = null,
		sessionType = 'NormalSession',
		dataKey = this.params.id
	) {
		const { style } = this.params;
		const { defaults } = this.core.styleMap.indicator;
		if (sessionType === 'SessionBreak') {
			if (
				style[dataKey] &&
				style[dataKey].sessionBreak &&
				style[dataKey].sessionBreak[key]
			) {
				// ex: bollinger.sma.sessionBreak.color
				return style[dataKey].sessionBreak[key];
			} else if (style.sessionBreak && style.sessionBreak[key]) {
				// ex: bollinger.sessionBreak.color
				return style.sessionBreak[key];
			} else if (style[dataKey] && style[dataKey][key]) {
				// ex: bollinger.sma.color
				return style[dataKey][key];
			} else if (style[key]) {
				// ex: bollinger.color
				return style[key];
			} else if (defaults.sessionBreak[key]) {
				// ex: defaults.sessionBreak.lineDash
				return defaults.sessionBreak[key];
			}
		} else if (style[dataKey] && style[dataKey][key]) {
			// ex: bollinger.sma.color
			return style[dataKey][key];
		} else if (style[key]) {
			// ex: bollinger.color
			return style[key];
		} else if (defaults[key]) {
			// ex: defaults.width
			return defaults[key];
		}
		return null;
	}

	/**
	 * A standardized render method for common line indicators.  Since most line indicators have similar render calls, this reduces redundant code.
	 * @returns {object} - Data series collection (eg. data.ema)
	 * @property {object} data - Data collection containing one or more data series
	 * @property {string} sessionType - Session type (eg. 'NormalSession', 'SessionBreak')
	 * @property {object} marker - Allow an alternate marker object to be used.  Usually it's markers.line.
	 * @property {object} dataKey - Which series to use from data (eg. 'ema')
	 * @property {array} dataMap - Allow an alternate dataMap to be passed.
	 * @property {string} styleKey - Which series to use from styles (eg. 'positive').  Usually the same as the dataKey.
	 * @property {string} color - Override color
	 * @property {number} width - Override width
	 * @property {string} lineDash - Override lineDash
	 * @property {boolean} isStepped - Specify the isStepped property needed for the marker render
	 */
	_renderLineMarker({
		data = {},
		sessionType = 'NormalSession',
		marker = this.markers.line,
		dataKey = this.params.id,
		dataMap = this._getDataMap(data, dataKey),
		styleKey = dataKey,
		color = this._getMarkerLineStyle('color', sessionType, styleKey),
		width = this._getMarkerLineStyle('width', sessionType, styleKey),
		lineDash = this._getMarkerLineStyle('lineDash', sessionType, styleKey),
		isStepped = false
	}) {
		marker.render(
			this.panel.rootContext,
			dataMap,
			this.xScale,
			this.yScale,
			color,
			width,
			lineDash,
			isStepped
		);
	}

	/**
	 * Update the name and description of this indicator based on the current locale.
	 * Falls back to the original name and description if no translation is found
	 */
	_updateTranslations() {
		this.params.name = translate(
			this.panel.core.locale,
			`indicator.${this.params.id}.name`,
			[],
			this.state.originalParams.name
		);
		this.params.description = translate(
			this.panel.core.locale,
			`indicator.${this.params.id}.description`,
			[],
			this.state.originalParams.description
		);
	}
}

/** Accumulation / Distribution Line indicator */
class ADLIndicator extends Indicator {
	/**
	 * Returns the default params for this type of indicator. Overrides abstract
	 * method inherited from Indicator.
	 * @returns {object} - Default parameters object
	 * @property {string} id - Indicator ID
	 * @property {string} name - Indicator Name
	 * @property {string} description - Indicator Description
	 */
	_getDefaultParams() {
		return {
			id: 'adl',
			name: 'Accumulation / Distribution',
			description: 'The Accumulation/Distribution Line is calculated by adding or subtracting a portion of each day\'s volume from a cumulative total. The amount of volume to add or subtract is based on the relationship between the close and the high-low range. The closer the close is to the high, the more volume is added to the cumulative total. Conversely, the closer the close is to the low, the more volume is subtracted from the cumulative total. If the close is exactly midway between the high and low prices, nothing is added to the cumulative total.',
			componentSeries: [
				{ id: 'adl' }
			]
		};
	}

	/** @param {object} data - Symbol data object */
	render(data, sessionType) {
		this.cumulativeAdjustment = this._getCumulativeAdjustment();
		this._renderLineMarker({
			data: data[this.params.id],
			sessionType
		});
	}
}

/** Advance/Decline Indicator */
class AdvanceDeclineIndicator extends Indicator {
	/**
	 * Returns the default params for this type of indicator. Overrides abstract
	 * method inherited from Indicator.
	 * @returns {object} - Default parameters object
	 * @property {string} id - Indicator ID
	 * @property {string} name - Indicator Name
	 * @property {string} description - Indicator Description
	 * @property {object[]} inputs - The advancer and decliner inputs
	 */
	_getDefaultParams() {
		return {
			id: 'advancedecline',
			name: 'Advance/Decline Line',
			description:
				'The Advance/Decline Line is a breadth indicator based on Net Advances, which is the number of advancing stocks less the number of declining stocks. Net Advances is positive when advances exceed declines and negative when declines exceed advances.',
			inputs: [
				{ name: 'Advancers', value: 10025197 }, // hard-coded NYSE issues as defaults
				{ name: 'Decliners', value: 10025198 }
			]
		};
	}

	/** @param {object} data - Symbol data object */
	render(data, sessionType) {
		this.cumulativeAdjustment = this._getCumulativeAdjustment();
		this._renderLineMarker({
			data: data[this.params.id],
			sessionType
		});
	}
}

/** Average True Range indicator */
class AverageTrueRangeIndicator extends Indicator {
	/**
	 * Returns the default params for this type of indicator. Overrides abstract
	 * method inherited from Indicator.
	 * @returns {object} - Default parameters object
	 * @property {string} id - Indicator ID
	 * @property {string} name - Indicator Name
	 * @property {string} description - Indicator Description
	 * @property {object[]} inputs - Indicator inputs
	 */
	_getDefaultParams() {
		return {
			id: 'avgtruerange',
			name: 'Average True Range',
			description:
				'Average True Range is a volatility indicator that measures how much a security\'s price moves in a typical period. It differs from statistical measures like Standard Deviation in that it uses the Open, High, Low, and Closing prices to calculate the range, whereas Standard Deviation only uses closing prices.',
			inputs: [{ name: 'period', value: 14 }],
			componentSeries: [
				{ id: 'avgtruerange' }
			]
		};
	}

	/** @param {object} data - Symbol data object */
	render(data, sessionType) {
		this._renderLineMarker({
			data: data[this.params.id],
			sessionType
		});
	}
}

/** Bar marker */
class BarMarker extends Marker {

	getDefaultParams() {
		return {
			fillColor: '#000'
		};
	}

	/**
	 * Renders data to a canvas using the provided scales and styling parameters
	 *
	 * @param {object} panel - Panel information
	 * @param {CanvasRenderingContext2D} ctx - Canvas rendering context
	 * @param {Array} data - Data to be rendered
	 * @param {function} xScale - Function to scale x-values
	 * @param {function} yScale - Function to scale y-values
	 * @param {string} fillColor - Color for filling the bars
	 * @param {string} align - Alignment of the bars, default is 'default'
	 * @param {string|null} radii - Radii for rounded corners, null for square corners
	 */
	render(panel, ctx, data, xScale, yScale, fillColor, align = 'default', radii = null) {
	// Calculate the width of the bars and the y-axis range
		const widthBar = this.getBarWidth(panel);
		const [yMin, yMax] = yScale.range();
		const yZero = yScale(0); // Y-coordinate for zero value

		// Initialize last x and y coordinates
		let [xLast, yLast] = data[data.length - 1].map(d => Math.round(yScale(d)));

		// Parse the radii for rounded corners, if provided
		radii = this._parseRadii(radii);

		// Determine the top y-coordinate for drawing bars
		let yTop;
		if (yZero > yMax) {
			yTop = yMax;
		} else if (yZero < yMin) {
			yTop = yMin;
		} else {
			yTop = yZero;
		}

		// Set up canvas drawing styles
		ctx.lineWidth = 1;
		ctx.fillStyle = fillColor || this.params.fillColor;
		ctx.strokeStyle = ctx.fillStyle;
		ctx.beginPath();

		// Iterate through the data to draw each bar
		for (const [xValue, yValue] of data) {
			if (yValue === 0) continue; // Ignore data points with no value

			const x = xScale(xValue);
			let y = yScale(yValue);
			const heightBar = yTop - y; // Calculate the height of the bar

			// Adjust y-coordinate based on alignment
			if (align === 'bottom') {
				y = yMax - heightBar - 2;
			}

			// Draw the bars only if there is a change in coordinates
			if (x !== xLast || y < yLast) {
				if (widthBar > 0) {
					// Draw rounded or square bars based on radii
					if (radii) {
						subpixel.roundRect(ctx, x - widthBar, y, widthBar * 2, heightBar, radii);
					} else {
						subpixel.fillRect(ctx, x - widthBar, y, widthBar * 2, heightBar);
					}
				} else {
					// Draw a line for very narrow bars
					subpixel.moveTo(ctx, x, y);
					subpixel.lineTo(ctx, x, y + heightBar);
				}

				// Update the last drawn coordinates
				xLast = x;
				yLast = y;
			}
		}

		// Finalize drawing with fill or stroke based on bar width
		if (widthBar > 0) {
			ctx.fill();
		} else {
			ctx.stroke();
		}
	}

	/**
	 * Parses the radii input for different formats.
	 *
	 * This method takes a radii input, which can be either a null, a string representing a single number,
	 * or a string representing an array of numbers. It then tries to parse the string and convert it
	 * into a single number or an array of numbers as required by the roundRect() method.
	 *
	 * @param {string|null} radii - The radii input to be parsed. Can be a string or null.
	 * @returns {number|number[]|null} - The parsed radii as a single number, an array of numbers, or null if parsing fails.
	 */
	_parseRadii(radii) {
	// Check if radii is null or empty
		if (radii === null || !radii.length) {
			return null;
		}

		// Try to parse as a single number
		const parsedNumber = parseFloat(radii);
		if (!isNaN(parsedNumber)) {
			return parsedNumber;
		}

		// Try to parse as an array
		try {
			const parsedArray = JSON.parse(radii);
			if (Array.isArray(parsedArray)) {
				return parsedArray.map(num => parseFloat(num));
			}
		} catch (e) {
			console.error('Invalid radii format');
		}

		// Return null if parsing fails
		return null;
	}
}

/** Awesome Oscillator indicator */
class AwesomeOscillatorIndicator extends Indicator {
	/**
	 * Returns the default params for this type of indicator. Overrides abstract
	 * method inherited from Indicator.
	 * @returns {object} - Default parameters object
	 * @property {string} id - Indicator ID
	 * @property {string} name - Indicator Name
	 * @property {string} description - Indicator Description
	 * @property {object[]} inputs - Indicator inputs
	 */
	_getDefaultParams() {
		return {
			id: 'awesomeoscillator',
			name: 'Awesome Oscillator',
			description:
				"Awesome Oscillator measures short-term momentum in a security. It is displayed as a histogram of the difference between two simple moving averages of the midpoints of each period's prices.",
			inputs: [
				{ name: 'period1', value: 5 },
				{ name: 'period2', value: 34 }
			]
		};
	}

	/**
	 * @param {object} data - Symbol data object
	 * @return {number} - d3-range minimum
	 */
	getRangeMin(data) {
		const dataCopy = deepClone(data);
		const panelRange = this.panel.xAxis.primaryScale.domain();

		// Remove first element when returnPreviousPoint is selected so panel size is not affected
		if (this.panel.core.params.returnPreviousPoint) {
			dataCopy.awesomeoscillator.shift();
		}

		//Remove last element if it is out of view in panel so range size is not affected
		if (dataCopy.awesomeoscillator[dataCopy.awesomeoscillator.length - 1].dateIndex > panelRange[1]) {
			dataCopy.awesomeoscillator.pop();
		}

		return this._min(
			min(dataCopy.awesomeoscillator || [], i => i.awesomeoscillator),
			0
		);
	}

	/**
	 * @param {object} data - Symbol data object
	 * @return {number} - d3-range maximum
	 */
	getRangeMax(data) {
		const dataCopy = deepClone(data);
		const panelRange = this.panel.xAxis.primaryScale.domain();

		// Remove first element when returnPreviousPoint is selected so panel size is not affected
		if (this.panel.core.params.returnPreviousPoint) {
			dataCopy.awesomeoscillator.shift();
		}

		//Remove last element if it is out of view in panel so range size is not affected
		if (dataCopy.awesomeoscillator[dataCopy.awesomeoscillator.length - 1].dateIndex > panelRange[1]) {
			dataCopy.awesomeoscillator.pop();
		}

		return this._max(
			max(dataCopy.awesomeoscillator || [], i => i.awesomeoscillator),
			0
		);
	}

	/**
	 * @returns {object}
	 * @property {BarMarker} barUp
	 * @property {BarMarker} barDown
	 */
	_getMarkers() {
		return (
			this.markers || {
				barUp: new BarMarker(),
				barDown: new BarMarker()
			}
		);
	}

	/**
	 *
	 * @param {object} data - Indicator data
	 * @param {string} type - Indicator type
	 * @returns {array[]}
	 */
	_getDataMap(data, type) {
		switch (type) {
			case 'pos':
				return data.map((d, idx) => {
					const val = d.awesomeoscillator;
					const prev = data[idx - 1] || data[idx];
					const valPrev = prev.awesomeoscillator;

					return [d.dateIndex, val >= valPrev ? val : 0];
				});

			case 'neg':
				return data.map((d, idx) => {
					const val = d.awesomeoscillator;
					const prev = data[idx - 1] || data[idx];
					const valPrev = prev.awesomeoscillator;

					return [d.dateIndex, val < valPrev ? val : 0];
				});

			default:
				return [];
		}
	}

	/** @param {object} data - Symbol data object */
	render(data) {
		const ctx = this.panel.rootContext;
		const x = this.xScale;
		const y = this.yScale;

		this.markers.barUp.render(
			this.panel,
			ctx,
			this._getDataMap(data.awesomeoscillator, 'pos'),
			x,
			y,
			this.params.style.positive.color
		);

		this.markers.barDown.render(
			this.panel,
			ctx,
			this._getDataMap(data.awesomeoscillator, 'neg'),
			x,
			y,
			this.params.style.negative.color
		);
	}
}

/** High/Low indicator */
class BestWorstIndicator extends Indicator {
	/**
	 * Returns the default params for this type of indicator. Overrides abstract
	 * method inherited from Indicator.
	 * @returns {object} - Default parameters object
	 * @property {string} id - Indicator ID
	 * @property {string} name - Indicator name
	 * @property {string} description - Indicator description
	 * @property {string} timeframe - Number of trading days (or trading minutes) to measure
	 * @property {boolean} auto - If true, calculate ranges automatically from timeframe input
	 * @property {string} dateStartBest - Starting date of best period.  Must be parseable by Date.parse()
	 * @property {string} dateStopBest - Ending date of best period.  Must be parseable by Date.parse()
	 * @property {string} dateStartWorst - Starting date of worst period.  Must be parseable by Date.parse()
	 * @property {string} dateStopWorst - Ending date of worst period.  Must be parseable by Date.parse()
	 */
	_getDefaultParams() {
		return {
			id: 'bestworst',
			name: 'Best / Worst',
			description: 'Shade the best/worst performing time range',
			auto: true,
			dateStartBest: null,
			dateStopBest: null,
			dateStartWorst: null,
			dateStopWorst: null,
			timeframe: 20
		};
	}

	/** @returns {null} */
	getRangeMin() {
		return 10000;
	}

	/** @returns {null} */
	getRangeMax() {
		return 10000;
	}

	/** @returns {null} */
	_getMarkers() {
		return {};
	}

	/** @returns {null} */
	_getDataMap() {
		return null;
	}

	/**
	 * @param {CanvasRenderingContext2D} ctx - Chart canvas context
	 * @param {number} dateStart - Start date of region
	 * @param {number} dateStop - Stop date of region
	 * @param {string} bgColor - Background color of region
	 * @param {string} lineColor - Left/right border color of region
	 * @param {string} lineDash - Line dash
	 * @returns {undefined}
	 */
	_renderRegion(id, dateStart, dateStop, bgColor, lineColor, lineDash) {
		if (dateStart && dateStop) {
			const ctx = this.panel.rootContext;
			const minDate = new Date(dateStart);
			const maxDate = new Date(dateStop);
			const size = this.panel.size;
			const height = size.height - 1.5;
			const { core } = this;
			const x0 = this.xScale(core.closestExchangeIndex(minDate));
			const x1 = Math.min(
				this.xScale(core.closestExchangeIndex(maxDate)),
				size.width
			);
			ctx.beginPath();
			ctx.fillStyle = bgColor;
			ctx.strokeStyle = lineColor;
			ctx.lineWidth = this.params.style.width;
			if (lineDash) ctx.setLineDash(lineDash.split(/\s*,\s*/));
			// fill
			subpixel.fillRect(ctx, x0 + 0.5, 0.5, x1 - x0 - 1, height - 1.5);
			// borders
			subpixel.moveTo(ctx, x0, 0);
			subpixel.lineTo(ctx, x0, height);
			subpixel.moveTo(ctx, x1, 0);
			subpixel.lineTo(ctx, x1, height);
			ctx.stroke();
			ctx.setLineDash([]);
			ctx.closePath();
			// event
			core.eventEmitter.emit(types.INDICATOR_BESTWORST_UPDATE, {
				id,
				dateStart,
				dateStop
			});
		}
	}

	/** Return start/stop dates for highest and lowest percentage change */
	_getDateRanges(data, timeframe) {
		let maxPct = -Infinity;
		let minPct = Infinity;
		const dates = {
			best: {},
			worst: {}
		};
		for (let i = data.length - 1; i >= timeframe; i--) {
			const thisVal = data[i].close;
			const compareVal = data[i - timeframe].close;
			const pct = (thisVal - compareVal) / Math.abs(compareVal);
			if (pct < minPct) {
				minPct = pct;
				dates.worst.start = data[i - timeframe].date;
				dates.worst.stop = data[i].date;
			} else if (pct > maxPct) {
				maxPct = pct;
				dates.best.start = data[i - timeframe].date;
				dates.best.stop = data[i].date;
			}
		}

		return dates;
	}

	/** Adjust the timeframe input for different periods */
	_getTimeframe(timeframe) {
		const period = this.core.params.dataPeriod;
		if (period === 'Year') {
			return Math.round(timeframe / 20 * 12);
		} else if (period === 'Month') {
			return Math.round(timeframe / 20);
		} else if (period === 'Week') {
			return Math.round(timeframe / 7);
		}

		return timeframe;
	}

	/**
	 * @param {object} data Symbol data object
	 * @returns {undefined}
	 */
	render(data) {
		if (!data.price) return;

		const params = this.params;
		const style = params.style;
		let startBest = params.dateStartBest;
		let stopBest = params.dateStopBest;
		let startWorst = params.dateStartWorst;
		let stopWorst = params.dateStopWorst;
		if (params.auto && params.timeframe) {
			const timeframe = this._getTimeframe(this.params.timeframe);
			const { best, worst } = this._getDateRanges(data.price, timeframe);
			startBest = best.start;
			stopBest = best.stop;
			startWorst = worst.start;
			stopWorst = worst.stop;
		}
		// best region
		this._renderRegion(
			'best',
			startBest,
			stopBest,
			style.best.background.color,
			style.best.color,
			style.best.lineDash
		);
		// worst region
		this._renderRegion(
			'worst',
			startWorst,
			stopWorst,
			style.worst.background.color,
			style.worst.color,
			style.worst.lineDash
		);
	}
}

/** Bollinger Bands\u00AE indicator */
class BollingerBandsIndicator extends Indicator {
	/**
	 * Returns the default params for this type of indicator. Overrides abstract
	 * method inherited from Indicator.
	 * @returns {object} - Default parameters object
	 * @property {string} id - Indicator ID
	 * @property {string} name - Indicator name
	 * @property {string} description - Indicator description
	 * @property {object[]} inputs - Indicator inputs
	 * @property {object[]} componentSeries - Indicator component series
	 */
	_getDefaultParams() {
		return {
			id: 'bollinger',
			name: 'Bollinger Bands®',
			description:
				'Bollinger Bands® are created by plotting the standard deviation around a moving average and are expected to capture approximately 95% of all price action. They are also used to highlight periods of high and low volatility.',
			inputs: [
				{ name: 'period', value: 20 },
				{ name: 'standardDeviations', value: 2, max: 10 }
			],
			componentSeries: [
				{ id: 'top' },
				{ id: 'sma', styleRef: 'sma' },
				{ id: 'bottom' }
			]
		};
	}

	/**
	 * @param {object} data - Symbol data object
	 * @return {number} - d3-range minimum
	 */
	getRangeMin(data) {
		return min(data.bollinger, i => +i.bottom);
	}

	/**
	 * @param {object} data - Symbol data object
	 * @return {number} - d3-range maximum
	 */
	getRangeMax(data) {
		return max(data.bollinger, i => +i.top);
	}

	/**
	 * @returns {object}
	 * @property {LineMarker} lineSMA
	 * @property {LineMarker} lineTop
	 * @property {LineMarker} lineBottom
	 * @property {LineMarker} lineFill
	 */
	_getMarkers() {
		return (
			this.markers || {
				lineSMA: new LineMarker(),
				lineTop: new LineMarker(),
				lineBottom: new LineMarker(),
				lineFill: new LineMarker()
			}
		);
	}

	/** @param {object} data - Symbol data object */
	render(data, sessionType) {
		const dataMapTop = this._getDataMap(data.bollinger, 'top');
		const dataMapBottom = this._getDataMap(data.bollinger, 'bottom');
		this.markers.lineFill.renderFillBetween(
			this.panel.rootContext,
			dataMapTop,
			dataMapBottom,
			this.xScale,
			this.yScale,
			this.params.style.background.color
		);
		this._renderLineMarker({
			dataMap: dataMapTop,
			marker: this.markers.lineTop,
			sessionType
		});
		this._renderLineMarker({
			dataMap: dataMapBottom,
			marker: this.markers.lineBottom,
			sessionType
		});
		this._renderLineMarker({
			data: data.bollinger,
			dataKey: 'sma',
			marker: this.markers.lineSMA,
			sessionType
		});
	}
}

/** Bollinger Band Width indicator */
class BollingerBandWidthIndicator extends Indicator {
	/**
	 * Returns the default params for this type of indicator. Overrides abstract
	 * method inherited from Indicator.
	 * @returns {object} - Default parameters object
	 * @property {string} id - Indicator ID
	 * @property {string} name - Indicator name
	 * @property {string} dataSeries - Indicator data series
	 * @property {string} description - Indicator description
	 * @property {object[]} inputs - Indicator inputs
	 */
	_getDefaultParams() {
		return {
			id: 'bollingerbandwidth',
			name: 'Bollinger Band Width',
			dataSeries: 'bandwidth',
			description: 'The Bollinger Band Width indicator measures the difference between the upper and lower Bollinger Bands, providing insights into market volatility by showing how widely or narrowly the bands are spread.',
			inputs: [
				{ name: 'period', value: 20 },
				{ name: 'standardDeviations', value: 2, max: 10 }
			],
			componentSeries: [
				{ id: 'bandwidth' }
			]
		};
	}

	/**
	 * @param {object} data - Symbol data object
	 * @return {number} - d3-range minimum
	 */
	getRangeMin(data) {
		return min(data.bollingerbandwidth, i => +i.bandwidth);
	}

	/**
	 * @param {object} data - Symbol data object
	 * @return {number} - d3-range maximum
	 */
	getRangeMax(data) {
		return max(data.bollingerbandwidth, i => +i.bandwidth);
	}

	/** @param {object} data - Symbol data object */
	render(data, sessionType) {
		this._renderLineMarker({
			data: data.bollingerbandwidth,
			dataKey: 'bandwidth',
			sessionType,
			marker: this.markers.line
		});
	}
}

/** Chaikin's Volatility indicator */
class ChaikinsIndicator extends Indicator {
	/**
	 * Returns the default params for this type of indicator. Overrides abstract
	 * method inherited from Indicator.
	 * @returns {object} - Default parameters object
	 * @property {string} id - Indicator ID
	 * @property {string} name - Indicator name
	 * @property {string} description - Indicator description
	 * @property {object[]} inputs - Indicator inputs
	 * @property {string} dataSeries - Indicator data series
	 */
	_getDefaultParams() {
		return {
			id: 'chaikins',
			name: "Chaikin's Volatility",
			dataSeries: 'chaikinsvolatility',
			description:
				"Chaikin's Volatility indicator relays information about the daily dispersion of prices.",
			inputs: [
				{ name: 'EMAPeriod', value: 10 },
				{ name: 'DifferencePeriod', value: 10 }
			],
			componentSeries: [
				{ id: 'chaikinsvolatility' }
			]
		};
	}

	/**
	 * @param {object} data - Symbol data object
	 * @return {number} - d3-range minimum
	 */
	getRangeMin(data) {
		return min(data.chaikins || [], i => +i.chaikinsvolatility);
	}

	/**
	 * @param {object} data - Symbol data object
	 * @return {number} - d3-range maximum
	 */
	getRangeMax(data) {
		return max(data.chaikins || [], i => +i.chaikinsvolatility);
	}

	/**
	 * @returns {object}
	 * @property {LineMarker} line
	 * @property {LineMarker} lineLow
	 */
	_getMarkers() {
		return {
			line: new LineMarker(),
			lineLow: new LineMarker()
		};
	}

	/** @param {object} data - Symbol data object */
	render(data, sessionType) {
		this._renderLineMarker({
			data: data.chaikins,
			dataKey: 'chaikinsvolatility',
			sessionType,
			marker: this.markers.line
		});
	}
}

/** Band marker */
class BandMarker extends Marker {
	constructor(params, panel = null) {
		super(params, panel);
	}

	getDefaultParams() {
		return {
			labelText: 'Band',
			style: this.panel.core.getStyle('marker.band'),
			valHigh: 100,
			valLow: 0
		};
	}

	// Use the isUpperBand input if you want to draw only a single line for the band, leave empty for both horizontal edges of the rectangle
	render(panel, style = null, isUpperBand = undefined) {
		const ctx = panel.rootContext;
		const yScale = panel.yAxis.primaryScale;
		// Use default styles if none passed in
		if (!style) style = this.params.style;
		const panelLeft = panel.size.padding.left;
		const panelWidth = panel.size.width;
		const panelHeight = panel.size.height;
		const yHigh = yScale(this.params.valHigh);
		const yLow = yScale(this.params.valLow);
		const yMin = Math.floor(Math.max(0, Math.min(yHigh, yLow)));
		const yMax = Math.ceil(Math.min(panelHeight, Math.max(yHigh, yLow)));

		ctx.fillStyle = style.background.color;
		ctx.lineWidth = style.width;
		ctx.strokeStyle = style.color;
		ctx.beginPath();

		// fill rect
		subpixel.rect(ctx, panelLeft, yMin, panelWidth, yMax - yMin);
		ctx.fill();

		// Draw the rectangle's edges
		ctx.beginPath();

		ctx.save();
		if (style.lineDash) ctx.setLineDash(style.lineDash.split(/\s*,\s*/));
		if (isUpperBand === undefined) {
			subpixel.moveTo(ctx, panelLeft, yMin);
			subpixel.lineTo(ctx, panelLeft + panelWidth, yMin);

			subpixel.moveTo(ctx, panelLeft, yMax);
			subpixel.lineTo(ctx, panelLeft + panelWidth, yMax);
		} else if (isUpperBand) {
			// draws the lower edge of the band's rectangle
			subpixel.moveTo(ctx, panelLeft, yMax);
			subpixel.lineTo(ctx, panelLeft + panelWidth, yMax);
		} else {
			// draws the upper edge of the band's rectangle
			subpixel.moveTo(ctx, panelLeft, yMin);
			subpixel.lineTo(ctx, panelLeft + panelWidth, yMin);
		}
		ctx.stroke();
		ctx.restore();

		// Only render labels if there's enough room
		if (yMax - yMin > 18) {
			// label text
			ctx.textBaseline = 'middle';
			ctx.font = panel.getFontStyle(
				style.label.font.weight,
				style.label.font.size,
				style.label.font.family
			);

			// Calculate correct x coordinate
			let x;
			switch (style.label.textAlign) {
				case canvasEnums.textAlign.CENTER:
					x = panelLeft + panelWidth / 2;
					break;
				case canvasEnums.textAlign.LEFT:
					x = panelLeft + style.label.padding.left;
					break;
				case canvasEnums.textAlign.RIGHT:
					x = panelLeft + panelWidth - style.label.padding.right;
					break;
			}

			ctx.fillStyle = style.label.color;
			ctx.textAlign = style.label.textAlign;
			ctx.fillText(this.labelText, x, yMin + (yMax - yMin) / 2);
		}
	}

	/**
	 * The value of the band label
	 * If a translation key exists, translate dynamically on render (CH-2181)
	 * Otherwise use the legacy labelText param
	 */
	get labelText() {
		if (this.params.labelTranslationKey) {
			return translate(
				this.panel.core.locale,
				this.params.labelTranslationKey
			);
		} else {
			return this.params.labelText;
		}
	}
}

/** Price Channel indicator */
class CommodityChannelIndexIndicator extends Indicator {
	/**
	 * Returns the default params for this type of indicator. Overrides abstract
	 * method inherited from Indicator.
	 * @returns {Object} - Default parameters object
	 * @property {String} id - Indicator ID
	 * @property {String} name - Indicator name
	 * @property {String} description - Indicator description
	 * @property {Object[]} inputs - Indicator inputs
	 * @property {Object[]} bands - Bands to display
	 */
	_getDefaultParams() {
		return {
			id: 'commoditychannelindex',
			name: 'Commodity Channel Index',
			description:
				'Commodity Channel Index is a momentum indicator that compares current prices to the average typical price. Above 100 is considered overbought, where upwards momentum is strong but unsustainable. Similarly, below -100 is considered oversold, where downwards momentum is also strong but unsustainable.',
			inputs: [{ name: 'period', value: 20 }],
			bands: [
				{
					labelTranslationKey: 'band.overbought',
					valHigh: 10000, // The indicator should never have values this high
					valLow: 100
				},
				{
					labelTranslationKey: 'band.oversold',
					valHigh: -100,
					valLow: -10000 // sufficiently negative number
				}
			],
			componentSeries: [
				{ id: 'commoditychannelindex' }
			]
		};
	}

	/**
	 * @returns {Object}
	 * @property {LineMarker} line
	 * @property {BandMarker} overbought
	 * @property {BandMarker} zero
	 * @property {BandMarker} oversold
	 */
	_getMarkers() {
		return {
			line: new LineMarker(),
			overbought: new BandMarker(this.params.bands[0], this.panel),
			oversold: new BandMarker(this.params.bands[1], this.panel)
		};
	}

	/** @param {Object} data - Symbol data object */
	render(data, sessionType) {
		this.markers.overbought.render(this.panel, this.params.style.overbought, true);
		this.markers.oversold.render(this.panel, this.params.style.oversold, false);
		this._renderLineMarker({
			data: data[this.params.id],
			sessionType
		});
	}
}

/** Comparative Relative Strength indicator */
class CRSIndicator extends Indicator {
	/**
	 * Returns the default params for this type of indicator. Overrides abstract
	 * method inherited from Indicator.
	 * @returns {Object} - Default parameters object
	 * @property {String} id - Indicator ID
	 * @property {String} name - Indicator name
	 * @property {Object[]} inputs - Indicator inputs
	 * @property {Number} valueMultiplier - Indicator value multiplier. Used to
	 * normalize percent values depending on data - 1 or 100
	 */
	_getDefaultParams() {
		return {
			id: 'crs',
			name: 'Comparative Relative Strength',
			description: 'Comparative Relative Strength (CRS) is a technical analysis indicator that compares the performance of two securities or markets by dividing one security\'s price by the other\'s, helping to identify which one is stronger or weaker relative to each other.',
			inputs: [
				{ name: 'comparisonsymbol', value: '' },
				{ name: 'normalize', value: 1 }
			],
			componentSeries: [
				{ id: 'crs' }
			],
			valueMultiplier: 1
		};
	}

	/**
	 * @param {Object} data - Symbol data object
	 * @return {Number} - d3-range minimum
	 */
	getRangeMin(data) {
		return min(data.crs || [], i => i.crs || -1);
	}

	/**
	 * @param {Object} data - Symbol data object
	 * @return {Number} - d3-range maximum
	 */
	getRangeMax(data) {
		return max(data.crs || [], i => i.crs || 1);
	}

	/**
	 * Renders the indicator
	 * @param {object} data - Symbol data object
	 * @property {string} sessionType - Session type (eg. NormalSession, SessionBreak)
	 */
	render(data, sessionType) {
		this._renderLineMarker({
			data: data[this.params.id],
			sessionType
		});
	}
}

/** Dividend Yield indicator */
class DividendYieldIndicator extends Indicator {
	/**
	 * Returns the default params for this type of indicator. Overrides abstract
	 * method inherited from Indicator.
	 * @returns {object} - Default parameters object
	 * @property {String} id - Indicator ID
	 * @property {String} name - Indicator name
	 * @property {String} description - Indicator description
	 */
	_getDefaultParams() {
		return {
			id: 'dividendyield',
			name: 'Dividend Yield',
			description: 'Dividend Yield is the dividend per share divided by the price per share.',
			componentSeries: [
				{ id: 'dividendyield' }
			]
		};
	}

	/** @param {Object} data - Symbol data object */
	render(data, sessionType) {
		this._renderLineMarker({
			data: data[this.params.id],
			sessionType
		});
	}
}

/** DMI indicator */
class DMIIndicator extends Indicator {


	/**
	 * Returns the default params for this type of indicator. Overrides abstract
	 * method inherited from Indicator.
	 * @returns {Object} - Default parameters object
	 * @property {String} id - Indicator ID
	 * @property {String} name - Indicator name
	 * @property {String} markerType - Render the indicator in different ways
	 * @property {String} description - Indicator description
	 * @property {Object[]} inputs - Indicator inputs
	 */
	_getDefaultParams() {
		return {
			id: 'dmi',
			name: 'DMI',
			description:
				'The DMI is a trend indicator that uses high and low prices as well as the true range to determine whether a security is in an uptrend or a downtrend. The ADX line describes the strength of the trend.',
			inputs: [{ name: 'period', value: 14 }],
			markerType: 'default', // "default", "adx", "adxr"
			componentSeries: [
				{ id: 'adx', styleRef: 'adx' }
			],
			dataSeries: 'adx'
		};
	}

	/**
	 * @param {Object} data - Symbol data object
	 * @return {Number} - d3-range minimum
	 */
	getRangeMin(data) {
		const dataDMI = data.dmi || [];

		if (this.params.markerType === 'adx') {
			return min(dataDMI, i => +i.adx);
		} else if (this.params.markerType === 'adxr') {
			return this._min(
				min(dataDMI, i => +i.adxr),
				min(dataDMI, i => +i.adx)
			);
		}

		return this._min(
			min(dataDMI, i => +i.minusdi),
			min(dataDMI, i => +i.plusdi),
			min(dataDMI, i => +i.adx)
		);
	}

	/**
	 * @param {Object} data - Symbol data object
	 * @return {Number} - d3-range maximum
	 */
	getRangeMax(data) {
		const dataDMI = data.dmi || [];

		if (this.params.markerType === 'adx') {
			return max(dataDMI, i => +i.adx);
		} else if (this.params.markerType === 'adxr') {
			return this._max(
				max(dataDMI, i => +i.adxr),
				max(dataDMI, i => +i.adx)
			);
		}

		return this._max(
			max(dataDMI, i => +i.minusdi),
			max(dataDMI, i => +i.plusdi),
			max(dataDMI, i => +i.adx)
		);
	}

	/**
	 * @returns {Object}
	 * @property {LineMarker} lineADX
	 * @property {LineMarker} lineADXR
	 * @property {LineMarker} lineMinusDI
	 * @property {LineMarker} linePlusDI
	 */
	_getMarkers() {
		return (
			this.markers || {
				lineADX: new LineMarker(),
				lineADXR: new LineMarker(),
				lineMinusDI: new LineMarker(),
				linePlusDI: new LineMarker()
			}
		);
	}

	/** @param {Object} data - Symbol data object */
	render(data, sessionType) {

		if (this.params.markerType === 'adx') {

			this._renderLineMarker({
				data: data.dmi,
				sessionType,
				marker: this.markers.lineADX,
				dataKey: 'adx'
			});

		} else if (this.params.markerType === 'adxr') {

			this._renderLineMarker({
				data: data.dmi,
				sessionType,
				marker: this.markers.lineADX,
				dataKey: 'adx'
			});

			this._renderLineMarker({
				data: data.dmi,
				sessionType,
				marker: this.markers.lineADX,
				dataKey: 'adxr'
			});

		} else {

			this._renderLineMarker({
				data: data.dmi,
				sessionType,
				marker: this.markers.lineMinusDI,
				dataKey: 'minusdi',
				styleKey: 'negative'
			});

			this._renderLineMarker({
				data: data.dmi,
				sessionType,
				marker: this.markers.linePlusDI,
				dataKey: 'plusdi',
				styleKey: 'positive'
			});

			this._renderLineMarker({
				data: data.dmi,
				sessionType,
				marker: this.markers.lineADX,
				dataKey: 'adx'
			});
		}
	}
}

/** EMA indicator */
class EMAIndicator extends Indicator {
	/**
	 * Returns the default params for this type of indicator. Overrides abstract
	 * method inherited from Indicator.
	 * @returns {object} - Default parameters object
	 * @property {string} id - Indicator ID
	 * @property {string} name - Indicator name
	 * @property {string} description - Indicator description
	 * @property {object[]} inputs - Indicator inputs
	 * @property {object[]} componentSeries - Indicator component series
	 */
	_getDefaultParams() {
		return {
			id: 'ema',
			name: 'EMA',
			description:
				'Moving Averages are trend indicators that smooth stock price movements. Comparing the moving averages from multiple time frames can help investors spot changes in trend. Exponential Moving Averages add greater weight to more recent prices.',
			inputs: [{ name: 'period', value: 20 }],
			componentSeries: [{ id: 'ema' }]
		};
	}

	/**
	 * Renders the indicator
	 * @param {object} data - Symbol data object
	 * @property {string} sessionType - Session type (eg. NormalSession, SessionBreak)
	 */
	render(data, sessionType) {
		this._renderLineMarker({
			data: data[this.params.id],
			sessionType
		});
	}
}

/** Region Series Indicator */
class RegionSeriesIndicator extends Indicator {
	/** @param {object} args Custom args*/
	constructor(args) {
		super(args);
		this.regions = [];
		this.state.hover = false;
		this.hoverEventOver = types.INDICATOR_REGIONSERIES_MOUSE_OVER;
		this.hoverEventOut = types.INDICATOR_REGIONSERIES_MOUSE_OUT;
	}

	/**
	 * Returns the default params for this type of indicator. Overrides abstract
	 * method inherited from Indicator.
	 * @returns {object} - Default parameters object
	 * @property {string} id - Indicator ID
	 * @property {string} name - Indicator name
	 * @property {string} description - Indicator description
	 */
	_getDefaultParams() {
		return {
			id: 'regionseries',
			name: 'Region Series',
			description: 'This indicator allows you to add a rectangular region to the chart.',
			noClip: true
		};
	}

	/** @returns {null} */
	getRangeMin() {
		return null;
	}

	/** @returns {null} */
	getRangeMax() {
		return null;
	}

	/** @returns {null} */
	_getMarkers() {
		return {};
	}

	/** @returns {null} */
	_getDataMap() {
		return null;
	}

	isWithin(mouse) {
		let regionWithin;
		this.regions.every(region => {
			if (
				mouse[0] >= region.coords.left &&
				mouse[0] < region.coords.right &&
				mouse[1] - this.panel.size.margin.top >= region.coords.top - this.params.style.margin.hover &&
				mouse[1] - this.panel.size.margin.top < region.coords.bottom + this.params.style.margin.hover
			) {
				regionWithin = region;
				return false;
			}
			return true;
		});
		return regionWithin;
	}

	/**
	 * @param {CanvasRenderingContext2D} ctx - Chart canvas context
	 * @param {number} dateStart - Start date of region
	 * @param {number} dateStop - Stop date of region
	 * @param {string} bgColor - Background color of region
	 * @returns {undefined}
	 */
	_renderRegion(dateStart, dateStop, bgColor) {
		if (dateStart && dateStop) {
			const ctx = this.panel.rootContext;
			const minDate = new Date(dateStart);
			const maxDate = new Date(dateStop);
			const size = this.panel.size;
			const height = Math.min(this.params.style.height, size.height);
			const margin = this.params.style.margin;
			const y0 = this.params.style.align === 'top' ? margin.top : size.height - height - margin.bottom;
			const { core } = this;
			const x0 = this.xScale(core.closestExchangeIndex(minDate));
			const x1 = Math.min(
				this.xScale(core.closestExchangeIndex(maxDate)),
				size.width
			);
			if (x0 < size.width) {
				ctx.beginPath();
				ctx.fillStyle = bgColor;
				// fill
				subpixel.fillRect(ctx, x0, y0, x1 - x0 - 1, height - 2);
				// left border
				ctx.strokeStyle = this.params.style.border.left.color;
				ctx.lineWidth = 1.5;
				ctx.moveTo(x0, y0);
				ctx.lineTo(x0, y0 + height - 1);
				ctx.stroke();
				ctx.closePath();
				if (x1 >= 0) {
					return {
						left: x0,
						right: x1,
						top: y0,
						bottom: y0 + height
					};
				}
			}
		}
	}
}

/** Goldman Sachs Recommendation History Indicator */
class GSRecommendationIndicator extends RegionSeriesIndicator {
	/**
	 * Returns the default params for this type of indicator. Overrides abstract
	 * method inherited from Indicator.
	 * @returns {object} - Default parameters object
	 * @property {string} id - Indicator ID
	 * @property {string} name - Indicator name
	 * @property {string} description - Indicator description
	 */
	_getDefaultParams() {
		return {
			id: 'gsrecommendation',
			name: 'Goldman Sachs Recommendation History',
			description: 'The Goldman Sachs Recommendation History chart indicator represents historical buy, sell, or hold recommendations.',
			inputs: [
				{ name: 'WSODIssue', value: 'auto' },
				{ name: 'ReportType', value: 'Research Report' }
			],
			noClip: true
		};
	}

	/**
	 * Returns a color based on the rating
	 * @returns {string} - Color from styles collection
	 * @property {string} rating - Accumulate / Hold / Reduce
	 */
	_getBackgroundColor(rating) {
		const style = this.params.style;
		switch (rating) {
			case 'Not Rated':
				return style.notrated.background.color;
			case 'CoverageDrop':
			case 'Reduce':
			case 'Sell':
				return style.negative.background.color;
			case 'Accumulate':
			case 'Buy':
				return style.positive.background.color;
			default: return style.neutral.background.color;
		}
	}

	/**
	 * @param {object} data Symbol data object
	 * @returns {undefined}
	 */
	render(data = {}) {
		this.regions = [];
		const rows = data.gsrecommendation;
		if (rows && rows.length) {
			rows.forEach((row, idx) => {
				const dateStart = row.date;
				const dateStop = idx < rows.length - 1 ? rows[idx + 1].date : new Date();
				const rating = row.gsrecommendation;
				const color = this._getBackgroundColor(rating.ProviderRating);
				const coords = this._renderRegion(dateStart, dateStop, color);
				if (coords) {
					this.regions.push({
						coords: this._renderRegion(dateStart, dateStop, color),
						date: row.date,
						providerRating: rating.ProviderRating
					});
				}
			});
		}
	}
}

/** High/Low indicator */
class HighLowIndicator extends Indicator {
	/**
	 * Returns the default params for this type of indicator. Overrides abstract
	 * method inherited from Indicator.
	 * @returns {object} - Default parameters object
	 * @property {string} id - Indicator ID
	 * @property {string} name - Indicator name
	 * @property {string} description - Indicator description
	 * @property {boolean} noClip - Sets default clipping
	 * @property {object[]} inputs - Indicator inputs. Ex { name: 'extentType', value: 'auto' | 'ohlc' | 'close }
	 */
	_getDefaultParams() {
		return {
			id: 'highlow',
			name: 'High / Low',
			description: 'Label the high/low for current chart.',
			noClip: true,
			inputs: [
				{ name: 'extentType', value: 'auto' } // auto, ohlc or close
			]
		};
	}

	/** @returns {null} */
	getRangeMin() {
		return null;
	}

	/** @returns {null} */
	getRangeMax() {
		return null;
	}

	/** @returns {null} */
	_getMarkers() {
		return {};
	}

	/** @returns {null} */
	_getDataMap() {
		return null;
	}

	/**
	 * The top and bottom extents will differ depending on the markertype of the price indicator.
	 * Line/fill charts should look at the close while ohlc/candlestick charts should use low and high.
	 * @returns {string} The type of extent minimum to use
	 */
	_getExtentMin() {
		const extentType = this.params.inputs[0].value;

		if (
			extentType === 'auto' &&
			this.panel.indicators[0].params.id === 'price'
		) {
			return /candle|ohlc/.test(
				this.panel.indicators[0].params.markerType
			)
				? 'low'
				: 'close';
		} else {
			return extentType === 'ohlc' ? 'low' : 'close';
		}
	}

	/**
	 * The top and bottom extents will differ depending on the markertype of the price indicator.
	 * Line/fill charts should look at the close while ohlc/candlestick charts should use low and high.
	 * @returns {string} The type of extent maximum to use
	 */
	_getExtentMax() {
		const extentType = this.params.inputs[0].value;

		if (
			extentType === 'auto' &&
			this.panel.indicators[0].params.id === 'price'
		) {
			return /candle|ohlc/.test(
				this.panel.indicators[0].params.markerType
			)
				? 'high'
				: 'close';
		} else {
			return extentType === 'ohlc' ? 'high' : 'close';
		}
	}

	/**
	 * Gets a reference to the previous close indicator on this panel. This will
	 * allow us to call the _getDimensions method to enable collision detection.
	 * @returns {Indicator} Panel previous close indicator
	 */
	_getPreviousCloseIndicator() {
		for (let x = 0, xLen = this.panel.indicators.length; x < xLen; x++) {
			if (this.panel.indicators[x].params.id === 'previousclose') {
				return this.panel.indicators[x];
			}
		}
	}

	/**
	 * Calculates the extent for the high/low indicator
	 * @param {object} data - Indicator data
	 * @returns {object} Indicator extent
	 * @property {number} minValue - Symbol minimum price
	 * @property {number} maxValue - Symbol maximum price
	 * @property {string} minFormat - Formatted minimum symbol price
	 * @property {string} maxFormat - Formatted maximum symbol price
	 * @property {string} minDate - Date of the symbol's lowest price
	 * @property {string} maxDate - Date of the symbol's highest price
	 * @property {number} maxY - Adjusted y axis maximum
	 * @property {number} minY - Adjusted y axis minimum
	 * @property {number} maxX - Adjusted x axis maximum
	 * @property {number} minX - Adjusted x axis minimum
	 */
	_getExtent(data) {
		const { core } = this;
		let maxValue = -Infinity;
		let minValue = Infinity;
		let maxDate = new Date();
		let minDate = new Date();
		const rectSize = 10;
		const priceData = [...data.price];
		const minDatapoint = this._getExtentMin();
		const maxDatapoint = this._getExtentMax();
		const formatString = this.panel.yAxis
			.getFormatString(this.panel.yAxis.primaryScale, 5)
			.replace(/s/, 'f');
		const limitLeft = this.panel.size.padding.left;
		const limitRight = limitLeft + this.panel.size.width;

		// if previous point is returned, do not include it in the range for high/low indicator CH-3095
		if (this.panel.core.params.returnPreviousPoint) {
			priceData.shift();
		}

		for (let x = 0, xLen = priceData.length; x < xLen; x++) {
			// discard points that are clipped out
			if (
				this.xScale(core.closestExchangeIndex(priceData[x].date)) >
				limitRight
			) {
				continue;
			}
			if (
				this.xScale(core.closestExchangeIndex(priceData[x].date)) <
				limitLeft
			) {
				continue;
			}

			maxValue = Math.max(priceData[x][maxDatapoint], maxValue);
			minValue = Math.min(priceData[x][minDatapoint], minValue);

			if (priceData[x][maxDatapoint] === maxValue) {
				maxDate = priceData[x].date;
			}

			if (priceData[x][minDatapoint] === minValue) {
				minDate = priceData[x].date;
			}
		}

		const maxY = this.yScale(maxValue);
		const minY = this.yScale(minValue);
		const maxX = this.xScale(core.closestExchangeIndex(maxDate));
		const minX = this.xScale(core.closestExchangeIndex(minDate));
		const minFormat = this.panel.yAxis.getFormatValue(minValue, formatString);
		const maxFormat = this.panel.yAxis.getFormatValue(maxValue, formatString);

		return {
			minValue,
			maxValue,
			minFormat,
			maxFormat,
			minDate,
			maxDate,
			maxY: maxY - rectSize - 4,
			minY: minY + 4,
			maxX,
			minX,
			data: {
				high: {
					date: maxDate,
					value: maxValue,
					valueFormatted: maxFormat,
					x: maxX,
					y: maxY
				},
				low: {
					date: minDate,
					value: minValue,
					valueFormatted: minFormat,
					x: minX,
					y: minY
				}
			}
		};
	}

	/**
	 * Determines how text should be aligned based on label width and content
	 * @param {number} val - X axis value
	 * @param {number} width - Text label width
	 * @returns {string} 'left' | 'right' | 'center'
	 */
	_getTextAlignment(val, width) {
		if (val - width <= this.panel.size.padding.left) {
			return 'left';
		} else if (
			val + width >
			this.panel.size.padding.left + this.panel.size.width
		) {
			return 'right';
		}

		return 'center';
	}

	/**
	 * Renders a label on the panel
	 * @param {CanvasRenderingContext2D} ctx - Chart canvas context
	 * @param {string} val - Label value
	 * @param {number} x - X axis location to draw label
	 * @param {number} y - Y axis location to draw laabel
	 * @param {string} color - Label color
	 */
	_drawLabel(ctx, val, x, y, color) {
		this.panel.glowText(
			ctx,
			val,
			subpixel.px(x),
			subpixel.px(y - 1) + 11.5,
			color,
			this.params.style.label.glow.color
		);
	}

	/**
	 * Draws a high low icon on the panel
	 * @param {CanvasRenderingContext2D} ctx - Chart canvas context
	 * @param {string} val - Icon value
	 * @param {number} x - X axis location to draw icon
	 * @param {number} y - Y axis location to draw icon
	 * @param {string} color - Icon color
	 * @param {number} size - Icon size in px
	 */
	_drawIcon(ctx, val, x, y, color, size) {
		ctx.font = '11px Arial';
		ctx.fillStyle = color;
		ctx.strokeStyle = color === '#FFFFFF' ? '#000000' : '#FFFFFF';
		subpixel.fillRect(ctx, x, y, size, size);

		ctx.beginPath();

		// drawing individual lines this way makes for a crisper icon than using antialiased text.
		if (val === 'H') {
			// H
			subpixel.moveTo(ctx, x + 3, y + 2);
			subpixel.lineTo(ctx, x + 3, y + 8);
			subpixel.moveTo(ctx, x + 3, y + 5);
			subpixel.lineTo(ctx, x + 7, y + 5);
			subpixel.moveTo(ctx, x + 7, y + 2);
			subpixel.lineTo(ctx, x + 7, y + 8);
		} else {
			// L
			subpixel.moveTo(ctx, x + 3, y + 2);
			subpixel.lineTo(ctx, x + 3, y + 8);
			subpixel.lineTo(ctx, x + 7, y + 8);
		}

		ctx.stroke();
		ctx.closePath();
	}

	/**
	 * Render High/Low indicator to the panel
	 * @param {CanvasRenderingContext2D} ctx - Chart canvas context
	 * @param {object} data - Symbol data
	 * @param {object} extentData - Extent data from this._getExtent()
	 */
	_renderHighLow(ctx, data, extentData) {
		const { core } = this.panel;
		// if a render listener has been registered, emit and return
		if (core.eventEmitter.listeners('INDICATOR_HIGHLOW_RENDER').length > 0) {
			return core.eventEmitter.emit('INDICATOR_HIGHLOW_RENDER', {
				boundingBox: {
					top: this.panel.size.top,
					right: this.panel.size.left + this.panel.size.width,
					bottom: this.panel.size.top + this.panel.size.height,
					left: this.panel.size.left
				},
				ctx,
				data: extentData.data,
				indicator: this
			});
		}
		let lowY;
		let highY;
		const lowLabel = extentData.minFormat;
		const highLabel = extentData.maxFormat;
		const gap = 3;
		const rectSize = 10;
		const lowWidth = ctx.measureText(highLabel).width + rectSize + gap;
		const highWidth = ctx.measureText(lowLabel).width + rectSize + gap;
		const lowAlign = this._getTextAlignment(extentData.minX, lowWidth / 2);
		const highAlign = this._getTextAlignment(extentData.maxX, highWidth / 2);
		const lowColor = this.params.style.min.color;
		const highColor = this.params.style.max.color;
		let highIntersect = false;
		let lowIntersect = false;
		const prevCloseIndicator = this._getPreviousCloseIndicator();
		const prevCloseDimensions =
				prevCloseIndicator && data.previousclose
					? prevCloseIndicator._getDimensions(
						data.previousclose[data.previousclose.length - 1],
						data.previousclose.length - 1,
						data.previousclose
					)
					: {};

		ctx.textAlign = 'left';
		ctx.textBaseline = 'bottom';

		// high
		const highX =
			highAlign === 'left'
				? extentData.maxX
				: highAlign === 'center'
					? extentData.maxX - lowWidth / 2
					: extentData.maxX - lowWidth;
		highY = extentData.maxY;

		if (prevCloseIndicator) {
			highIntersect = prevCloseIndicator.intersect(
				{ x: highX, y: highY, width: highWidth, height: rectSize },
				prevCloseDimensions
			);

			if (highIntersect) {
				highY = prevCloseDimensions.y - rectSize - 1;
			}
		}

		this._drawIcon(ctx, 'H', highX, highY, highColor, rectSize);
		this._drawLabel(
			ctx,
			extentData.maxFormat,
			highX + rectSize + gap,
			highY + 1,
			highColor
		);

		// low
		const lowX =
			lowAlign === 'left'
				? extentData.minX
				: lowAlign === 'center'
					? extentData.minX - lowWidth / 2
					: extentData.minX - lowWidth;
		lowY = extentData.minY;

		if (prevCloseIndicator) {
			lowIntersect = prevCloseIndicator.intersect(
				{ x: lowX, y: lowY, width: lowWidth, height: rectSize },
				prevCloseDimensions
			);

			if (lowIntersect) {
				lowY = prevCloseDimensions.y + prevCloseDimensions.height + 1;
			}
		}

		this._drawIcon(ctx, 'L', lowX, lowY, lowColor, rectSize);
		this._drawLabel(
			ctx,
			extentData.minFormat,
			lowX + rectSize + gap,
			lowY + 1,
			lowColor
		);
	}

	/**
	 * @param {object} data Symbol data object
	 * @returns {undefined}
	 */
	render(data) {
		if (!data.price) return;

		const extentData = this._getExtent(data);

		if (extentData.minValue === extentData.maxValue) {
			return;
		} else {
			return this._renderHighLow(this.panel.rootContext, data, extentData);
		}
	}
}

/** Horizontal Annotation flag */
class HorizontalAnnotationFlag extends Flag {
	_getStyle(args = {}) {
		const returnStyle = this.panel.core.getStyle('flag.horizontalAnnotation');
		const panelStyle = this.panel.params.style;
		const indicatorStyle = this.indicator ? this.indicator.params.style : {};

		// Use indicator color if it exists
		if (indicatorStyle.color) returnStyle.background.color = indicatorStyle.color;

		// Use fallback values if necessary
		if (!returnStyle.font.family) returnStyle.font.family = panelStyle.yAxis.font.family;
		if (!returnStyle.font.size) returnStyle.font.size = panelStyle.yAxis.font.size;

		return {
			...returnStyle,
			...args.style
		};
	}

	/**
	 * Private method to get flag value
	 * @return {number}
	 * @override
	 * @private
	 */
	getValue() {
		let value;

		if (this.panel.isNormalized) {
			value = this.panel.actualToNormalized(
				this.indicator.handle[0].params.value
			);
		} else if (this.indicator.handle) {
			value = this.indicator.handle[0].params.value;
		}

		if (!this.panel) return null;

		return value;
	}

	/**
	 * @returns The appropriate event emitter types
	 * @type {object}
	 */
	get eventType() {
		return {
			render: 'FLAG_RENDER_HORIZ_ANNOTATION',
			hide: 'FLAG_HIDE_HORIZ_ANNOTATION',
			show: 'FLAG_SHOW_HORIZ_ANNOTATION'
		};
	}
}

/** Horizontal Annotation indicator */
class HorizontalAnnotationIndicator extends Indicator {
	/** @param {object} args Custom args */
	constructor(args) {
		super(args);
		/**
		 * @type {object}
		 * @property {boolean} hover Is the mouse hovering over this indicator
		 */
		this.state = {
			hover: false
		};
	}

	/**
	 * Returns the default params for this type of indicator. Overrides abstract
	 * method inherited from Indicator.
	 * @returns {object} Default parameters object
	 * @property {string} id - Indicator ID
	 * @property {string} name - Indicator name
	 * @property {string} description - Indicator description
	 * @property {string} text - Annotation text
	 * @property {string} textHover - Annotation text to display on hover
	 * @property {number} value - Annotation price value
	 * @property {boolean} noClip - Don't clip the annotation
	 * @property {object[]} inputs - Indicator inputs
	 */
	_getDefaultParams() {
		return {
			id: 'horizontalannotation',
			name: 'Horizontal Annotation',
			description: 'This indicator allows you to add a horizontal line to the chart.',
			text: 'Text',
			textHover: 'Hover Text',
			value: null,
			noClip: true,
			inputs: [{
				name: 'annotationValue',
				value: null
			}],
			style: this.core.getStyle('indicator.horizontalannotation')
		};
	}

	/** @returns {null} */
	getRangeMin() {
		return null;
	}

	/** @returns {null} */
	getRangeMax() {
		return null;
	}

	/** @returns {object} */
	_getMarkers() {
		return {};
	}

	/** @returns {object} */
	_getDataMap() {
		return {};
	}

	/**
	 * Returns true if mouse coordinates intersect indicator
	 * @param {number[]} mouse - Mouse [x, y] coordinates
	 * @returns {boolean}
	 */
	isWithin(mouse) {
		const { panel } = this;
		const mousePanel = [mouse[0], mouse[1] - panel.size.top];
		const style = this.params.style;
		const margin = this.params.style.margin;
		const value = this.getValue();
		const y0 = this.yScale(value);
		const left = panel.size.padding.left + margin;
		const mouseRect = {
			x: mousePanel[0],
			y: mousePanel[1],
			width: 1,
			height: 1
		};
		const collidesBottom = y0 > panel.size.height - margin - style.height;
		let ySnap = Math.min(panel.size.height - margin, Math.max(margin, y0));

		if (collidesBottom) {
			ySnap = panel.size.height - margin - style.height;
		}

		const boundingBox = {
			x: left,
			y: ySnap - style.height / 2,
			width: this.panel.size.width,
			height: style.height
		};

		return this._intersect(boundingBox, mouseRect);
	}

	/**
	 * Returns the value of this annotation
	 * @returns {number}
	 */
	getValue() {
		/** @type {number} */
		this.value = this.params.inputs[0].value || this.params.value;
		this.params.inputs[0].value = this.value; // just to make sure they're in sync

		if (this.panel.isNormalized) {
			return this.panel.actualToNormalized(this.value);
		} else {
			return this.value;
		}
	}

	/** @returns {undefined} */
	render() {
		const { panel } = this;
		const { style } = this.params;
		const { margin } = style;
		const value = this.getValue();
		const y0 = this.yScale(value);
		let left = panel.size.padding.left;
		const right = left + panel.size.width;
		const ctx = panel.rootContext;
		let ySnap = Math.min(panel.size.height - margin, Math.max(margin, y0));


		// create/update/delete flag
		if (!isNaN(value)) {
			if (this.flags['horizontalAnnotation']) {
				this.flags['horizontalAnnotation'].render(value);
			} else {
				this.flags[
					'horizontalAnnotation'
				] = new HorizontalAnnotationFlag({
					panel,
					indicator: this,
					id: 'horizontalAnnotation'
				});
			}
		} else if (this.flags['horizontalAnnotation']) {
			this.flags['horizontalAnnotation'].remove();
			delete this.flags['horizontalAnnotation'];
		} else {
			return; // bad value results in no further attempts to draw
		}

		ctx.beginPath();

		ctx.font = [
			style.text.font.weight,
			`${style.text.font.size}px`,
			style.text.font.family
		].join(' ');

		const textWidth = ctx.measureText(this.params.text).width;
		const textHeight = Math.round(ctx.measureText('M').width) + 0.5; // http://stackoverflow.com/questions/1134586/how-can-you-find-the-height-of-text-on-an-html-canvas;

		let yMid = Math.round(ySnap + style.height / 2 - textHeight / 2);
		const collidesTop = y0 < margin;
		const collidesBottom = y0 > panel.size.height - margin - style.height;

		ctx.textBaseline = 'top';

		// line
		if (!collidesTop && !collidesBottom) {
			ctx.lineWidth = this.params.style.width;
			ctx.strokeStyle = this.params.style.color;
			subpixel.moveTo(ctx, left, y0);
			subpixel.lineTo(ctx, right, y0);
			ctx.stroke();
		}

		// regular text label
		if (this.params.text && this.params.text.length) {
			left += margin;

			if (collidesBottom) {
				ySnap = panel.size.height - margin - style.height;
				yMid = Math.round(ySnap + style.height / 2 - textHeight / 2);
			}

			ctx.beginPath();
			ctx.textBaseline = 'top';

			// rect background
			ctx.fillStyle = this.params.style.text.background.color;
			subpixel.rect(ctx, left, ySnap, style.height, style.height);
			ctx.fill();

			ctx.beginPath();
			ctx.textBaseline = 'top';

			// text
			this.panel.glowText(
				ctx,
				this.params.text,
				left + style.height / 2 - textWidth / 2,
				yMid,
				this.params.style.text.color
			);

			// hover text
			if (this.state.hover) {
				// temp

				left += style.height;

				ctx.font = panel.getFontStyle(
					style.text.hover.font.weight,
					style.text.hover.font.size,
					style.text.hover.font.family
				);

				const textHoverWidth = ctx.measureText(this.params.textHover)
					.width;

				ctx.textBaseline = 'top';

				// rect background
				ctx.beginPath();
				ctx.fillStyle = this.params.style.text.hover.background.color;
				subpixel.rect(
					ctx,
					left,
					ySnap,
					textHoverWidth + 10,
					style.height + 2
				);
				ctx.fill();

				ctx.beginPath();

				left += 5;

				// hover text
				this.panel.glowText(
					ctx,
					this.params.textHover,
					left,
					yMid,
					this.params.style.text.hover.color
				);
			}
		}
	}

	/**
	 * Gets generic object representation of indicator -> Not to be exported
	 * don't save certain indicators // see NXC-581
	 * @returns {undefined}
	 */
	toJSON() {
		return undefined;
	}
}

/** Ichimoku Cloud indicator */
class IchimokuCloudIndicator extends Indicator {
	/**
	 * Returns the default params for this type of indicator. Overrides abstract
	 * method inherited from Indicator.
	 * @returns {object} Default parameters object
	 * @property {string} id - Indicator ID
	 * @property {string} name - Indicator name
	 * @property {string} description - Indicator description
	 * @property {boolean} senkouCloudOnly - Whether to show only the cloud
	 * @property {object[]} inputs - Indicator inputs
	 * @property {object[]} componentSeries - Indicator component series
	 */
	_getDefaultParams() {
		// terminology:
		//
		// chikouspan = lagging span
		// senkou a/b = leading spans
		// kijunsen = base line
		// tenkansen = conversion line

		return {
			id: 'ichimokucloud',
			name: 'Ichimoku Cloud',
			description: 'The Ichimoku Cloud is a comprehensive technical analysis tool that provides information about support and resistance levels, trend direction, and momentum, using multiple averages to create a "cloud" that helps forecast price movements.',
			senkouCloudOnly: false, // sometimes users only want the cloud
			inputs: [
				{ name: 'fastPeriod', value: 9 },
				{ name: 'mediumPeriod', value: 26 },
				{ name: 'slowPeriod', value: 52 },
				{ name: 'displacementCloud', value: 26 }
			],
			componentSeries: [
				{
					id: 'senkouspana',
					styleRef: 'senkou.a'
				},
				{
					id: 'senkouspanb',
					styleRef: 'senkou.b'
				},
				{
					id: 'chikouspan',
					styleRef: 'chikou'
				},
				{
					id: 'kijunsen',
					styleRef: 'kijun'
				},
				{
					id: 'tenkansen',
					styleRef: 'tenkan'
				}
			]
		};
	}

	/**
	 * @param {object} data - Symbol data object
	 * @return {number} - d3-range minimum
	 */
	getRangeMin(data) {
		const vals = [];

		for (const row of data.ichimokucloud) {
			if (!isNaN(row.chikouspan)) {
				vals.push(row.chikouspan);
			}
			if (!isNaN(row.kijunsen)) {
				vals.push(row.kijunsen);
			}
			if (!isNaN(row.senkouspana)) {
				vals.push(row.senkouspana);
			}
			if (!isNaN(row.senkouspanb)) {
				vals.push(row.senkouspanb);
			}
			if (!isNaN(row.tenkansen)) {
				vals.push(row.tenkansen);
			}
		}

		return Math.min.apply(null, vals);
	}

	/**
	 * @param {object} data - Symbol data object
	 * @return {number} - d3-range maximum
	 */
	getRangeMax(data) {
		const vals = [];

		for (const row of data.ichimokucloud) {
			if (!isNaN(row.chikouspan)) {
				vals.push(row.chikouspan);
			}
			if (!isNaN(row.kijunsen)) {
				vals.push(row.kijunsen);
			}
			if (!isNaN(row.senkouspana)) {
				vals.push(row.senkouspana);
			}
			if (!isNaN(row.senkouspanb)) {
				vals.push(row.senkouspanb);
			}
			if (!isNaN(row.tenkansen)) {
				vals.push(row.tenkansen);
			}
		}

		return Math.max.apply(null, vals);
	}

	/**
	 * @returns {object}
	 * @property {LineMarker} line
	 */
	_getMarkers() {
		return (
			this.markers || {
				line: new LineMarker()
			}
		);
	}

	/**
	 * @param {object} data - Indicator data
	 * @param {string} type - Indicator type
	 * @return {number[][]}
	 */
	_getDataMap(data, type) {
		// use an alternate date index (dateIndexChikou) for the chikou series only (CW-3069)
		if (type === 'chikouspan' && data.length > 1) {
			return data
				.map((d, idx) => {
					if (typeof d.dateIndexChikou === 'number') {
						// chikou dates can exceed the left XAxis boundary on some "Max" requests, leading
						// to multiple dateIndexChikou values snapping to 0.  Discard the duplicates to prevent
						// multiple points stacking up at the same position.
						if (
							d.dateIndexChikou > 0 ||
							data[idx + 1].dateIndexChikou !== 0
						) {
							return [d.dateIndexChikou, +d['chikouspan']];
						}
					}
					return false;
				})
				.filter(row => row !== undefined); // trim out undefined's
		}

		// all other series
		let prevDateIndex = null;
		return data
			.map(d => {
				// don't allow more than one datapoint per dateIndex.  this can happen
				// with senkou datasets on monthly frequencies, which plot into the future
				// and sometimes exceed the date of the right-hand edge of the XAxis.
				if (!prevDateIndex || d.dateIndex !== prevDateIndex) {
					prevDateIndex = d.dateIndex;
					return [d.dateIndex, +d[type]];
				}
				return false;
			})
			.filter(row => row !== undefined); // trim out undefined's
	}

	/**
	 * Gets the regions to clip
	 * @param {number} xScale
	 * @param {array} dataUpper - Upper region data
	 * @param {array} dataLower - Lower region data
	 * @returns {array[]}
	 */
	_getClipRegions(xScale, dataUpper, dataLower) {
		const regions = [];
		let newRegion = {};
		let x = 0;

		mainLoop: for (
			x = 0;
			x < Math.min(dataUpper.length, dataLower.length);
			x++
		) {
			if (dataUpper[x][1] >= dataLower[x][1]) {
				// begin new region
				newRegion = [x, x];

				for (x++; x < dataUpper.length; x++) {
					if (dataUpper[x][1] >= dataLower[x][1]) {
						// widen region by 1
						newRegion[1] = x;

						if (x === dataUpper.length - 1) {
							// reached end of data, finalize region
							regions.push(newRegion);
						}
					} else {
						// line crossed back under, finalize region
						regions.push(newRegion);
						continue mainLoop;
					}
				}
			}
		}

		return regions;
	}

	/**
	 * Returns the x coordinate for a line cross
	 * @param {number} dateIndex
	 */
	_getX(dateIndex) {
		return this.xScale(dateIndex);
	}

	/**
	 * Returns the y coordinate for a line cross
	 * @param {number} dateIndex
	 */
	_getY(dateIndex) {
		return this.yScale(dateIndex);
	}

	/**
	 * Renders clip regions
	 * @param {CanvasRenderingContext2D} ctx
	 * @param {number[][]} regions - Array of clip regions
	 * @param {number[][]} dataUpper - Upper region data
	 * @param {number[][]} dataLower - Lower region data
	 * @param {boolean} isPos
	 */
	_renderClipRegions(ctx, regions, dataUpper, dataLower, isPos) {
		const self = this;
		let currTop = [];
		let currBot = [];
		const height = this.panel.size.height;
		let neighborIdx = 0;
		let neighborUpper = 0;
		let neighborLower = 0;
		let xIntersect = 0;
		let left = 0;
		let right = 0;
		let x1 = 0;
		let y1 = 0;
		let x2 = 0;
		let y2 = 0;
		let x3 = 0;
		let y3 = 0;
		let x4 = 0;
		let y4 = 0;

		ctx.beginPath();

		regions.forEach(r => {
			// find left edge of region for top and bottom
			currTop = dataUpper[r[0]];
			currBot = dataLower[r[0]];

			// find preceding index
			neighborIdx = r[0] - 1;

			// clamp to left edge of data if needed
			if (typeof dataUpper[neighborIdx] === 'undefined') {
				neighborIdx = r[0];
			}

			// find left neighbor
			neighborUpper = dataUpper[neighborIdx];
			neighborLower = dataLower[neighborIdx];

			// get xy coords of crossing lines
			x1 = self._getX(neighborUpper[0]);
			y1 = self._getY(neighborUpper[1]);
			x2 = self._getX(currTop[0]);
			y2 = self._getY(currTop[1]);
			x3 = self._getX(neighborLower[0]);
			y3 = self._getY(neighborLower[1]);
			x4 = self._getX(currBot[0]);
			y4 = self._getY(currBot[1]);

			// find x intersection with left neighbor
			xIntersect = self._getIntersection(x1, y1, x2, y2, x3, y3, x4, y4);

			// set new region left coord
			left = xIntersect === null ? x2 : xIntersect;

			// find right edge of region for top and bottom
			currTop = dataUpper[r[1]];
			currBot = dataLower[r[1]];

			// find following index
			neighborIdx = r[1] + 1;

			// clamp to right edge of data if needed
			if (typeof dataUpper[neighborIdx] === 'undefined') {
				neighborIdx = r[1];
			}

			// find right neighbor
			neighborUpper = dataUpper[neighborIdx];
			neighborLower = dataLower[neighborIdx];

			// get xy coords of crossing lines
			x1 = self._getX(currTop[0]);
			y1 = self._getY(currTop[1]);
			x2 = self._getX(neighborUpper[0]);
			y2 = self._getY(neighborUpper[1]);
			x3 = self._getX(currBot[0]);
			y3 = self._getY(currBot[1]);
			x4 = self._getX(neighborLower[0]);
			y4 = self._getY(neighborLower[1]);

			// find x intersection with right neighbor
			xIntersect = self._getIntersection(x1, y1, x2, y2, x3, y3, x4, y4);

			// set new region right coord
			right = xIntersect === null ? x1 : xIntersect;

			// draw rect
			ctx.rect(left, 0, right - left, height);
		});

		ctx.clip();
	}

	/**
	 * Returns the intersection point
	 * @param {number} x1 - Point 1 x coordinate
	 * @param {number} y1 - Point 1 y coordinate
	 * @param {number} x2 - Point 2 x coordinate
	 * @param {number} y2 - Point 2 y coordinate
	 * @param {number} x3 - Point 3 x coordinate
	 * @param {number} y3 - Point 3 y coordinate
	 * @param {number} x4 - Point 4 x coordinate
	 * @param {number} y4 - Point 4 y coordinate
	 * @return {number}
	 */
	_getIntersection(x1, y1, x2, y2, x3, y3, x4, y4) {
		let ua = 0.0;
		const denom = (y4 - y3) * (x2 - x1) - (x4 - x3) * (y2 - y1);

		if (denom === 0) {
			return null;
		}

		ua = ((x4 - x3) * (y1 - y3) - (y4 - y3) * (x1 - x3)) / denom;

		return x1 + ua * (x2 - x1);
	}

	/** @param {object} data Symbol data object */
	render(data) {
		const ctx = this.panel.rootContext;
		const xScale = this.xScale;
		const yScale = this.yScale;
		let dataSenkouA = this._getDataMap(
			data.ichimokucloud || [],
			'senkouspana'
		);
		let dataSenkouB = this._getDataMap(
			data.ichimokucloud || [],
			'senkouspanb'
		);
		const dataChikou = this._getDataMap(
			data.ichimokucloud || [],
			'chikouspan'
		);
		const dataKijun = this._getDataMap(data.ichimokucloud, 'kijunsen');
		const dataTenkan = this._getDataMap(data.ichimokucloud, 'tenkansen');

		// clean up empty points
		dataSenkouA = dataSenkouA.filter(row => !isNaN(row[1]));
		dataSenkouB = dataSenkouB.filter(row => !isNaN(row[1]));

		// trim SenkouA to match SenkouB
		dataSenkouA = dataSenkouA.splice(
			dataSenkouA.length - dataSenkouB.length,
			dataSenkouA.length
		);

		// get clip regions
		const regionsPos = this._getClipRegions(xScale, dataSenkouA, dataSenkouB);
		const regionsNeg = this._getClipRegions(xScale, dataSenkouB, dataSenkouA);

		// neg fill
		ctx.save();

		// clips for neg only
		this._renderClipRegions(ctx, regionsNeg, dataSenkouA, dataSenkouB);

		// neg fill
		this.markers.line.renderFillBetween(
			ctx,
			dataSenkouA,
			dataSenkouB,
			xScale,
			yScale,
			this.params.style.senkou.negative.color
		);

		// restore context
		ctx.restore();

		ctx.save();

		// clips for pos only
		this._renderClipRegions(ctx, regionsPos, dataSenkouA, dataSenkouB, true);

		// pos fill
		this.markers.line.renderFillBetween(
			ctx,
			dataSenkouA,
			dataSenkouB,
			xScale,
			yScale,
			this.params.style.senkou.positive.color
		);

		// restore context
		ctx.restore();

		// dataSenkouB line
		this.markers.line.render(
			ctx,
			dataSenkouB,
			xScale,
			yScale,
			this.params.style.senkou.b.color,
			this.params.style.senkou.b.width,
			this.params.style.senkou.b.lineDash
		);

		// dataSenkouA line
		this.markers.line.render(
			ctx,
			dataSenkouA,
			xScale,
			yScale,
			this.params.style.senkou.a.color,
			this.params.style.senkou.a.width,
			this.params.style.senkou.a.lineDash
		);

		if (!this.params.senkouCloudOnly) {
			// kijun line
			this.markers.line.render(
				ctx,
				dataKijun,
				xScale,
				yScale,
				this.params.style.kijun.color,
				this.params.style.kijun.width,
				this.params.style.kijun.lineDash
			);

			// tenkan line
			this.markers.line.render(
				ctx,
				dataTenkan,
				xScale,
				yScale,
				this.params.style.tenkan.color,
				this.params.style.tenkan.width,
				this.params.style.tenkan.lineDash
			);

			// chikou line
			this.markers.line.render(
				ctx,
				dataChikou,
				xScale,
				yScale,
				this.params.style.chikou.color,
				this.params.style.chikou.width,
				this.params.style.chikou.lineDash
			);
		}
	}
}

/** Linear Regression indicator */
class LinearRegressionIndicator extends Indicator {
	/**
	 * Returns the default params for this type of indicator. Overrides abstract
	 * method inherited from Indicator.
	 * @returns {object} - Default parameters object
	 * @property {string} id - Indicator ID
	 * @property {string} name - Indicator name
	 * @property {string} description - Indicator description
	 * @property {soolean} noClip - Sets to not clip by default
	 */
	_getDefaultParams() {
		return {
			componentSeries: [{ id: 'linearregression' }],
			description:
				'The Linear Regression Indicator function returns the current value of a linear regression line of a field to time over a given number of periods.',
			id: 'linearregression',
			name: 'Linear Regression',
			noClip: true
		};
	}

	/** @returns {null} */
	getRangeMin() {
		return null;
	}

	/** @returns {null} */
	getRangeMax() {
		return null;
	}

	/** @returns {object} */
	_getMarkers() {
		return {};
	}

	/**
	 * Linear Regression calculation
	 * see https://svn.wsod.local/svn/wsod/netframework/framework_repository/3.0-stable/Math/TechnicalIndicators.cs
	 * @param {object} data Indicator data
	 * @returns {object} Regression data object
	 */
	getLinearRegression(data = []) {
		let count = 1;
		let sumX = 0;
		let sumX2 = 0;
		let sumY = 0;
		let sumXY = 0;
		let temp = 0;
		const regressionData = {};
		const period = data.length;
		let seriesId = 'close';

		// use a different series id if based on a parent indicator
		if (
			this.params.parentUid ||
			this.params.parentUID
		) {
			const parentIndicator = this.core.getIndicatorByUid(
				this.params.parentUid ||
				this.params.parentUID
			);
			seriesId =
				parentIndicator.params.dataSeries || parentIndicator.params.id;
		}

		for (let index = 0; count <= period; index++, count++) {
			sumX += count;
			sumX2 += count * count;
			temp = data[index][seriesId];
			sumY += temp;
			sumXY += count * temp;
		}

		const d = period * sumX2 - sumX * sumX;
		const m = (period * sumXY - sumX * sumY) / d; // Slope
		const b = (sumX2 * sumY - sumX * sumXY) / d; // Intercept

		regressionData['Beginning'] = b + m * 1;
		regressionData['End'] = b + m * period;
		regressionData['Slope'] = m;

		return regressionData;
	}

	/** @param {object} data Symbol data object */
	render(data) {
		const lrData = this.getLinearRegression(data.linearregression);
		const ctx = this.panel.rootContext;
		const size = this.panel.size;
		const style = this.params.style;

		// Calculate coordinates for line
		const x0 = size.padding.left;
		const y0 = this.yScale(lrData.Beginning);
		const x1 = size.padding.left + size.width;
		const y1 = this.yScale(lrData.End);

		if (!isNaN(y0) && !isNaN(y1)) {
			// Only clip the line if it would go past the panel height
			if (y0 > size.height || y1 > size.height) {
				this.panel.clipPanel();
			}
			ctx.beginPath();
			ctx.strokeStyle = style.color;
			ctx.lineWidth = style.width;
			if (style.lineDash) {
				ctx.setLineDash(style.lineDash.split(/\s*,\s*/));
			}
			subpixel.moveTo(ctx, x0, y0);
			subpixel.lineTo(ctx, x1, y1);
			ctx.stroke();
			ctx.setLineDash([]);
		}
	}
}

/** MACD indicator */
class MACDIndicator extends Indicator {
	/**
	 * Returns the default params for this type of indicator. Overrides abstract
	 * method inherited from Indicator.
	 * @returns {object} - Default parameters object
	 * @property {string} id - Indicator ID
	 * @property {string} name - Indicator name
	 * @property {string} description - Indicator description
	 * @property {object[]} inputs - Indicator inputs
	 * @property {string} markerType - Indicator marker type - 'default' | 'histogram'
	 */
	_getDefaultParams() {
		return {
			id: 'macd',
			name: 'MACD',
			description:
				'The MACD is the difference between 2 exponential moving averages and is a trend and momentum indicator. It is normally considered bullish when the MACD line is above the signal line and more so when both are above the 0-line.',
			inputs: [
				{ name: 'fastperiod', value: 12 },
				{ name: 'slowperiod', value: 26 },
				{ name: 'smoothing', value: 9 }
			],
			markerType: 'default', // "default", "histogram"
			componentSeries: [
				{ id: 'macd' },
				{ id: 'ema', styleRef: 'ema' }
			]
		};
	}

	/**
	 * @param {object} data - Symbol data object
	 * @return {number} - d3-range minimum
	 */
	getRangeMin(data) {
		if (this.params.markerType === 'histogram') {
			const dataCopy = deepClone(data);
			const panelRange = this.panel.xAxis.primaryScale.domain();

			// Remove first element when returnPreviousPoint is selected so panel size is not affected
			if (this.panel.core.params.returnPreviousPoint) {
				dataCopy.macd.shift();
			}

			//Remove last element if it is out of view in panel so range size is not affected
			if (dataCopy.macd[dataCopy.macd.length - 1].dateIndex > panelRange[1]) {
				dataCopy.macd.pop();
			}

			return this._min(
				min(dataCopy.macd || [], i => i.divergence),
				0
			);
		}

		return this._min(
			min(data.macd || [], i => +i.ema),
			min(data.macd || [], i => +i.divergence),
			min(data.macd || [], i => +i.macd),
			0
		);
	}

	/**
	 * @param {object} data - Symbol data object
	 * @return {number} - d3-range maximum
	 */
	getRangeMax(data) {
		if (this.params.markerType === 'histogram') {
			const dataCopy = deepClone(data);
			const panelRange = this.panel.xAxis.primaryScale.domain();

			// Remove first element when returnPreviousPoint is selected so panel size is not affected
			if (this.panel.core.params.returnPreviousPoint) {
				dataCopy.macd.shift();
			}

			//Remove last element if it is out of view in panel so range size is not affected
			if (dataCopy.macd[dataCopy.macd.length - 1].dateIndex > panelRange[1]) {
				dataCopy.macd.pop();
			}

			return this._max(
				max(dataCopy.macd || [], i => i.divergence),
				0
			);
		}

		return this._max(
			max(data.macd || [], i => +i.ema),
			max(data.macd || [], i => +i.divergence),
			max(data.macd || [], i => +i.macd),
			0
		);
	}

	/**
	 * @returns {object}
	 * @property {LineMarker} lineEMA
	 * @property {LineMarker} lineMACD
	 * @property {BarMarker} barDivUp
	 * @property {BarMarker} barDivDown
	 */
	_getMarkers() {
		return (
			this.markers || {
				lineEMA: new LineMarker(),
				lineMACD: new LineMarker(),
				barDivUp: new BarMarker(),
				barDivDown: new BarMarker()
			}
		);
	}

	/**
	 * @param {object} data - Indicator data
	 * @param {string} type - indicator type
	 * @return {array[]}
	 */
	_getDataMap(data = [], type = null) {
		switch (type) {
			case 'macd':
				return data.map(d => [d.dateIndex, +d.macd]);

			case 'ema':
				return data.map(d => [d.dateIndex, +d.ema]);

			case 'divUp':
				return data.map(d => [d.dateIndex, d.divergence >= 0 ? d.divergence : 0]);

			case 'divDown':
				return data.map(d => [d.dateIndex, d.divergence < 0 ? d.divergence : 0]);

			default:
				return [];
		}
	}

	/** @param {object} data Symbol data object */
	render(data, sessionType) {
		const ctx = this.panel.rootContext;
		const x = this.xScale;
		const y = this.yScale;
		const style = this.params.style;

		this.markers.barDivUp.render(
			this.panel,
			ctx,
			this._getDataMap(data.macd, 'divUp'),
			x,
			y,
			style.positive.color
		);

		this.markers.barDivDown.render(
			this.panel,
			ctx,
			this._getDataMap(data.macd, 'divDown'),
			x,
			y,
			style.negative.color
		);

		if (this.params.markerType !== 'histogram') {

			this._renderLineMarker({
				data: data.macd,
				sessionType,
				marker: this.markers.lineEMA,
				dataKey: 'ema'
			});

			this._renderLineMarker({
				data: data.macd,
				sessionType,
				marker: this.markers.lineMACD,
				dataKey: 'macd'
			});
		}

		// zero line
		ctx.beginPath();
		const yZero = subpixel.px(y(0.0));
		ctx.strokeStyle = style.zero.color;
		ctx.lineWidth = style.zero.width;
		if (style.zero.lineDash) {
			ctx.setLineDash(style.zero.lineDash.split(/\s*,\s*/));
		}
		ctx.moveTo(this.panel.size.padding.left, yZero);
		ctx.lineTo(
			this.panel.size.padding.left + this.panel.size.width,
			yZero
		);
		ctx.stroke();
		ctx.setLineDash([]);
	}
}

/** MAE indicator */
class MAEIndicator extends Indicator {
	/**
	 * Returns the default params for this type of indicator. Overrides abstract
	 * method inherited from Indicator.
	 * @returns {object} - Default parameters object
	 * @property {string} id - Indicator ID
	 * @property {string} name - Indicator name
	 * @property {string} description - Indicator description
	 * @property {object[]} inputs - Indicator inputs
	 * @property {object[]} componentSeries - Indicator component series
	 */
	_getDefaultParams() {
		return {
			id: 'mae',
			name: 'Moving Average Envelope',
			description:
				"Moving Average Envelopes provide context around a security's recent price range.",
			inputs: [
				{ name: 'period', value: 20 },
				{ name: 'lowfactor', value: 0.95, max: 10 },
				{ name: 'highfactor', value: 1.05, max: 10 },
				{ name: 'averagetype', value: averageTypes.SMA }
			],
			componentSeries: [{ id: 'maelow' }, { id: 'maehigh' }]
		};
	}

	/**
	 * @param {object} data - Symbol data object
	 * @return {number} - d3-range minimum
	 */
	getRangeMin(data) {
		return min(data.mae || [], i => +i.maelow);
	}

	/**
	 * @param {object} data - Symbol data object
	 * @return {number} - d3-range maximum
	 */
	getRangeMax(data) {
		return max(data.mae || [], i => +i.maehigh);
	}

	/**
	 * @returns {object}
	 * @property {LineMarker} lineUp
	 * @property {LineMarker} lineDown
	 * @property {LineMarker} lineFill
	 */
	_getMarkers() {
		return (
			this.markers || {
				lineUp: new LineMarker(),
				lineDown: new LineMarker(),
				lineFill: new LineMarker()
			}
		);
	}

	/** @param {object} data Symbol data object */
	render(data, sessionType) {
		const ctx = this.panel.rootContext;
		const dataUp = this._getDataMap(data.mae, 'maehigh');
		const dataDown = this._getDataMap(data.mae, 'maelow');

		this.markers.lineFill.renderFillBetween(
			ctx,
			dataUp,
			dataDown,
			this.xScale,
			this.yScale,
			this.params.style.background.color
		);

		this._renderLineMarker({
			dataMap: dataUp,
			sessionType,
			marker: this.markers.lineUp
		});

		this._renderLineMarker({
			dataMap: dataDown,
			sessionType,
			marker: this.markers.lineDown
		});
	}
}

/** Mass index indicator */
class MassIndexIndicator extends Indicator {
	/**
	 * Returns the default params for this type of indicator. Overrides abstract
	 * method inherited from Indicator.
	 * @returns {object} - Default parameters object
	 * @property {string} id - Indciator ID
	 * @property {string} name - Indicator name
	 * @property {string} description - Indicator description
	 * @property {object[]} inputs - Indicator inputs
	 * @property {object[]} extraInputs - Extra indicator inputs
	 */
	_getDefaultParams() {
		return {
			id: 'massindex',
			name: 'Mass Index',
			description: 'The Mass Index helps to identify reversals of trend.',
			inputs: [{ name: 'period', value: 25 }],
			extraInputs: [
				{ name: 'setup', value: 27 },
				{ name: 'trigger', value: 26.5 }
			],
			componentSeries: [
				{ id: 'massindex' }
			]
		};
	}

	/**
	 * @param {object} data - Symbol data object
	 * @return {number} - d3-range minimum
	 */
	getRangeMin(data) {
		return this._min(
			min(data.massindex || [], i => +i.massindex),
			this.getInput('setup'),
			this.getInput('trigger')
		);
	}

	/**
	 * @param {object} data - Symbol data object
	 * @return {number} - d3-range maximum
	 */
	getRangeMax(data) {
		return this._max(
			max(data.massindex || [], i => +i.massindex),
			this.getInput('setup'),
			this.getInput('trigger')
		);
	}

	/** @param {object} data Symbol data object */
	render(data, sessionType) {
		const { panel } = this;
		const ctx = panel.rootContext;
		const x = panel.size.padding.left;
		const w = panel.size.width;
		const style = this.params.style;

		this._renderLineMarker({
			data: data[this.params.id],
			sessionType
		});

		// setup line
		const ySetup = this.yScale(this.getInput('setup'));
		ctx.lineWidth = style.setup.width;
		ctx.strokeStyle = style.setup.color;
		if (style.setup.lineDash) {
			ctx.setLineDash(style.setup.lineDash.split(/\s*,\s*/));
		}
		ctx.beginPath();
		subpixel.moveTo(ctx, x, ySetup);
		subpixel.lineTo(ctx, x + w, ySetup);
		ctx.stroke();
		ctx.setLineDash([]);

		// trigger line
		const yTrigger = this.yScale(this.getInput('trigger'));
		ctx.lineWidth = style.trigger.width;
		ctx.strokeStyle = style.trigger.color;
		if (style.trigger.lineDash) {
			ctx.setLineDash(style.trigger.lineDash.split(/\s*,\s*/));
		}
		ctx.beginPath();
		subpixel.moveTo(ctx, x, yTrigger);
		subpixel.lineTo(ctx, x + w, yTrigger);
		ctx.stroke();
		ctx.setLineDash([]);
	}
}

/** EMA indicator */
class MomentumIndicator extends Indicator {
	/**
	 * Returns the default params for this type of indicator. Overrides abstract
	 * method inherited from Indicator.
	 * @returns {object} - Default parameters object
	 * @property {string} id - Indicator ID
	 * @property {string} name - Indicator name
	 * @property {string} description - Indicator description
	 * @property {object[]} inputs - Indicator inputs
	 * @property {number} fillToValue - Value to fill to with background color
	 */
	_getDefaultParams() {
		return {
			id: 'momentum',
			name: 'Momentum',
			description: 'The Momentum chart indicator measures the rate of change or speed of price movement of a financial instrument, signaling whether the price is moving up or down and how strongly, by comparing current prices to past prices.',
			inputs: [{ name: 'period', value: 20 }],
			fillToValue: 100,
			componentSeries: [
				{ id: 'momentum' }
			]
		};
	}

	/** @param {object} data Symbol data object */
	render(data, sessionType) {
		const style = this.params.style;
		// fill
		this.markers.line.renderFill(
			this.panel.rootContext,
			this._getDataMap(data.momentum),
			this.panel.size.padding.top + this.panel.size.height,
			this.xScale,
			this.yScale,
			style.background.color,
			this.params.fillToValue
		);

		// line
		this._renderLineMarker({
			data: data[this.params.id],
			sessionType
		});
	}
}

/** Money Flow indicator */
class MoneyFlowIndicator extends Indicator {
	/**
	 * Returns the default params for this type of indicator. Overrides abstract
	 * method inherited from Indicator.
	 * @returns {object} - Default parameters object
	 * @property {string} id - Indicator ID
	 * @property {string} name - Indicator name
	 */
	_getDefaultParams() {
		return {
			id: 'moneyflow',
			name: 'Money Flow',
			description: 'The Money Flow Indicator measures buying and selling pressure by combining price and volume data, helping traders assess whether an asset is experiencing inflow (accumulation) or outflow (distribution) of capital.',
			componentSeries: [
				{ id: 'moneyflow' }
			]
		};
	}

	/** @param {object} data Symbol data object */
	render(data, sessionType) {
		this._renderLineMarker({
			data: data[this.params.id],
			sessionType
		});
	}
}

/** Money Flow Index indicator */
class MoneyFlowIndexIndicator extends Indicator {
	/**
	 * Returns the default params for this type of indicator. Overrides abstract
	 * method inherited from Indicator.
	 * @returns {object} - Default parameters object
	 * @property {string} id - Indicator ID
	 * @property {string} name - Indicator name
	 * @property {string} description - Indicator description
	 * @property {object[]} inputs - Indicator inputs
	 * @property {object[]} bands - Indicator bands
	 */
	_getDefaultParams() {
		return {
			id: 'moneyflowindex',
			name: 'Money Flow Index',
			description:
				'The Money Flow Index describes the rate at which money is flowing into or out of a security. It is similar to the RSI but includes volume within the calculation in addition to price.',
			inputs: [{ name: 'period', value: 15 }],
			bands: [
				{
					labelTranslationKey: 'band.overbought',
					valHigh: 100,
					valLow: 80
				},
				{
					labelTranslationKey: 'band.oversold',
					valHigh: 20,
					valLow: 0
				}
			],
			componentSeries: [
				{ id: 'moneyflowindex' }
			]
		};
	}

	/** @return {number} Minimum range (default 0)*/
	getRangeMin() {
		return 0;
	}

	/** @return {number} Maximum range (default 100) */
	getRangeMax() {
		return 100;
	}

	/**
	 * @return {object}
	 * @property {LineMarker} line
	 * @property {BandMarker} overbought
	 * @property {BandMarker} oversold
	 */
	_getMarkers() {
		return {
			line: new LineMarker(),
			overbought: new BandMarker(this.params.bands[0], this.panel),
			oversold: new BandMarker(this.params.bands[1], this.panel)
		};
	}

	/** @param {object} data Symbol data object */
	render(data, sessionType) {
		this.markers.overbought.render(this.panel, this.params.style.overbought);
		this.markers.oversold.render(this.panel, this.params.style.oversold);
		this._renderLineMarker({
			data: data[this.params.id],
			sessionType
		});
	}
}

/** Normalized Relative Volume indicator */
class NRVIndicator extends Indicator {
	/**
	 * Returns the default params for this type of indicator. Overrides abstract
	 * method inherited from Indicator.
	 * @returns {object} - Default parameters object
	 * @property {string} id - Indicator ID
	 * @property {string} name - Indicator name
	 * @property {string} description - Indicator description
	 * @property {string} markerType - Indicator marker type - 'default' | 'posneg'
	 */
	_getDefaultParams() {
		return {
			id: 'nrv',
			name: 'Normalized Relative Volume',
			description:
				'Relative volume compared to an index, and normalized using average historical data',
			inputs: [
				{ name: 'comparisonsymbol', value: '593933' },
				{ name: 'longperiod', value: 125 },
				{ name: 'shortperiod', value: 1 }
			],
			markerType: 'posneg', // default, posneg
			valueMultiplier: 1
		};
	}

	/**
	 * @param {object} data - Symbol data object
	 * @return {number} - d3-range minimum
	 */
	getRangeMin(data) {
		const dataCopy = deepClone(data);
		const panelRange = this.panel.xAxis.primaryScale.domain();

		// Remove first element when returnPreviousPoint is selected so panel size is not affected
		if (this.panel.core.params.returnPreviousPoint) {
			dataCopy.nrv.shift();
		}

		//Remove last element if it is out of view in panel so range size is not affected
		if (dataCopy.nrv[dataCopy.nrv.length - 1].dateIndex > panelRange[1]) {
			dataCopy.nrv.pop();
		}

		return this._min(
			0,
			min(dataCopy.nrv || [], i => +i.nrv)
		);
	}

	/**
	 * @param {object} data - Symbol data object
	 * @return {number} - d3-range maximum
	 */
	getRangeMax(data) {
		// Remove first and last elements so panel size is not affected
		const dataCopy = deepClone(data);
		const panelRange = this.panel.xAxis.primaryScale.domain();

		// Remove first element when returnPreviousPoint is selected so panel size is not affected
		if (this.panel.core.params.returnPreviousPoint) {
			dataCopy.nrv.shift();
		}

		//Remove last element if it is out of view in panel so range size is not affected
		if (dataCopy.nrv[dataCopy.nrv.length - 1].dateIndex > panelRange[1]) {
			dataCopy.nrv.pop();
		}

		return this._max(
			0,
			max(dataCopy.nrv || [], i => +i.nrv)
		);
	}

	/**
	 * @return {object}
	 * @property {BarMarker} bar
	 */
	_getMarkers() {
		return { bar: new BarMarker() };
	}

	/**
	 * Returns pos/neg/unchanged volume data based against the price dataset
	 * @param {object[]} data - Indicator data
	 * @param {object[]} dataPrice - Symbol price data
	 * @param {string} type - Type of volume data - 'pos' | 'neg' | 'unch'
	 */
	_getDataMapPosNeg(data, dataPrice, type) {
		// return pos/neg/unchanged volume data based against the price dataset
		switch (type) {
			case 'pos':
				return data.map((d, x) => {
					return [
						d.dateIndex,
						typeof dataPrice[x].diff === 'number' &&
						dataPrice[x].diff > 0
							? +d.nrv
							: 0
					];
				});

			case 'neg':
				return data.map((d, x) => {
					return [
						d.dateIndex,
						typeof dataPrice[x].diff === 'number' &&
						dataPrice[x].diff < 0
							? +d.nrv
							: 0
					];
				});

			case 'unch':
				return data.map((d, x) => {
					return [
						d.dateIndex,
						dataPrice[x].diff === 0 ? +d.nrv : 0
					];
				});

			default: return null;
		}
	}

	/** @param {object} data Symbol data object */
	render(data) {
		if (
			this.params.markerType === 'posneg' &&
			data.price &&
			data.nrv &&
			data.price.length &&
			data.nrv.length &&
			data.nrv[0].date - data.price[0].date === 0
		) {
			this.markers.bar.render(
				this.panel,
				this.panel.rootContext,
				this._getDataMapPosNeg(data.nrv, data.price, 'pos'),
				this.xScale,
				this.yScale,
				this.params.style.positive.color,
				'bottom'
			);

			this.markers.bar.render(
				this.panel,
				this.panel.rootContext,
				this._getDataMapPosNeg(data.nrv, data.price, 'neg'),
				this.xScale,
				this.yScale,
				this.params.style.negative.color,
				'bottom'
			);

			this.markers.bar.render(
				this.panel,
				this.panel.rootContext,
				this._getDataMapPosNeg(data.nrv, data.price, 'unch'),
				this.xScale,
				this.yScale,
				this.params.style.neutral.color,
				'bottom'
			);
		} else {
			this.markers.bar.render(
				this.panel,
				this.panel.rootContext,
				this._getDataMap(data.nrv),
				this.xScale,
				this.yScale,
				this.params.style.color,
				'bottom'
			);
		}
	}
}

/** On Balance Volume indicator */
class OnBalanceVolumeIndicator extends Indicator {
	/**
	 * Returns the default params for this type of indicator. Overrides abstract
	 * method inherited from Indicator.
	 * @returns {object} - Default parameters object
	 * @property {string} id - Indicator ID
	 * @property {string} name - Indicator name
	 * @property {string} description - Indicator description
	 */
	_getDefaultParams() {
		return {
			id: 'onbalancevolume',
			name: 'On Balance Volume',
			description: 'On Balance Volume measures the strength of buying or selling pressure on a security. It represents the cumulative total of volume since indicator inception and adds volume on up days while subtracting the volume on down days.',
			componentSeries: [
				{ id: 'onbalancevolume' }
			]
		};
	}

	/** @param {object} data Symbol data object */
	render(data, sessionType) {
		this.cumulativeAdjustment = this._getCumulativeAdjustment();
		this._renderLineMarker({
			data: data[this.params.id],
			sessionType
		});
	}
}

/** P/E Range indicator */
class PERangeIndicator extends Indicator {
	/**
	 * Returns the default params for this type of indicator. Overrides abstract
	 * method inherited from Indicator.
	 * @returns {object} - Default parameters object
	 * @property {string} id - Indicator ID
	 * @property {string} name - Indicator name
	 * @property {string} description - Indicator description
	 * @property {object[]} inputs - Indicator inputs
	 */
	_getDefaultParams() {
		return {
			id: 'perange',
			name: 'P/E Range',
			dataSeries: 'perangeupper',
			description: 'P/E Range shows the high and low P/E for the period.',
			inputs: [{ name: 'earningstype', value: 0 }],
			componentSeries: [
				{ id: 'perangeupper' },
				{ id: 'perangelower', styleRef: 'low' }
			]
		};
	}

	/**
	 * @param {object} data - Symbol data object
	 * @return {number} - d3-range minimum
	 */
	getRangeMin(data) {
		return min(data.perange || [], i => +i.perangelower);
	}

	/**
	 * @param {object} data - Symbol data object
	 * @return {number} - d3-range maximum
	 */
	getRangeMax(data) {
		return max(data.perange || [], i => +i.perangeupper);
	}

	/**
	 * @return {object}
	 * @property {LineMarker} line
	 * @property {LineMarker} lineLow
	 */
	_getMarkers() {
		return {
			line: new LineMarker(),
			lineLow: new LineMarker()
		};
	}

	/** @param {object} data Symbol data object */
	render(data, sessionType) {

		this._renderLineMarker({
			data: data[this.params.id],
			dataKey: 'perangeupper',
			sessionType
		});

		this._renderLineMarker({
			data: data[this.params.id],
			dataKey: 'perangelower',
			marker: this.markers.lineLow,
			sessionType,
			styleKey: 'low'
		});
	}
}

/** P/E Ratio indicator */
class PERatioIndicator extends Indicator {
	/**
	 * Returns the default params for this type of indicator. Overrides abstract
	 * method inherited from Indicator.
	 * @returns {object} - Default parameters object
	 * @property {string} id - Indicator ID
	 * @property {string} name - Indicator name
	 * @property {string} description - Indicator description
	 */
	_getDefaultParams() {
		return {
			id: 'peratio',
			name: 'P/E Ratio',
			description: 'P/E Ratio plots the change in the P/E Ratio over the period of the chart.',
			componentSeries: [
				{ id: 'peratio' }
			]
		};
	}

	/**
	 * @return {object}
	 * @property {LineMarker} line
	 * @property {LineMarker} lineLow
	 */
	_getMarkers() {
		return {
			line: new LineMarker(),
			lineLow: new LineMarker()
		};
	}

	/** @param {object} data Symbol data object */
	render(data, sessionType) {
		this._renderLineMarker({
			data: data[this.params.id],
			sessionType
		});
	}
}

/** Percent B indicator */
class PercentBIndicator extends Indicator {
	/**
	 * Returns the default params for this type of indicator. Overrides abstract
	 * method inherited from Indicator.
	 * @returns {object} - Default parameters object
	 * @property {string} id - Indicator ID
	 * @property {string} name - Indicator name
	 * @property {string} description - Indicator description
	 * @property {object[]} inputs - Indicator inputs
	 * @property {object[]} bands - Indicator bands
	 */
	_getDefaultParams() {
		return {
			id: 'percentb',
			name: 'Bollinger Band %B',
			description:
				"%B quantifies a security's price relative to the upper and lower Bollinger Band.",
			inputs: [
				{ name: 'period', value: 20 },
				{ name: 'standardDeviations', value: 2, max: 10 }
			],
			bands: [
				{
					labelTranslationKey: 'band.overbought',
					valHigh: 10000, // effectively infinite
					valLow: 1
				},
				{
					labelTranslationKey: 'band.oversold',
					valHigh: 0,
					valLow: -10000 // effectively infinitely negative
				}
			],
			componentSeries: [
				{ id: 'percentb' }
			]
		};
	}

	/**
	 * @return {object}
	 * @property {LineMarker} line
	 * @property {BandMarker} overbought
	 * @property {BandMarker} oversold
	 */
	_getMarkers() {
		return {
			line: new LineMarker(),
			overbought: new BandMarker(this.params.bands[0], this.panel),
			oversold: new BandMarker(this.params.bands[1], this.panel)
		};
	}

	/**
	 *
	 * @param {CanvasRenderingContext2D} ctx - Chart canvas context
	 * @param {number} yScale - Y axis scale
	 */
	_renderMedianLine(ctx, yScale) {
		const left = this.panel.size.padding.left;
		const y0 = subpixel.px(yScale(0.5));
		const style = this.params.style;

		ctx.beginPath();
		ctx.lineWidth = style.median.width;
		ctx.strokeStyle = style.median.color;
		if (style.median.lineDash) ctx.setLineDash(style.median.lineDash.split(/\s*,\s*/));
		ctx.moveTo(left, y0);
		ctx.lineTo(this.panel.size.width + left, y0);
		ctx.stroke();
		ctx.setLineDash([]);
	}

	/** @param {object} data Symbol data object */
	render(data, sessionType) {
		this.markers.overbought.render(this.panel, this.params.style.overbought, true);
		this.markers.oversold.render(this.panel, this.params.style.oversold, false);
		this._renderLineMarker({
			data: data[this.params.id],
			sessionType
		});

		this._renderMedianLine(this.panel.rootContext, this.yScale);
	}
}

/** General indicator flag */
class IndicatorFlag extends Flag {
	_getStyle(args = {}) {
		const returnStyle = this.panel.core.getStyle('flag.indicator');
		const panelStyle = this.panel.params.style;
		const indicatorStyle = this.indicator ? this.indicator.params.style : {};

		// Use indicator color if it exists
		if (indicatorStyle.color) returnStyle.background.color = indicatorStyle.color;

		// Use component style color if it exists
		if (this.indicator.params.componentSeries) {
			// Get proper component where ID's (top, sma, etc.) match
			const component = this.indicator.params.componentSeries.find(row => row.id === args.seriesId);

			if (component && component.styleRef) {
				returnStyle.background.color = get(this.indicator.params.style, component.styleRef).color;
			}
		}

		// Use fallback values if necessary
		if (!returnStyle.font.family) returnStyle.font.family = panelStyle.yAxis.font.family;
		if (!returnStyle.font.size) returnStyle.font.size = panelStyle.yAxis.font.size;

		return {
			...returnStyle,
			...args.style
		};
	}

	/**
	 * Private method to get flag value
	 * @return {number}
	 * @override
	 * @private
	 */
	getValue() {
		if (!this.panel || !this.panel.core.data) return null;

		const { core } = this.panel;

		try {
			const data =
				core.data[core.params.symbol][this.indicator.params.id][
					this.indicator.params.uid
				];
			let dataIndex = data.length - 1;

			// special case: ichimoku series (senkou, kijun etc) have different final indexes
			if (this.indicator.params.id === 'ichimokucloud') {
				const d = data.filter(x => x[this.seriesId] !== undefined);
				dataIndex = d.length - 1;
			}

			return data[dataIndex][this.seriesId];
		} catch (e) {
			core.warn(e.message);
			return null;
		}
	}
	/**
	 * @returns The appropriate event emitter types
	 * @type {object}
	 */
	get eventType() {
		return {
			render: 'FLAG_RENDER_INDICATOR',
			hide: 'FLAG_HIDE_INDICATOR',
			show: 'FLAG_SHOW_INDICATOR'
		};
	}
}

/** Previous Close indicator */
class PreviousCloseIndicator extends Indicator {
	/**
	 * Returns the default params for this type of indicator. Overrides abstract
	 * method inherited from Indicator.
	 * @returns {object} - Default parameters object
	 * @property {string} id - Indicator ID
	 * @property {string} name - Indicator name
	 * @property {object[]} inputs - Indicator inputs
	 * @property {string} description - Indicator description
	 * @property {null} text - Indicator text
	 * @property {boolean} showFlag - Whether to show a Y axis flag
	 * @property {string} labelAlignment - How to align label - 'ne' | 'nw' | 'sw' | 'se'
	 * @property {number} numDaysLabel - Number of days to label. -1 for all days, 0 for none
	 * @property {number} numDaysLine - Number of days to render line. -1 for all days, 0 for none
	 * @property {boolean} noClip - Whether to clip the indicator
	 */
	_getDefaultParams() {
		return {
			id: 'previousclose',
			name: 'Previous Close',
			inputs: [{ name: 'Version', value: 1 }],
			description:
				"Previous Close graphically depicts today's net change. A horizontal line is drawn at the vertical position of the previous day's close. When the chart style is set to mountain, the fill color is typically red below this line, or green above the line.",
			text: null,
			showFlag: false, // optional yaxis flag
			labelAlignment: 'nw', // ne, nw, sw, se
			numDaysLabel: 1, // <int> number of days to label (-1 = all days, 0 = none)
			numDaysLine: -1, // <int> number of days to render line (-1 = all days, 0 = none)
			noClip: true
		};
	}

	/**
	 * @param {object} data - Symbol data object
	 * @return {number} - d3-range minimum
	 */
	getRangeMin(data) {
		return data.previousclose.length
			? data.previousclose[data.previousclose.length - 1]
				.previousclose * 0.99 : 0;
	}

	/**
	 * @param {object} data - Symbol data object
	 * @return {number} - d3-range maximum
	 */
	getRangeMax(data) {
		return data.previousclose.length
			? data.previousclose[data.previousclose.length - 1]
				.previousclose * 1.01 : 0;
	}

	/** @return {object} */
	_getMarkers() {
		return {};
	}

	/** @return {object} */
	_getDataMap() {
		return {};
	}

	/**
	 * Returns a formatted string for the indicator label
	 * @param {object} data - Indicator data
	 * @return {string} formatted label text
	 */
	_getLabelText(data) {
		const { yAxis } = this.panel;
		const { core } = this;
		const formatString = yAxis
			.getFormatString(yAxis.primaryScale)
			.replace(/s/, 'f'); // Use fixed point notation
		const valueFormat = yAxis.getFormatValue(data.previousclose, formatString);

		return typeof this.params.text === 'string'
			? this.params.text
			: translate(
				core.locale,
				'indicator.previousclose.label',
				[valueFormat]
			);
	}

	/**
	 * Returns an object with dimension specifications for this indicator
	 * @param {object} row - Current dimension object
	 * @param {number} index - Current dimension index
	 * @param {object[]} arr - Full dimensions array
	 * @return {object}
	 */
	_getDimensions(row, index, arr) {
		const { core, panel } = this;
		const ctx = panel.rootContext;
		const size = panel.size;
		const paddingLeft = size.padding.left;
		let x = paddingLeft;
		let dayLeft = 0;
		let dayRight = 0;
		const yClose = this.yScale(row.previousclose);
		let y = yClose;
		let labelWidth = 0;
		let labelHeight = 0;
		let labelText = '';
		let rightEdgeChart = paddingLeft + size.width;
		let leftEdgeChart = paddingLeft;
		const isNorth = /ne|nw/.test(this.params.labelAlignment);
		const isWest = /nw|sw/.test(this.params.labelAlignment);

		// edge adjustments
		rightEdgeChart = Math.min(
			rightEdgeChart,
			panel.xAxis.primaryScale(
				core.closestExchangeIndex(
					new Date(core.exchangeDates[core.exchangeDates.length - 1])
				)
			)
		);
		leftEdgeChart = Math.max(
			leftEdgeChart,
			panel.xAxis.primaryScale(
				core.closestExchangeIndex(new Date(core.exchangeDates[0]))
			)
		);

		if (!isNaN(y)) {
			ctx.font = panel.getFontStyle(
				this.params.style.label.font.weight,
				this.params.style.label.font.size,
				this.params.style.label.font.family
			);

			labelText = this._getLabelText(row);
			labelWidth = ctx.measureText(labelText).width;
			labelHeight = /(\d+)px/.test(ctx.font)
				? Number(RegExp.$1) * 1.2
				: 12; // rough height calc

			// initial x, y
			x = isWest ? paddingLeft + 5 : rightEdgeChart - labelWidth - 5;
			y += isNorth ? -labelHeight - 1 : 2;

			// adjust y for margin
			if (isNorth && this.params.style.margin.bottom) {
				y -= this.params.style.margin.bottom;
			}
			if (!isNorth && this.params.style.margin.top) {
				y += this.params.style.margin.top;
			}

			if (this.version === 2) {
				x =
					5 +
					panel.xAxis.primaryScale(
						core.closestExchangeIndex(new Date(row.date))
					);
			}

			const dateStart = new Date(row.date);
			let dateEnd;

			if (index < arr.length - 1) {
				dateEnd = new Date(arr[index + 1].date);
			} else {
				dateEnd = new Date(
					core.dataPrimary[core.dataPrimary.length - 1].date
				);
				dateEnd.setHours(0);
				dateEnd.setSeconds(0);
				dateEnd.setMilliseconds(0);

				if (core.state.isIntraday && core.state.typicalSessions) {
					dateEnd.setMinutes(core.state.typicalSessions.max);
				}
			}

			dayLeft = panel.xAxis.primaryScale(
				core.closestExchangeIndex(dateStart)
			);
			dayRight = Math.min(
				panel.xAxis.primaryScale(core.closestExchangeIndex(dateEnd)),
				panel.size.width
			);
		}

		return {
			x,
			y,
			dayLeft,
			dayRight,
			widthDay: dayRight - dayLeft,
			yClose,
			prevClose: row.previousclose,
			labelText,
			width: labelWidth,
			height: labelHeight,
			rightEdgeChart,
			leftEdgeChart
		};
	}

	/**
	 * Checks whether two rectangles on the canvas intersect
	 * @param {object} r1 - Rectangle 1 coordinates and size
	 * @param {object} r2 - Rectangle 2 coordinates and size
	 * @return {boolean}
	 */
	intersect(r1, r2) {
		if (
			r1.x < r2.x + r2.width &&
			r2.x < r1.x + r1.width &&
			r1.y < r2.y + r2.height
		) {
			return r2.y < r1.y + r1.height;
		}
		return false;

	}
	/**
	 * @param {object} data
	 * @returns array of bounding boxes, one for each day
	 * @example: [
			{
				"x": 368.3333333333333,
				"y": 55.786838679133076,
				"dayLeft": 363.3333333333333,
				"dayRight": 545,
				"widthDay": 181.66666666666669,
				"yClose": 48.786838679133076,
				"labelText": "Previous Close 194.03",
				"prevClose": 194.03,
				"width": 100.615234375,
				"height": 12,
				"rightEdgeChart": 545,
				"leftEdgeChart": 0
			},
			...
		]
	 */
	_getV2BoundingBoxes(data) {
		const dimensionsAll = [];

		// bounding boxes
		data.previousclose.forEach((row, index, arr) => {
			dimensionsAll.push(this._getDimensions(row, index, arr));
		});

		return dimensionsAll;
	}

	/** @param {object} data Symbol data object */
	render(data) {
		const self = this;

		// lines
		if (this.version === 2) {
			const dimensionsAll = this._getV2BoundingBoxes(data);

			self._renderLineV2(dimensionsAll);

			dimensionsAll.forEach((row, index, arr) => {
				self._renderLabel(row, index, arr);
			});
		} else {
			const index = data.previousclose.length - 1;
			const dimensions = self._getDimensions(
				data.previousclose[index],
				index,
				data.previousclose
			);

			self._renderLine(dimensions);
			self._renderLabel(dimensions, 0, 1);
		}

		if (this.params.showFlag && !this.flags['previousclose']) {
			this.flags['previousclose'] = new IndicatorFlag({
				panel: this.panel,
				indicator: this,
				id: 'previousclose'
			});
		}
	}

	/**
	 * Renders a line
	 * @param {object} dimensions - Indicator dimensions
	 * @return {number}
	 */
	_renderLine(dimensions) {
		const { panel } = this;
		const ctx = panel.rootContext;

		ctx.textBaseline = 'top';
		ctx.lineWidth = this.params.style.width;
		ctx.strokeStyle = this.params.style.color;
		if (this.params.style.lineDash) {
			ctx.setLineDash(this.params.style.lineDash.split(/\s*,\s*/));
		}
		ctx.beginPath();
		subpixel.moveTo(ctx, dimensions.leftEdgeChart, dimensions.yClose);
		subpixel.lineTo(ctx, dimensions.rightEdgeChart, dimensions.yClose);
		ctx.stroke();
		ctx.setLineDash([]);
		return dimensions.rightEdgeChart - dimensions.leftEdgeChart;
	}

	/**
	 * Renders a line
	 * @param {object} dimensions - Indicator dimensions
	 * @return {number}
	 */
	_renderLineV2(dimensions) {
		const { panel } = this;
		const self = this;
		const ctx = panel.rootContext;
		ctx.textBaseline = 'top';
		ctx.lineWidth = this.params.style.width;
		ctx.strokeStyle = this.params.style.color;
		if (this.params.style.lineDash) {
			ctx.setLineDash(this.params.style.lineDash.split(/\s*,\s*/));
		}
		ctx.beginPath();

		dimensions.forEach((row, index, arr) => {
			if (self.params.numDaysLine === 0) return;

			if (
				self.params.numDaysLine > 0 &&
				index < arr.length - self.params.numDaysLine
			) {
				return;
			}

			if (row.dayRight < row.dayLeft) return;

			if (row.yClose < 0 || row.yClose >= panel.size.height) return;

			subpixel.moveTo(ctx, row.dayLeft, row.yClose);
			subpixel.lineTo(ctx, row.dayRight, row.yClose);
		});
		ctx.stroke();
		ctx.setLineDash([]);
	}

	/**
	 * Renders a label for the indicator
	 * @param {object} dimensions - Indicator dimensions
	 * @param {number} index - Current array index
	 * @param {object[]} arr - Array of data
	 */
	_renderLabel(dimensions, index, arr) {
		const { panel } = this;
		const ctx = panel.rootContext;

		if (this.params.numDaysLabel === 0) return;

		if (
			this.params.numDaysLabel > 0 &&
			index < arr.length - this.params.numDaysLabel
		) {
			return;
		}

		if (!isNaN(dimensions.y)) {
			if (
				this.version === 1 ||
				(dimensions.x + dimensions.width < panel.size.width &&
					dimensions.width < dimensions.widthDay)
			) {
				// label
				this.panel.glowText(
					ctx,
					dimensions.labelText,
					dimensions.x,
					dimensions.y,
					this.params.style.label.color,
					this.params.style.label.glow.color
				);
			}

			// debug bounding box
			//ctx.strokeStyle = this.params.style.color;
			//ctx.strokeRect(dimensions.x, dimensions.y, dimensions.width, dimensions.height);
		}
	}

	get version() {
		return Number(this.getInput('Version'));
	}
}

/** Price Channel indicator */
class PriceChannelIndicator extends Indicator {
	/**
	 * Returns the default params for this type of indicator. Overrides abstract
	 * method inherited from Indicator.
	 * @returns {object} - Default parameters object
	 * @property {string} id - Indicator ID
	 * @property {string} name - Indicator name
	 * @property {string} description - Indicator description
	 * @property {object[]} inputs - Indicator inputs
	 * @property {object[]} componentSeries - Indicator component series
	 */
	_getDefaultParams() {
		return {
			id: 'pricechannel',
			name: 'Price Channel',
			description:
				'Price Channel is a trending indicator that shows the high and low prices for a period. It is typically a bullish signal, when a stock breaks out above the prior high for a period.',
			inputs: [{ name: 'period', value: 20 }],
			componentSeries: [
				{ id: 'pricechannellower' },
				{ id: 'pricechannelupper' }
			]
		};
	}

	/**
	 * @param {object} data - Symbol data object
	 * @return {number} - d3-range minimum
	 */
	getRangeMin(data) {
		return min(data.pricechannel || [], i => +i.pricechannellower);
	}

	/**
	 * @param {object} data - Symbol data object
	 * @return {number} - d3-range maximum
	 */
	getRangeMax(data) {
		return max(data.pricechannel || [], i => +i.pricechannelupper);
	}

	/**
	 * @return {object}
	 * @property {LineMarker} lineUp
	 * @property {LineMarker} lineDown
	 * @property {LineMarker} lineFill
	 */
	_getMarkers() {
		return (
			this.markers || {
				lineUp: new LineMarker(),
				lineDown: new LineMarker(),
				lineFill: new LineMarker()
			}
		);
	}

	/** @param {object} data Symbol data object */
	render(data, sessionType) {
		const dataUp = this._getDataMap(data.pricechannel, 'pricechannelupper');
		const dataDown = this._getDataMap(data.pricechannel, 'pricechannellower');

		this.markers.lineFill.renderFillBetween(
			this.panel.rootContext,
			dataUp,
			dataDown,
			this.xScale,
			this.yScale,
			this.params.style.background.color
		);

		this._renderLineMarker({
			dataMap: dataUp,
			marker: this.markers.lineUp,
			sessionType
		});

		this._renderLineMarker({
			dataMap: dataDown,
			marker: this.markers.lineDown,
			sessionType
		});
	}
}

/** Last Close flag */
class LastCloseFlag extends Flag {
	_getStyle(args = {}) {
		const returnStyle = this.core.getStyle('flag.lastClose');
		const panelStyle = this.panel.params.style;

		// Use fallback values if necessary
		if (!returnStyle.font.family) returnStyle.font.family = panelStyle.yAxis.font.family;
		if (!returnStyle.font.size) returnStyle.font.size = panelStyle.yAxis.font.size;

		return {
			...returnStyle,
			...args.style
		};
	}

	/**
	 * Private method to get flag value
	 * @return {number}
	 * @override
	 * @private
	 */
	getValue() {
		if (!this.core.data) return null;

		const data = this.core.data[this.indicator.params.symbol];
		let symbolData = this.getFirstDataset(data ? data.price || [] : []);
		let key = 'close';
		let val = null;

		// Check for custom data.  Custom data will live in a slightly different place.
		if (this.core.params.customData) {
			symbolData = this.core.data[this.indicator.params.datasetId];
			if (symbolData?.length > 0 && 'value' in symbolData[0]) {
				key = 'value'; // use "value" over "close" if it's available
			}
		}

		if (symbolData?.length) {
			for (const row of [...symbolData].reverse()) {
				val = row[key];

				if (typeof val === 'number') {
					val = val.toFixed(4);
					break;
				}
			}
		}
		return +val;
	}

	/**
	 * Gets the first available data set for this symbol
	 * @param {object} data Symbol data
	 * @private
	 */
	getFirstDataset(data) {
		for (const x in data) {
			if (Object.prototype.hasOwnProperty.call(data, x)) return data[x];
		}
		return null;
	}

	/**
	 * @returns The appropriate event emitter types
	 * @type {object}
	 */
	get eventType() {
		return {
			render: 'FLAG_RENDER_LASTCLOSE',
			hide: 'FLAG_HIDE_LASTCLOSE',
			show: 'FLAG_SHOW_LASTCLOSE'
		};
	}

	render() {
		// Only render flag if enabled
		if (this.core.params.showLastCloseFlags) return super.render();
	}
}

/** Candlestick marker */
class CandlestickMarker extends Marker {
	getDefaultParams() {
		return {
			fillType: 'hollow', // "hollow", "filled"
			lineColor: '#666',
			fillColorNeg: '#000',
			fillColorPos: '#fff'
		};
	}
	/**
	 * calculate dynamic bar width based on data period and panel domain
	 * (method used by more than one marker subclass so it lives here)
	 * @param {Panel} panel
	 * @return {int}
	 */
	getBarWidth(panel, min = null, max = null) {
		if (!min && !max) [min, max] = panel.xAxis.primaryScale.domain();
		const { width } = panel.size;
		let len = max - min;
		const tolerance = panel.core.params.candleWidthTolerance;
		let w0;

		switch (panel.core.params.dataPeriod) {
			case 'Year':
				len /= 365;
				break;
			case 'Month':
				len /= 15;
				break;
			case 'Week':
				len /= 3;
				break;
			case 'Day':
				break;
			case 'Hour':
				len /= 30;
				break;
			case 'Minute':
				len /= panel.core.params.dataInterval
					? panel.core.params.dataInterval
					: 1;
				break;
		}

		w0 = Math.min(30, (0.6 * width) / len);

		// STS-1340
		if (tolerance !== 0 && w0 < tolerance) w0 = 1;

		return w0;
	}

	render(
		panel,
		ctx,
		data,
		xScale,
		yScale,
		fillColorPos,
		fillColorNeg,
		fillColorNeutral,
		lineStyle
	) {
		const w0 = this.getBarWidth(panel);
		const w1 = w0 / 2;
		const d = []; // date
		const o = []; // open
		const h = []; // high
		const l = []; // low
		const c = []; // close
		let x = 0;
		let dataRow = [];
		const isFilled = this.params.fillType === 'filled';
		const isJapaneseFill = this.params.fillRule === 'openClose';
		const isMF =
			panel.core.xref[panel.core.params.symbol] &&
			panel.core.xref[panel.core.params.symbol].issueType === 'MF';
		const showCandleBody = isMF || w0 > 1 || data.length < 100; // conceal body on well-populated datasets with thin candles, but don't do it for funds which have no vertical height on a Daily period

		fillColorNeutral = fillColorNeutral || '#999999';

		// pos wicks
		ctx.strokeStyle = fillColorPos;
		ctx.lineWidth = lineStyle;
		ctx.beginPath();

		for (x = 0; x < data.length; x++) {
			dataRow = data[x];
			// 0 = date
			// 1 = close
			// 2 = open
			// 3 = high
			// 4 = low
			// 5 = diff (change from previous)
			// 6 = hollow/filled
			// 7 = for "filled mode" only, red/green color

			// normally already available, but this can happen for comparisons
			if (typeof dataRow[5] === 'undefined') {
				// get diff (for determining color)
				if (x > 0) {
					dataRow[5] = dataRow[1] - data[x - 1][1];
				} else {
					dataRow[5] = 0;
				}
			}

			// cache xScale/yScale lookups
			// Math.round to ensure array of ints instead of doubles
			d[x] = Math.round(xScale(dataRow[0]));
			o[x] = Math.round(yScale(dataRow[2]));
			h[x] = Math.round(yScale(dataRow[3]));
			l[x] = Math.round(yScale(dataRow[4]));
			c[x] = Math.round(yScale(dataRow[1]));

			// "filled mode" candles derive color from open/close instead of close/close
			if (isFilled) {
				if (dataRow[1] > dataRow[2]) {
					dataRow[7] = 1; // pos
				} else if (dataRow[1] < dataRow[2]) {
					dataRow[7] = -1; // neg
				} else {
					dataRow[7] = 0; // unch
				}
			}

			// normally already available, but this can happen for comparisons
			if (typeof dataRow[6] === 'undefined') {
				// get candle hollow bool
				dataRow[6] = dataRow[1] > dataRow[2] ? 1 : 0;
			}

			if (
				(!isFilled && dataRow[5] > 0) ||
				(isFilled && dataRow[7] === 1)
			) {
				if (showCandleBody) {
					subpixel.moveTo(ctx, d[x], h[x]);
					subpixel.lineTo(ctx, d[x], Math.min(o[x], c[x]));
					subpixel.moveTo(ctx, d[x], Math.max(o[x], c[x]));
					subpixel.lineTo(ctx, d[x], l[x]);
				} else {
					subpixel.moveTo(ctx, d[x], h[x]);
					subpixel.lineTo(ctx, d[x], l[x]);
				}
			}
		}
		ctx.closePath();
		ctx.stroke();

		// neg wicks
		ctx.strokeStyle = fillColorNeg;
		ctx.beginPath();

		for (x = 0; x < data.length; x++) {
			dataRow = data[x];

			if (
				(!isFilled && dataRow[5] < 0) ||
				(isFilled && dataRow[7] === -1)
			) {
				if (showCandleBody) {
					subpixel.moveTo(ctx, d[x], h[x]);
					subpixel.lineTo(ctx, d[x], Math.min(o[x], c[x]));
					subpixel.moveTo(ctx, d[x], Math.max(o[x], c[x]));
					subpixel.lineTo(ctx, d[x], l[x]);
				} else {
					subpixel.moveTo(ctx, d[x], h[x]);
					subpixel.lineTo(ctx, d[x], l[x]);
				}
			}
		}
		ctx.closePath();
		ctx.stroke();

		// unchanged wicks
		ctx.strokeStyle = fillColorNeutral;
		ctx.beginPath();

		for (x = 0; x < data.length; x++) {
			dataRow = data[x];

			if (
				(!isFilled && dataRow[5] === 0) ||
				(isFilled && dataRow[7] === 0)
			) {
				if (showCandleBody) {
					subpixel.moveTo(ctx, d[x], h[x]);
					subpixel.lineTo(ctx, d[x], Math.min(o[x], c[x]));
					subpixel.moveTo(ctx, d[x], Math.max(o[x], c[x]));
					subpixel.lineTo(ctx, d[x], l[x]);
				} else {
					subpixel.moveTo(ctx, d[x], h[x]);
					subpixel.lineTo(ctx, d[x], l[x]);
				}
			}
		}
		ctx.closePath();
		ctx.stroke();

		// draw candle body.  there are two types of filled candles -
		// 1) default - compare close to previous close
		// 2) "filled" candle mode - compare close to open
		if (showCandleBody) {
			// unchanged filled
			ctx.fillStyle = fillColorNeutral;
			ctx.beginPath();

			for (x = 0; x < data.length; x++) {
				dataRow = data[x];

				if (isFilled) {
					// close === open
					if (dataRow[1] === dataRow[2]) {
						subpixel.fillRect(ctx, d[x] - w1, o[x], w0, c[x] - o[x]);
					}
				} else if (dataRow[5] === 0) {
					// regular filled
					if (dataRow[6] === 0) {
						subpixel.fillRect(ctx, d[x] - w1, o[x], w0, c[x] - o[x]);
					}
				}
			}
			ctx.closePath();
			ctx.fill();

			// unchanged hollow
			ctx.strokeStyle = fillColorNeutral;
			ctx.beginPath();

			for (x = 0; x < data.length; x++) {
				dataRow = data[x];

				if (!isFilled && dataRow[5] === 0) {
					if (dataRow[6] !== 0 && dataRow[6] === 1) {
						subpixel.hollowRect(ctx, d[x] - w1, o[x], w0, c[x] - o[x]);
					}
				}
			}
			ctx.closePath();
			ctx.stroke();

			// pos filled
			ctx.fillStyle = fillColorPos;
			ctx.strokeStyle = fillColorPos;
			ctx.beginPath();

			for (x = 0; x < data.length; x++) {
				dataRow = data[x];

				if (isFilled) {
					// close > open
					if (dataRow[1] > dataRow[2]) {
						if (isJapaneseFill) {
							// special case here - would normally be a pos fill, but this style uses a pos hollow (NXC-797)

							subpixel.hollowRect(
								ctx,
								d[x] - w1,
								o[x],
								w0,
								c[x] - o[x]
							);
						} else {
							subpixel.fillRect(
								ctx,
								d[x] - w1,
								o[x],
								w0,
								c[x] - o[x]
							);
						}
					}
				} else if (dataRow[5] > 0) {
					// regular filled
					if (dataRow[6] === 0) {
						subpixel.fillRect(ctx, d[x] - w1, o[x], w0, c[x] - o[x]);
					}
				}
			}
			ctx.closePath();
			if (isJapaneseFill) {
				ctx.stroke();
			} else {
				ctx.fill();
			}

			// pos hollow
			ctx.strokeStyle = fillColorPos;
			ctx.beginPath();
			for (x = 0; x < data.length; x++) {
				dataRow = data[x];

				if (!isFilled && dataRow[5] > 0) {
					if (
						dataRow[6] !== 0 &&
						dataRow[5] > 0 &&
						dataRow[6] === 1
					) {
						subpixel.hollowRect(ctx, d[x] - w1, o[x], w0, c[x] - o[x]);
					}
				}
			}
			ctx.closePath();
			ctx.stroke();

			// neg filled
			ctx.fillStyle = fillColorNeg;
			ctx.beginPath();

			for (x = 0; x < data.length; x++) {
				dataRow = data[x];

				if (isFilled) {
					// close < open
					if (dataRow[1] < dataRow[2]) {
						subpixel.fillRect(ctx, d[x] - w1, o[x], w0, c[x] - o[x]);
					}
				} else if (dataRow[5] < 0) {
					// regular filled
					if (dataRow[6] === 0) {
						subpixel.fillRect(ctx, d[x] - w1, o[x], w0, c[x] - o[x]);
					}
				}
			}
			ctx.closePath();
			ctx.fill();

			// neg hollow
			ctx.strokeStyle = fillColorNeg;
			ctx.beginPath();
			for (x = 0; x < data.length; x++) {
				dataRow = data[x];

				if (!isFilled && dataRow[5] < 0) {
					// regular filled
					if (dataRow[6] !== 0) {
						subpixel.hollowRect(ctx, d[x] - w1, o[x], w0, c[x] - o[x]);
					}
				}
			}
			ctx.closePath();
			ctx.stroke();
		}
	}

	renderOHLC(panel, ctx, data, xScale, yScale, lineColor, markerType, lineStyle) {
		const w0 = this.getBarWidth(panel) * 0.7;
		const d = [];
		let x;
		const xLen = data.length;

		// 0 = date
		// 1 = close
		// 2 = open
		// 3 = high
		// 4 = low

		for (x = 0; x < xLen; x++) {
			d[x] = xScale(data[x][0]);
		}

		ctx.lineWidth = lineStyle;
		ctx.strokeStyle = lineColor || this.params.lineColor;
		ctx.beginPath();

		for (x = 0; x < xLen; x++) {
			this.drawOHLC(panel, ctx, d, yScale, data, w0, markerType, x);
		}

		ctx.stroke();
	}

	renderOHLCNewHighs(
		panel,
		ctx,
		data,
		xScale,
		yScale,
		lineColorUp,
		lineColorNeutral = '#999999',
		markerType = 'ohlc',
		lineStyle
	) {
		const w0 = this.getBarWidth(panel) * 0.7;
		const d = [];
		let x;
		const xLen = data.length;

		let currentHigh = -Infinity;
		const indiciesLessThanCurrentHigh = [];

		/**
		 * Indices for elements in data array:
		 * 0 = date
		 * 1 = close
		 * 2 = open
		 * 3 = high
		 * 4 = low
		 */

		// Scale all of the dates
		for (x = 0; x < xLen; x++) {
			d[x] = xScale(data[x][0]);
		}

		// draw in three passes, one per color, for better canvas performance

		ctx.lineWidth = lineStyle;

		// Draw new highs
		ctx.strokeStyle = lineColorUp;
		ctx.beginPath();

		for (x = 0; x < xLen; x++) {
			if (
				d[x] + w0 > 0 && // Only check data points on the chart
				data[x][1] > currentHigh
			) {
				currentHigh = data[x][1];
				this.drawOHLC(panel, ctx, d, yScale, data, w0, markerType, x);
			} else {
				indiciesLessThanCurrentHigh.push(x);
			}
		}

		ctx.stroke();

		// Draw everything else
		ctx.strokeStyle = lineColorNeutral;
		ctx.beginPath();

		for (const idx of indiciesLessThanCurrentHigh) {
			this.drawOHLC(panel, ctx, d, yScale, data, w0, markerType, idx);
		}

		ctx.stroke();
	}

	renderOHLCNewLows(
		panel,
		ctx,
		data,
		xScale,
		yScale,
		lineColorDown,
		lineColorNeutral = '#999999',
		markerType = 'ohlc',
		lineStyle
	) {
		const w0 = this.getBarWidth(panel) * 0.7;
		const d = [];
		let x;
		const xLen = data.length;

		let currentLow = Infinity;
		const indiciesGreaterThanCurrentlow = [];

		/**
		 * Indices for elements in data array:
		 * 0 = date
		 * 1 = close
		 * 2 = open
		 * 3 = high
		 * 4 = low
		 */

		// Scale all of the dates
		for (x = 0; x < xLen; x++) {
			d[x] = xScale(data[x][0]);
		}

		ctx.lineWidth = lineStyle;

		// Draw new lows
		ctx.strokeStyle = lineColorDown;
		ctx.beginPath();

		for (x = 0; x < xLen; x++) {
			if (
				d[x] + w0 > 0 && // Only check data points on the chart
				data[x][1] < currentLow
			) {
				currentLow = data[x][1];
				this.drawOHLC(panel, ctx, d, yScale, data, w0, markerType, x);
			} else {
				indiciesGreaterThanCurrentlow.push(x);
			}
		}

		ctx.stroke();

		// Draw everything else
		ctx.strokeStyle = lineColorNeutral;
		ctx.beginPath();

		for (const idx of indiciesGreaterThanCurrentlow) {
			this.drawOHLC(panel, ctx, d, yScale, data, w0, markerType, idx);
		}

		ctx.stroke();
	}

	renderOHLCPosNeg(
		panel,
		ctx,
		data,
		xScale,
		yScale,
		lineColorUp,
		lineColorDown,
		lineColorNeutral,
		markerType,
		lineStyle
	) {
		const w0 = this.getBarWidth(panel) * 0.7;
		const d = [];
		let x;
		const xLen = data.length;

		lineColorNeutral = lineColorNeutral || '#999999';

		// 0 = date
		// 1 = close
		// 2 = open
		// 3 = high
		// 4 = low

		for (x = 0; x < xLen; x++) {
			d[x] = xScale(data[x][0]);
		}

		// draw in three passes, one per color, for better canvas performance

		ctx.lineWidth = lineStyle;
		ctx.strokeStyle = lineColorNeutral;
		ctx.beginPath();

		// unchanged
		for (x = 0; x < xLen; x++) {
			if (x === 0 || data[x][1] === data[x - 1][1]) {
				this.drawOHLC(panel, ctx, d, yScale, data, w0, markerType, x);
			}
		}

		ctx.stroke();
		ctx.strokeStyle = lineColorUp;
		ctx.beginPath();

		// pos
		for (x = 1; x < xLen; x++) {
			if (data[x][1] > data[x - 1][1]) {
				this.drawOHLC(panel, ctx, d, yScale, data, w0, markerType, x);
			}
		}

		ctx.stroke();
		ctx.strokeStyle = lineColorDown;
		ctx.beginPath();

		// neg
		for (x = 1; x < xLen; x++) {
			if (data[x][1] < data[x - 1][1]) {
				this.drawOHLC(panel, ctx, d, yScale, data, w0, markerType, x);
			}
		}

		ctx.stroke();
	}

	drawOHLC(panel, ctx, d, yScale, data, w0, markerType, x) {
		// vert
		subpixel.moveTo(ctx, d[x], yScale(data[x][3]));
		subpixel.lineTo(ctx, d[x], yScale(data[x][4]));

		if (markerType === 'ohlc') {
			// open
			subpixel.moveTo(ctx, d[x] - w0, yScale(data[x][2]));
			subpixel.lineTo(ctx, d[x], yScale(data[x][2]));
		}

		// close
		subpixel.moveTo(ctx, d[x], yScale(data[x][1]));
		subpixel.lineTo(ctx, d[x] + w0, yScale(data[x][1]));
	}
}

/** Circle marker */
class CircleMarker extends Marker {
	getDefaultParams() {
		return {
			fillColor: '#CCC'
		};
	}

	render(ctx, data, xScale, yScale,
		{
			beginPath = true,
			color = null,
			length: diameter = 4,
			fillColor,
			lineWidth = 1
		}
	) {
		ctx.save();
		if (beginPath) ctx.beginPath();
		ctx.fillStyle = fillColor;
		ctx.strokeStyle = color;
		ctx.lineWidth = lineWidth;

		for (const row of data) {
			const x0 = xScale(row[0]);
			const y0 = yScale(row[1]);

			ctx.moveTo(x0 + diameter / 2, y0);
			ctx.arc(x0, y0, diameter / 2, 0, 2 * Math.PI, true);
		}
		if (fillColor) ctx.fill();
		if (color) ctx.stroke();

		ctx.restore();
	}
}

/** Diamond marker */
class DiamondMarker extends Marker {
	getDefaultParams() {
		return {
			fillColor: '#CCC'
		};
	}

	render(ctx, data, xScale, yScale,
		{
			beginPath = true,
			color = null,
			fillColor,
			length: height = 4,
			lineWidth = 1
		}
	) {
		const halfHeight = height / 2;

		ctx.save();
		ctx.fillStyle = fillColor;
		ctx.strokeStyle = color;
		ctx.lineWidth = lineWidth;

		if (beginPath) ctx.beginPath();
		for (const row of data) {
			const x0 = xScale(row[0]);
			const y0 = yScale(row[1]);

			ctx.moveTo(x0 - halfHeight, y0);
			ctx.lineTo(x0, y0 + halfHeight);
			ctx.lineTo(x0 + halfHeight, y0);
			ctx.lineTo(x0, y0 - halfHeight);
			ctx.closePath();
		}
		if (fillColor) ctx.fill();
		if (color) ctx.stroke();

		ctx.restore();
	}
}

/** Square marker */
class SquareMarker extends Marker {
	getDefaultParams() {
		return {
			fillColor: '#CCC'
		};
	}

	render(ctx, data, xScale, yScale,
		{
			beginPath = true,
			color = null,
			fillColor,
			length: height = 4,
			lineWidth = 1
		}
	) {
		const halfHeight = height / 2;

		ctx.save();
		ctx.fillStyle = fillColor;
		ctx.strokeStyle = color;
		ctx.lineWidth = lineWidth;

		if (beginPath) ctx.beginPath();
		for (const row of data) {
			const x0 = xScale(row[0]);
			const y0 = yScale(row[1]);

			ctx.rect(
				x0 + halfHeight, // Draw CCW for clipping offset region
				y0 - halfHeight,
				-height, // Draw CCW for clipping offset region
				height
			);
		}
		if (fillColor) ctx.fill();
		if (color) ctx.stroke();

		ctx.restore();
	}
}

/** Triangle marker */
class TriangleMarker extends Marker {
	getDefaultParams() {
		return {
			fillColor: '#CCC'
		};
	}

	render(ctx, data, xScale, yScale,
		{
			beginPath = true,
			color = null,
			fillColor,
			length = 4,
			lineWidth = 1
		}
	) {
		// Use length as side length instead of height
		const halfLength = length / 2;
		const height = halfLength * Math.sqrt(3);
		const thirdHeight = height / 3;

		ctx.save();
		ctx.fillStyle = fillColor;
		ctx.strokeStyle = color;
		ctx.lineWidth = lineWidth;

		if (beginPath) ctx.beginPath();
		for (const row of data) {
			const x0 = xScale(row[0]);
			const y0 = yScale(row[1]);

			ctx.moveTo(x0 - halfLength, y0 + thirdHeight);
			ctx.lineTo(x0 + halfLength, y0 + thirdHeight);
			ctx.lineTo(x0, y0 - 2 * thirdHeight);
			ctx.closePath();
		}
		if (fillColor) ctx.fill();
		if (color) ctx.stroke();

		ctx.restore();
	}
}

/** Price indicator */
class PriceIndicator extends Indicator {
	/** @param {object} args Custom args */
	constructor(args) {
		super(args);
		/** @type {object} */
		this.flags['lastClose'] = new LastCloseFlag({
			panel: this.panel,
			indicator: this,
			id: 'lastClose'
		});
		this.state.hover = false;
		this.hoverEventOver = types.INDICATOR_MOUSE_OVER;
		this.hoverEventOut = types.INDICATOR_MOUSE_OUT;
		this.hoverEventMove = types.INDICATOR_MOUSE_MOVE;
	}

	/**
	 * Returns the default params for this type of indicator. Overrides abstract
	 * method inherited from Indicator.
	 * @returns {object} - Default parameters object
	 * @property {string} id - Indicator ID
	 * @property {string} name - Indicator name
	 * @property {object[]} inputs - Indicator inputs
	 * @property {string} dataSeries - Which data series to use
	 * @property {string} markerType - Indicator marker type - 'posneg' |
	 * 'candlestick' | 'fill' | 'ohlc' | 'hlc' | 'bar' | 'line'
	 * @property {string} candleFillType - Whether to fill candlestick markers -
	 * 'hollow' | 'filled'
	 * @property {string} candleFillRule - How to fill candlestick markers -
	 * 'default' | 'openClose'
	 * @property {boolean} ohlcPosNeg - Set false for OHLC/HLC to get monochrome coloring
	 * @property {string} symbol - Symbol for this indicator
	 */
	_getDefaultParams() {
		return {
			id: 'price',
			name: 'Price',
			description: 'The Price Indicator represents the price action of a security in various visual formats such as Line, Fill, OHLC (Open, High, Low, Close), and Candlestick.',
			inputs: [{ name: 'type', value: 'ohlc' }],
			dataSeries: 'close',
			markerType: markerType.LINE, // posneg, candlestick, fill, ohlc, hlc, bar
			candleFillType: 'hollow', // "hollow", "filled"
			candleFillRule: 'default', // "default", "openClose"
			ohlcPosNeg: true, // for OHLC/HLC set false for monochrome coloring,
			ohlcType: ohlcType.POSNEG,
			symbol: this.core.params.symbol
		};
	}

	/**
	 * @param {object} data - Symbol data object
	 * @return {number} - d3-range minimum
	 */
	getRangeMin(data) {
		const dataset =
			typeof data.price === 'object' && data.price.length
				? data.price
				: data.length ? data : [];
		let minVal;
		switch (this.params.markerType) {
			case markerType.CANDLESTICK:
			case markerType.HLC:
			case markerType.OHLC:
				// use low if available, otherwise use close or value (used in custom datasets)
				minVal = min(dataset, i => i.low || i.close || i.value); break;
			default: // bar, fill, line, linewithshapes, posneg, shape, stepped
				// use close if available, otherwise use value (used in custom datasets)
				minVal = min(dataset, i => i.close || i.value); break;
		}

		// Add normalized point (if available) to make sure line to normalized point doesn't draw through top/bottom of chart
		const normalizedPoint = dataset.length ? this._getNormalizedPoint(dataset[0].dateIndex) : null;
		if (normalizedPoint) minVal = Math.min(minVal, normalizedPoint[1]);

		return minVal;
	}

	/**
	 * @param {object} data - Symbol data object
	 * @return {number} - d3-range maximum
	 */
	getRangeMax(data) {
		const dataset =
			typeof data.price === 'object' && data.price.length
				? data.price
				: data.length ? data : [];
		let maxVal;
		switch (this.params.markerType) {
			case markerType.CANDLESTICK:
			case markerType.HLC:
			case markerType.OHLC:
				// use high if available, otherwise use close or value (used in custom datasets)
				maxVal = max(dataset, i => i.high || i.close || i.value); break;
			default: // bar, fill, line, linewithshapes, posneg, shape, stepped
				// use close if available, otherwise use value (used in custom datasets)
				maxVal = max(dataset, i => i.close || i.value); break;
		}

		// Add normalized point (if available) to make sure line to normalized point doesn't draw through top/bottom of chart
		const normalizedPoint = dataset.length ? this._getNormalizedPoint(dataset[0].dateIndex) : null;
		if (normalizedPoint) maxVal = Math.max(maxVal, normalizedPoint[1]);

		return maxVal;
	}

	/**
	 * @return {object}
	 * @property {BarMarker} bar - Bar marker
	 * @property {CandlestickMarker} candlestick - Candlestick marker
	 * @property {CircleMarker} circle - Circle marker
	 * @property {LineMarker} line - Line marker
	 * @property {LineMarker} stepped - Stepped marker
	 */
	_getMarkers() {
		return {
			bar: new BarMarker(),
			candlestick: new CandlestickMarker(),
			line: new LineMarker(),
			shape: {
				[shapeType.CIRCLE]: new CircleMarker(),
				[shapeType.DIAMOND]: new DiamondMarker(),
				[shapeType.SQUARE]: new SquareMarker(),
				[shapeType.TRIANGLE]: new TriangleMarker()
			},
			stepped: new LineMarker()
		};
	}

	/**
	 * @param {object} data - Indicator data
	 * @return {number[][]}
	 */
	_getDataMap(data = []) {
		if (!data) return [];
		return data.map(d => {
			const close = typeof d.close === 'number' ? d.close : d.value;

			return [
				d.dateIndex,
				close,
				typeof d.open === 'number' ? d.open : close,
				typeof d.high === 'number' ? d.high : close,
				typeof d.low === 'number' ? d.low : close,
				d.diff,
				d.hollow
			];
		});
	}

	// Function to calculate distance from a point to a line segment
	_pointToLineDistance(x, y, x1, y1, x2, y2) {
		// Calculate the distance
		const A = x - x1;
		const B = y - y1;
		const C = x2 - x1;
		const D = y2 - y1;

		const dot = A * C + B * D;
		const lenSQ = C * C + D * D;
		let param = -1;
		if (lenSQ !== 0) { // in case of 0 length line
			param = dot / lenSQ;
		}

		let xx; let
			yy;

		if (param < 0) {
			xx = x1;
			yy = y1;
		} else if (param > 1) {
			xx = x2;
			yy = y2;
		} else {
			xx = x1 + param * C;
			yy = y1 + param * D;
		}

		const dx = x - xx;
		const dy = y - yy;
		return Math.sqrt(dx * dx + dy * dy);
	}

	/**
	 * @param {array} mouse - Mouse coordinates with respect to this indicator's panel
	 * @param {array[]} lineCoords - Array of coordinate arrays [[x,y],[x,y]].  Only used for testing.
	 * @return {boolean}
	 */
	isWithin(mouse, lineCoords) {
		// Adjust for top margin
		const adjustedMouseY = mouse[1] - this.panel.params.style.margin.top;

		// Get line coordinates
		if (!lineCoords) lineCoords = this.markers.line.getLineCoords(this.state.dataPrice, this.xScale, this.yScale, this.params.markerType === 'stepped');
		const threshold = 5; // Distance in pixels to consider the mouse as 'hovering' over the line

		// Check if mouse is near any line segment
		for (let i = 0; i < lineCoords.length - 1; i++) {
			const [x1, y1] = lineCoords[i];
			const [x2, y2] = lineCoords[i + 1];

			if (this._pointToLineDistance(mouse[0], adjustedMouseY, x1, y1, x2, y2) < threshold) {
				return true; // Mouse is within threshold distance of a line segment
			}
		}

		return false; // Mouse is not near any line segment
	}

	/**
	 * Prepends a point to the beginning of the data array at the normalized
	 * date/value position.  This is non-destructive to the overall chart dataset
	 * since the prepend only affects the temporary render dataset.
	 * @param {object} data - Processed indicator data array
	 * @return {object}
	 */
	_prependNormalizedPoint(data) {
		const normalizedPoint = data.length && this._getNormalizedPoint(data[0][0]);
		if (normalizedPoint) data = [normalizedPoint, ...data];

		return data;
	}

	/**
	 * If enabled and conditions are correct, returns the normalized point that is ready for prepending.
	 * Description of correct conditions:
	 * - The chart has not been panned
	 * - The normalizedValues collection must exist
	 * - The params.prependNormalizedPoint feature is enabled
	 * - The chart is interday OR not a mix of domestic and international exchanges
	 * @param {object} data - Processed indicator data array
	 * @return {object}
	 */
	_getNormalizedPoint(firstSeriesIndex) {
		const { core } = this;
		if (
			!core.params.endOffsetDays &&
			core.params.normalizeValues &&
			core.params.prependNormalizedPoint &&
			(
				!core.state.isIntraday ||
				!this._isMixedDomesticIntlExchanges()
			) &&
			(
				this.params.markerType === markerType.LINE ||
				this.params.markerType === markerType.LINEWITHSHAPES ||
				this.params.markerType === markerType.FILL ||
				this.params.markerType === markerType.POSNEG ||
				this.params.markerType === markerType.STEPPED
			)
		) {
			const normalizeDate = new Date(core.params.normalizeValues.Date);
			normalizeDate.setMinutes(normalizeDate.getMinutes() + core.xref[core.params.symbol].utcOffset);
			const normalizedIndex = core.closestExchangeIndex(normalizeDate);

			// Only apply if we haven't panned and normalized point is on screen
			const domainLeft = this.panel.xAxis.primaryScale.domain()[0];
			if (
				normalizedIndex < firstSeriesIndex &&
				normalizedIndex >= domainLeft
			) {
				const normalizedClose = !core.panels[0].isNormalized
					? core.params.normalizeValues.Close
					: 0;

				return [normalizedIndex, normalizedClose];
			}
		}
	}

	/**
	 * Returns true if there is a mix of international and domestic exchanges on the chart
	 * @return {boolean}
	 */
	_isMixedDomesticIntlExchanges() {
		const { core } = this;
		const domesticRE = /^(DJI|MXP|NAQ|NAS|NSQ|NYQ|PSE|TOR)$/;
		const mixedExchanges = {};
		Object.keys(core.data).forEach(xid => {
			mixedExchanges[domesticRE.test(core.xref[xid].exchangeId)] = 1;
		});
		return Object.keys(mixedExchanges).length > 1;
	}

	/**
	 * @param {object} data Symbol data object
	 * @param {string} sessionType Indicator session type
	 */
	render(data = {}, sessionType = 'NormalSession') {
		const ctx = this.panel.rootContext;
		const { core } = this;
		data = data || {};

		const dataset =
			typeof data?.price === 'object' && data.price.length
				? data.price
				: data?.length ? data : [];

		const dataPrice = this._getDataMap(dataset);
		this.state.dataPrice = dataPrice; // store off for isWithin (hover) check later
		let priceMarkerType = this.params.markerType;
		let normalizeValue = 0;
		const hasPreviousClose = data?.previousclose?.length;
		const { style } = this.params;

		// determine normalizeValue for use in posneg markerType
		if (hasPreviousClose) {
			normalizeValue = data.previousclose[data.previousclose.length - 1].previousclose;
		} else if (!core.params.dataNormalized && core.params.normalizeValues) {
			// the dataset isn't normalized, but we know the normalized value (starting value) since it's returned with every API call
			normalizeValue = core.params.normalizeValues.Close;
		}

		// CH-1894 When returnPreviousPoint is enabled, and it's a bar-type marker (markers that actually have a width, like candlesticks), don't draw the first point because it will be truncated otherwise
		if (
			core.params.returnPreviousPoint &&
			typeof data.price === 'object' &&
			data.price.length > 1 &&
			(
				priceMarkerType === markerType.BAR ||
				priceMarkerType === markerType.CANDLESTICK ||
				priceMarkerType === markerType.HLC ||
				priceMarkerType === markerType.OHLC
			)
		) {
			dataPrice.shift();
		}

		// Downgrade posneg markerType if no normalizeValue found (technically should never happen, but just in case)
		if (
			priceMarkerType === 'posneg' &&
			typeof normalizeValue !== 'number'
		) {
			priceMarkerType = markerType.LINE;
			this.core.warn('Marker type set to "posneg" but normalize values not found. Changing to "line" markerType.');
		}

		// render as either SessionBreak or NormalSession
		if (sessionType === 'SessionBreak') {
			const color = this._getMarkerLineStyle('color', sessionType);
			const width = this._getMarkerLineStyle('width', sessionType);
			const lineDash = this._getMarkerLineStyle('lineDash', sessionType);
			const dataBreak = this._prependNormalizedPoint(dataPrice);

			// the SessionBreak indicator supports less markerTypes than the NormalSession indicator (only posneg, line, lineWithShapes, fill, and stepped)
			switch (priceMarkerType) {
				case markerType.POSNEG:
					// top clip
					this._clip(ctx, 'above', normalizeValue);
					this._renderLine(
						dataBreak,
						color,
						width,
						lineDash
					);
					this._clipEnd(ctx);
					// bottom clip
					this._clip(ctx, 'below', normalizeValue);
					this._renderLine(
						dataBreak,
						color,
						width,
						lineDash
					);
					this._clipEnd(ctx);
					break;
				case markerType.STEPPED:
					this._renderSteppedLine(
						dataBreak,
						color,
						width,
						lineDash
					);
					break;
				default: // line, linewithshapes, fill
					this._renderLine(
						dataBreak,
						color,
						width,
						lineDash
					);
					break;
			}
		} else {

			const prevCloseIndicators = this.panel.core.getIndicatorsById('previousclose');
			const prevCloseIndicator = prevCloseIndicators.length ? prevCloseIndicators[0] : null;
			const prevCloseVersion = prevCloseIndicator ? prevCloseIndicator.params.inputs.find(i => i.name === 'Version').value : 1;

			// Normal Session
			switch (priceMarkerType) {
				case markerType.BAR:
					this._renderBar(
						dataPrice,
						style.bar.color
					);
					break;
				case markerType.CANDLESTICK:
					this._renderCandlestick(
						dataPrice,
						style.candlestick.positive.color,
						style.candlestick.negative.color,
						style.candlestick.neutral.color,
						style.candlestick.line
					);
					break;
				case markerType.HLC:
					this._renderHLC(
						dataPrice,
						style.ohlc.positive.color,
						style.ohlc.negative.color,
						style.ohlc.neutral.color,
						style.ohlc.line
					);
					break;
				case markerType.LINE:
					this._renderLine(
						dataPrice,
						style.color,
						style.width,
						style.lineDash
					);
					break;
				case markerType.LINEWITHSHAPES:
					ctx.save();
					ctx.beginPath();
					// Create shape with offset for clipping area, but don't draw
					ctx.rect(0, 0, this.panel.size.width, this.panel.size.height);
					this._renderShape(
						dataPrice,
						{
							...style.shape,
							beginPath: false,
							color: null,
							// color: 'black',
							fillColor: null,
							length: style.shape.length + 2 * style.shape.offset,
							lineWidth: style.shape.width
						}
					);
					ctx.clip();
					this._renderLine(
						dataPrice,
						style.color,
						style.width,
						style.lineDash
					);
					ctx.restore(); // Get rid of clipping area
					this._renderShape(
						dataPrice,
						{
							...style.shape,
							fillColor: style.shape.background.color,
							lineWidth: style.shape.width
						}
					);
					break;
				case markerType.OHLC:
					this._renderOHLC(
						dataPrice,
						style.color,
						style.ohlc.positive.color,
						style.ohlc.negative.color,
						style.ohlc.neutral.color,
						style.ohlc.line
					);
					break;
				case markerType.POSNEG:

					if (!prevCloseIndicator || prevCloseVersion === 1 || !this.core.state.isIntraday) {
						// top clip
						this._clip(ctx, 'above', normalizeValue);
						this._renderFill(
							dataPrice,
							style.posneg.positive.background.color,
							style.posneg.positive.background.colorStop,
							normalizeValue,
							'pos'
						);
						this._renderLine(
							dataPrice,
							style.posneg.positive.color,
							style.posneg.width,
							style.posneg.lineDash
						);
						this._clipEnd(ctx);
						// bottom clip
						this._clip(ctx, 'below', normalizeValue);
						this._renderFill(
							dataPrice,
							style.posneg.negative.background.color,
							style.posneg.negative.background.colorStop,
							normalizeValue,
							'neg'
						);
						this._renderLine(
							dataPrice,
							style.posneg.negative.color,
							style.posneg.width,
							style.posneg.lineDash
						);
						this._clipEnd(ctx);
					} else {
						// special case for previous close indicator version 2

						const boxes = prevCloseIndicator._getV2BoundingBoxes(data);

						boxes.forEach(box => {

							// top clip
							this._clip(ctx, 'above', box.prevClose, box.dayLeft, box.dayRight);
							this._renderFill(
								dataPrice,
								style.posneg.positive.background.color,
								style.posneg.positive.background.colorStop,
								box.prevClose,
								'pos'
							);
							this._renderLine(
								dataPrice,
								style.posneg.positive.color,
								style.posneg.width,
								style.posneg.lineDash
							);
							this._clipEnd(ctx);
							// bottom clip
							this._clip(ctx, 'below', box.prevClose, box.dayLeft, box.dayRight);
							this._renderFill(
								dataPrice,
								style.posneg.negative.background.color,
								style.posneg.negative.background.colorStop,
								box.prevClose,
								'neg'
							);
							this._renderLine(
								dataPrice,
								style.posneg.negative.color,
								style.posneg.width,
								style.posneg.lineDash
							);
							this._clipEnd(ctx);
						});
					}

					break;
				case markerType.SHAPE:
					this._renderShape(
						dataPrice,
						{
							...style.shape,
							fillColor: style.shape.background.color,
							lineWidth: style.shape.width
						}
					);
					break;
				case markerType.STEPPED:
					this._renderSteppedLine(
						dataPrice,
						style.color,
						style.width,
						style.lineDash
					);
					break;
				default:
					this._renderFill(
						dataPrice,
						style.background.color,
						style.background.colorStop
					);
					this._renderLine(
						dataPrice,
						style.color,
						style.width,
						style.lineDash
					);
					break;
			}
		}
	}

	/**
	 * Renders indicator fill
	 * @param {object} data Symbol data object
	 * @param {string} fillColor Indicator fill color
	 * @param {string} fillColorStop Indicator stop fill color
	 * @param {number} fillToValue Value to fill to
	 */
	_renderFill(data, fillColor, fillColorStop, fillToValue, posNeg) {
		data = this._prependNormalizedPoint(data);
		// Handle case where non-numeric fillToValue is passed in
		if (typeof fillToValue !== 'number' && this.panel.isNormalized) fillToValue = 0.0;

		const fillToY = subpixel.px(Math.min(this.yScale(fillToValue), this.panel.size.height));
		const y0 = (!posNeg || posNeg === 'pos') ? 0 : fillToY;
		const y1 = (!posNeg || posNeg === 'neg') ? this.panel.size.height : fillToY;
		const fillStyle = fillColorStop
			? this._getLinearGradient(
				this.panel.rootContext,
				fillColor,
				fillColorStop,
				0, y0,
				0, y1
			)
			: fillColor;

		this.markers.line.renderFill(
			this.panel.rootContext,
			data,
			this.panel.size.padding.top + this.panel.size.height,
			this.xScale,
			this.yScale,
			fillStyle,
			fillToValue
		);
	}

	/**
	 * Renders a line indicator
	 * @param {object} data Symbol data object
	 * @param {string} lineColor Indicator line color
	 * @param {number} penPxOn Dashed line settings
	 * @param {number} penPxOff Dashed line settings
	 * @return {void}
	 */
	_renderLine(data, lineColor, lineWidth, lineDash) {
		data = this._prependNormalizedPoint(data);
		return this.markers.line.render(
			this.panel.rootContext,
			data,
			this.xScale,
			this.yScale,
			lineColor,
			lineWidth,
			lineDash
		);
	}

	/**
	 * Renders a stepped line
	 * @param {object} data Symbol data object
	 * @param {string} lineColor Indicator line color
	 * @return {void}
	 */
	_renderSteppedLine(data, lineColor, lineWidth, lineDash) {
		data = this._prependNormalizedPoint(data);
		return this.markers.line.render(
			this.panel.rootContext,
			data,
			this.xScale,
			this.yScale,
			lineColor,
			lineWidth,
			lineDash,
			true
		);
	}

	/**
	 * Renders a bar line
	 * @param {object} data Symbol data object
	 * @return {void}
	 */
	_renderBar(data = [], color) {
		return this.markers.bar.render(
			this.panel,
			this.panel.rootContext,
			data,
			this.xScale,
			this.yScale,
			color
		);
	}

	/**
	 * Renders the specified shape at the points specified by the data passed in
	 * @param {Object} data - Data to be plotted on chart
	 * @param {*} args - Arguments used to configure how to render the shape
	 */
	_renderShape(
		data,
		{
			beginPath,
			color,
			fillColor,
			length,
			lineWidth,
			offset,
			type
		}
	) {
		data = this._prependNormalizedPoint(data);
		this.markers.shape[type].render(
			this.panel.rootContext,
			data,
			this.xScale,
			this.yScale,
			{
				beginPath,
				color,
				fillColor,
				length,
				lineWidth,
				offset
			}
		);
	}

	/**
	 * Renders a candlestick line
	 * @param {object} data Symbol data object
	 */
	_renderCandlestick(data, colorPos, colorNeg, colorNeutral, lineStyle) {
		this.markers.candlestick.params.fillType = this.params.candleFillType;
		this.markers.candlestick.params.fillRule = this.params.candleFillRule;
		if (this.params.style.candlestick.size.large === true) {
			lineStyle = lineStyle.large.width;
		} else {
			lineStyle = lineStyle.default.width;
		}
		this.markers.candlestick.render(
			this.panel,
			this.panel.rootContext,
			data,
			this.xScale,
			this.yScale,
			colorPos,
			colorNeg,
			colorNeutral,
			lineStyle
		);
	}

	/**
	 * Renders an OHLC line
	 * @param {object} data Symbol data object
	 */
	_renderOHLC(data, color, colorUp, colorDown, colorNeutral, lineStyle) {
		if (this.params.style.ohlc.size.large === true) {
			lineStyle = lineStyle.large.width;
		} else {
			lineStyle = lineStyle.default.width;
		}
		switch (this.params.ohlcType) {
			case ohlcType.POSNEG:
				this.markers.candlestick.renderOHLCPosNeg(
					this.panel,
					this.panel.rootContext,
					data,
					this.xScale,
					this.yScale,
					colorUp,
					colorDown,
					colorNeutral,
					'ohlc',
					lineStyle
				);
				break;
			case ohlcType.NEWHIGHS:
				this.markers.candlestick.renderOHLCNewHighs(
					this.panel,
					this.panel.rootContext,
					data,
					this.xScale,
					this.yScale,
					colorUp,
					colorNeutral,
					'ohlc',
					lineStyle
				);
				break;
			case ohlcType.NEWLOWS:
				this.markers.candlestick.renderOHLCNewLows(
					this.panel,
					this.panel.rootContext,
					data,
					this.xScale,
					this.yScale,
					colorDown,
					colorNeutral,
					'ohlc',
					lineStyle
				);
				break;
			case ohlcType.MONO:
			default: // Draw monochrome
				this.markers.candlestick.renderOHLC(
					this.panel,
					this.panel.rootContext,
					data,
					this.xScale,
					this.yScale,
					color,
					'ohlc',
					lineStyle
				);
		}
	}

	/**
	 * Renders an HLC line
	 * @param {object} data Symbol data object
	 */
	_renderHLC(data, colorUp, colorDown, colorNeutral, lineStyle) {
		if (this.params.style.ohlc.size.large === true) {
			lineStyle = lineStyle.large.width;
		} else {
			lineStyle = lineStyle.default.width;
		}
		this.markers.candlestick.renderOHLCPosNeg(
			this.panel,
			this.panel.rootContext,
			data,
			this.xScale,
			this.yScale,
			colorUp,
			colorDown,
			colorNeutral,
			'hlc',
			lineStyle
		);
	}

	/**
	 * Sets the OHLC type used for rendering OHLC markers
	 * @param {ohlcType} type - The OHLC type
	 */
	set ohlcType(type) {
		if (Object.values(ohlcType).includes(type)) {
			this.params.ohlcType = type;
		} else {
			this.core.warn("Can't set invalid OHLC type");
		}
	}

	/**
	 * Gets the OHLC type
	 * @return {ohlcType} The OHLC type
	 */
	get ohlcType() {
		return this.params.ohlcType;
	}

	/**
	 * Sets the marker type
	 * @param {markerType} type - The marker type
	 */
	set markerType(type) {
		if (Object.values(markerType).includes(type)) {
			this.params.markerType = type;
			this.core.eventEmitter.emit(types.PRICE_MARKER_TYPE_SET, { type });
		} else {
			this.core.warn("Can't set invalid marker type");
		}
	}

	get markerType() {
		return this.params.markerType;
	}
}

/** Price Rate of Change indicator */
class PROCIndicator extends Indicator {
	/**
	 * Returns the default params for this type of indicator. Overrides abstract
	 * method inherited from Indicator.
	 * @returns {object} - Default parameters object
	 * @property {string} id - Indicator ID
	 * @property {string} name - Indicator name
	 * @property {string} description - Indicator description
	 * @property {object[]} inputs - Indicator inputs
	 */
	_getDefaultParams() {
		return {
			id: 'proc',
			name: 'Price Rate of Change',
			description:
				'A technical indicator that measures the percentage change between the most recent price and the price "n" periods in the past.',
			inputs: [{ name: 'period', value: 25 }],
			componentSeries: [
				{ id: 'proc' }
			]
		};
	}

	/**
	 * Renders the indicator zero line
	 * @param {CanvasRenderingContext2D} ctx Chart canvas context
	 * @param {number} yScale Y axis scale
	 */
	_renderZeroLine(ctx, yScale) {
		const left = this.panel.size.padding.left;
		const y0 = subpixel.px(yScale(0));
		const style = this.params.style;

		ctx.beginPath();
		ctx.lineWidth = style.zero.width;
		ctx.strokeStyle = style.zero.color;
		if (style.zero.lineDash) ctx.setLineDash(style.zero.lineDash.split(/\s*,\s*/));
		ctx.moveTo(left, y0);
		ctx.lineTo(this.panel.size.width + left, y0);
		ctx.stroke();
		if (style.zero.lineDash) ctx.setLineDash([]);
	}

	/** @param {object} data Symbol data object */
	render(data, sessionType) {
		const ctx = this.panel.rootContext;
		const yScale = this.yScale;

		this._renderLineMarker({
			data: data[this.params.id],
			sessionType
		});

		this._renderZeroLine(ctx, yScale);
	}
}

/** PSAR indicator */
class PSARIndicator extends Indicator {
	/**
	 * Returns the default params for this type of indicator. Overrides abstract
	 * method inherited from Indicator.
	 * @returns {object} - Default parameters object
	 * @property {string} id - Indicator ID
	 * @property {string} name - Indicator name
	 * @property {string} description - Indicator description
	 * @property {object[]} inputs - Indicator inputs
	 * @property {object[]} componentSeries - Indicator component series
	 */
	_getDefaultParams() {
		return {
			id: 'psar',
			name: 'PSAR',
			description:
				'Parabolic SAR signals the potential reversal of a trend.',
			inputs: [
				{ name: 'initialPosition', value: 0 },
				{ name: 'accelerationIncrease', value: 0.02, max: 1 },
				{ name: 'accelerationMaximum', value: 0.2, max: 1 }
			],
			componentSeries: [{ id: 'psar' }]
		};
	}

	/**
	 * @return {object}
	 * @property {CircleMarker} circlePSAR
	 */
	_getMarkers() {
		return (
			this.markers || {
				circlePSAR: new CircleMarker()
			}
		);
	}

	/** @param {object} data Symbol data object */
	render(data) {
		const ctx = this.panel.rootContext;
		const dataPSAR = this._getDataMap(data.psar);

		this.markers.circlePSAR.render(
			ctx,
			dataPSAR,
			this.xScale,
			this.yScale,
			{
				color: this.params.style.color,
				fillColor: this.params.style.color,
				length: this.params.style.length
			}
		);
	}
}

/** Revenue indicator */
class RevenuesIndicator extends Indicator {
	/**
	 * Returns the default params for this type of indicator. Overrides abstract
	 * method inherited from Indicator.
	 * @returns {object} - Default parameters object
	 * @property {string} id - Indicator ID
	 * @property {string} name - Indicator name
	 * @property {string} description - Indicator description
	 */
	_getDefaultParams() {
		return {
			id: 'revenues',
			name: 'Revenues',
			description: 'The Revenues chart indicator graphically represents a company\'s gross income over a specific period, providing a visual analysis of its sales performance and growth trends.',
			componentSeries: [
				{ id: 'revenues' }
			],
			markerType: 'stepped'
		};
	}

	/**
	 * @param {object} data - Symbol data object
	 * @return {number} - d3-range minimum
	 */
	getRangeMin(data) {
		return min(data.revenues || [], i => +i.revenues * 0.95);
	}

	/**
	 * @param {object} data - Symbol data object
	 * @return {number} - d3-range maximum
	 */
	getRangeMax(data) {
		return max(data.revenues || [], i => +i.revenues * 1.05);
	}

	/** @param {object} data Symbol data object */
	render(data, sessionType) {
		const dataRevenues = this._getDataMap(data.revenues);
		const xScale = this.xScale;
		const yScale = this.yScale;
		const style = this.params.style;
		const ctx = this.panel.rootContext;

		this._renderLineMarker({
			dataMap: dataRevenues,
			sessionType,
			isStepped: this.params.markerType === 'stepped'
		});

		// draw final segment to edge of chart
		if (dataRevenues.length) {
			const { core } = this;
			const lastDate = core.dataPrimary[core.dataPrimary.length - 1].date;
			const lastIndex = core.closestExchangeIndex(lastDate);
			const lastRevenuePoint = dataRevenues[dataRevenues.length - 1];
			const x0 = xScale(lastRevenuePoint[0]);
			const x1 = xScale(lastIndex);
			const y0 = yScale(lastRevenuePoint[1]);
			ctx.beginPath();
			ctx.strokeStyle = style.color;
			ctx.lineWidth = style.width;
			if (style.lineDash) {
				ctx.setLineDash(style.lineDash.split(/\s*,\s*/));
			}
			ctx.moveTo(subpixel.px(x0), subpixel.px(y0));
			ctx.lineTo(subpixel.px(x1), subpixel.px(y0));
			ctx.stroke();
			ctx.setLineDash([]);
		}
	}
}

/** Rolling Dividend indicator */
class RollingDividendIndicator extends Indicator {
	/**
	 * Returns the default params for this type of indicator. Overrides abstract
	 * method inherited from Indicator.
	 * @returns {object} - Default parameters object
	 * @property {string} id - Indicator ID
	 * @property {string} name - Indicator name
	 * @property {string} description - Indicator description
	 */
	_getDefaultParams() {
		return {
			id: 'rollingdividend',
			name: 'Rolling Dividend',
			description: 'The Rolling Dividend chart indicator tracks and visualizes the total dividend payments made by a company over a rolling period.',
			componentSeries: [
				{ id: 'rollingdividend' }
			],
			markerType: 'stepped'
		};
	}

	/**
	 * @param {object} data - Symbol data object
	 * @return {number} - d3-range minimum
	 */
	getRangeMin(data) {
		return min(data.rollingdividend || [], i => +i.rollingdividend * 0.95);
	}

	/**
	 * @param {object} data - Symbol data object
	 * @return {number} - d3-range maximum
	 */
	getRangeMax(data) {
		return max(data.rollingdividend || [], i => +i.rollingdividend * 1.05);
	}

	/** @param {object} data Symbol data object */
	render(data, sessionType) {
		const dataDividend = this._getDataMap(data.rollingdividend);
		const xScale = this.xScale;
		const yScale = this.yScale;
		const style = this.params.style;
		const ctx = this.panel.rootContext;

		this._renderLineMarker({
			dataMap: dataDividend,
			sessionType,
			isStepped: this.params.markerType === 'stepped'
		});

		// draw final segment to edge of chart
		if (dataDividend.length) {
			const { core } = this;
			const lastDate = core.dataPrimary[core.dataPrimary.length - 1].date;
			const lastIndex = core.closestExchangeIndex(lastDate);
			const lastDividend = dataDividend[dataDividend.length - 1];
			const x0 = xScale(lastDividend[0]);
			const x1 = xScale(lastIndex);
			const y0 = yScale(lastDividend[1]);
			ctx.beginPath();
			ctx.strokeStyle = style.color;
			ctx.lineWidth = style.width;
			if (style.lineDash) {
				ctx.setLineDash(style.lineDash.split(/\s*,\s*/));
			}
			ctx.moveTo(subpixel.px(x0), subpixel.px(y0));
			ctx.lineTo(subpixel.px(x1), subpixel.px(y0));
			ctx.stroke();
			ctx.setLineDash([]);
		}
	}
}

/** Rolling EPS indicator */
class RollingEPSIndicator extends Indicator {
	/**
	 * Returns the default params for this type of indicator. Overrides abstract
	 * method inherited from Indicator.
	 * @returns {object} - Default parameters object
	 * @property {string} id - Indicator ID
	 * @property {string} name - Indicator name
	 * @property {string} description - Indicator description
	 */
	_getDefaultParams() {
		return {
			id: 'rollingeps',
			name: 'Rolling EPS',
			description: "A measure of a company's earnings per share based on the previous two quarters added to the upcoming two quarters' estimated EPS.",
			componentSeries: [
				{ id: 'rollingeps' }
			],
			markerType: 'stepped'
		};
	}

	/**
	 * @param {object} data - Symbol data object
	 * @return {number} - d3-range minimum
	 */
	getRangeMin(data) {
		return min(data.rollingeps || [], i => +i.rollingeps * 0.95);
	}

	/**
	 * @param {object} data - Symbol data object
	 * @return {number} - d3-range maximum
	 */
	getRangeMax(data) {
		return max(data.rollingeps || [], i => +i.rollingeps * 1.05);
	}

	/** @param {object} data Symbol data object */
	render(data, sessionType) {
		const dataEPS = this._getDataMap(data.rollingeps);
		const xScale = this.xScale;
		const yScale = this.yScale;
		const { style } = this.params;
		const ctx = this.panel.rootContext;

		this._renderLineMarker({
			dataMap: dataEPS,
			sessionType,
			isStepped: this.params.markerType === 'stepped'
		});

		// draw final segment to edge of chart
		if (dataEPS.length) {
			const { core } = this;
			const lastDate = core.dataPrimary[core.dataPrimary.length - 1].date;
			const lastIndex = core.closestExchangeIndex(lastDate);
			const lastEPS = dataEPS[dataEPS.length - 1];
			const x0 = xScale(lastEPS[0]);
			const x1 = xScale(lastIndex);
			const y0 = yScale(lastEPS[1]);
			ctx.beginPath();
			ctx.strokeStyle = style.color;
			ctx.lineWidth = style.width;
			if (style.lineDash) {
				ctx.setLineDash(style.lineDash.split(/\s*,\s*/));
			}
			ctx.moveTo(subpixel.px(x0), subpixel.px(y0));
			ctx.lineTo(subpixel.px(x1), subpixel.px(y0));
			ctx.stroke();
			ctx.setLineDash([]);
		}
	}
}

/** RSI indicator */
class RSIIndicator extends Indicator {
	/**
	 * Returns the default params for this type of indicator. Overrides abstract
	 * method inherited from Indicator.
	 * @returns {object} - Default parameters object
	 * @property {string} id - Indicator ID
	 * @property {string} name - Indicator name
	 * @property {string} description - Indicator description
	 * @property {object[]} inputs - Indicator inputs
	 * @property {object[]} bands - Indicator bands
	 */
	_getDefaultParams() {
		return {
			id: 'rsi',
			name: 'Relative Strength Index',
			description:
				'The Relative Strength Index is a momentum indicator which measures the strength of price changes relative to prior moves.',
			inputs: [{ name: 'period', value: 14 }],
			bands: [
				{
					labelTranslationKey: 'band.overbought',
					valHigh: 100,
					valLow: 70
				},
				{
					labelTranslationKey: 'band.oversold',
					valHigh: 30,
					valLow: 0
				}
			],
			componentSeries: [
				{ id: 'rsi' }
			]
		};
	}

	/** @return {number} 0 */
	getRangeMin() {
		return 0;
	}

	/** @return {number} 100 */
	getRangeMax() {
		return 100;
	}

	/**
	 * @return {object}
	 * @property {LineMarker} line
	 * @property {BandMarker} overbought
	 * @property {BandMarker} oversold
	 */
	_getMarkers() {
		return {
			line: new LineMarker(),
			overbought: new BandMarker(this.params.bands[0], this.panel),
			oversold: new BandMarker(this.params.bands[1], this.panel)
		};
	}

	/**
	 * Sets indicator bands by period
	 * per JIRA NXC-368
	 */
	_setBandsByPeriod() {
		const period = this.getInput('period') || 0;
		let overBought = [100, 70];
		let overSold = [30, 0];

		if (period <= 2) {
			overBought = [100, 90];
			overSold = [10, 0];
		} else if (period >= 22) {
			overBought = [100, 60];
			overSold = [40, 0];
		}

		this.markers.overbought.params.valHigh = overBought[0];
		this.markers.overbought.params.valLow = overBought[1];

		this.markers.oversold.params.valHigh = overSold[0];
		this.markers.oversold.params.valLow = overSold[1];
	}

	/** @param {object} data Symbol data object */
	render(data, sessionType) {
		this._setBandsByPeriod();
		this.markers.overbought.render(this.panel, this.params.style.overbought);
		this.markers.oversold.render(this.panel, this.params.style.oversold);
		this._renderLineMarker({
			data: data[this.params.id],
			sessionType
		});
	}
}

/** Sector Industry indicator */
class SectorIndustryIndicator extends Indicator {
	/**
	 * Returns the default params for this type of indicator. Overrides abstract
	 * method inherited from Indicator.
	 * @returns {object} - Default parameters object
	 * @property {string} id - Indicator ID
	 * @property {string} name - Indicator name
	 * @property {string} description - Indicator description
	 * @property {object[]} inputs - Indicator inputs - Make sure to pass in a
	 * valid cusip as part of your args.params.inputs when creating a sector
	 * industry indicator
	 * @property {object[]} componentSeries - Indicator component series
	 * @property {number} valueMultiplier - Indicator value multiplier. Used to
	 * normalize percent values depending on data - 1 or 100
	 */
	_getDefaultParams() {
		return {
			id: 'sectorindustry',
			name: 'Sector & Industry',
			description: 'The Sector/Industry chart indicator enables a comparative analysis between a company\'s stock performance and that of a broader sector or industry symbol.',
			inputs: [
				{ name: 'cusip', value: 'INVALIDCUSIP' } // note: pass valid cusip when creating indicator
			],
			componentSeries: [{ id: 'sectorindustry' }],
			valueMultiplier: 1
		};
	}

	/** @param {object} data Symbol data object */
	render(data, sessionType) {
		this._renderLineMarker({
			data: data[this.params.id],
			sessionType
		});
	}
}

/** SMA indicator */
class SMAIndicator extends Indicator {
	/**
	 * Returns the default params for this type of indicator. Overrides abstract
	 * method inherited from Indicator.
	 * @returns {object} - Default parameters object
	 * @property {string} id - Indicator ID
	 * @property {string} name - Indicator name
	 * @property {string} description - Indicator description
	 * @property {object[]} inputs - Indicator inputs
	 * @property {object[]} componentSeries - Indicator component series
	 */
	_getDefaultParams() {
		return {
			id: 'sma',
			name: 'SMA',
			description:
				'Moving Averages are trend indicators that smooth stock price movements. Comparing the moving averages from multiple time frames can help investors spot changes in trend.',
			inputs: [{ name: 'period', value: 20 }],
			componentSeries: [{ id: 'sma' }]
		};
	}

	/**
	 * Renders the line marker
	 * @param {object} data - Symbol data object
	 * @property {string} sessionType - Session type (eg. NormalSession, SessionBreak)
	 */
	render(data, sessionType) {
		this._renderLineMarker({
			data: data[this.params.id],
			sessionType
		});
	}
}

/** Stochastics indicator */
class StochasticsIndicator extends Indicator {
	/**
	 * Returns the default params for this type of indicator. Overrides abstract
	 * method inherited from Indicator.
	 * @returns {object} - Default parameters object
	 * @property {string} id - Indicator ID
	 * @property {string} name - Indicator name
	 * @property {string} dataSeries - Indicator data series
	 * @property {string} description - Indicator description
	 * @property {object[]} inputs - Indicator inputs
	 */
	_getDefaultParams() {
		return {
			id: 'stochastics',
			name: 'Stochastics',
			dataSeries: 'stochastick',
			description:
				'Stochastics show periods when a stock is overbought or oversold.',
			inputs: [
				{ name: 'kperiod', value: 5 },
				{ name: 'smoothing', value: 3 },
				{ name: 'dperiod', value: 3 }
			],
			bands: [
				{
					labelTranslationKey: 'band.overbought',
					valHigh: 100,
					valLow: 80
				},
				{
					labelTranslationKey: 'band.oversold',
					valHigh: 20,
					valLow: 0
				}
			],
			componentSeries: [
				{ id: 'stochastick' },
				{ id: 'stochasticd', styleRef: 'd' }
			]
		};
	}

	/** @return {number} 0 */
	getRangeMin() {
		return 0;
	}

	/** @return {number} 100 */
	getRangeMax() {
		return 100;
	}

	/**
	 * @return {object}
	 * @property {LineMarker} lineK
	 * @property {LineMarker} lineD
	 * @property {BandMarker} overbought
	 * @property {BandMarker} oversold
	 */
	_getMarkers() {
		return {
			lineD: new LineMarker(),
			lineK: new LineMarker(),
			overbought: new BandMarker(this.params.bands[0], this.panel),
			oversold: new BandMarker(this.params.bands[1], this.panel)
		};
	}

	/** @param {object} data Symbol data object */
	render(data, sessionType) {
		const { style } = this.params;

		this.markers.overbought.render(this.panel, style.overbought);
		this.markers.oversold.render(this.panel, style.oversold);

		this._renderLineMarker({
			dataMap: this._getDataMap(data.stochastics, 'stochastick'),
			marker: this.markers.lineK,
			sessionType
		});

		this._renderLineMarker({
			dataMap: this._getDataMap(data.stochastics, 'stochasticd'),
			marker: this.markers.lineD,
			dataKey: 'd',
			sessionType
		});
	}
}

/** Time Series Forecast indicator */
class TSFIndicator extends Indicator {
	/**
	 * Returns the default params for this type of indicator. Overrides abstract
	 * method inherited from Indicator.
	 * @returns {object} - Default parameters object
	 * @property {string} id - Indicator ID
	 * @property {string} name - Indicator name
	 * @property {string} description - Indicator description
	 * @property {object[]} inputs - Indicator inputs
	 * @property {object[]} componentSeries - Indicator component series
	 */
	_getDefaultParams() {
		return {
			id: 'tsf',
			name: 'Time Series Forecast',
			description: 'The Time Series Forecast function predicts a future value of a field using the value of a given period linear regression line.',
			inputs: [{ name: 'period', value: 20 }],
			componentSeries: [{ id: 'tsf' }]
		};
	}

	/** @param {object} data Symbol data object */
	render(data, sessionType) {
		this._renderLineMarker({
			data: data[this.params.id],
			sessionType
		});
	}
}

/** Ultimate Oscillator indicator */
class UltimateOscillatorIndicator extends Indicator {
	/**
	 * Returns the default params for this type of indicator. Overrides abstract
	 * method inherited from Indicator.
	 * @returns {object} - Default parameters object
	 * @property {string} id - Indicator ID
	 * @property {string} name - Indicator name
	 * @property {string} description - Indicator description
	 * @property {object[]} inputs - Indicator inputs
	 * @property {object[]} bands - Indicator bands
	 */
	_getDefaultParams() {
		return {
			id: 'ultimateoscillator',
			name: 'Ultimate Oscillator',
			description:
				"The Ultimate Oscillator reduces the effect of time from the oscillator's bullish and bearish signals. This indicator is most effective when it offers a bullish or bearish divergence.",
			inputs: [
				{ name: 'period1', value: 7 },
				{ name: 'period2', value: 14 },
				{ name: 'period3', value: 28 },
				{ name: 'factor1', value: 4, max: 10 },
				{ name: 'factor2', value: 2, max: 10 },
				{ name: 'factor3', value: 1, max: 10 }
			],
			bands: [
				{
					labelTranslationKey: 'band.overbought',
					valHigh: 100,
					valLow: 70
				},
				{
					labelTranslationKey: 'band.oversold',
					valHigh: 30,
					valLow: 0
				}
			],
			componentSeries: [
				{ id: 'ultimateoscillator' }
			]
		};
	}

	/** @return {number} 0 */
	getRangeMin() {
		return 0;
	}

	/** @return {number} 100 */
	getRangeMax() {
		return 100;
	}

	/**
	 * @return {object}
	 * @property {LineMarker} line
	 * @property {BandMarker} overbought
	 * @property {BandMarker} oversold
	 */
	_getMarkers() {
		return {
			line: new LineMarker(),
			overbought: new BandMarker(this.params.bands[0], this.panel),
			oversold: new BandMarker(this.params.bands[1], this.panel)
		};
	}

	/** @param {object} data Symbol data object */
	render(data, sessionType) {
		this.markers.overbought.render(this.panel, this.params.style.overbought);
		this.markers.oversold.render(this.panel, this.params.style.oversold);
		this._renderLineMarker({
			data: data[this.params.id],
			sessionType
		});
	}
}

/** Up/Down indicator */
class UpDownIndicator extends Indicator {
	/**
	 * Returns the default params for this type of indicator. Overrides abstract
	 * method inherited from Indicator.
	 * @returns {object} - Default parameters object
	 * @property {string} id - Indicator ID
	 * @property {string} name - Indicator name
	 * @property {string} description - Indicator description
	 */
	_getDefaultParams() {
		return {
			id: 'updown',
			name: 'Up/Down',
			description: 'The Up/Down Ratio indicator shows the relationship between the volume of advancing issues and the volume of declining issues. Upside volume is simply the sum of all volume associated with stocks that closed up in price, while downside volume is the sum of all volume associated with stocks that closed down in price.'
		};
	}

	/**
	 * @return {object}
	 * @property {BarMarker} bar
	 */
	_getMarkers() {
		return { bar: new BarMarker() };
	}

	/** @param {object} data Symbol data object */
	render(data) {
		this.markers.bar.render(
			this.panel,
			this.panel.rootContext,
			this._getDataMap(data.updown),
			this.xScale,
			this.yScale,
			this.params.style.color
		);
	}
}

/** Up/Down Ratio indicator */
class UpDownRatioIndicator extends Indicator {
	/**
	 * Returns the default params for this type of indicator. Overrides abstract
	 * method inherited from Indicator.
	 * @returns {object} - Default parameters object
	 * @property {string} id - Indicator ID
	 * @property {string} name - Indicator name
	 */
	_getDefaultParams() {
		return {
			id: 'updownratio',
			name: 'Up/Down Ratio',
			description: 'The Up/Down ratio indicator shows the relationship between the volume of advancing issues and the volume of declining issues. Upside volume is simply the sum of all volume associated with stocks that closed up in price, while downside volume is the sum of all volume associated with stocks that closed down in price.',
			inputs: [
				{ name: 'Numerator', value: 10025197 },
				{ name: 'Denominator', value: 10025198 }
			]
		};
	}

	/** @param {object} data Symbol data object */
	render(data, sessionType) {
		this._renderLineMarker({
			data: data[this.params.id],
			sessionType
		});
	}
}

/** Volume indicator */
class VolumeIndicator extends Indicator {
	/**
	 * Returns the default params for this type of indicator. Overrides abstract
	 * method inherited from Indicator.
	 * @returns {object} - Default parameters object
	 * @property {string} id - Indicator ID
	 * @property {string} name - Indicator name
	 * @property {string} description - Indicator description
	 * @property {string} markerType - Indicator marker type - 'default' | 'posneg'
	 */
	_getDefaultParams() {
		return {
			id: 'volume',
			name: 'Volume',
			description:
				'Volume is the number of shares traded in a given period.',
			markerType: 'default', // default, posneg
			valueMultiplier: 1 // see CH-1793
		};
	}

	/**
	 * @param {object} data - Symbol data object
	 * @return {number} - d3-range minimum
	 */
	getRangeMin(data) {
		const panelRange = this.panel.xAxis.primaryScale.domain();

		// Filter the volume array
		const filteredVolume = (data.volume || []).filter((v, index) => {
			// Skip first element if returnPreviousPoint is selected so range size is not affected
			if (index === 0 && this.panel.core.params.returnPreviousPoint) {
				return false;
			}
			// Skip elements that are out of view so range size is not affected
			return v.dateIndex <= panelRange[1];
		});

		return this._min(
			0,
			min(filteredVolume, i => +i.volume)
		);
	}

	/**
	 * @param {object} data - Symbol data object
	 * @return {number} - d3-range maximum
	 */
	getRangeMax(data) {
		const panelRange = this.panel.xAxis.primaryScale.domain();

		// Filter the volume array instead of mutating it
		const filteredVolume = (data.volume || []).filter((v, index) => {
			// Skip first element if returnPreviousPoint is selected so range size is not affected
			if (index === 0 && this.panel.core.params.returnPreviousPoint) {
				return false;
			}
			// Skip elements that are out of view so range size is not affected
			return v.dateIndex <= panelRange[1];
		});

		return this._max(
			0,
			max(filteredVolume, i => +i.volume)
		);
	}

	/**
	 * @return {object}
	 * @property {BarMarker} bar
	 */
	_getMarkers() {
		return { bar: new BarMarker() };
	}

	/**
	 * Returns pos/neg/unchanged volume data based against the price dataset
	 * @param {object[]} data - Indicator data
	 * @param {object[]} dataPrice - Symbol price data
	 * @param {string} type - Type of volume data - 'pos' | 'neg' | 'unch'
	 */
	_getDataMapPosNeg(data, dataPrice, type) {
		// return pos/neg/unchanged volume data based against the price dataset
		switch (type) {
			case 'pos':
				return data.map((d, x) => {
					return [
						d.dateIndex,
						typeof dataPrice[x].diff === 'number' &&
						dataPrice[x].diff > 0
							? +d.volume
							: 0
					];
				});

			case 'neg':
				return data.map((d, x) => {
					return [
						d.dateIndex,
						typeof dataPrice[x].diff === 'number' &&
						dataPrice[x].diff < 0
							? +d.volume
							: 0
					];
				});

			case 'unch':
				return data.map((d, x) => {
					return [
						d.dateIndex,
						dataPrice[x].diff === 0 ? +d.volume : 0
					];
				});
		}
	}

	/** @param {object} data Symbol data object */
	render(data) {
		const positiveColor = this.panel.isUpper
			? this.params.style.upper?.positive?.color ?? this.params.style.positive.color
			: this.params.style.positive.color;

		const negativeColor = this.panel.isUpper
			? this.params.style.upper?.negative?.color ?? this.params.style.negative.color
			: this.params.style.negative.color;

		const neutralColor = this.panel.isUpper
			? this.params.style.upper?.neutral?.color ?? this.params.style.neutral.color
			: this.params.style.neutral.color;

		// either posneg rendering (requires a price dataset to determine colors) or default rendering (single color)
		if (
			this.params.markerType === 'posneg' &&
			data.price &&
			data.volume &&
			data.price.length &&
			data.volume.length &&
			data.volume[0].date - data.price[0].date === 0
		) {

			const posData = this._getDataMapPosNeg(data.volume, data.price, 'pos');
			const negData = this._getDataMapPosNeg(data.volume, data.price, 'neg');
			const unchData = this._getDataMapPosNeg(data.volume, data.price, 'unch');

			// CH-1894 When ReturnPreviousPoint is enabled, don't draw the first point
			if (this.panel.core.params.returnPreviousPoint) {
				if (posData.length > 1) posData.shift();
				if (negData.length > 1) negData.shift();
				if (unchData.length > 1) unchData.shift();
			}

			this.markers.bar.render(
				this.panel,
				this.panel.rootContext,
				posData,
				this.xScale,
				this.yScale,
				positiveColor,
				'bottom',
				this.params.style.radii
			);

			this.markers.bar.render(
				this.panel,
				this.panel.rootContext,
				negData,
				this.xScale,
				this.yScale,
				negativeColor,
				'bottom',
				this.params.style.radii
			);

			this.markers.bar.render(
				this.panel,
				this.panel.rootContext,
				unchData,
				this.xScale,
				this.yScale,
				neutralColor,
				'bottom',
				this.params.style.radii
			);
		} else {
			// Use a single default color for non-posneg rendering
			const defaultColor = this.panel.isUpper
				? this.params.style.upper?.color ?? this.params.style.color
				: this.params.style.color;

			this.markers.bar.render(
				this.panel,
				this.panel.rootContext,
				this._getDataMap(data.volume),
				this.xScale,
				this.yScale,
				defaultColor,
				'bottom',
				this.params.style.radii
			);
		}
	}
}

/** Volume By Price indicator */
class VolumeByPriceIndicator extends Indicator {
	/**
	 * Returns the default params for this type of indicator. Overrides abstract
	 * method inherited from Indicator.
	 * @returns {object} - Default parameters object
	 * @property {string} id - Indicator ID
	 * @property {string} datasetId - ID to send with the chartAPI request. Most
	 * indicators will not pass this in.
	 * @property {string} name - Indicator name
	 * @property {string} description - Indicator description
	 * @property {boolean} noClip - Whether to clip the indicator
	 * @property {object[]} inputs - Indicator inputs
	 */
	_getDefaultParams() {
		return {
			id: 'volumebyprice',
			datasetId: 'volume', // this will be the id sent to the chartAPI request.  typical indicators will not pass this.
			name: 'Volume by Price',
			description:
				'An indicator that shows the amount of volume for a particular price range.',
			noClip: true,
			inputs: [
				{ name: 'numBars', value: 12 },
				{ name: 'gapSize', value: 1 },
				{ name: 'barWidth', value: 0.5 }
			]
		};
	}

	/** @return {null} */
	getRangeMin() {
		return null;
	}

	/** @return {null} */
	getRangeMax() {
		return null;
	}

	/** @return {object} */
	_getMarkers() {
		return {};
	}

	/** @return {null} */
	_getDataMap() {
		return null;
	}

	/** @param {object} data */
	render(data) {
		if (!data.volume || !data.price) {
			this.core.warn('The Volume indicator is needed to render Volume By Price');
			return;
		}

		const ctx = this.panel.rootContext;
		const gapSize = this.params.inputs[1].value;
		const maxData = this.panel.yAxis.primaryScale.domain();
		const dist = maxData[0] - maxData[1];
		const numBars = this.params.inputs[0].value;
		const size = dist / numBars;
		const barHeight = this.panel.size.height / numBars;
		const bucketsUp = [];
		const bucketsDown = [];
		const bucketsUpWidth = [];
		const xDomain = this.panel.xAxis.primaryScale.domain();
		const paddingLeft = this.panel.size.padding.left;
		let x = 0;
		let y = 0;
		let maxVal = 0;
		let y0 = 0;
		let width = 0;
		const fullWidth = this.panel.size.width * this.params.inputs[2].value;

		// init buckets
		for (y = 0; y < numBars; y++) {
			bucketsUp[y] = 0;
			bucketsDown[y] = 0;
		}

		// populate buckets
		for (x = 0; x < data.volume.length; x++) {
			if (!data.price[x]) {
				continue;
			}

			const diff = x > 0 && data.price[x - 1] ? data.price[x].close - data.price[x - 1].close : 0;
			const val = data.price[x].close;
			const volume = data.volume[x].volume;
			let threshold = maxData[1];

			// don't include datapoints that are offscreen
			if (
				!diff ||
				data.price[x].dateIndex < xDomain[0] ||
				data.price[x].dateIndex > xDomain[1]
			) {
				continue;
			}

			// populate
			for (y = 0; y < numBars; y++) {
				if (val >= threshold && val < threshold + size) {
					if (diff >= 0) {
						bucketsUp[y] += volume;
					} else if (diff < 0) {
						bucketsDown[y] += volume;
					}
				}

				threshold += size;
			}
		}

		// find max row
		for (y = 0; y < numBars; y++) {
			maxVal = Math.max(bucketsUp[y] + bucketsDown[y], maxVal);
		}

		// render bucketsUp
		ctx.beginPath();
		ctx.fillStyle = this.params.style.up.color;

		for (x = bucketsUp.length - 1; x >= 0; x--) {
			width = (bucketsUp[x] / maxVal) * fullWidth;

			subpixel.fillRect(
				ctx,
				paddingLeft + 1, // Plus 1 so as not to draw over the left border
				y0 + gapSize,
				width,
				barHeight - gapSize * 2
			);

			y0 += barHeight;

			bucketsUpWidth[x] = width;
		}

		ctx.fill();
		ctx.stroke();

		// render bucketsDown
		ctx.beginPath();
		ctx.fillStyle = this.params.style.down.color;
		y0 = 0;

		for (x = bucketsDown.length - 1; x >= 0; x--) {
			width = (bucketsDown[x] / maxVal) * fullWidth;

			subpixel.fillRect(
				ctx,
				paddingLeft + bucketsUpWidth[x] + 2, // Plus 2 so as not overlap with up buckets
				y0 + gapSize,
				width,
				barHeight - gapSize * 2
			);

			y0 += barHeight;
		}

		ctx.fill();
	}
}

/** Volume Rate of Change indicator */
class VROCIndicator extends Indicator {
	/**
	 * Returns the default params for this type of indicator. Overrides abstract
	 * method inherited from Indicator.
	 * @returns {object} - Default parameters object
	 * @property {string} id - Indicator ID
	 * @property {string} name - Indicator name
	 * @property {string} description - Indicator description
	 * @property {object[]} inputs - Indicator inputs
	 */
	_getDefaultParams() {
		return {
			id: 'vroc',
			name: 'Volume Rate of Change',
			description:
				'Volume Rate of Change measures the difference in volume from one period vs. an earlier period. It is a useful comparison that helps investors understand how committed buyers and sellers are to the recent price trend.',
			inputs: [{ name: 'period', value: 14 }],
			componentSeries: [
				{ id: 'vroc' }
			]
		};
	}

	/**
	 * Renders the zero line for this indicator
	 * @param {CanvasRenderingContext2D} ctx - Chart canvas context
	 * @param {number} yScale - Chart y axis scale
	 */
	_renderZeroLine(ctx, yScale) {
		const style = this.params.style;
		// zero line
		ctx.beginPath();
		const yZero = subpixel.px(yScale(0));
		ctx.strokeStyle = style.zero.color;
		ctx.lineWidth = style.zero.width;
		if (style.zero.lineDash) {
			ctx.setLineDash(style.zero.lineDash.split(/\s*,\s*/));
		}
		ctx.moveTo(this.panel.size.padding.left, yZero);
		ctx.lineTo(
			this.panel.size.padding.left + this.panel.size.width,
			yZero
		);
		ctx.stroke();
		ctx.setLineDash([]);
	}

	/** @param {object} data Symbol data object */
	render(data, sessionType) {
		const ctx = this.panel.rootContext;
		const yScale = this.yScale;

		this._renderZeroLine(ctx, yScale);

		this._renderLineMarker({
			data: data[this.params.id],
			sessionType
		});
	}
}

/** Volume-Weighted Average Price indicator */
class VWAPIndicator extends Indicator {
	/**
	 * Returns the default params for this type of indicator. Overrides abstract
	 * method inherited from Indicator.
	 * @returns {object} - Default parameters object
	 * @property {string} id - Indicator ID
	 * @property {string} name - Indicator name
	 * @property {string} description - Indicator description
	 * @property {object[]} inputs - Indicator inputs
	 */
	_getDefaultParams() {
		return {
			id: 'vwap',
			name: 'VWAP',
			description: 'The volume-weighted average price (VWAP) is a trading benchmark used by traders that gives the average price a security has traded at throughout the day, based on both volume and price. (Investopedia)  It is only available for Intraday timeframes.',
			inputs: [],
			componentSeries: [{ id: 'vwap' }]
		};
	}

	/** @param {object} data Symbol data object */
	render(data, sessionType) {
		this._renderLineMarker({
			data: data[this.params.id],
			sessionType
		});
	}
}

/** Williams %R indicator */
class WilliamsIndicator extends Indicator {
	/**
	 * Returns the default params for this type of indicator. Overrides abstract
	 * method inherited from Indicator.
	 * @returns {object} - Default parameters object
	 * @property {string} id - Indicator ID
	 * @property {string} name - Indicator name
	 * @property {string} description - Indicator description
	 * @property {object[]} inputs - Indicator inputs
	 * @property {object[]} bands - Indicator bands
	 */
	_getDefaultParams() {
		return {
			id: 'williamspctr',
			name: 'Williams %R',
			description:
				'Williams %R uses a similar calculation to the Stochastic Oscillator and provides information on the price momentum of a security. It is also used to identify overbought and oversold levels.',
			inputs: [{ name: 'period', value: 14 }],
			bands: [
				{
					labelTranslationKey: 'band.overbought',
					valHigh: 0,
					valLow: -20
				},
				{
					labelTranslationKey: 'band.oversold',
					valHigh: -80,
					valLow: -100
				}
			],
			componentSeries: [
				{ id: 'williamspctr' }
			]
		};
	}

	/** @return {number} -100 */
	getRangeMin() {
		return -100;
	}

	/** @return {number} 0 */
	getRangeMax() {
		return 0;
	}

	/**
	 * @return {object}
	 * @property {LineMarker} line
	 * @property {BandMarker} overbought
	 * @property {BandMarker} oversold
	 */
	_getMarkers() {
		return {
			line: new LineMarker(),
			overbought: new BandMarker(this.params.bands[0], this.panel),
			oversold: new BandMarker(this.params.bands[1], this.panel)
		};
	}

	/** @param {object} data Symbol data object */
	render(data, sessionType) {
		this.markers.overbought.render(this.panel, this.params.style.overbought);
		this.markers.oversold.render(this.panel, this.params.style.oversold);
		this._renderLineMarker({
			data: data.williamspctr,
			sessionType
		});
	}
}

/** WMA indicator */
class WMAIndicator extends Indicator {
	/**
	 * Returns the default params for this type of indicator. Overrides abstract
	 * method inherited from Indicator.
	 * @returns {object} - Default parameters object
	 * @property {string} name - Indicator name
	 * @property {string} id - Indicator ID
	 * @property {string} description - Indicator description
	 * @property {object[]} inputs - Indicator inputs
	 * @property {object[]} componentSeries - Indicator component series
	 */
	_getDefaultParams() {
		return {
			name: 'WMA',
			id: 'wma',
			description:
				'Moving Averages are trend indicators that smooth stock price movements. Comparing the moving averages from multiple time frames can help investors spot changes in trend. Like the EMA, Weighted Moving Averages place greater emphasis on more recent periods.',
			inputs: [{ name: 'period', value: 20 }],
			componentSeries: [{ id: 'wma' }]
		};
	}

	/**
	 * Renders the line marker
	 * @param {object} data - Symbol data object
	 * @property {string} sessionType - Session type (eg. NormalSession, SessionBreak)
	 */
	render(data, sessionType) {
		this._renderLineMarker({
			data: data[this.params.id],
			sessionType
		});
	}
}

/** Comparative Relative Strength indicator */
class YieldSpreadIndicator extends Indicator {
	/**
	 * Returns the default params for this type of indicator. Overrides abstract
	 * method inherited from Indicator.
	 * @returns {Object} - Default parameters object
	 * @property {String} id - Indicator ID
	 * @property {String} name - Indicator name
	 * @property {Object[]} inputs - Indicator inputs
	 * @property {Number} valueMultiplier - Indicator value multiplier. Used to
	 * normalize percent values depending on data - 1 or 100
	 */
	_getDefaultParams() {
		return {
			id: 'yieldspread',
			name: 'Yield Spread',
			description: 'The Yield Spread Indicator measures the difference in yields between two distinct debt instruments, typically bonds, highlighting potential economic trends or investor sentiment shifts in the bond market.',
			inputs: [
				{ name: 'comparisonsymbol', value: '' }
			],
			valueMultiplier: 1
		};
	}

	/** @param {Object} data - Symbol data object */
	render(data, sessionType) {
		this._renderLineMarker({
			data: data[this.params.id],
			sessionType
		});
	}
}

var Indicators = new Map([
	['adl', ADLIndicator],
	['advancedecline', AdvanceDeclineIndicator],
	['avgtruerange', AverageTrueRangeIndicator],
	['awesomeoscillator', AwesomeOscillatorIndicator],
	['bestworst', BestWorstIndicator],
	['bollinger', BollingerBandsIndicator],
	['bollingerbandwidth', BollingerBandWidthIndicator],
	['chaikins', ChaikinsIndicator],
	['commoditychannelindex', CommodityChannelIndexIndicator],
	['crs', CRSIndicator],
	['custom', PriceIndicator],
	['dividendyield', DividendYieldIndicator],
	['dmi', DMIIndicator],
	['ema', EMAIndicator],
	['gsrecommendation', GSRecommendationIndicator],
	['highlow', HighLowIndicator],
	['horizontalannotation', HorizontalAnnotationIndicator],
	['ichimokucloud', IchimokuCloudIndicator],
	['linearregression', LinearRegressionIndicator],
	['macd', MACDIndicator],
	['mae', MAEIndicator],
	['massindex', MassIndexIndicator],
	['momentum', MomentumIndicator],
	['moneyflow', MoneyFlowIndicator],
	['moneyflowindex', MoneyFlowIndexIndicator],
	['nrv', NRVIndicator],
	['onbalancevolume', OnBalanceVolumeIndicator],
	['perange', PERangeIndicator],
	['peratio', PERatioIndicator],
	['percentb', PercentBIndicator],
	['previousclose', PreviousCloseIndicator],
	['price', PriceIndicator],
	['pricechannel', PriceChannelIndicator],
	['proc', PROCIndicator],
	['psar', PSARIndicator],
	['revenues', RevenuesIndicator],
	['rollingdividend', RollingDividendIndicator],
	['rollingeps', RollingEPSIndicator],
	['rsi', RSIIndicator],
	['sectorindustry', SectorIndustryIndicator],
	['sma', SMAIndicator],
	['stochastics', StochasticsIndicator],
	['tsf', TSFIndicator],
	['ultimateoscillator', UltimateOscillatorIndicator],
	['updownnyse', UpDownRatioIndicator], // NXC-411, for backwards-compatibility
	['updownratio', UpDownRatioIndicator],
	['updown', UpDownIndicator],
	['volume', VolumeIndicator],
	['volumebyprice', VolumeByPriceIndicator],
	['vroc', VROCIndicator],
	['vwap', VWAPIndicator],
	['williamspctr', WilliamsIndicator],
	['wma', WMAIndicator],
	['yieldspread', YieldSpreadIndicator]
]);

/** Parent class for chart overlay events (dividends, earnings etc.) */
class Event {
	/** @param {Object} args - Custom event args */
	constructor(args) {
		/** @type {Panel} */
		this.panel = args.panel;
		/** @type {object} */
		this.params = this._getParams(args.params);
		/** @type {object[]} */
		this.coords = [];
	}

	/**
	 * A wrapper for getDefaultParams. Merges default params with
	 * developer-provided params.
	 * @param {object} args - Custom event args
	 * @returns {object} Combined default and developer-provided params.
	 */
	_getParams(params) {
		return {
			...this.getDefaultParams(),
			style: this.panel.core.getStyle('event'),
			uid: createUid(),
			visible: true, // Whether or not the event should be rendered
			...params
		};
	}

	/**
	 * Gets the x/y coordinates for each event
	 * @param {object} data - Symbol data
	 * @param {string} eventType - Specific event type
	 * @returns {object[]} Normalized array of event coordinates with data
	 */
	getPositions(data, eventType) {
		if (!data[eventType] || !data.price) return [];

		const coords = [];
		let date;
		let i = 0;
		let x = 0;
		let y = 0;
		let closestPriceIdx = 0;
		let closestExchangeIdx = 0;
		let dataPrice = [];
		let dataSeries = 0;
		let customValue = false;
		const dataEvent = data[eventType];
		const isIntraday = this.panel.core.state.isIntraday;
		const isOHLC = /candlestick|ohlc|hlc/.test(
			this.panel.indicators[0].params.markerType
		);
		const yOHLC = isOHLC ? 'high' : 'close';

		for (const uid in dataEvent || {}) {
			if (Object.prototype.hasOwnProperty.call(dataEvent, uid)) {
				dataSeries = dataEvent[uid];

				for (i = 0; i < dataSeries.length; i++) {
					if (!dataSeries[i]) continue;

					customValue = typeof dataSeries[i].value === 'number';

					let dateIn = dataSeries[i].date;

					// discard time portion of interday dates
					if (!isIntraday && typeof dateIn === 'string') {
						dateIn = dateIn.replace(/T[\d:Z]+$/, '');
					}

					// Enforce zulu time for cross-browser compatibility
					if (typeof dateIn === 'string' && /T.+[^Z]$/.test(dateIn)) {
						dateIn = `${dateIn}Z`;
					}

					date = new Date(Date.parse(dateIn));

					if (eventType === 'custom') {
						// offset date to local tz
						date.setMinutes(
							date.getMinutes() + date.getTimezoneOffset()
						);
					}

					closestExchangeIdx = this.panel.core.closestExchangeIndex(
						date
					);

					if (customValue) {
						x = this.panel.xAxis.primaryScale(closestExchangeIdx);
						y = this.panel.yAxis.primaryScale(dataSeries[i].value);
					} else {
						if (date > new Date()) {
							x = -1;
						} else {
							dataPrice = this.getFirstDataset(data.price);

							closestPriceIdx = this.panel.core._getIndexByDate(
								dataPrice,
								date
							);

							x = this.panel.xAxis.primaryScale(closestExchangeIdx);
							y = this.panel.yAxis.primaryScale(
								dataPrice[closestPriceIdx][yOHLC]
							);
						}
					}

					// must fall within panel area
					if (
						x > this.panel.size.padding.left - this.params.style.canvasBufferWidth.left &&
						x < this.panel.size.padding.left + this.panel.size.width + this.params.style.canvasBufferWidth.right
					) {
						coords.push({
							x,
							y,
							event: dataSeries[i],
							uid: this.params.uid
						});
					}
				}
			}
		}

		return this.getStackPositions(coords);
	}

	/**
	 * Updates coordinate positions to stack events vertically that are close
	 * in horizontal proximity
	 * @param {object[]} coords - array of event coordinates and data
	 * @returns {object[]} array of updated event coordinates and data
	 * @private
	 */
	getStackPositions(coords) {
		const { style } = this.params;
		// The horizontal distance between event icons that will trigger stacking
		const clusterDist = this.params.clusterDist || style.cluster.distance || 15;
		// The vertical distance between stacked event icons
		const stackDist = this.params.stackDist || style.stack.distance || 17;
		let prev = {};
		let curr = {};
		let alt = {};

		// check for collisions with other event groups
		for (let i = coords.length - 1; i >= 0; i--) {
			curr = coords[i];

			for (const altEvent of this.panel.events) {
				if (
					altEvent !== this &&
					altEvent.coords &&
					!altEvent.isHidden
				) {
					for (let a = altEvent.coords.length - 1; a >= 0; a--) {
						alt = altEvent.coords[a];

						if (Math.abs(alt.x - curr.x) < clusterDist) {
							// stack curr event on top of alt
							curr.y = alt.y - stackDist;

							// snap curr x to alt
							curr.x = alt.x;
						}
					}
				}
			}
		}

		// check for collisions with neighbor events in the same event group
		for (let i = coords.length - 1; i >= 0; i--) {
			prev = coords[i - 1];
			curr = coords[i];

			// check left-hand neighbor in current event group
			if (prev && Math.abs(prev.x - curr.x) < clusterDist) {
				// stack prev on curr
				prev.y = curr.y - stackDist;

				// snap prev x to curr x
				prev.x = curr.x;
			}
		}

		return coords;
	}

	/**
	 * Returns the first available dataset
	 * @param {object} data - symbol data
	 * @return {object[]}
	 * @private
	 */
	getFirstDataset(data) {
		for (const x in data) {
			if (Object.prototype.hasOwnProperty.call(data, x)) {
				return data[x];
			}
		}
	}

	/**
	 * Render default event flags if none overridden locally. Called in sub-class
	 * defined render function with default params for that sub-class.
	 * @param {object} data - symbol data
	 * @param {string} eventType - the event type name
	 * @param {string} fillColor - the hex color fill
	 * @param {string} label - the event label to be rendered
	 * @private
	 */
	renderDefault(data, eventType, fillColor, label) {
		const { core } = this.panel;
		// if a render listener has been registered, emit and return
		if (core.eventEmitter.listeners('EVENT_RENDER').length > 0) {
			return core.eventEmitter.emit('EVENT_RENDER', {
				id: eventType,
				name: this.params.name,
				coords: this.coords,
				ctx: this.panel.rootContext,
				event: this
			});
		}

		// draw default icon
		const { coords } = this;
		const ctx = this.panel.rootContext;
		const { diameter } = this.params.style;
		const radius = diameter / 2;
		const padding = 5;
		const offsetY = 20;

		ctx.fillStyle = fillColor;
		ctx.textBaseline = 'top';

		// pointer shape
		coords.forEach(c => {
			const x = c.x;
			const y = c.y - offsetY;
			ctx.beginPath();
			ctx.arc(x, y, radius, 0, 2 * Math.PI);
			ctx.moveTo(x + radius, y + 1);
			ctx.lineTo(x, y + diameter);
			ctx.lineTo(x - radius, y + 1);
			ctx.fill();
		});

		ctx.beginPath();

		// label
		ctx.fillStyle = '#222222';
		ctx.font = 'bold 14px Arial';
		coords.forEach(c => {
			const x = c.x;
			const y = c.y - offsetY;
			ctx.fillText(label, x - 5, y - 5.5);
		});

		// metadata
		coords.forEach(c => {
			const x = c.x;
			const y = c.y - offsetY;
			c.id = this.params.id;
			c.left = x - (radius + padding);
			c.top = y - (radius + padding);
			c.right = c.left + diameter + 2 * padding;
			c.bottom = c.top + diameter + 2 * padding;
			// debug hitbox
			//ctx.rect(coords[i].left, coords[i].top, coords[i].right - coords[i].left, coords[i].bottom - coords[i].top);
		});

		ctx.stroke();
		ctx.closePath();
	}
	/**
	 * Render function must be implemented in sub-class
	 * @abstract
	 */
	render() {
		throw new Error('Required Event method not found');
	}

	/**
	 * Remove function must be implemented in sub-class
	 * @abstract
	 */
	remove() {
		return;
	}

	/**
	 * Checks if mouse is within coordinates of one of the currently-visible events
	 * @param {number[]} mouse - mouse coordinates
	 * @return {boolean}
	 */
	isWithin([x, y]) {
		// Adjust Y coord for top margin. This is necessary because event y coords are saved relative to the zero value of the scale.
		y -= this.panel.size.margin.top;

		let response = null;
		const coords = this.coords || {};

		for (const key in coords) {
			const eventHitbox = coords[key];

			// Check to see if the mouse is within the event hitbox
			if (
				x > eventHitbox.left &&
				y > eventHitbox.top &&
				x < eventHitbox.right &&
				y < eventHitbox.bottom
			) {
				response = eventHitbox;
				break;
			}
		}

		return response;
	}

	/**
	 * Returns the default params for this type of Event. Must be implemented in sub-class
	 * @abstract
	 * @private
	 */
	getDefaultParams() {
		throw new Error('Required Event method not found');
	}
}

/**
 * Dividend event
 */
class DividendEvent extends Event {
	/**
	 * Overrides abstract method in Event class. Sets default params for this event
	 * @returns {object} object with event information
	 * @override
	 * @private
	 */
	getDefaultParams() {
		return {
			id: 'dividends',
			name: 'Dividends',
			description: 'Dividend events'
		};
	}
	/**
	 * Overrides abstract method in Event class. Sets default render params for this event
	 * @param {object} data - Symbol data
	 * @override
	 */
	render(data) {
		this.renderDefault(data, 'dividends', '#98E7F8', 'D');
	}
}

/**
 * Dividend Complete event (includes special dividends)
 */
class DividendCompleteEvent extends Event {
	/**
	 * Overrides abstract method in Event class. Sets default params for this event
	 * @returns {object} object with event information
	 * @override
	 * @private
	 */
	getDefaultParams() {
		return {
			id: 'dividendscomplete',
			name: 'Dividends Complete',
			description: 'Dividend events including special/irregular dividends'
		};
	}
	/**
	 * Overrides abstract method in Event class. Sets default render params for this event
	 * @param {object} data - Symbol data
	 * @override
	 */
	render(data) {
		this.renderDefault(data, 'dividendscomplete', '#F56F42', 'D');
	}
}

/**
 * Splits event
 */
class SplitsEvent extends Event {
	/**
	 * Overrides abstract method in Event class. Sets default params for this event
	 * @returns {object} object with event information
	 * @override
	 * @private
	 */
	getDefaultParams() {
		return {
			id: 'splits',
			name: 'Splits',
			description: 'description'
		};
	}
	/**
	 * Overrides abstract method in Event class. Sets default render params for this event
	 * @param {object} data - Symbol data
	 * @override
	 */
	render(data) {
		this.renderDefault(data, 'splits', '#D398F8', 'S');
	}
}

/**
 * Earnings event
 */
class EarningsEvent extends Event {
	/**
	 * Overrides abstract method in Event class. Sets default params for this event
	 * @returns {object} object with event information
	 * @override
	 * @private
	 */
	getDefaultParams() {
		return {
			id: 'earnings',
			name: 'Earnings',
			description: 'description',
			inputs: [{ name: 'includeextraordinary', value: 1 }]
		};
	}
	/**
	 * Overrides abstract method in Event class. Sets default render params for this event
	 * @param {object} data - Symbol data
	 * @override
	 */
	render(data) {
		this.renderDefault(data, 'earnings', '#99F997', 'E');
	}
}

/**
 * Custom Dividends event
 */
class DividendCustomEvent extends Event {
	/**
	 * Overrides abstract method in Event class. Sets default params for this event
	 * @returns {object} object with event information
	 * @override
	 * @private
	 */
	getDefaultParams() {
		return {
			id: 'dividendscustom',
			name: 'Custom Dividends',
			description: 'description'
		};
	}
	/**
	 * Overrides abstract method in Event class. Sets default render params for this event
	 * @param {object} data - Symbol data
	 * @override
	 */
	render(data) {
		this.renderDefault(data, this.params.id, '#97BEF9', 'D');
	}
}

/**
 * Custom splits event
 */
class SplitsCustomEvent extends Event {
	/**
	 * Overrides abstract method in Event class. Sets default params for this event
	 * @returns {object} object with event information
	 * @override
	 * @private
	 */
	getDefaultParams() {
		return {
			id: 'splitscustom',
			name: 'Custom Splits',
			description: 'description'
		};
	}
	/**
	 * Overrides abstract method in Event class. Sets default render params for this event
	 * @param {object} data - Symbol data
	 * @override
	 */
	render(data) {
		this.renderDefault(data, this.params.id, '#E498F8', 'S');
	}
}

/**
 * EarningsCustomEvent event
 */
class EarningsCustomEvent extends Event {
	/**
	 * Overrides abstract method in Event class. Sets default params for this event
	 * @returns {object} object with event information
	 * @override
	 * @private
	 */
	getDefaultParams() {
		return {
			id: 'earningscustom',
			name: 'Custom Earnings',
			description: 'description',
			inputs: [{ name: 'includeextraordinary', value: 1 }]
		};
	}
	/**
	 * Overrides abstract method in Event class. Sets default render params for this event
	 * @param {object} data - Symbol data
	 * @override
	 */
	render(data) {
		this.renderDefault(data, this.params.id, '#97F9D5', 'E');
	}
}

/**
 * Announced Earnings event
 */
class AnnouncedEarningsEvent extends Event {
	/**
	 * Overrides abstract method in Event class. Sets default params for this event
	 * @returns {object} object with event information
	 * @override
	 * @private
	 */
	getDefaultParams() {
		return {
			id: 'announcedearnings',
			name: 'Announced Earnings',
			description: 'description'
		};
	}
	/**
	 * Overrides abstract method in Event class. Sets default render params for this event
	 * @param {object} data - Symbol data
	 * @override
	 */
	render(data) {
		this.renderDefault(data, 'announcedearnings', '#99F997', 'E');
	}
}

/** Custom event */
class CustomEvent extends Event {
	/** @param {object} args - Custom event args */
	constructor(args) {
		super(args);

		// tag each datapoint with a uid
		this.params.dataset.forEach(datum => {
			datum.uid = createUid();
		});
	}
	/**
	 * Overrides abstract method in Event class. Sets default params for this event
	 * @returns {object} object with event information
	 * @override
	 * @private
	 */
	getDefaultParams() {
		return {
			id: 'custom',
			name: 'Custom',
			dataset: [],
			renderMethod: null,
			removeMethod: null
		};
	}
	/**
	 * Adds a new event data point to the event dataset
	 * @param {object} datapoint - the new event data point to add
	 * @returns {datapoint} returns newly created datapoint with attached uid
	 */
	addDatapoint(datapoint) {
		if (datapoint) {
			// add a uid
			datapoint.uid = createUid();

			this.params.dataset.push(datapoint);

			return datapoint;
		}
	}
	/**
	 * Removes an event data point based on the point's uid
	 * @param {number} uid - unique chart identifier for the data point
	 */
	removeDatapointByUid(uid) {
		if (uid) {
			for (let x = this.params.dataset.length - 1; x >= 0; x--) {
				if (this.params.dataset[x].uid === uid) {
					this.params.dataset.splice(x, 1);
				}
			}
		}
	}
	/**
	 * Perform developer-defined actions before removal
	 */
	remove() {
		if (this.params.removeMethod) {
			this.params.removeMethod(this);
		}
	}
	/**
	 * Overrides abstract method in Event class. Sets default render params for
	 * this event based on custom render method provided in initial args
	 * @override
	 */
	render(data) {
		if (this.params.renderMethod && this.params.dataset) { // backward compatible for v1 handling
			this.params.renderMethod(this.coords, this);
		} else {
			this.renderDefault(data, this.params.id, '#97BEF9', 'C');
		}
	}
}

/**
 * Draws vertical bands highlighting the extended hours sessions on each panel
 * Note: the extended data itself is rendered as normal via the price indicator, this
 * is just for the vertical band rendering.
 */
class ExtendedHoursMarker extends Marker {
	getDefaultParams() {
		return {
			style: this.panel.core.getStyle('marker.extendedHours')
		};
	}

	getPrePostData(dataIn) {
		return {
			pre: dataIn['PreMarketTrading'] || [],
			post: dataIn['PostMarketTrading'] || []
		};
	}

	render(dataIn) {
		const data = this.getPrePostData(dataIn);

		if (data.pre.length === 0 && data.post.length === 0) return;

		const ctx = this.panel.rootContext;

		// draw pre rects
		ctx.beginPath();
		ctx.fillStyle = this.params.style.preMarket.background.color;
		Object.keys(data.pre).forEach(key => {
			const val = data.pre[key];
			subpixel.rect(ctx, val.left, 1, val.width, val.height - 2);
		});
		ctx.fill();

		// draw post rects
		ctx.beginPath();
		ctx.fillStyle = this.params.style.postMarket.background.color;
		Object.keys(data.post).forEach(key => {
			const val = data.post[key];
			subpixel.rect(ctx, val.left, 1, val.width, val.height - 2);
		});
		ctx.fill();

		// draw pre start line
		ctx.beginPath();
		ctx.lineWidth =
			this.params.style.preMarket.startLine.width ||
			this.params.style.width;
		ctx.strokeStyle =
			this.params.style.preMarket.startLine.color ||
			this.params.style.color;
		Object.keys(data.pre).forEach(key => {
			const val = data.pre[key];
			subpixel.moveTo(ctx, val.left, 1);
			subpixel.lineTo(ctx, val.left, 1 + val.height - 3);
		});
		ctx.stroke();

		// draw pre end line
		ctx.beginPath();
		ctx.lineWidth =
			this.params.style.preMarket.stopLine.width ||
			this.params.style.width;
		ctx.strokeStyle =
			this.params.style.preMarket.stopLine.color ||
			this.params.style.color;
		Object.keys(data.pre).forEach(key => {
			const val = data.pre[key];
			subpixel.moveTo(ctx, val.left + val.width, 1);
			subpixel.lineTo(ctx, val.left + val.width, 1 + val.height - 3);
		});
		ctx.stroke();

		// draw post start line
		ctx.beginPath();
		ctx.lineWidth =
			this.params.style.postMarket.startLine.width ||
			this.params.style.width;
		ctx.strokeStyle =
			this.params.style.postMarket.startLine.color ||
			this.params.style.color;
		Object.keys(data.post).forEach(key => {
			const val = data.pre[key];
			subpixel.moveTo(ctx, val.left, 1);
			subpixel.lineTo(ctx, val.left, 1 + val.height - 3);
		});
		ctx.stroke();
	}
}

/**
 * Recursive function to create a new style object based upon new/current/old styles.
 * @param {object} newStyle - Collection of new styles, not yet applied.
 * @param {object} [currentStyle={}] - Collection of current styles.
 * @param {object} [oldStyle={}] - Collection of old styles, previously applied.
 * @returns {object} - Final collection of styles.
 */
function buildSoftStyles(newStyle, currentStyle = {}, oldStyle = {}) {
	// Check if the newStyle is an object and not null
	if (typeof newStyle === 'object' && newStyle !== null) {
		// Initialize updatedStyle as an array if newStyle is an array, otherwise as an object
		const updatedStyle = Array.isArray(newStyle) ? [] : {};

		// Iterate over keys in newStyle
		for (const key in newStyle) {
			// Ensure the key is an own property and not inherited
			if (Object.prototype.hasOwnProperty.call(newStyle, key)) {
				// Recursively call buildSoftStyles for each sub-property
				// This allows the function to handle nested style objects
				updatedStyle[key] = buildSoftStyles(
					newStyle[key],
					currentStyle[key],
					oldStyle[key]
				);
			}
		}

		// Return the aggregated style object
		return updatedStyle;
	}

	// If newStyle is not an object (i.e., a base case for recursion):
	// Check if the current style is the same as the old style
	if (currentStyle === oldStyle) {
		// If current style is the same as old style, return the new style
		return newStyle;
	}

	// If current style differs from old style, keep the current style
	return currentStyle;
}

/**
 * Utility function to update component styles as part of panel style updates.
 * @param {object} collection - Collection of components to update.
 * @param {boolean} doSoftUpdate - Flag that enables/disables check for if individual components have
 *                                 been altered from oldComponentStyles.
 * @param {Function} getOldStyles - Getter for old component styles.
 * @param {Function} getNewStyles - Getter for new component styles.
 * @param {Function} getCurrentStylesParent - Getter for parent of current styles. This is necessary to
 *                                            update the proper collection after the function has executed.
 * @param {object[]} subUpdates - Collection of updates to run on sub-components if a test is passed.
 */
function updateComponentStyles(collection, doSoftUpdate, getOldStyles, getNewStyles, getCurrentStylesParent, subUpdates = []) {
	for (const key in collection) {
		if (Object.prototype.hasOwnProperty.call(collection, key)) {
			const component = collection[key];
			const currentComponentStylesParent = getCurrentStylesParent(component);
			const oldComponentStyles = getOldStyles(component);
			const newComponentStyles = getNewStyles(component);
			const localComponentStyles = doSoftUpdate
				? buildSoftStyles(
					newComponentStyles,
					currentComponentStylesParent.style,
					oldComponentStyles
				)
				: newComponentStyles;

			// Update the current component styles
			currentComponentStylesParent.style = {
				...oldComponentStyles,
				...newComponentStyles,
				...localComponentStyles
			};

			// Update styles of sub-components (collections that live in the current component)
			subUpdates.forEach(sub => {
				if (sub.test(component)) sub.updater(component);
			});
		}
	}
}

/* eslint-disable brace-style */

/**
 * Panels are related groups of axes, indicators and markers on a shared pane.
 * Multiple panels can be stacked vertically and are managed by the parent chart.
 */
class Panel {
	constructor() {
		return;
	}
	/**
	 * initialize the fundamental collections of params, scales and indicators for a single panel
	 * initialize the canvas dom element
	 */
	init(core, params = {}) {
		this.core = core;

		// then merge with any custom params that were passed to init
		this.params = merge({}, this.getDefaultParams(), params);

		// unique id
		this.params.uid = 'uid' in params ? params.uid : createUid();

		// deprecated id, here for backward compatibility
		this.params.id = this.params.uid;

		// Use this.size collection internally instead of modifying the original params
		this.initPanelSize();

		// grid functionality
		this.grid = new Grid(this);

		// Set up important collections
		this.indicators = [];
		this.events = [];
		this.tools = [];
		this.flags = {};

		// Set up axes
		const start = this.size.padding.left;
		const stop = start + this.size.width;
		this.xAxis = new AxisDate(this, { range: [start, stop] });
		this.yAxis = new AxisNumber(this);

		// Set up important DOM nodes

		// Create container for all panel nodes
		this.rootPanel = this.core.rootModchart
			.insert('div', 'div.modcharts-rootmouse')
			.attr('class', 'modcharts-panel')
			.classed('modcharts-panel-first', this.isUpper === true)
			.attr('uid', this.params.uid)
			.style('width', '100%')
			.style('position', 'absolute');

		// use fixed or dynamic height
		this.size.height = this.calculatedHeight;

		// container for panel legend
		this.rootLegend = this.rootPanel
			.append('div')
			.attr('class', `modcharts-legend${(this.index === 0 ? ' first' : '')}`)
			.style('position', 'absolute')
			.on('mouseup', event => {
				this.core.onMouseup(event);
			});

		// main panel drawing canvas
		this.rootCanvas = this.rootPanel
			.append('canvas')
			.attr('aria-hidden', true)
			.attr('width', this.size.width)
			.attr('height', this.size.height)
			.style('position', 'absolute')
			.attr('class', 'modcharts-panel-root');

		// tool canvas
		this.rootTools = this.rootPanel
			.append('canvas')
			.attr('aria-hidden', true)
			.attr('width', this.size.width)
			.attr('height', this.size.height)
			.style('position', 'absolute')
			.attr('class', 'modcharts-panel-tools');

		// crosshair/overlay root svg
		this.rootOverlay = this.rootPanel
			.append('svg')
			.attr('aria-hidden', true)
			.attr('width', this.core.size.width)
			.attr('height', this.size.height)
			.style('position', 'absolute')
			.attr('class', 'modcharts-rootoverlay')
			.append('g');

		// error message
		this.rootError = this.rootPanel
			.append('div')
			.style('position', 'absolute')
			.style('display', 'none')
			.attr('class', 'modcharts-panel-error');

		// save reference to frequently-used contexts
		this.rootContext = this.getContext(this.rootCanvas.node());
		this.rootToolContext = this.getToolContext(this.rootTools.node());

		return this;
	}

	/**
	 * return main panel canvas context
	 */
	getContext(rootCanvasNode) {
		const context = rootCanvasNode.getContext('2d');

		if (!context.setLineDash) {
			context.setLineDash = () => {
				return;
			};
		}

		return context;
	}

	/**
	 * return tool canvas context
	 */
	getToolContext(rootToolsNode) {
		const context = rootToolsNode.getContext('2d');

		if (!context.setLineDash) {
			context.setLineDash = () => {
				return;
			};
		}

		return context;
	}

	/** default panel param collection.  any custom user params will be merged into this base collection. */
	getDefaultParams() {
		let panelStyle = this.core.getStyle('panel');
		// alternate styling for lower panels
		if (!this.isUpper) {
			panelStyle = merge(panelStyle, this.core.getStyle('panelLower'));
		}
		// alternate styling for left-hand YAxis panels
		if (this.core.params.yAxisSide === 'left') {
			panelStyle = merge(panelStyle, this.core.getStyle('panelYAxisLeft'));
		}

		return {
			enableCrosshair: true, // Use for per panel disabling of crosshair
			hasXAxis: null,
			style: panelStyle,
			size: {
				heightPct: null,
				heightToChartWidthRatio: null
			},
			yAxis: {
				format: 'default',
				range: null, // custom axis range. ex: [-100, 100]
				rangeValues: []
			}
		};
	}

	/** Remove root panel children and then the root panel itself */
	remove() {
		const panelNode = this.rootPanel.node();
		// Remove all children from the panel
		while (panelNode.firstChild) {
			panelNode.removeChild(panelNode.firstChild);
		}
		if (panelNode && panelNode.parentNode) {
			// Remove the panel from the chart
			panelNode.parentNode.removeChild(panelNode);
		}
	}

	/**
	 * add a new Indicator object to the indicators collection
	 * @param {string} id
	 * @param {object} params
	 * @returns {Indicator}
	 */
	addIndicator(id, params = { id }) {
		if (id && !params.id) params.id = id;

		const newIndicator = this.addIndicators([params]);

		return newIndicator.length > 0
			? newIndicator[0]
			: null;
	}

	/**
	 * Add multiple Indicator objects to the indicators collection
	 * @param {object[]} indicatorParams - An array of indicator params (panel reference added automatically)
	 * @returns {Indicator[]}
	 */
	addIndicators(indicatorParams) {
		let indicator;
		let IndicatorClass;
		const newIndicators = [];
		for (const [index, params] of indicatorParams.entries()) {
			IndicatorClass = Panel.getIndicatorClassName(params.id);
			if (IndicatorClass) {
				// Is this a lower indicator being placed on the upper panel?
				const foundLioup =
					this.isUpper &&
					this._checkForLioup(
						params.id,
						index + this.indicators.length
					);
				if (foundLioup) params.lowerIndicatorOnUpperPanel = true;

				// Build args for indicator constructor
				const args = {
					panel: this,
					params
				};

				if (params.lowerIndicatorOnUpperPanel) {
					// Add new scale to yAxis
					const newScaleUid = this.yAxis.addScale();

					// Change scale reference if lower indicator is on upper panel (point to the new scale)
					args.scaleUids = [this.xAxis.primaryScaleUid, newScaleUid];
				}

				// Create new indicator (e.g. BollingerBandsIndicator)
				indicator = new IndicatorClass(args);

				// Apply translations to name and description, if available
				indicator._updateTranslations();

				// Add new indicator to panel indicator array
				this.indicators.push(indicator);
				newIndicators.push(indicator);

				// Comparison was added
				if (
					indicator.params.id === 'price' &&
					indicator.params.symbol !== this.core.params.symbol
				) {
					this.core.eventEmitter.emit(types.COMPARISON_ADD, { issueId: indicator.params.symbol });
					// Reset the domain since the new request can have different exchangeDates and will make the old domain invalid (CH-1504)
					this.core.resetDomain();
				}

				// Emit new indicator
				const event = this.isUpper
					? types.INDICATOR_UPPER_ADD
					: types.INDICATOR_LOWER_ADD;
				this.core.eventEmitter.emit(event, {
					indicator,
					indicatorType: indicator.params.id
				});
			}
		}

		// Create any needed component series flags
		this.resetComponentSeriesFlags();

		return newIndicators;
	}

	_updateTranslations() {
		this.indicators.forEach(indicator => {
			indicator._updateTranslations();
		});
	}

	/** create or remove component series flags for this panel's indicators, depending on the param boolean */
	resetComponentSeriesFlags() {
		this.indicators.forEach(indicator => {
			// only run for indicators that have a componentSeries param
			if (indicator.params.componentSeries) {
				const { componentSeries } = indicator.params;

				if (this.core.params.showComponentSeriesFlags) {
					// create indicator flags
					for (const c of componentSeries) {
						const { id } = c;

						if (!indicator.flags[id]) {
							indicator.flags[id] = new IndicatorFlag({
								id: 'indicator',
								indicator,
								panel: this,
								seriesId: id
							});
						}
					}
				} else {
					// remove indicator flags
					for (const c of componentSeries) {
						const { id } = c;

						if (indicator.flags[id]) {
							indicator.flags[id].remove();
							delete indicator.flags[id];
						}
					}
				}
			}
		});
	}

	/**
	 * Returns true if mouse coordinates are within chart region (not including padding)
	 * @param {Number} x - X mouse coordinate for modcharts-rootmouse
	 * @param {Number} y - Y mouse coordinate for modcharts-rootmouse
	 */
	isWithin([x, y]) {
		const { height, padding, top, width } = this.size;

		return (
			x > padding.left &&
			x < padding.left + width &&
			y > top + padding.top &&
			y < top + padding.top + height
		);
	}

	/**
	 * add a new Event object to the events collection
	 * @param {string} id
	 * @param {object} params
	 */
	addEvent(id, params) {
		const args = { params, panel: this };
		let event;

		switch (id) {
			case 'dividends':
				event = new DividendEvent(args);
				break;
			case 'dividendscomplete':
				event = new DividendCompleteEvent(args);
				break;
			case 'splits':
				event = new SplitsEvent(args);
				break;
			case 'earnings':
				event = new EarningsEvent(args);
				break;
			case 'dividendscustom':
				event = new DividendCustomEvent(args);
				break;
			case 'splitscustom':
				event = new SplitsCustomEvent(args);
				break;
			case 'earningscustom':
				event = new EarningsCustomEvent(args);
				break;
			case 'announcedearnings':
				event = new AnnouncedEarningsEvent(args);
				break;
			case 'custom':
				event = new CustomEvent(args);
				break;
		}

		if (event) this.events.push(event);

		this.core.eventEmitter.emit(types.EVENT_ADD, {
			eventType: id
		});

		return event;
	}

	/**
	 * Set primary yAxis scaling method
	 * @param {string} scaleType - "linear" or "log"
	 */
	setScale(scaleType) {
		// don't allow log scales on normalized charts (has problems with zeroes and negative numbers)
		if (scaleType === 'log' && this.isNormalized) scaleType = 'linear';

		if (this.index > 0) scaleType = 'linear';

		this.core.params.yAxisScale = scaleType;

		this.yAxis.primaryScale = this.yAxis.createScale(
			this.yAxis.primaryScale.domain(),
			scaleType
		);

		this.core.eventEmitter.emit(types.PANEL_SCALE_CHANGE, { scaleType });
	}

	/**
	 * Get the min and max values for the given indicator.  This is used to determine the current y-axis range.
	 * @param {*} indicator
	 * @param {*} data
	 * @return {number[]}
	 */
	_getDataRange(indicator, data) {
		let min = Infinity;
		let max = -Infinity;

		for (const indicatorType in data) {
			if (
				data[indicatorType][indicator.params.uid] &&
				typeof indicator.params.visible === 'boolean' && indicator.params.visible
			) {
				const dataSeries = {};
				dataSeries[indicatorType] = data[indicatorType][indicator.params.uid];

				const rangeMin = indicator.getRangeMin(dataSeries);
				const rangeMax = indicator.getRangeMax(dataSeries);

				if (typeof rangeMin === 'number' && !isNaN(rangeMin)) {
					min = Math.min(min, rangeMin);
				}
				if (typeof rangeMax === 'number' && !isNaN(rangeMax)) {
					max = Math.max(max, rangeMax);
				}

				// ensure the yaxis has a range when min === max
				if (min === max) {
					min = min * 0.95;
					max = max * 1.05;
				}
			}
		}

		return { min, max };
	}

	/**
	 * Apply any max/min adjustments to range
	 * @param {number} min
	 * @param {number} max
	 * @param {number} maxLineWidth
	 * @param {Object} makeAdjustment
	 */
	_adjustRange(min, max, maxLineWidth, adjustForMarker = { max: true, min: true }) {
		if (
			this.core.params.yAxisScale === 'log' &&
			this.isUpper
		) {
			min = Math.max(min, 0.00001); // ensure min doesn't contain 0
		} else {
			// vertical yaxis padding (used to carve out room for chart legends, etc)
			const yAxisPadding = this._getYAxisPadding();

			// get number of pixels per price unit
			const pxDist = this.size.height / (max - min);

			// adjust max value by the specific price value to create a gap of specified pixel height
			max += yAxisPadding.top / pxDist;

			// adjust min value by the specific price value to create a gap of specified pixel height
			min -= yAxisPadding.bottom / pxDist;

			// Adjust max/min for the width of marker lines themselves
			if (adjustForMarker.max && yAxisPadding.top < maxLineWidth) max += maxLineWidth / pxDist;
			if (adjustForMarker.min && yAxisPadding.bottom < maxLineWidth) min -= maxLineWidth / pxDist;
		}

		return { min, max };
	}

	/**
	 * Checks to see if given marker type is part of the draw to bottom markers list
	 * @param {string} markerType - Marker type to check
	 * @return {Boolean} Whether or not marker type is on the list
	 */
	_checkForDrawToBottomMarker(markerType) {
		const drawToBottomMarkers = ['bar', 'posneg'];
		return !!drawToBottomMarkers.find(type => type === markerType);
	}

	/**
	 * Checks to see if given ID is part of the lower indicators on upper panel list
	 * @param {string} id - Indicator ID (eg. volume) to check
	 * @return {Boolean} Whether or not ID is on the list
	 */
	_checkForLioup(id, index) {
		const lowerIndicatorsOnUpperPanel = [
			'volume',
			'nrv'
		];
		return index > 0 && !!lowerIndicatorsOnUpperPanel.find(lioup => lioup === id);
	}

	/**
	 * Returns the max line width value given a current max value and and indicator with params
	 * @param {number} currentMax - The current max line width
	 * @param {Indicator} indicator - The indicator to check for a line width param
	 * @return {number} The updated max line width
	 */
	_updateMaxLineWidth(currentMax, indicator) {
		if (
			indicator.params.style &&
			typeof indicator.params.style.width === 'number'
		) {
			currentMax = Math.max(
				currentMax,
				indicator.params.style.width
			);
		}

		return currentMax;
	}

	/**
	 * Returns the min/max data range for all indicator datasets, based on the current slice of data
	 * Also adjusts for the Y-axis padding settings and marker thickness.
	 * @param {object[]} data
	 * @return {object}
	 */
	_getOverallDataRange(data) {
		// min/max calcs
		let min = Infinity;
		let max = -Infinity;
		let dataSymbol;
		let maxLineWidth = 1;
		const adjustForMarker = { max: true, min: true };
		const primarySymbol = this.core.params.symbol;

		// get min/max of each indicator's current range
		for (const [index, indicator] of this.indicators.entries()) {
			// Skip LIOUP's in this calculation
			const foundLioup = this.isUpper && this._checkForLioup(indicator.params.id, index);
			if (foundLioup) continue;

			maxLineWidth = this._updateMaxLineWidth(maxLineWidth, indicator);

			dataSymbol = indicator.params.symbol || primarySymbol;

			// Update min/max for data associated with current indicator
			const rangeBounds = this._getDataRange(indicator, data[dataSymbol]);
			if (rangeBounds.min < min) min = rangeBounds.min;
			if (rangeBounds.max > max) max = rangeBounds.max;

			// Don't adjust max value for marker thickness if marker type draws to bottom of chart
			const { markerType } = indicator.params;
			const foundDrawToBottomMarker = this._checkForDrawToBottomMarker(markerType);
			if (foundDrawToBottomMarker) adjustForMarker.min = false;
		}

		// adjust for user-defined range values
		this.params.yAxis.rangeValues.forEach(val => {
			min = Math.min(min, val);
			max = Math.max(max, val);
		});

		// Make adjustments for Y-axis padding and marker thickness
		({ min, max } = this._adjustRange(
			min,
			max,
			maxLineWidth,
			adjustForMarker
		));

		// ensure 0.0 is always visible on normalized charts
		if (this.isNormalized && !this.core.params.endOffsetDays) {
			min = Math.min(min, 0);
			max = Math.max(max, 0);
		}

		return { min, max };
	}

	/** inspect panel and indicator params for current padding value. */
	_getYAxisPadding() {
		// panel level
		let top = this.params.style.yAxis.padding.top || 0;
		let bottom = this.params.style.yAxis.padding.bottom || 0;

		// Certain indicators can override the value (currently those that contain bands, but not those using a secondary scale)
		this.indicators.forEach(indicator => {
			// Make sure padding styles exist and that indicator is using primary scale
			if (
				indicator.params.style.yAxisPadding &&
				indicator.scaleUids[1] === this.yAxis.primaryScaleUid
			) {
				if (typeof indicator.params.style.yAxisPadding.top === 'number') {
					top = indicator.params.style.yAxisPadding.top;
				}

				if (typeof indicator.params.style.yAxisPadding.bottom === 'number') {
					bottom = indicator.params.style.yAxisPadding.bottom;
				}
			}
		});

		return { top, bottom };
	}

	/**
	 * similar to _getOverallDataRange, this determines the min/max for all custom datasets based on the current slice
	 * of data about to be rendered.
	 */
	_getCustomDataRange(data) {
		// min/max calcs
		let min = Infinity;
		let max = -Infinity;
		let rangeMin;
		let rangeMax;
		let maxLineWidth = 0;

		for (const [index, el] of this.indicators.entries()) {
			// Skip volume indicator in this calculation
			const foundLioup = this.isUpper && this._checkForLioup(el.params.id, index);
			if (foundLioup) continue;

			// don't include hidden indicators
			if (typeof el.params.visible === 'boolean' && !el.params.visible) continue;

			if (el.params.style && el.params.style.width) {
				maxLineWidth = Math.max(
					maxLineWidth,
					el.params.style.width
				);
			}

			rangeMin = el.getRangeMin(data[el.params.datasetId]);
			rangeMax = el.getRangeMax(data[el.params.datasetId]);

			min = Math.min(rangeMin, min);
			max = Math.max(rangeMax, max);
		}

		/**
		 * vertical padding (used to carve out room for chart legends, etc)
		 */

		// get number of pixels per price unit
		const pxDist = this.size.height / (max - min);

		if (this.params.style.yAxis.padding.top) {
			// adjust max value by the specific price value to create a gap of specified pixel height
			max += this.params.style.yAxis.padding.top / pxDist;
		}

		if (this.params.style.yAxis.padding.bottom && min !== 0) {
			// adjust min value by the specific price value to create a gap of specified pixel height
			min -= this.params.style.yAxis.padding.bottom / pxDist;
		}

		// adjust max/min for the width of marker lines themselves
		max += maxLineWidth / pxDist;
		min -= maxLineWidth / pxDist;

		return { min, max };
	}

	/** search panel for indicator id ("sma", "bollinger") */
	getIndicatorsById(id) {
		const indicators = [];

		for (let x = 0; x < this.indicators.length; x++) {
			if (id === this.indicators[x].params.id) {
				indicators.push(this.indicators[x]);
			}
		}

		return indicators;
	}

	/** Measure the preceding panels' heights to get current top position */
	get calculatedTop() {
		let top = 0;

		for (const panel of this.core.panels) {
			const { size } = panel;

			// Add top margin before check because we should always add the top margin
			top += size.margin.top;

			// Stop adding if we reached this panel
			if (panel === this) break;

			top +=
				size.height +
				// size.margin.top +
				size.padding.bottom +
				size.padding.top +
				panel.getPanelMarginBottom(); // This shouldn't be necessary here, but using it for consistency
		}

		return top;
	}

	/**
 * Calculates the height of a chart panel. This function determines the panel height based on various parameters,
 * considering specific panel size settings, ratios, and the overall chart dimensions. It handles different scenarios,
 * such as when height values are explicitly set or need to be computed based on other factors.
 *
 * The function follows a set priority to determine the panel height:
 * 1. Direct panel size parameters.
 * 2. Panel size ratios (relative to the chart width).
 * 3. Global chart panel size parameters.
 * 4. Global chart panel size ratios.
 *
 * The calculated height includes padding and margin, and is adjusted based on the percentage of total chart height,
 * if not manually set.
 */
	get calculatedHeight() {
		const { isUpper } = this;
		let value = 0;
		let doAutosize = false;

		// Direct panel size setting has the highest priority
		if (this.params.size.height) {
			value = this.params.size.height;
		}
		// If panel height isn't set, use core parameters for upper or lower panels
		else if (this.core.params.panelHeightUpper && isUpper) {
			value = this.core.params.panelHeightUpper;
		} else if (this.core.params.panelHeightLower && !isUpper) {
			value = this.core.params.panelHeightLower;
		}
		else {
			// If no height or ratio is set, enable autosizing
			doAutosize = true;
		}

		// Autosize the panel height based on chart height and panel proportions
		if (doAutosize) {
			let nonChartHeight = 0;

			// Calculate the total non-chart height (padding and margin)
			for (const panel of this.core.panels) {
				const { size } = panel;
				nonChartHeight += size.padding.bottom + size.padding.top +
							panel.getPanelMarginBottom() + panel.getPanelMarginTop();
			}

			// Calculate the panel height as a proportion of the remaining chart height
			value = Math.round(
				this.size.heightPct * (this.core.size.height - nonChartHeight)
			);
		}
		return Math.max(0, value);
	}


	/**
	 * Returns the bottom margin for the parent panel based upon panel order and param flag
	 * @returns {Number} Value (in pixels) to be used for the panel bottom margin
	 */
	getPanelMarginBottom() {
		// Don't use the last panel bottom margin if flag is set
		return this === this.core.panels.slice(-1)[0] &&
			this.core.params.ignoreLastPanelBottomMargin
			? 0
			: this.size.margin.bottom;
	}

	/**
	 * Returns the top margin for the parent panel based upon panel order and param flag
	 * @returns {Number} Value (in pixels) to be used for the panel top margin
	 */
	getPanelMarginTop() {
		return this.isUpper ||	!this.core.params.ignoreLowerPanelTopMargin
			? this.size.margin.top
			: 0;
	}

	/** Initialize the panel size object */
	initPanelSize() {
		this.size = {
			height: 0,
			heightPct: this.params.size.heightPct,
			heightToChartWidthRatio: this.params.size.heightToChartWidthRatio,
			padding: { ...this.params.style.padding },
			margin: { ...this.params.style.margin },
			top: 0,
			width:
				+this.core.size.width -
				this.params.style.padding.left -
				this.params.style.padding.right
		};
	}

	/**
	 * Calculates the maximum text width for a given array of labels.
	 * @param {string[]} labels - An array of label strings to measure.
	 * @param {CanvasRenderingContext2D} ctx - The canvas rendering context used for measuring text width.
	 * @returns {number} The width of the longest label in pixels.
	 */
	_getMaxTextWidth(labels, ctx) {
		const { style } = this.params;
		if (labels.length === 0) return 0;

		// Sort the array based on the length of the strings in descending order
		labels.sort((a, b) => b.length - a.length);

		// Measure the width of the longest string
		const longestText = labels[0];
		ctx.textAlign = style.yAxis.label.textAlign;
		ctx.textBaseline = style.yAxis.label.textBaseline;
		ctx.fillStyle = style.yAxis.label.color;
		ctx.font = this.getFontStyle(
			style.yAxis.font.weight,
			style.yAxis.font.size,
			style.yAxis.font.family,
			style.yAxis.font.style
		);
		const textWidth = ctx.measureText(longestText).width;
		return textWidth;
	}

	/**
	 * Return the width of the YAxis padding. Normally this would just be a style, but now it can also support an auto width mode.
	 * @param {string} side - The side of the padding to calculate ('left' or 'right').
	 * @returns {number} The padding value in pixels for the specified side.
	 */
	_getPadding(side) {
		let padding = this.params.style.padding[side];

		if (typeof padding !== 'number') throw new Error('Invalid padding value'); // can happen with invalid user inputs

		if (this.core.params.panelYAxisAutoWidth && this.core.params.yAxisSide === side) {
			if (this.isUpper) {
				let labels = [];
				this.core.panels.forEach(panel => {
					const labelObjs = panel.yAxis.calculateRenderedLabels();
					const newLabels = labelObjs.map(row => row.label);
					labels = labels.concat(newLabels);
				});

				// Find the widest string using the YAxis font styles
				const maxWidth = this._getMaxTextWidth(labels, this.rootContext);
				padding = maxWidth + this.params.style.yAxis.label.padding.left + this.params.style.yAxis.label.padding.right;

				// Search for the flag with id 'lastClose' within the flags array
				const lastCloseFlag = this.indicators.length && this.indicators[0].flags.lastClose ? this.indicators[0].flags.lastClose : null;

				// Check if the lastCloseFlag exists and then calculate its width
				if (lastCloseFlag) {
					const lastCloseWidth = lastCloseFlag._calculateFlagLabelWidth();
					padding = Math.max(padding, lastCloseWidth);
				}

			} else if (this.core.panels?.length) {
				// If this is a lower panel, reuse the first panel's calculated padding so we don't have to recalculate
				padding = this.core.panels[0].size.padding[side];
			}
		}

		return padding;
	}

	_getPaddingRight() {
		return this._getPadding('right');
	}

	_getPaddingLeft() {
		return this._getPadding('left');
	}

	/**
	 * This method updates dynamic size properties of the panel:
	 * - Resizes and repositions panel DOM elements - the panel DIV itself, its CANVAS element, the error DIV, and the panel legend DIV
	 * - Updates the panel.size collection
	 */
	resize() {
		const { calculatedTop, core } = this;
		const rootOverlay = select(this.rootOverlay.node().parentNode);

		// Update size collection
		this.size.height = this.calculatedHeight; // dynamic height
		this.size.top = calculatedTop; // this panel's top, relative to the top of the overall chart container
		this.size.padding.right = this._getPaddingRight(); // either defined in styles, or auto-calculated based on widest label
		this.size.padding.left = this._getPaddingLeft(); // either defined in styles, or auto-calculated based on widest label
		this.size.left = this.size.padding.left;
		this.size.width = core.size.width - this.size.padding.left - this.size.padding.right;

		// Calculate canvas size collection
		const canvasSize = {
			height: this.size.height + this.size.padding.bottom + this.size.padding.top,
			left: 0,
			top: this.size.top,
			width: core.size.width
		};

		// Update DOM elements
		const ratio = core.getScreenRatio();
		const items = [
			this.rootCanvas,
			rootOverlay,
			this.rootTools
		];

		this.rootPanel.style('top', `${canvasSize.top}px`);

		for (const el of items) {
			const canvas = el.node();

			if (!canvas) return;

			if (ratio !== 1 && el !== rootOverlay) {
				// scale canvas by device ratio for High DPI screens
				canvas.width = Math.round(canvasSize.width * ratio);
				canvas.height = Math.round(canvasSize.height * ratio);

				canvas.style.width = `${canvasSize.width}px`;
				canvas.style.height = `${canvasSize.height}px`;

				canvas.getContext('2d').scale(ratio, ratio);

			} else if (canvasSize.height >= 0) {
				if (el !== rootOverlay) {
					canvas.width = canvasSize.width;
					canvas.height = canvasSize.height;
				}

				canvas.style.width = `${canvasSize.width}px`;
				canvas.style.height = `${canvasSize.height}px`;
			}
		}

		// Error container
		this.rootError
			.style('width', `${this.size.width}px`)
			.style('height', `${this.size.height}px`)
			.style('line-height', `${this.size.height}px`)
			.style('text-align', 'center');

		// Legend container
		this.rootLegend
			.style('width', `${this.size.width + this.size.padding.left}px`)
			.style('padding-left', `${this.size.padding.left}px`);
	}

	/** return true if panel is using normalized data */
	get isNormalized() {
		const { isUpper } = this;

		if (this.core.params.dataNormalized && isUpper) return true;

		if (this.core.comparisons.length && isUpper) return true;

		if (this.core.params.symbolCompare.length && isUpper) return true; // backward compatibility (CH-1504)

		if (this.getIndicatorsById('sectorindustry').length) return true;

		return false;
	}

	/** return true if panel is first on chart */
	get isUpper() {
		return this === this.core.panels[0];
	}

	/**
	 * Create a clip covering the chart area of the panel. This should be called BEFORE
	 * drawing on the canvas to properly clip the canvas.
	 * @param {CanvasRenderingContext2D} ctx - Canvas rendering context on which to create a clip
	 * @param {Number} clipWidth - Multipler used to increase the clip width. Defaults to 1.
	 */
	clipPanel(ctx, clipWidth = 1) {
		ctx = ctx || this.rootContext;

		const top = this.size.padding.top;
		const left = this.size.padding.left;
		const width = this.size.width * clipWidth;
		const height = this.size.height;

		ctx.beginPath();
		ctx.rect(left, top, width, height);
		ctx.clip();
	}

	/**
	 * render all axes, indicators and tools for this panel
	 * clip contents to panel region to remove any overlaps
	 */
	render(data, dateLabels, clipWidth) {
		if (!data && this.prevData) data = this.prevData;
		if (!dateLabels && this.prevDateLabels) dateLabels = this.prevDateLabels;

		this.prevData = data;
		this.prevDateLabels = dateLabels;

		// calculate event positions before rendering
		this.getEventPositions(data[this.core.params.symbol]);

		// legend
		this.revealLegend();

		// render axes
		this.renderAxes(dateLabels);

		// prepare main clip
		this.rootContext.save();

		// clip region around panel
		this.clipPanel(this.rootContext, clipWidth);

		// render indicators
		this.renderIndicators(data);

		// render extendedHours marker
		this.renderExtendedHours();

		// render special sessions
		this.renderSessions('SessionBreak');
		this.renderSessions('PreMarketTrading');
		this.renderSessions('PostMarketTrading');

		// restore clipped panel
		this.rootContext.restore();

		// render event icons outside of clip
		this.renderEvents(data);

		// render no-clip indicators outside of clip
		this.renderNoClipIndicators(data);

		// render tools
		this.renderTools();

		// render borders
		this.renderBorders();

		// render indicator flags outside of clip
		this.renderFlags();
	}

	/** Render the majority of panel indicators to the primary canvas element (skip no-clip indicators) */
	renderIndicators(data) {
		let indicatorSymbol = '';
		let elType = '';
		let ds = {};
		let exchangeId = '';
		const primarySymbol = this.core.params.symbol;
		const isCustom = this.indicators.length && this.indicators[0].params.id === 'custom';

		this.sessionClips = {};

		for (const indicator of this.indicators) {
			// Skip non-visible and no-clip indicators
			if (!indicator.params.visible || indicator.params.noClip) continue;

			// Custom indicators using custom datasets
			if (
				indicator.params.id === 'custom' &&
				data[indicator.params.datasetId]
			) {
				indicator.render(data[indicator.params.datasetId]);

			} else if (indicator.params.id === 'bestworst') {

				if (isCustom) {
					ds['price'] = data[this.indicators[0].params.datasetId].map(row => {
						return {
							date: row.date,
							close: row.value
						};
					});
				} else if (data[primarySymbol] && data[primarySymbol].price) {
					ds['price'] = this.core.getFirstDataset(
						data[primarySymbol].price
					);
				}
				indicator.render(ds);
			}

			indicatorSymbol = indicator.params.symbol || primarySymbol;

			// API-based indicators (data.MSFT.price, data.MSFT.sma, ...)
			for (elType in data[indicatorSymbol]) {
				if (Object.prototype.hasOwnProperty.call(data[indicatorSymbol], elType)) {
					// data.MSFT.price.<uid>
					if (data[indicatorSymbol][elType][indicator.params.uid]) {
						// construct the dataset passed to this indicator's render method
						ds = {};
						ds[elType] =
							data[indicatorSymbol][elType][indicator.params.uid];

						// special case: the "posneg" price indicator needs an additional previousclose datapoint in order to render
						if (
							data[indicatorSymbol].previousclose &&
							elType === 'price' &&
							indicator.params.markerType === 'posneg'
						) {
							ds['previousclose'] = this.core.getFirstDataset(
								data[indicatorSymbol].previousclose
							);
						}

						// special case: the "volume" and "nrv" indicators will need the price dataset when in "posneg" mode.
						if (
							data[indicatorSymbol].price &&
							(elType === 'volume' || elType === 'nrv') &&
							indicator.params.markerType === 'posneg'
						) {
							ds['price'] = this.core.getFirstDataset(
								data[indicatorSymbol].price
							);
						}

						// if intraday we need to clip to individual exchange sessions
						if (this.core.state.isIntraday) {

							if (this.core.xref[indicatorSymbol]) {
								exchangeId = this.core.xref[indicatorSymbol].exchangeId;

								// clip rectangular regions around intraday sessions
								if (this.core.params.clipSessions) {
									// normal session
									this.renderSession(
										ds,
										exchangeId,
										indicator,
										'NormalSession'
									);

									// extended sessions
									if (this.core.params.extendedHours) {
										this.renderSession(
											ds,
											exchangeId,
											indicator,
											'PreMarketTrading'
										);
										this.renderSession(
											ds,
											exchangeId,
											indicator,
											'PostMarketTrading'
										);
									}

									// session breaks (eg. lunch breaks). for performance, only render if session breaks actually exist
									if (
										this.core.timeService.sessionBreak &&
										this.params.style.sessionBreak
									) {
										this.renderSession(
											ds,
											exchangeId,
											indicator,
											'SessionBreak'
										);
									}

								} else {
									// don't clip sessions
									indicator.render(ds);
								}
							} else {
								indicator.render(ds);
							}
						} else {
							// not intraday; render everything
							indicator.render(ds);
						}
					}
				}
			}
		}
	}

	/**
	 * render a single intraday indicator clipped to its exchange sessions.  this eliminates any line segments between sessions.
	 */
	renderSession(ds, exchangeId, indicator, sessionType) {
		this.rootContext.save();

		if (!this.sessionClips[exchangeId]) {
			this.sessionClips[exchangeId] = {};
		}

		if (!this.sessionClips[exchangeId][sessionType]) {
			this.sessionClips[exchangeId][sessionType] = this.getSessionClips(
				exchangeId,
				sessionType
			);
		}

		this.clipIntradaySessions(this.sessionClips[exchangeId][sessionType]);

		indicator.render(ds, sessionType);

		this.rootContext.restore();
	}

	/** Render event icons outside of clip */
	renderEvents(data) {
		for (const event of this.events) {
			if (event.params.visible) event.render(data[this.core.params.symbol] || {});
		}
	}

	/** Render intraday extended hours vertical bands */
	renderExtendedHours() {
		if (!this.core.state.isIntraday) return;

		if (!this.extendedHoursMarker) {
			this.extendedHoursMarker = new ExtendedHoursMarker({}, this);
		}

		const primaryExchange = this.core.xref[this.core.params.symbol]
			? this.core.xref[this.core.params.symbol].exchangeId
			: null;

		if (this.sessionClips[primaryExchange]) {
			this.extendedHoursMarker.render(this.sessionClips[primaryExchange]);
		}
	}

	/** Calculate the coords property for all event icons */
	getEventPositions(data) {
		for (let i = 0; i < this.events.length; i++) {
			this.events[i].coords = null;
		}

		for (let i = 0; i < this.events.length; i++) {
			if (this.events[i].params.id === 'custom') {
				this.events[i].coords = this.events[i].getPositions(
					{
						custom: [this.events[i].params.dataset],
						price: data.price
					},
					'custom'
				);
			} else {
				this.events[i].coords = this.events[i].getPositions(
					data,
					this.events[i].params.id
				);
			}
		}
	}

	/** Render special indicators that aren't clipped to their intraday sessions (eg highlow, volumebyprice, etc) */
	renderNoClipIndicators(data) {
		let indicatorSymbol = '';
		let indicator = {};
		let elType = '';
		let ds = {};
		const primarySymbol = this.core.params.symbol;

		for (let i = 0; i < this.indicators.length; i++) {
			indicator = this.indicators[i];

			// Only draw visble, no-clip indicators
			if (!indicator.params.visible || !indicator.params.noClip) continue;

			indicatorSymbol = indicator.params.symbol || primarySymbol;
			ds = {};

			// Non-API driven indicators
			switch (indicator.params.id) {
				case 'highlow':
					// the highlow indicator uses the price dataset
					if (data[primarySymbol] && data[primarySymbol].price) {
						ds['price'] = this.core.getFirstDataset(
							data[primarySymbol].price
						);
					}

					// special case:  the "highlow" indicator can use the previousclose dataset to avoid collisions.
					if (data[primarySymbol].previousclose) {
						ds['previousclose'] = this.core.getFirstDataset(
							data[primarySymbol].previousclose
						);
					}

					indicator.render(ds);

					continue;
				case 'horizontalannotation':
					indicator.render();

					continue;
				case 'linearregression':
					// The "linearregression" indicator uses the price dataset
					if (indicator.params.parentUid || indicator.params.parentUID) {
						const parentIndicator = this.core.getIndicatorByUid(
							indicator.params.parentUid || indicator.params.parentUID
						);

						const symbol = parentIndicator.params.symbol || indicatorSymbol;
						ds['linearregression'] = this.core.getFirstDataset(
							data[symbol][parentIndicator.params.id]
						);
					} else if (data[primarySymbol] && data[primarySymbol].price) {
						ds['linearregression'] = this.core.getFirstDataset(
							data[primarySymbol].price
						);
					}
					indicator.render(ds);

					continue;
				case 'gsrecommendation':
					if (data[primarySymbol].gsrecommendation) {
						ds['gsrecommendation'] = data[primarySymbol].gsrecommendation[indicator.params.uid];
						indicator.render(ds);
					}
					continue;
				case 'volumebyprice':
					// the volumebyprice indicator uses the price dataset and volume dataset
					if (data[primarySymbol] && data[primarySymbol].price) {
						ds['price'] = this.core.getFirstDataset(
							data[primarySymbol].price
						);
					}

					if (data[primarySymbol] && data[primarySymbol].volume) {
						ds['volume'] = this.core.getFirstDataset(
							data[primarySymbol].volume
						);
					}

					indicator.render(ds);

					continue;
			}

			// API-based indicators (data.MSFT.price, data.MSFT.sma, ...)
			for (elType in data[indicatorSymbol]) {
				if (Object.prototype.hasOwnProperty.call(data[indicatorSymbol], elType)) {
					// data.MSFT.price.<uid>
					if (data[indicatorSymbol][elType][indicator.params.uid]) {
						// construct the dataset passed to this indicator's render method
						ds = {};
						ds[elType] =
							data[indicatorSymbol][elType][indicator.params.uid];

						indicator.render(ds);
					}
				}
			}
		}
	}

	/**
	 * return an array of rectangles representing clip regions for each exchange session.
	 * also draw holiday labels as needed
	 */
	getSessionClips(exchangeId, sessionType) {
		const clips = [];
		const dates = this.core.exchangeDates;
		const dateStart = new Date(dates[0]);
		const dateStop = new Date(dates[dates.length - 1]);
		const padding = this.size.padding;
		const scale = this.xAxis.primaryScale;
		const typicalSessions = this.core.timeService.typicalSessions;
		const labelHeight = 10;
		const alternateSessions = this.core.timeService.alternateSessions;

		let sessions = [];
		let session = {};
		let left = 0;
		let right = 0;
		let mid = 0;
		let s = 0;
		let labelWidth = 0;
		let ctx = {};
		let dateLeft = new Date();
		let dateRight = new Date();
		let exchangeClosure = null;
		let regionWidthAdjustment = null;

		// default sessiontype
		if (!sessionType) {
			sessionType = 'NormalSession';
		}

		// ensure candlesticks don't get cut in half when a normal session is followed by SessionBreak or AlternateSession (CW-880)
		if (
			sessionType === 'NormalSession' &&
			Object.keys(this.core.xAxisDomainAdjustmentCategories).length &&
			this.core.panels[0].indicators[0].markers['candlestick']
		) {
			// extend clip region by this amount (half of one bar)
			regionWidthAdjustment =
				Math.ceil(
					this.core.panels[0].indicators[0].markers[
						'candlestick'
					].getBarWidth(this.core.panels[0]) / 2
				) + 1;
		}

		// start at midnight
		dateStart.setHours(0);
		dateStart.setMinutes(0);
		dateStart.setSeconds(0);
		dateStart.setMilliseconds(0);

		while (dateStart < dateStop) {
			// grab this day's sessions
			sessions = typicalSessions[dateStart.getDay()].sessions;

			// replace with alternatesessions if applicable (NXC-576)
			Object.keys(alternateSessions).forEach(key => {
				if (new Date(key) - dateStart === 0) {
					// this day's date matches alternatesessions date

					// grab this day's alternate sessions for this exchange
					const altSessions = alternateSessions[key].filter(row => row['exchangeId'] === exchangeId);

					// replace this day's sessions with alternate sessions
					if (altSessions.length > 0) sessions = altSessions;
				}
			});

			// exchange closed on this day?
			exchangeClosure = this.getExchangeClosure(dateStart, exchangeId);

			if (!exchangeClosure) {
				for (s = 0; s < sessions.length; s++) {
					session = sessions[s];

					if (
						session.exchangeId === exchangeId &&
						(!session.sessionType ||
							session.sessionType === sessionType)
					) {
						// left edge of current session
						dateLeft = new Date(
							dateStart.getFullYear(),
							dateStart.getMonth(),
							dateStart.getDate(),
							0,
							session.open
						);

						// right edge of current session
						dateRight = new Date(
							dateStart.getFullYear(),
							dateStart.getMonth(),
							dateStart.getDate(),
							0,
							session.close
						);

						left = scale(this.core.closestExchangeIndex(dateLeft));
						right = scale(
							this.core.closestExchangeIndex(dateRight)
						);

						// extend the region by half a bar width
						if (typeof regionWidthAdjustment === 'number') {
							right += regionWidthAdjustment;
						}

						clips.push({
							left,
							top: padding.top,
							width: Math.ceil(right - left),
							height: this.size.height + padding.top,
							debugColor: 'rgba(255, 0, 50, 0.1)',
							exchangeId: session.exchangeId
						});
					}
				}
			}

			// draw holiday closure labels
			if (
				exchangeClosure &&
				this.core.params.showClosureLabels &&
				this.isUpper &&
				(
					this.core.params.symbolCompare.length ||
					this.core.comparisons.length
				)
			) {
				const typicalSession = this.core.timeService.typicalSessions[
					dateStart.getDay()
				];

				if (typicalSession.rulerSessions[exchangeId]) {
					const rulerSession = typicalSession.rulerSessions[exchangeId];

					// left edge
					dateLeft = new Date(
						dateStart.getFullYear(),
						dateStart.getMonth(),
						dateStart.getDate(),
						0,
						rulerSession.open
					);

					// right edge
					dateRight = new Date(
						dateStart.getFullYear(),
						dateStart.getMonth(),
						dateStart.getDate(),
						0,
						rulerSession.close
					);

					ctx = this.rootContext;

					labelWidth = ctx.measureText(exchangeClosure.name).width;

					left = scale(this.core.closestExchangeIndex(dateLeft));
					right = scale(this.core.closestExchangeIndex(dateRight));
					mid = left + (right - left) / 2 - labelHeight / 2;

					// draw label if it fits
					if (
						this.size.height > labelWidth &&
						right - left > labelHeight
					) {
						ctx.save();

						ctx.translate(
							subpixel.px(mid),
							this.size.height / 2 + labelWidth / 2
						);
						ctx.rotate(-Math.PI / 2);
						ctx.fillStyle = this.params.style.label.closure.color || '#777';
						ctx.fillText(exchangeClosure.name, 0, 0);

						ctx.restore();
					}
				}
			}

			// increment by 1 day
			dateStart.setDate(dateStart.getDate() + 1);
		}

		return clips;
	}

	/** Perform the canvas clip using generated clip regions */
	clipIntradaySessions(sessionClips) {
		const ctx = this.rootContext;
		let clip = {};

		// create one big clip region with several rectangles, one per session
		ctx.beginPath();

		for (let r = 0; r < sessionClips.length; r++) {
			clip = sessionClips[r];

			if (clip.left + clip.width > 0) {
				ctx.moveTo(clip.left, clip.top); //nw
				ctx.lineTo(clip.left, clip.height); //sw
				ctx.lineTo(clip.left + clip.width, clip.height); //se
				ctx.lineTo(clip.left + clip.width, clip.top); //ne
			}
		}

		ctx.clip();
		ctx.closePath();
	}

	/**
	 * return true if there is a closure for this exchange and date
	 */
	getExchangeClosure(date, exchangeId) {
		const closures = this.core.timeService.fullClosures;

		for (let x = 0, xLen = closures.length; x < xLen; x++) {
			if (date - closures[x].date === 0) {
				for (
					let i = 0, iLen = closures[x].exchangeIds.length;
					i < iLen;
					i++
				) {
					if (closures[x].exchangeIds[i] === exchangeId) {
						return closures[x];
					}
				}
			}
		}

		return false;
	}

	/**
	 * render tools clipped to chart boundaries
	 */
	renderTools() {
		// renderLowers
		// see if we have any upper tools that have a renderLower method (aka, OHLCCrosshair, returnsCalculator)
		const renderLowers = this.core.panels[0].tools.filter(tool => typeof tool.renderLower !== 'undefined');

		renderLowers.forEach(tool => {
			this.core.panels.forEach(panel => {
				if (!panel.isUpper) {
					panel.clearTools();
					panel.rootToolContext.save();
					panel.clipPanel(panel.rootToolContext);
					tool.renderLower(panel);
					panel.rootToolContext.restore();
				}
			});
		});

		// clip the panel so that no marker can overlap edges
		this.rootToolContext.save();
		this.clipPanel(this.rootToolContext);

		const flags = [];
		this.tools.forEach(tool => {
			if (tool.state.visible) {
				tool.render();
				if (tool.flags?.horizontalAnnotation) {
					const position = tool.flags.horizontalAnnotation.render();
					if (position?.y) {
						position.flag = tool.flags.horizontalAnnotation;
						position.tool = tool;
						flags.push(position);
					}
				}
			}
		});

		this.rootToolContext.restore();

		// optional adjustment for horizontalAnnotation flag collisions
		if (this.core.params.flagOverlap === false && flags.length > 0) {
			this.preventFlagOverlap(flags);
		}
	}

	/**
	 * Render indicator flags.
	 * Hide flags if showFlags mode is false, or if lastCloseFlagOnlyPrimary condition is not met.
	 */
	renderFlags() {
		const core = this.core;
		const flags = [];

		// Render flags in their default positions (may have overlaps)
		for (const ind of this.indicators) {
			for (const key in ind.flags) {
				if (!Object.prototype.hasOwnProperty.call(ind.flags, key)) continue;
				const flag = ind.flags[key];

				// Check if flags should be shown
				if (core.params.showFlags) {
					// Check for lastCloseFlagOnlyPrimary condition
					// CH-4898 If the flag type is 'lastClose' and the conditions are not met (primary panel and primary indicator), hide the flag
					if (
						core.params.lastCloseFlagOnlyPrimary &&
						key === 'lastClose' &&
						!(this.isUpper === true && ind === this.indicators[0])
					) {
						flag.hide();
						continue; // Skip rendering this lastClose flag
					}

					// Render the flag if the conditions are met
					const position = flag.render();
					if (position?.y) {
						position.flag = flag;
						flags.push(position);
					}
				} else {
					// Hide all flags if showFlags mode is false
					flag.hide();
				}
			}
		}

		// Optional adjustment for flag collisions
		if (core.params.flagOverlap === false && flags.length > 0) {
			this.preventFlagOverlap(flags);
		}
	}


	/**
	 * Adjust the y position of each non-primary flag to ensure no overlaps.
	 */
	preventFlagOverlap(flags) {
		if (!flags.length) return;
		const height = this.params.style.yAxis.font.size + flags[0].flag._getPadding('top') + flags[0].flag._getPadding('bottom');
		const halfHeight = height / 2;

		// Helper function to find the index of the primary flag.  This is the flag considered the most "important".  Other flags will be spread away from this flag as needed.
		const findPrimaryFlagIndex = () => {
			const index = flags.findIndex(flag =>
				flag.tool === this.core.state.dragTool ||
					(flag.flag && flag.flag.indicator && flag.flag.indicator.params.symbol === this.core.params.symbol)
			);
			// If no flag matches the criteria, default to the middle index of the array
			return index !== -1 ? index : Math.floor(flags.length / 2);
		};

		// Sort flags by their y position
		flags.sort((a, b) => a.y - b.y);

		const primaryIndex = findPrimaryFlagIndex();
		this.adjustFlagPositions(flags, primaryIndex, height);
		this.repositionFlags(flags, halfHeight);
	}

	/**
	 * Adjust flag positions based on the primary flag to prevent overlaps.
	 */
	adjustFlagPositions(flags, primaryIndex, height) {
		let lastY = flags[primaryIndex].y;

		// Adjust positions for flags after the primary flag
		for (let i = primaryIndex + 1; i < flags.length; i++) {
			if (flags[i].y < lastY + height) {
				flags[i].y = lastY + height;
			}
			lastY = flags[i].y;
		}

		// Adjust positions for flags before the primary flag
		lastY = flags[primaryIndex].y;
		for (let i = primaryIndex - 1; i >= 0; i--) {
			if (flags[i].y > lastY - height) {
				flags[i].y = lastY - height;
			}
			lastY = flags[i].y;
		}
	}

	/**
	 * Reposition flag SVG elements and hide flags near panel edges.
	 */
	repositionFlags(flags, halfHeight) {
		const { top, bottom } = this.size;

		flags.forEach(flag => {
			const flagY = top + flag.y;
			if (flagY + halfHeight > bottom || flagY - halfHeight < top) {
				flag.flag.hide();
			} else {
				flag.flag._updateFlagBody(this, flag.x, flag.y);
				flag.flag._updateFlagLabel(this, flag.val, flag.x, flag.y);
			}
		});
	}


	/** Clear out the current context and tools */
	clearPanel() {
		const { style } = this.params;
		const { rootContext: ctx } = this;
		const { height, padding, width } = this.size;
		const fullWidth = width + padding.left + padding.right;
		const fullHeight = height + padding.top + padding.bottom;

		// empty canvas contents
		ctx.clearRect(0, 0, fullWidth, fullHeight);

		// fill grid area rect
		if (style.grid.background.color) {
			ctx.fillStyle = style.grid.background.color;
			ctx.fillRect(padding.left, padding.top, width, height);
		}

		// fill yaxis rect
		if (style.yAxis.background.color) {
			ctx.fillStyle = style.yAxis.background.color;
			ctx.fillRect(
				padding.left + width,
				padding.top,
				padding.right,
				height
			);
		}

		// fill xaxis rect
		if (style.xAxis.background.color) {
			ctx.fillStyle = style.xAxis.background.color;
			ctx.fillRect(
				padding.left,
				padding.top + height,
				fullWidth,
				padding.bottom
			);
		}

		// clear tools canvas
		this.clearTools();

		// clear legends
		this.concealLegend();

		// clear flags
		this.clearFlags();
	}

	/** Hide all panel and indicator flags */
	clearFlags() {
		// remove panel flags
		Object.keys(this.flags).forEach(key => {
			const flag = this.flags[key];
			flag.hide();
		});

		// remove indicator flags
		Object.keys(this.indicators).forEach(key => {
			const indicator = this.indicators[key];
			Object.keys(indicator.flags).forEach(indicatorFlagKey => {
				const flag = indicator.flags[indicatorFlagKey];
				flag.hide();
			});
		});

		// remove tool flags
		Object.keys(this.tools).forEach(key => {
			const tool = this.tools[key];
			Object.keys(tool.flags).forEach(toolFlagKey => {
				const flag = tool.flags[toolFlagKey];
				flag.hide();
			});
		});
	}

	/** Clears the tool canvas for this panel */
	clearTools() {
		const canvasWidth =
				this.size.width +
				this.size.padding.left +
				this.size.padding.right;
		const canvasHeight =
				this.size.height +
				this.size.padding.top +
				this.size.padding.bottom;

		this.rootToolContext.clearRect(0, 0, canvasWidth, canvasHeight);
	}

	/** Internal method for setting up axes for chart render */
	configureAxes() {
		let domain;
		const { customData } = this.core.params;
		const data = customData
			? this.core._getCustomFrameData()
			: this.core._getFrameData();

		if (
			this.params.yAxis.range &&
			this.params.yAxis.range.length === 2
		) {
			// user-specified yaxis domain
			domain = {
				min: this.params.yAxis.range[0],
				max: this.params.yAxis.range[1]
			};
		} else {
			// get min/max values for every indicator in filtered set
			domain = customData
				? this._getCustomDataRange(data)
				: this._getOverallDataRange(data);
		}

		const start = this.size.padding.left;
		const stop = start + this.size.width;

		// Update scale range
		this.xAxis.setScaleRange({ start, stop });

		// Update range for present xAxis scale (for calculating zoom transform updates)
		if (this.index === 0) this.core.state.xAxisPresentScale.range([start, stop]);

		if (this.core.zoom) {
			// Use the panel width here because rootmouse covers the entire panel
			const { height, padding, width } = this.size;

			// Set transform extent to only exclude the grid area (not the yAxis area)
			this.core.zoom.extent([[padding.left, padding.top], [width, height]]);
		}

		// Only apply .nice() if domain doesn't intersect 0
		const shouldSetNice = domain.max !== 0 && domain.min !== 0;

		// Assume no draw to bottom markers in use
		let foundDrawToBottomMarker = false;

		const range = {
			min: this.size.padding.top,
			max: this.size.padding.top + this.size.height
		};

		// Set domain for primary Y-axis scale
		this.yAxis.setScaleDomain({
			shouldSetNice,
			start: domain.max,
			stop: domain.min
		});

		for (const [index, i] of this.indicators.entries()) {
			const { markerType } = i.params;
			const foundLioup = this.isUpper && this._checkForLioup(i.params.id, index);

			if (!foundLioup) {
				// Check if indicator has marker that draws to bottom
				foundDrawToBottomMarker =
				foundDrawToBottomMarker ||
				this._checkForDrawToBottomMarker(markerType);
			} else {
				// Set domain/range for secondary Y-axis scales
				const secondaryDomain = this._getDataRange(
					i,
					// Should we get the symbol in a different way?
					data[this.core.params.symbol]
				);

				// Get % of panel height for LIOUP height (default to full panel height)
				let { panelHeightRatio = 1 } = i.params.style;
				if (
					panelHeightRatio > 1 ||
					panelHeightRatio <= 0
				) {
					console.warn(`Indicator-to-panel-height ratio for indicator '${i.params.id}' is out of range. Using fallback.`);
					panelHeightRatio = 1;
				}

				this.yAxis.setScaleDomain({
					scaleUid: i.scaleUids[1], // Get Y-axis scale UID
					start: secondaryDomain.max / panelHeightRatio,
					stop: secondaryDomain.min
				});

				this.yAxis.setScaleRange({
					start: range.min,
					stop: range.max,
					scaleUid: i.scaleUids[1] // Get Y-axis scale UID
				});
			}
		}

		if (domain.max !== 0 &&	domain.min !== 0) {
			// On log scales, altering the scale's range here is the fastest way to enforce yaxis padding.
			// (linear scales will be done by expanding the actual domain within _getOverallDataRange)
			if (this.index === 0 && this.core.params.yAxisScale === 'log') {
				// Only add bottom padding if marker doesn't draw to bottom of chart
				if (!foundDrawToBottomMarker) range.max -= this.params.style.yAxis.padding.bottom;
				range.min += this.params.style.yAxis.padding.top;
			}
		}

		// Set range for primary Y-axis scale
		this.yAxis.setScaleRange({
			start: range.min,
			stop: range.max
		});

		// Update padding automatically or use panel styles
		this.size.padding.right = this._getPaddingRight();
		this.size.width = this.core.size.width - this.size.padding.left - this.size.padding.right;
	}

	/**
	 * Renders all axes within the panel
	 * @param {array} dateLabels
	 */
	renderAxes(dateLabels = {}) {
		this.yAxis.render(); // Horizontal grid lines will be rendered in this method
		this.grid.renderVerticalGrid(dateLabels);
		this.xAxis.render(dateLabels);
	}

	/** Render an overall panel border and/or selected edges */
	renderBorders() {
		const ctx = this.rootContext;
		const fullBorderOffset = 1;
		const { size } = this;
		const { padding } = size;
		const { style } = this.params;
		const width =
			size.width -
			(style.border.left.padding || style.border.default.padding) -
			(style.border.right.padding || style.border.default.padding) - 1;
		const height =
			size.height -
			fullBorderOffset -
			(style.border.top.padding || style.border.default.padding) -
			(style.border.bottom.padding || style.border.default.padding);
		const left =
			padding.left +
			(style.border.left.padding || style.border.default.padding);
		const top =
			padding.top +
			(style.border.top.padding || style.border.default.padding);
		let color;
		let lineDash;
		let lineWidth;

		// render full border rectangle
		if (style.border.default.color) {
			ctx.save();
			ctx.beginPath();

			ctx.lineWidth = style.border.default.width;
			ctx.strokeStyle = style.border.default.color;
			ctx.setLineDash(style.border.default.lineDash.split(/\s*,\s*/));
			subpixel.rect(ctx, left, top, width, height);
			ctx.stroke();

			ctx.restore();
		}

		// render left border line
		color =
			style.border.left.color ||
			style.border.default.color;
		lineDash =
			style.border.left.lineDash ||
			style.border.default.lineDash;
		lineWidth =
			style.border.left.width ||
			style.border.default.width;
		this.grid.renderVerticalLines(ctx, [subpixel.px(left)], top, height, color, lineDash, lineWidth);

		// render right border line
		color =
			style.border.right.color ||
			style.border.default.color;
		lineDash =
			style.border.right.lineDash ||
			style.border.default.lineDash;
		lineWidth =
			style.border.right.width ||
			style.border.default.width;
		this.grid.renderVerticalLines(ctx, [subpixel.px(left + width)], top, height, color, lineDash, lineWidth);

		// render top border line
		color =
			style.border.top.color ||
			style.border.default.color;
		lineDash =
			style.border.top.lineDash ||
			style.border.default.lineDash;
		lineWidth =
			style.border.top.width ||
			style.border.default.width;
		this.grid.renderHorizontalLines(ctx, [subpixel.px(top)], left, width, color, lineDash, lineWidth);

		// render bottom border line
		color =
			style.border.bottom.color ||
			style.border.default.color;
		lineDash =
			style.border.bottom.lineDash ||
			style.border.default.lineDash;
		lineWidth =
			style.border.bottom.width ||
			style.border.default.width;
		this.grid.renderHorizontalLines(ctx, [subpixel.px(top + height)], left, width, color, lineDash, lineWidth);
	}

	/**
	 * Renders session segments on the chart with appropriate styles and emits an event for further customization.
	 * @param {string} sessionType - Type of the session to render. Defaults to 'SessionBreak'.
	 */
	renderSessions(sessionType = 'SessionBreak') {
		if (Object.keys(this.sessionClips).length) {
			// Determine the style based on the session type
			const style = this.params.style[sessionType.toLowerCase()] || this.params.style.sessionBreak;

			// Context and panel dimensions
			const ctx = this.rootContext;
			const panelHeight = this.size.height;
			const panelWidth = this.size.width;
			const regions = []; // To store calculated session regions

			// Extract session clips based on exchange ID and session type
			const symbol = this.indicators[0].params.symbol || this.params.symbol; // the indicator symbol can sometimes be different (ARMS index, CRS)
			const exchangeId = this.core.xref[symbol]?.exchangeId;
			let sessionClips = [];

			if (exchangeId && this.sessionClips[exchangeId] && this.sessionClips[exchangeId][sessionType]) {
				sessionClips = this.sessionClips[exchangeId][sessionType] || [];
			}

			sessionClips.forEach(region => {
				const borderWidth = style.border.width;
				// Adjusting region bounds considering border width
				const sessionLeft = Math.max(region.left + Math.ceil(borderWidth / 2), 0);
				const sessionRight = Math.min(region.left + region.width - Math.ceil(borderWidth / 2), panelWidth);

				// Render side borders if defined
				if (style.border.color) {
					const { color: borderColor, lineDash: borderLineDash } = style.border;

					// Draw left border
					if (sessionLeft > 0) {
						this._renderSessionBorder(
							ctx, [sessionLeft, 0], [sessionLeft, panelHeight],
							borderColor, borderWidth, borderLineDash
						);
					}

					// Draw right border
					if (sessionRight < panelWidth) {
						this._renderSessionBorder(
							ctx, [sessionRight, 0], [sessionRight, panelHeight],
							borderColor, borderWidth, borderLineDash
						);
					}
				}

				// Fill session region with background color if defined
				if (style.background.color) {
					ctx.fillStyle = style.background.color;
					ctx.fillRect(sessionLeft, 0, sessionRight - sessionLeft, panelHeight);
				}

				// Store region information if it's within the panel width
				if (sessionRight > 0 && sessionLeft < panelWidth) {
					const midX = (sessionLeft + sessionRight) / 2;
					const midY = panelHeight / 2;

					regions.push({
						left: sessionLeft,
						right: sessionRight,
						top: 0,
						bottom: panelHeight,
						width: sessionRight - sessionLeft,
						height: panelHeight,
						midpoint: { x: midX, y: midY }
					});
				}
			});

			// Emit a SESSION_RENDER event with session details for further customization
			if (regions.length) {
				this.core.eventEmitter.emit(types.SESSION_RENDER, {
					regions, ctx, exchangeId, isUpper: this.isUpper, sessionType
				});
			}
		}
	}

	/**
	 * Renders a vertical session break line
	 */
	_renderSessionBorder(
		ctx,
		start = [0, 0],
		end = [0, 0],
		color = '#000',
		lineWidth = 1.5,
		lineDash = ''
	) {
		if (!lineWidth) return; // Exit early if lineWidth is 0 or null

		ctx.beginPath();
		ctx.lineJoin = 'round';
		ctx.lineWidth = lineWidth || this.params.lineWidth;
		ctx.strokeStyle = color || this.params.lineColor;

		ctx.moveTo(subpixel.px(start[0]), subpixel.px(start[1]));
		ctx.lineTo(subpixel.px(end[0]), subpixel.px(end[1]));

		if (ctx.setLineDash) {
			const dashArray = lineDash ? lineDash.split(/\s*,\s*/) : [];
			ctx.setLineDash(dashArray);
		}

		ctx.stroke();

		// Reset dashed line setting to default
		if (ctx.setLineDash) ctx.setLineDash([]);
	}

	/** fillText with a glow behind it */
	glowText(ctx, labelText, x, y, textColor, glowColor, glowWidth = 2) {
		// background
		if (
			typeof glowColor === 'string' &&
			glowColor.length &&
			glowColor !== 'none'
		) {
			ctx.strokeStyle = glowColor;
			ctx.lineWidth = glowWidth;
			ctx.strokeText(labelText, x, y);
		}

		// foreground
		ctx.fillStyle = textColor;
		ctx.lineWidth = 1;
		ctx.fillText(labelText, x, y);
	}

	/**
	 * remove from indicators collection
	 */
	removeIndicator(indicator) {
		// final cleanup before removal
		indicator.remove();

		// remove from panel's indicators collection
		for (let x = this.indicators.length - 1; x >= 0; x--) {
			if (indicator === this.indicators[x]) {
				this.indicators.splice(x, 1);
				break;
			}
		}
		this.updateLegend();
		const event = this.isUpper ? types.INDICATOR_UPPER_REMOVE : types.INDICATOR_LOWER_REMOVE;
		this.core.eventEmitter.emit(event, {
			indicator,
			indicatorType: indicator.params.id
		});
	}

	/**
	 * Remove from events collection by event object, event id, or event uid
	 */
	removeEvent(id, uid) {
		let type;
		let removedEventParams;
		// support either string or object for backward compatibilty
		if (typeof id === 'object' && id.params) {
			type = id.params.id;
			uid = id.params.uid;
		} else {
			type = id;
		}
		// remove matching events
		for (let x = this.events.length - 1; x >= 0; x--) {
			if (type === this.events[x].params.id) {
				if (!uid || this.events[x].params.uid === uid) {
					removedEventParams = { ...this.events[x].params };
					this.events[x].remove();
					this.events.splice(x, 1);
				}
			}
		}
		if (removedEventParams) {
			this.core.eventEmitter.emit(types.EVENT_REMOVE, {
				eventType: type,
				name: removedEventParams.name,
				uid: removedEventParams.uid
			});
			this.core.render();
		} else {
			this.core.warn(`Could not remove event ${type} ${uid}`);
		}
	}

	/**
	 * Remove from events collection by event name
	 */
	removeEventByName(name) {
		let removedEventParams = {};
		// remove matching events
		for (let x = this.events.length - 1; x >= 0; x--) {
			if (this.events[x].params.name === name) {
				removedEventParams = { ...this.events[x].params };
				this.events[x].remove();
				this.events.splice(x, 1);
			}
		}
		this.core.eventEmitter.emit(types.EVENT_REMOVE, {
			eventType: removedEventParams.id,
			name: removedEventParams.name,
			uid: removedEventParams.uid
		});
		this.core.render();
	}

	/**
	 * remove all tools from this panel
	 */
	removeTools() {
		for (let x = this.tools.length - 1; x >= 0; x--) {
			this.tools[x].remove();
		}

		this.core.render();
	}

	/** emit an event for the panel's legend element to be updated */
	updateLegend() {
		const legendElement = this.rootLegend.node();
		const { indicators } = this;
		const lastClose = {};

		try { // try/catch just to make it easier to unit test when data isn't fully mocked
			// determine the final point for each price indicator
			indicators.forEach(indicator => {
				const { symbol, id, uid } = indicator.params;
				if (id && id === 'price' && this.core.data[symbol] && this.core.data[symbol][id] && this.core.data[symbol][id][uid]) {
					const dataSeries = this.core.data[symbol][id][uid];
					if (dataSeries && dataSeries.length) {
						lastClose[symbol] = dataSeries[dataSeries.length - 1].close;
					}
				}
			});
			// emit the legend update event
			this.core.eventEmitter.emit(types.PANEL_LEGEND_UPDATE, {
				panel: this,
				legendElement,
				indicators,
				lastClose
			});
		} catch (error) {
			this.core.warn('Error emitting PANEL_LEGEND_UPDATE event:', error);
		}
	}

	/**
	 * hide panel's legend dom element
	 */
	concealLegend() {
		this.rootLegend.style('visibility', 'hidden');
	}

	/**
	 * reveal panel's legend dom element
	 */
	revealLegend() {
		if (this.rootLegend.style('display') !== 'none') {
			this.rootLegend.style('visibility', 'visible');
		}
	}

	/**
	 * @method onClickRemoveLegendIndicator
	 * Deprecated.  A wrapper for removeLegendIndicator.  Used for backwards-compatibility.
	 */
	onClickRemoveLegendIndicator(arg, options = {}) {
		return this.removeLegendIndicator(arg, options);
	}

	/**
	 * @method removeLegendIndicator
	 * This is a convenience method around removeIndicator() that performs extra cleanup
	 * including selectively removing lower panels and loading new data.
	 * call this method with one of:
	 *    - indicator object reference
	 *    - an indicator UID
	 *    - a d3 selector that points to an element with an "idx" and "panelId" attribute
	 */
	removeLegendIndicator(arg, options = {}) {

		const core = this.core;
		let loadData = false;
		let removeCompare = false;
		const overlays = [];
		let panel = {};
		let indicator;

		// argument was indicator reference
		if (arg.params && arg.params.uid) {
			indicator = arg;
		} else if (typeof arg === 'string') {
			// argument was an indicator uid
			indicator = core.getIndicatorByUid(arg);
		}

		if (indicator) {
			panel = indicator.panel;
		} else {
			// argument was a d3 selector

			const el = select(arg);
			const idx = el.attr('idx');

			panel = core.getPanelByUid(el.attr('panelId'));

			if (panel) {
				indicator = panel.indicators[idx];
			}
		}

		if (!indicator) {
			this.core.warn('Indicator reference not found.');
			return;
		}

		// check if this is a lower indicator with overlays
		for (let x = 1; x < panel.indicators.length; x++) {
			if (
				panel.indicators[x].params.parentUid === indicator.params.uid ||
				panel.indicators[x].params.parentUID === indicator.params.uid
			) {
				overlays.push(panel.indicators[x]);
			}
		}

		// special handling for price markers
		if (indicator.params.id === 'price' && indicator.params.symbol) {
			// always reload data after deleting a symbol.  this helps restore a chart if it just broke due to adding a bad symbol,
			// and forces the domain to be reset in case the deleted symbol was on a different exchange.
			loadData = true;

			// remove from symbolCompare collection (Deprectated CH-1504)
			const symbolIndex = panel.core.params.symbolCompare.indexOf(indicator.params.symbol);
			if (symbolIndex !== -1) {
				panel.core.params.symbolCompare.splice(symbolIndex, 1);
				removeCompare = true;
			}

			// detect removal of a comparison
			if (this.core.comparisons.find(i => i.params.symbol === indicator.params.symbol)) {
				removeCompare = true;
			}
		}

		// always reload data after deleting a sectorindustry
		if (indicator.params.id === 'sectorindustry') loadData = true;

		// remove indicator object
		this.removeIndicator(indicator);

		// selectively reset the domain if removing the last remaining ichimoku cloud
		if (
			indicator.params.id === 'ichimokucloud' &&
			core.getIndicatorsById('ichimokucloud').length === 0
		) {
			core.resetDomain();
			const defaultDomain = core.getDefaultDomain();
			core._updateXAxisScaleDomains(defaultDomain[0], defaultDomain[1]);
		}

		// remove the entire panel if that was its last non-overlay indicator
		if (this.indicators.length === 0 || overlays.length > 0) {
			core.removePanel(this);
			core.resize();
		} else if (removeCompare) {
			core.resetDomain();

			if (core.params.dateStart && core.params.dateStop) {
				const daysRange = Math.ceil(
					(core.params.dateStop.getTime() -
						core.params.dateStart.getTime()) /
						1000 /
						60 /
						60 /
						24
				);

				core.params.days = Math.min(
					core.state.isIntraday
						? core.limitIntradayTrading
						: core.params.rulerInterdayStart,
					daysRange
				);
			}

			this.updateLegend();
		} else {
			// update legend immediately
			this.updateLegend();
		}

		// load new data or render
		if (loadData && !options.noLoad) {
			core.loadData();
		} else {
			core.render();
		}
	}

	/**
	 * render error message on top of a panel
	 * @param {string} message
	 */
	renderErrorMessage(message) {
		this.rootError
			.html(message)
			.style('display', null);
	}

	/**
	 * Clear out the error message
	 */
	clearErrorMessage() {
		this.rootError.html('');
	}

	/**
	 * convert a normalized close value to an actual
	 */
	normalizedToActual(val) {
		if (
			this.core.params.normalizeValues &&
			this.core.params.normalizeValues.Close
		) {
			const normalizedValue = this.core.params.normalizeValues.Close;
			const valOrig = normalizedValue || 0;
			return (val / 100) * valOrig + valOrig;
		}

		return val;
	}

	/**
	 * convert an actual value to normalized
	 * see also: https://wiki.wsod.local/wsodwiki/index.php/Percent_Change_Display_Policy
	 */
	actualToNormalized(val) {
		let normalizedValue = 0;

		if (this.core.params.normalizeValues) {
			normalizedValue = this.core.params.normalizeValues.Close;
		}

		const valOrig = normalizedValue || this.core.params.normalizeValue;

		return ((val - valOrig) / valOrig) * 100;
	}

	/** assemble font-related styles into a single CSS font string.  Used by X and Y axis during renders */
	getFontStyle(fontWeight = 'normal', fontSize = 11, fontFamily = 'Arial, Helvetica, sans-serif', fontStyle) {
		// pull apart comma-delimited family list
		let newFamily = fontFamily.split(/,\s*/);

		// wrap quotes around each family individually - this is needed so certain families
		// with multiple spaces don't break the font string, such as "Gotham Narrow Ssm 5r"
		newFamily = newFamily.map(f => `"${f.replace(/["']/g, '')}"`);

		// assemble style, weight, size and family
		return [fontStyle, fontWeight, `${fontSize}px`, newFamily].join(' ').trim();
	}
	/**
	 * Gets generic object representation of Panel object
	 * @method toJSON
	 */
	toJSON() {
		const json = {
			params: { ...this.params },
			indicators: [],
			tools: [],
			events: [],
			size: {}
		};

		// Saved indicators
		const indicators = this.indicators || [];
		for (let i = 0; i < indicators.length; i++) {
			const indicator = indicators[i];
			const indicatorJSON = indicator.toJSON();

			if (indicatorJSON) {
				json.indicators.push(indicatorJSON);
			}
		}

		// Saved tools & handles
		const tools = this.tools || [];
		for (let t = 0; t < tools.length; t++) {
			const tool = tools[t];
			const toolJSON = tool.toJSON();
			json.tools.push(toolJSON);
		}

		// Saved events
		const events = this.events || [];
		for (let e = 0; e < events.length; e++) {
			const event = events[e];
			const eventParams = { ...event.params }; // Create a shallow copy to avoid mutating original data

			if (eventParams.id === 'custom') {
				// Don't save certain params if they exist (CH-5956)
				if ('dataset' in eventParams) delete eventParams.dataset;
				if ('removeMethod' in eventParams) delete eventParams.removeMethod;
				if ('renderMethod' in eventParams) delete eventParams.renderMethod;
			}

			json.events.push({
				params: eventParams
			});
		}

		// heightPct
		if (this.size && typeof this.size.heightPct === 'number') {
			json.params.size = json.params.size || {};
			json.params.size.heightPct = this.size.heightPct;
		}

		return json;
	}

	/**
	 * static map from id to Class name
	 * @returns {string}
	 */
	static getIndicatorClassName(id) {
		return Indicators.get(id) || null;
	}

	/**
	 * Rebuild the panel style/size params
	 * @param {*} oldStyles - Styles previously stored in via chart.params.style before the update
	 * @param {*} oldStyleMap - Chart style map from before the update
	 * @param {*} doSoftUpdate - Flag that enables/disables check for if invidual components have
	 * been altered from oldStyles/default styles
	 */
	updateStyles(oldStyles = {}, oldStyleMap = {}, doSoftUpdate = true) {
		function updatePanelStyles(panelObj) {
			updateComponentStyles(
				[panelObj],
				doSoftUpdate,
				() => merge({}, oldStyleMap.panel, oldStyles.panel || {}),
				panel => panel.getDefaultParams().style,
				panel => panel.params,
				[
					{ test: panel => panel.flags, updater: updateComponentFlagStyles }
				]
			);

			// Update extended hours marker styles (one of these lives on each panel if that option is enabled)
			if (panelObj.extendedHoursMarker) {
				updateComponentStyles(
					[panelObj.extendedHoursMarker],
					doSoftUpdate,
					() => merge({}, oldStyleMap.marker.extendedHours, get(oldStyles, 'marker.extendedHours') || {}),
					marker => marker.getDefaultParams().style,
					marker => marker.params
				);
			}

			// Update radius attribute of crosshair circle SVG
			if (panelObj.crosshairCircle) panelObj.crosshairCircle.attr('r', panelObj.params.style.crosshair.circleRadius);

			// Update panel size
			panelObj.params.size = merge({}, panelObj.getDefaultParams().size);
			panelObj.initPanelSize();
		}

		function updateComponentFlagStyles(component) {
			updateComponentStyles(
				component.flags,
				doSoftUpdate,
				flag => merge({}, get(oldStyleMap, `flag.${flag.id}`), get(oldStyles, `flag.${flag.id}`) || {}),
				flag => flag._getStyle(),
				flag => flag
			);
		}

		function updatePanelToolStyles(panel) {
			updateComponentStyles(
				panel.tools,
				doSoftUpdate,
				tool => merge({}, get(oldStyleMap, `tool.${tool.params.id}`), get(oldStyles, `tool.${tool.params.id}`) || {}),
				tool => tool._getDefaultParams().style,
				tool => tool.params,
				[
					{ test: tool => tool.flags, updater: updateComponentFlagStyles },
					{ test: tool => tool.handle.length, updater: updateToolHandleStyles }
				]
			);
		}

		function updatePanelEventStyles(panel) {
			updateComponentStyles(
				panel.events,
				doSoftUpdate,
				() => merge({}, get(oldStyleMap, 'event'), get(oldStyles, 'event') || {}),
				event => event._getParams().style,
				event => event.params
			);
		}

		function updatePanelIndicatorStyles(panel) {
			updateComponentStyles(
				panel.indicators,
				doSoftUpdate,
				indicator => merge({}, get(oldStyleMap, `indicator['${indicator.params.id}']`), get(oldStyles, `indicator['${indicator.params.id}']`) || {}),
				indicator => indicator._getDefaultStyles(),
				indicator => indicator.params,
				[
					{ test: indicator => indicator.flags, updater: updateComponentFlagStyles }
				]
			);
		}

		function updateToolHandleStyles(tool) {
			updateComponentStyles(
				tool.handle,
				doSoftUpdate,
				() => merge({}, get(oldStyleMap, 'tool.handle'), get(oldStyles, 'tool.handle') || {}),
				() => tool.core.getStyle('tool.handle'),
				handle => handle.params
			);
		}

		updatePanelStyles(this);
		updatePanelToolStyles(this);
		updatePanelEventStyles(this);
		updatePanelIndicatorStyles(this);
	}

	/** Returns the first panel indicator, if it exists. This should only be used for debugging. */
	get i0() {
		return this.indicators.length > 0
			? this.indicators[0]
			: null;
	}

	/** Returns the first panel tool, if it exists. This should only be used for debugging. */
	get t0() {
		return this.tools.length > 0
			? this.tools[0]
			: null;
	}

	/** Returns the first panel event, if it exists. This should only be used for debugging. */
	get e0() {
		return this.events.length > 0
			? this.events[0]
			: null;
	}

	get index() {
		return this.core.panels.indexOf(this);
	}

	/** Decreases panel index by 1 and re-renders. */
	moveUp() {
		this._moveIndex(-1);
		this.core.resize();
		this.core.render();
	}

	/** Increases panel index by 1 and re-renders. */
	moveDown() {
		this._moveIndex(1);
		this.core.resize();
		this.core.render();
	}

	/**
	 * Moves this panel's index up or down in the panels array, and ensures the DOM order
	 * matches the new index order (this is important for keyboard tab focus ordering)
	 * @param {number} indexDelta -1 or 1
	 * @private
	 */
	_moveIndex(indexDelta) {
		const panelIndex = this.index;
		const panels = this.core.panels;
		const newIndex = panelIndex + indexDelta;
		const chartEl = this.core.rootModchart.node();
		const panelEl = this.rootPanel.node();
		// error check
		if (typeof panels[newIndex] === 'undefined') {
			return this.core.warn('Panel move limit reached');
		}
		// reorder DOM
		if (indexDelta === -1) { // up
			chartEl.insertBefore(panelEl, panelEl.previousSibling);
		} else { // down
			chartEl.insertBefore(panelEl.nextSibling, panelEl);
		}
		// reorder panels array
		const thisPanel = panels.splice(panelIndex, 1)[0];
		panels.splice(newIndex, 0, thisPanel);
	}
}

/**
 * Determines the number of days between two dates passed in
 * @param {Date} startDate - The starting date
 * @param {Date} endDate - The ending date
 * @returns {number} The number of days between the two dates
 */

/**
 * Determines the number of trading days between two trading dates passed in
 * @param {Date} startDate - The starting trading date
 * @param {Date} endDate - The ending trading date
 * @param {string[]} tradingDays - An array of trading dates represented by strings
 * @returns {number} The number of trading days between the two trading dates
 */
const calcTradingDaysBetweenTradingDates = (startDate, endDate, tradingDays) => {
	let [minDate, maxDate] = _prepareDates(startDate, endDate);

	// Remove time zone offset for comparison to trading days
	minDate = new Date(minDate.getTime() - minDate.getTimezoneOffset() * 60 * 1000);
	maxDate = new Date(maxDate.getTime() - maxDate.getTimezoneOffset() * 60 * 1000);

	// Get indices in tradingDays to calculate the correct number of trading days between dates passed in
	const minIndex = tradingDays.findIndex(d => new Date(d).getTime() === minDate.getTime());
	const maxIndex = tradingDays.findIndex(d => new Date(d).getTime() === maxDate.getTime());

	if (minIndex === -1 || maxIndex === -1) {
		return 0;
	}

	return maxIndex - minIndex;
};

/**
 * Prepares dates for comparison by finding min and max, and setting to midnight.
 * @param {Date} startDate - The starting date
 * @param {Date} endDate - The ending date
 * @returns {Date[]} The prepared dates
 */
const _prepareDates = (startDate, endDate) => {
	let minDate;
	let maxDate;

	// Find the min, max dates
	if (endDate.getTime() >= startDate.getTime()) {
		maxDate = endDate;
		minDate = startDate;
	} else {
		minDate = endDate;
		maxDate = startDate;
	}

	// Set dates to midnight
	minDate.setHours(0, 0, 0, 0);
	maxDate.setHours(0, 0, 0, 0);

	return [minDate, maxDate];
};

/* eslint-disable no-nested-ternary */

/**
 * Create the zoom behavior
 * @param {ModchartsInteractive} chart - Modcharts chart instance
 * @returns {object} - D3 zoom behavior
 */
const createZoom = chart => {
	// The minimum number of steps (between adjacent data points) to show when zoomed in
	const minSteps = 5;

	return zoom()
		.on('start', event => {
			// Only set the scale extent once. The extent gets reset in the chart.days setter.
			if (chart.state?.xAxisPresentScale && chart.zoom.scaleExtent()[1] === Infinity) {
				// Get present domain bounds for comparison
				const [start, stop] = chart.state.xAxisPresentScale.domain();
				const domainLength = stop - start;

				// Calculate index step size between data points in initial time frame
				const firstDatumDateIndex = chart.closestExchangeIndex(chart.dataPrimary[0].date);
				const secondDatumDateIndex = chart.closestExchangeIndex(chart.dataPrimary[1].date);
				const indexStep = secondDatumDateIndex - firstDatumDateIndex;

				// The zoom in limit is the inverse of the ratio of the length of the minSteps to the total length (shown simplified)
				const zoomInBoundary = domainLength / (indexStep * minSteps);

				// Set the scale extent
				chart.zoom.scaleExtent([0, zoomInBoundary]);

				// NOTE: We don't need a zoom out boundary because that's handled by the left/right boundaries
			}
			onZoomStart(chart, event);
		})
		.on('zoom', event => {
			onZoom(chart, event);
		})
		.on('end', event => {
			onZoomEnd(chart, event);
		})
		.wheelDelta(event => {
			return wheelDelta(chart, event);
		});
};

/**
 * Calculates the resulting zoom transform given two different scales
 * @param {object} fromScale - The D3 scale with the domain that you're changing from
 * @param {object} toScale - The D3 scale with the domain that the transform will change to
 * @param {object} currentTransform - The current D3 zoom transform. Used to retrieve the current translation values. Defaults to zoomIdentity.
 * @param {string} scaleAxis - The axis associated with the scales: 'x' or 'y'. Defaults to 'x'.
 * @returns {object} D3 zoom transform that represents the modification required to get from the fromScale domain to the toScale domain
 */
const getTransformFromScales = (fromScale, toScale, currentTransform = zoomIdentity, scaleAxis = 'x') => {
	/* See CH-1607 for more info */

	// Make sure ranges are the same
	const fromRange = fromScale.range();
	const toRange = toScale.range();
	if (!fromRange.every((value, i) => value === toRange[i])) toRange.range(fromRange);

	const fromDomain = fromScale.domain();
	const toDomain = toScale.domain();
	const translation = toScale(fromDomain[0]) - toScale(toDomain[0]);
	const k = (fromDomain[1] - fromDomain[0]) / (toDomain[1] - toDomain[0]);

	// Set correct translations
	const [tX, tY] = scaleAxis === 'x'
		? [translation, currentTransform[scaleAxis]]
		: [currentTransform[scaleAxis], translation];

	return zoomIdentity.translate(tX, tY).scale(k);
};

/**
 * Gets the current zoom transform for the chart.rootmouse node
 * @param {ModchartsInteractive} chart - Chart object to read the zoom transform from
 * @returns {Transform} The current zoom transform
 */
const getZoomTransform = chart => zoomTransform(chart.rootMouse.node());

/**
 * Programmatic pan
 * @param {object} chart - Modcharts chart instance
 * @param {number} distance - Pan distance in XAxis range pixels (positive goes left, negative goes right)
 * @param {number} duration - Animation duration in milliseconds
 */
const panChart = ({ chart, distance = -10, duration = chart.params.zoomDuration || 350 }) => {
	const { k: scale } = getZoomTransform(chart);
	if (!chart.state.zooming) { // This check is needed to ensure that a new event doesn't get triggered before the current one has finished
		if (duration === -1) { // This test needed for unit testing as transitions don't play well in unit tests
			chart.rootMouse
				.call(chart.zoom.translateBy, distance / scale, 0);
		} else {
			chart.rootMouse
				.transition()
				.duration(duration)
				.call(chart.zoom.translateBy, distance / scale, 0);
		}
	}
};

/**
 * Pans the chart back to the present view
 * @param {object} chart - Modcharts chart instance
 */
const panToPresent = chart => {
	// Calculate distance to get back to present view (but keep the current scale)
	const frameScale = chart.xAxis.primaryScale;
	const frameDomainStop = frameScale.domain()[1];
	const presentDomainStop = chart.state.xAxisPresentScale.domain()[1];
	const distance = frameScale(frameDomainStop) - frameScale(presentDomainStop);

	panChart({ chart, distance });
};

/**
 * Binds the zoom behavior to the rootMouse element
 * @param {object} chart - Modcharts chart instance
 */
const registerZoom = chart => {
	if (
		chart.params.zoomEnabled &&
		!chart.state.zoomRegistered &&
		!chart.state.tool
	) {
		chart.rootMouse
			.call(chart.zoom)
			.on('dblclick.zoom', null); // Disable double-click handling to make sure tool double clicking works
		chart.state.zoomRegistered = true;
	}
};

/**
 * Resets the zoom transform to the specified transform. If no transform is passed in, the initial transform
 * will be used. If no initial transform is available, the zoom identity ({ k: 1, x: 0, y: 0 }) will be used.
 * @param {object} chart - Modcharts chart instance
 * @param {number} duration - Animation duration in milliseconds
 * @param {object} transform - The D3 zoom transform to use for the reset
 */
const resetZoomCenter = ({
	chart,
	duration = chart.params.zoomResetDuration,
	transform = chart.state.initialTransform || zoomIdentity // Use zoomIdentity as fallback in case initialTransform hasn't been set
}) => {
	// Using a transition here will cause the chart to animate back to the given zoom state (initial by default) by causing a number of zoom events
	chart.rootMouse
		.transition()
		.duration(duration)
		.call(chart.zoom.transform, transform);
};

/**
 * Unbinds the zoom behavior from the rootMouse element
 * @param {object} chart - Modcharts chart instance
 */
const unregisterZoom = chart => {
	if (chart.state.zoomRegistered) {
		chart.rootMouse.on('.zoom', null);
		chart.state.zoomRegistered = false;
		chart.state.zooming = false;
	}
};

/**
 * Updates the zoom transform to the current zoom location. This is usually needed when
 * scales or new data have been loaded. If no transform is provided, the zoomIdentity
 * is used, effectively resetting the chart to the present view.
 * @param {object} chart - Modcharts chart instance
 * @param {object} transform - The D3 zoom transform to use for the update
 */
const updateZoomCenter = ({ chart, transform = zoomIdentity }) => {
	chart.state.domainAtZoomStart = null;
	chart.state.scaleAtZoomStart = null;
	// Calling transform without a transition results in setting the current location to represent  { k: 1, x: 0, y: 0 }
	chart.rootMouse.call(chart.zoom.transform, transform);
};

/**
 * Customize the distance zoomed during a WheelEvent
 * @param {object} chart - Modcharts chart instance
 * @param {object} d3event - D3 zoom event
 * @returns {number} - wheel delta
 */
const wheelDelta = (chart, d3event) => {
	chart.state.isWheelEvent = true; // will be set back to false on zoom end
	return -d3event.deltaY * (d3event.deltaMode === 1 ? 0.03 : d3event.deltaMode ? 1 : 0.002);
};

/**
 * Programmatic zoom
 * zoom in with a value like 1.3
 * zoom out with a value like (1 / 1.3)
 * @param {object} chart - Modcharts chart instance
 * @param {number} scale - Zoom distance in XAxis scale units
 * @param {number} duration - Animation duration in milliseconds
 */
const zoomChart = ({ chart, duration = chart.params.zoomDuration, scale = 1.3 }) => {
	if (!chart.state.zooming) { // This check is needed to ensure that a new event doesn't get triggered before the current one has finished
		if (duration === -1) { // This test needed for unit testing as transitions don't play well in unit tests
			chart.rootMouse
				.call(chart.zoom.scaleBy, scale);
		} else {
			chart.rootMouse
				.transition()
				.duration(duration)
				.call(chart.zoom.scaleBy, scale);
		}
	}
};

/**
 * Fires when zoom action begins
 * @param {Object} d3event - D3 zoom event
 */
const onZoomStart = (chart, d3event) => {
	// reset zoomreset
	window.clearTimeout(chart.zoomResetTimeout);

	// cancel and unregister zoom handling in specific circumstances
	if (
		chart.status === 0 ||
		chart.state.tool ||
		!chart.params.zoomEnabled ||
		chart.state.resizePanel
	) {
		unregisterZoom(chart);
		return;
	}

	// unselect any selected tools
	if (chart.selectTool) chart.selectTool(null);

	chart.state.zooming = true;

	// store date of zoom
	chart.state.zoomDate = new Date();

	// set closed_hand cursor if we're panning
	if (!chart.state.isWheelEvent) chart.setCursor(types$1.CLOSED_HAND);

	// Store scale domain before zoom takes place
	if (chart.xAxis) chart.state.domainAtZoomStart = chart.xAxis.primaryScale.domain();

	chart.eventEmitter.emit(types.ZOOM_START, { d3event });
};

/**
 * Fires during a zoom
 * @param {object} d3event - D3 zoom event object
 */
const onZoom = (chart, d3event) => {

	if (chart.status === 0) return;

	if (chart.state.xAxisPresentScale) {
		// Update the scale by applying the transform to the present scale
		chart.xAxis.primaryScale = d3event.transform.rescaleX(chart.state.xAxisPresentScale);
	}

	if (!chart.state.resizePanel) chart.renderQueue();

	chart.eventEmitter.emit(types.ZOOM, { d3event });
};

/**
 * Fires when zoom action has finished
 * @param {object} d3event - D3 zoom event object
 */
const onZoomEnd = (chart, d3event) => {
	if (!chart.panels || chart.panels.length === 0 || !chart.state.domainAtZoomStart) return;

	chart.state.isWheelEvent = false;
	const domain = chart.xAxis.primaryScale.domain();
	const domainZoomStart = chart.state.domainAtZoomStart;
	const domainZoomStop = chart.xAxis.primaryScale.domain();
	const domainChanged =
		domainZoomStart[0] !== domainZoomStop[0] ||
		domainZoomStart[1] !== domainZoomStop[1];

	// set chart params dateStart/Stop to ensure that any backfills/polls return to the same domain range.
	// only alter dateStart/dateStop if domain has actually changed.
	if (chart.exchangeDates && domainChanged) {
		const domainFirst = chart.exchangeDates[Math.floor(domain[0])];
		const domainLast = chart.exchangeDates[Math.floor(domain[1])];

		if (domainFirst && domainLast) {
			chart.params.dateStart = new Date(domainFirst);
			chart.params.dateStop = new Date(domainLast);
		}

		if (chart.params.zoomReset) enforceZoomReset(chart);

		chart.normalizePanelDomains();

		// check if backfill request is needed
		if (
			chart.exchangeDates &&
			chart.status !== 0 &&
			chart.params.backfill &&
			!chart.params.customData // CH-4898 backfill is not supported for custom data
		) {
			chart.backfillData();
		}

		// a final render here fixes occasional mouseweheel quirks (NXC-429)
		chart.renderQueue();
	}

	chart.state.zooming = false;
	chart.setCursor(types$1.CROSSHAIR);

	chart.eventEmitter.emit(types.ZOOM_END, d3event);
	chart.eventEmitter.emit(types.BOUNDARY_STATUS, chart._boundaryStatus);
};

/** Enforce zoom boundaries to either ruler edge or available data edge. */
const enforceZoomReset = chart => {
	const lastIndex = chart._calcLastIndex();

	if (lastIndex) {
		const currentDomain = chart.xAxis.primaryScale.domain();

		// Check if left/right edge of xAxis is out of bounds
		if (currentDomain[1] > lastIndex || currentDomain[0] < 0) {
			const domainChange = currentDomain[1] > lastIndex
				? currentDomain[1] - lastIndex // This would be a positive value
				: currentDomain[0]; // This would be a negative value
			const newRightDomain = currentDomain[1] - domainChange < lastIndex
				? currentDomain[1] - domainChange
				: lastIndex;

			const extendsLeft = Math.round(currentDomain[0]) < 0;
			const extendsRight = Math.round(currentDomain[1]) > lastIndex;

			// Chart has zoomed out such that we're past the edge on both sides
			if (extendsRight && extendsLeft) {
				const newTransform = getTransformFromScales(
					chart.state.xAxisPresentScale,
					// Copy present scale and set domain to the proper exchangeDates indices
					chart.state.xAxisPresentScale.copy().domain([0, lastIndex])
				);
				resetZoomCenter({ chart, transform: newTransform });
			} else if (extendsRight) {
				// Get current transform values
				const { k } = getZoomTransform(chart);

				const leftBoundaryPresentScale = chart.state.xAxisPresentScale(0);
				const rightBoundaryPresentScale = chart.state.xAxisPresentScale(newRightDomain);

				// Create new transform
				const { height, width, left } = chart.panels[0].size;
				const newXTranslation = width - (rightBoundaryPresentScale * k) + left;
				const newYTranslation = height - (height * k);
				let newTransform = zoomIdentity.translate(newXTranslation, newYTranslation).scale(k);

				// If the new translation would cause the chart to go out of bounds on the other side, zoom out to show all data
				if (newTransform.applyX(leftBoundaryPresentScale) > 0) {
					newTransform = getTransformFromScales(
						chart.state.xAxisPresentScale,
						// Copy present scale and set domain to the proper exchangeDates indices
						chart.state.xAxisPresentScale.copy().domain([0, lastIndex])
					);
				}

				// Move right edge back to data
				resetZoomCenter({ chart, transform: newTransform });
			} else if (extendsLeft) {
				// Get current transform values
				const { k } = getZoomTransform(chart);

				const leftBoundaryInitialScale = chart.state.xAxisPresentScale(0);
				const rightBoundaryInitialScale = chart.state.xAxisPresentScale(lastIndex);

				// Create new transform
				const { height, padding, width } = chart.panels[0].size;
				const newXTranslation = padding.left - (leftBoundaryInitialScale * k);
				const newYTranslation = height * (1 - k);
				let newTransform = zoomIdentity.translate(newXTranslation, newYTranslation).scale(k);

				// If the new translation would cause the chart to go out of bounds on the other side, zoom out to show all data
				if (newTransform.applyX(rightBoundaryInitialScale) < width) {
					newTransform = getTransformFromScales(
						chart.state.xAxisPresentScale,
						// Copy present scale and set domain to the proper exchangeDates indices
						chart.state.xAxisPresentScale.copy().domain([0, lastIndex])
					);
				}

				// Move left edge back to data
				resetZoomCenter({ chart, transform: newTransform });
			}
		}
	}
};

const zoomOutWithRightEdgeAnchor = (chart, scaleChange, duration = 300) => {
	const lastIndex = chart._calcLastIndex();
	const rightEdgeX = chart.xAxis.primaryScale(lastIndex); // X position of the last data point in data space

	// Get the current zoom transform
	const currentTransform = getZoomTransform(chart);
	const { x: currentX, y: currentY, k: currentK } = currentTransform;

	// Convert the right edge X position from data space to screen space
	const effectiveMouseX = rightEdgeX * currentK + currentX;

	// Calculate the new scale
	const newScale = currentK * scaleChange;

	// Calculate the new translation to keep the right edge as anchor
	const newTranslationX = effectiveMouseX - rightEdgeX * newScale;

	// Create the new transform
	const newTransform = zoomIdentity.translate(newTranslationX, currentY).scale(newScale);

	// Apply the new transform with a transition
	chart.rootMouse.transition()
		.duration(duration)
		.call(chart.zoom.transform, newTransform);
};

var zoomUtil = {
	createZoom,
	enforceZoomReset,
	getTransformFromScales,
	getZoomTransform,
	onZoom,
	onZoomEnd,
	onZoomStart,
	panChart,
	panToPresent,
	registerZoom,
	resetZoomCenter,
	unregisterZoom,
	updateZoomCenter,
	zoomChart,
	zoomIdentity,
	zoomOutWithRightEdgeAnchor
};

/* eslint-disable no-prototype-builtins */
/* eslint-disable no-invalid-this */

/**
 * Convert and export the current dataset as a JavaScript object literal.
 * @param {string} outputFormat - The format of the series output ('object' or 'array'). Defaults to 'object' for backward compatibility.
 * @param {Object} args - Optional parameters for future configurations (e.g., { dateFormat: 'ISO' }).
 * @returns {Object} The exported data as a JavaScript object literal, including series, columns, and symbolData.
 */
function exportData(outputFormat = 'object', args = { dateFormat: 'local' }) {
	const isIntraday = this.state && this.state.isIntraday;
	const symbolData = (this.data && this.data[this.params.symbol]) ? this.data[this.params.symbol] : {};
	const series = outputFormat === 'array' ? [] : {};
	const columns = [];

	// Retrieve symbol details from xref to include in symbolData metadata
	const symbolXref = this.xref[this.params.symbol];
	const exchangeUTCOffset = symbolXref?.utcOffset || 0;

	if (typeof symbolData === 'object' && Object.keys(symbolData).length > 0) {
		Object.keys(symbolData).forEach(datasetEntry => {
			const dataset = this.getFirstDataset(symbolData[datasetEntry]);
			for (const item of dataset) {
				const localDate = new Date(item.date);

				// Determine the date to use based on format and whether it is intraday
				let formattedDate;
				if (args.dateFormat === 'ISO') {
					let adjustedUTCDate = new Date(localDate.getTime() - (exchangeUTCOffset * 60 * 1000)); // Apply exchange offset
					adjustedUTCDate = new Date(adjustedUTCDate.getTime() - (localDate.getTimezoneOffset() * 60 * 1000)); // Apply local offset

					// Format without time portion if interday and using ISO format
					formattedDate = isIntraday ? adjustedUTCDate.toISOString() : adjustedUTCDate.toISOString().split('T')[0];
				} else {
					// Use preformatted localDate if not using ISO
					formattedDate = isIntraday ? localDate.toLocaleString() : localDate.toLocaleDateString();
				}

				if (outputFormat === 'object') {
					// Avoid overwriting entries by checking if date exists
					if (!series[formattedDate]) {
						series[formattedDate] = {};
					}
				} else if (outputFormat === 'array') {
					// Ensure no duplicate date entries in the array format
					const existingEntry = series.find(entry => entry.date === formattedDate);
					if (!existingEntry) {
						series.push({ date: formattedDate });
					}
				}

				// Process data fields
				for (const key in item) {
					if (item.hasOwnProperty(key) && !/date|diff|hollow/.test(key)) {
						if (outputFormat === 'object') {
							series[formattedDate][key] = series[formattedDate][key] || item[key];
						} else if (outputFormat === 'array') {
							const currentEntry = series.find(entry => entry.date === formattedDate);
							if (currentEntry) {
								currentEntry[key] = item[key];
							}
						}

						if (!columns.includes(key)) {
							columns.push(key);
						}
					}
				}
			}
		});
	} else {
		console.warn('Symbol data not found or invalid format.');
	}

	return {
		series,
		columns,
		symbolData: {
			xid: this.params.symbol,
			companyName: symbolXref?.companyName,
			displaySymbol: symbolXref?.displaySymbol,
			currency: symbolXref?.currency,
			exchangeId: symbolXref?.exchangeId,
			utcOffset: exchangeUTCOffset,
			dateFormat: args.dateFormat,
			isIntraday
		}
	};
}

/**
 * Convert and export the complete dataset as a JSON object, including symbols, indicators, comparisons, and normalized values.
 * Repeated metadata (like indicator descriptions) is pulled into a separate metadata section to reduce redundancy.
 * Indicator values are grouped by date under 'series'.
 * The date is formatted in UTC ISO format.
 * @returns {Object} The exported data.
 */
function exportAllData() {
	const exportDataOut = {
		metadata: {
			indicators: {}
		},
		symbols: []
	};

	Object.keys(this.data).forEach(xid => {
		const symbolData = this.data[xid];
		const symbolXref = this.xref[xid];

		const symbolExport = {
			xid,
			companyName: symbolXref ? symbolXref.companyName : undefined,
			displaySymbol: symbolXref ? symbolXref.displaySymbol : undefined,
			currency: symbolXref ? symbolXref.currency : undefined,
			exchangeId: symbolXref ? symbolXref.exchangeId : undefined,
			utcOffset: symbolXref ? symbolXref.utcOffset : 0,
			series: {}
		};

		Object.keys(symbolData).forEach(datasetType => {
			const datasetEntries = symbolData[datasetType];

			Object.keys(datasetEntries).forEach(uid => {
				const dataset = datasetEntries[uid];
				const indicatorParams = this.getIndicatorByUid(uid);

				if (datasetType !== 'price' && !exportDataOut.metadata.indicators[uid]) {
					exportDataOut.metadata.indicators[uid] = {
						uid,
						type: datasetType,
						name: indicatorParams ? indicatorParams.params.name : datasetType,
						description: indicatorParams ? indicatorParams.params.description : ''
					};
				}

				dataset.forEach(item => {
					const dateObj = item.date;

					const localDate = new Date(dateObj);

					// Convert the date back to the original UTC exchange date
					let adjustedUTCDate = localDate;
					adjustedUTCDate = new Date(localDate.getTime() - (symbolExport.utcOffset * 60 * 1000)); // Subtract the exchange offset (in minutes)
					adjustedUTCDate = new Date(adjustedUTCDate.getTime() - (localDate.getTimezoneOffset() * 60 * 1000)); // Subtract the local offset (in minutes)

					const formattedDate = new Date(adjustedUTCDate).toISOString();

					if (!symbolExport.series[formattedDate]) {
						symbolExport.series[formattedDate] = { price: {}, indicators: {} };
					}

					if (datasetType === 'price') {
						symbolExport.series[formattedDate].price = {
							open: item.open,
							high: item.high,
							low: item.low,
							close: item.close
						};

						if (this.panels[0].isNormalized && this.state.normalizeValues?.indicators && this.state.normalizeValues.indicators[uid]) {
							const normalizedValues = this.state.normalizeValues.indicators[uid];
							symbolExport.series[formattedDate].price.actual = {
								open: this.getActualFromNormalized(item.open, normalizedValues.open),
								high: this.getActualFromNormalized(item.high, normalizedValues.high),
								low: this.getActualFromNormalized(item.low, normalizedValues.low),
								close: this.getActualFromNormalized(item.close, normalizedValues.close)
							};
						}
					} else {
						const valueObj = {};
						const valueKeys = Object.keys(item).filter(key => !/date|dateIndex|diff|hollow/.test(key));
						valueKeys.forEach(key => {
							valueObj[key] = item[key];
						});
						symbolExport.series[formattedDate].indicators[uid] = valueObj;
					}
				});
			});
		});

		exportDataOut.symbols.push(symbolExport);
	});

	return exportDataOut;
}

/* eslint-disable no-console */
class ChartStreamer {

	constructor(chart = {}) {
		this.chart = chart;
		this.params = {
			rollup: 250,
			timeout: 300,
			logLevel: null,
			url: this._buildBaseURL(chart.params.stream)
		};
		this.streamManager = new StreamManager({
			url: this.params.url,
			rollup: this.params.rollup,
			timeout: this.params.timeout,
			logLevel: this.params.logLevel
		});
		this.StreamManagerTest = StreamManager; // temp for testing
		this.chart.eventEmitter.on('STREAM_TRADE', e => {
			if (this.params.logLevel === 'debug') console.log('ChartStreamer emit: STREAM_TRADE', e);
			this.chart.onStreamTrade({
				date: e?.date,
				last: e?.price,
				symbol: e?.xid
			});
		});

		this.chart.eventEmitter.on('STREAM_ERROR', e => {
			if (this.params.logLevel === 'debug') console.error('ChartStreamer emit: STREAM_ERROR', e);
		});

		this.chart.eventEmitter.on('STREAM_QUOTE', e => {
			if (this.params.logLevel === 'debug') console.log('ChartStreamer emit: STREAM_QUOTE', e);
		});
	}

	_buildBaseURL(params) {
		if (params.url) return params.url; // url takes precedence if specified
		if (params.environment) {
			return `https://streamer.${params.environment}.services.mdgapp.net/streamserver/streamserver.dll`;
		}
		return null;
	}

	_attachStreamManagerListeners() {

		this._detachStreamManagerListeners(); // detach to ensure we aren't adding duplicate listeners later

		if (
			!this.chart.state.authToken &&
			!this.chart.state.sessionId) {
			return;
		}

		// listeners
		this.streamManager.on('streamserver.trade', e => {
			this._log('Receieved event: streamserver.trade', e.data);
			if (e.data && typeof e.data.price === 'number') {
				this.chart.eventEmitter.emit('STREAM_TRADE', e.data);
			}
		});

		this.streamManager.on('streamconnector.complete', e =>
			this._log('Receieved event: streamconnector.complete', e.data)
		);

		this.streamManager.on('streamconnector.done', e =>
			this._log('Receieved event: streamconnector.done', e)
		);

		this.streamManager.on('streamconnector.connecting', e =>
			this._log('Receieved event: streamconnector.connecting', e.data)
		);

		this.streamManager.on('streamconnector.connected', e =>
			this._log('Receieved event: streamconnector.connected', e.data)
		);

		this.streamManager.on('streamserver.quote', e => {
			this._log('Receieved event: streamserver.quote', e.data);
			if (e.data && typeof e.data.close === 'number') {
				this.chart.eventEmitter.emit('STREAM_QUOTE', e.data);
			}
		});

		this.streamManager.on('streamserver.heartbeat', e => {
			this._log('Receieved event: streamserver.heartbeat', e.data);
			this.chart.eventEmitter.emit('STREAM_HEARTBEAT', e.data);
		});

		this.streamManager.on('streamconnector.error', e => {
			this._log('Receieved event: streamconnector.error', e);
			this.chart.eventEmitter.emit('STREAM_ERROR', e);
		});

		this.streamManager.on('streamserver.ask', e =>
			this._log('Receieved event: streamserver.ask', e.data)
		);

		this.streamManager.on('streamserver.bid', e =>
			this._log('Receieved event: streamserver.bid', e.data)
		);

		this.streamManager.on('streamserver.guid', e =>
			this._log('Receieved event: streamserver.guid', e.data)
		);

		this.streamManager.on('streamserver.init.start', e =>
			this._log('Receieved event: streamserver.init.start', e.data)
		);

		this.streamManager.on('streamserver.init.end', e =>
			this._log('Receieved event: streamserver.init.end', e.data)
		);

		this.streamManager.on('streamconnector.forbidden', e => {
			this._log('Receieved event: streamconnector.forbidden', e);
			this.chart.eventEmitter.emit('STREAM_ERROR', e);
		});
	}

	/**
	 * Detaches all listeners from the current StreamManager
	 * @private
	 */
	_detachStreamManagerListeners() {
		if (this.streamManager) {
			this.streamManager.removeAllListeners();
		}
	}

	_log(msg, detail) {
		if (this.params.logLevel === 'debug') console.log(`ChartStreamer: ${msg}`, detail);
	}

	start() {
		if (this.chart?.panels?.length) {
			// add all price symbols to the stream call
			const currSymbols = this.chart.panels[0].indicators
				.filter(i => i.params.id === 'price')
				.map(i => {
					return {
						xid: i.params.symbol,
						events: 't'
					};
				});
			// clear out any tokens or session ids
			this.streamManager.removeSessionId();
			this.streamManager.removeBearerToken();
			// set the session id or token if they exist
			if (this.chart.state.sessionId) {
				this.streamManager.setSessionId(this.chart.state.sessionId);
			} else if (this.chart.state.authToken) {
				this.streamManager.setBearerToken(this.chart.state.authToken);
			}
			this._attachStreamManagerListeners();
			this.streamManager.setSymbols(currSymbols);
			this.chart.params.stream.enabled = true;
			this.streamManager.start();
		}
	}

	stop() {
		this._detachStreamManagerListeners();
		this.streamManager.stop();
	}
}

/* eslint-disable no-use-before-define */

/**
 * Main configuration collection. These properties can be serialized and stored.
 * @param {Object} params - Collection of custom params passed in to the chart
 * @returns {Object} Merged collection of custom params (passed in as the params argument) and default params
 */
const getDefaultParams = function (chart, params) {
	let chartParams = {
		/**
		 * use an animated effect to reveal chart panels
		 *  "all" = animate every time data loads
		 *  "first" = animate only the first time data loads
		 *  "staged" = animation will be staged but not immediately run
		 */
		animation: params.animation || null, // "all", "first", "staged"

		/** use an animated effect to reveal the panels on first-time load */
		animationDuration:
			typeof params.animationDuration === 'number'
				? params.animationDuration
				: 1200,

		/** following a pan/zoom event, backfill any historic data gaps with a delta data request */
		backfill:
			typeof params.backfill === 'boolean' ? params.backfill : true,

		/** candle width tolerance - snap to 1px wide candles at this width (0 to disable snapping completely) */
		candleWidthTolerance:
			typeof params.candleWidthTolerance === 'number'
				? params.candleWidthTolerance
				: 1.2,

		/** clip intraday markers to exchange sessions */
		clipSessions:
			typeof params.clipSessions === 'boolean'
				? params.clipSessions
				: true,

		/**
		 * Collapse gaps in XAxis when exchange has an early closure. Note: gaps will still appear
		 * when compared to an exchange that does trade during the closure.
		 */
		collapseAlternateSessionGaps:
			typeof params.collapseAlternateSessionGaps === 'boolean'
				? params.collapseAlternateSessionGaps
				: true,

		/** Configure how the crosshair is displayed. Make sure the object is an object and also not an array. */
		crosshair:
			Object(params.crosshair) === params.crosshair && !Array.isArray(params.crosshair)
				? params.crosshair
				: {
					enabled: false, // Disabled by default
					flagEnabled: false,
					persist: false,
					primaryOnly: false,
					returnClosestCustomData: true,
					showCircle: false,
					showFlagCrosshairDisabled: false,
					snapHorizontalToData: true
				},

		/** Specify a desired currency code (ex. "EUR") if different from the default currency a symbol is priced in. */
		currency: params.currency || null,

		/**
		 * an object containing one or more custom datasets.  datasets should be in the format:
		 * { "mydata": [{date: "2015-01-05", value: 12.4, additionalVal: 14.2, additionalVal2: 41.4, ...}] }
		 * the key of the dataset ("mydata") can be used as the custom indicator's "datasetId" param.
		 */
		customData: params.customData || null,

		/** enable custom dataInterface */
		dataInterface: params.dataInterface || null,

		/** data interval (1, 3, 5, 10) */
		dataInterval: params.dataInterval || null,

		/** data api should return values as normalized figures (percent change) instead of actuals */
		dataNormalized:
			typeof params.dataNormalized === 'boolean'
				? params.dataNormalized
				: false,

		/** data period ("Hour", "Minute", "Day") */
		dataPeriod: params.dataPeriod || null,

		/** data precision - series values will return this number of decimal points */
		dataPrecision: params.dataPrecision || 3,

		/** If dateStart/Stop specified, it will become the visible canvas range even if the chartapi data range is different. */
		dateStart: params.dateStart || null,
		dateStop: params.dateStop || null,

		/** chart api - total days of data to request */
		days: params.days || null,

		/**
		 * Ignore streamed events until we have at least one datapoint for the current day.  This prevents double candlesticks
		 * at market open but also will mean the chart won't stream for up to one interval/period at the beginning of the day. (NXC-690)
		 */
		delayMarketOpenStream:
			typeof params.delayMarketOpenStream === 'boolean'
				? params.delayMarketOpenStream
				: false,

		disableLogMessages:
			typeof params.disableLogMessages === 'boolean'
				? params.disableLogMessages
				: false,

		enableRightAlignFinalLabel:
			typeof params.enableRightAlignFinalLabel === 'boolean'
				? params.enableRightAlignFinalLabel
				: true,

		/** exchangeOffset */
		exchangeOffset:
			typeof params.exchangeOffset === 'number'
				? params.exchangeOffset
				: 0,

		/** data api should return extended sessions along with the regular session */
		extendedHours:
			typeof params.extendedHours === 'boolean'
				? params.extendedHours
				: false,

		/** duration of the premarket session (minutes) */
		extendedHoursDurationPre:
			typeof params.extendedHoursDurationPre === 'number'
				? params.extendedHoursDurationPre
				: 330,

		/** duration of the postmarket session (minutes) */
		extendedHoursDurationPost:
			typeof params.extendedHoursDurationPost === 'number'
				? params.extendedHoursDurationPost
				: 240,

		/** feed selection group (optional) */
		feedGroup: params.feedGroup || null,

		/** Only render lastClose flags on primary indicator */
		lastCloseFlagOnlyPrimary:
			typeof params.lastCloseFlagOnlyPrimary === 'boolean'
				? params.lastCloseFlagOnlyPrimary
				: false,

		/** Allow price flags to overlap (default = true) */
		flagOverlap:
			typeof params.flagOverlap === 'boolean'
				? params.flagOverlap
				: true,

		/** A feature used by the mobile SDK implementation of Modcharts.  Array of lower indicator uids. */
		hiddenLowers: params.hiddenLowers || [],

		/** Ignore the bottom margin of the last panel in sizing calculations */
		ignoreLastPanelBottomMargin:
			typeof params.ignoreLastPanelBottomMargin === 'boolean'
				? params.ignoreLastPanelBottomMargin
				: true,

		/** Ignore the top margin of the lower panels in sizing calculations */
		ignoreLowerPanelTopMargin:
			typeof params.ignoreLowerPanelTopMargin === 'boolean'
				? params.ignoreLowerPanelTopMargin
				: false,

		/** The limit of consecutive 401 requests due to invalid tokens */
		invalidTokenLimit:
			typeof params.invalidTokenLimit === 'number'
				? params.invalidTokenLimit
				: 50,

		/** Default locale file */
		locale: typeof params.locale === 'object'
			? params.locale
			: LocaleEnUs,

		/** Switch to a 2-day chart if on 1-day with only a single datapoint */
		minMarketOpen:
			typeof params.minMarketOpen === 'boolean'
				? params.minMarketOpen
				: true,

		/** normalizeDate - the date which should be used as the chartapi normalize value */
		normalizeDate: params.normalizeDate
			? new Date(params.normalizeDate)
			: null,

		/** normalizeValue - (deprecated) the value which should be used as the normalization starting point */
		normalizeValue: params.normalizeValue,

		/** normalizeValues - the ohlc value which should be used as the normalization starting point */
		normalizeValues: params.normalizeValues,

		/** A ratio of the panel height to the chart width which can be used to size the lower panel height (OPTIONAL) */
		panelHeightToChartWidthRatioLower: params.panelHeightToChartWidthRatioLower
			? Number(params.panelHeightToChartWidthRatioLower)
			: null,

		/** A ratio of the panel height to the chart width which can be used to size the upper panel height (OPTIONAL) */
		panelHeightToChartWidthRatioUpper: params.panelHeightToChartWidthRatioUpper
			? Number(params.panelHeightToChartWidthRatioUpper)
			: null,

		/** optional fixed upper panel height */
		panelHeightUpper: params.panelHeightUpper
			? Number(params.panelHeightUpper)
			: null,

		/** optional fixed lower panel height */
		panelHeightLower: params.panelHeightLower
			? Number(params.panelHeightLower)
			: null,

		/** optional minimum height percentage for upper panel (used in conjunction with panelHeightPctLower) */
		panelHeightPctMinUpper:
			typeof params.panelHeightPctMinUpper === 'number'
				? params.panelHeightPctMinUpper
				: 0.25,

		/** optional lower panel height percentage for fluid-height charts.  ex: 0.1 would mean a tenth of the overall chart height. */
		panelHeightPctLower:
			typeof params.panelHeightPctLower === 'number'
				? params.panelHeightPctLower
				: null,

		/** Enable panel resizing */
		panelResize:
			typeof params.panelResize === 'boolean'
				? params.panelResize
				: false,

		/** which panel(s) should receive an XAxis by default (can be overridden on a per-panel basis) */
		panelXAxis: params.panelXAxis || 'first', // "last", "first", "all", "none"

		/** If set to true, this allows the YAxis width to be auto-calculated to fit all current YAxis labels, overriding the panel.padding.right style. */
		panelYAxisAutoWidth:
			typeof params.panelYAxisAutoWidth === 'boolean'
				? params.panelYAxisAutoWidth
				: false,

		/** number of minutes between fresh data poll (0 to turn off polling) */
		poll: typeof params.poll === 'number' ? params.poll : 0,

		/** (deprecated) Draw to the normalized point on line/fill charts */
		prependNormalizedPoint:
			typeof params.prependNormalizedPoint === 'boolean'
				? params.prependNormalizedPoint
				: false,

		/** enable the price hover emitters */
		hoverIndicator: typeof params.hoverIndicator === 'boolean' ? params.hoverIndicator : false,

		/** request realtime data (is enforced via feed entitlements on server-side) */
		realtime: typeof params.realtime === 'boolean' ? params.realtime : false,

		/**
		 * Flag to render duplicate row labels, even if they are the same as those to the left/right.
		 * Background: this came out of a request to have the same date on the upper row as the lower row, but formatted differently.
		 * For instance, "Monday" (upper) and "5/24" (lower), where "Monday" was just another way to format 5/24.
		 * To achieve this effect, the user can specify somthing like:
		 *    chart.params.locale.labelTemplates.xAxis.doubleRow.hours = { default: '%A', first: '%-m/%-d' }; // 'A%' = 'Monday', '%-m/%-d' = '5/24'
		 *    chart.params.locale.labelTemplates.xAxis.doubleRow.days.intraday = '%-m/%-d'; // '%-m/%-d' = '5/24'
		 * The labeler then processes the current labels according to the rules as usual, based on chart width, number of labels, etc.
		 * In certain circumstances, that previous alteration to the labelTemplates will create duplicate labels in one of the collections, e.g. "hours": ["Monday", "Monday", "Monday"].
		 * So, the renderDuplicateLabels flag will determine how to handle this situation.
		 *   If set to KEEP_LEFT (the default), it will only render the first "Monday" label.
		 *   If set to KEEP_ALL, it will render all "Monday" labels (this will probably never be needed)
		 *   If set to KEEP_RIGHT, it will render the final "Monday" label.
		 * Nearly always you'll want KEEP_LEFT, but KEEP_ALL and KEEP_RIGHT are provided for flexibility.
		 */
		renderDuplicateLabels: params.renderDuplicateLabels || renderDuplicateLabelsCategories.KEEP_LEFT,

		/** Trap keydown events to remove active tool on backspace/delete */
		removeToolByKey:
			typeof params.removeToolByKey === 'boolean'
				? params.removeToolByKey
				: true,

		/** Return an extra datapoint to remove left edge gaps in line and fill markers (CH-1745) */
		returnPreviousPoint:
			typeof params.returnPreviousPoint === 'boolean'
				? params.returnPreviousPoint
				: true,

		/**
		 * date ranges for the timeservice call.  unit is days.  the timeservice call returns
		 * tradable days within a date range, which is used to construct the XAxis ruler.
		 */
		rulerIntradayStart: params.rulerIntradayStart || 31,
		rulerIntradayStop: params.rulerIntradayStop || 3,
		rulerInterdayStart:
				params.rulerInterdayStart || Math.floor(365.25 * 35),
		rulerInterdayStop: params.rulerInterdayStop || 10,

		/** Enable/disable display of all flags */
		showFlags:
			typeof params.showFlags === 'boolean'
				? params.showFlags
				: false,

		/** turn on component series flags */
		showComponentSeriesFlags:
			typeof params.showComponentSeriesFlags === 'boolean'
				? params.showComponentSeriesFlags
				: false,

		/** Show the crosshair at the most recent position after initial chart load and time span changes */
		showCrosshairAfterLoad:
			typeof params.showCrosshairAfterLoad === 'boolean'
				? params.showCrosshairAfterLoad
				: false,

		/** Enable/disable display of last close flags. This will affect all price indicators. */
		showLastCloseFlags:
			typeof params.showLastCloseFlags === 'boolean'
				? params.showLastCloseFlags
				: true,

		/** enable xaxis flag */
		showXAxisFlag:
			typeof params.showXAxisFlag === 'boolean'
				? params.showXAxisFlag
				: false,

		/** major currency flag (NXC-748) */
		showMajorCurrency:
			typeof params.showMajorCurrency === 'boolean'
				? params.showMajorCurrency
				: true,

		/** turn on lunch break labels (DEPRECATED) */
		showLunchBreakLabels:
			typeof params.showLunchBreakLabels === 'boolean'
				? params.showLunchBreakLabels
				: false,

		/** turn on holiday/closure labels */
		showClosureLabels:
			typeof params.showClosureLabels === 'boolean'
				? params.showClosureLabels
				: false,

		/** Show market close labels on the X-axis. ('once', 'all', null). */
		showMarketCloseLabels: params.showMarketCloseLabels
			? params.showMarketCloseLabels
			: null,

		/** Show a vertical legend to the right of the YAxis */
		showVerticalPanelLegend:
			typeof params.showVerticalPanelLegend === 'boolean'
				? params.showVerticalPanelLegend
				: false,

		/** Set the chart size manually */
		size:
			params.size
				? { ...params.size }
				: null,

		/** Configure the ChartStreamer class */
		stream: params.stream || {
			enabled: false,
			rollup: 250,
			timeout: 300,
			logLevel: null, // 'debug'
			environment: 'dev', // 'dev', 'qa', 'prod'
			//url: 'https://demo.markitqa.com/streamserver/streamserver.dll', // TEMPORARY FOR TESTING
			url: null, // 'https://streamer.dev.services.mdgapp.net/streamserver/streamserver.dll',
			...params.stream
		},

		/** overall chart style vars */
		style: { ...(params.style || {}) },

		/** styles that are common to multiple components */
		styleCommon: { ...(params.styleCommon || {}) },

		/** primary symbol */
		symbol: (params.symbol && params.symbol.toString()) || '',

		/** array of comparison symbols (deprecated) */
		symbolCompare: params.symbolCompare || [],

		/** allow custom initial tool params */
		tools: { ...(params.tools || {}) },

		/** Enable tool handle snapping */
		toolSnapOHLC:
			typeof params.toolSnapOHLC === 'boolean'
				? params.toolSnapOHLC
				: false,

		/** enable the use of longDoublePress crosshair tool (mobile only) */
		toolReturnsCalculatorEnabled:
			typeof params.toolReturnsCalculatorEnabled === 'boolean'
				? params.toolReturnsCalculatorEnabled
				: false,

		/** enable the use of longPress crosshair tool (mobile only) */
		toolOHLCCrosshairEnabled:
			typeof params.toolOHLCCrosshairEnabled === 'boolean'
				? params.toolOHLCCrosshairEnabled
				: false,

		/** optional usage reporting flag */
		usageReportingAppFeatureType:
			params.usageReportingAppFeatureType || null,


		/** Rely on computed dimensions of panel DOM element to size each panel. */
		usePanelComputedHeight: !!params.usePanelComputedHeight,

		/** Adjust the domain for certain marker types (bar, candle) to avoid truncated bars */
		useXAxisDomainAdjustment:
			typeof params.useXAxisDomainAdjustment === 'boolean'
				? params.useXAxisDomainAdjustment
				: true,

		/**
		 * Enable alternate short month labels on the XAxis
		 */
		xAxisShortMonths:
			typeof params.xAxisShortMonths === 'boolean'
				? params.xAxisShortMonths
				: true,

		/**
		 * Enable single-row mode on the XAxis
		 */
		xAxisSingleRowMode:
			typeof params.xAxisSingleRowMode === 'boolean'
				? params.xAxisSingleRowMode
				: false,

		/**
		 * Pass additional headers to data XHR request.  expects an array in this format:
		 * [{"name": "header-x", "value": "value-1"}, {"name": "header-y", "value": "value-2"}]
		 */
		xhrRequestHeaders: params.xhrRequestHeaders || [],

		/**
		 * Pass cookies in cross-domain requests for chart data
		 */
		xhrWithCredentials:
			typeof params.xhrWithCredentials === 'boolean'
				? params.xhrWithCredentials
				: false,

		/** yAxis scale type */
		yAxisScale: params.yAxisScale || 'linear', // "linear", "log"

		/** Defines the side of the panel where the YAxis will be positioned */
		yAxisSide:
			params.yAxisSide || 'right', // 'left', 'right'

		/** Duration (in ms) for zoom transitions */
		zoomDuration:
			!isNaN(params.zoomDuration)
				? params.zoomDuration
				: 350,

		/** Duration (in ms) for the zoom reset transition */
		zoomResetDuration:
			!isNaN(params.zoomResetDuration)
				? params.zoomResetDuration
				: 150,

		/** snap to a domain limit after zoom/pan event */
		zoomReset:
			typeof params.zoomReset === 'boolean' ? params.zoomReset : true,

		/** is the zoomReset domain limited by the dataset or the XAxis ruler? */
		zoomResetBoundary:
			params.zoomResetBoundary || 'ruler', // "ruler", "dataset"

		/** toggle ability to zoom/pan with mouse */
		zoomEnabled:
			typeof params.zoomEnabled === 'boolean'
				? params.zoomEnabled
				: true
	};

	/** api auth mode */
	chartParams.apiAuthMode =
		typeof params.apiAuthMode === 'string'
			? params.apiAuthMode
			: 'default';

	/** custom content-type */
	chartParams.apiContentType =
		params.apiContentType || 'application/json';

	/** apiPath prefix for related API requests (series, save, load) */
	chartParams.apiPath = params.apiPath || 'https://cdn-api.markitdigital.com/apiman-gateway/MOD/chartworks-data/1.0/';

	/** API series action (deprecated) */
	chartParams.apiSeries = params.apiSeries || 'chartapi/series';

	/** location of chartAPI service */
	chartParams.chartAPI =
		params.chartAPI || chartParams.apiPath + chartParams.apiSeries;

	// Update API paths to https so requests don't break
	const apiCheck = ['apiPath', 'chartAPI'];
	apiCheck.forEach(param => {
		if (chartParams[param]) {
			const apiParam = chartParams[param];
			if (/^\/\//.test(apiParam)) { // Does param begin with // instead of https?
				chartParams[param] = `https:${apiParam}`;
			}
		}
	});

	/** constants (seems like a weird place to put these) */
	chart.limitIntradayTrading = 31;
	chart.limitIntradayCalendar = 30;

	// deprecation notice
	if (chartParams.imgPath) chart.warn('imgPath param is deprecated.');
	if (chartParams.usePanelComputedHeight) chart.warn('usePanelComputedHeight param is deprecated.');

	// If there are params in the query string, merge them into chartParams (this is mostly used for the demo / testing)
	if (typeof window === 'object' && window.location.search) {
		chartParams = merge(chartParams, _getQueryParams(chart, chartParams));
	}

	return chartParams;
};

const _getQueryParams = function (chart, chartParams) {
	// Query string params will override all other params
	const queryParams = new URLSearchParams(window.location.search);

	if (queryParams.get('enableQueryParams')) {
		// Don't add enableQueryParams to params
		queryParams.delete('enableQueryParams');
		_updateChartParams(chart, queryParams, chartParams);
	}
};

const _parseValue = value => {
	const valueLowerCase = typeof value === 'string'
		? value.toLowerCase()
		: value;

	if (value === true || valueLowerCase === 'true') return true;
	else if (value === false || valueLowerCase === 'false') return false;
	else if (!isNaN(value)) return Number(value);
	return value;
};

const _updateChartParams = (chart, newParams, chartParams) => {
	let breakOutOfLoop = false;
	let jsonParams;

	for (const [key, value] of newParams) {
		switch (key) {
			case 'params':
				// Parse and sanitize JSON string
				jsonParams = JSON.parse(value) || {};
				if (jsonParams.enableQueryParams) delete jsonParams.enableQueryParams;
				if (jsonParams.params) delete jsonParams.params;

				// Add JSON params to chart
				_updateChartParams(chart, Object.entries(jsonParams), chartParams);

				breakOutOfLoop = true;
				break;
			case 'symbol':
				// Save as string
				chartParams[key] = String(value);
				break;
			case 'token':
				// Call setToken
				chart.setAuthToken(value);
				break;
			default:
				// Only merge if new value is object
				chartParams[key] = typeof value === 'object' && value !== null
					// Don't nullify other param properties if not passed in
					? merge({}, _parseValue(value), chartParams[key])
					: _parseValue(value);
		}

		// Don't update any more params after handling 'params' case
		if (breakOutOfLoop) break;
	}
};

/* eslint-disable no-prototype-builtins */

/** Initialize panels, params, state and overall root dom */
class Modcharts {
	/**
	 * @param {string} rootSelector Root DOM selector
	 * @param {object} params Custom chart params
	 * @param {object} parent Optional parent object reference
	 */
	constructor(rootSelector, params = {}, parent = null) {
		this.version = pkg.version;
		this.uid = createUid();
		this.eventEmitter = new EventEmitter();
		this.chartLoader = new ChartLoader(this);
		this.callbackManager = new CallbackManager();
		this.panels = [];
		/** Optional reference for parent object of chart (if one exists) */
		this.parent = parent;
		this.state = this.getChartState();
		// Generate the default styles, applying common style overrides if needed
		const defaultStyles = generateDefaultStyles(params.styleCommon);
		this.getDefaultParams = getDefaultParams;
		this.params = this.getDefaultParams(this, params);
		// Merge defaultStyles with specific style overrides provided in params
		this.styleMap = merge({}, defaultStyles, params.style);
		this.rootSelector = rootSelector;
		/** The status of if the chart is in a "Chart Not Available" state. 0 means the chart isn't available; 1 means it is. */
		this.status = 0;
		this.xref = {};
		this.metaData = {};
		this.timeService = {};
		this.data = {};
		/** XHR object used to retrieve chart data. Should contain an object when chart is requesting data; should be null when chart isn't requesting data. Many functions use this variable to determine how to proceed. */
		this.dataXHR = null;
		this.xhrCancelSource = null;
		this.dataPrimary = [];
		this.translate = translate; // expose translate method
		this.setLocale(this.params.locale || LocaleEnUs);
		if (rootSelector) this.mount(rootSelector);
		this.exportData = exportData.bind(this);
		this.exportAllData = exportAllData.bind(this);
		this.stream = new ChartStreamer(this);
		return this;
	}

	/**
	 * Sets the DOM element that the chart will be rendered to
	 * @param {String} rootSelector - Selector string for root node
	 * @return {Boolean} Whether or not mount was successful
	 */
	mount(rootSelector) {
		this.rootModchart = select(rootSelector);

		// Get the document body for use on touch devices
		this.body = select(document.body);

		// Verify root DOM element exists
		if (!this.rootModchart.node()) {
			this.warn('Could not find root chart element');
			return false;
		}
		this.initDOM();
		return true;
	}

	/** FPS benchmark */
	getFPS(sec = 3) {
		const startDate = new Date().getTime();
		let x = 0;

		while (new Date().getTime() - startDate < sec * 1000) {
			this.render();
			x++;
		}

		return `${(x / sec).toFixed(2)} fps`;
	}

	/**
	 * Sets the overall chart size. The initial size is (width-of-node)px X 0px.
	 * During resize, the size is set according to the following priority listed below.
	 * @param {Boolean} setInitialSize - Whether or not to set the initial size
	 */
	setChartSize(setInitialSize = false) {
		/**
		 * Chart sizing priority:
		 * 1. Panel heights
		 * 2. Chart size params
		 * 3. Parent node dimensions
		 * 4. Fallback dimensions
		 */

		// Get root node dimensions
		const rect = this.rootModchart.node().getBoundingClientRect();

		// Set initial chart size so that panel sizes can be properly calculated
		if (setInitialSize) {
			this.size = { height: 0 };

			if (this.params.size) {
				this.size.width = this.params.size.width;
			} else {
				this.size.width = rect.width;
			}
		} else {
			if (this.params.size) { // Use manually set chart height/width
				this.size = {
					height: this.params.size.height,
					width: this.params.size.width
				};
			} else if (
				this.panels.length &&
				typeof this.params.panelHeightUpper === 'number' ||
				typeof this.params.panelHeightLower === 'number'
			) {
				const sumHeight = this.panels.reduce((total, panel) => total + panel.size.height + panel.size.margin.top + panel.size.margin.bottom + panel.size.padding.top + panel.size.padding.bottom, 0);
				this.size = {
					height: sumHeight,
					width: rect.width
				};
			} else if (rect.height > 0 && rect.width > 0) { // Use root node dimensions to size the chart
				this.size = {
					height: rect.height,
					width: rect.width
				};
			} else { // Warn user that no height could be found, use fallback height
				this.warn('No dimensions were specified for the chart or chart container. Using fallback dimensions.');
				this.size = {
					height: 200,
					width: rect.width
				};
			}
		}
	}

	/** Create DOM elements and some bindings */
	initDOM() {
		// Apply required styles
		this.rootModchart.style('position', 'relative');

		// Make sure chart has required classes
		this.rootModchart.classed('modcharts-root modcharts-noselect', true);

		this.setChartSize(true);

		// Add root messages element.  This is an empty element where content can be appended, like "Chart not available".
		this.rootMessages = this.rootModchart
			.append('div')
			.attr('class', 'modcharts-rootmessages');

		// This is a blanket element to track mouse/touch events over the entire chart and all its panels.
		this.rootMouse = this.rootModchart
			.append('div')
			.attr('class', 'modcharts-rootmouse')
			.attr('pointer-events', 'all')
			.style('width', `${this.size.width}px`)
			.style('height', `${this.size.height}px`)
			.style('position', 'absolute')
			.style('background-color', 'rgba(0, 0, 0, 0)');

		// Add resize handler
		this._resizeHandler = () => { this.resizeHandler(); };
		window.addEventListener('resize', this._resizeHandler);

		// Check for optional live debugging flag
		this.updateDebugReference();

		// Initialize zoom
		this.zoom = zoomUtil.createZoom(this);
		this.zoomUtil = zoomUtil;
	}

	/** handle resizing of the window */
	resizeHandler() {
		// an immediate resize/render, followed by delayed resize/render, seems
		// to work more reliably cross-device than a single event.
		this.resize();
		this.renderQueue();

		window.clearTimeout(this.resizeTimeout);

		this.resizeTimeout = window.setTimeout(() => {
			this.resize();
			this.renderQueue();
		}, 15);
		this.eventEmitter.emit(types.WINDOW_RESIZE);
	}

	/**
	 * tear down js and dom in preparation for the chart instance to be removed
	 * @method destroy
	 */
	destroy() {
		// cancel any active xhr
		if (this.xhrCancelSource) this.xhrCancelSource.cancel();
		this.dataXHR = null;

		// remove panel dom and js
		this.removePanels();

		// null the bigger stored objects
		this.state = null;
		this.params = null;
		this.timeService = null;
		this.exchangeDates = null;
		this.data = null;
		this.dataPrimary = null;

		// remove root elements and unregister event handlers
		this.rootMouse.remove();
		this.rootMessages.remove();

		// unregister resize handler
		window.removeEventListener('resize', this._resizeHandler);

		// unregister any emitter listeners
		this.eventEmitter.removeAllListeners();

		// empty the root element contents
		if (this.rootModchart.node()) this.rootModchart.node().innerHTML = '';
	}

	/** Track temporary app states */
	getChartState() {
		return {
			/**
			 * optional X-MOD-ACCESS-TOKEN header to be added to ajax request headers.  use setAccessToken() to set.
			 * used when accessing an ApiAuthModule-based data API directly, bypassing the APIMAN gateway.
			 */
			accessToken: null,

			/**
			 * optional OAuth bearer token to be added to ajax request headers.  use setAuthToken() to set.
			 * used when accessing a data API through the APIMAN gateway.
			 */
			authToken: null,

			/** number of times data API has been loaded */
			dataLoadCount: 0,

			/** in debug mode (trigged by ?..modchartsdebug..=on or #..modchartsdebug..=on) */
			debug: null,

			/** Whether or not normalized point domain adjustment occurred */
			didNormalizedPointDomainAdjustment: false,

			/** the currently-dragged tool before mouseup */
			dragTool: null,

			/** in the middle of hovering over a panel resize bar */
			hoverResizePanel: null,

			/** Used to track initial domain adjustment that takes places before the normalized point domain adjustment */
			initialDomainAdjustment: [0, 0],

			/** Initial view zoom transform, to be used when resetting the chart to the initial view */
			initialTransform: null,

			/** A count of consecutive invalid token requests */
			invalidTokenCount: 0,

			/** Flag for if chart is intraday */
			isIntraday: false,

			/** Save the present primary frame data to be used in calculating domain transforms */
			presentDataPrimary: null,

			/** the panel currently being resized (the one below the currently-active resize bar) */
			resizePanel: null,

			/** current y value of mouse during panel resizing */
			resizePanelY: null,

			/** optional session manager id.  used for streamserver requests. */
			sessionId: null,

			/** Show the crosshair at the most recent position after chart load */
			showCrosshairAfterLoad: true,

			/**
			 * if we're about to start drawing a tool or in the middle of drawing it, this will contain the tool's type (eg. "line")
			 * note - this does not affect the selected state of the tool.
			 */
			toolmode: null,

			/** the currently-active tool object */
			tool: null,

			/** Store for intraday session information */
			typicalSessions: null,

			/**
			 * Flag to proceed with updating present scale domain. This should take place when
			 * exchangeDates gets updated because the exchangeDates indices will change.
			 */
			updateXAxisPresentScaleDomain: false,

			/** Flag to use the dataPrimary collection when updating the xAxis present scale domain */
			useDataPrimaryPresentScaleUpdate: false,

			/** Save the present xAxis scale to be used in calculating domain transforms */
			xAxisPresentScale: null,

			/** in the middle of a zoom event */
			zooming: null
		};
	}

	/** Adds modcharts reference to window context for live debugging */
	updateDebugReference() {
		let isDebug = false;

		// Check query string for debug flag
		if (this.getParameterByName('..modchartsdebug..') === 'on') {
			isDebug = true;
		} else if (document.location.hash && document.location.hash.length) {
			// check hash values for debug flag
			const hash = window.location.hash.substring(1).split('=');

			if (
				hash.length === 2 &&
				hash[0] === '..modchartsdebug..' &&
				hash[1] === 'on'
			) {
				isDebug = true;
			}
		}

		if (isDebug) {
			if (!window.ModchartsDebug) window.ModchartsDebug = [];

			window.ModchartsDebug.push(this);

			this.state.debug = true;
		}
	}

	/** timeFormat with locale rules */
	timeFormat(specifier) {
		if (!this.state.locale) this.state.locale = formatLocale(this.locale);
		if (!this.state.timeFormatDefaultLocale) {
			this.state.timeFormatDefaultLocale = timeFormatDefaultLocale(this.locale);
		}

		return this.state.timeFormatDefaultLocale.format(specifier);
	}

	/** Return position information for chart elements */
	getChartDimensions() {
		const output = { panels: [] };

		this.panels.forEach(panel => {
			output.panels.push({
				top: panel.size.top + panel.size.padding.top,
				left: panel.size.padding.left,
				height: panel.size.height,
				width: panel.size.width
			});
		});

		return output;
	}

	/**
	 * return ratio between devicepixelratio and backingstoreratio
	 * http://www.html5rocks.com/en/tutorials/canvas/hidpi/
	 */
	getScreenRatio() {
		if (!this.panels.length) {
			return 1;
		}

		const rootContext = this.panels[0].rootContext;
		const devicePixelRatio = window.devicePixelRatio || 1;
		const backingStoreRatio =
				rootContext.webkitBackingStorePixelRatio ||
				rootContext.mozBackingStorePixelRatio ||
				rootContext.msBackingStorePixelRatio ||
				rootContext.oBackingStorePixelRatio ||
				rootContext.backingStorePixelRatio ||
				1;

		return devicePixelRatio / backingStoreRatio;
	}

	/**
	 * cross-browser fullscreen
	 * @method toggleFullScreen
	 */
	toggleFullScreen() {
		const element = document.body;

		// Supports most browsers and their versions.
		const requestMethod =
			element.requestFullScreen ||
			element.webkitRequestFullScreen ||
			element.mozRequestFullScreen ||
			element.msRequestFullscreen;

		if (requestMethod) {
			// Native full screen.
			requestMethod.call(element);
		}
	}

	/** Send warning to browser console */
	warn(msg) {
		if (!this.params.disableLogMessages) console.warn(msg);
	}

	/**
	 * return panel currently under mouse
	 * @method getActivePanel
	 * @param {Number[]} mouse - Mouse position information [x, y]
	 */
	getActivePanel(mouse) {
		let activePanel = null;

		for (const panel of this.panels) {
			if (
				!activePanel &&
				panel.isWithin(mouse)
			) {
				activePanel = panel;
				break;
			}
		}

		return activePanel;
	}

	/** get primary indicator */
	getPrimaryIndicator() {
		return this.primaryIndicator;
	}

	get primaryIndicator() {
		return this.panels.length && this.panels[0].indicators.length
			? this.panels[0].indicators[0]
			: null;
	}

	/**
	 * Sets the markerType of the primary price indicator
	 * @param {priceMarkerTypes} type - The marker type for the price indicator
	 */
	setPriceMarkerType(type) {
		this.primaryMarkerType = type;
	}
	set primaryMarkerType(type) {
		this.primaryIndicator.markerType = type;
	}

	get primaryMarkerType() {
		return this.primaryIndicator.markerType;
	}

	set primaryOhlcType(type) {
		this.primaryIndicator.ohlcType = type;
	}

	/** Set all panel domains to the first panel's domain */
	normalizePanelDomains() {
		const firstPanel = this.panels[0];
		const firstDomain = firstPanel.xAxis.primaryScale.domain();

		this.panels.forEach(panel => {
			if (panel !== firstPanel) panel.xAxis.primaryScale.domain(firstDomain);
		});
	}

	/** snap date to the nearest following interval-adjusted date */
	getNextIntervalMinute(date) {
		while (date.getMinutes() % this.params.dataInterval !== 0) {
			date.setMinutes(date.getMinutes() + 1);
		}

		return date;
	}

	/**
	 * receive a datapoint from external streamserver.  this will either modify the last chart
	 * datapoint or create a new one.
	 *
	 * the input datapoint object should contain these properties:
		{
			"date": 1404230760000,
			"last": 26.4,
			"symbol": "36276"
		}
	* optionally high and low and open can also be passed.
	* open will only be used when creating new datapoints.
		{
			"date": 1404230760000,
			"last": 26.4,
			"high": 26.72,
			"low": 24.23,
			"open": 24.23,
			"symbol": "36276"
		}
	* @method onStreamTrade
	*/
	onStreamTrade(datapoint) {
		// null checks
		if (
			!this.data ||
			!this.data[this.params.symbol] ||
			!datapoint ||
			!datapoint.date ||
			!datapoint.last ||
			!datapoint.symbol ||
			!this.data[datapoint.symbol]
		) {
			// console.error('received invalid datapoint', datapoint);
			return;
		}

		// ignore if streaming is currently suppressed
		if (this.isPollStreamSuppressed()) {
			// console.error('streaming is suppressed');
			return;
		}

		const localDate = new Date(datapoint.date);
		const panelNormalized = this.panels[0].isNormalized;
		let newDate;

		// get adjusted date
		if (!this.state.isIntraday) {
			newDate = new Date(localDate);
			newDate.setHours(0);
			newDate.setMinutes(0);
			newDate.setSeconds(0);
		} else {
			// adjust for local tz offset
			localDate.setMinutes(
				localDate.getMinutes() + localDate.getTimezoneOffset()
			);

			newDate = new Date(
				this.exchangeDates[this.closestExchangeIndex(localDate)]
			);

			if (this.params.dataInterval > 1) {
				newDate = this.getNextIntervalMinute(newDate);
			}
		}

		let newClose = Number(datapoint.last);
		const openDate = this.getExchangeOpenDate();
		const datasetOHLC = this.getFirstDataset(this.data[datapoint.symbol].price);
		const prevOHLC = datasetOHLC[datasetOHLC.length - 1];
		const prevDate = prevOHLC.date;
		const diffSec = Number(((newDate - prevDate) / 1000).toFixed(2));
		const period = this.params.dataPeriod;

		// if panel is normalized, convert value
		if (panelNormalized) {
			const symbolToFind = datapoint.symbol.toString();
			const normalizeStart = Object.values(this.state.normalizeValues.indicators).find(indicator => indicator.Symbol === symbolToFind);
			if (normalizeStart?.Close) {
				newClose = this.getNormalizedFromActual(newClose, normalizeStart.Close);
			}
		}

		if (newDate >= prevDate) {
			if (openDate && newDate - openDate < 1000) {
				// skip datapoint if < 1 second from market open - since streamserver rounds to the nearest
				// second these events will appear exactly on market open, which will overlap the last bar of
				// the previous day if allowed to render.
				if (this.state.debug) {
					console.info('SKIP, mkt open');
				}
			} else if (newDate.getDate() !== prevDate.getDate()) {
				// prevent erroneous snapping to ruler point on the following trading day, which can happen
				// at market close
				if (this.state.debug) {
					console.info('SKIP, late', newDate.getDate(), prevDate.getDate());
				}
			} else if (
				(period === 'Minute' &&
					diffSec < 60 * this.params.dataInterval) ||
				(period === 'Day' && diffSec < 86400) ||
				(period === 'Hour' && diffSec < 3600) ||
				(period === 'Week' && diffSec < 604800) ||
				(period === 'Month' && diffSec < 2678400)
			) {
				// if within current interval, merge new point into our last existing point.
				// note we ignore open here, since that's the open from a different, smaller rollup
				// (eg. a rollup of the last 250ms vs the fixed 1-minute buckets the chart uses)
				if (datapoint.high && datapoint.low) {
					// support high and low if available
					prevOHLC.close = newClose;
					prevOHLC.high = Math.max(
						prevOHLC.high,
						Number(datapoint.high)
					);
					prevOHLC.low = Math.min(
						prevOHLC.low,
						Number(datapoint.low)
					);
				} else {
					// otherwise roll up high and low manually
					prevOHLC.close = newClose;
					prevOHLC.high = Math.max(prevOHLC.high, newClose);
					prevOHLC.low = Math.min(prevOHLC.low, newClose);
				}

				// render the update
				this.renderQueue();

				if (this.state.debug) {
					console.info(`REPLACE, last=${newClose}`);
				}
			} else {
				// date is beyond the interval of the last datapoint, so create a brand new point
				datasetOHLC.push({
					date: newDate,
					open: Number(datapoint.open || newClose),
					close: newClose,
					high: Number(datapoint.high || newClose),
					low: Number(datapoint.low || newClose)
				});

				// render the update
				this.renderQueue();

				if (this.state.debug) {
					console.info(`APPEND, diff sec=${diffSec}`);
				}
			}
		} else {
			// skip when streamed date is earlier than last known dataset date, this can happen at
			// the beginning of a stream due to timestamp differences between streamserver and quoteserver
			if (this.state.debug) {
				console.info('SKIP, early');
			}
		}

		if (this.state.debug) {
			console.log('   stream date', localDate);
			console.log('prev ohlc date', prevDate);
			console.log(' mkt open date', openDate);
			console.log('      new date', newDate);
			console.log('      diff sec', diffSec);
			console.log('');
		}
	}

	/**
	 * get market open date for the current day in local exchange time
	 */
	getExchangeOpenDate() {
		const d = new Date();
		const openDate = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);

		try {
			const typical = this.timeService.typicalSessions[d.getDay()];
			const exchangeId = this.xref[this.params.symbol].exchangeId;

			openDate.setMinutes(typical.rulerSessions[exchangeId].open);
		} catch (e) {
			return null;
		}

		return openDate;
	}

	/**
	 * @method startTestStream
	 */
	startTestStream(dateStart, ms) {
		if (!dateStart) {
			dateStart = new Date();
			dateStart.setDate(dateStart.getDate());
			dateStart.setHours(13 - dateStart.getTimezoneOffset() / 60);
			dateStart.setMinutes(30);
			dateStart.setSeconds(0);
			dateStart.setMilliseconds(250);
		}

		let lastTrade = this.dataPrimary[this.dataPrimary.length - 1].close;
		let tradeCount = 0;
		const speed = 4;

		this.stopTestStream();

		this.testStreamInterval = window.setInterval(() => {
			lastTrade = lastTrade * (Math.random() > 0.5 ? 1.0001 : 0.9999);

			this.onStreamTrade({
				date: new Date(dateStart.setMilliseconds(speed * tradeCount++)),
				last: lastTrade
			});
		}, ms || 1000);
	}

	/**
	 * @method stopTestStream
	 */
	stopTestStream() {
		if (this.testStreamInterval) {
			window.clearInterval(this.testStreamInterval);
		}
	}

	/**
	 * Empty out the primary domain. This is usually done ahead of a data request that will require
	 * an entirely new timeframe or symbol with different trading days. The domain will then be
	 * reconstructed against the new data in getDefaultDomain.
	 * @param {boolean} updateXAxisPresentScaleDomain - Flag to update the xAxis present scale domain
	 * @param {boolean} useDataPrimaryPresentScaleUpdate - Flag to use the present dataPrimary collection for the present scale update
	 */
	resetDomain(updateXAxisPresentScaleDomain = true, useDataPrimaryPresentScaleUpdate = false) {
		if (this.panels.length) {
			this.panels[0].xAxis.primaryScale.domain([0, 0]);
			this.state.updateXAxisPresentScaleDomain = updateXAxisPresentScaleDomain;
			this.state.useDataPrimaryPresentScaleUpdate = useDataPrimaryPresentScaleUpdate;
		}
	}

	/**
	 * @method setLocale
	 * @param {Object} locale - The new locale object to be merged with the default locale.
	 *
	 * This method sets the locale for the application by merging the provided locale object
	 * with the default English (US) locale. It then updates the internal state and parameters
	 * with the new locale, and regenerates the labels for the panel axes if they exist.
	 */
	setLocale(locale) {
		const newLocale = merge({}, LocaleEnUs, locale);
		this.locale = newLocale;
		this.params.locale = newLocale;
		this.state.locale = null;
		this.state.timeFormatDefaultLocale = null;

		if (this.panels?.length) {
			// regenerate axis labels
			this.panels[0].xAxis.labeler._createLabels();
			// regenerate legends
			this.panels.forEach(panel => {
				panel._updateTranslations();
				panel.updateLegend(); // pick up any new translations
			});
		}
	}

	/**
	 * @method setAuthToken
	 * sets an authorization token to be added to ajax request headers
	 * @param {string} token
	 */
	setAuthToken(token) {
		this.state.authToken = token;
	}

	/**
	 * @method setSessionId
	 * sets an optional sessionId, which can be used for streamserver requests
	 * @param {string} sessionId
	 */
	setSessionId(sessionId) {
		this.state.sessionId = sessionId;
	}

	/**
	 * @method setAccessToken
	 * sets an authorization token to be added to ajax request headers
	 */
	setAccessToken(token) {
		this.state.accessToken = token;
	}

	/**
	 * Changes the mouse cursor to the type passed in by updating the chart classes
	 */
	setCursor(id) {
		if (this.cursor === id) return;

		this.cursor = id;
		if (!this.rootModchart.classed(`modcharts-cursor-${id}`)) {
			// remove old class
			Object.values(types$1).forEach(cursorType => {
				this.rootModchart.classed(`modcharts-cursor-${cursorType}`, false);
			});

			this.rootModchart.classed(`modcharts-cursor-${id}`, true);
		}
		this.eventEmitter.emit(types.CURSOR_CHANGE, { cursor: this.cursor });
	}

	/**
	 * (Deprecated CH-1504)
	 * Clear out old symbolCompare values and indicators and add new ones
	 * @param {Number[]} newSymbolCompare - Array of new comparison symbol XID's
	 */
	setSymbolCompare(newSymbolCompare, compareStyles = []) {
		// symbolCompare should be an array
		if (typeof newSymbolCompare !== 'object') {
			this.warn('Invalid comparison symbols');
			return;
		}

		const { symbolCompare } = this.params;

		// Change XID's to strings
		newSymbolCompare = newSymbolCompare.map(s => String(s));

		// Remove symbol from this.params.symbolCompare if not in new symbolCompare collection
		const removedSymbols = [];
		forEachRight(symbolCompare, (symbol, index) => {
			if (!newSymbolCompare.includes(symbol)) {
				const removedSymbol = symbolCompare.splice(index, 1)[0];
				removedSymbols.push(removedSymbol);

				// Also remove any price indicators associated with this symbol
				this.panels.forEach(panel => {
					for (const i of panel.indicators) {
						if (
							i.params.symbol &&
							i.params.symbol === removedSymbol
						) {
							panel.removeIndicator(i);
						}
					}
				});
			}
		});

		// Add new comparison symbols in to collection
		const newIndicators = [];
		newSymbolCompare.forEach((symbol, index) => {
			if (!symbolCompare.includes(symbol)) {
				symbolCompare.push(symbol);

				// Add new indicator to collection
				newIndicators.push({
					id: 'price', // Consider switching to ENUM value
					style: compareStyles[index] || {},
					symbol
				});
				this.eventEmitter.emit(types.COMPARISON_ADD, { issueId: symbol });
			}
		});

		// Add new indicators if upper panel exists
		if (this.panels[0])	this.panels[0].addIndicators(newIndicators);

		if (removedSymbols.length || newIndicators.length) {
			// Reset the domain since the new request can have different exchangeDates and will make the old domain invalid.
			// Update the domain for the present xAxis scale since the old exchangeDates indicies are no longer valid (CH-1719)
			this.resetDomain(true, true);
		}
	}

	/**
	 * Setter for the dateStart/dateStop chart params. Input dates should be parseable as JS dates.
	 * @param {Date} dateStart - Starting date of range
	 * @param {Date} dateStop - Ending date of range
	 */
	setDateRange(dateStart, dateStop) {
		// Transform arguments into JS dates
		dateStart = new Date(dateStart);
		dateStop = new Date(dateStop);

		// Enforce that start is always earlier than stop
		let minDate = new Date(Math.min(dateStart, dateStop));
		let maxDate = new Date(Math.max(dateStart, dateStop));

		const dateLimit = new Date().setDate(
			new Date().getDate() - this.params.rulerInterdayStart
		);

		minDate = new Date(Math.max(minDate, dateLimit));
		maxDate = new Date(Math.min(maxDate, new Date()));

		// adjust for local tz offset
		minDate.setMinutes(minDate.getMinutes() + minDate.getTimezoneOffset());
		maxDate.setMinutes(maxDate.getMinutes() + maxDate.getTimezoneOffset());

		// custom intraday ranges should extend to midnight of max day (NXC-572)
		const limitIntradayCalendarMs = this.limitIntradayCalendar * 1000 * 60 * 60 * 24;
		if (maxDate.getTime() - minDate.getTime() <= limitIntradayCalendarMs) {
			maxDate.setHours(0, 0, 0, 0);
			maxDate.setDate(maxDate.getDate() + 1);
		}

		// Track if crosshair should be shown after load (if proper param is set)
		this.state.showCrosshairAfterLoad = true;

		// Update time span tracking
		this.params.dateStart = minDate;
		this.params.dateStop = maxDate;
		this.params.days = null;

		// Reset domain - will be repopulated next time getDefaultDomain runs
		this.resetDomain();

		// Clear the initial transform since a custom date range has been set and it will need to be redefined
		this.state.initialTransform = null;
	}

	/**
	 * Update the number of days to be used when requesting data
	 * @param {number} days - The number of days to be used
	 */
	setDays(days) {	this.days = days; }
	set days(days) {
		// Track if crosshair should be shown after load (if proper param is set)
		if (this.params.days !== days) this.state.showCrosshairAfterLoad = true;

		this.resetDomain(false); // Don't update the present xAxis scale

		// Update time span tracking
		this.params.dateStart = null;
		this.params.dateStop = null;
		this.params.days = days;

		this.eventEmitter.emit(types.TIME_SERIES_SET, {
			days: this.params.days,
			interval: this.params.dataInterval,
			period: this.params.dataPeriod,
			updatedProperty: 'days'
		});
	}

	/**
	 * Update the data interval to be used when requesting data
	 * @param {number} interval - The interval to be used
	 */
	setDataInterval(interval) {	this.dataInterval = interval; }
	set dataInterval(interval) {
		this.params.dataInterval = interval;
		this.eventEmitter.emit(types.TIME_SERIES_SET, {
			days: this.params.days,
			interval: this.params.dataInterval,
			period: this.params.dataPeriod,
			updatedProperty: 'interval'
		});
	}

	/**
	 * Update the data period to be used when requesting data ('Minute', 'Hour', etc.)
	 * @param {string} period - The period to be used
	 */
	setDataPeriod(period) { this.dataPeriod = period; }
	set dataPeriod(period) {
		this.params.dataPeriod = period;
		this.eventEmitter.emit(types.TIME_SERIES_SET, {
			days: this.params.days,
			interval: this.params.dataInterval,
			period: this.params.dataPeriod,
			updatedProperty: 'period'
		});
	}

	/**
	 * set new primary symbol and reset price indicators params.symbol
	 * @param {string} symbol - The XID for the current selected symbol
	 */
	setSymbol(symbol) { this.symbol = symbol; }
	set symbol(symbol) {
		symbol = String(symbol || '');

		if (this.params.symbol === symbol || !this.panels.length) return;

		this.resetDomain();

		// Update the zoom center due to xAxis changing
		this.state.updateZoomCenter = true;

		let i;
		const ind = this.panels[0].indicators;

		for (i = ind.length - 1; i >= 0; i--) {
			if (ind[i].params.symbol === symbol) {
				// remove old comparison if it is of the new symbol
				this.panels[0].removeIndicator(ind[i]);
			} else if (ind[i].params.symbol === this.params.symbol) {
				// switch older primary symbol in applicable indicators to the new symbol
				ind[i].params.symbol = symbol;
			}
		}

		const index = this.params.symbolCompare.indexOf(symbol);

		if (index > -1) this.params.symbolCompare.splice(index, 1);

		this.eventEmitter.emit(types.SYMBOL_UPDATE, { new: symbol, old: this.params.symbol });
		this.params.symbol = symbol;
	}

	/**
	 * setter for params.extendedHours
	 */
	setExtendedHours(isEnabled) {
		if (typeof isEnabled === 'boolean') {
			this.params.extendedHours = isEnabled;
			this.resetDomain();
		}
	}

	/**
	 * setter for params.extendedHoursDurationPre/Post
	 */
	setExtendedHoursDuration(pre, post) {
		this.params.extendedHoursDurationPre = pre;
		this.params.extendedHoursDurationPost = post;
		this.resetDomain();
	}

	/**
	 * Returns style info for the given selector from the overall styleMap
	 * @param {string} selector - Selector to use when getting style (eg. 'panel.padding.right')
	 * @return {string|number} The style found in the styleMap
	 */
	getStyle(selector) {
		const val = get(this.styleMap, selector);
		if (val === undefined) {
			this.warn(`Style not found for selector: ${selector}`);
			return val;
		}
		return deepClone(val);
	}

	/**
	 * get all datasets for a given symbol and data type (price, sma etc.)
	 * one use of this is to grab the first price dataset for the primary chart symbol so that a legend can display OHLC info.
	 * @method getDataByType
	 * @returns {array}
	 */
	getDataByType(symbol, type, symbolData) {
		if (!this.data) return [];

		const indicators = [];

		if (!symbolData) {
			symbolData = this.data[symbol || this.params.symbol] || {};
		}

		const data = symbolData[type] || {};
		Object.values(data).forEach(val => {
			indicators.push(val);
		});

		return indicators;
	}

	/**
	 * add a new panel object to panels collection
	 * @param {object} params
	 * @method addPanel
	 * @return {Panel}
	 */
	addPanel(params = {}) {
		const panel = new Panel();

		this.panels.push(panel);

		panel.init(this, params);

		this.combineXAxes();

		if (!params.size || !params.size.heightPct) {
			this.updatePanelHeightPercents();
		}

		return panel;
	}

	/**
	 * remove panel object from panels collection
	 * @method removePanel
	 * @param {Panel} panel
	 */
	removePanel(panelToRemove) {
		if (panelToRemove && panelToRemove.remove) {
			for (const panel of this.panels) {
				if (panel === panelToRemove) {
					const { uid } = panel.params;
					panelToRemove.remove();
					this.panels = this.panels.filter(p => p.params.uid !== uid);
					break;
				}
			}

			this.combineXAxes();
			this.updatePanelHeightPercents();
		}
	}

	/**
 * Updates the height percentage (heightPct) for each panel in the chart. This function adjusts the relative height
 * of each panel based on the total number of panels and specific height percentage parameters set for upper and lower panels.
 *
 * There are two scenarios for setting the panel heights:
 * 1. Custom height percentages: If custom lower panel height percentages are specified and there are multiple panels,
 *    the function calculates the upper panel's height as the remaining space.
 * 2. Default height distribution: If no custom settings are provided, the upper panel is assigned double the height
 *    percentage of each lower panel.
 *
 * The function also enforces minimum height constraints to ensure that panels are not disproportionately small.
 */
	updatePanelHeightPercents() {
		let isUpper = false;
		const numPanels = this.panels.length;
		let lowerPct = 0.0;
		let upperPct = 0.0;

		if (this.params.panelHeightPctLower && numPanels > 1) {
			// Use custom lower panel height percentage
			lowerPct = this.params.panelHeightPctLower;
			upperPct = 1.0 - lowerPct * (this.panels.length - 1);

			// Enforce a minimum height percentage for the upper panel
			if (upperPct < this.params.panelHeightPctMinUpper) {
				upperPct = this.params.panelHeightPctMinUpper;
				lowerPct = (1.0 - upperPct) / (this.panels.length - 1);
			}

			// Set the height percentage for each panel
			this.panels.forEach(panel => {
				panel.size.heightPct = panel.isUpper ? upperPct : lowerPct;
			});
		} else {
			// Default case: adjust upper panel height relative to lower panels using upperToLowerRatio

			// Variable to adjust the ratio between upper and lower panel heights in the default case (higher == taller upper panel)
			const upperToLowerRatio = 4;

			lowerPct = 1.0 / (numPanels + upperToLowerRatio - 1);
			upperPct = lowerPct * upperToLowerRatio;

			this.panels.forEach(panel => {
				isUpper = panel.isUpper;

				if (isUpper && numPanels === 1) {
					panel.size.heightPct = 1;
					panel.params.size.heightPct = null;
					return false;
				}

				panel.size.heightPct = isUpper ? upperPct : lowerPct;
			});
		}
	}


	/**
	 * modify the padding of all panels so that only
	 * specified panels contains an x-axis
	 * @method combineXAxes
	 */
	combineXAxes() {
		this.panels.forEach(panel => {
			panel.size.padding.bottom = panel.xAxis.hasAxis()
				? panel.params.style.padding.bottom
				: 0;
		});
	}

	/**
	 * Remove all indicators by panel type (upper/lower)
	 */
	removeIndicatorsByPanelType(panelType) {
		if (panelType === 'lower') {
			this.removeLowerIndicators();
		} else if (panelType === 'upper') {
			this.removeUpperIndicators();
		}
	}

	/**
	 * Remove all indicators by id ("sma", "bollinger", etc.)
	 */
	removeIndicatorsById(indicatorId) {
		// remove matching lowers
		this.removeLowerIndicators({ indicatorId });

		// remove matching uppers
		this.removeUpperIndicators({ indicatorId });
	}

	/**
	 * Remove all upper indicators (except the primary price indicator)
	 */
	removeUpperIndicators(args = {}) {
		const startCompareLen = this.comparisons.length;
		// remove indicators from the first panel in reverse order
		[...this.panels[0].indicators]
			.reverse()
			.forEach(indicator => {
				if (indicator !== this.panels[0].indicators[0]) {
					if (
						!args.indicatorId ||
						args.indicatorId === indicator.params.id
					) {
						this.panels[0].onClickRemoveLegendIndicator(indicator, {
							noLoad: true
						});
					}
				}
			});

		if (this.comparisons.length !== startCompareLen) {
			// reconstruct axis via new API call instead of just re-rendering. this is sometimes needed
			// if we've removed comparison symbols on exchanges with different sessions.
			this.resize();
			this.loadData();
		} else {
			// just re-render if no comparisons were removed
			this.render();
		}
	}

	/**
	 * Remove all lower indicators
	 */
	removeLowerIndicators(args = {}) {
		// remove lower panels in reverse order
		[...this.panels]
			.reverse()
			.forEach(panel => {
				if (panel.index > 0) {
					// remove indicators from this panel in reverse order
					panel.indicators
						.slice()
						.reverse()
						.forEach(indicator => {
							if (
								!args.indicatorId ||
								args.indicatorId === indicator.params.id
							) {
								panel.onClickRemoveLegendIndicator(indicator);
							}
						});
				}
			});

		// recalculate panel heights and re-render
		this.updatePanelHeightPercents();
		this.resize();
		this.render();
	}

	/** Set loading state to true. Adds class to root chart element. */
	loadingStart() {
		this.clearMessage();
		this.rootModchart.classed('modcharts-root-loading', true);
		this.eventEmitter.emit(types.DATA_LOAD_START);
	}

	/** Set loading state to false. Removes class from root chart element. */
	loadingStop() {
		if (this.rootTools) this.rootTools.show();
		this.rootModchart.classed('modcharts-root-loading', false);
		this.eventEmitter.emit(types.DATA_LOAD_STOP);
	}

	/**
	 * Get specified value from URL query string
	 * @param {string} name - Name of variable to search for
	 * @param {string} search - Query string to search through
	 * @return {boolean|string} Value of variable from query string or false
	 */
	getParameterByName(name, search = window.location.search) {
		const queryParams = new URLSearchParams(search);
		return queryParams.has(name) && queryParams.get(name);
	}

	/**
	 * Return a collection of what we deem "comparison indicators" (CH-1504, CH-3897)
	 */
	get comparisons() {
		return this.panels[0].indicators.filter((i, idx) => {
			return idx > 0 &&
				i.params.id === 'price' &&
				i.params.symbol &&
				String(i.params.symbol) !== String(this.params.symbol); // edge case where comparison symbol is a number
		});
	}

	/** Recalculate panel dimensions and reset the rootMouse element */
	resize() {
		// Set the chart size here to catch if switching from panel computed heights
		this.setChartSize();

		this.combineXAxes();

		this.panels.forEach(panel => panel.resize());

		// Set the chart size here to reflect new panel sizes
		this.setChartSize();

		// DOM update - rootMouse and rootMessages
		const domWidth = this.size.width;
		const domHeight = this.size.height;
		this.rootMouse.style('width', `${domWidth}px`);
		this.rootMouse.style('height', `${domHeight}px`);
		this.rootMessages.style('width', `${domWidth}px`);
		// Set the height of the rootMessages element to the height of the first panel
		if (this.panels.length) this.rootMessages.style('height', `${this.panels[0].size.height}px`);

		// DOM update - rootModchart (only needed if panel heights are manually set)
		if (
			typeof this.params.panelHeightUpper === 'number' ||
			typeof this.params.panelHeightLower === 'number'
		) {
			this.rootModchart.style('height', `${this.size.height}px`);
		}

		this.eventEmitter.emit(types.RESIZE);
	}

	/**
	 * filter main dataset by current timerange
	 * update all panel scales against primary scale
	 * render panels
	 */
	render(args = {}) {

		if (!this.panels.length) return;
		if (!this.dataPrimary) return;
		if (!this.exchangeDates || !this.exchangeDates.length) return;

		// update panels x domain / ranges
		this.normalizePanelDomains();

		// update panels y domain / range
		this.panels.forEach(panel => panel.configureAxes());

		// generate subset of labels to draw
		const filteredLabels = this.panels[0].xAxis.labeler.filterLabels();

		const eventEmitterData = { domain: this.panels[0].xAxis.primaryScale.domain() };

		// Check if dataFrame should be updated
		const isCustomData = !!this.params.customData;
		const data = isCustomData ? this._getCustomFrameData() : this._getFrameData();

		if (
			(data[this.params.symbol]?.price) ||
			data[this.primaryIndicator.params.datasetId]
		) {
			// Store off data subset for quick lookups later (crosshair and crosshair tool use this)
			const firstDataset = isCustomData
				? data[this.primaryIndicator.params.datasetId]
				: this.getFirstDataset(data[this.params.symbol].price);
			this.dataFrame = firstDataset;
			this.eventEmitter.emit(types.DATA_FRAME_UPDATE);

			// Simulate mouse moving at the right edge of the chart so that the crosshair will appear after chart load
			if (
				this.panels.length &&
				this.params.showCrosshairAfterLoad &&
				this.state.showCrosshairAfterLoad
			) {
				this.state.showCrosshairAfterLoad = false;

				this.eventEmitter.emit(types.PANEL_MOUSE_MOVE, {
					mouse: [
						this.panels[0].size.width - 1, // Right edge of upper panel
						this.panels[0].size.height / 2 // Middle of upper panel
					],
					activePanel: this.panels[0]
				});
			}

			// Add primary data from chart frame to event emitter
			eventEmitterData.dataFrame = firstDataset;
		}

		// empty out panels in preparation for new content
		this.clearPanels();

		// Render panels
		this.panels.forEach(panel => panel.render(data, filteredLabels, args.clipWidth));

		this.eventEmitter.emit(types.RENDER, eventEmitterData);

		if (this.eventEmitter.listeners(types.DATA_PRESENT).length > 0) {
			this.eventEmitter.emit(types.DATA_PRESENT, this.getPresentData());
		}
	}

	/**
	 * Collect coordinate information about every indicator's final point
	 * @returns Array of objects in this format:
	 * [{
	 * 	id: <string>,
	 * 	panelIndex: 0,
	 * 	date: <date>,
	 * 	coords: <double[]>,
	 * 	params: <object>,
	 * 	value: <number>
	 * },...]
	 *
	 */
	getPresentData() {
		const output = [];
		this.panels.forEach((panel, panelIndex) => {
			panel.indicators.forEach(ind => {
				const symbol = ind.params.symbol || this.params.symbol;
				const symbolData = this.data[symbol];
				const out = {};
				if (symbolData && symbolData[ind.params.id]) {
					const data = this.getFirstDataset(symbolData[ind.params.id]); // this.data['205778'].volume
					if (data.length) {
						const last = data[data.length - 1];
						out.id = ind.params.id;
						out.panelIndex = panelIndex;
						for (const seriesId in last) {
							if (
								!/dateIndex|hollow|diff/.test(seriesId) &&
								seriesId !== ind.params.id
							) {
								out[seriesId] = last[seriesId];
							}
						}
						if (last[ind.params.id] || last.close) {
							const scale = panel.yAxis.primaryScale;
							out.value = last[ind.params.id] || last.close;
							out.params = { ...ind.params };
							out.coords = [
								this.params.yAxisSide === 'right' ? panel.size.left + panel.size.width : panel.size.left,
								scale(out.value)
							];
						}
						output.push(out);
					}
				}
			});
		});
		return output;
	}

	/** Wrapping the render in a requestAnimationFrame */
	renderQueue() {
		if (window.requestAnimationFrame) {
			window.cancelAnimationFrame(this.renderAnimFrame);

			this.renderAnimFrame = window.requestAnimationFrame(() => this.render());
		} else {
			this.render();
		}
	}

	/** Call render repeatedly with an animated clip region */
	animate(duration, onComplete) {
		// cubic easing in/out (http://gizma.com/easing/)
		function easing(t, b, c, d) {
			t /= d / 2;
			if (t < 1) {
				return (c / 2) * t * t * t + b;
			}
			t -= 2;
			return (c / 2) * (t * t * t + 2) + b;
		}

		const renderFrame = () => {
			const diffTime = currTime - startTime;
			if (diffTime < duration) {
				this.render({
					clipWidth: easing(diffTime, 0, 1, duration)
				});

				currTime = new Date().getTime();

				if (window.requestAnimationFrame) {
					window.requestAnimationFrame(renderFrame);
				} else {
					renderFrame();
				}
			} else {
				// prevent errors when chart is destroyed before animation is complete
				try {
					this.render();
					this.state.isAnimating = false;

					// restore zoom/pan
					this.setZoomEnabled(zoomEnabled);

					if (onComplete)	onComplete();
				} catch (e) {
					console.warn('Animation cancelled');
				}
			}
		};

		const startTime = new Date().getTime();
		let currTime = startTime;
		const zoomEnabled = this.params.zoomEnabled;

		// set duration of animation
		duration = duration || this.params.animationDuration;

		this.state.isAnimating = true;

		// disable zoom/pan
		this.setZoomEnabled(false);

		// kick off animation
		window.requestAnimationFrame(renderFrame);
	}

	/** Deletes all panels from panels collection */
	removePanels() {
		this.panels.slice().reverse().forEach(panel => this.removePanel(panel));
	}

	/** Clears all panel canvases */
	clearPanels() {
		this.panels.forEach(panel => panel.clearPanel());
	}

	/**
	 * @method concealLegends
	 */
	concealLegends() {
		this.panels.forEach(panel => {
			panel.concealLegend();
		});
	}

	/**
	 * @method revealLegends
	 */
	revealLegends() {
		this.panels.forEach(panel => {
			panel.revealLegend();
		});
	}

	/**
	 * save all customized chart params to a portable JSON string
	 * @method save
	 * @return {string}
	 */
	async save(noStore = true, name = 'Saved Chart', args = {}) {
		const save = {
			params: { ...this.params },
			panels: [],
			metaData: {}
		};

		// Get JSON for each panels
		this.panels.forEach(panel => {
			const panelJSON = panel.toJSON();
			save.panels.push(panelJSON);
		});

		// Get chart meta data
		save.metaData = { ...this.metaData };

		// Decide if chart should be saved in cloud
		if (!noStore) {
			return this.saveData(save, name, args);
		}
		return Promise.resolve(save);
	}

	/** Make ajax call to ChartApi/Save endpoint with blob of JSON */
	async saveData(save, name = 'Saved Chart', args = {}) {
		const message = window.encodeURIComponent(JSON.stringify(save));
		const errMsg = "Can't complete saveData request";
		name = window.encodeURIComponent(name);
		const qsArgs = [
			`name=${name}`,
			`json=${message}`
		];
		// allow override of existing template/chart (CH-1890)
		if (args.id) qsArgs.push(`id=${args.id}`);

		try {
			const response = await axios.post(
				`${this.params.apiPath}ChartApi/Save`,
				qsArgs.join('&'),
				{
					headers: this._getXHRHeaders('application/x-www-form-urlencoded'),
					withCredentials: this.params.xhrWithCredentials ? true : undefined
				}
			);

			const result = this.parseDebugInfo(response.request.responseText);
			if (result.Id) {
				this.eventEmitter.emit(types.SAVE_COMPLETE, {
					id: result.Id,
					save,
					result,
					args
				});
				return {
					id: result.Id,
					save,
					result,
					args
				};
			}
			throw new Error(
				errMsg
			);
		} catch (error) {
			throw new Error(
				error.message ||
				errMsg
			);
		}
	}

	/**
	 * Makes an XHR call to ChartApi/SaveTemplate endpoint with blob of JSON
	 */
	async saveTemplateData(save, args = {}) {
		const name = window.encodeURIComponent(args.name || 'Saved Template');
		const message = window.encodeURIComponent(JSON.stringify(save));
		const errMsg = "Can't complete saveTemplateData request";
		const qsArgs = [
			`name=${name}`,
			`json=${message}`,
			`isDefault=${String(typeof args.isDefault === 'boolean' ? args.isDefault : false)}`
		];
		// allow override of existing template/chart (CH-1890)
		if (args.id) qsArgs.push(`id=${args.id}`);

		try {
			const response = await axios.post(
				`${this.params.apiPath}ChartApi/SaveTemplate`,
				qsArgs.join('&'),
				{
					headers: this._getXHRHeaders('application/x-www-form-urlencoded'),
					withCredentials: this.params.xhrWithCredentials ? true : undefined
				}
			);

			const result = this.parseDebugInfo(response.request.responseText);
			if (result.Id) {
				this.eventEmitter.emit(types.TEMPLATE_SAVE_COMPLETE, {
					id: result.Id,
					save,
					result,
					args
				});
				return {
					id: result.Id,
					save,
					result,
					args
				};
			}
			throw new Error(
				errMsg
			);
		} catch (error) {
			throw new Error(
				error.message ||
				errMsg
			);
		}
	}

	/**
	 * save chart template
	 */
	async saveTemplate(noStore = true, name = 'Saved Template', args = {}) {
		const saveData = await this.save();
		const skipIndicators = ['horizontalannotation']; // don't save these indicators
		let panelStyle = {};

		// only save a subset of params
		const templateParams = {
			crosshair: saveData.params.crosshair,
			dataInterval: saveData.params.dataInterval,
			dataPeriod: saveData.params.dataPeriod,
			extendedHours: saveData.params.extendedHours,
			hiddenLowers: saveData.params.hiddenLowers,
			panelHeightLower: saveData.params.panelHeightLower,
			panelHeightUpper: saveData.params.panelHeightUpper,
			panelXAxis: saveData.params.panelXAxis,
			showComponentSeriesFlags: saveData.params.showComponentSeriesFlags,
			showXAxisFlag: saveData.params.showXAxisFlag,
			symbolCompare: saveData.params.symbolCompare,
			yAxisScale: saveData.params.yAxisScale,
			zoomEnabled: saveData.params.zoomEnabled
		};

		// optional themeId
		if (saveData.params.themeId) templateParams.themeId = saveData.params.themeId;

		// save either date range or days
		if (saveData.params.dateStart && saveData.params.dateStop) {
			templateParams.dateStart = new Date(saveData.params.dateStart);
			templateParams.dateStop = new Date(saveData.params.dateStop);
		} else if (saveData.params.days) {
			templateParams.days = saveData.params.days;
		}

		saveData.params = {};

		// meta data
		saveData.metaData = { ...{}, ...this.metaData };

		// templateParams
		Object.keys(templateParams).forEach(paramKey => {
			const paramValue = templateParams[paramKey];
			saveData.params[paramKey] = paramValue;
		});

		// panels
		saveData.panels.forEach(panel => {
			if (args.panelStyles === 'all') {
				// save ALL panel styles
				panelStyle = { ...{}, ...panel.params.style };
			} else {
				// only save specific panel styles
				const { padding } = panel.params.style.yAxis;
				panelStyle = {
					yAxis: { ...padding }
				};
			}

			panel.params.style = panelStyle;

			// set (only) primary symbol params to -1 so they will be dynamic when loadTemplate is called.
			// non-primary symbols will be retained as comparisons. (see NXC-566)
			for (let x = panel.indicators.length - 1; x >= 0; x--) {
				// don't save certain indicators
				if (skipIndicators.includes(panel.indicators[x].id)) {
					panel.indicators.splice(x, 1);
					continue;
				}

				if (panel.indicators[x].symbol === this.params.symbol) {
					panel.indicators[x].symbol = -1;
				}
			}

			// don't save tools on templates
			delete panel.tools;
		});

		// Decide if template should be saved in cloud
		if (!noStore && typeof name === 'string') {
			args.name = name;
			return this.saveTemplateData(saveData, args);
		}
		return Promise.resolve(saveData);
	}

	/**
	 * Load a saved chart using json data retrieved from the ChartApi/Load endpoint
	 * @param {string} id - The chart ID to be retrieved
	 * @param {function} callback - Function to call after chart has loaded
	 * @param {boolean} loadChart - Should the saved chart get loaded
	 * @returns {object} Chart params for retrieved chart
	 */
	async loadSaved(id, callback, loadChart = true) {
		const errMsg = 'There was an error retrieving the saved chart';
		this.exchangeDates = [];

		try {
			const response = await axios.post(
				`${this.params.apiPath}chartapi/load/${id}`,
				{},
				{
					headers: this._getXHRHeaders(),
					withCredentials: this.params.xhrWithCredentials ? true : undefined
				}
			);
			let result = this.parseDebugInfo(response.request.responseText);

			// Add check to avoid unhandled rejection errors during testing
			if (typeof result === 'string') {
				// Clean up response and convert to object
				result = JSON.parse(
					result
						.replace(/&quot;/g, '"')
						.replace(/&#174;/g, '®')
						.replace(/\t/g, '    ')
				);
			}

			// Load chart
			if (loadChart) this.load(result, callback);

			return result;
		} catch (error) {
			throw new Error(
				error.message ||
				errMsg
			);
		}
	}

	/**
	 * Return a list of saved charts/templates/images from the chartapi/list endpoint
	 * @param {string} saveType - The chart type to retrieve (Modcharts/Modcharts-Template/Modcharts-Image)
	 * @returns {object[]} Array of saved items
	 */
	async listSaved(saveType = null) {
		const errMsg = 'There was an error retrieving the list of saved items';
		try {
			const response = await axios.post(
				`${this.params.apiPath}chartapi/list/`,
				{},
				{
					headers: this._getXHRHeaders(),
					withCredentials: this.params.xhrWithCredentials ? true : undefined
				}
			);
			const result = response.data;

			if (saveType && Array.isArray(result) && result.filter) {
				return result.filter(row => row.Category === saveType);
			}
			return result;

		} catch (error) {
			throw new Error(
				error.message ||
					errMsg
			);
		}
	}

	/**
	 * Return the current default template Id if one exists
	 * @returns {string} template Id
	 */
	async loadDefaultTemplateId() {
		try {
			const response = await axios.post(
				`${this.params.apiPath}chartapi/loaddefaulttemplateid`,
				{},
				{
					headers: this._getXHRHeaders(),
					withCredentials: this.params.xhrWithCredentials ? true : undefined
				}
			);
			return this.parseDebugInfo(response.request.responseText);
		} catch (error) {
			throw new Error(
				error.message ||
				'There was an error retrieving the default template id'
			);
		}
	}

	/**
	 * Return the current default template Id if one exists
	 * @param {string} id - The saved id
	 * @param {string} category - Modcharts / Modcharts-Template / Modcharts-Image
	 * @returns {string} status
	 */
	async deleteSaved(id = null, category = 'Modcharts') {
		if (id) {
			try {
				const response = await axios.post(
					`${this.params.apiPath}chartapi/delete?id=${id}&category=${category}`,
					{},
					{
						headers: this._getXHRHeaders(),
						withCredentials: this.params.xhrWithCredentials ? true : undefined
					}
				);
				return this.parseDebugInfo(response.request.responseText);
			} catch (error) {
				throw new Error(
					error.message ||
					'There was an error deleting the saved item'
				);
			}
		}
	}

	/**
	 * Deletes the current default template Id if one exists
	 * @returns {string} status
	 */
	async deleteDefaultTemplateId() {
		try {
			const response = await axios.post(
				`${this.params.apiPath}chartapi/deletedefaulttemplateid`,
				{},
				{
					headers: this._getXHRHeaders(),
					withCredentials: this.params.xhrWithCredentials ? true : undefined
				}
			);
			return this.parseDebugInfo(response.request.responseText);
		} catch (error) {
			throw new Error(
				error.message ||
				'There was an error deleting the default template id'
			);
		}
	}

	/**
	 * Return the current inception date
	 * @returns {object} The first trading date available for symbol
	 */
	async loadInceptionDate(xid = this.params.symbol) {
		try {
			const response = await axios.post(
				`${this.params.apiPath}chartapi/inceptiondate?symbol=${xid}`,
				{},
				{
					headers: this._getXHRHeaders(),
					withCredentials: this.params.xhrWithCredentials ? true : undefined
				}
			);
			const result = this.parseDebugInfo(response.request.responseText);
			const msDate = result.InceptionDate > 0 ? result.InceptionDate : null;
			const date = msDate ? this.msToJsDate(msDate) : null;
			return {
				symbol: result.Symbol,
				msDate,
				date
			};
		} catch (error) {
			throw new Error(
				error.message ||
				'There was an error retrieving the inception date'
			);
		}
	}

	/**
	 * return appropriate XHR request headers
	 * @return {object}
	 */
	_getXHRHeaders(contentType = 'application/json', authToken = null) {
		const bearerToken = authToken || this.state.authToken;
		const headers = {
			'Content-Type': contentType
		};
		if (bearerToken) {
			headers.Authorization = `Bearer ${bearerToken}`;
		} else if (this.state.accessToken) {
			headers['X-MOD-ACCESS-TOKEN'] = this.state.accessToken;
		}

		// Add optional headers from this.params.xhrRequestHeaders
		this.params.xhrRequestHeaders.forEach(row => {
			headers[row.name] = row.value;
		});

		return headers;
	}

	/**
	 * load a saved template using json data retrieved from the ChartApi/Template endpoint
	 */
	async template(id, args = {}) {
		await axios.post(
			`${this.params.apiPath}chartapi/template/${id}`,
			{},
			{
				headers: this._getXHRHeaders(),
				withCredentials: this.params.xhrWithCredentials ? true : undefined
			})
			.then(res => {
				const result = this.parseDebugInfo(res.request.responseText);

				if (!result || (!result.params && !result.ChartSettings)) {
					// response did not have expected values
					if (!this.eventEmitter.listeners('TEMPLATE_LOAD_ERROR').length) {
						this.showMessageChartNotAvailable({
							description: 'Invalid template response'
						});
					}
					this.eventEmitter.emit(types.TEMPLATE_LOAD_ERROR, { id, args, result });
				} else {
					this.applyTemplate(result, args);
					this.loadData();
				}
				this.eventEmitter.emit(types.TEMPLATE_LOAD, { id, result, args });
				return result;
			})
			.catch(error => {
				// catch any other error with XHR processing
				this.warn(`An error occurred.\n\n${error.message}`);
				if (!this.eventEmitter.listeners('TEMPLATE_LOAD_ERROR').length) {
					this.showMessageChartNotAvailable({
						description: 'Unknown error loading template',
						error
					});
				}
				this.eventEmitter.emit(types.TEMPLATE_LOAD_ERROR);
			});
	}

	/** apply template params against current chart prior to loading new data */
	applyTemplate(data = {}, args = {}) {
		// Recalculate present scale domain because saved params are being loaded
		this.state.updateXAxisPresentScaleDomain = true;

		let eventParams;

		// merge in the new params
		if (data.params && this.params) {
			merge(this.params, data.params);
		}

		// AC-91 remove comparisons that match primary symbol
		if (this.params.symbolCompare) {
			this.params.symbolCompare =
				this.params.symbolCompare.filter(symbol => symbol !== this.params.symbol);
		}

		// convert dateStart/Stop back to date object
		if (data.params.dateStart && data.params.dateStop) {
			this.params.dateStart = new Date(data.params.dateStart);
			this.params.dateStop = new Date(data.params.dateStop);

			// Clear the initial transform since a custom date range has been set and it will need to be redefined
			this.state.initialTransform = null;
		} else if (data.params.days) {
			// CW-387
			this.params.dateStart = null;
			this.params.dateStop = null;
		}

		// prepare for new panels
		this.removePanels();

		data.panels.forEach((savedPanel, savedPanelIdx) => {
			// if panelParams was passed in, merge it into new panel params.
			const panelParams = {
				...{},
				...savedPanel.params,
				...(args.panelParams || {})
			};
			const panel = this.addPanel(panelParams);

			// restore indicators
			savedPanel.indicators.forEach((row, idx) => {
				// symbols of -1 were previously the primary symbol - replace with the current primary symbol
				if (row.symbol === -1) {
					row.symbol = this.params.symbol;
				}

				// AC-91 don't add comparison price indicators that match primary symbol
				if (
					savedPanelIdx === 0 &&
					idx > 0 &&
					row.symbol === this.params.symbol
				) {
					return true;
				}

				// fix grandfathered templates with erroneous prototypeJS arrays
				if (typeof row.inputs === 'string') {
					row.inputs = JSON.parse(row.inputs);
				}
				if (typeof row.extraInputs === 'string') {
					row.extraInputs = JSON.parse(row.extraInputs);
				}

				panel.addIndicator(row.id, row);
			});

			// restore events
			for (let i = 0; i < (savedPanel.events || []).length; i++) {
				eventParams = savedPanel.events[i].params;
				panel.addEvent(
					savedPanel.events[i].id || eventParams.id,
					eventParams || {}
				);
			}
		});

		// scale
		if (this.panels.length && this.params.yAxisScale) {
			this.panels[0].setScale(this.params.yAxisScale);
		}

		// restore metadata
		if (data.metaData) {
			// fix grandfathered templates with erroneous prototypeJS arrays
			if (typeof data.metaData === 'string') {
				data.metaData = JSON.parse(data.metaData);
			}

			this.metaData = { ...{}, ...data.metaData };
		}

		this.eventEmitter.emit(types.TEMPLATE_APPLY, {
			data,
			args
		});
	}

	/**
	 * load a chart via JS or a saved JSON string
	 * @param {string|object} newParams
	 * @return {Modcharts}
	 */
	load(newParams, callback, args) {
		return this.chartLoader.load(newParams, callback, args);
	}

	/**
	 * Virtual Method for charts that accept tools
	 * @method getToolsFromJSON
	 */
	getToolsFromJSON() {
		return [];
	}

	/**
	 * Returns the number of trading days within calendar date range based on the exchangeDates ruler
	 * @param {Date} dateStart - Start date
	 * @param {Date} dateStop - Stop date
	 * @returns {number} The number of trading days
	 */
	getTradingDays(dateStart, dateStop) {
		const idxStart = this.closestExchangeIndex(dateStart);
		const idxStop = this.closestExchangeIndex(dateStop);
		let idx = idxStart;
		let days = this.state.isIntraday ? 0 : 1;
		let currDay = new Date(this.exchangeDates[idx]).getDate();

		while (idx <= idxStop) {
			const thisDay = new Date(this.exchangeDates[idx++]).getDate();

			if (currDay !== thisDay) {
				days++;
				currDay = thisDay;
			}
		}

		return Math.max(days, 1);
	}

	/** Returns the number of days in the year to date */
	getYTDDays() {
		const date = new Date();
		const jan = new Date(date.getFullYear(), 0, 1);

		return Math.ceil((date - jan) / 1000 / 60 / 60 / 24);
	}

	/**
	 * begin polling - pass pollMin of 0 to turn off poll
	 * @method pollData
	 */
	pollData(pollMin) {
		if (typeof pollMin === 'number') {
			this.params.poll = pollMin;
		}

		// clear any still-active setTimeouts
		if (this.state.pollTimeout) {
			window.clearTimeout(this.state.pollTimeout);
			this.state.pollTimeout = null;
		}

		// fire off new poll
		if (this.params.poll > 0) {
			this.state.pollTimeout = window.setTimeout(() => {
				// don't poll while suppressed - try again after next poll interval
				if (this.isPollStreamSuppressed()) {
					// short delay before retry
					window.setTimeout(() => {
						return this.pollData();
					}, 3000);
				} else {
					// load new data
					this.loadData(() => {
						// resume polling after data load completes
						this.pollData();

						// if an intraday chart is left up overnight we could potentially blow away the previous
						// days' data with the first poll on the new day.  so add a backfill check here.
						if (this.params.days < this.limitIntradayTrading) {
							this.backfillData();
						}
					},
					{ silent: true }
					);
				}
			}, this.params.poll * 60 * 1000);
		}
	}

	/**
	 * detect whether to suppress polling/streaming at this moment based on current chart state
	 */
	isPollStreamSuppressed(isStream) {
		// don't poll for data while in the middle of loading data, resizing panels, or drawing tools
		// also wait 1000ms after last zoom to make sure mousewheel is done firing

		if (this.state.zooming) {
			return true;
		} else if (this.dataXHR) {
			return true;
		} else if (
			typeof this.state.zoomDate === 'number' &&
			new Date() - this.state.zoomDate < 1000
		) {
			return true;
		} else if (this.state.toolmode && !isStream) {
			return true;
		} else if (this.state.resizePanel) {
			return true;
		} else if (this.state.isAnimating) {
			return true;
		}

		return false;
	}

	/**
	 * Make an XHR request for chart data and process it into a standardized data structure.
	 * typically this will be a request to <api domain>/ChartApi/Series but it can support other calls as well
	 * @param {Function} callback - Callback function passed to _processChartApiResponse
	 * @param {object} args - Arguments passed to _processChartApiResponse
	 */
	loadData(callback, args = {}) {
		// prevent infinite invalid token requests (CH-2872)
		if (this.state.invalidTokenCount > this.params.invalidTokenLimit) {
			return this.warn('Invalid authorization token count limit reached.  Please refresh the page.');
		}

		const path = this.params.chartAPI;
		const isYTD = this.params.days === -1;
		const authToken = this._getAuthToken();

		// Reset initial transform if loading new chart
		if (args && args.loadSaved) this.state.initialTransform = null;

		// Cancel ongoing XHR
		if (this.xhrCancelSource) this.xhrCancelSource.cancel();
		this.dataXHR = null;

		// stop the stream if active - if we make it past the upcoming error checks, then we'll update the stream symbols and start it again
		if (this.stream) this.stream.stop();

		// Enforce linear scale on normalized charts
		this._enforceLinearScale();

		// Show loading graphic and reset notable vars
		if (!args.silent) {
			this.loadingStart();

			if (!args.prepend) this._resetNormalizeDate();
		}

		// set days for YTD charts
		if (isYTD) this.params.days = this.getYTDDays();

		this.state.isIntraday = this._getIntradayStatus();

		// Build XHR
		this.dataXHR = this._buildXHR(authToken);

		// Send request to API
		this._sendXHR(
			this._setPathDebugParams(path),
			this._getChartAPIInputs(args),
			callback,
			args
		);

		if (isYTD) this.params.days = -1;

		return true;
	}

	/** Resets normalizeDate params (normalizeDate, normalizeValue, normalizeValues, exchangeDates) */
	_resetNormalizeDate() {
		this.params.normalizeDate = null;
		this.params.normalizeValue = null;
		this.params.normalizeValues = null;
		this.exchangeDates = [];
	}

	/**
	 * Determines if data should be intraday based on dateStart, dateStop, days and dataPrimary and limitIntradayMs
	 * @returns {boolean}
	 */
	_getIntradayStatus() {

		// CH-3204 reduce limitIntradayTrading to 21 days for non-FX charts
		// this is only possible after the first data load because we need to know the issueType
		if (
			this.dataPrimary?.length &&
			this.params.symbol &&
			this.xref[this.params.symbol]
		) {
			const issueType = this.xref[this.params.symbol].issueType;
			this.limitIntradayTrading = (issueType === 'FX') ? 31 : 21;
		}

		const limitIntradayTradingMs = this.limitIntradayTrading * 1000 * 60 * 60 * 24;
		const limitIntradayCalendarMs = this.limitIntradayCalendar * 1000 * 60 * 60 * 24;

		let isIntraday = false;
		if (this.params.dateStart && this.params.dateStop) {
			isIntraday = /Hour|Minute/.test(this.params.dataPeriod);
		} else if (this.params.days) {
			isIntraday =
				this.params.days * 1000 * 60 * 60 * 24 <= limitIntradayTradingMs &&
				/hour|minute/i.test(this.params.dataPeriod);
		} else if (this.dataPrimary && this.dataPrimary.length) {
			isIntraday =
				new Date() - this.dataPrimary[0].date <= limitIntradayCalendarMs &&
				this.dataPrimary[this.dataPrimary.length - 1].date -
					this.dataPrimary[0].date <=
					limitIntradayCalendarMs;
		}
		return isIntraday;
	}

	/** Ensures that primary panel is linear scale when the primary panel is normalized */
	_enforceLinearScale() {
		if (this.params.yAxisScale === 'log' &&
			this.panels.length &&
			this.panels[0].isNormalized) {
			this.panels[0].setScale('linear');
		}
	}

	/**
	 * Gets auth token based on state and params.apiAuthMode
	 * @returns {String} Auth Token
	 */
	_getAuthToken() {
		const shouldReturnToken =
			this.state.authToken &&
			!this.state.accessToken &&
			this.params.apiAuthMode !== 'querystring';

		return shouldReturnToken
			? this.state.authToken
			: null;
	}

	/**
	 * Modifies the API path with any debug parameters
	 * @method _setPathDebugParams
	 * @param {String} path
	 * @param {string} [search] - optional search parameter
	 */
	_setPathDebugParams(path, search) {
		const debugInfo = this.getParameterByName('..showdebuginfo..', search);
		const noCache = this.getParameterByName('..nocache..', search);
		const kvp = [];

		if (debugInfo && debugInfo.length) {
			kvp.push(`..showdebuginfo..=${debugInfo}`);
		}

		if (noCache && noCache.length) {
			kvp.push(`..nocache..=${noCache}`);
		}

		if (this.state.authToken && this.params.apiAuthMode === 'querystring') {
			kvp.push(`access_token=${this.state.authToken}`);
		}

		if (kvp.length) {
			const qsPrefix = /\?/.test(path) ? '&' : '?';
			path += qsPrefix + kvp.join('&');
		}
		return path;
	}

	/**
	 * Build the XHR object to make an API Request for new data
	 * @param {String} authToken - API authorization token
	 */
	_buildXHR(authToken) {
		// Initialize Axios cancel source for request cancellation; each request gets its own source
		this.xhrCancelSource = axios.CancelToken.source();

		return axios.create(
			{
				cancelToken: this.xhrCancelSource.token,
				headers: this._getXHRHeaders(this.params.apiContentType, authToken),
				withCredentials: this.params.xhrWithCredentials ? true : undefined,
				method: 'post'
			});
	}


	/**
 * Sends XHR with appropriate inputs
 * @param {String} path - URL for request
 * @param {Object} inputs - Data to be sent in request
 * @param {Function} callback - Optional callback function once request completes
 * @param {*} args - Collection of optional arguments
 */
	_sendXHR(path, inputs, callback, args = {}) {

		// Process a mock response for testing.  If mockResponse is passed in, skip the XHR request altogether.
		if (args.mockResponse) {
			this._processChartApiResponse(args.mockResponse, null, callback, args);
			this.dataXHR = null;
			return;
		}

		// Post the request
		this.dataXHR.request({
			url: path,
			data: inputs
		})
			.then(res => {
				// Handle successful response
				const result = this.parseDebugInfo(res.request.responseText);
				this._processChartApiResponse(result, null, callback, args);
			})
			.catch(error => {
				let errObject;

				if (axios.isCancel(error)) {
					// Don't throw an error on a purposeful request cancellation. Do nothing for now.
					this.dataXHR = null;
				} else {
					if (error.response) {
						// Response was received with a non 2XX status code
						errObject = {
							responseText: this.parseDebugInfo(error.request.responseText),
							status: error.request.status,
							statusText: error.request.statusText,
							responseType: error.request.responseType
						};
					} else if (error.request) {
						// The request was made but no response was received
						errObject = new Error('No response to request');
						errObject.name = 'XHR Error';
					} else {
						// Error when setting up the request
						console.error(error);
						errObject = new Error(`Incorrect request configuration: ${error}`);
						errObject.name = 'XHR Error';
					}
					this._processChartApiResponse({}, errObject, callback, args);
				}
			})
			.finally(() => {
				// The XHR object needs to be cleared as some functions will assume a request
				// is still ongoing if the request isn't cleared
				this.dataXHR = null;
			});
	}

	/**
	 * Split off the debuginfo part of response, add it to DOM so it fires the debug popup,
	 * then return just the json part of the response
	 * @param {String} text - Raw text of response, which may contain debug info
	 * @returns {JSON} JSON part of the response, without the debug info
	 */
	parseDebugInfo(text) {
		const rows = (text || '').split('\n');
		let json = {};

		if (rows.length && rows[0] === '{') {
			console.warn(JSON.stringify(rows));
			return;
		}

		try {
			// Remove JSON from response and save it for later
			json = JSON.parse(rows.shift());
		} catch (e) {
			console.warn(e);
		}

		// Only add debug code if it exists
		if (rows.length && document) {
			// Get debug container from DOM
			let debugContainer = document.getElementById('debuginfo-container');

			// If debug container doesn't exist, create it
			if (!debugContainer) {
				debugContainer = document.createElement('div');
				debugContainer.setAttribute('id', 'debuginfo-container');
				document.body.appendChild(debugContainer);
			}

			// See if first row contains opening/closing script tags for debug.js file
			const hasDebugScriptTag =
				/^<script/.test(rows[0]) &&
				/^.*<\/script>$/m.test(rows[0]);

			if (hasDebugScriptTag) {
				// Add debug script tag to document
				let debugScriptTag = rows.shift();

				// TODO: Is this still needed?
				// Switch to different debug.js location if we're a Node.js site
				if (
					typeof window.Debug !== 'undefined' &&
					/index\.html/.test(window.Debug.url || '')
				) {
					debugScriptTag = debugScriptTag.replace(
						'/includes/Jslib/WSDOM/Debug/debug.js',
						'/includes/showdebuginfo/debug.js'
					);
				}
				debugContainer.innerHTML = debugScriptTag;
			}

			// Find next script tag which contains our debug statements
			const indexNextScriptTag = rows.findIndex(row => /^<script/.test(row));

			let resDebugScript;

			// If next script tag found, get everything inside script tags and add it to an element
			if (indexNextScriptTag > -1) {
				resDebugScript = rows.slice(indexNextScriptTag + 1, -1).join('\n');
			} else {
				// Add all rows to new script tag
				resDebugScript = rows.join('\n');
			}

			// Create new element to store debug script from response, add script, add element
			const resDebugTag = document.createElement('script');
			resDebugTag.innerHTML = resDebugScript;

			try {
				// Add response debug script to DOM
				debugContainer.appendChild(resDebugTag);
			} catch (e) {
				this.warn(e.message);
			}
		}

		return json;
	}

	/**
	 * Similar to loadData except it doesn't do an ajax request as it already has data.
	 * Initialize some variables and kick off the _processCustomResponse call.
	 * @param {Function} callback - Callback function passed to _processCustomResponse
	 * @param {object} args - Arguments passed to _processCustomResponse
	 */
	loadCustomData(callback, args = {}) {
		// Reset initial transform if loading new chart
		if (args && args.loadSaved) this.state.initialTransform = null;

		// show loading graphic and reset notable vars

		this.loadingStart();

		this.exchangeDates = [];

		// determine state.isIntraday
		this.state.isIntraday = false; // currently hardcoded to false to prevent odd rendering issues with custom intraday data.

		return this._processCustomResponse(
			this.params.customData,
			null,
			callback,
			args
		);
	}

	/**
	 * Find the correct start/stop domain indices for the current intraday dataset.
	 * Here are a few scenarios:
	 * Scenario #1: 1-day/15-minute intraday chart, loaded at noon.
	 * First datapoint in the series is at 9:45, last datapoint is at 11:45.
	 * However the eventual goal is to present an XAxis from 9:30 to 16:00.
	 * _getIntradaySessionIndex is called twice, once to obtain the 9:30 domain index and once for the 16:00 index.
	 * It looks at the dateIn to determine the current day, and sets the time portion to midnight.
	 * Then offsets the minutes using a session value obtained from the timeService collection.
	 * For instance on a typical Monday for NSQ, the session will begin at 570 minutes past midnight and end at 960 minutes.
	 * Scenario #2: 5-day/5-minute intraday chart, loaded at noon on a Tuesday.
	 * First datapoint in the data series is at 9:35 last Wednesday.  Last point is 11:55 on this Tuesday.
	 * In this case we want the XAxis to begin at 9:30 last Wednesday and end at 16:00 on this Tuesday.
	 * @param {Date} dateIn A date object from a given point in the data collection
	 * @param {Number} offsetMinutes Session start (or end) time, offset from midnight (in minutes)
	 * @return {Number} The domain index that corresponds to the session offset for the dateIn input
	 * @private
	 */
	_getIntradaySessionIndex(dateIn, offsetMinutes) {
		let date = new Date(dateIn);
		const exchangeOffset = this.getExchangeOffsetInput();

		// adjust for correct session day (CW-653)
		if (exchangeOffset > 0 && this.xref[this.params.symbol]) {
			const utcOffset = this.xref[this.params.symbol].utcOffset;
			date = new Date(
				date.setMinutes(date.getMinutes() - utcOffset + exchangeOffset)
			);
		}

		// snap to the offsetMinutes passed (session open or session close)
		date.setHours(0, offsetMinutes, 0, 0);

		return this.closestExchangeIndex(date);
	}

	/**
	 * Calculates padding for domain given the current domain bounds and the padding value in pixels.
	 * This is useful in cases when the scale domain hasn't been set yet.
	 * @param {Number} min - Lower bound for current domain
	 * @param {Number} max - Upper bound for current domain
	 * @param {Number} padding - Padding value in pixels to add to the domain
	 * @returns {Number} The padding value in domain units
	 */
	_calcXAxisDomainPadding(min, max, padding) {
		return (max - min) * (padding / this.panels[0].size.width);
	}

	/**
	 * Determines the proper new domain to use (eg. where the XAxis begins and ends).
	 * By default the domain will stretch to the full dataset width.
	 * @param {*} primary - The data set to be used for calculating the domain
	 * @param {*} panelDomain - The existing domain to be used instead of calculating a new one
	 * @param {*} applyAdjustments - Flag to control if domain adjustments should be applied
	 * @returns
	 */
	getDefaultDomain(primary = this.dataPrimary, panelDomain = this.panels[0].xAxis.primaryScale.domain(), applyAdjustments = true) {
		let left = 0;
		let right = 0;
		const panel = this.panels[0];
		// ichimoku is different because some of its datasets plot into the future.
		const hasIchimoku =	panel.indicators.filter(i => i.params.id === 'ichimokucloud').length > 0;
		// make sure the domain is valid, and not just undefined or [0,0] (which is the reset state for a domain).
		const hasNonZeroDomain =
			panelDomain &&
			panelDomain[1] - panelDomain[0] > 0;
		const isIntraday = this.state.isIntraday && this.state.typicalSessions;
		// try to determine if the chart has been panned by the user.  if so, keep the domain as-is,
		// since you don't want it resetting on backfill requests.
		const hasPanned = hasNonZeroDomain &&
			this.dataPrimary &&
			this.dataPrimary.length > 0 &&
			(
				this.params.endOffsetDays ||
				(
					!hasIchimoku &&
					Math.abs(panelDomain[1] - this.closestExchangeIndex(this.dataPrimary[this.dataPrimary.length - 1].date)) > 2
				)
			);
		// (saved charts) if we found a saved dateStart and dateStop with no domain, try to use it
		// else use existing domain if neq [0,0]
		// else default to full range of loaded dataset
		if (
			panelDomain && // This is needed to make sure panelDomain was actually defined, not just non-zero
			!hasNonZeroDomain &&
			this.params.dateStart &&
			this.params.dateStop
		) {
			left = this.closestExchangeIndex(new Date(this.params.dateStart));
			right = this.closestExchangeIndex(new Date(this.params.dateStop));

			// same left/right domain
			if (right === left) {
				// valid domain (ex: [45, 45]), but the stop must at least be +1 start
				if (
					left !== 0 &&
					right !== 0 &&
					left < this.exchangeDates.length - 1
				) {
					right = left + 1;
				} else {
					// domain wasn't valid or [0,0]
					// fall back to full dataset range instead
					left = this.closestExchangeIndex(primary[0].date);
					right = this.closestExchangeIndex(
						primary[primary.length - 1].date
					);
				}
			}
		} else if (hasNonZeroDomain) {
			// domain is already set, so continue using that.
			left = Math.max(0, panelDomain[0]);
			right = Math.min(this.exchangeDates.length - 1, panelDomain[1]);
		} else if (isIntraday) {
			// intraday domain is not set, so calculate it.
			// begin at the session start time (eg 9:30) and end at the session stop time (eg 16:00)
			// if params.returnPreviousPoint is true, use the second index instead of the first.  if the first is used, it will choose the wrong day, since that will be the session end point of the previous day (eg. 4:00pm)
			const leftIndex = this.params.returnPreviousPoint && primary.length > 1 ? 1 : 0;
			left = this._getIntradaySessionIndex(
				primary[leftIndex].date,
				this.state.typicalSessions.min
			);
			right = this._getIntradaySessionIndex(
				primary[primary.length - 1].date,
				this.state.typicalSessions.max
			);
		} else {
			// interday domain is not set, so calculate it.
			// cinch x-axis to the available data
			// if params.returnPreviousPoint is true, ignore the first datapoint and use the second as the start point.  this prevents a gap on the left, more noticeable with bar charts
			const startIndex =
				this.params.returnPreviousPoint &&
				primary.length > 1 &&
				!this.params.customData &&
				!panel.isNormalized
					? 1 : 0;
			left = this.closestExchangeIndex(primary[startIndex].date);
			right = this.closestExchangeIndex(
				primary[primary.length - 1].date
			);
		}

		// Adjust domain given certain criteria
		if (applyAdjustments) {
			// ichimoku special case: adjust right-hand domain edge since this indicator plots into the future.
			// and don't do this if we have already panned around
			if (hasIchimoku && !hasPanned) {
				// get ichimoku data
				const ichimokuData = this.getDataByType(
					this.params.symbol,
					'ichimokucloud'
				);

				if (ichimokuData.length > 0 && ichimokuData[0].length) {
					// find right edge of data
					const finalDate =
								ichimokuData[0][ichimokuData[0].length - 1].date;
					let finalIndex = this.closestExchangeIndex(finalDate);

					// if intraday, don't shrink earlier than the closing date of the current trading day. (CW-2408)
					if (this.state.isIntraday && this.state.typicalSessions) {
						const dateEnd = new Date(
							this.dataPrimary[this.dataPrimary.length - 1].date
						);
						dateEnd.setHours(0, 0, this.state.typicalSessions.max, 0);
						finalIndex = Math.max(
							finalIndex,
							this.closestExchangeIndex(dateEnd)
						);
					}

					right = finalIndex;
				}
			}

			// Domain adjustment for items that need to be calculated BEFORE the normalized point domain adjustment
			if (
				Object.keys(this.xAxisDomainAdjustmentCategories).length &&
				!hasNonZeroDomain &&
				this.params.useXAxisDomainAdjustment
			) {
				// Run the initial domain adjustment and save it to state
				this.state.initialDomainAdjustment = this._getXAxisDomainAdjustment(left, right, [0, 0]);
			}

			// Domain adjustment for items that need to be calculated AFTER the normalized point domain adjustment
			if (
				Object.keys(this.xAxisDomainAdjustmentCategories).length &&
				!hasNonZeroDomain &&
				this.params.useXAxisDomainAdjustment
			) {
				let [leftDist, rightDist] = this._getXAxisDomainAdjustment(left, right, this.state.initialDomainAdjustment, false);

				// We only check the padding on the upper panel for now. We could potentially
				// add padding per panel, but that doesn't seem useful right now.
				const { padding } = this.panels[0].params.style.xAxis;
				if (padding.left && padding.left > 0) {
					leftDist = Math.max(
						leftDist,
						this._calcXAxisDomainPadding(left, right, padding.left)
					);
				}

				if (padding.right && padding.right > 0) {
					rightDist = Math.max(
						rightDist,
						this._calcXAxisDomainPadding(left, right, padding.right)
					);
				}

				left -= leftDist;
				right += rightDist;
			}
		}

		// return the new domain
		return [left, right];
	}

	/**
	 * Categories defined in xAxisDomainAdjustmentCategories can get truncated when plotted exactly
	 * at the horizontal boundry of a panel. This method determines the additional domain
	 * padding needed to ensure the full width of the item can be displayed, BEFORE normalized
	 * point domain adjustment (if that option is turned on).
	 * @param {number} min - Minimum domain value
	 * @param {number} max - Maximum domain value
	 * @param {Boolean} beforeNormalizedPointDomainAdjustment - Whether or not domain adjustments should take place before normalized point domain adjustment
	 * @returns {number[]} The appropriate amount of XAxis domain padding (left and right) needed for the adjustment categories
	 */
	_getXAxisDomainAdjustment(min = 0, max = 0, [leftDist, rightDist] = [0, 0], beforeNormalizedPointDomainAdjustment = true) {
		if (beforeNormalizedPointDomainAdjustment) {
			let maxBarWidth = 0;
			for (const category in this.xAxisDomainAdjustmentCategories) {
				switch (category) {
					case domainAdjustmentCategories.xAxis.BAR_MARKER:
						// Get marker bar width
						for (const i of this.xAxisDomainAdjustmentCategories[domainAdjustmentCategories.xAxis.BAR_MARKER]) {
							const marker = i.markers[i.params.markerType]
								? i.markers[i.params.markerType]
								: i.markers[markerType.BAR];
							maxBarWidth = Math.max(
								maxBarWidth,
								marker.getBarWidth(i.panel, min, max)
							);
						}

						// Convert half of bar width to domain units (add 5 to show a gap)
						maxBarWidth = this._calcXAxisDomainPadding(min, max, 0.5 * maxBarWidth + 5);

						if (this.state.isIntraday) {
							rightDist = Math.max(rightDist, maxBarWidth);
						} else {
							leftDist = Math.max(leftDist, maxBarWidth);
							rightDist = Math.max(rightDist, maxBarWidth);
						}
						break;
					case domainAdjustmentCategories.xAxis.EVENT:
						// Calculate the proper domain adjustment to account for the icon size showing at the edge of the chart
						leftDist = Math.max(
							leftDist,
							// This will only get the value from the default style
							this._calcXAxisDomainPadding(min, max, this.getStyle('event.xAxis.padding')) * (this.state.isIntraday ? 1 : 0.5)
						);
						rightDist = Math.max(
							rightDist,
							// This will only get the value from the default style
							this._calcXAxisDomainPadding(min, max, this.getStyle('event.xAxis.padding')) * (this.state.isIntraday ? 1 : 0.5)
						);
						break;
				}
			}
		} else {
			let maxShapeLength = 0;
			// If normalized point domain adjustment took place, ignore previously calculated left adjustment distance
			if (this.state.didNormalizedPointDomainAdjustment) leftDist = 0;

			for (const category in this.xAxisDomainAdjustmentCategories) {
				switch (category) { // Leave as switch for now, anticipating that we might need to add other categories in the future
					case domainAdjustmentCategories.xAxis.SHAPE:
						// Check every indicator and get the max length
						for (const i of this.xAxisDomainAdjustmentCategories[domainAdjustmentCategories.xAxis.SHAPE]) {
							maxShapeLength = Math.max(maxShapeLength, i.params.style.shape.length);
						}

						/**
						 * Only apply adjustments to left if:
						 * 1. Chart is interday (because data goes all the way to the left chart edge)
						 * 2. Prepending normalized point (because data goes all the way to the left chart edge, including intraday charts)
						 * */
						if (
							!this.state.isIntraday ||
							this.params.prependNormalizedPoint
						) {
							leftDist = Math.max(
								leftDist,
								this._calcXAxisDomainPadding(min, max, (0.5 * maxShapeLength) + 2) // Add extra pixels to account for border
							);
						}
						rightDist = Math.max(
							rightDist,
							this._calcXAxisDomainPadding(min, max, (0.5 * maxShapeLength) + 1) // Add extra pixel to account for border
						);
						break;
				}
			}
		}

		return [leftDist, rightDist];
	}

	/** return first dataset available */
	getFirstDataset(data) {
		for (const x in data) {
			if (Object.prototype.hasOwnProperty.call(data, x)) {
				return data[x];
			}
		}
	}

	/** Return the first non-empty custom dataset available */
	getFirstCustomDataset(data) {
		for (const key in data) {
			if (Object.prototype.hasOwnProperty.call(data, key) && data[key].length > 0) {
				return data[key];
			}
		}
		return null;
	}

	/**
	 * Searches panels array for indicator with UID (eg. "9ffeb4e", "7ac3c3a")
	 * @param {string} uid - Indicator UID to search for
	 * @return {Indicator|undefined} - Indicator that was found (or undefined if not found)
	 */
	getIndicatorByUid(uid) {
		return this.getPanelItemByUid('indicators', uid);
	}

	/**
	 * Searches panels array for indicators with ID (eg. "sma", "bollinger")
	 * @param {string} id - Indicator ID to search for
	 * @return {Indicator[]} - Array of indicators with the specified ID
	 */
	getIndicatorsById(id) {
		const indicators = [];

		this.panels.forEach(panel => {
			panel.indicators.forEach(i => {
				if (id === i.params.id) indicators.push(i);
			});
		});

		return indicators;
	}

	getPanelItemByUid(key, uid) {
		let item;

		for (const panel of this.panels) {
			for (const i of panel[key]) {
				if (uid === i.params.uid) {
					item = i;
					// Found item, break out of loop
					break;
				}
			}
			if (item) break;
		}

		return item;
	}

	/**
	 * Searches panels array for tool with UID (eg. "9ffeb4e", "7ac3c3a")
	 * @param {string} uid - Tool UID to search for
	 * @return {Tool|undefined} - Tool that was found (or undefined if not found)
	 */
	getToolByUid(uid) {
		return this.getPanelItemByUid('tools', uid);
	}

	/**
	 * Searches panels array for event UID (eg. "9ffeb4e", "7ac3c3a")
	 * @param {string} uid - Event UID to search for
	 * @return {Event|undefined} - Event that was found (or undefined if not found)
	 */
	getEventByUid(uid) {
		return this.getPanelItemByUid('events', uid);
	}

	/**
	 * Searches panels array for event datapoint UID (eg. "9ffeb4e", "7ac3c3a")
	 * @param {string} uid - Event datapoint UID to search for
	 * @return {object|undefined} - Event datapoint that was found (or undefined if not found)
	 */
	getEventDatapointByUid(uid) {
		let dataObj;

		for (const panel of this.panels) {
			for (const event of panel.events) {
				if (event.params.dataset) {
					for (const set of event.params.dataset) {
						if (uid === set.uid) {
							dataObj = set;
							break;
						}
					}
				}
				if (dataObj) break;
			}
			if (dataObj) break;
		}

		return dataObj;
	}

	/** convert a normalized (%) figure into its respective actual (requires the starting value) */
	getActualFromNormalized(normalizedValue, normalizedStart) {
		if (!normalizedStart && this.params.normalizeValues) {
			normalizedStart = this.params.normalizeValues.Close;
		}

		if (!normalizedStart) return normalizedValue;

		let val = normalizedStart * (1 + normalizedValue / 100);
		val = Number(val.toFixed(2));

		return val;
	}

	/**
	 * convert an actual figure ($) into its respective normalized value (requires the starting value)
	 */
	getNormalizedFromActual(value, normalizedStart) {
		if (!normalizedStart && this.params.normalizeValues) normalizedStart = this.params.normalizeValues.Close;

		if (!normalizedStart) return value;

		return ((value - normalizedStart) / Math.abs(normalizedStart)) * 100;
	}

	/**
	 * Returns collection of categories that would necessitate a domain adjustment
	 * (eg. if we have a bar-type marker: ohlc, candle, bar, or if events are showing on the chart)
	 * @returns {Object} Collection of categories found which would necessitate a domain adjustment
	 */
	get xAxisDomainAdjustmentCategories() {
		const adjustments = {};

		if (!this.params.useXAxisDomainAdjustment) return adjustments;

		for (const panel of this.panels) {
			for (const indicator of panel.indicators) {
				// Check for bar markers
				if (
					indicator.params.id === 'volume' ||
					(
						/price|custom/.test(indicator.params.id) &&
						/bar|candlestick|hlc/.test(indicator.params.markerType)
					)
				) {
					if (!adjustments[domainAdjustmentCategories.xAxis.BAR_MARKER]) {
						adjustments[domainAdjustmentCategories.xAxis.BAR_MARKER] = [indicator];
					} else {
						adjustments[domainAdjustmentCategories.xAxis.BAR_MARKER].push(indicator);
					}
				}

				// Check for shape marker types
				if (
					indicator.params.markerType === domainAdjustmentCategories.xAxis.LINEWITHSHAPES ||
					indicator.params.markerType === domainAdjustmentCategories.xAxis.SHAPE
				) {
					if (!adjustments[domainAdjustmentCategories.xAxis.SHAPE]) {
						adjustments[domainAdjustmentCategories.xAxis.SHAPE] = [indicator];
					} else {
						adjustments[domainAdjustmentCategories.xAxis.SHAPE].push(indicator);
					}
				}
			}

			// Check for events
			if (panel.events.length > 0) adjustments[domainAdjustmentCategories.xAxis.EVENT] = true;
		}

		return adjustments;
	}

	/**
	 * Searches panels array for uid or id
	 * @param {string} uid - Panel UID to search for
	 * @return {Panel|undefined} - Panel that was found (or undefined if not found)
	 */
	getPanelByUid(uid) {
		let panelMatch;

		for (const panel of this.panels) {
			if (panel.params.uid === uid || panel.params.id === uid) {
				panelMatch = panel;
				break;
			}
		}

		return panelMatch;
	}

	getRandomHexColor() {
		return `#${(0x1000000 + Math.random() * 0xffffff).toString(16).substr(1, 6)}`;
	}

	/**
	 * date index offset of dataset compared to global dataset
	 * @method getDateOffset
	 * @param {array} data
	 * @param {array} dataGlobal
	 * @return {int}
	 */
	getDateOffset(data, dataGlobal) {
		for (let i = 0; i < dataGlobal.length; i++) {
			if (dataGlobal[i] >= data[0]) {
				return i;
			}
		}

		return 0;
	}

	/**
	 * add message to messages div
	 * @method showMessage
	 * @param {string} message
	 */
	showMessage(message) {
		const el = this.rootModchart.node().querySelector('.modcharts-rootmessages');
		if (this.panels.length) {
			el.style.width = `${this.panels[0].size.width}px`;
		}
		const isHTML = /</.test(message);

		// reveal parent element
		this.rootMessages.node().style.display = 'flex';

		const html = isHTML
			? message
			: `<div style="padding:50px;">${message}</div>`;

		el.innerHTML = html;
	}

	/** Clear message DIV */
	clearMessage() {
		const el = this.rootModchart.node().querySelector('.modcharts-rootmessages');

		// conceal parent element
		this.rootMessages.node().style.display = 'none';

		el.innerHTML = '';
	}

	/**
	 * Clear the chart and display a "Chart Not Available" message.
	 * Supports two input types:
	 * - **String**: Displays the provided string as the message.
	 * - **Object**: Uses `message` and `description` properties from the object. Defaults to a translated message if `message` is not provided.
	 * If no valid input is given, defaults to a translated "chart unavailable" message.
	 * @param {string|object} input - The message string or an object with `message` and `description`.
	 */
	showMessageChartNotAvailable(input) {
		let message;
		let description;

		// Check if the input is a string or an object
		if (typeof input === 'string') {
			message = input;
		} else if (typeof input === 'object' && input !== null) {
			message = input.message || translate(this.locale, 'chart.unavailable');
			description = input.description;
		} else {
			// If no argument is passed or if it's an invalid type, use the default message
			message = translate(this.locale, 'chart.unavailable');
		}

		this.clearPanels();
		this.setCursor(types$1.DEFAULT);
		this.loadingStop(false); // Don't emit BOUNDARY_STATUS event
		this.showMessage(`<div class="modcharts-chartnotavailable">${message}</div>`);

		// Emit the event with the description if available
		this.eventEmitter.emit(types.CHART_NOT_AVAILABLE, {
			message,
			description
		});
	}

	/** Determines current visible data, and, if we're missing any historic data, retrieves it. */
	backfillData() {
		const domain = this.panels[0].xAxis.primaryScale.domain();
		const self = this;
		const domainLeft = Math.max(0, Math.floor(domain[0]));
		const domainRight = Math.min(
			this.exchangeDates
				? this.exchangeDates.length - 1
				: Math.floor(domain[1]),
			Math.floor(domain[1])
		);
		const leftIndex = this.params.returnPreviousPoint && this.dataPrimary.length > 1 ? 1 : 0;
		const dataLeft = this.closestExchangeIndex(this.dataPrimary[leftIndex].date);
		const isIntraday =
				typeof this.state.isIntraday === 'boolean'
					? this.state.isIntraday
					: /minute|hour/i.test(this.params.dataPeriod);
		const dayLimit = isIntraday ? this.limitIntradayCalendar : this.params.rulerInterdayStart;
		const dataNormalized = this.panels[0].isNormalized;
		const now = new Date();
		const dateLimit = new Date(now.setDate(now.getDate() - dayLimit));
		const dateDomainLeft =
				this.exchangeDates && this.exchangeDates[domainLeft]
					? this.getUTCDate(
						new Date(this.exchangeDates[domainLeft])
					)
					: null;

		const dateDataLeft = this.dataPrimary[leftIndex].date;
		// backfill only if domain's left is less than data's left AND if data's left is within the dateLimit time period (e.g. 35 years on interday).
		if (
			domainLeft < dataLeft &&
			dateDataLeft > dateLimit
		) {
			const dayDiff = (dateDataLeft - dateDomainLeft) / 1000 / 60 / 60 / 24;
			const offsetDays = Math.max(
				1,
				Math.ceil((new Date() - dateDataLeft) / 1000 / 60 / 60 / 24)
			);

			// invalidate if we haven't panned over enough based on a threshold of days (different for each period)
			let isValid = dayDiff >= this.backfillThreshold;

			// invalidate lengthy inter/intraday requests
			if (isIntraday && offsetDays > dayLimit) {
				isValid = false;
			}

			if (isValid) {

				// set (or reset) dateStart/Stop to the currently visible date range on the axis (this includes the empty gap about to be filled).  This preserves the start/stop dates if indicators are added or the chart is saved.
				this.params.dateStart = new Date(
					this.exchangeDates[domainLeft]
				);
				this.params.dateStop = new Date(
					this.exchangeDates[domainRight]
				);
				// limit offsetDays by the dayLimit
				this.params.endOffsetDays = Math.min(dayLimit, offsetDays);

				// if we're looking at more than one symbol, and we've panned over, define the normalizedate
				if (!this.params.normalizeDate && dataNormalized) {
					this.params.normalizeDate = this.dataPrimary[0].date;
				}

				this.loadData(() => {
					// reset endOffsetDays
					self.params.endOffsetDays = 0;
				}, { silent: false, prepend: true });
			}
		}
	}

	/**
	 * Determine when to trigger a backfill after panning.
	 * A backfill won't trigger if the panned date is less than this number of days from the first data date.
	 */
	get backfillThreshold() {
		switch (this.params.dataPeriod) {
			case 'Month': return 30;
			case 'Week': return 7;
			default: return 0;
		}
	}

	/**
	 * get nearest exchangeDates index by Date
	 * @method closestExchangeIndex
	 * @param {Date} jsDate
	 * @return {int}
	 */
	closestExchangeIndex(jsDate) {
		const eD = this.exchangeDates;
		const val = new Date(jsDate).getTime();
		let idx = 0;
		let mid = 0;
		let done = false;
		let min = 0;
		let max = eD.length - 1;

		while (!done) {
			if (max - min > 1) {
				mid = Math.floor((max + min) / 2);

				if (eD[mid] <= val) {
					min = mid;
				} else {
					max = mid;
				}
			} else {
				idx = eD[min] >= val ? min : max;
				done = true;
			}
		}

		return idx;
	}

	/**
	 * Search dataset for the closest round index to a given domain value
	 * For example, a mouse operation may result in a floating point domain value of 7341.3926090283085.
	 * The method will do a binary search for the nearest OHLC datapoint to that value, and return that datapoint's index.
	 * If missing, the method will assign the value of data[x].dateIndex as it performs the search.
	 * The method does double-duty (represented by the optional 'key' input and the data input passed)
	 * If key is 'dateIndex', the search will be done against the main OHLC dataset.  This is used for snapping a floating point x-coordinate to specific round index (crosshair and tool events use this)
	 * If key is omitted or 'index', the search will be done against the labels dataset.  This is used by the labeler to determine which subset of the larger labels array to process.
	 * @param {Number} val Domain value (ex: 7341.3926090283085)
	 * @param {Object[]} data Dataset to parse
	 * @param {String} key Search against either 'index' or 'dateIndex' property in the 'data' array
	 * @private
	*/
	_getIndexByDomain(val, data, key = 'index') {
		if (!data || !data.length) return 0;

		let idx = 0;
		let mid = 0;
		let midV = 0;
		let min = 0;
		let max = data.length - 1;
		let done = false;
		let distMax = 0;
		let distMin = 0;
		const isKeyDateIndex = key === 'dateIndex';

		while (!done) {
			if (max - min <= 1) {
				// calculate dateIndex here if not already present
				if (isKeyDateIndex && typeof data[min][key] !== 'number') {
					data[min][key] = this.closestExchangeIndex(data[min].date);
				}

				// calculate dateIndex here if not already present
				if (isKeyDateIndex && typeof data[max][key] !== 'number') {
					data[max][key] = this.closestExchangeIndex(data[max].date);
				}

				distMin = Math.abs(data[min][key] - val);
				distMax = Math.abs(data[max][key] - val);

				idx = distMin < distMax ? min : max;
				done = true;
			} else {
				// calculate dateIndex here if not already present
				if (isKeyDateIndex && typeof data[mid][key] !== 'number') {
					data[mid][key] = this.closestExchangeIndex(data[mid].date);
				}

				mid = Math.floor((max + min) / 2);
				midV = data[mid][key];

				if (midV <= val) {
					min = mid;
				} else {
					max = mid;
				}
			}
		}

		return idx;
	}

	/**
	 * Given a search date and a dataset that includes .date properties, find the nearest
	 * datapoint to the search date, and return the array index of that match.
	 * @param {Object[]} data - A data array of objects with a date property
	 * @param {Date} date - JS date
	 * @param {Boolean} checkExact - Whether or or not to make sure the date matches exactly.
	 * Defaults to false.
	 * @return {Number} - Returns the closest array index to the given date
	 */
	_getIndexByDate(data, date, checkExact = false) {
		if (!date || !data.length) return 0;
		let closestIndex;
		let min = 0;
		let max = data.length - 1;
		let done = false;
		while (!done) {
			if (max - min <= 1) {
				const distMax = date - data[max].date;
				const distMin = date - data[min].date;
				closestIndex = Math.abs(distMax) < Math.abs(distMin)
					? max
					: min;
				done = true;
			} else {
				const mid = Math.floor((max + min) / 2);
				if (data[mid].date <= date) {
					min = mid;
				} else {
					max = mid;
				}
			}
		}

		if (checkExact) {
			return data[closestIndex].date.getTime() === date.getTime()
				? closestIndex
				: -1;
		}
		return closestIndex;

	}

	/**
	 * Find earliest data index by date.  Similar to _getIndexByDate, but favors the left-hand match.
	 * Eg., it won't snap to the right-hand match even when that is closer to the search date.
	 * This is useful for stepped indicators that need to stay on the left-hand match for the entire
	 * duration of a given horizontal line segment.
	 * Note: if the search date is greater than the last date in the series, it will return the final
	 * series index.
	 * @method _getPrevIndexByDate
	 * @param {Array} data
	 * @param {Date} date
	 * @return {int}
	 */
	_getPrevIndexByDate(data, date) {
		let lowerIdx;
		let min = 0;
		let max = (data.length) ? data.length - 1 : 0;
		let mid = 0;
		let done = false;
		if (!date || !data.length) { return min; }
		if (data[max].date < date) { return max; }
		while (!done) {
			if (max - min <= 1) {
				lowerIdx = min;
				done = true;
			} else {
				mid = Math.floor((max + min) / 2);
				if (data[mid].date <= date) {
					min = mid;
				} else {
					max = mid;
				}
			}
		}
		return lowerIdx;
	}

	/** Get slice of datasets that match the current domain */
	_getFrameData(domain = this.panels[0].xAxis.primaryScale.domain()) {
		const data = {};
		const ruler = this.exchangeDates;
		const rulerLength = ruler.length;
		const dateMin = new Date(
			ruler[
				Math.min(
					rulerLength - 1,
					Math.max(0, Math.floor(domain[0]))
				)
			]
		);
		const dateMax = new Date(
			ruler[
				Math.min(
					rulerLength - 1,
					Math.max(0, Math.floor(domain[1]))
				)
			]
		);
		let dataSymbol;
		let filteredSymbol;
		let dataSymbolEl;
		let isPreviousClose;
		let isEvent;
		let t;
		let tLen;
		let el;
		let elId;
		let elUid;
		let filteredSymbolEl;
		let thisFiltered;
		let thisMinIdx;
		let thisMaxIdx;

		// loop over each symbol
		for (const symbol in this.data) {
			if (Object.prototype.hasOwnProperty.call(this.data, symbol)) {
				dataSymbol = this.data[symbol];

				if (!data[symbol]) data[symbol] = {};

				filteredSymbol = data[symbol];

				// loop over indicators for this symbol (bollinger, sma, etc)
				for (elId in dataSymbol) {
					if (Object.prototype.hasOwnProperty.call(dataSymbol, elId)) {
						dataSymbolEl = dataSymbol[elId];
						isPreviousClose = elId === 'previousclose';
						isEvent = /dividends|earnings|splits|custom/.test(elId);
						let maxOffset = 0;

						// CW-1348 special case - ichimoku clouds' maxIdx will need to be bumped to the right
						// to accomodate the chikou series being bumped to the left during rendering.
						if (elId === 'ichimokucloud') {
							maxOffset = Math.max.apply(
								null,
								this.getIndicatorsById('ichimokucloud').map(row => row.getInput('mediumPeriod')
								)
							);
						}

						// loop over series (open, high, low, close) for this indicator
						for (elUid in dataSymbolEl) {
							if (Object.prototype.hasOwnProperty.call(dataSymbolEl, elUid)) {
								el = dataSymbolEl[elUid];

								if (!el.length) continue;

								if (!filteredSymbol[elId]) filteredSymbol[elId] = {};

								filteredSymbolEl = filteredSymbol[elId];

								if (isPreviousClose) {
									filteredSymbol.previousclose[elUid] = el;
								} else if (isEvent) {
									// events

									filteredSymbolEl[elUid] =
										dataSymbolEl[elUid];
								} else {
									thisMinIdx = this._getIndexByDate(
										el,
										dateMin
									);
									thisMaxIdx = this._getIndexByDate(
										el,
										dateMax
									);

									if (maxOffset) {
										thisMaxIdx += maxOffset;
									}

									// slice at different indexes for each element
									// -1/+2 is to ensure the data is wider than viewable panel
									filteredSymbolEl[elUid] = el.slice(
										Math.max(thisMinIdx - 1, 0),
										Math.min(
											thisMaxIdx + 2,
											rulerLength - 1
										)
									);

									thisFiltered = filteredSymbolEl[elUid];

									for (
										tLen = thisFiltered.length, t = 0;
										t < tLen;
										t++
									) {
										if (thisFiltered[t]) {
											// precalculate the dateIndex for fast lookups later
											if (
												typeof thisFiltered[t]
													.dateIndex === 'undefined'
											) {
												thisFiltered[
													t
												].dateIndex = this.closestExchangeIndex(
													thisFiltered[t].date
												);
											}

											// special case: precalculate the chikou dateIndex if necessary (CW-2212)
											if (
												thisFiltered[t].dateChikou &&
												typeof thisFiltered[t]
													.dateIndexChikou ===
													'undefined'
											) {
												thisFiltered[
													t
												].dateIndexChikou = this.closestExchangeIndex(
													thisFiltered[t].dateChikou
												);
											}
										}
									}
								}
							}
						}
					}
				}
			}
		}

		return data;
	}

	/**
	 * similar to _getFrameData, this will return the slice of custom datasets that match the current domain.
	 */
	_getCustomFrameData(domain = this.panels[0].xAxis.primaryScale.domain()) {
		const data = {};
		const ruler = this.exchangeDates;
		const rulerLength = ruler.length;
		const dateMin = new Date(
			ruler[
				Math.min(
					rulerLength - 1,
					Math.max(0, Math.floor(domain[0]))
				)
			]
		);
		const dateMax = new Date(
			ruler[
				Math.min(
					rulerLength - 1,
					Math.max(0, Math.floor(domain[1]))
				)
			]
		);
		let t;
		let tLen;
		let el;
		let thisMinIdx;
		let thisMaxIdx;

		// loop over each custom dataset
		for (const dataset in this.data) {
			if (Object.prototype.hasOwnProperty.call(this.data, dataset)) {
				el = this.data[dataset];

				if (!el.length) continue;

				thisMinIdx = this._getIndexByDate(el, dateMin);
				thisMaxIdx = this._getIndexByDate(el, dateMax);

				el = el.slice(
					Math.max(thisMinIdx - 1, 0),
					Math.min(thisMaxIdx + 1, rulerLength)
				);

				for (tLen = el.length, t = 0; t < tLen; t++) {
					if (el[t]) {
						el[t].dateIndex = this.closestExchangeIndex(el[t].date);
					}
				}

				data[dataset] = el;
			}
		}

		return data;
	}

	/**
	 * convert params into an input object ready to be posted to ChartAPI
	 * @method _getChartAPIInputs
	 */
	_getChartAPIInputs(args = {}) {
		let dataPeriod = this.params.dataPeriod || 'Day';
		let days = args.days || this.params.days || 365;
		let daysInput;

		if (isNaN(Number(days))) {
			days = this.params.days = 365;
			dataPeriod = this.params.dataPeriod = 'Day';
		} else {
			days = Number(days);
		}

		// calculate the days between params.dateStart/dateStop
		if (this.params.dateStart && this.params.dateStop) {
			// CW-2432 The days input calculated here will normally reflect the number of days between dateStart and dateStop.  However, this value may not be large enough in some situations - specifically when older comparisons are present and the user has continued panning beyond the inception date of the younger primary symbol.  For context, the API request will ultimately use both the "days" and "endOffsetDays" inputs to determine the date range to return.  Since the endOffsetDays value doesn't change once the inception date of the primary symbol is reached, future pans will incorrectly request the same range from the API repeatedly.  To handle this, the dateStop input should use the first date of the primary symbol's dataset, rather than the date defined by the right-hand edge of the XAxis.
			let dateStop = this.params.dateStop;
			if (this.dataPrimary?.length) {
				dateStop = Math.max(dateStop, this.dataPrimary[0].date);
			}
			days =
				Math.ceil(
					(dateStop - this.params.dateStart) / 1000 / 60 / 60 / 24
				);
		}

		// earliest days value (via params.days or dateStart)
		const daysMin = Math.max(1, this.params.dateStart
			? Math.ceil((new Date() - this.params.dateStart) / 1000 / 60 / 60 / 24)
			: days
		);

		// if not a prepend request, we will request all days from dateStart until present
		// if a prepend request, just request the days that fall between dateStart/Stop, and include an additional endOffsetDays
		if (this.state.isIntraday) {
			const endOffset = this.params.rulerIntradayStart - (this.params.endOffsetDays || 0);
			const numWeekend = Math.floor(endOffset / 7);
			daysInput = Math.min(
				endOffset - (2 * numWeekend),
				args.prepend ? days : daysMin
			);
		} else {
			daysInput = Math.min(
				this.params.rulerInterdayStart - (this.params.endOffsetDays || 0),
				args.prepend ? days : daysMin
			);
		}

		// number of days to return
		daysInput = Math.max(1, daysInput);

		// initialize the chartapi input package
		const inputs = {
			days: daysInput,
			dataNormalized: this.panels[0].isNormalized,
			dataPeriod,
			dataInterval: Number(this.params.dataInterval || 1),
			realtime: this.params.realtime,
			yFormat: this.getChartAPIDataPrecision(),
			timeServiceFormat: 'JSON', //  requires MOD.Web.ChartAPI 0.1.75-beta
			rulerIntradayStart: this.params.rulerIntradayStart,
			rulerIntradayStop: this.params.rulerIntradayStop,
			rulerInterdayStart: this.params.rulerInterdayStart,
			rulerInterdayStop: this.params.rulerInterdayStop
		};

		// prepend request inputs
		if (args.prepend && this.params.endOffsetDays) {
			// endOffsetDays. (offset days from last trading day)
			inputs.endOffsetDays = this.params.endOffsetDays;
			if (this.state.isIntraday) {
				// exchangeOffset.  only needed on intraday. (CW-322)
				inputs.exchangeOffset = this.getExchangeOffsetInput();
			}
		}

		// returnDateType
		if (/chartapi\/series/i.test(this.params.apiSeries)) {
			inputs.returnDateType = 'ISO8601';
		}

		// isMax
		if (this.params.isMax) {
			inputs.isMax = true;
		}

		// set optional dataInterface
		if (this.params.dataInterface) {
			inputs.dataInterface = this.params.dataInterface;
		}

		// set optional feedSelection Group
		if (this.params.feedGroup) {
			inputs.feedGroup = this.params.feedGroup;
		}

		// optional usage reporting feature type
		if (this.params.usageReportingAppFeatureType) {
			inputs.usageReportingAppFeatureType = this.params.usageReportingAppFeatureType;
		}

		// major currency flag
		if (this.params.showMajorCurrency) {
			inputs.showMajorCurrency = true;
		}

		// poll date (optional).  the data api will limit its next response by this date or later.
		if (args.silent && this.dataPrimary.length) {
			const pollDate = new Date(
				this.dataPrimary[this.dataPrimary.length - 1].date
			);

			// account for client timezone offset
			pollDate.setMinutes(
				pollDate.getMinutes() - pollDate.getTimezoneOffset()
			);

			// pollDate input expects MSDate format
			inputs.pollDate = this.jsToMsDate(pollDate);
		}

		if (this.params.extendedHours) {
			inputs.tradeType = 'NormalAndExtendedHours';
		}

		// normalize date
		if (!inputs.dataNormalized) {
			this.params.normalizeDate = null;
			this.params.normalizeValue = null;
			this.params.normalizeValues = null;
		} else if (this.params.normalizeDate) {
			const normalizeDate = new Date(this.params.normalizeDate);

			// account for client timezone offset
			normalizeDate.setMinutes(
				normalizeDate.getMinutes() - normalizeDate.getTimezoneOffset()
			);

			inputs.normalizeDate = normalizeDate.toISOString();
		}

		// CH-1681 reduce API response size
		if (!this.state.isIntraday) {
			inputs.returnDateFormat = 'interday';
		}

		inputs.elements = this._getElementsInput();

		// CH-1799 customConfig
		inputs.customConfig = {
			Version: this.version,
			ReturnPreviousPoint: this.params.returnPreviousPoint
		};

		// reset params.isMax as this is only intended for a one-time use
		this.params.isMax = null;

		// special case: increase the interday XAxis if ichimoku is present (CH-1942)
		if (
			!this.state.isIntraday &&
			this.panels[0].indicators.filter(i => i.params.id === 'ichimokucloud').length > 0
		) {
			inputs.rulerInterdayStop = Math.max(365, inputs.rulerInterdayStop);
		}

		return inputs;
	}

	/**
	 * Get dynamic exchangeOffset value for correct intraday quote requests (CW-322)
	 * @method getExchangeOffsetInput
	 */
	getExchangeOffsetInput() {
		// use hardcoded number if passed as an input (this is for backward compatibility)
		if (this.params.exchangeOffset) {
			return this.params.exchangeOffset;
		}

		// or else calculate the dynamic exchangeOffset
		const utcOffset = this.xref[this.params.symbol]
			? this.xref[this.params.symbol].utcOffset || 0
			: 0;
		const earliestOpen = this.state.typicalSessions
			? this.state.typicalSessions.min || 0
			: 0;

		if (earliestOpen - utcOffset < 0) {
			return Math.abs(earliestOpen - utcOffset);
		}

		return 0;
	}

	/**
	 * convert dataPrecision into format string
	 * @method getChartAPIDataPrecision
	 */
	getChartAPIDataPrecision() {
		let format = '0.#';
		const xLen =
				typeof this.params.dataPrecision === 'number'
					? Math.max(0, this.params.dataPrecision - 1)
					: 2;

		for (let x = 0; x < xLen; x++) {
			format += '#';
		}

		return format;
	}

	/**
	 * Returns elements in MOD.Web.ChartAPI input format
	 * @return {Object[]} Array of elements in the proper format
	 */
	_getElementsInput() {
		let newElement;
		let symbol;
		const elements = [];

		this.panels.forEach(panel => {
			const dataNormalized = panel.isNormalized;

			// add indicator elements (sma, bollingers)
			for (const indicator of panel.indicators) {
				symbol = indicator.params.symbol || this.params.symbol;

				// overlay indicators will be added in getOverlayIndicators
				if (indicator.params.parentUid || indicator.params.parentUID) {
					continue;
				}

				newElement = {
					Label: indicator.params.uid,
					Type: indicator.params.datasetId || indicator.params.id,
					Symbol: symbol,
					OverlayIndicators: this.getOverlayIndicators(
						panel,
						indicator
					),
					Params: this.getIndicatorParamsInput(panel, indicator)
				};

				// NXC-735 specify alternative currency for returned prices
				if (
					this.params.currency &&
					newElement['Symbol'] === this.params.symbol
				) {
					newElement['DesiredCurrency'] = this.params.currency;
				}

				// normalized price data needs a multiplier
				if (dataNormalized) {
					newElement['ValueMultiplier'] =
						indicator.params.valueMultiplier || 100;
					newElement['ValueFormat'] = '{0:#,##0.######}'; // see NXC-609
				}

				// optional currency cross rate flag (NXC-734)
				if (/\w+\|\w+/.test(symbol.toString())) {
					newElement['IsCurrencyCrossRate'] = true;
				}

				elements.push(newElement);
			}

			// add event elements (splits, dividends, etc)
			for (const event of panel.events) {
				newElement = {};

				if (event.params.id === 'custom') continue;

				newElement.Label = event.params.uid;
				newElement.Type = event.params.id;
				newElement.Symbol = this.params.symbol;
				newElement.Params = this.getIndicatorParamsInput(panel, event);

				// AC-281 Add optional DesiredCurrency value
				if (this.params.currency) newElement['DesiredCurrency'] = this.params.currency;

				elements.push(newElement);
			}
		});

		return elements;
	}

	/**
	 * collect inputs specific to each chartapi element (could be none)
	 */
	getIndicatorParamsInput(panel, obj) {
		const params = {};
		const inputs = obj.params.inputs || [];
		inputs.forEach(row => {
			if (
				row.name === 'WSODIssue' &&
				row.value === 'auto'
			) {
				params[row.name] = panel.core.params.symbol;
			} else if (row.value !== 'ohlc') {
				params[row.name] = row.value;
			}
		});

		// Add cumulativeadjustment inputs when panning
		if (obj.cumulativeAdjustment && this.params.endOffsetDays > 0) {
			params['adjustmentValue'] = obj.cumulativeAdjustment.value;
			params['adjustmentDate'] = obj.cumulativeAdjustment.date;
		}
		return params;
	}

	/**
	 * get overlay param inputs in newer json input format
	 */
	getOverlayIndicators(panel, indicator) {
		const uid = indicator.params.uid;
		const overlays = [];
		let ind = {};
		let parentUid = '';

		for (ind of panel.indicators) {
			parentUid = ind.params.parentUid || ind.params.parentUID;
			if (parentUid && parentUid === uid) {
				overlays.push({
					Label: ind.params.uid,
					Type: ind.params.id,
					Symbol: ind.params.symbol || this.params.symbol,
					DataSeries: indicator.params.dataSeries || indicator.params.id,
					Params: this.getIndicatorParamsInput(panel, ind)
				});
			}
		}
		return overlays;
	}

	/**
	 * Updates the xAxis domain based on provided start and stop indices.
	 *
	 * Usage:
	 * - Default Mode: Displays the last N trading days.
	 * - Custom Domain Mode: Focuses on a specific date range.
	 *
	 * Parameters:
	 * @param {number} start - Start index in exchangeDates.
	 * @param {number} stop - Stop index in exchangeDates.
	 */
	_updateXAxisScaleDomains(start, stop) {
		const [domainStart, domainStop] = this.xAxis.primaryScale.domain();

		// Update domain if necessary
		if (
			(domainStart === 0 && domainStop === 0) ||
			start !== domainStart ||
			stop !== domainStop
		) {
			this.panels.forEach((panel, i) => {
				panel.xAxis.setScaleDomain({ start, stop });

				// Update only once for all panels
				if (i === 0) {
					if (this.state.updateXAxisPresentScaleDomain) {
						/**
						 * CH-1719
						 * Update the present scale domain given the new exchangeDates collection.
						 * If we don't do this, the domain (which represents the exchangeDates indices)
						 * will refer to the old exchangeDates collection indicies instead of the new ones.
						 */
						this.state.updateXAxisPresentScaleDomain = false;

						// Get the updated domain by calculating the present frame data
						let domain;
						let domainData;

						if (this.state.useDataPrimaryPresentScaleUpdate) {
							this.state.useDataPrimaryPresentScaleUpdate = false;
							domainData = this.state.presentDataPrimary;
						} else {
							/* This path requires calculating a new zoom transform for seamless use of pan/zoom */

							// Get current frame domain without adjustments, to be used in calculating proper timespan
							const [defaultStart, defaultStop] = this.getDefaultDomain(this.dataPrimary, [0, 0], false);
							const lastDatePrimary = new Date(this.dataPrimary[this.dataPrimary.length - 1].date).getTime();
							const domainStopDate = this.exchangeDates[defaultStop];
							const isCustomMode = this._isCustomMode(domainStopDate, lastDatePrimary);
							const effectiveLastDate = this._getEffectiveLastDate(isCustomMode, domainStopDate, lastDatePrimary);

							const firstDate = this.params.days && (!this.params.dateStart || !this.params.dateStop)
								? this.dataPrimary[0].date
								: this._calculateFirstDateByTradingDays(
									defaultStart,
									defaultStop,
									effectiveLastDate,
									this.exchangeDates,
									this.timeService.tradingDays
								);

							domain = this._getDomainIndices(
								firstDate,
								effectiveLastDate,
								this.state.isIntraday,
								this.state.typicalSessions
							);

							const frameData = this.params.customData
								? this._getCustomFrameData(domain)
								: this._getFrameData(domain);
							domainData = this.getFirstDataset(frameData[this.params.symbol].price);

							// CH-1759: There's some funkiness with being off by one day in certain scenarios. We might need to force data
							// to exclude market close from previous day so that domain is correctly calculated in getDefaultDomain.
							// if (this.state.isIntraday) domainData.shift();

							// Update the present dataPrimary collection (this gets used when adding comparisons)
							this.state.presentDataPrimary = domainData;
						}

						const presentDomain = this.getDefaultDomain(domainData, null, true, true);
						this.state.xAxisPresentScale = this.xAxis.primaryScale.copy().domain(presentDomain);

						// Calculate new zoom transform
						const newTransform = this.zoomUtil.getTransformFromScales(
							this.state.xAxisPresentScale,
							this.xAxis.primaryScale
						);

						// Save transform to allow for resetting view
						if (!this.state.initialTransform) this.state.initialTransform = newTransform;

						// Update the zoom center to show how the saved chart is different from the present chart
						this.zoomUtil.updateZoomCenter({ chart: this, transform: newTransform });
					} else {
						// Save dataPrimary, present xAxis scale for calculating zoom transform updates
						this.state.xAxisPresentScale = this.xAxis.primaryScale.copy();
						this.state.presentDataPrimary = [...this.dataPrimary];

						// Save zoomIdentity to allow for resetting view
						this.state.initialTransform = zoomIdentity;
					}
				}
			});
		}
	}

	/**
	 * Checks if the current mode is custom based on domainStopDate and lastDatePrimary.
	 *
	 * Examples:
	 * - Returns `true` if `domainStopDate` !== `lastDatePrimary`.
	 * - Returns `false` otherwise.
	 *
	 * @param {number} domainStopDate - Current domain stop date (timestamp).
	 * @param {number} lastDatePrimary - Last date in primary data (timestamp).
	 * @returns {boolean}
	 */
	_isCustomMode(domainStopDate, lastDatePrimary) {
		return domainStopDate !== lastDatePrimary;
	}

	/**
	 * Determines the effective last date based on custom mode.
	 *
	 * Examples:
	 * - If in custom mode and `domainStopDate` is valid, returns `domainStopDate`.
	 * - If invalid, logs a warning and returns `lastDatePrimary`.
	 *
	 * @param {boolean} isCustomMode - Indicates custom mode.
	 * @param {number} domainStopDate - Current domain stop date (timestamp).
	 * @param {number} lastDatePrimary - Last date in primary data (timestamp).
	 * @returns {number} - Effective last date (timestamp).
	 */
	_getEffectiveLastDate(isCustomMode, domainStopDate, lastDatePrimary) {
		if (isCustomMode) {
			if (!this.exchangeDates.includes(domainStopDate)) {
				console.warn('Invalid custom end date. Using lastDatePrimary.');
				return lastDatePrimary;
			}
			return domainStopDate;
		}
		return lastDatePrimary;
	}

	/**
	 * Determines the first date based on a trading days limit.
	 *
	 * Examples:
	 * - If limit is 5 trading days, finds the date 5 trading days before `effectiveLastDate`.
	 * - If not enough trading days, sets to the earliest available date.
	 *
	 * @param {number} defaultStart - Start index in exchangeDates.
	 * @param {number} defaultStop - Stop index in exchangeDates.
	 * @param {number} effectiveLastDate - Effective last date (timestamp).
	 * @param {number[]} exchangeDates - Array of exchange date timestamps.
	 * @param {number[]} tradingDays - Array of trading day timestamps.
	 * @returns {Date}
	 */
	_calculateFirstDateByTradingDays(defaultStart, defaultStop, effectiveLastDate, exchangeDates, tradingDays) {

		const limit = calcTradingDaysBetweenTradingDates(
			new Date(exchangeDates[defaultStart]),
			new Date(exchangeDates[defaultStop]),
			tradingDays
		);

		let count = 0;
		let firstDate = new Date(effectiveLastDate);
		let checkDate = new Date(effectiveLastDate);

		// Iterate backwards to find the first date within the trading days limit
		for (let j = this.dataPrimary.length - 1; j >= 0; j--) {
			const datum = this.dataPrimary[j];
			const datumDate = new Date(datum.date).getTime();

			if (checkDate.getTime() - datumDate >= 86400000) { // 1 day in ms
				firstDate = new Date(checkDate);
				count++;
				if (count >= limit) break;
				checkDate = new Date(datumDate);
			}
		}

		if (count < limit) {
			this.warn('Insufficient trading days. Using earliest available date.');
			firstDate = new Date(this.dataPrimary[0].date);
		}

		return firstDate;
	}

	/**
	 * Determines the start and stop indices based on intraday settings.
	 *
	 * Examples:
	 * - Intraday: Snaps dates to session start/end times.
	 * - Non-Intraday: Uses closest exchange indices.
	 *
	 * @param {Date} firstDate - Calculated first date.
	 * @param {number} lastDate - Effective last date (timestamp).
	 * @param {boolean} isIntraday - Intraday data flag.
	 * @param {Object} typicalSessions - Session timings (min/max in minutes).
	 * @returns {number[]} - [startIndex, stopIndex]
	 */
	_getDomainIndices(firstDate, lastDate, isIntraday, typicalSessions) {
		if (isIntraday) {
			return [
				this._getIntradaySessionIndex(new Date(firstDate), typicalSessions.min),
				this._getIntradaySessionIndex(new Date(lastDate), typicalSessions.max)
			];
		}
		return [
			this.closestExchangeIndex(new Date(firstDate)),
			this.closestExchangeIndex(new Date(lastDate))
		];

	}

	/**
	 * This is similar to _processChartApiData, but used for custom data.
	 * This will convert all custom datasets into the native date format, also
	 * ensuring date inputs are parsed as Date objects
	 */
	_processCustomData(dataIn) {
		const data = {};
		let x;
		let d;
		let datapoint;
		Object.keys(dataIn).forEach(customId => {
			const customRows = dataIn[customId];
			data[customId] = [];

			for (x = 0; x < customRows.length; x++) {
				d = customRows[x];

				datapoint = {};
				Object.keys(d).forEach(key => {
					const value = d[key];
					if (key === 'date') {
						if (
							/^\d{5}$/.test(value.toString()) ||
							/\d{5}\.\d+/.test(value.toString())
						) {
							datapoint.date = this.msToJsDate(value);
						} else {
							datapoint.date = new Date(value);
							// adjust for local timezone (CH-1917)
							datapoint.date.setMinutes(
								datapoint.date.getMinutes() + datapoint.date.getTimezoneOffset()
							);
						}
					} else {
						datapoint[key] = value;
					}
				});

				// diff from previous neighbor (CW-1210)
				if (x > 0) {
					datapoint.diff = d.close - customRows[x - 1].close;
				} else {
					datapoint.diff = 0;
				}

				// hollow calc (CW-1210)
				if (typeof d.close === 'number' && typeof d.open === 'number') {
					datapoint.hollow = d.close > d.open ? 1 : 0;
				} else {
					datapoint.hollow = 0;
				}

				data[customId].push(datapoint);
			}
		});

		// Use the first non-empty custom dataset
		this.dataPrimary = this.getFirstCustomDataset(data);

		return data;
	}

	/**
	 * Convert dates into legacy structure
	 */
	convertSeriesDataDates(dates) {
		return dates;
	}

	/**
	 * Convert newer chartapi data format into legacy structure
	 */
	convertSeriesDataResponse(dataIn) {
		const components = dataIn.Elements;
		const elements = {};
		let label;

		if (dataIn.Dates) {
			dataIn.Dates = this.convertSeriesDataDates(dataIn.Dates);
		}

		for (const component of components) {
			label = (component.Label || component.Id).split('-')[0];
			elements[label] = this.convertSeriesComponent(component);
		}

		dataIn.Elements = elements;

		delete dataIn.ComponentSeries;

		return dataIn;
	}

	/**
	 * extract component data from API value.
	 */
	convertSeriesComponent(component) {
		const componentOut = {};
		const label = (component.Label || component.Id).split('-')[0];
		const series = {};
		component.ComponentSeries.forEach(seriesItem => {
			series[seriesItem.Type.toLowerCase()] = {
				min: seriesItem.MinValue,
				max: seriesItem.MaxValue,
				minDate: seriesItem.MinValueDate,
				maxDate: seriesItem.MaxValueDate,
				values: seriesItem.Values,
				dates: this.convertSeriesDataDates(seriesItem.Dates || [])
			};
		});

		componentOut[component.Type] = {
			OverlayIndicators: [],
			CompanyName: component.CompanyName,
			Currency: component.Currency,
			Dates: this.convertSeriesDataDates(component.Dates || []),
			DisplaySymbol: component.DisplaySymbol,
			ExchangeId: component.ExchangeId,
			Error: component.Error,
			Id: label,
			IssueType: component.IssueType,
			Positions: null,
			QuoteTimeLast: -1,
			Series: series,
			Status: component.Status,
			StatusString:
				component.Message?.length
					? component.Message
					: component.Status === 1
						? 'Success'
						: '',
			Symbol: component.Symbol,
			TimeZoneLabel: component.TimeZoneLabel,
			TimingData: component.TimingData,
			TimingRender: component.TimingRender,
			Type: component.Type,
			UtcOffsetMinutes: component.UtcOffsetMinutes,
			XSegments: null
		};

		// Price indicators will have a NormalizeValues property (CH-4821)
		if (component.NormalizeValues) {
			componentOut[component.Type].NormalizeValues = component.NormalizeValues;
		}

		// store metadata in separate collection
		if (component.Meta) {
			this.metaData[component.Label] = component.Meta;
		}

		// store timezone label
		if (
			component.Symbol === this.params.symbol &&
			component.TimeZoneLabel
		) {
			this.state.timezoneLabel = component.TimeZoneLabel;
		}

		if (component.OverlayIndicators) {
			for (const overlay of component.OverlayIndicators) {
				componentOut[component.Type].OverlayIndicators.push(
					this.convertSeriesComponent(overlay)
				);
			}
		}

		return componentOut;
	}

	/**
	 * The final stage of loading new data:
	 * process ChartAPI response
	 * convert response to new api format
	 * precalculate label datasets
	 * update parent class x-scales
	 * redraw
	 * @param {Object} dataIn
	 * @param {String} error
	 * @param {Function} callback
	 * @param {Object} args
	 * @return {Function}
	 */
	_processChartApiResponse(dataIn, error, callback, args = {}) {

		if (error) {
			return this._processChartApiNullResponse(
				dataIn,
				error,
				callback,
				args
			);
		}

		// CISC-1309 Update ruler thresholds if they differ from client side params.
		// This should be rare, but can occur if the ruler thresholds were updated on the server side, like when restricting
		// the size of the XAxis for certain user tiers.  The below will keep the client side params in sync.
		// So far only rulerInterdayStart is needed, but the rest could be added if necessary.
		if (dataIn?.MetaData?.RulerInterdayStart) {
			this.params.rulerInterdayStart = Number(dataIn.MetaData.RulerInterdayStart);
		}

		// convert from chartapi format
		if (dataIn?.Elements && typeof dataIn.Elements.length === 'number') {
			dataIn = this.convertSeriesDataResponse(dataIn);
		}

		// get result in modcharts format
		const result = this._processChartApiData(dataIn, args);

		if (
			this.timeService.status === 0 &&
				dataIn.TimeService.Status === 0 &&
				!args.silent
		) {
			// timeservice request was null
			return this._processChartApiNullResponse(
				dataIn,
				error,
				callback,
				args
			);
		} else if (
			!this.dataPrimary ||
				this.dataPrimary.length === 0 ||
				result === null
		) {
			// main dataset was null
			return this._processChartApiNullResponse(
				dataIn,
				error,
				callback,
				args
			);
		} else if (
			this.params.minMarketOpen &&
			this.params.days === 1 &&
			this.dataPrimary.length === 1 &&
			!args.silent
		) {
			// 1 day charts with only a single available datapoint should re-request with 2 days (CW-505)
			this.resetDomain();

			// update days param
			this.params.days = 2;

			return this.loadData(null, {
				days: 2
			});

		} else if (
			this.dataPrimary.length < 2 &&
				!Object.keys(this.xAxisDomainAdjustmentCategories).length &&
				!args.silent
		) {
			// line/mountain charts shouldn't attempt to draw until we have at least two datapoints
			return this._processChartApiNullResponse(
				dataIn,
				error,
				callback,
				args
			);
		}

		// create exchangeDates collection based on the params.days and/or first/last data dates
		if (!this.exchangeDates || !this.exchangeDates.length) {
			this.exchangeDates = this.panels[0].xAxis.getExchangeRuler();

			// create a full collection of fresh labels.
			this.panels[0].xAxis.labeler._createLabels();
		}

		// valid data result.  either prepend, append, or replace data
		if (args.prepend) {
			// prepend to beginning (backfill)
			this.data = this.prependChartAPIResponse(result, this.data);
		} else if (args.silent) {
			// append to end (polling)
			this.data = this.appendChartAPIResponse(result, this.data);
		} else {
			// replace everything (all new data)
			this.data = result;
		}

		// calculate the diff/hollow properties here so they don't have to be recalculated during renders
		this.data = this.getCandleData(this.data);

		// clean up any indicators associated with invalid/failed symbols
		this.data = this.processInvalidSymbols(this.data);

		// get an appropriate xAxis domain for this data
		const domain = this.getDefaultDomain();

		// Update each xAxis domain
		this._updateXAxisScaleDomains(domain[0], domain[1]);

		// resize / set panel dimensions
		if (!args.silent) this.resize();

		const status = (error || result === null) && !args.prepend ? 0 : 1;

		args = { status, ...args };

		// optionally set up a once() listener for the next render event
		this.handlePanelYAxisAutoWidth();

		// render
		this.postProcessRender(args);

		this.panels.forEach(panel => {
			try {
				panel.updateLegend();
			} catch (e) {
				console.error('Error updating legend for panel', panel, e);
			}
		});

		this.loadingStop();

		this.setZoomEnabled(this.params.zoomEnabled);

		// increment load count
		this.state.dataLoadCount += 1;

		// reset 401 counter
		this.state.invalidTokenCount = 0;

		// inform whomever that data request has completed, and include the final point data
		if (this.dataPrimary.length && this.dataPrimary[this.dataPrimary.length - 1].date) {
			// exclude some variables from the regular crosshair data
			// eslint-disable-next-line no-unused-vars
			const { activePanel, coords, panels, ...rest } = buildCrosshairData(
				this.panels[0],
				this.dataPrimary[this.dataPrimary.length - 1].date
			);
			args.lastDataPoint = { ...rest };
		}

		this.eventEmitter.emit(types.DATA_LOAD_COMPLETE, args);

		// data density estimation (buggy)
		this.eventEmitter.emit(types.DATA_DENSITY, this.dataDensity);

		// restart streaming if enabled
		if (this.params.stream.enabled) this.stream.start();

		if (callback) return callback(this);
	}

	// If using auto YAxis width, then - a single time - do a second render to pick up flag widths.
	// I couldn't see a better way to do this, since there's a chicken-and-egg situation where the
	// flags need to render first before we can measure their width, and then update the panel
	// padding on the second render pass.  This will add ~16ms to the first load time.
	handlePanelYAxisAutoWidth() {
		if (
			this.params.panelYAxisAutoWidth &&
			!this.eventEmitter.listeners('FLAG_RENDER_LASTCLOSE').length // if custom flags are used, don't try to auto-size since we don't know the custom flag width
		) {
			// note the once(), not on()
			this.eventEmitter.once(types.RENDER, () => {
				this.resize();
				this.render();
			});
		}
	}

	/** Render the chart after processing data */
	postProcessRender(args) {
		const animateType = this.params.animation;
		const loadCount = this.state.dataLoadCount;

		// render chart (normal or animated)
		if (animateType && !args.silent && !args.prepend) {
			if (animateType === 'first' && loadCount === 0) {
				// first time animation
				this.animate();
			} else if (animateType === 'staged' && loadCount === 0) {
				// stage the animation but don't fully render
				this.render({ clipWidth: 0 });
			} else if (animateType === 'all') {
				// animate new data
				this.animate();
			} else {
				// default render
				this.render();
			}
		} else {
			// default render
			this.renderQueue();
		}
	}

	/**
	 * handle several types of data failures
	 */
	_processChartApiNullResponse(dataIn, error, callback, args) {
		this.loadingStop(false); // Don't emit BOUNDARY_STATUS event

		// track the count of consecutive 401s
		if (error && error.status === 401) {
			this.state.invalidTokenCount++;
		}

		const statusReturn = {
			status: 0,
			statusMsg: '',
			invalidTokenCount: this.state.invalidTokenCount
		};

		if (args.prepend) {
			statusReturn.statusMsg = 'Prepend error - result was null';
			this.eventEmitter.emit(types.DATA_PREPEND_ERROR, {
				status: statusReturn,
				error
			});
		} else if (args.silent) {
			statusReturn.statusMsg = `Poll error ${new Date()}`;
			this.eventEmitter.emit(types.DATA_POLL_ERROR, {
				status: statusReturn,
				error
			});
		} else {
			this.status = 0;
			this.clearPanels();
			this.data = null;
			this.showMessageChartNotAvailable({
				description: 'Invalid chart data response',
				error
			});

			statusReturn.statusMsg = `Invalid chart data : ${JSON.stringify(error || {})}`;
			this.eventEmitter.emit(types.DATA_LOAD_ERROR, {
				status: statusReturn,
				error
			});
		}

		// clear ongoing XHR reference
		if (this.xhrCancelSource) {
			this.xhrCancelSource.cancel();
		}
		this.dataXHR = null;

		// re-enable zoom
		this.setZoomEnabled(this.params.zoomEnabled);

		// inform whomever that data request has completed
		this.eventEmitter.emit(types.DATA_LOAD_COMPLETE, args);

		this.warn({
			status: statusReturn,
			error
		});

		if (callback) return callback(statusReturn.status);

		return statusReturn;
	}

	// invalid indicators
	// if a symbol was invalid, remove any indicators assigned to it, and remove from symbolCompare collection
	processInvalidSymbols(data) {
		const panel = this.panels[0];

		if (this.params.symbolCompare.length) { // backward compatibility (CH-1504)
			for (let i = this.params.symbolCompare.length - 1; i >= 0; i--) {
				const symbolCompare = this.params.symbolCompare[i];
				if (!data[symbolCompare]) {
					this.params.symbolCompare.splice(i, 1);
					for (let x = panel.indicators.length - 1; x >= 0; x--) {
						if (panel.indicators[x].params.symbol === symbolCompare) {
							panel.removeIndicator(panel.indicators[x]);
						}
					}
				}
			}
		} else {
			for (let i = this.comparisons.length - 1; i >= 0; i--) {
				if (!data[this.comparisons[i]]) {
					for (let x = panel.indicators.length - 1; x >= 0; x--) {
						if (panel.indicators[x].params.symbol === this.comparisons[i]) {
							panel.removeIndicator(panel.indicators[x]);
						}
					}
				}
			}
		}

		return data;
	}

	// add result to beginning of current dataset
	prependChartAPIResponse(result, data) {
		Object.keys(data).forEach(symbol => {
			const resultSymbolData = result[symbol];
			if (resultSymbolData) {
				Object.entries(resultSymbolData).forEach(([row, dataPoint]) => {
					// row === "price"
					Object.entries(dataPoint).forEach(([uid, series]) => {
						// data.GE.price.<uid>
						if (series && series.length) {
							for (let x = series.length - 1; x >= 0; x--) {
								const newDate = series[x].date;
								const dataSet = data[symbol] && data[symbol][row] && data[symbol][row][uid];
								// only splice in the new datapoint if it's earlier than the first date
								if (dataSet) {
									if (!dataSet.length) {
										dataSet.push(series[x]);
									} else if (
										newDate < dataSet[0].date
									) {
										dataSet.splice(
											0,
											0,
											series[x]
										);
									}
								}
							}
						}
					});
				});
			}
		});

		return data;
	}

	// add result to end of current dataset
	appendChartAPIResponse(result, data) {
		let x = 0;
		let currData = [];

		Object.keys(data).forEach(symbol => {
			if (result[symbol]) {
				Object.keys(result[symbol]).forEach(row => {
					// row === "price"
					const dataPoint = result[symbol][row];
					Object.keys(dataPoint).forEach(uid => {
						const series = dataPoint[uid];
						// this === this.data.GE.price.<uid>

						currData = data[symbol][row][uid];

						for (x = 0; x < series.length; x++) {
							if (!currData.length) {
								// append the new datapoint if it's new
								currData.push(series[x]);
							} else if (
								series[x].date -
									currData[currData.length - 1].date ===
								0
							) {
								// replace the last datapoint if it's == last existing date
								currData[currData.length - 1] = series[x];
							} else if (
								series[x].date >
								currData[currData.length - 1].date
							) {
								// append the new datapoint if it's > last existing date
								currData.push(series[x]);
							}
						}
					});
				});
			}
		});

		return data;
	}

	/**
	 * similar to _processChartApiResponse, this is the final stage of loading a chart with custom data.
	 * errors are handled, "this.data" is populated (via _processCustomData), panel domains reset,
	 * exchangeDates collection is created, label collections created, loading graphic removed and final callbacks called.
	 */
	_processCustomResponse(dataIn, error, callback, args = {}) {
		let isValid = true;

		if (error) {
			this.warn(`XHR error ${error.status || '-1'}: ${error.statusText || 'Unknown status'}`);
			this.warn(
				error.responseText ? error.responseText : 'Unknown error'
			);
			isValid = false;
		} else {
			this.clearMessage();

			// ensure customData contains at least one valid dataset
			const validationResult = this._validateCustomData(dataIn);

			if (!validationResult.isValid) {
				// console.error('Validation errors:', validationResult.errors);

				if (Object.keys(validationResult.validDatasets).length === 0) {
					// If there are no valid custom datasets, treat it as a null response
					return this._processChartApiNullResponse(dataIn, error, callback, args);
				}
			}

			// valid data result.  replace this.data
			this.data = this._processCustomData(dataIn, args);

			// create exchangeDates collection
			if (!this.exchangeDates || !this.exchangeDates.length) {
				this.exchangeDates = this.panels[0].xAxis.getCustomRuler(this.getFirstCustomDataset(this.data));

				// create a full collection of fresh labels
				this.panels[0].xAxis.labeler._createLabels();
			}

			const domain = this.getDefaultDomain();

			// Update each xAxis domain
			this._updateXAxisScaleDomains(domain[0], domain[1]);

		}

		// resize / set panel dimensions
		this.resize();

		const status = error && !args.prepend ? 0 : 1;

		args = { ...{ status }, ...args };

		this.status = status;

		// don't attempt to render invalid datasets
		if (isValid) this.postProcessRender(args);

		this.panels.forEach(panel => panel.updateLegend());

		this.loadingStop(isValid); // Check to see if BOUNDARY_STATUS event should be emitted

		this.setZoomEnabled(this.params.zoomEnabled);

		this.eventEmitter.emit(types.DATA_LOAD_COMPLETE, args);

		if (callback) return callback(this);
	}

	/**
	 * Validate the custom dataset
	 * @param {object} data
	 * @returns {Object} An object containing valid datasets and a list of errors
	 */
	_validateCustomData(data) {
		const result = {
			isValid: true,
			validDatasets: {},
			errors: []
		};

		if (!data || typeof data !== 'object' || !Object.keys(data).length) {
			const errorMsg = 'No custom data found or data is not an object';
			this.warn(errorMsg);
			result.isValid = false;
			result.errors.push(errorMsg);
			return result;
		}

		const domainAdj = Object.keys(this.xAxisDomainAdjustmentCategories || {}).length;

		for (const [key, value] of Object.entries(data)) {
			let datasetIsValid = true;

			// Validate key
			if (typeof key !== 'string' || key.trim().length === 0) {
				const errorMsg = `Invalid custom data key: '${key}'`;
				this.warn(errorMsg);
				result.errors.push(errorMsg);
				datasetIsValid = false;
			}

			// Validate value
			if (!Array.isArray(value)) {
				const errorMsg = `Custom dataset '${key}' is not an array`;
				this.warn(errorMsg);
				result.errors.push(errorMsg);
				datasetIsValid = false;
			} else {
				if (value.length === 0) {
					const errorMsg = `Custom dataset '${key}' has no datapoints`;
					this.warn(errorMsg);
					result.errors.push(errorMsg);
					datasetIsValid = false;
				} else if (!domainAdj && value.length < 2) {
					const errorMsg = `Custom dataset '${key}' must contain at least two datapoints when using line or fill markerTypes`;
					this.warn(errorMsg);
					result.errors.push(errorMsg);
					datasetIsValid = false;
				}
			}

			// If dataset is valid, add it to the validDatasets object
			if (datasetIsValid) {
				result.validDatasets[key] = value;
			} else {
				// Mark overall result as invalid if any dataset fails
				result.isValid = false;
			}
		}

		// Check if there are any valid datasets
		if (Object.keys(result.validDatasets).length === 0) {
			const errorMsg = 'All datasets are invalid. No valid custom data available.';
			this.warn(errorMsg);
			result.errors.push(errorMsg);
			result.isValid = false;
		}

		return result;
	}

	/**
	 * convert local date to UTC
	 * @method getUTCDate
	 * @param {Date} date
	 * @return {Date}
	 */
	getUTCDate(date) {
		date = new Date(date);

		return new Date(
			date.getUTCFullYear(),
			date.getUTCMonth(),
			date.getUTCDate(),
			date.getUTCHours(),
			date.getUTCMinutes(),
			date.getUTCSeconds()
		);
	}

	/**
	 * Convert arrays of numbers from raw strings or OA Dates
	 * @param {Object[]} data
	 * @param {Number} utcOffset The UTC offset in minutes (should be sourced from chartapi request)
	 * Note: if a 24-hour axis is needed, the utcoffset should be the same for all symbols.
	 * If symbols should all be plotted in exchange local time, pass individual respective offsets.
	 * The UTC offset is not applied to interday charts.
	 * @return {Object[]}
	 * @private
	 */
	convertChartApiDates(data, utcOffset) {
		const dataOut = [];
		let date;
		let hours;
		let minutes;
		let seconds;
		let pct;
		let i;
		const iLen = data.length;
		const intradayISO = /^(\d{4})-(\d\d)-(\d\d)T(\d\d):(\d\d):(\d\d)(\.*\d*)/;
		const interdayISO = /^(\d{4})-(\d\d)-(\d\d)$/;
		for (i = 0; i < iLen; i++) {

			if (interdayISO.test(data[i])) {

				date = new Date(
					Number(RegExp.$1),
					Number(RegExp.$2) - 1,
					Number(RegExp.$3)
				);

				if (this.state.isIntraday) {
					// utc offset to get dates back to local exchange time
					date.setUTCMinutes(date.getUTCMinutes() + utcOffset);
				}

				date.setTime(Math.round(date.getTime() / 60000) * 60000);
				dataOut.push(date);

			} else if (intradayISO.test(data[i])) {

				hours = Number(RegExp.$4);
				minutes = Number(RegExp.$5);
				seconds = Number(RegExp.$6);
				pct = Number(RegExp.$7);

				date = new Date(
					Number(RegExp.$1),
					Number(RegExp.$2) - 1,
					Number(RegExp.$3),
					hours,
					minutes,
					seconds
				);

				// if percent of second is > 0.5, round up to next second
				if (pct >= 0.5) {
					date.setUTCSeconds(date.getUTCSeconds() + 1);
				}

				if (this.state.isIntraday) {
					// utc offset to get dates back to local exchange time
					date.setUTCMinutes(date.getUTCMinutes() + utcOffset);
				}

				date.setTime(Math.round(date.getTime() / 60000) * 60000);

				dataOut.push(date);
			} else if (/^\d{5}$/.test(data[i]) || /\d{5}\.\d+/.test(data[i])) {
				date = this.msToJsDate(data[i]);

				if (this.state.isIntraday) {
					// utc offset to get dates back to local exchange time
					date.setUTCMinutes(date.getUTCMinutes() + utcOffset);
				}

				dataOut.push(date);
			}
		}

		return dataOut;
	}

	/**
	 * Convert OA Dates to JS
	 * @method msToJsDate
	 * @param {int} msDate
	 * @return {Date}
	 */
	msToJsDate(msDate) {
		const jO = new Date((msDate - 25569) * 86400000);
		const tz = jO.getTimezoneOffset();
		const ms = (msDate - 25569 + tz / (60 * 24)) * 86400000;

		// round to nearest second
		return new Date(Math.round(ms / 1000) * 1000);
	}

	/**
	 * Convert JS Date to OA Date
	 * @method jsToMsDate
	 * @param {Date} jsDate
	 * @return {int}
	 */
	jsToMsDate(jsDate) {
		return (
			(jsDate - new Date(Date.UTC(1899, 11, 30))) / (24 * 60 * 60 * 1000)
		);
	}

	/**
	 * convert dynamic ChartAPI json into modcharts standardized data structure.
	 * @method _processChartApiData
	 * @param {object} dataIn
	 * @param {object} [args]
	 * @return {object}
	 */
	_processChartApiData(dataIn, args = {}) {
		// check for any hard failures
		if (
			!dataIn ||
			!dataIn.Dates ||
			!dataIn.Elements ||
			(!args.prepend && !args.silent && !dataIn.TimeService)
		) {
			return null;
		}

		this._processChartApiElementsFailure(dataIn);

		// chartapi will return the normalizedate if used either implicitly in the service logic or explicitly as an input
		if (dataIn && dataIn.NormalizeDate) {
			if (/^\d{5}$/.test(dataIn.NormalizeDate.toString())) {
				this.params.normalizeDate = this.msToJsDate(
					dataIn.NormalizeDate
				);
			} else {
				this.params.normalizeDate = new Date(dataIn.NormalizeDate);
			}

			// deprecated, use dataIn.NormalizeValues instead
			if (dataIn.NormalizeCloseValue) {
				this.params.normalizeValue = Number(dataIn.NormalizeCloseValue);
			}

			// store API NormalizeValues - as of KB-739 this should always be returned.
			if (dataIn.NormalizeValues) {
				this.params.normalizeValues = dataIn.NormalizeValues;
			}
		}

		this.clearMessage();

		const elementsIn = this.getChartAPIElements(dataIn);
		const dataOut = this.createDataSeries(elementsIn, args);

		// parse timeservice data
		this.processTimeServiceData(dataIn.TimeService, args);

		// set primary data reference and overall status
		if (!args.prepend && !args.silent) {
			this.dataPrimary = this._getPrimaryDataReference(
				elementsIn,
				dataOut
			);
			this.status = this.dataPrimary.length ? 1 : 0;
		}

		return dataOut;
	}

	/**
	 * Process element-level failures
	 * @param {Object} data - Object with response information from chart data request
	 */
	_processChartApiElementsFailure(data) {
		if (
			data.Elements &&
			(typeof this.params.endOffsetDays === 'undefined' ||
				this.params.endOffsetDays === 0)
		) {
			Object.keys(data.Elements).forEach(key => {
				// Each item in Elements is a KVP with the indicator type as the key, and the element info
				// as the value. We need to look at the element info, so dig down one more level.
				const elementContainer = data.Elements[key];

				Object.keys(elementContainer).forEach(elementName => {
					const element = elementContainer[elementName];
					const indicator = this.getIndicatorByUid(element.Id);
					const panel = indicator && indicator.panel;

					// Don't warn on error status code 130 (Invalid indicator was requested). See CH-298, CH-349 for more info.
					if (element.Error && element.Error.Code !== '130') {
						this.warn(`Element error: ${element.Symbol || ''} : ${element.Error.Code} : ${element.Error.Message} : Element Id: '${elementName}'`);
						if (this.eventEmitter.listeners(types.DATA_ELEMENT_ERROR).length) {
							// Custom error handling
							this.eventEmitter.emit(types.DATA_ELEMENT_ERROR, {
								element,
								indicator,
								panel
							});
						} else if (panel && !panel.isUpper) {
							// Default error handling
							panel.renderErrorMessage(
								translate(
									this.locale,
									'chart.unavailableIndicator',
									[indicator ? indicator.params.name : 'Indicator']
								)
							);
						}
					} else if (panel) {
						// The indicator is working again (can happen if the symbol changed)
						panel.clearErrorMessage();
					}
				});
			});
		}
	}

	/**
	 * Flatten uid/elementId together for easier parsing and attach dates (this is step 1).
	 * Dates can live in three places: global, element-level, or series-level.
	 * The majority of indicators with series-level dates share the same dates across all series.
	 * @param {Object} data - Object with response information from chart data request
	 */
	getChartAPIElements(data) {
		const elements = [];
		let globalDates = [];
		let globalUTCOffset;

		// find global utc offset and generate globalDates
		Object.keys(data.Elements).forEach(symbol => {
			const dataSet = data.Elements[symbol];
			Object.keys(dataSet).forEach(field => {
				const value = dataSet[field];
				if (
					typeof globalUTCOffset !== 'number' &&
					value.Symbol === this.params.symbol &&
					value.Type === 'price'
				) {
					globalUTCOffset = value.UtcOffsetMinutes;
				}

				if (
					typeof globalUTCOffset !== 'number' &&
					this.xref[this.params.symbol] &&
					typeof this.xref[this.params.symbol].utcOffset === 'number'
				) {
					globalUTCOffset = this.xref[this.params.symbol].utcOffset;
				}

				// generate the globalDates collection once.
				if (
					!globalDates.length &&
					typeof globalUTCOffset === 'number'
				) {
					globalDates = this.convertChartApiDates(
						data.Dates,
						globalUTCOffset
					);
				}
			});
		});

		// extract elements from the returned data
		Object.keys(data.Elements).forEach(symbol => {
			const dataSet = data.Elements[symbol];
			Object.keys(dataSet).forEach(field => {
				const value = dataSet[field];
				// regular element
				elements.push(
					this.getChartAPIElement(
						value,
						data,
						globalUTCOffset,
						globalDates
					)
				);

				// child elements
				const overlayIndicators = value.OverlayIndicators || [];
				overlayIndicators.forEach(indicator => {
					elements.push(
						this.getChartAPIElement(
							this.getFirstDataset(indicator),
							data,
							globalUTCOffset,
							globalDates
						)
					);
				});
			});
		});

		return elements;
	}

	/**
	 * @method getChartAPIElement
	 * extract a single element from returned API data, and populate seriesDates for any collections that use
	 * element-level or series-level dates instead of global dates.
	 */
	getChartAPIElement(element, data, globalUTCOffset, globalDates) {
		let isValidElement = false;
		const newElement = {
			currency: element.Currency,
			uid: element.Id,
			series: {},
			seriesDates: {},
			issueType: element.IssueType,
			companyName: element.CompanyName,
			exchangeId: element.ExchangeId,
			status: element.Status,
			symbol: element.Symbol,
			displaySymbol: element.DisplaySymbol,
			type: element.Type,
			utcOffset: element.UtcOffsetMinutes,
			meta: element.Meta
		};

		// Price elements will return a NormalizeValues collection (CH-4821)
		// Store off element-level NormalizeValues into state for use in crosshair and export data later.  (we don't need to add it to the dataset series.)
		if (element.NormalizeValues) {
			if (!this.state.normalizeValues) {
				this.state.normalizeValues = {
					indicators: {}
				};
			}
			this.state.normalizeValues.indicators[element.Id] = {
				...element.NormalizeValues,
				Symbol: element.Symbol
			};
		}

		// Store company information in the xref collection for future use
		if (
			typeof newElement.utcOffset === 'number' &&
			newElement.exchangeId &&
			newElement.issueType &&
			newElement.symbol &&
			!this.xref[newElement.symbol]
		) {
			this.xref[newElement.symbol] = {
				companyName: newElement.companyName,
				currency: newElement.currency,
				exchangeId: newElement.exchangeId,
				issueType: newElement.issueType,
				meta: newElement.meta,
				displaySymbol: newElement.displaySymbol,
				utcOffset: newElement.utcOffset
			};
		}

		// get the union of all element dates and series dates
		const elDates = element.Dates && element.Dates.length;
		let datesCombined = elDates ? element.Dates : data.Dates;
		const elementDates = elDates
			? this.convertChartApiDates(element.Dates, globalUTCOffset)
			: null;

		// splits are the only events with series-level dates (AC-101)
		if (newElement.type === 'splits') {
			datesCombined = [];
		}

		// extract series data (eg. if the element.Type is 'price', it will have four 'Series': open, high, low, close)
		Object.keys(element.Series).forEach(seriesId => {
			const series = element.Series[seriesId];

			if (series.values && series.values.length) {
				const newSeriesDates = {};
				const newElementDates = {};
				let seriesDates = [];

				if (/psarbuy|psarsell/.test(seriesId)) {

					// special case: ignore psarbuy/psarsell series
					return true;

				} else if (seriesId === 'chikouspan') {

					// special case: chikouspan needs to retain its own dates (CW-3069)
					// see _getDataMap in the IchimokuCloudIndicator class for more info
					newElement.datesChikou = this.convertChartApiDates(
						series.dates,
						newElement.utcOffset
					);
					newElement.series[seriesId] = series.values;

				} else if (series.dates && series.dates.length) {

					// use series-level dates
					seriesDates = this.convertChartApiDates(
						series.dates,
						newElement.utcOffset
					);
					datesCombined = datesCombined.concat(series.dates);

					seriesDates.forEach((date, idx) => {
						newSeriesDates[date] = series.values[idx];
					});

					newElement.seriesDates[seriesId] = newSeriesDates;
					newElement.series[seriesId] = [];

				} else if (element.Dates && element.Dates.length) {

					datesCombined = datesCombined.concat(element.Dates);

					elementDates.forEach((date, idx) => {

						if (newElementDates[date]) { // duplicate element-level date. should only occur for events (CH-608)

							if (!Array.isArray(newElementDates[date])) {
								// convert to array
								newElementDates[date] = [newElementDates[date]];
							}

							newElementDates[date].push(series.values[idx]);

						} else { // normal element-level date

							newElementDates[date] = series.values[idx];
						}
					});

					newElement.seriesDates[seriesId] = newElementDates;
					newElement.series[seriesId] = [];

				} else {
					// regular element with no series/element dates
					newElement.series[seriesId] = series.values;
				}

				isValidElement = true;
			}
		});

		// sort and remove duplicates
		datesCombined = datesCombined
			.sort()
			.filter((item, pos, ary) => !pos || item !== ary[pos - 1]);

		// set element dates
		if (isValidElement) {
			if (!datesCombined.length) {
				// use global dates if there were no dates at the element or series level

				newElement.dates = globalDates;
			} else if (datesCombined.length) {
				// use custom dates. these will be either element-level dates, or a combination of series-level dates.

				newElement.dates = this.convertChartApiDates(
					datesCombined,
					globalUTCOffset
				);
			}
		}

		// store off currency
		this.state.currency = newElement.currency;

		return newElement;
	}

	/**
	 * @method createDataSeries
	 * step 2: now pivot elements into individual datapoints that contain a date and one value per series (eg. open, high, low, close)
	 * Note: use dates from the seriesDates collection where applicable.  These are series that have different dates than GlobalDates, either at series or element level.

			Before conversion:
		[
			{
				type: "price",
				symbol: <symbol>,
				dates: [<date>,<date>,...],
				series: {
					open: [1,2,3,4,...],
					high: [1,2,3,4,...],
					low: [1,2,3,4,...],
					close: [1,2,3,4,...]
				}
			},
			{
				type: "ichimokucloud",
				symbol: <symbol>,
				dates: [<date>,<date>,...],
				seriesDates: {
					"senkouspana": [{<date>: 13687.725},{<date>: 13687.725},{<date>: 13687.725},...],
					"senkouspanb: [{<date>: 13687.725},{<date>: 13687.725},{<date>: 13687.725},...]
				},
				series: {
					kijunsen: [1,2,3,4,...],
					tenkansen: [1,2,3,4,...],
					chikouspan: [1,2,3,4,...],
					senkouspana: [],
					senkouspanb: []
				}
			}
		]

			After conversion:
			{
			<symbol>: {
				"price":
					<uid>:
						[
							{
								date: <date>,
								dateIndex: <dateIndex>,
								open: 12.14,
								high: 22.31,
								low: 11.41,
								close: 22.30
							},
							{
								date: <date>,
								dateIndex: <dateIndex>,
								open: 13.14,
								high: 13.19,
								low: 12.98,
								close: 13.15
							}
						]
				},
				"ichimokucloud":
					<uid>:
						[
							{
								date: <date>,
								dateIndex: <dateIndex>,
								dateChikou: <date>,
								dateIndexChikou: <dateIndexChikou>,
								chikouspan: 41.31,
								kijunsen: 41.30,
								senkouspana: 29.41,
								senkouspanb: 20.11,
								tenkansen: 39.99
							},
							{
								date: <date>,
								dateIndex: <dateIndex>,
								dateChikou: <date>,
								dateIndexChikou: <dateIndexChikou>,
								chikouspan: 41.32,
								kijunsen: 41.29,
								senkouspana: 49.50,
								senkouspanb: 47.23,
								tenkansen: 40.21
							}
						]
				}
			}
			}

	*/
	createDataSeries(elements, args) {
		const dataOut = {};
		let newDatapoint = {};
		let isValid = false;
		let seriesValue = 0.0;

		// price, sma, volume etc.
		elements.forEach(element => {
			const symbol = element.symbol;
			const currency = element.currency;
			const uid = element.uid;

			if (!dataOut[symbol]) {
				dataOut[symbol] = {};
			}

			if (!dataOut[symbol][element.type]) {
				dataOut[symbol][element.type] = {};
			}

			// grouping by uid isn't really necessary and should be removed one day
			dataOut[symbol][element.type][uid] = [];

			// loop over each element's dates collection and collect into individual datapoints
			for (let i = 0; i < (element.dates || []).length; i++) {
				// only add new datapoints that contain date and value(s)
				isValid = false;

				// stub out a new datapoint with this date
				newDatapoint = {
					date: element.dates[i]
				};

				// add all series (open, high, low close) to this new datapoint
				Object.keys(element.series).forEach(seriesId => {
					const seriesItem = element.series[seriesId];
					// special case: ignore new previousclose values on prepends
					if (
						seriesId === 'previousclose' &&
						args.prepend &&
						!this.state.isIntraday
					) {
						return true;
					}

					if (element.seriesDates[seriesId]) {
						// special case: pull value from the seriesDates collection (eg. senkouspan, advancedecline, percentb, etc)
						seriesValue = element.seriesDates[seriesId][element.dates[i]];
						newDatapoint[seriesId] = typeof seriesValue !== 'undefined' ? seriesValue : NaN;
					} else {
						// default
						newDatapoint[seriesId] = seriesItem[i];
					}

					// special case: chikouspan needs to retain its own dates (CW-3069)
					// see _getDataMap in the IchimokuCloudIndicator class for more info
					if (
						seriesId === 'chikouspan' &&
						typeof element.datesChikou[i] !== 'undefined'
					) {
						newDatapoint.dateChikou = element.datesChikou[i];
					}

					isValid = true;
				});

				// add currency to dividend datapoints
				if (
					element.type === 'dividends' ||
					element.type === 'dividendscomplete'
				) {
					newDatapoint.currency = currency;
				}

				// if valid, append to main dataset
				if (isValid) {
					// dataOut["205778"]["sma"]["e88daf17"]
					dataOut[symbol][element.type][uid].push(newDatapoint);
				}
			}
		});

		return dataOut;
	}

	/**
	 * @method getCandleData
	 * precalculate diff and hollow to be used for candlestick/ohlc/hlc/posneg volume indicator rendering
	 */
	getCandleData(data) {
		const primary = this.getFirstDataset(data[this.params.symbol]['price']) || [];

		primary.forEach((row, idx) => {
			// precalculate the candle hollow flag
			row.hollow = row.close > row.open ? 1 : 0;

			// precalculate the candle diff value
			if (idx > 0) {
				// diff from previous neighbor
				row.diff = row.close - primary[idx - 1].close;
			} else {
				// calculate diff for very first point
				if (this.panels[0].isNormalized) {
					// first normalized diff is just the first close point
					row.diff = row.close;
				} else {
					// first regular diff is compared against normalizeValues.Close
					const prevClose = this.params.normalizeValues
						? this.params.normalizeValues.Close
						: null;

					if (typeof prevClose === 'number') {
						row.diff = row.close - prevClose;
					} else {
						row.diff = 0;
					}
				}
			}
		});

		return data;
	}

	/** Store off primary dataset (convenience) */
	_getPrimaryDataReference(elements, data) {
		let primary = [];
		for (const element of elements) {
			const { symbol, type, uid } = element;
			if (!primary.length && symbol === this.params.symbol) {
				primary = data[symbol][type][uid];
				break;
			}
		}

		return primary;
	}

	/** Parse the raw TimeService data into a JSON structure */
	processTimeServiceData(data = {}, args = {}) {
		const dayKeys = [
			'Sunday',
			'Monday',
			'Tuesday',
			'Wednesday',
			'Thursday',
			'Friday',
			'Saturday'
		];
		const dataTypicalIn = data.TypicalSessions || {};
		const dataTypical = [];

		let dayOfWeek;
		let minSession;
		let maxSession;
		let newSession;
		let typicalSessions;
		let rulerSession = {};
		let sessionBreak = false;

		if (!args.prepend && !args.silent) {
			if (this.state.isIntraday) {
				this.state.typicalSessions = null; // typical sessions for one single day

				// loop over each day of the week
				for (let dayIdx = 0; dayIdx < dayKeys.length; dayIdx++) {
					dataTypical[dayIdx] = {
						sessions: [], // individual sessions, used by markers
						rulerSessions: {} // one session per exchange that stretches from its min and max, used by getExchangeRuler
					};

					dayOfWeek = dayKeys[dayIdx];
					minSession = Infinity;
					maxSession = -Infinity;

					// grab all sessions for this day
					typicalSessions = dataTypicalIn[dayOfWeek] || [];

					for (const typicalSession of typicalSessions) {
						// start creating a new session object
						newSession = {
							exchangeId: typicalSession.ExchangeID,
							open: typicalSession.Open,
							close: typicalSession.Close,
							offset: typicalSession.Offset,
							sessionType:
								typicalSession.SessionType ||
								'NormalSession'
						};

						// optional premarket duration
						if (
							newSession.sessionType === 'PreMarketTrading' &&
							typeof this.params.extendedHoursDurationPre === 'number'
						) {
							newSession.open =
								newSession.close -
								this.params.extendedHoursDurationPre;
						}

						// optional postmarket duration
						if (
							newSession.sessionType === 'PostMarketTrading' &&
							typeof this.params.extendedHoursDurationPost === 'number'
						) {
							newSession.close =
								newSession.open +
								this.params.extendedHoursDurationPost;
						}

						// flag session breaks for quick lookups during renders
						if (newSession.sessionType === 'SessionBreak') {
							sessionBreak = true;
						}

						// store each individual session
						dataTypical[dayIdx].sessions.push(newSession);

						// get ruler session (one session that skips all session breaks)
						rulerSession =
							dataTypical[dayIdx].rulerSessions[
								newSession.exchangeId
							];

						if (!rulerSession) {
							dataTypical[dayIdx].rulerSessions[
								newSession.exchangeId
							] = {
								open: newSession.open,
								close: newSession.close,
								offset: newSession.offset
							};
						} else {
							rulerSession.open = Math.min(
								rulerSession.open,
								newSession.open
							);
							rulerSession.close = Math.max(
								rulerSession.close,
								newSession.close
							);
						}

						// get min/max session across all exchanges for this day
						minSession = Math.min(minSession, newSession.open);
						maxSession = Math.max(maxSession, newSession.close);
					}

					// save min/max sessions; these are used when setting the initial domain of intraday charts when domain is not specified.
					if (typicalSessions.length) {
						dataTypical[dayIdx].min = minSession;
						dataTypical[dayIdx].max = maxSession;
						dataTypical[dayIdx].duration = maxSession - minSession;
					}

					// store off a typical day containing sessions
					if (dataTypical[dayIdx].sessions.length > 0) {
						// use the day with the longest session
						if (
							!this.state.typicalSessions ||
							dataTypical[dayIdx].duration >
								this.state.typicalSessions.duration
						) {
							this.state.typicalSessions = dataTypical[dayIdx];
						}
					}
				}

				this.timeService.typicalSessions = dataTypical;
			}

			const tradingDays = data.TradingDays || [];

			this.timeService.fullClosures = this.processTimeServiceClosures(
				data.FullClosures || []
			);
			this.timeService.nonTradingDays = data.NonTradingDays || [];
			this.timeService.tradingDays = this.processTradingDays(tradingDays);
			this.timeService.status = data.Status;
			this.timeService.alternateSessions = this.processAlternateSessions(data.AlternateSessions);
			this.timeService.sessionBreak = sessionBreak;

			// warn on timeservice error
			if (this.timeService.status !== 1) {
				this.warn(`TimeService error: ${this.timeService.status}`);
			}
		}
	}

	/**
	 * @method processAlternateSessions
	 * convert ChartAPI AlternateSessions to local format
	 */
	processAlternateSessions(dates = {}) {
		const out = {};
		Object.keys(dates).forEach(date => {
			out[date] = dates[date].map(session => {
				return {
					close: session.Close,
					exchangeId: session.ExchangeID,
					offset: session.Offset,
					open: session.Open,
					sessionType: 'NormalSession'
				};
			});
		});

		return out;
	}

	/**
	 * @method processTradingDays
	 * convert various ISO formats to YYYY-MM-DDTHH:MM:SSZ
	 */
	processTradingDays(days) {
		if (!days || !days.length) {
			return [];
		}

		const isLongISO = /:/.test(days[0]) && !/Z/.test(days[0]);
		const isShortISO = /^\d{4}-\d{2}-\d{2}$/.test(days[0]);

		if (isLongISO) {
			// chartapi doesn't return the "Z"

			for (let x = 0; x < days.length; x++) {
				days[x] = `${days[x]}Z`;
			}
		} else if (isShortISO) {
			// some browsers don't like the short iso form

			for (let x = 0; x < days.length; x++) {
				days[x] = `${days[x]}T00:00:00.000Z`;
			}
		}

		return days;
	}

	/**
	 * @method processTimeServiceClosures
	 * extract closures from timeservice API response
	 */
	processTimeServiceClosures(closures) {
		const closuresOut = [];

		for (const closure of closures) {
			let date;

			if (/^\d{5}/.test(closure.Date.toString())) {
				date = this.msToJsDate(closure.Date);
			} else {
				date = new Date(closure.Date);
			}

			closuresOut.push({
				date,
				name: closure.Name,
				exchangeIds: (closure.ExchangeIDs || '').split(',')
			});
		}

		return closuresOut;
	}

	/** Set zoom enabled param and register/unregister zoom handler */
	setZoomEnabled(isEnabled) {
		if (!this.zoomUtil) return;

		this.params.zoomEnabled = typeof isEnabled === 'boolean'
			? isEnabled
			: true;

		if (this.params.zoomEnabled) {
			this.zoomUtil.registerZoom(this);
			if (this.state.updateZoomCenter) {
				this.state.updateZoomCenter = false;
				this.zoomUtil.updateZoomCenter({ chart: this });
			}
		} else {
			this.zoomUtil.unregisterZoom(this);
		}
	}

	/**
	 * Updates the chart style map. Used to apply styles (for theming) after the chart has loaded.
	 * @param {Object} newStyles - Style object containing the new styles to update
	 * @param {Boolean} ignoreCurrent - Whether or not to ignore the current styles when loading the new styles.
	 * This will cause styles not defined in newStyles to fall back to default settings.
	 * @param {Boolean} doSoftUpdate - Whether or not to keep styles that have been changed from the default
	 * (i.e. when a user changes an indicator color manually)
	 */
	updateStyles(newStyles = {}, ignoreCurrent = true, doSoftUpdate = true) {
		const oldStyles = this.params.style;
		this.params.style = ignoreCurrent
			? { ...newStyles } // Ignore the current styles
			: merge({}, this.params.style, newStyles); // Merge new styles with current styles

		// Generate the default styles, applying common style overrides
		const defaultStyles = generateDefaultStyles(this.params.styleCommon);

		// Update panel styles/sizes with new changes
		const oldStyleMap = this.styleMap;
		this.styleMap = merge({}, defaultStyles, this.params.style);
		this.panels.forEach(p => p.updateStyles(oldStyles, oldStyleMap, doSoftUpdate));

		this.updatePanelHeightPercents();

		// Resize chart and render with new params
		this.resize();
		this.render();
	}

	/** Returns the upper panel, if it exists. This should only be used for debugging. */
	get p0() {
		return this.panels[0];
	}

	/** @type {axisDate} */
	get xAxis() {
		return this.panels[0] && this.panels[0].xAxis;
	}

	/** @type {axisDate} */
	get yAxis() {
		return this.panels[0] && this.panels[0].yAxis;
	}

	/** Returns an estimate of the expected number of datapoints versus the actual number of datapoints */
	get dataDensity() {
		let expectedDataSize;
		const days = this.params.days || 365;
		const period = this.params.dataPeriod || 'Day';
		const isIntraday = this.state.isIntraday;
		const tradingDayPct = 0.7;

		if (isIntraday) { // TODO Add intraday handling
			return null;
		}

		switch (period) {
			// case 'Hour': expectedDataSize = days * dayMinutes / 60; break;
			// case 'Minute': expectedDataSize = days * dayMinutes / chart.params.dataInterval; break;
			case 'Day': expectedDataSize = days * tradingDayPct; break;
			case 'Week': expectedDataSize = (days / 7) * tradingDayPct; break;
			case 'Month': expectedDataSize = (days / 30.4) * tradingDayPct; break;
			default: expectedDataSize = 1;
		}

		return {
			dataSize: this.dataPrimary.length,
			expectedDataSize,
			description: this.dataPrimary.length / expectedDataSize < 0.9 ? 'sparse' : 'good'
		};
	}

	/**
	 * @method getNextUnusedPaletteColor
	 * Given an indicator type, find all existing colors in use. Then compare to the palette array and return the next unused color.
	 * @param {string} indicatorId - Indicator id (eg. 'price')
	 * @param {string[]} palette - Array of colors (eg. 'red' or 'rgba(255, 0, 0, 0.5)')
	 */
	getNextUnusedPaletteColor(
		indicatorId = 'price',
		palette = this.styleMap.indicator[indicatorId].palette
	) {
		const indicators = this.getIndicatorsById(indicatorId);
		if (indicators.length && palette) {
			// get current indicator colors (eg. all current price indicator colors)
			const existingColors = indicators
				.filter(i => i.params.id === indicatorId)
				.map(i => i.params.style.color);
			// find the first color from palette that is unused in existingColors
			const match = palette.filter(c => !existingColors.includes(c));
			return match.length ? match[0] : existingColors[0];
		}
	}

	/**
	 * Utility function to calculate the total trading minutes for a given array of sessions.
	 * This function handles overlapping sessions to ensure accurate calculation of trading time.
	 * @param {Array} sessions - An array of session objects, each containing 'open' and 'close' times in minutes.
	 * @returns {number} - The total trading minutes.
	 */
	calculateTotalTradingMinutes(sessions) {
		let totalMinutes = 0;
		let lastClose = 0;

		for (const session of sessions) {
			if (session.open > lastClose) {
				totalMinutes += session.close - session.open;
			} else {
				totalMinutes += Math.max(0, session.close - lastClose);
			}
			lastClose = session.close;
		}

		return totalMinutes;
	}

	/**
	 * Automatically chooses an appropriate period/interval based on the number of days, chart width, and desired spacing between data points.
	 * @param {number} days - The number of days to display on the chart.
	 * @param {number} chartWidth - The width of the chart in pixels (default is 500).
	 * @param {number} desiredSpacing - The desired space between data points in pixels (default is 3).
	 * @returns {Object} - An object containing the calculated data interval and data period.
	 */
	getBestFitPeriodInterval(days = this.params.days, chartWidth = 500, desiredSpacing = 3) {
		// Update chartWidth if a panel's width is available
		if (this.panels.length && this.panels[0].size?.width) chartWidth = this.panels[0].size.width;

		let totalMinutesPerDay = 390; // Default to 390 minutes / 6.5 hours

		// Check if Monday's typical sessions are available and use them to calculate total trading minutes
		if (this.timeService?.typicalSessions && this.timeService.typicalSessions.length > 1) {
			const mondaySessions = this.timeService.typicalSessions[1].sessions;
			if (mondaySessions && mondaySessions.length > 0) {
				totalMinutesPerDay = this.calculateTotalTradingMinutes(mondaySessions);
			}
		}

		// Base data points per day for different periods
		const baseDatapointsPerDay = {
			Minute: totalMinutesPerDay,
			Hour: totalMinutesPerDay / 60,
			Day: 1,
			Week: 1 / 7,
			Month: 1 / 30
		};

		let dataInterval;
		let dataPeriod;
		let minDiff = Infinity;

		// Determine intervals and periods based on the number of days
		const intervals = days < 22 ? [1, 3, 5, 15] : [1];
		const periods = days < 22 ? ['Minute', 'Hour'] : ['Day', 'Week', 'Month'];

		// Calculate the best data interval and period based on desired spacing
		for (const period of periods) {
			for (const interval of intervals) {
				const totalDatapoints = baseDatapointsPerDay[period] * days / interval;
				const spacing = chartWidth / totalDatapoints;
				const diff = Math.abs(desiredSpacing - spacing);

				if (diff < minDiff) {
					minDiff = diff;
					dataInterval = interval;
					dataPeriod = period;
				}
			}
		}

		return { dataInterval, dataPeriod };
	}

	/** Determines the last exchangeDates index given the available data */
	_calcLastIndex() {
		let lastIndex;

		if (this.params.zoomResetBoundary === 'ruler') {
			// The right edge of the present domain seems to look better than the exchangeDates length
			lastIndex = this.state.xAxisPresentScale.domain()[1];
		} else {
			if (this.state.isIntraday && this.state.typicalSessions) {
				// boundary is edge of most recent intraday session
				const typicalClose = new Date(
					this.dataPrimary[this.dataPrimary.length - 1].date
				);
				typicalClose.setHours(0);
				typicalClose.setSeconds(0);
				typicalClose.setMilliseconds(0);
				typicalClose.setMinutes(this.state.typicalSessions.max);

				lastIndex = Math.min(
					this.exchangeDates.length - 1,
					this.closestExchangeIndex(typicalClose) - 1
				);
			} else {
				// boundary is edge of data
				lastIndex = this.dataPrimary[this.dataPrimary.length - 1].dateIndex;
			}
		}

		return lastIndex;
	}
}

/** Interactive handle */
class Handle {
	constructor(tool, params) {
		this.tool = tool;
		this.core = tool.core;
		this.state = {
			visible: true,
			hover: false
		};
		this.params = {
			date: new Date(params.date).getTime(),
			value: Number(params.value),
			pairedHandle: params.pairedHandle,
			pairedOffsetX: params.pairedOffsetX,
			pairedOffsetY: params.pairedOffsetY,
			style: this.core.getStyle('tool.handle')
		};
	}

	/**
	 * x calc - go from date to idx to px
	 * y calc - go from value to px
	 */
	getCoords() {
		const { panel } = this.tool;
		const isPaired = typeof this.params.pairedHandle === 'number';
		const handleCoords = isPaired
			? this.tool.handle[this.params.pairedHandle]
			: this;
		const xIdx = this.core.closestExchangeIndex(
			new Date(handleCoords.params.date)
		);
		let xPx = panel.xAxis.primaryScale(xIdx);
		let yPx = 0;

		if (panel.isNormalized) {
			// render as normalized value.  value stays as an actual.
			yPx = panel.yAxis.primaryScale(
				this.tool.panel.actualToNormalized(handleCoords.params.value)
			);
		} else {
			yPx = panel.yAxis.primaryScale(handleCoords.params.value);
		}

		// check if handle is earlier than ruler (can happen when drawing handles in inter and switching to intra)
		if (
			new Date(handleCoords.params.date) -
				new Date(this.core.exchangeDates[0]) <
				0 &&
			this.tool.params.numberOfAnchors > 1
		) {
			return null;
		}

		if (isPaired) {
			xPx += this.params.pairedOffsetX;
			yPx += this.params.pairedOffsetY;
		}

		return [xPx, yPx];
	}

	isWithin(mousePanel) {
		const coords = this.getCoords();
		const tolerance = this.core.state.isTouchDevice ? 5 : 3; // use a larger hitbox for touch events
		const size = this.params.style.length * tolerance;
		const half = size / 2;
		const top = this.tool.panel.size.padding.top;

		if (!coords) {
			return false;
		}

		return (
			mousePanel[0] >= coords[0] - half &&
			mousePanel[1] - top >= coords[1] - half &&
			mousePanel[0] < coords[0] - half + size &&
			mousePanel[1] - top < coords[1] - half + size
		);
	}

	onClick() {
		// onClick fired
	}

	render() {
		const ctx = this.tool.panel.rootToolContext;
		if (this.state.visible) {
			const coords = this.getCoords();
			if (!coords) return;

			if (this.renderCustom) {
				return this.renderCustom(ctx, coords, this.params);
			}

			const lengthDefault = this.params.style.length;
			const lengthHover = this.params.style.hover.length;
			let length = this.state.hover ? lengthHover : lengthDefault;

			ctx.beginPath();
			ctx.lineWidth = this.params.style.width;

			length /= 4;

			ctx.arc(
				subpixel.px(coords[0] - length / 2),
				subpixel.px(coords[1] - length / 2),
				length * 2,
				0,
				2 * Math.PI
			);

			if (this.tool.state.selected) {
				ctx.strokeStyle = this.params.style.color;
				ctx.fillStyle = this.params.style.background.color;
				ctx.lineWidth = this.params.style.width;
				ctx.fill();
				ctx.stroke();
			} else if (this.state.hover) {
				ctx.strokeStyle = this.params.style.hover.color;
				ctx.fillStyle = this.params.style.hover.background.color;
				ctx.lineWidth = this.params.style.hover.width;
				ctx.fill();
				ctx.stroke();
			}
		}
	}

	/** Gets generic object representation of Handle */
	toJSON() {
		let value = null;
		if (typeof this.params.pairedHandle === 'number') {
			// some tools (text) use a paired handle
			value = {
				pairedHandle: this.params.pairedHandle,
				pairedOffsetX: this.params.pairedOffsetX,
				pairedOffsetY: this.params.pairedOffsetY
			};
		} else {
			// default tool handle
			value = {
				date: this.params.date,
				value: this.params.value
			};
		}
		return value;
	}
	/**
	 * Gets instance of Handle based on generic JSON object provided
	 * @method toJSON
	 * @params {Object} json
	 * @params {Tool} tool
	 */
	static fromJSON(json, tool) {
		return new Handle(tool, {
			date: json.date,
			value: json.value,
			pairedHandle: json.pairedHandle,
			pairedOffsetX: json.pairedOffsetX,
			pairedOffsetY: json.pairedOffsetY
		});
	}
}

/**
 * Tools are customizable chart widgets overlaid on the chart and sharing its coordinate
 * space (on a separate canvas element)
 */
class Tool {
	/**
	 * @param {ModchartsInteractive} core - Modcharts chart object
	 * @param {Object} settings - Parameters for tool
	 */
	constructor(core, settings) {
		/** @type {ModchartsInteractive} */
		this.core = core;
		/** @type {Object} */
		this.params = this.getParams(settings);
	}

	/**
	 * Create new tool handle
	 * @param {Number[]} mousePanel - Array of mouse coordinates
	 * @param {Handle} HandleClass - Instance of tool handle class
	 * @return {Handle} New instance of given HandleClass
	 */
	createHandle(mousePanel, HandleClass) {
		const idx = Math.round(this.panel.xAxis.primaryScale.invert(mousePanel[0]));
		const valIn = this.panel.yAxis.primaryScale.invert(mousePanel[1]);
		let value = 0;
		const date = this.core.exchangeDates[idx];

		// allow different handle types to be passed
		HandleClass = HandleClass || Handle;

		// always store handle values as actuals (not normalized)
		if (this.panel.isNormalized) {
			value = this.panel.normalizedToActual(valIn).toFixed(4);
		} else {
			value = valIn.toFixed(4);
		}

		return new HandleClass(this, {
			date: new Date(date).getTime(),
			value: Number(value)
		});
	}

	/**
	 * Drag both handles at once based on mouse position
	 * @param {Number[]} mousePanel - Array of mouse coordinates
	 */
	dragTool(mousePanel) {
		if (
			!this.core.state.dragTool ||
			!this.state.dragToolOrigin ||
			!this.state.handleOrigin
		) {
			return;
		}

		const diffTool = [
			mousePanel[0] - this.state.dragToolOrigin[0],
			mousePanel[1] - this.state.dragToolOrigin[1]
		];
		const coordsLeft = this.state.handleOrigin[0];
		const coordsRight = this.state.handleOrigin[1];
		const newCoordsLeft = [
			coordsLeft[0] + diffTool[0],
			coordsLeft[1] + diffTool[1]
		];
		const newCoordsRight = [
			coordsRight[0] + diffTool[0],
			coordsRight[1] + diffTool[1]
		];
		const dateIdxLeft = Math.round(
			this.panel.xAxis.primaryScale.invert(newCoordsLeft[0])
		);
		const dateIdxRight = Math.round(
			this.panel.xAxis.primaryScale.invert(newCoordsRight[0])
		);
		let valLeft = this.panel.yAxis.primaryScale.invert(newCoordsLeft[1]);
		let valRight = this.panel.yAxis.primaryScale.invert(newCoordsRight[1]);

		// check for tool dragged beyond the panel's domain boundaries
		if (
			dateIdxRight > this.core.exchangeDates.length - 1 ||
			dateIdxRight < 0 ||
			dateIdxLeft > this.core.exchangeDates.length - 1 ||
			dateIdxLeft < 0
		) {
			// reset drag/handle origins, makes UX feel less sticky
			this.state.dragToolOrigin = [mousePanel[0], mousePanel[1]];
			this.state.handleOrigin = [
				this.handle[0].getCoords(),
				this.handle[1].getCoords()
			];

			// don't update handles
			return;
		}

		// always store handle values as actuals
		if (this.panel.isNormalized) {
			valLeft = this.panel.normalizedToActual(valLeft);
			valRight = this.panel.normalizedToActual(valRight);
		}

		// update handles with the new dates and values
		this.handle[0].params.date = new Date(
			this.core.exchangeDates[dateIdxLeft]
		);
		this.handle[1].params.date = new Date(
			this.core.exchangeDates[dateIdxRight]
		);
		this.handle[0].params.value = valLeft;
		this.handle[1].params.value = valRight;
	}

	/**
	 * Calculate the closest point to the mouse using linear interpolation
	 * @param {number} x0 - X-coordinate of first point
	 * @param {number} y0 - Y-coordinate of first point
	 * @param {number} x1 - X-coordinate of second point
	 * @param {number} y1 - Y-coordinate of second point
	 * @param {number} mouseX - X-coordinate of mouse cursor
	 * @param {number} mouseY - Y-coordinate of mouse cursor
	 * @return {number[]} - Coordinates for closest point
	 */
	_getClosestPoint(x0, y0, x1, y1, mouseX, mouseY) {
		const dx = x1 - x0;
		const dy = y1 - y0;
		const t = ((mouseX - x0) * dx + (mouseY - y0) * dy) / (dx * dx + dy * dy);
		const lineX = this._lerp(x0, x1, t);
		const lineY = this._lerp(y0, y1, t);

		return [lineX, lineY];
	}

	/**
	 * Get data associated with X-coordinate
	 * @param {Number} valX - X-coordinate for which to get associated data
	 * @return {Object} Data associated with X-coordinate
	 */
	getDataPointFromX(valX) {
		const domainIndex = this.getDataPointIndexFromX(valX);
		return this.core.dataPrimary[domainIndex];
	}

	/**
	 * Get domain index associated with X-coordinate
	 * @param {Number} valX - X-coordinate for which to get associated domain index
	 * @return {Number} - Domain index associated with X-coordinate
	 */
	getDataPointIndexFromX(valX) {
		const valIndex = this.panel.xAxis.primaryScale.invert(valX);
		return this.core._getIndexByDomain(valIndex, this.core.dataPrimary, 'dateIndex');
	}

	/**
	 * Retrieves default parameters for tool, including details and styles
	 * @return {object} Object containing default tool parameters
	 * @abstract
	 */
	_getDefaultParams() {
		return {};
	}

	/**
	 * Get formatted value with the specified options for Fibonacci tool
	 * @param {Number} val - Value to format
	 * @return {String} Formatted value
	 */
	_getFibFormatValue(val) {
		const formatString = this.params.labelFormat === FibToolLabelFormat$4.PERCENT
			? this.params.style.label.format.percent
			: this.params.style.label.format.default;

		const valOut = formatLocale(this.core.locale).format(formatString)(val);

		// Use B for billion instead of G for giga
		return valOut.replace(/G/, 'B');
	}

	/**
	 * Get merged params object
	 * @param {Object} settings - Parameters for tool
	 * @return {Object} Merged params object
	 */
	getParams(settings) {
		const params = {
			name: 'Chart Tool',
			id: 'tool',
			uid: createUid(),
			style: {}
		};

		return merge({}, params, this._getDefaultParams(), settings);
	}

	/**
	 * Get tool constructor for supplied tool type/id
	 * @param {String} type - Short name for tool
	 * @param {Object} toolMap - List of tool classes that can be added to the chart
	 * @return {Object} Tool class associated with given short name
	 */
	static getToolFromType(type, toolMap) {
		return toolMap[type];
	}

	/**
	 * Determine if the tool is immediately created when tool button is clicked
	 * @type {Boolean}
	 */
	get isImmediate() { return this.params.isImmediate; }

	/**
	 * Set the isImmediate property of the tool
	 * @param {Boolean} value - Whether or not tool is immediately created
	 * @type {Boolean}
	 */
	set isImmediate(value) { this.params.isImmediate = value; }

	/**
	 * Initialize the tool
	 * @param {Panel} panel - Parent panel for the tool
	 * @param {Handle[]} handles - Array containing tool handle objects
	 */
	init(panel, handles = []) {
		/** @type {Panel} */
		this.panel = panel;
		/** @type {Handle[]} */
		this.handle = handles;
		this.path = [];

		/** @type {Object} */
		this.state = {
			dragHandle: null,
			hover: false,
			inProgressHandle: false,
			selected: false,
			visible: true
		};
		/** @type {Object} */
		this.flags = {};
	}

	/**
	 * Check for overlap between two boxes
	 * @param {Object} r1 - Info about first box
	 * @param {Object} r2 - Info about second box
	 * @return {Boolean} Whether or not the two boxes overlap
	 */
	intersect(r1, r2) {
		// todo: performance improvement - convert to simple arrays instead of objects with x,y,width,height props
		if (
			r1.x < r2.x + r2.width &&
			r2.x < r1.x + r1.width &&
			r1.y < r2.y + r2.height
		) {
			return r2.y < r1.y + r1.height;
		}
		return false;
	}

	/**
	 * Determines if mouse is within tolerance area of tool
	 * @return {Boolean} Whether or not tool is within tolerance area of tool
	 * @abstract
	 */
	isWithin() {
		return false;
	}

	/**
	 * Determines if mouse is within tool handle
	 * @param {Number[]} mousePanel - Array of mouse coordinates
	 * @return {Boolean} Whether or not mouse is within tool handle
	 */
	isWithinHandle(mousePanel) {
		for (const handle of this.handle) {
			if (handle.isWithin(mousePanel)) return handle;
		}

		return false;
	}

	/**
	 * Perform linear interpolation
	 * @param {number} a - Beginning coordinate
	 * @param {number} b - Ending coordinate
	 * @param {number} x - Parameter representing distance between coordinates
	 * @return {number} - Interpolated coordinate
	 */
	_lerp(a, b, x) {
		return a + x * (b - a);
	}

	/**
	 * Handle click on canvas for this tool
	 * @abstract
	 */
	onClickCanvas() {
		throw new Error('Required Tool method not found');
	}

	/**
	 * Mousedown on handle
	 * @abstract
	 */
	onClickHandle() {
		throw new Error('Required Tool method not found');
	}

	/**
	 * Mousedown on tool itself
	 * @abstract
	 */
	onClickTool() {
		throw new Error('Required Tool method not found');
	}

	/** Should be called by a tool when it is finished drawing and no longer active */
	onComplete() {
		if (this.state) {
			// reset state variables
			this.state.dragHandle = null;
			this.state.dragToolOrigin = null;
			this.state.handleOrigin = null;
			this.state.inProgressHandle = false;
			this.storePrevHandles();
		}
		if (this.core) {
			this.core.state.dragTool = null;
			this.core.state.tool = null;
			if (this.core.setToolMode) this.core.setToolMode(null);
		}
	}

	/**
	 * Mousedown handler.  select the tool and/or begin dragging
	 * @param {Number[]} mouse - Array of mouse coordinates
	 * @param {String} touchId - Identifier associated with the current touch event
	 */
	onMousedown(mouse, touchId) {
		// loop through all handles to see if it was a mousedown on a handle or tool body
		// if so set this tool's draghandle to handle and select this tool

		const mousePanel = [mouse[0], mouse[1] - this.panel.size.top];
		const handleWithin = this.isWithinHandle(mousePanel, touchId);
		const toolWithin = this.isWithin(mousePanel);
		const core = this.core;

		// if within a handle, begin the handle dragging process
		if (handleWithin) {
			this.state.inProgressHandle = true;
			this.state.dragHandle = handleWithin;
			core.state.dragTool = null;
		} else if (toolWithin && this.params.numberOfAnchors === 1) {
			this.state.dragHandle = this.handle[0];
			this.state.inProgressHandle = true;
			this.state.dragToolOrigin = [mousePanel[0], mousePanel[1]];
			this.state.handleOrigin = [
				this.handle[0].getCoords(),
				this.handle[1].getCoords()
			];
			core.state.dragTool = this;
		} else if (toolWithin) {
			this.state.dragHandle = null;
			this.state.inProgressHandle = null;
			this.state.dragToolOrigin = [mousePanel[0], mousePanel[1]];
			this.state.handleOrigin = [
				this.handle[0].getCoords(),
				this.handle[1].getCoords()
			];
			core.state.dragTool = this;
		}

		// select the tool (and unselect others)
		if (handleWithin || toolWithin) {
			zoomUtil.unregisterZoom(core); // prevent zoom event from firing
			core.setToolMode(this.params.id);
			if (core.selectTool) core.selectTool(this);

			// update handle colors immediately rather than waiting for mousemove
			this.panel.clearTools();
			this.panel.renderTools();

			core.onToolMousedown(this);

			return true;
		}
	}

	/**
	 * Handle mouse move event for this tool
	 * @abstract
	 */
	onMousemove() {
		throw new Error('Required Tool method not found');
	}

	/** Mouseup handler. Clears out drag states. */
	onMouseup() {
		this.core.onToolMouseup(this);

		this.state.dragHandle = null;
		this.state.dragToolOrigin = null;
		this.state.handleOrigin = null;
		this.state.inProgressHandle = false;
		this.core.state.dragTool = null;
		this.core.state.tool = null;

		this.onComplete();
	}

	/** Removes tool reference from panel's tools collection */
	remove() {
		const chart = this.core;
		this.onComplete();

		Object.keys(this.panel.tools).forEach(idx => {
			const el = this.panel.tools[idx];
			if (el === this) {
				this.panel.tools.splice(idx, 1);
				return false;
			}
		});

		// remove any associated flags
		const flags = this.flags || {};
		Object.keys(flags).forEach(key => {
			const flag = flags[key];
			flag.remove();
			this.flags[key] = null;
		});

		chart.clearPanels();
		chart.render();

		chart.eventEmitter.emit(types.TOOL_REMOVE, {
			toolType: this.params && this.params.id
		});
	}

	/** Draws the tool on the canvas */
	render() {
		throw new Error('Required Tool method not found');
	}

	/**
	 * Sets a style on this tool
	 * @param {string} name - The style to set
	 * @param {string} value - The new style value
	 */
	setStyle(name, value) {
		if (this.getStyle(name) !== undefined) {
			let updateStyle = false;

			// Make sure that numbers are saved as numbers, not strings
			if (typeof this.getStyle(name) === 'number') {
				// Only replace number type with new number
				if (typeof value !== 'number' && !isNaN(value)) {
					updateStyle = true;
					value = Number(value);
				} else {
					this.core.warn('Tool.setStyle: Setting a non-numeric value where a numeric value is required is not allowed');
				}
			} else {
				updateStyle = true;
			}

			if (updateStyle) set(this.params.style, name, value);
		} else {
			this.core.warn(`Style ${name} not found`);
		}
	}

	/**
	 * Returns style info for the given selector from this tool's params.style collection
	 * @param {string} selector - Selector to use when getting style (eg. 'panel.padding.right')
	 * @return {string|number} The style found in the styleMap
	 */
	getStyle(selector) {
		return get(this.params.style, selector);
	}

	/**
	 * Determines if the tool should be able to snap to data
	 * @type {Boolean}
	 */
	get shouldSnap() { return true; }

	/**
	 * Toggle the visibility of the tool handles
	 * @param {Boolean} isVisible Whether of not the handles should be visible
	 */
	toggleHandles(isVisible) {
		for (let h = 0; h < this.handle.length; h++) {
			this.handle[h].state.visible = isVisible;
		}
	}

	/**
	 * Creates generic object representation of tool
	 * @return {object} Generic object representation of tool
	 */
	toJSON() {
		const handles = this.handle || [];
		return {
			params: { ...this.params },
			handle: handles.map(handle => handle.toJSON())
		};
	}

	// save handle positions to the tool's state, in case we want to revert to them later
	storePrevHandles() {
		if (
			this.handle?.length &&
			this.handle.length === 2 &&
			this.handle[0].params &&
			this.handle[1].params
		) {
			this.state.prevHandles = [
				{
					date: this.handle[0].params.date,
					value: this.handle[0].params.value
				},
				{
					date: this.handle[1].params.date,
					value: this.handle[1].params.value
				}
			];
		}
	}

	revertHandles() {
		const prevHandles = this.state.prevHandles;
		if (prevHandles && this.handle.length === 2) {
			this.handle[0].params.date = prevHandles[0].date;
			this.handle[0].params.value = prevHandles[0].value;
			this.handle[1].params.date = prevHandles[1].date;
			this.handle[1].params.value = prevHandles[1].value;
		}

		// re-render tool at previous location
		this.panel.clearTools();
		this.panel.renderTools();

		this.onComplete();
	}
}

/** Line tool base class */
class LineTool extends Tool {
	/**
	 * Calculate the distance between two points
	 * @param {number[]} coord1 - Coordinates for point 1
	 * @param {number[]} coord2 - Coordinates for point 2
	 * @return {number} Distance between the given points
	 */
	distance(coord1, coord2) {
		let xs = coord2[0] - coord1[0];
		let ys = coord2[1] - coord1[1];

		xs *= xs; // Square the x-distance
		ys *= ys; // Square the y-distance

		return Math.sqrt(xs + ys);
	}

	/**
	 * @property {string} id - Tool ID
	 * @property {string} name - Tool name
	 * @property {number} style.color - Tool line color
	 * @property {number} style.width - Tool line width
	 */
	_getDefaultParams() {
		return {
			id: 'line',
			name: 'Trendline',
			style: this.core.getStyle('tool.line')
		};
	}

	/**
	 * Determine if current mouse coordinates are within hitbox of this tool
	 * @param {number[]} mousePanel - Array of coordinates for current mouse location
	 * @return {boolean} - Whether or not mouse is within hitbox
	 */
	isWithin(mousePanel) {
		return this._isWithinLineTool(mousePanel);
	}

	/**
	 * Determine if current mouse coordinates are within tolerance area for line
	 * @param {number[]} mousePanel - Array of coordinates for current mouse location
	 * @return {boolean} - Whether or not mouse is within tolerance area
	 */
	_isWithinLineTool(mousePanel) {
		const handles = this.handle;

		if (handles.length === 2) {
			// use either regular handle coords or special endpoint coords (if present)
			const coords = [
				this.state.endpointLeft || handles[0].getCoords(),
				this.state.endpointRight || handles[1].getCoords()
			];

			// couldn't find one or more handles
			if (!coords[0] || !coords[1]) {
				return false;
			}

			let tolerance = 5 + this.params.style.width / 2;
			const isVertical = coords[0][0] === coords[1][0];
			const left = Math.min(coords[0][0], coords[1][0]);
			const right = Math.max(coords[0][0], coords[1][0]);
			const top = Math.min(coords[0][1], coords[1][1]);
			const bottom = Math.max(coords[0][1], coords[1][1]);

			// use a larger hitbox for touch events
			if (this.panel.core.state.isTouchDevice) {
				tolerance += 10;
			}

			// handle vertical lines as a simple bounding box intersection check
			if (isVertical) {
				const r1 = {
					x: left - tolerance,
					y: top,
					width: right - left + 2 * tolerance,
					height: bottom - top
				};
				const r2 = {
					x: mousePanel[0],
					y: mousePanel[1],
					width: 1,
					height: 1
				};

				return this.intersect(r1, r2);
			}

			// handle diagonal or horizontal lines with a distance calculation
			const linepoint = this._getClosestPoint(
				coords[0][0],
				coords[0][1],
				coords[1][0],
				coords[1][1],
				mousePanel[0],
				mousePanel[1]
			);
			const dx = mousePanel[0] - linepoint[0];
			const dy = mousePanel[1] - linepoint[1];
			const distance = Math.abs(Math.sqrt(dx * dx + dy * dy));

			if (
				distance > tolerance ||
				mousePanel[0] < left ||
				mousePanel[0] > right
			) {
				return false;
			} else if (distance < tolerance) {
				return true;
			}
		}
	}

	/**
	 * Called from core.onClick() on both first and second clicks.
	 * @param {number[]} mouse - Array of mouse coordinates
	 */
	onClickCanvas(mouse) {
		const { core } = this;
		const { state } = core;
		const { isUpper } = this.panel;
		const numberOfAnchors = this.params.numberOfAnchors || 2;

		if (!this.state.inProgressHandle) {
			// first click
			let mousePanel = [mouse[0], mouse[1] - this.panel.size.top];

			// use snapHandle coords if available
			if (isUpper && state.snapHandle?.coords) {
				mousePanel = [
					state.snapHandle.coords[0],
					state.snapHandle.coords[1] - this.panel.size.top
				];
			}

			// first click - create and render first handle
			this.handle[0] = this.createHandle(mousePanel);
			this.handle[0].state.visible = true;

			// create second handle (for its coords) but don't render yet
			this.handle[1] = this.createHandle(mousePanel);

			// Exit out of the handle placement if only one handle is necessary
			if (numberOfAnchors === 1) {
				this.state.inProgressHandle = false;
				this.state.dragHandle = null;
				return;
			}
			this.state.dragHandle = this.handle[1];

			// tool is now active
			this.state.inProgressHandle = true;
		} else {
			// second click

			// Special case for adding extended horizontal line when using a touch device,
			// with a single touch and no drag (CH-1466)
			if (
				state.isTouchDevice &&
				mouse &&
				/^extendedhorizontal/.test(this.params.id)
			) {
				const valIn = this.panel.yAxis.primaryScale.invert(mouse[1]);

				// Always store handle values as actuals (not normalized)
				const value = this.panel.isNormalized
					? this.panel.normalizedToActual(valIn).toFixed(4)
					: valIn.toFixed(4);

				this.handle[0].params.value = Number(value);
				this.handle[1].params.value = Number(value);

			} else if (
				isUpper &&
				state.snapHandle?.coords &&
				!this.params.lockVertical
			) { // use snapHandle coords if available
				const snapDateIdx = Math.round(
					this.panel.xAxis.primaryScale.invert(state.snapHandle.coords[0])
				);

				if (core.exchangeDates[snapDateIdx]) {
					let val = this.panel.yAxis.primaryScale.invert(
						state.snapHandle.coords[1]
					);

					// always store handle values in actuals
					if (this.panel.isNormalized) {
						val = this.panel.normalizedToActual(val);
					}

					this.state.dragHandle.params.value = Number(val);
					this.state.dragHandle.params.date = new Date(core.exchangeDates[snapDateIdx]).getTime();
				}
			}

			// no longer actively drawing a change in the tool
			this.state.inProgressHandle = false;
			this.state.dragHandle = null;

			this.onComplete();
		}
	}

	/**
	 * @param {number[]} mouse - Array of coordinates for current mouse location
	 */
	onMousemove(mouse) {
		const mousePanel = [mouse[0], mouse[1] - this.panel.size.top];
		this.core.onToolMousemove(this, mousePanel);

		if (this.state.inProgressHandle) {
			let idx = Math.round(
				this.panel.xAxis.primaryScale.invert(mousePanel[0])
			);

			idx = Math.min(this.core.exchangeDates.length - 1, idx);
			idx = Math.max(0, idx);

			this.state.dragHandle.params.date = this.core.exchangeDates[idx];

			if (!this.params.lockVertical) {
				let val = this.panel.yAxis.primaryScale.invert(mousePanel[1]);

				// always store handle values in actuals
				if (this.panel.isNormalized) {
					val = this.panel.normalizedToActual(val);
				}

				this.state.dragHandle.params.value = val;
			}
		} else {
			this.dragTool(mousePanel);
		}
	}

	render() {
		const coords = [this.handle[0].getCoords(), this.handle[1].getCoords()];

		// couldn't find one or more handles
		if (!coords[0] || !coords[1]) return;

		const ctx = this.panel.rootToolContext;
		const distX = coords[1][0] - coords[0][0];
		const distY = coords[1][1] - coords[0][1];

		ctx.beginPath();
		ctx.strokeStyle = this.params.style.color;
		ctx.fillStyle = '#FFF';
		ctx.lineWidth = this.params.style.width;

		// regular line segment
		ctx.moveTo(coords[0][0], coords[0][1]);

		if (this.params.lockVertical) {
			ctx.lineTo(coords[1][0], coords[0][1]);
		} else {
			ctx.lineTo(coords[1][0], coords[1][1]);
		}
		ctx.stroke();

		// arrowhead
		if (this.params.arrow) {
			this.drawArrowheads(
				ctx,
				coords[0][0],
				coords[0][1],
				coords[1][0],
				coords[1][1]
			);
		}

		// extend right and store endpoint
		if (this.params.extendRight) {
			this.state.endpointRight = this._renderRay(
				ctx,
				coords[1][0],
				coords[1][1],
				distX,
				distY
			);
		} else {
			this.state.endpointRight = null;
		}

		// extend left and store endpoint
		if (this.params.extendLeft) {
			this.state.endpointLeft = this._renderRay(
				ctx,
				coords[0][0],
				coords[0][1],
				-distX,
				-distY
			);
		} else {
			this.state.endpointLeft = null;
		}

		ctx.stroke();

		// handles
		this.handle[0].render();
		this.handle[1].render();

		this.panel.core.onToolRender(this);
	}

	/**
	 * Draw extended line to canvas edge
	 * @param {CanvasRenderingContext2D} ctx - HTML canvas context to be used for drawing graphics
	 * @param {number} x - X-coordinate for starting point of ray
	 * @param {number} y - Y-coordinate for starting point of ray
	 * @param {number} distX - X-distance to edge of canvas
	 * @param {number} distY - Y-distance to edge of canvas
	 * @return {number[]} - Starting point of ray
	 */
	_renderRay(ctx, x, y, distX, distY) {
		// don't render short rays
		if (Math.abs(distX) < 1 && Math.abs(distY) < 1) {
			return [x, y];
		}

		const size = this.panel.size;
		const right = size.width + size.padding.left;
		const left = size.padding.left;
		const isVertical = distX === 0;

		ctx.moveTo(x, y);

		if (isVertical) {
			if (distY >= 0 && 1 / distY !== -Infinity) {
				// vertical to bottom edge, don't include negative 0

				y = size.padding.top + size.height;
			} else {
				// vertical to top edge

				y = size.padding.top;
			}

			ctx.lineTo(x, y);
		} else {
			if (distX > 0) {
				// horizontal to right edge

				while (x < right) {
					x += distX;
					y += distY;

					ctx.lineTo(x, y);
				}
			} else {
				// horizontal to left edge

				while (x >= left) {
					x += distX;
					y += distY;

					ctx.lineTo(x, y);
				}
			}
		}

		return [x, y];
	}
}

/* eslint-disable no-trailing-spaces */

/** A line with customizable arrowheads */
class ArrowLineTool extends LineTool {
	/**
	 * Retrieves default parameters for tool, including details and styles
	 * @return {Object} Object containing default tool parameters
	 * @property {String} id - Tool ID
	 * @property {String} name - Tool name
	 * @property {Boolean} arrow - Flag to determine if line should be arrow
	 * @property {Object} style - Tool styles
	 */
	_getDefaultParams() {
		return {
			id: 'arrow',
			name: 'Arrow',
			arrow: true,
			style: this.core.getStyle('tool.arrow')
		};
	}

	/**
	 * Draws arrowheads for tool
	 * @param {CanvasRenderingContext2D} ctx - HTML canvas context to be used for drawing graphics
	 * @param {Number} x1 - First x-coordinate of line
	 * @param {Number} y1 - First y-coordinate of line
	 * @param {Number} x2 - Second x-coordinate of line
	 * @param {Number} y2 - Second x-coordinate of line
	 */
	drawArrowheads(ctx, x1, y1, x2, y2) {
		// draw the ending arrowhead
		let endRadians = Math.atan((y2 - y1) / (x2 - x1));
		endRadians += ((x2 >= x1 ? 90 : -90) * Math.PI) / 180;
		this.drawArrowhead(ctx, x2, y2, endRadians);
	}

	/**
	 * Draws a single arrowhead at the given coordinates
	 * @param {CanvasRenderingContext2D} ctx - HTML canvas context to be used for drawing graphics
	 * @param {Number} x - X-coordinate for point of arrow
	 * @param {Number} y - Y-coordinate for point of arrow
	 * @param {Number} radians - Angle at which to rotate the canvas to properly draw the lines of the arrowhead
	 */
	drawArrowhead(ctx, x, y, radians) {
		ctx.save();
		ctx.beginPath();
		ctx.translate(x, y);
		ctx.rotate(radians);
		ctx.moveTo(0, 0);
		ctx.lineTo(this.params.style.arrow.width, this.params.style.arrow.height);
		ctx.lineTo(
			-this.params.style.arrow.width,
			this.params.style.arrow.height
		);
		ctx.closePath();
		ctx.restore();
		ctx.fillStyle = this.params.style.background.color;
		ctx.fill();
	}
}

/** Interactive handle that is a long skinny rectangle instead of a point. */
class EdgeHandle extends Handle {
	constructor(tool, params) {
		super(tool, params);

		this.state = {
			visible: true
		};

		this.params = {
			date: new Date(params.date),
			value: params.value,
			style: this.core.getStyle('tool.edgeHandle')
		};
	}

	/**
	 * x calc - go from date to idx to px
	 * y calc - go from value to px
	 */
	getCoords() {
		const { panel } = this.tool;
		const xDomain = panel.core.closestExchangeIndex(new Date(this.params.date));
		const xRange = panel.xAxis.primaryScale(xDomain);
		const xRangeRoundedUp = subpixel.px(xRange);
		const xPx = subpixel.px(
			xRange,
			// Only round up for subpixel offset if act won't cause index to go out of range
			xRangeRoundedUp <= panel.xAxis.primaryScale.range()[1]
		);
		const yPx = 0;

		return [xPx, yPx];
	}

	isWithin(mousePanel) {
		const coords = this.getCoords();
		const halfWidth = this.params.style.width / 2;

		if (!coords) return false;

		return (
			mousePanel[0] >= coords[0] - halfWidth &&
			mousePanel[0] < coords[0] + halfWidth
		);
	}

	render() {
		if (this.state.visible) {
			const coords = this.getCoords();
			if (!coords) return;

			const ctx = this.tool.panel.rootToolContext;
			const halfWidth = this.params.style.width / 2;

			if (this.renderCustom) {
				return this.renderCustom(ctx, coords, this.params);
			}

			ctx.save();
			ctx.beginPath();

			ctx.rect(
				coords[0] - halfWidth,
				2,
				this.params.style.width,
				this.tool.panel.size.height - 3
			);

			if (this.tool.state.selected) {
				ctx.strokeStyle = this.params.style.color || 'transparent';
				ctx.fillStyle = this.params.style.background.color || 'transparent';
				ctx.fill();
				ctx.stroke();
			} else if (this.tool.state.hover) {
				ctx.strokeStyle = this.params.style.hover.color || 'transparent';
				ctx.fillStyle = this.params.style.hover.background.color || 'transparent';
				ctx.fill();
				ctx.stroke();
			}
			ctx.restore();
		}
	}
}

/** Crosshair Tool */
class CrosshairTool extends LineTool {

	createHandle(mousePanel) {
		const date = this._getHandleDate();
		const valIn = this.panel.yAxis.primaryScale.invert(mousePanel[1]);

		// Always store handle values as actuals
		const value = this.panel.isNormalized
			? this.panel.normalizedToActual(valIn).toFixed(4)
			: valIn.toFixed(4);

		return new EdgeHandle(this, { date, value });
	}

	_emitCrosshairData() {
		const [xCoord] = this.handle[0].getCoords();

		// Don't emit data if tool position hasn't changed
		if (xCoord !== this.state.previousHandleXCoord) {
			this.state.previousHandleXCoord = xCoord;
			const dataPointIndex = this.getDataPointIndexFromX(xCoord);
			const dataPoint = this.core.dataPrimary[dataPointIndex];
			const valueY = dataPoint[this.state.dataPointKey];
			const dataY = subpixel.px(Number(this.panel.yAxis.primaryScale(valueY)));
			// Use setTimeout so as not to block when there are repeated calls to update the crosshair
			setTimeout(
				() => {
					this.core.eventEmitter.emit(
						types.CROSSHAIR_MOVE,
						buildCrosshairData(
							this.panel,
							dataPoint.date,
							xCoord,
							dataY, // The yCoord should always be the dataY since we're not using the mouse
							dataY
						)
					);
				},
				0
			);
		}
	}

	init(panel, handles) {
		super.init(panel, handles);
		this.state.dataPointKey = this.core.params.customData ? 'value' : 'close';
		this.state.previousHandleXCoord = null;
		this.state.avoidClick = true;

		// Add event listeners
		this.core.eventEmitter.on(types.DATA_FRAME_UPDATE, this._updateHandleDate.bind(this));
		this.core.eventEmitter.on(types.SYMBOL_UPDATE, this._resetHandleDate.bind(this));
		this.core.eventEmitter.on(types.TIME_SERIES_SET, this._manageTimeSeriesSet.bind(this));

	}

	_getDefaultParams() {
		return {
			id: 'crosshair',
			isAutomatic: true,
			name: 'Crosshair Tool',
			startingPosition: 'right',
			style: this.core.getStyle('tool.crosshair')
		};
	}

	_getHandleDate(position = this.params.startingPosition) {
		const [leftIndex, rightIndex] = this.core.xAxis.primaryScale.domain();

		switch (position) {
			case 'left':
				// If date is off of the chart, then move to the right by 1 date
				return this.core.exchangeDates[Math.ceil(leftIndex)] >= this.core.dataFrame[0].date
					? this.core.exchangeDates[Math.ceil(leftIndex) + 1]
					: this.core.dataFrame[0].date;
			case 'right':
				// If date is off of the chart, then move to the left by 1 date
				return this.core.exchangeDates[Math.floor(rightIndex)] <= this.core.dataFrame[this.core.dataFrame.length - 1].date
					? this.core.exchangeDates[Math.floor(rightIndex) - 1]
					: this.core.dataFrame[this.core.dataFrame.length - 1].date;
			default:
				// Use the middle date
				return this.core.dataFrame[Math.round(this.core.dataFrame.length / 2)].date; // The middle of the chart data
		}
	}

	_manageTimeSeriesSet(args) {
		if (args.updatedProperty === 'days') this._resetHandleDate();
	}

	onClickCanvas(mouse) {
		let secondClick = false;

		// called from core.onClick() on both first and second clicks.
		if (!this.state.inProgressHandle) {
			// first click - create and render first handle
			this.handle[0] = this.createHandle([mouse[0], mouse[1] - this.panel.size.top]);
			this.handle[0].state.visible = true;

			if (this.state.avoidClick) {
				this.state.avoidClick = false;
				secondClick = true;
			}
		} else {
			secondClick = true;
		}

		if (secondClick) {
			this.state.inProgressHandle = false; // no longer actively drawing a change in the tool
			this.state.dragHandle = null;
			this.onComplete();
		}
	}

	remove() {
		// Remove event listeners
		this.core.eventEmitter.off(types.DATA_FRAME_UPDATE, this._updateHandleDate);
		this.core.eventEmitter.off(types.SYMBOL_UPDATE, this._resetHandleDate);
		this.core.eventEmitter.off(types.TIME_SERIES_SET, this._manageTimeSeriesSet);

		super.remove();
	}

	render() {
		const [start, stop] = this.panel.xAxis.primaryScale.domain();

		// Skip rendering when no domain has been set to avoid showing crosshair in wrong location
		if (!(start === 0 && stop === 0)) {
			this._renderHorizontal(this.panel);
			this._renderVertical(this.panel);
			this.handle[0].render();
			this._emitCrosshairData();
			this.panel.core.onToolRender(this);
		}
	}

	_renderHorizontal(panel) {
		const { core } = this;
		const { style } = this.params;
		const ctx = panel.rootToolContext;
		const coords = this.handle[0].getCoords();
		const dataPointIndex = this.getDataPointIndexFromX(coords[0]);
		const dataPoint = core.dataPrimary[dataPointIndex];
		const dataY = subpixel.px(Number(this.panel.yAxis.primaryScale(dataPoint[this.state.dataPointKey])));

		ctx.save();
		ctx.beginPath();
		ctx.strokeStyle =
			style.horizontal.color ||
			style.default.color;
		ctx.lineWidth =
			style.horizontal.width ||
			style.default.width;
		const lineDash =
			style.horizontal.lineDash ||
			style.default.lineDash ||
			[];
		ctx.setLineDash(lineDash.split(/\s*,\s*/));
		ctx.moveTo(0, dataY);
		ctx.lineTo(this.panel.size.width, dataY);
		ctx.stroke();
		ctx.restore();
	}

	/** Alternate render method for this tool on lower panels (just render the line) */
	renderLower(panel) {
		this._renderVertical(panel);
	}

	_renderVertical(panel) {
		const { style } = this.params;
		const ctx = panel.rootToolContext;
		const coords = this.handle[0].getCoords();

		ctx.save();
		ctx.beginPath();
		ctx.strokeStyle =
			style.vertical.color ||
			style.default.color;
		ctx.lineWidth =
			style.vertical.width ||
			style.default.width;
		const lineDash =
			style.vertical.lineDash ||
			style.default.lineDash ||
			[];
		ctx.setLineDash(lineDash.split(/\s*,\s*/));
		ctx.moveTo(coords[0], 0);
		ctx.lineTo(coords[0], panel.size.height);
		ctx.stroke();
		ctx.restore();
	}

	_resetHandleDate() {
		this.state.resetHandle = true;
	}

	_updateHandleDate() {
		const handle = this.handle[0];

		// Reset handle date to starting position, or move to left/right per getHandleDate
		if (this.state.resetHandle) {
			this.state.resetHandle = false;
			handle.params.date = new Date(this._getHandleDate());
		} else if (handle.params.date < this.core.dataFrame[0].date) {
			handle.params.date = new Date(this._getHandleDate('left'));
		} else if (handle.params.date > this.core.dataFrame[this.core.dataFrame.length - 1].date) {
			handle.params.date = new Date(this._getHandleDate('right'));
		}
	}

}

/** Tool used to draw ellipse on chart object */
class EllipseLineTool extends LineTool {
	/**
	 * @param {ModchartsInteractive} core - Modcharts chart object
	 * @param {object} settings - Parameters for tool
	 */
	constructor(core, settings) {
		super(core, settings);

		/** @type {number} */
		this.INNER_TOLERANCE = 5;
		/** @type {number} */
		this.OUTER_TOLERANCE = 8;
	}

	/**
	 * Calculate data needed to create ellipse path
	 * @return {object} Data used to create ellipse path
	 */
	calcEllipseData() {
		const coords = [this.handle[0].getCoords(), this.handle[1].getCoords()];

		if (!coords[0] || !coords[1]) return {};

		const xDist = coords[1][0] - coords[0][0];
		const yDist = coords[1][1] - coords[0][1];
		const lineDist = this.distance(coords[0], coords[1]);
		const center = [coords[0][0] + xDist / 2, coords[0][1] + yDist / 2];

		return {
			center,
			coords,
			lineDist,
			xDist,
			yDist
		};
	}

	/**
	 * Draws tool control line
	 * @param {CanvasRenderingContext2D} ctx - HTML canvas context to be used for drawing graphics
	 * @param {Number[]} coords - Array containing coordinates for each end of the control line
	 */
	_drawControlLine(ctx, coords) {
		if (this.state.hover || this.state.selected) {
			ctx.save();
			ctx.strokeStyle = '#ccc';
			ctx.setLineDash([2, 3]);
			ctx.beginPath();
			ctx.moveTo(coords[0][0], coords[0][1]);
			ctx.lineTo(coords[1][0], coords[1][1]);
			ctx.stroke();
			ctx.restore();
		}
	}

	/**
	 * @return {object} Object containing default tool parameters
	 * @property {string} id - Tool ID
	 * @property {string} name - Tool name
	 * @property {object} style - Tool styles
	 */
	_getDefaultParams() {
		return {
			id: 'ellipse',
			name: 'Ellipse',
			style: this.core.getStyle('tool.ellipse')
		};
	}

	/**
	 * Draws inner and outer ellipses for donut shape
	 * @param {CanvasRenderingContext2D} ctx - HTML canvas context to be used for drawing graphics
	 * @param {number} cx - X coordinate of center of ellipse
	 * @param {number} cy - Y coordinate of center of ellipse
	 * @param {number} rx1 - Major axis radius of outer ellipse
	 * @param {number} ry1 - Minor axis radius of outer ellipse
	 * @param {number} rx2 - Major axis radius of inner ellipse
	 * @param {number} ry2 - Minor axis radius of inner ellipse
	 */
	_getDonutPath(ctx, cx, cy, rx1, ry1, rx2, ry2) {
		ctx.save();
		ctx.beginPath();
		// Draw outer ellipse
		ctx.ellipse(cx, cy, Math.abs(rx1), Math.abs(ry1), 0, 0, 2 * Math.PI, true);
		ctx.moveTo(cx + rx1, cy);
		// Draw inner ellipse
		ctx.ellipse(cx, cy, Math.abs(rx2), Math.abs(ry2), 0, 0, 2 * Math.PI, false);
		ctx.restore();
	}

	/**
	 * Creates two partial ellipses for use in creating filled arc shape
	 * @param {CanvasRenderingContext2D} ctx - HTML canvas context to be used for drawing graphics
	 * @param {number} cx - X coordinate of center of ellipse
	 * @param {number} cy - Y coordinate of center of ellipse
	 * @param {number} rx1 - Major axis radius of outer ellipse
	 * @param {number} ry1 - Minor axis radius of outer ellipse
	 * @param {number} rx2 - Major axis radius of inner ellipse
	 * @param {number} ry2 - Minor axis radius of inner ellipse
	 * @param {boolean} isSmiley - Whether or not arc is concave upward
	 */
	_getDonutSegmentPath(ctx, cx, cy, rx1, ry1, rx2, ry2, isSmiley) {
		let beginEllipse = Math.PI;
		let endEllipse = 0;

		// Change drawing direction if concave downward
		if (!isSmiley) {
			beginEllipse = 0;
			endEllipse = Math.PI;
		}

		ctx.save();
		ctx.beginPath();
		// Draw outer ellipse
		ctx.ellipse(cx, cy, Math.abs(rx1), Math.abs(ry1), 0, beginEllipse, endEllipse, true);
		ctx.moveTo(cx + rx1, cy);
		// Draw inner ellipse
		ctx.ellipse(cx, cy, Math.abs(rx2), Math.abs(ry2), 0, endEllipse, beginEllipse, false);
		ctx.restore();
	}

	/**
	 * Creates ellipse
	 * @param {CanvasRenderingContext2D} ctx - HTML canvas context to be used for drawing graphics
	 * @param {number} cx - X coordinate of center of ellipse
	 * @param {number} cy - Y coordinate of center of ellipse
	 * @param {number} rx - Major axis radius of ellipse
	 * @param {number} ry - Minor axis radius of ellipse
	 */
	_getEllipsePath(ctx, cx, cy, rx, ry) {
		ctx.save(); // save state
		ctx.beginPath();
		ctx.ellipse(cx, cy, Math.abs(rx), Math.abs(ry), 0, 0, 2 * Math.PI);
		ctx.restore(); // restore to original state
	}

	/**
	 * Creates half ellipse
	 * @param {CanvasRenderingContext2D} ctx - HTML canvas context to be used for drawing graphics
	 * @param {number} cx - X coordinate of center of ellipse
	 * @param {number} cy - Y coordinate of center of ellipse
	 * @param {number} rx - Major axis radius of ellipse
	 * @param {number} ry - Minor axis radius of ellipse
	 * @param {boolean} isSmiley - Whether or not arc is concave upward
	 */
	_getHalfArcPath(ctx, cx, cy, rx, ry, isSmiley) {
		ctx.save(); // save state
		ctx.beginPath();

		ctx.translate(cx - rx, cy - ry);
		ctx.scale(rx, ry);
		if (isSmiley) {
			ctx.arc(1, 1, 1, 0, Math.PI, false);
		} else {
			ctx.arc(1, 1, 1, Math.PI, 2 * Math.PI, false);
		}

		ctx.restore(); // restore to original state
	}

	/**
	 * @param {number[]} mousePanel - Array of mouse coordinates
	 */
	isWithin(mousePanel) {
		const {
			center,
			xDist,
			yDist
		} = this.calcEllipseData();

		// Fail if no ellipse data provided
		if (!center) return;

		const ctx = this.panel.rootToolContext;

		// within a slightly larger ellipse?
		this._getEllipsePath(
			ctx,
			center[0],
			center[1],
			Math.abs(xDist / 2) + this.OUTER_TOLERANCE,
			Math.abs(yDist / 2) + this.OUTER_TOLERANCE
		);
		const isWithinOuter = ctx.isPointInPath(mousePanel[0], mousePanel[1]);

		// within a slightly smaller ellipse?
		this._getEllipsePath(
			ctx,
			center[0],
			center[1],
			Math.abs(xDist / 2) - this.INNER_TOLERANCE,
			Math.abs(yDist / 2) - this.INNER_TOLERANCE
		);
		const isWithinInner = ctx.isPointInPath(mousePanel[0], mousePanel[1]);

		// has to be within outer but not inner
		return isWithinOuter && !isWithinInner;
	}

	onMousemove(mouse, touch, event) {

		const staticHandle = this.handle.find(h => h !== this.state.dragHandle);
		const staticCoords = staticHandle.getCoords();
		const newMouse = [...mouse];

		// Force drawing circle if dragging handle (i.e. not moving) and shift key is pressed
		if (
			this.state.dragHandle &&
			event &&
			event.shiftKey
		) {
			// Get X-distance between handles
			let xDiff = mouse[0] - staticCoords[0];
			const yDiff = mouse[1] - staticCoords[1];

			// Force Y-distance to match X-distance, account for
			if (
				(yDiff >= 0 && xDiff < 0) ||
					(yDiff < 0 && xDiff >= 0)
			) {
				// Flip sign if in lower left or upper right quadrants
				xDiff *= -1;
			}
			newMouse[1] = staticCoords[1] + xDiff;

			// Add panel top since it will be removed by parent method
			newMouse[1] += this.panel.size.top;
		}

		// Call parent method
		super.onMousemove(newMouse);
	}

	render() {
		const {
			center,
			coords,
			xDist,
			yDist
		} = this.calcEllipseData();

		// Fail if no ellipse data provided
		if (!center) return;

		const ctx = this.panel.rootToolContext;
		ctx.beginPath();

		// Set default styles
		ctx.strokeStyle = this.params.style.color;
		ctx.fillStyle = this.params.style.background.color;
		ctx.lineWidth = this.params.style.width;

		this._getEllipsePath(
			ctx,
			center[0],
			center[1],
			xDist / 2,
			yDist / 2
		);

		// Draw circle fills
		ctx.fill();

		// Draw circle lines (with no transparency)
		ctx.stroke();

		if (this.state.selected || this.state.hover) {
			this._renderBoundingBox(ctx, coords);
			this.handle[0].render();
			this.handle[1].render();
		}

		this.panel.core.onToolRender(this);
	}

	/**
	 * Draw dashed bounding box around tool
	 * @param {CanvasRenderingContext2D} ctx
	 * @param {number[]} coords
	 */
	_renderBoundingBox(ctx, coords) {
		ctx.save();
		ctx.strokeStyle = this.params.style.boundingBox.color;
		ctx.setLineDash(this.params.style.boundingBox.lineDash.split(/\s*,\s*/));
		ctx.globalAlpha = 0.5;
		ctx.beginPath();
		subpixel.rect(
			ctx,
			coords[0][0],
			coords[0][1],
			coords[1][0] - coords[0][0],
			coords[1][1] - coords[0][1]
		);
		ctx.stroke();
		ctx.restore();
	}
}

/**
 * Extended line tool
 * @constructor
 * @class ExtendedLineTool
 * @extends LineTool
 */

class ExtendedLineTool extends LineTool {
	_getDefaultParams() {
		return {
			id: 'extendedLine',
			name: 'Extended Line',
			extendLeft: true,
			extendRight: true,
			style: this.core.getStyle('tool.extendedLine')
		};
	}
}

/**
 * Gets mouse coords from DOM event
 * @param {Object} event - DOM event
 * @returns {Number[]} Array of mouse coordinates
 */
const getMouseFromDomEvent = (event = {}) => {
	// Handle touch events
	if (
		event &&
		event.type &&
		event.type.match(/touch/)
	) {
		// Assume first touch is correct location of crosshair
		const { pageX, pageY } = event.changedTouches[0];

		// Get page offset in case page has been scrolled
		const { scrollX, scrollY } = window;

		const { left, top } = event.target.getBoundingClientRect();

		return [
			(pageX - scrollX) - left,
			(pageY - scrollY) - top
		];
	}	else if (
		event &&
		event.offsetX &&
		event.offsetY
	) { // Handle mouse/pointer events (check if event is falsy because null can be passed in (CH-1119))
		return [
			event.offsetX,
			event.offsetY
		];
	}
	// Fail gracefully (effectively, there's no click on the chart)
	return [0, 0];
};

var eventUtil = {
	getMouseFromDomEvent
};

/** Extended Horizontal Tool */
class ExtendedHorizontalTool extends LineTool {

	get flavor() {
		return 'default';
	}

	_getDefaultParams() {
		return {
			id: `extendedHorizontal.${this.flavor}`,
			name: 'Extended Horizontal',
			value: null,
			extendLeft: true,
			extendRight: true,
			isImmediate: true,
			numberOfAnchors: 1,
			style: this.core.getStyle(`tool.extendedHorizontal.${this.flavor}`)
		};
	}

	init(panel, handles = []) {
		this.panel = panel;
		this.path = [];
		this.handle = handles;

		this.state = {};
		this.flags = {};
	}

	isWithin(mousePanel) {
		const handles = this.handle;

		if (handles) {
			const coords = [handles[0].getCoords()];

			if (!coords[0]) return false;
			const tolerance = 10 + this.params.style.width / 2;

			const r1 = {
				x: 0,
				y: coords[0][1] - tolerance / 2,
				width: this.panel.size.width,
				height: tolerance
			};
			const r2 = {
				x: mousePanel[0],
				y: mousePanel[1],
				width: 1,
				height: 1
			};

			return this.intersect(r1, r2);
		}
	}

	render(event) {
		let coords;
		let value;
		if (event) {
			coords = [eventUtil.getMouseFromDomEvent(event)];
		} else {
			coords = [this.handle[0].getCoords()];
		}

		if (this.state.inProgressHandle) {
			value = this.panel.yAxis.primaryScale.invert(
				subpixel.px(coords[0][1])
			);
			this.params.value = value;
		} else {
			value = this.params.value;
		}

		// couldn't find one or more handles
		if (!coords[0]) return;

		const ctx = this.panel.rootToolContext;

		ctx.beginPath();
		ctx.strokeStyle = this.params.style.color;
		ctx.fillStyle = '#FFF';
		ctx.lineWidth = this.params.style.width;

		// regular line segment
		ctx.moveTo(this.panel.size.padding.left, coords[0][1]);
		ctx.lineTo(this.panel.size.width, coords[0][1]);
		ctx.stroke();

		// create/update/delete flag
		if (!isNaN(value)) {
			if (this.flags.horizontalAnnotation) {
				this.flags.horizontalAnnotation.render();
			} else {
				this.flags.horizontalAnnotation = new HorizontalAnnotationFlag(
					{
						panel: this.panel,
						indicator: this,
						id: 'horizontalAnnotation'
					}
				);
				this.flags.horizontalAnnotation.render();
			}
		} else if (this.flags.horizontalAnnotation) {
			this.flags.horizontalAnnotation.remove();
			delete this.flags.horizontalAnnotation;
		} else {
			return; // bad value results in no further attempts to draw
		}

		this.panel.core.onToolRender(this);
	}
}

class ExtendedHorizontalToolResistance extends ExtendedHorizontalTool {
	get flavor() {
		return 'resistance';
	}
}

class ExtendedHorizontalToolSupport extends ExtendedHorizontalTool {
	get flavor() {
		return 'support';
	}
}

const { FibToolLabelFormat: FibToolLabelFormat$3 } = toolEnums.FIBONACCI;

/** Tool used to draw Fibonacci Arc on chart object */
class FibonacciArcTool extends EllipseLineTool {
	/**
	 * Retrieves default parameters for tool, including details and styles
	 * @return {Object} Object containing default tool parameters
	 * @property {String} id - Tool ID
	 * @property {String} name - Tool name
	 * @property {String} labelFormat - Style used to display tool labels
	 * @property {object} style - Tool styles
	 */
	_getDefaultParams() {
		return {
			id: 'fibArc',
			name: 'Fibonacci Arc',
			labelFormat: FibToolLabelFormat$3.DECIMAL,
			style: this.core.getStyle('tool.fibArc')
		};
	}

	/**
	 * Draws section of Fibonacci (elliptical) arc
	 * @param {CanvasRenderingContext2D} ctx - HTML canvas context to be used for drawing graphics
	 * @param {Number[]} center - Array of coordinates for center of tool
	 * @param {Number} xDist - Major axis radius of ellipse arc
	 * @param {Number} yDist - Minor axis radius of ellipse arc
	 * @param {Number} i - Index of threshold array
	 * @param {Boolean} isSmiley - Whether or not arc is concave upward
	 */
	fillHalfEllipse(ctx, center, xDist, yDist, i, isSmiley) {
		const { thresholds } = this.params.style;
		ctx.save();
		ctx.beginPath();
		if (i !== 0) {
			this._getDonutSegmentPath(
				ctx,
				center[0],
				center[1],
				xDist * thresholds[i],
				yDist * thresholds[i],
				xDist * thresholds[i - 1],
				yDist * thresholds[i - 1],
				isSmiley
			);
		} else {
			this._getDonutSegmentPath(
				ctx,
				center[0],
				center[1],
				xDist * thresholds[i],
				yDist * thresholds[i],
				1,
				1,
				isSmiley
			);
		}
		ctx.fill();
		ctx.restore();
	}

	/**
	 * Returns true if current mouse coordinates are within hitbox of this tool
	 * @param {Number[]} mousePanel - Array containing mouse coordinates
	 * @return {Boolean}
	 */
	isWithin(mousePanel) {
		return this._isWithinLineTool(mousePanel);
	}

	render() {
		const coords = [this.handle[0].getCoords(), this.handle[1].getCoords()];
		if (!coords[0] || !coords[1]) return;

		const { thresholds } = this.params.style;
		const ctx = this.panel.rootToolContext;
		const upSlope = coords[0][1] > coords[1][1];
		const xDist = Math.abs(coords[0][0] - coords[1][0]); // x radius component
		const yDist = Math.abs(coords[0][1] - coords[1][1]); // y radius component
		const center = [coords[1][0], coords[1][1]];

		// Set default styles
		ctx.font = `${this.params.style.label.font.weight} ${this.params.style.label.font.size}px ${this.params.style.label.font.family}`;
		ctx.strokeStyle = '#CCC';
		ctx.fillStyle = '#CCC';
		ctx.lineWidth = this.params.style.width;

		for (let i = thresholds.length - 1; i >= 0; i--) {
			// Set the section line and fill colors
			ctx.strokeStyle = this.params.style.color[i];
			ctx.fillStyle = this.params.style.background.color[i];

			ctx.beginPath();
			this._getHalfArcPath(
				ctx,
				center[0],
				center[1],
				xDist * thresholds[i],
				yDist * thresholds[i],
				upSlope
			);
			ctx.stroke();

			this.fillHalfEllipse(ctx, center, xDist, yDist, i, upSlope);
			ctx.textAlign = 'center';

			// Use specific label color if defined, otherwise use the section color
			ctx.fillStyle = this.params.style.label.color || this.params.style.color[i];

			if (upSlope) {
				ctx.fillText(
					this._getFibFormatValue(thresholds[i]),
					center[0],
					center[1] + yDist * thresholds[i] - 5
				);
			} else {
				ctx.fillText(
					this._getFibFormatValue(thresholds[i]),
					center[0],
					center[1] - yDist * thresholds[i] + 15
				); // to put it inside the rings
			}
		}

		ctx.textAlign = 'left'; // reset the text alignment

		if (this.state.selected || this.state.hover) {
			this.handle[0].render();
			this.handle[1].render();
		}

		this._drawControlLine(ctx, coords);

		this.panel.core.onToolRender(this);
	}
}

const { FibToolLabelFormat: FibToolLabelFormat$2 } = toolEnums.FIBONACCI;

/** Fibonacci Circle tool */
class FibonacciCircleTool extends EllipseLineTool {
	constructor(core, settings) {
		super(core, settings);
		this.RADIUS_FACTOR = 0.707;
	}

	_getDefaultParams() {
		return {
			id: 'fibCircle',
			name: 'Fibonacci Circle',
			labelFormat: FibToolLabelFormat$2.DECIMAL,
			style: this.core.getStyle('tool.fibCircle')
		};
	}

	isWithin(mousePanel) {
		return this._isWithinLineTool(mousePanel);
	}

	fillEllipse(ctx, center, adjustedXDist, adjustedYDist, i) {
		const { thresholds } = this.params.style;
		ctx.save();

		if (i > 0) {
			// Don't need to clip the center-most point
			this._getDonutPath(
				ctx,
				center[0],
				center[1],
				adjustedXDist * thresholds[i],
				adjustedYDist * thresholds[i],
				adjustedXDist * thresholds[i - 1],
				adjustedYDist * thresholds[i - 1]
			);
		} else {
			this._getDonutPath(
				ctx,
				center[0],
				center[1],
				adjustedXDist * thresholds[i],
				adjustedYDist * thresholds[i]
			);
		}
		ctx.fill();

		ctx.restore();
	}

	render() {
		const coords = [this.handle[0].getCoords(), this.handle[1].getCoords()];
		if (!coords[0] || !coords[1]) return;

		const { thresholds } = this.params.style;
		const ctx = this.panel.rootToolContext;
		const upSlope = coords[0][1] > coords[1][1];
		const xDist = coords[1][0] - coords[0][0]; // x radius component
		const yDist = coords[1][1] - coords[0][1];
		const center = [coords[0][0] + xDist / 2, coords[0][1] + yDist / 2];
		const adjustedXDist = xDist * this.RADIUS_FACTOR;
		const adjustedYDist = yDist * this.RADIUS_FACTOR;

		ctx.beginPath();

		// Set default styles
		ctx.font = `${this.params.style.label.font.weight} ${this.params.style.label.font.size}px ${this.params.style.label.font.family}`;
		ctx.strokeStyle = '#CCC';
		ctx.fillStyle = '#CCC';
		ctx.lineWidth = this.params.style.width;

		thresholds.forEach((threshold, i) => {
			// Set the section line and fill colors
			ctx.strokeStyle = this.params.style.color[i];
			ctx.fillStyle = this.params.style.background.color[i];

			this.fillEllipse(
				ctx,
				center,
				adjustedXDist,
				adjustedYDist,
				i
			);

			this._getEllipsePath(
				ctx,
				center[0],
				center[1],
				adjustedXDist * threshold,
				adjustedYDist * threshold
			);
			ctx.stroke();

			ctx.textAlign = 'center';

			// Use specific label color if defined, otherwise use the section color
			ctx.fillStyle = this.params.style.label.color || this.params.style.color[i];

			if (upSlope) {
				ctx.fillText(
					this._getFibFormatValue(threshold),
					center[0],
					center[1] + adjustedYDist * threshold + 15
				);
			} else {
				ctx.fillText(
					this._getFibFormatValue(threshold),
					center[0],
					center[1] + adjustedYDist * threshold - 4
				);
			}
		});

		if (this.state.selected || this.state.hover) {
			this.handle[0].render();
			this.handle[1].render();
		}

		ctx.textAlign = 'left'; // reset the text alignment
		this._drawControlLine(ctx, coords);

		this.panel.core.onToolRender(this);
	}
}

const { FibToolLabelFormat: FibToolLabelFormat$1 } = toolEnums.FIBONACCI;

/** Fibonacci Fan tool */
class FibonacciFanTool extends LineTool {
	constructor(core, settings) {
		super(core, settings);

		this.slopes = [];
		this.isPointingLeft = false;
		this.isPointingDown = true;
		this.BOUNDING_BOX_HEIGHT = 5;
		this.BIG_VALUE = 100000;
		this.BIG_NEGATIVE_VALUE = -100000;
	}

	_getDefaultParams() {
		return {
			id: 'fibFan',
			name: 'Fibonacci Fan',
			labelFormat: FibToolLabelFormat$1.DECIMAL,
			style: this.core.getStyle('tool.fibFan')
		};
	}

	/**
	 * @param {array} mouseCoords Coordinates of the active mouse
	 * @param {array} handleCoords Coordinates of the tool's two handles
	 * @returns {boolean} true if the two coordinates intersect
	 */
	isWithinGrid(mouseCoords, handleCoords) {
		const padding = 16;

		const boundingBox = {
			x: Math.min(handleCoords[0][0], handleCoords[1][0]),
			y: Math.min(handleCoords[0][1], handleCoords[1][1]),
			width: Math.abs(handleCoords[0][0] - handleCoords[1][0]),
			height: Math.abs(handleCoords[0][1] - handleCoords[1][1])
		};

		const mouseRect = {
			x: mouseCoords[0] - padding / 2,
			y: mouseCoords[1] - padding / 2,
			width: padding,
			height: padding
		};

		return this.intersect(boundingBox, mouseRect);
	}

	isWithin(mousePanel) {
		const handles = this.handle;
		const coords = [
			this.state.endpointLeft || handles[0].getCoords(),
			this.state.endpointRight || handles[1].getCoords()
		];

		if (!coords[0] || !coords[1]) return false;

		if (this.isWithinGrid(mousePanel, coords)) return true;
	}

	getSpacing(last, curr) {
		const range = Math.abs(last - curr);

		return this.params.style.thresholds.map(t => t * range);
	}

	getFibDistances(fibLevels) {
		const distances = [0];
		let i;

		for (i = 0; i < fibLevels.length - 1; i++) {
			// The +1 is to ensure the color bar covers the white line, tinting it
			distances.push(fibLevels[i + 1] - fibLevels[i] + 1);
		}
		return distances;
	}

	getLineFunction(ctx, x1, x2, y1, y2) {
		const rise = y2 - y1;
		const run = x2 - x1;
		const slope = rise / run;
		const bigConstant = this.isPointingLeft
			? this.BIG_NEGATIVE_VALUE
			: this.BIG_VALUE;

		// add unique slopes to this array so we can color in the slices later
		if (this.slopes.indexOf(slope) < 0) this.slopes.push(slope);

		// could be (x1, y1) OR (x2, y2)
		const yIntercept = y2 - slope * x2;

		return slope * bigConstant + yIntercept;
	}

	drawGridLinePair(ctx, coords, x, y) {
		// Add previous canvas settings to call stack
		ctx.save();

		// Describe grid path
		ctx.beginPath();
		ctx.strokeStyle = '#ccc'; // Allow user to change this style?

		// x grid line (vertical line)
		ctx.moveTo(subpixel.px(x), this.y1);
		ctx.lineTo(subpixel.px(x), subpixel.px(coords[1][1]));

		// y grid line (horizontal line)
		ctx.moveTo(this.x1, subpixel.px(y));
		ctx.lineTo(subpixel.px(coords[1][0]), subpixel.px(y));

		// Draw grid line pair
		ctx.stroke();

		// Restore previous canvas settings from call stack
		ctx.restore();
	}

	drawRay(ctx, x, y) {
		ctx.beginPath();
		ctx.moveTo(this.x1, this.y1);
		const distantY = subpixel.px(
			this.getLineFunction(ctx, this.x1, x, this.y1, y)
		);

		if (this.isPointingLeft) {
			ctx.lineTo(subpixel.px(this.BIG_NEGATIVE_VALUE), distantY);
		} else {
			ctx.lineTo(subpixel.px(this.BIG_VALUE), distantY);
		}
		ctx.stroke();
	}

	_drawControlLine(ctx, coords) {
		if (this.state.hover || this.state.selected) {
			ctx.save();
			ctx.strokeStyle = '#ccc';
			ctx.setLineDash([2, 3]);
			ctx.beginPath();
			ctx.moveTo(this.x1, this.y1);
			ctx.lineTo(subpixel.px(coords[1][0]), subpixel.px(coords[1][1]));
			ctx.stroke();
			ctx.restore();
		}
	}

	drawVerticalAndHorizontalRays(ctx) {
		// Draw vertical ray
		ctx.beginPath();
		if (this.isPointingDown) {
			ctx.moveTo(this.x1, this.y1);
			ctx.lineTo(this.x1, subpixel.px(this.BIG_VALUE));
		} else {
			ctx.moveTo(this.x1, this.y1);
			ctx.lineTo(this.x1, subpixel.px(0));
		}
		ctx.stroke();

		// Draw horizontal ray
		ctx.beginPath();
		if (this.isPointingLeft) {
			ctx.moveTo(this.x1, this.y1);
			ctx.lineTo(subpixel.px(0), this.y1);
		} else {
			ctx.moveTo(this.x1, this.y1);
			ctx.lineTo(subpixel.px(this.BIG_VALUE), this.y1);
		}
		ctx.stroke();
	}

	drawLabels(ctx, i, coords, xVal, yVal) {
		const { yAxis } = this.panel;
		const { thresholds } = this.params.style;
		ctx.font = `${this.params.style.label.font.weight} ${this.params.style.label.font.size}px ${this.params.style.label.font.family}`;
		// Use specific label color if defined, otherwise use the section color
		ctx.fillStyle = this.params.style.label.color || this.params.style.color[i];
		ctx.globalAlpha = 1.0;

		let xTextPadding = 13;
		let yTextPadding = -5;

		// x labels
		ctx.textAlign = 'center';
		if (this.isPointingDown) {
			xTextPadding = -5;
		}

		if (thresholds[i] !== 1) {
			// Don't draw the x-axis' '1' label
			ctx.fillText(
				this._getFibFormatValue(thresholds[i]),
				subpixel.px(coords[1][0] - xVal),
				coords[0][1] + xTextPadding
			);
		}

		// y labels
		ctx.textAlign = 'right';
		if (this.isPointingLeft) {
			ctx.textAlign = 'left';
			yTextPadding = 5;
		}

		// Get yAxis value for label
		const yAxisValue = yAxis.getFormatValue(
			yAxis.primaryScale.invert(subpixel.px(coords[1][1] + yVal)),
			yAxis.getFormatString(yAxis.primaryScale, 5)
		);

		ctx.fillText(
			`${this._getFibFormatValue(thresholds[i])} (${yAxisValue})`,
			coords[0][0] + yTextPadding,
			subpixel.px(coords[1][1] + yVal + 3)
		);
	}

	drawColorSlices(ctx) {
		const sortNumber = (a, b) => a - b;
		this.slopes.sort(sortNumber);

		// Set counter variables
		let countForward = 0;
		let countBackward = this.slopes.length - 1;

		// Draw color slices from outside in
		while (countForward < countBackward) {
			// Slopes and thresholds orders are opposite of eachother, so work backward through thresholds.
			// This is cumbersome and should be made cleaner in the future.
			// Calculate proper element for thresholds:
			// 1. Get last element of thresholds (length - 1)
			// 2. Subtract countForward to work backwards
			// 3. Subtract 1 to skip threshold of 1.0
			const thresholdElement = (this.params.style.thresholds.length - 1) - countForward - 1;

			// Set the section line and fill colors
			const color = this.params.style.background.color[thresholdElement];

			// Define start ray
			const startRayFirst = this.slopes[countForward];
			const startRaySecond = this.slopes[countForward + 1];

			// Define end ray
			const endRayFirst = this.slopes[countBackward - 1];
			const endRaySecond = this.slopes[countBackward];

			// Draw start and end slices
			this.drawColorSlice(ctx, startRayFirst, startRaySecond, color);
			this.drawColorSlice(ctx, endRayFirst, endRaySecond, color);

			countForward++;
			countBackward--;
		}
	}

	/**
	 * Draws filled regions between rays
	 * @param {CanvasRenderingContext2D} ctx The tool's context
	 * @param {number} firstSlope Slope of first ray
	 * @param {number} secondSlope Slope of next ray
	 * @param {string} color Fill color
	 */
	drawColorSlice(ctx, firstSlope, secondSlope, color) {
		let run = this.BIG_VALUE;
		let rise1 = run * firstSlope;
		let rise2 = run * secondSlope;

		if (this.isPointingLeft) {
			rise1 *= -1;
			rise2 *= -1;
			run *= -1;
		}

		ctx.save();
		ctx.beginPath();
		ctx.moveTo(this.x1 + run, this.y1 + rise1);
		ctx.lineTo(this.x1, this.y1);
		ctx.lineTo(this.x1 + run, this.y1 + rise2);
		ctx.closePath();
		ctx.fillStyle = color;
		ctx.fill();
		ctx.restore();
	}

	render() {
		// Get coordinates for drawing elements
		const coords = [this.handle[0].getCoords(), this.handle[1].getCoords()];

		// Only draw if coordinates exist
		if (!coords[0] || !coords[1]) return;

		const { thresholds } = this.params.style;
		this.slopes = [];
		this.x1 = subpixel.px(coords[0][0]);
		this.y1 = subpixel.px(coords[0][1]);
		this.isPointingDown = coords[0][1] < coords[1][1];
		this.isPointingLeft = coords[1][0] - coords[0][0] < 0;

		const ctx = this.panel.rootToolContext;
		let yVal;
		let xVal;
		const fibYSpacing = this.getSpacing(coords[0][1], coords[1][1]);
		const fibXSpacing = this.getSpacing(coords[1][0], coords[0][0]);

		// Get line width for tool
		ctx.lineWidth = this.params.style.width;

		// Set default line color
		ctx.strokeStyle = '#ccc';

		// Draw control line for tool
		this._drawControlLine(ctx, coords);

		// Draw left and bottom bounding rays for tool
		this.drawVerticalAndHorizontalRays(ctx, coords);

		for (let i = 0; i < thresholds.length; i++) {
			yVal = this.isPointingDown ? -fibYSpacing[i] : fibYSpacing[i];
			xVal = this.isPointingLeft ? -fibXSpacing[i] : fibXSpacing[i];

			ctx.strokeStyle = this.params.style.color[i];
			ctx.fillStyle = ctx.strokeStyle;

			// Skip drawing the following on last pass through loop
			if (i !== thresholds.length - 1) {
				// the last one wreaks havoc on my slope/intercept function
				this.drawGridLinePair(
					ctx,
					coords,
					coords[1][0] - xVal,
					coords[1][1] + yVal
				);

				// Draw upper quadrant ray
				this.drawRay(ctx, coords[1][0] - xVal, coords[1][1]);

				// Draw lower quadrant ray
				this.drawRay(ctx, coords[1][0], coords[1][1] + yVal);
			}

			// Draw retracement labels around grid
			this.drawLabels(ctx, i, coords, xVal, yVal);
		}

		// Draw the fills between the lines
		this.drawColorSlices(ctx);

		this.handle[0].render();
		this.handle[1].render();
		this.panel.core.onToolRender(this);
	}
}

const { FibToolLabelFormat } = toolEnums.FIBONACCI;

/** Fibonacci retracement tool */
class FibonacciLineTool extends LineTool {
	constructor(core, settings, flavor) {
		super(core, settings);

		if (flavor && flavor.toLowerCase() === 'extended') {
			this.isExtended = true;
		} else {
			this.isExtended = false;
		}
		this.thresholds = this.params.style.thresholds;
		this.levelLines = {
			x: null,
			y: [],
			isFlipped: false
		};
		this.BOUNDING_BOX_HEIGHT = 5;
	}

	_getDefaultParams() {
		return {
			id: 'fibonacci',
			name: 'Fibonacci Retracement',
			showExtensions: this.isExtended,
			labelFormat: FibToolLabelFormat.DECIMAL,
			style: this.core.getStyle('tool.fibonacci')
		};
	}

	isWithinFibLines(mousePanel, coords) {
		let boundingBox = {};
		const mouseRect = {
			x: mousePanel[0],
			y: mousePanel[1],
			width: 1,
			height: 1
		};

		for (let i = 0; i < this.levelLines.y.length; i++) {
			if (this.levelLines.isFlipped) {
				boundingBox = {
					x: 0,
					y: this.levelLines.y[i],
					width: coords[0][0],
					height: this.BOUNDING_BOX_HEIGHT
				};
			} else {
				boundingBox = {
					x: this.levelLines.x,
					y: this.levelLines.y[i],
					width: this.panel.size.width + this.panel.size.padding.left,
					height: this.BOUNDING_BOX_HEIGHT
				};
			}

			if (this.intersect(boundingBox, mouseRect)) {
				return i;
			}
		}

		return -1;
	}

	/** Define hitbox to be full rectangle */
	isWithin(mousePanel) {
		const handles = this.handle;
		const coords = [
			this.state.endpointLeft || handles[0].getCoords(),
			this.state.endpointRight || handles[1].getCoords()
		];

		// couldn't find one or more handles
		if (!coords[0] || !coords[1]) return false;

		if (
			this.isWithinFibLines(mousePanel, coords) >= 0 ||
			this._isWithinLineTool(mousePanel)
		) {
			return true;
		}
	}

	getLevels(lastY, currY) {
		const range = Math.abs(lastY - currY);
		const vals = this.params.style.thresholds;

		const valuesToShow = [
			0,
			vals[1] * range,
			vals[2] * range,
			vals[3] * range,
			vals[4] * range,
			vals[5] * range
		];

		if (this.params.showExtensions) {
			valuesToShow.push(
				vals[6] * range,
				vals[7] * range,
				vals[8] * range,
				vals[9] * range
			);
		}

		return valuesToShow;
	}

	getFibHeights(fibLevels) {
		// Zero is necessary
		const heights = [0];
		for (let i = 0; i < fibLevels.length - 1; i++) {
			// The +1 is to ensure the color bar covers the white line, tinting it
			heights.push(fibLevels[i + 1] - fibLevels[i] + 1);
		}

		return heights;
	}

	render() {
		const coords = this.handle.map(h => h.getCoords());

		if (!coords[0] || !coords[1]) return;

		this.levelLines.x = coords[0][0];
		const ctx = this.panel.rootToolContext;
		const upSlope = coords[0][1] < coords[1][1];
		let textPadding = -5;
		const fibLevels = this.getLevels(coords[0][1], coords[1][1]);

		ctx.save();
		ctx.beginPath();
		ctx.font = `${this.params.style.label.font.weight} ${this.params.style.label.font.size}px ${this.params.style.label.font.family}`;
		ctx.textAlign = 'right';
		ctx.lineWidth = this.params.style.width;

		// When the retracement is rendered pointing left
		this.levelLines.isFlipped = coords[1][0] - coords[0][0] < 0;
		if (this.levelLines.isFlipped) {
			ctx.textAlign = 'left';
			textPadding = 5;
		}

		// control line
		ctx.strokeStyle = '#ccc';
		ctx.setLineDash([2, 3]);
		ctx.moveTo(coords[0][0], coords[0][1]);
		ctx.lineTo(coords[1][0], coords[1][1]);
		ctx.stroke();

		// Reset line dashed setting back to solid
		ctx.setLineDash([]);

		const heights = this.getFibHeights(fibLevels);
		const { yAxis } = this.panel;
		let height;
		let fillYValue;

		// Draw Fib lines
		for (let i = 0; i < fibLevels.length; i++) {
			// Start new path so only one line is drawn per pass through loop
			ctx.beginPath();
			const val = upSlope ? -fibLevels[i] : fibLevels[i];
			const prevVal = upSlope ? -fibLevels[i - 1] : fibLevels[i - 1];

			ctx.fillStyle = this.params.style.background.color[i];
			ctx.strokeStyle = this.params.style.color[i];
			ctx.moveTo(coords[0][0], subpixel.px(coords[1][1] + val));
			height = heights[i];

			this.levelLines.y[i] = subpixel.px(coords[1][1] + val);

			fillYValue = upSlope
				? subpixel.px(coords[1][1] + prevVal - heights[i])
				: subpixel.px(coords[1][1] + prevVal);

			if (this.levelLines.isFlipped) {
				ctx.lineTo(0, subpixel.px(coords[1][1] + val));
				ctx.fillRect(0, fillYValue, Math.abs(coords[0][0]), height);
			} else {
				ctx.lineTo(
					this.panel.size.width + this.panel.size.padding.left,
					subpixel.px(coords[1][1] + val)
				);
				ctx.fillRect(
					coords[0][0],
					fillYValue,
					this.panel.size.width +
						this.panel.size.padding.left +
						Math.abs(coords[0][0]),
					height
				);
			}

			// Get yAxis value for label
			const yAxisValue = yAxis.getFormatValue(
				yAxis.primaryScale.invert(subpixel.px(coords[1][1] + val)),
				yAxis.getFormatString(yAxis.primaryScale, 5)
			);

			// Use specific label color if defined, otherwise use the section color
			ctx.fillStyle = this.params.style.label.color || this.params.style.color[i];
			// Draw label
			ctx.fillText(
				`${this._getFibFormatValue(this.params.style.thresholds[i])} (${yAxisValue})`,
				coords[0][0] + textPadding,
				subpixel.px(coords[1][1] + val + 3)
			);

			// Draw the line from path specified above
			ctx.stroke();
		}
		ctx.restore();

		this.handle[0].render();
		this.handle[1].render();

		this.panel.core.onToolRender(this);
	}
}

/** Gann Fan tool */
class GannFanLineTool extends LineTool {
	constructor(core, settings) {
		super(core, settings);

		this.thresholds = this.params.style.thresholds;
		this.gannLabelsUpper = this.params.style.gannLabels;
		this.gannLabelsLower = this.gannLabelsUpper.map(label => label.split('/').reverse().join('/'));
	}

	_getDefaultParams() {
		return {
			id: 'gannFan',
			name: 'Gann Fan',
			style: this.core.getStyle('tool.gannFan')
		};
	}

	render() {
		const coords = [this.handle[0].getCoords(), this.handle[1].getCoords()];

		if (!coords[0] || !coords[1]) return;

		const ctx = this.panel.rootToolContext;
		let i;
		let newX;
		let newY;
		const x1 = coords[0][0];
		const y1 = coords[0][1];
		const x2 = coords[1][0];
		let y2 = coords[1][1];
		const distX = x2 - x1;
		const distY = y2 - y1;
		const { style } = this.params;
		const { color, thresholds } = style;

		ctx.lineWidth = style.width;
		ctx.font = `${this.params.style.label.font.weight} ${this.params.style.label.font.size}px ${this.params.style.label.font.family}`;

		// Middle line
		this.renderFan(
			ctx,
			color[0],
			this.gannLabelsUpper[0],
			x1,
			y1,
			x2,
			y2,
			x2 - x1,
			y2 - y1
		);

		// Lower lines
		ctx.textAlign = 'left';
		for (i = 1; i < thresholds.length; i++) {
			newY = y2 - (distY - distY * thresholds[i]);

			this.renderFan(
				ctx,
				color[i],
				this.gannLabelsLower[i],
				x1,
				y1,
				x2,
				newY,
				x2 - x1,
				newY - y1
			);
		}

		// Upper lines
		y2 = coords[1][1];
		ctx.textAlign = 'right';
		for (i = 1; i < thresholds.length; i++) {
			newX = x2 - (distX - distX * thresholds[i]);

			this.renderFan(
				ctx,
				color[i],
				this.gannLabelsUpper[i],
				x1,
				y1,
				newX,
				y2,
				newX - x1,
				y2 - y1
			);
		}

		this.handle[0].render();
		this.handle[1].render();

		this.panel.core.onToolRender(this);
	}

	renderFan(ctx, color, label, x1, y1, x2, y2, distX, distY) {
		ctx.beginPath();
		ctx.strokeStyle = color;
		ctx.fillStyle = color;
		ctx.moveTo(x1, y1);
		ctx.lineTo(x2, y2);
		ctx.stroke();

		this._renderRay(ctx, x2, y2, distX, distY);
		ctx.stroke();

		// Use specific label color if defined, otherwise use the section color
		ctx.fillStyle = this.params.style.label.color || color;
		ctx.fillText(label, x2, y2);
	}
}

/** Horizontal line tool */
class HorizontalLineTool extends LineTool {
	get shouldSnap() { return false; }

	_getDefaultParams() {
		return {
			id: 'horizontalLine',
			name: 'Horizontal Line',
			lockVertical: true,
			style: this.core.getStyle('tool.horizontalLine')
		};
	}
}

/** Measurement line tool base class (line + time and percent label) */
class MeasurementLineTool extends Tool {
	_getDefaultParams() {
		return {
			id: 'measurementline',
			name: 'Measurement Line',
			style: this.core.getStyle('tool.measurementLine')
		};
	}

	onClickCanvas(mouse) {
		const core = this.panel.core;
		const { state } = core;
		const isUpper = this.panel.isUpper;
		const numberOfAnchors = this.params.numberOfAnchors || 2;

		// called from core.onClick() on both first and second clicks.
		if (!this.state.inProgressHandle) {
			let mousePanel = [mouse[0], mouse[1] - this.panel.size.top];

			// use snapHandle coords if available
			if (isUpper && state.snapHandle && state.snapHandle.coords) {
				mousePanel = [
					state.snapHandle.coords[0],
					state.snapHandle.coords[1] - this.panel.size.top
				];
			}

			// first click - create and render first handle
			this.handle[0] = this.createHandle(mousePanel);
			this.handle[0].state.visible = true;

			// create second handle (for its coords) but don't render
			this.handle[1] = this.createHandle(mousePanel);

			// Exit out of the handle placement if only one handle is necessary
			if (numberOfAnchors === 1) {
				this.state.inProgressHandle = false;
				this.state.dragHandle = null;
				return;
			}
			this.state.dragHandle = this.handle[1];

			// tool is now active
			this.state.inProgressHandle = true;
		} else {
			// use snapHandle coords if available
			if (
				isUpper &&
				state.snapHandle &&
				state.snapHandle.coords &&
				!this.params.lockVertical
			) {
				const snapDateIdx = Math.round(
					this.panel.xAxis.primaryScale.invert(state.snapHandle.coords[0])
				);

				if (core.exchangeDates[snapDateIdx]) {
					let val = this.panel.yAxis.primaryScale.invert(
						state.snapHandle.coords[1]
					);

					// always store handle values in actuals
					if (this.panel.isNormalized) {
						val = this.panel.normalizedToActual(val);
					}

					this.state.dragHandle.params.value = val;
					this.state.dragHandle.params.date = new Date(
						core.exchangeDates[snapDateIdx]
					);
				}
			}

			// second click
			this.state.inProgressHandle = false; // no longer actively drawing a change in the tool
			this.state.dragHandle = null;
			this.handle[1].state.visible = true;

			this.onComplete();
		}
	}

	onMousemove(mouse) {
		const core = this.panel.core;
		const mousePanel = [mouse[0], mouse[1] - this.panel.size.top];
		core.onToolMousemove(this);

		if (this.state.inProgressHandle) {
			let idx = Math.round(
				this.panel.xAxis.primaryScale.invert(mousePanel[0])
			);

			idx = Math.min(core.exchangeDates.length - 1, idx);
			idx = Math.max(0, idx);

			this.state.dragHandle.params.date = core.exchangeDates[idx];

			if (!this.params.lockVertical) {
				let val = this.panel.yAxis.primaryScale.invert(mousePanel[1]);

				// always store handle values in actuals
				if (this.panel.isNormalized) {
					val = this.panel.normalizedToActual(val);
				}

				this.state.dragHandle.params.value = val;
			}
		} else {
			this.dragTool(mousePanel);
		}
	}

	/**
	 * return true if current mouse coordinates are within hitbox of this tool
	 */
	isWithin(mousePanel) {
		return this._isWithinLineTool(mousePanel);
	}

	_isWithinLineTool(mousePanel) {
		const handles = this.handle;

		if (handles.length === 2) {
			// use either regular handle coords or special endpoint coords (if present)
			const coords = [
				this.state.endpointLeft || handles[0].getCoords(),
				this.state.endpointRight || handles[1].getCoords()
			];

			// couldn't find one or more handles
			if (!coords[0] || !coords[1]) {
				return false;
			}

			let tolerance = 5 + this.params.style.width / 2;
			const isVertical = coords[0][0] === coords[1][0];
			const left = Math.min(coords[0][0], coords[1][0]);
			const right = Math.max(coords[0][0], coords[1][0]);
			const top = Math.min(coords[0][1], coords[1][1]);
			const bottom = Math.max(coords[0][1], coords[1][1]);

			// use a larger hitbox for touch events
			if (this.panel.core.state.isTouchDevice) {
				tolerance += 10;
			}

			// handle vertical lines as a simple bounding box intersection check
			if (isVertical) {
				const r1 = {
					x: left - tolerance,
					y: top,
					width: right - left + 2 * tolerance,
					height: bottom - top
				};
				const r2 = {
					x: mousePanel[0],
					y: mousePanel[1],
					width: 1,
					height: 1
				};

				return this.intersect(r1, r2);
			}

			// handle diagonal or horizontal lines with a distance calculation
			const linepoint = this._getClosestPoint(
				coords[0][0],
				coords[0][1],
				coords[1][0],
				coords[1][1],
				mousePanel[0],
				mousePanel[1]
			);
			const dx = mousePanel[0] - linepoint[0];
			const dy = mousePanel[1] - linepoint[1];
			const distance = Math.abs(Math.sqrt(dx * dx + dy * dy));

			if (
				distance > tolerance ||
				mousePanel[0] < left ||
				mousePanel[0] > right
			) {
				return false;
			} else if (distance < tolerance) {
				return true;
			}
		}
	}

	_getPctChange(rightHandle, leftHandle) {

		const pctChange =
			(
				(
					Number(rightHandle.params.value) -
					Number(leftHandle.params.value)
				) /
				Math.abs(Number(leftHandle.params.value))
			) *
			100;

		return pctChange;
	}

	getLabel(rightHandle, leftHandle) {
		const pctChange = this._getPctChange(rightHandle, leftHandle);
		let prefix = '+';
		let suffix = 'days';
		const timeframe = Math.round(
			(rightHandle.params.date - leftHandle.params.date) /
					(1000 * 60 * 60 * 24)
		);

		// "+/-" prefix
		if (pctChange < 0) prefix = '';

		// don't show timeframe on intraday
		if (this.panel.core.state.isIntraday || timeframe < 1) {
			return `${prefix + pctChange.toFixed(2)}%`;
		}

		// day/days
		if (timeframe === 1) suffix = 'day';

		return `${prefix}${pctChange.toFixed(2)}% (${timeframe} ${suffix})`;
	}

	render() {
		const coords = [this.handle[0].getCoords(), this.handle[1].getCoords()];

		// Couldn't find one or more handles
		if (!coords[0] || !coords[1]) return;

		const ctx = this.panel.rootToolContext;

		let rightHandle = this.handle[0];
		let leftHandle = this.handle[1];
		let rightCoords = coords[0];
		let leftCoords = coords[1];

		if (coords[1][0] >= coords[0][0]) {
			// 2nd handle is further right
			rightHandle = this.handle[1];
			leftHandle = this.handle[0];
			rightCoords = coords[1];
			leftCoords = coords[0];
		}

		// Draw background rectangle (optional)
		if (this.params.style.background.color) {
			ctx.save();
			ctx.fillStyle = this.params.style.background.color;
			ctx.fillRect(
				leftCoords[0],
				0,
				rightCoords[0] - leftCoords[0],
				this.panel.size.height
			);
			ctx.restore();
		}

		// Draw line segment
		ctx.beginPath();
		ctx.strokeStyle = this.params.style.color;
		ctx.lineWidth = this.params.style.width;
		ctx.moveTo(coords[0][0], coords[0][1]);
		ctx.lineTo(coords[1][0], coords[1][1]);
		ctx.stroke();

		// Check if a custom label callback is registered for MeasurementLine
		const isLabelCallbackRegistered = this.panel.core.callbackManager.isRegistered('TOOL_MEASUREMENTLINE_LABEL');

		// Prepare arguments for the callback
		const callbackArgs = {
			start: {
				date: new Date(leftHandle.params.date),
				value: Number(leftHandle.params.value)
			},
			end: {
				date: new Date(rightHandle.params.date),
				value: Number(rightHandle.params.value)
			},
			pctChange: this._getPctChange(rightHandle, leftHandle)
		};

		// Determine the label text
		const labelText = isLabelCallbackRegistered
			? this.panel.core.callbackManager.emit('TOOL_MEASUREMENTLINE_LABEL', callbackArgs)
			: this.getLabel(rightHandle, leftHandle);

		// Set text styles
		ctx.textAlign = 'center';
		ctx.textBaseline = 'middle';
		const fontSize = this.params.style.font.size;
		const fontSizePx = fontSize * 1.333;
		const labelMargin = this.params.style.label.margin || 0;
		const labelPadding = this.params.style.label.background.padding;
		ctx.font = this.panel.getFontStyle(
			this.params.style.font.weight,
			fontSize,
			this.params.style.font.family
		);
		ctx.fillStyle = this.params.style.label.color;

		// Determine the text and its measurements
		const textMetrics = ctx.measureText(labelText);
		const textWidth = textMetrics.width;
		const textHeight = fontSizePx;

		// Calculate position and size for text and its background
		let backgroundX = rightCoords[0] + labelMargin;
		let backgroundY = rightCoords[1] - textHeight / 2 - labelPadding.top;
		const backgroundWidth = textWidth + labelPadding.left + labelPadding.right;
		const backgroundHeight = textHeight + labelPadding.top + labelPadding.bottom;
		let textX = backgroundX + backgroundWidth / 2; // Initially center of the background rectangle
		let textY;

		// Horizontal Collision detection with the right edge of the panel
		const panelRightEdge = this.panel.size.width;
		const labelRightEdge = backgroundX + backgroundWidth;
		if (labelRightEdge + 5 > panelRightEdge) {
			// Adjust backgroundX and textX to be right-justified to the right handle
			backgroundX = rightCoords[0] - backgroundWidth - labelMargin;
			textX = backgroundX + backgroundWidth / 2;
		}

		// Vertical Collision detection with the top and bottom edges of the panel
		const panelTopEdge = 0;
		const panelBottomEdge = this.panel.size.height;
		if (backgroundY < panelTopEdge + 5) {
			// If the top edge is too close to the panel's top, adjust it downwards
			backgroundY = panelTopEdge + 5;
		} else if (backgroundY + backgroundHeight > panelBottomEdge - 5) {
			// If the bottom edge is too close to the panel's bottom, adjust it upwards
			backgroundY = panelBottomEdge - backgroundHeight - 5;
		}
		textY = backgroundY + backgroundHeight / 2; // Adjust textY based on the new backgroundY

		// Draw background and border for the label (optional)
		if (this.params.style.label.background) {
			ctx.save();
			const border = this.params.style.label.background.border;
			const radius = this.params.style.label.background.radius || 0;
			const fillStyle = this.params.style.label.background.color;
			const strokeStyle = border.color ? {
				color: border.color,
				width: border.width,
				lineDash: border.lineDash
			} : null;

			this._drawRoundedRect(ctx, backgroundX, backgroundY, backgroundWidth, backgroundHeight, radius, fillStyle, strokeStyle);

			ctx.restore();
		}

		// Draw the label text
		ctx.fillText(
			labelText,
			textX,
			textY
		);

		// Handles
		this.handle[0].render();
		this.handle[1].render();

		this.panel.core.onToolRender(this);
	}


	// Function to draw a rounded rectangle
	_drawRoundedRect(ctx, x, y, width, height, radius, fillStyle, strokeStyle) {
		// Draw the path of the rounded rectangle
		ctx.beginPath();
		ctx.moveTo(x + radius, y);
		ctx.arcTo(x + width, y, x + width, y + height, radius);
		ctx.arcTo(x + width, y + height, x, y + height, radius);
		ctx.arcTo(x, y + height, x, y, radius);
		ctx.arcTo(x, y, x + width, y, radius);
		ctx.closePath();

		// Apply fill style if provided
		if (fillStyle) {
			ctx.fillStyle = fillStyle;
			ctx.fill();
		}

		// Apply stroke style if provided
		if (strokeStyle) {
			ctx.strokeStyle = strokeStyle.color;
			ctx.lineWidth = strokeStyle.width;
			if (strokeStyle.lineDash) {
				ctx.setLineDash(strokeStyle.lineDash.split(/\s*,\s*/));
			}
			ctx.stroke();
			ctx.setLineDash([]); // Reset line dash
		}
	}


	distance(coord1, coord2) {
		let xs = coord2[0] - coord1[0];
		let ys = coord2[1] - coord1[1];

		xs *= xs;
		ys *= ys;

		return Math.sqrt(xs + ys);
	}
}

/**
 * Ray tool
 * @constructor
 * @class RayLineTool
 * @extends LineTool
 */

class RayLineTool extends LineTool {
	_getDefaultParams() {
		return {
			id: 'ray',
			name: 'Ray',
			extendRight: true,
			style: this.core.getStyle('tool.ray')
		};
	}
}

/** Rectangle tool */
class RectLineTool extends LineTool {
	_getDefaultParams() {
		return {
			id: 'rectangle',
			name: 'Rectangle',
			style: this.core.getStyle('tool.rectangle')
		};
	}

	/** Define hitbox to be four edges of rectangle */
	isWithin(mousePanel) {
		const handles = this.handle;
		const hitboxSize = 10;
		const halfSize = hitboxSize / 2;

		if (handles.length === 2) {
			const coords = [handles[0].getCoords(), handles[1].getCoords()];

			if (!coords[0] || !coords[1]) {
				return false;
			}

			// create a hitbox for each edge of the rectangle
			const boundingBox = {
				x: Math.min(coords[0][0], coords[1][0]),
				y: Math.min(coords[0][1], coords[1][1]),
				width: Math.abs(coords[0][0] - coords[1][0]),
				height: Math.abs(coords[0][1] - coords[1][1])
			};
			const rectMouse = {
				x: mousePanel[0],
				y: mousePanel[1],
				width: 1,
				height: 1
			};
			const rectLeft = {
				x: boundingBox.x - halfSize,
				y: boundingBox.y,
				width: hitboxSize,
				height: boundingBox.height
			};
			const rectRight = {
				x: boundingBox.x + boundingBox.width - halfSize,
				y: boundingBox.y,
				width: hitboxSize,
				height: boundingBox.height
			};
			const rectTop = {
				x: boundingBox.x,
				y: boundingBox.y - halfSize,
				width: boundingBox.width,
				height: hitboxSize
			};
			const rectBottom = {
				x: boundingBox.x,
				y: boundingBox.y + boundingBox.height - halfSize,
				width: boundingBox.width,
				height: hitboxSize
			};

			// check intersection against all edges
			return (
				this.intersect(rectLeft, rectMouse) ||
				this.intersect(rectRight, rectMouse) ||
				this.intersect(rectTop, rectMouse) ||
				this.intersect(rectBottom, rectMouse)
			);
		}
	}

	render() {
		const coords = [this.handle[0].getCoords(), this.handle[1].getCoords()];
		if (!coords[0] || !coords[1]) return;

		const ctx = this.panel.rootToolContext;

		ctx.beginPath();

		ctx.strokeStyle = this.params.style.color;
		ctx.lineWidth = this.params.style.width;

		// rectangle
		subpixel.rect(
			ctx,
			coords[0][0],
			coords[0][1],
			coords[1][0] - coords[0][0],
			coords[1][1] - coords[0][1]
		);

		if (this.params.style.background.color) {
			ctx.fillStyle = this.params.style.background.color;
			ctx.fill();
		}

		ctx.stroke();

		if (this.state.selected || this.state.hover) {
			this.handle[0].render();
			this.handle[1].render();
		}

		this.panel.core.onToolRender(this);
	}
}

/** Text tool */
class TextTool extends Tool {
	/**
	 * @param {ModchartsInteractive} core - Modcharts chart object
	 * @param {object} settings - Parameters for tool
	 */
	constructor(core, settings) {
		super(core, settings);

		/** @type {number} */
		this.offsetX = 10;
		/** @type {number} */
		this.offsetY = 4;
		/** @type {number} */
		this.TOLERANCE = 0; // Adds margin to hitbox for isWithin
		/** @type {number} */
		this.yValue = 0; // Makes height of box dynamic across methods
	}

	/**
	 * @return {object} Object containing default tool parameters
	 * @property {string} id - Tool ID
	 * @property {string} name - Tool name
	 * @property {string} value - Text to display in text box
	 * @property {object} style - Text box styles
	 */
	_getDefaultParams() {
		return {
			id: 'text',
			name: 'Text',
			value: '',
			style: this.core.getStyle('tool.text')
		};
	}

	/**
	 * Return a collection of words with additional metadata about each word (break,
	 * value, and whether it is followed by a word boundary).
	 *
	 * Long words (that don't fit on their own line with the current width) are split
	 * here into individual characters, and tagged to not receive a trailing
	 * word boundary when being rendered. This will allow them to wrap in the
	 * container while mid-word.
	 * @param {CanvasRenderingContext2D} ctx - HTML canvas context to be used for drawing graphics
	 * @return {string[]} Adjusted array of words to render in text box
	 */
	getParsedWords(ctx) {
		const self = this;
		let chars = [];
		let wordsOut = [];
		const words = this._parseValue();

		// build up new output array
		words.forEach(word => {
			if (!word.length) return;

			// trim trailing spaces
			word = word.replace(/^\s+/, '').replace(/\s+$/, '');

			if (word === '<br/>') {
				// support breaks
				wordsOut.push({
					break: true
				});
			} else if (
				ctx.measureText(word).width + self.offsetX * 2 >
				self.params.style.boxWidth
			) {
				// convert long words to characters with no boundary
				chars = word.split('').map((char, index, mappedChars) => {
					return {
						value: char,
						boundary: index === mappedChars.length - 1 // final character should be followed by a word boundary
					};
				});

				// add characters
				wordsOut = wordsOut.concat(chars);
			} else {
				// normal words
				wordsOut.push({
					value: word,
					boundary: true
				});
			}
		});
		return wordsOut;
	}

	/**
	 * Define hitbox to be full rectangle of text tool bounding box plus tolerance
	 * @param {number[]} mousePanel - Array of mouse coordinates
	 */
	isWithin(mousePanel) {
		if (this.handle.length === 2) {
			const handle1Coords = this.handle[0].getCoords();
			const handle2Coords = this.handle[1].getCoords();

			// Fail if handles aren't defined
			if (!handle1Coords || !handle2Coords) {
				return false;
			}

			// Define tolerance area for text box
			const r1 = {
				x: Math.min(handle1Coords[0], handle2Coords[0]) - this.TOLERANCE,
				y: Math.min(handle1Coords[1], handle2Coords[1]) - this.TOLERANCE,
				width: Math.abs(handle1Coords[0] - handle2Coords[0]) + 2 * this.TOLERANCE,
				height: Math.abs(handle1Coords[1] - handle2Coords[1]) + 2 * this.TOLERANCE
			};

			// Define 1x1 rectangle at mouse location
			const r2 = {
				x: mousePanel[0],
				y: mousePanel[1],
				width: 1,
				height: 1
			};

			return this.intersect(r1, r2);
		}

		// Fail if nothing works
		return false;
	}

	/**
	 * @param {number[]} mouse - Array of mouse coordinates
	 */
	onClickCanvas(mouse) {
		if (!this.handle.length) {
			const mousePanel = [mouse[0], mouse[1] - this.panel.size.top];

			// first click - create first handle
			this.handle[0] = this.createHandle(mousePanel);
			this.handle[0].state.visible = true;

			// create second handle
			this.handle[1] = this.createHandle([
				mousePanel[0] + this.params.style.boxWidth,
				mousePanel[1] + this.params.style.boxHeight
			]);
			this.handle[1].state.visible = true;

			this.handle[1].params.date = null;
			this.handle[1].params.value = null;
			this.handle[1].params.pairedHandle = 0;
			this.handle[1].params.pairedOffsetX = this.params.style.boxWidth;
			this.handle[1].params.pairedOffsetY = this.params.style.boxHeight;

		}

		this.state.inProgressHandle = true;
		this.onComplete();
	}

	/**
	 * @param {number[]} mouse - Array of mouse coordinates
	 */
	onMousemove(mouse) {
		this.core.onToolMousemove(this);

		const mousePanel = [mouse[0], mouse[1] - this.panel.size.top];
		const idx = Math.round(this.panel.xAxis.primaryScale.invert(mousePanel[0]));
		const isLeft = this.state.dragHandle === this.handle[0];
		const isRight = this.state.dragHandle === this.handle[1];

		if (isLeft) {
			// dragging nw handle

			if (this.panel.core.exchangeDates[idx]) {
				let val = this.panel.yAxis.primaryScale.invert(mousePanel[1]);

				// always store handle values in actuals
				if (this.panel.isNormalized) {
					val = this.panel.normalizedToActual(val);
				}

				this.state.dragHandle.params.value = val;
				this.state.dragHandle.params.date = this.panel.core.exchangeDates[
					idx
				];
			}
		} else if (isRight) {
			// dragging se handle

			const pairedCoords = this.handle[
				this.state.dragHandle.params.pairedHandle
			].getCoords();

			if (!pairedCoords) return;

			this.state.dragHandle.params.pairedOffsetX =
				mousePanel[0] - pairedCoords[0];
			this.state.dragHandle.params.pairedOffsetY =
				mousePanel[1] - pairedCoords[1];

			const coords = [
				this.handle[0].getCoords(),
				this.handle[1].getCoords()
			];

			this.params.style.boxWidth = Math.round(coords[1][0] - coords[0][0]);
			this.params.style.boxHeight = Math.round(coords[1][1] - coords[0][1]);
		} else {
			// Dragging full tool
			this.dragTool(mousePanel);
		}
	}

	/**
	 * Clean up tool value
	 * @return {string[]} Array of cleaned up words
	 */
	_parseValue() {
		// special chars
		this.params.value = this.params.value
			.replace(/&#39;/g, '\'')
			.replace(/&amp;/g, '&')
			.replace(/&gt;/g, '>')
			.replace(/&lt;/g, '<')
			.replace(/\n/g, ' <br/> ');

		try {
			// CW-490
			this.params.value = decodeURIComponent(this.params.value);
		} catch (e) {
			this.params.value = window.unescape(this.params.value);
		}

		return this.params.value.split(/\s/);
	}

	render() {
		const coords = [this.handle[0].getCoords(), this.handle[1].getCoords()];

		if (!coords[0] || !coords[1]) return;

		const ctx = this.panel.rootToolContext;
		ctx.textAlign = 'left';
		ctx.textBaseline = 'top';
		ctx.font = this.panel.getFontStyle(
			this.params.style.font.weight,
			this.params.style.font.size,
			this.params.style.font.family
		);

		ctx.strokeStyle = this.params.style.color;
		ctx.fillStyle = this.params.style.background.color;
		ctx.lineWidth = this.params.style.width;

		ctx.beginPath();

		ctx.rect(
			coords[0][0],
			coords[0][1],
			coords[1][0] - coords[0][0],
			coords[1][1] - coords[0][1]
		);
		// Render rectangle background
		ctx.fill();


		// Clip the text we're about to draw to the coords rectangle
		ctx.save();
		ctx.clip();

		// Render the words
		this._renderWords(ctx, coords);

		// Finish the clip
		ctx.restore();

		// Render bounding box
		if (this.state.selected || this.state.hover) {
			this._renderBoundingBox(ctx, coords);
			this.handle[0].render();
			this.handle[1].render();
		}


		this.panel.core.onToolRender(this);
	}

	/**
	 * Draw dashed bounding box around tool
	 * @param {CanvasRenderingContext2D} ctx
	 * @param {number[]} coords
	 */
	_renderBoundingBox(ctx, coords) {
		const { lineDash } = this.params.style;
		ctx.save();
		ctx.strokeStyle = this.params.style.color;
		if (lineDash) ctx.setLineDash(lineDash.split(/\s*,\s*/));
		ctx.beginPath();
		subpixel.rect(
			ctx,
			coords[0][0],
			coords[0][1],
			coords[1][0] - coords[0][0],
			coords[1][1] - coords[0][1]
		);
		ctx.stroke();
		ctx.restore();
	}

	/**
	 * Render parsed words to canvas
	 * @param {CanvasRenderingContext2D} ctx - HTML canvas context to be used for drawing graphics
	 * @param {number[]} coords - Array of text box handle coordinates
	 * @param {string} value - Text to render in text box
	 */
	_renderWords(ctx, coords, value) {
		const words = this.getParsedWords(ctx, value);
		const self = this;
		let width = 0;
		let x = 0;
		let y = 0;

		ctx.save();
		ctx.fillStyle = this.params.style.text.color;

		// render words
		words.forEach(word => {
			// crlf for breaks
			if (word.break) {
				x = 0;
				y++;
				return;
			}

			// measure word with or without trailing space
			if (word.value) {
				if (word.boundary === false) {
					width = ctx.measureText(word.value).width;
				} else {
					width = ctx.measureText(`${word.value} `).width;
				}
			}

			// crlf if we collide with edge
			if (x + width + self.offsetX > self.params.style.boxWidth) {
				x = 0;
				y++;
				this.yValue = y;
			}

			// draw
			ctx.fillText(
				word.value,
				coords[0][0] + self.offsetX + x,
				coords[0][1] +
					self.offsetY +
					(
						y *
						self.params.style.text.lineHeight *
						self.params.style.font.size
					)
			);

			x += width;
		});
		ctx.restore();
	}

	shrinkToText() {
		const ctx = this.panel.rootToolContext;
		if (ctx.measureText(this.params.value).width < 180) {
			this.handle[1].params.pairedOffsetX = (ctx.measureText(this.params.value).width * 1.4) + 20;
			this.params.style.boxWidth = this.handle[1].params.pairedOffsetX;
			this.handle[1].params.pairedOffsetY = this.params.style.text.lineHeight * this.params.style.font.size;
			this.params.style.boxHeight = this.handle[1].params.pairedOffsetY;
		} else {
			this.handle[1].params.pairedOffsetY = this.params.style.text.lineHeight * this.params.style.font.size * (this.yValue + 1);
			this.params.style.boxHeight = this.handle[1].params.pairedOffsetY;
			this.yValue = 0;
		}
	}

	get shouldSnap() { return false; }

	toJSON() {
		const json = super.toJSON();
		json.params.value = encodeURIComponent(this.params.value);

		return json;
	}
}

var toolMap = {
	arrow: ArrowLineTool,
	crosshair: CrosshairTool,
	ellipse: EllipseLineTool,
	'extendedhorizontal-resistance': ExtendedHorizontalToolResistance,
	'extendedhorizontal-support': ExtendedHorizontalToolSupport,
	extended: ExtendedLineTool,
	fibonacci: FibonacciLineTool,
	fibarc: FibonacciArcTool,
	fibcircle: FibonacciCircleTool,
	fibfan: FibonacciFanTool,
	gannfan: GannFanLineTool,
	horizontal: HorizontalLineTool,
	line: LineTool,
	measurementline: MeasurementLineTool,
	ray: RayLineTool,
	rect: RectLineTool,
	text: TextTool
};

/** OHLC Crosshair Tool */
class OHLCCrosshairTool extends LineTool {
	_getDefaultParams() {
		return {
			id: 'ohlccrosshair',
			name: 'OHLC Crosshair',
			value: null,
			style: this.core.getStyle('tool.ohlcCrosshair')
		};
	}

	initHandles(handles) {
		this.handle = deepClone(handles);
		this.handle[0].getCoords = function () {
			const panel = this.tool.panel;
			const xIdx = panel.core.closestExchangeIndex(
				new Date(this.params.date)
			);
			const xPx = panel.xAxis.primaryScale(xIdx);
			const yPx = panel.size.height / 2;

			return [xPx, yPx];
		};
	}

	renderOverlay(ctx, coords, dataPoint, dataY, dataPointVolume) {
		ctx.beginPath();
		ctx.textAlign = 'left';
		ctx.textBaseline = 'top';
		ctx.fillStyle = '#FFF';

		const isIntraday = this.core.state.isIntraday;
		const gap = 10;
		const width = isIntraday ? 134 : 100;
		let height = 100;
		let left = coords[0] + gap;
		let top = dataY - gap - height;
		const volume = dataPointVolume ? dataPointVolume.volume : null;
		const hasVolume = typeof volume === 'number';
		// increase height if volume present
		if (hasVolume) {
			height = 115;
		}
		// flip to left if colliding with right edge
		if (left + width > this.panel.size.width - gap) {
			left = coords[0] - gap - width;
		}

		// flip to bottom if colliding with top edge
		if (top < gap) {
			top = dataY + gap;
		}

		// push up if it now collides with bottom edge
		if (top + height > this.panel.size.height) {
			top = this.panel.size.height - height - gap;
		}

		// white background with dropshadow
		ctx.shadowColor = this.params.style.shadow.color;
		ctx.shadowBlur = this.params.style.shadow.blur;
		ctx.rect(left, top, width, height);
		ctx.fill();
		ctx.shadowColor = null;
		ctx.shadowBlur = null;

		// text

		const padding = 12;
		const rowHeight = 15;
		const rowHeaderPad = 5;
		let rowTop = top + padding;
		let rowLeft = left + padding;
		const dateFormatString = isIntraday ? '%x %X' : '%x';

		// header
		ctx.font = 'bold 12px Arial';
		ctx.fillStyle = '#000000';
		ctx.fillText(
			this.panel.core.timeFormat(dateFormatString)(dataPoint.date),
			rowLeft,
			rowTop
		);

		// first column
		rowTop += rowHeight + rowHeaderPad;
		ctx.font = '11px Arial';
		ctx.fillStyle = '#888888';
		ctx.fillText('Open', rowLeft, rowTop);

		rowTop += rowHeight;
		ctx.fillText('High', rowLeft, rowTop);

		rowTop += rowHeight;
		ctx.fillText('Low', rowLeft, rowTop);

		rowTop += rowHeight;
		ctx.fillText('Close', rowLeft, rowTop);

		if (hasVolume) {
			rowTop += rowHeight;
			ctx.fillText('Volume', rowLeft, rowTop);
		}

		// second column
		const { yAxis } = this.panel;
		rowTop = top + padding + rowHeight + rowHeaderPad;
		rowLeft = left + width - padding;
		ctx.textAlign = 'right';
		ctx.fillStyle = '#444444';
		ctx.fillText(
			yAxis.getFormatValue(
				dataPoint.open,
				yAxis.getFormatString(yAxis.primaryScale, 5)
			),
			rowLeft,
			rowTop
		);

		rowTop += rowHeight;
		ctx.fillText(
			yAxis.getFormatValue(
				dataPoint.high,
				yAxis.getFormatString(yAxis.primaryScale, 5)
			),
			rowLeft,
			rowTop
		);

		rowTop += rowHeight;
		ctx.fillText(
			yAxis.getFormatValue(
				dataPoint.low,
				yAxis.getFormatString(yAxis.primaryScale, 5)
			),
			rowLeft,
			rowTop
		);

		rowTop += rowHeight;
		ctx.fillText(
			yAxis.getFormatValue(
				dataPoint.close,
				yAxis.getFormatString(yAxis.primaryScale, 5)
			),
			rowLeft,
			rowTop
		);

		if (hasVolume) {
			rowTop += rowHeight;
			ctx.fillText(this.panel.yAxis.getFormatValue(volume, '0,.3s', false), rowLeft, rowTop);
		}
	}

	render() {

		const core = this.panel.core;
		const ctx = this.panel.rootToolContext;
		const coords = this.handle[0].getCoords();
		const dataPointIndex = this.getDataPointIndexFromX(coords[0]);
		const dataPoint = core.dataPrimary[dataPointIndex];
		const dataY = subpixel.px(Number(this.panel.yAxis.primaryScale(dataPoint.close)));
		const dataVolume = core.data[core.params.symbol].volume;
		let dataPointVolume = null;

		// volume if available
		if (dataVolume) {
			const datasetVolume = core.getFirstDataset(dataVolume);
			dataPointVolume = datasetVolume[dataPointIndex];
		}

		// if a render listener has been registered, emit and return
		if (core.eventEmitter.listeners('TOOL_OHLCCROSSHAIR_RENDER').length) {
			return core.eventEmitter.emit('TOOL_OHLCCROSSHAIR_RENDER', {
				tool: this,
				coords,
				dataPoint,
				dataY,
				dataPointVolume
			});
		}

		// default rendering is below

		// vertical line segment
		ctx.beginPath();
		ctx.strokeStyle = this.params.style.vertical.color;
		ctx.lineWidth = this.params.style.vertical.width;
		ctx.moveTo(coords[0], 0);
		ctx.lineTo(coords[0], this.panel.size.height);
		ctx.stroke();

		// horizontal line segment
		ctx.beginPath();
		ctx.strokeStyle = this.params.style.horizontal.color;
		ctx.lineWidth = this.params.style.vertical.width;
		ctx.moveTo(0, dataY);
		ctx.lineTo(this.panel.size.width, dataY);
		ctx.stroke();

		// info box
		this.renderOverlay(ctx, coords, dataPoint, dataY, dataPointVolume);

		return this.panel.core.onToolRender(this);
	}

	/** Alternate render method for this tool on lower panels (just render the line) */
	renderLower(panel) {
		const ctx = panel.rootToolContext;
		const coords = this.handle[0].getCoords();

		// vertical line segment
		ctx.beginPath();
		ctx.strokeStyle = this.params.style.color;
		ctx.lineWidth = this.params.style.width;
		ctx.moveTo(coords[0], 0);
		ctx.lineTo(coords[0], panel.size.height);
		ctx.stroke();

		this.handle[0].render();
	}
}

/** Returns Calculator Tool */
class ReturnsCalculatorTool extends LineTool {
	_getDefaultParams() {
		return {
			id: 'returnscalculator',
			name: 'Returns Calculator',
			value: null,
			style: this.core.getStyle('tool.returnsCalculator')
		};
	}

	renderOverlay(ctx, dataPointLeft, dataPointRight, leftCoords, rightCoords) {
		ctx.beginPath();
		ctx.textBaseline = 'top';
		ctx.fillStyle = '#FFF';

		const width = 163;
		const height = 102;
		const mid = leftCoords[0] + ((rightCoords[0] - leftCoords[0]) / 2);
		const left = mid - (width / 2);
		const top = (this.panel.size.height / 2) - (height / 2);
		let leftClose = dataPointLeft.close;
		let rightClose = dataPointRight.close;
		const leftHeader = this.panel.core.timeFormat('%x')(new Date(dataPointLeft.date));
		const rightHeader = this.panel.core.timeFormat('%x')(new Date(dataPointRight.date));
		const headerValue = (leftHeader === rightHeader) ? leftHeader : `${leftHeader} - ${rightHeader}`;

		// AC-275 convert normalized values to actuals if a comparison exists
		if (
			this.panel.core.params.symbolCompare.length ||
			this.panel.core.comparisons.length
		) {
			leftClose = this.panel.normalizedToActual(leftClose);
			rightClose = this.panel.normalizedToActual(rightClose);
		}

		// white background with dropshadow
		ctx.shadowColor = '#666666';
		ctx.shadowBlur = 15;
		ctx.rect(left, top, width, height);
		ctx.fill();
		ctx.shadowColor = null;
		ctx.shadowBlur = null;

		// text
		const padding = 12;
		const rowHeight = 15;
		const rowHeaderPad = 5;
		let rowTop = top + padding;
		let rowLeft = left + padding;

		// header
		ctx.textAlign = 'center';
		ctx.font = 'bold 12px Arial';
		ctx.fillStyle = '#000000';
		ctx.fillText(headerValue, left + width / 2, rowTop);

		// first column
		ctx.textAlign = 'left';
		rowTop += rowHeight + rowHeaderPad;
		ctx.font = '11px Arial';
		ctx.fillStyle = '#888888';
		ctx.fillText('Start', rowLeft, rowTop);

		rowTop += rowHeight;
		ctx.fillText('End', rowLeft, rowTop);

		rowTop += rowHeight;
		ctx.fillText('Change', rowLeft, rowTop);

		rowTop += rowHeight;
		ctx.fillText('Change Percent', rowLeft, rowTop);

		// second column
		const { yAxis } = this.panel;
		ctx.textAlign = 'right';
		rowTop = top + padding + rowHeight + rowHeaderPad;
		rowLeft = left + width - padding;
		ctx.fillStyle = '#444444';
		ctx.fillText(
			yAxis.getFormatValue(
				leftClose,
				yAxis.getFormatString(yAxis.primaryScale, 5)
			),
			rowLeft,
			rowTop
		);

		rowTop += rowHeight;
		ctx.fillText(
			yAxis.getFormatValue(
				rightClose,
				yAxis.getFormatString(yAxis.primaryScale, 5)
			),
			rowLeft,
			rowTop
		);

		rowTop += rowHeight;
		ctx.fillStyle = (rightClose >= leftClose) ? 'green' : 'red';
		ctx.fillText(
			yAxis.getFormatValue(
				rightClose - leftClose,
				yAxis.getFormatString(yAxis.primaryScale, 5)
			),
			rowLeft,
			rowTop
		);

		rowTop += rowHeight;
		const percentChange = (Number(rightClose) - Number(leftClose)) / Math.abs(Number(leftClose));
		ctx.fillText(
			yAxis.getFormatValue(
				percentChange,
				'0,.2%' // Should this format specifier be exposed via styles?
			),
			rowLeft,
			rowTop
		);
	}

	render(panel = this.panel) {
		const { isUpper } = panel;
		const ctx = panel.rootToolContext;
		const coords = [this.handle[0].getCoords(), this.handle[1].getCoords()];
		let [leftCoords, rightCoords] = coords;
		const height = panel.size.height;

		if (rightCoords[0] < leftCoords[0]) {
			// 2nd handle is further left, so flip
			[rightCoords, leftCoords] = coords;
		}

		// get entry and exit y positions
		const dataPointLeft = this.getDataPointFromX(leftCoords[0]);
		const dataPointRight = this.getDataPointFromX(rightCoords[0]);
		const dataYLeft = subpixel.px(
			Number(panel.yAxis.primaryScale(dataPointLeft.close))
		);
		const dataYRight = subpixel.px(
			Number(panel.yAxis.primaryScale(dataPointRight.close))
		);

		// inner fill
		ctx.save();
		ctx.fillStyle = this.params.style.center.background.color;
		ctx.fillRect(leftCoords[0], 0, rightCoords[0] - leftCoords[0], height);
		ctx.restore();

		// left and right fills
		ctx.fillStyle = this.params.style.side.background.color;
		ctx.fillRect(1, 1, leftCoords[0], height - 1);
		ctx.fillRect(
			rightCoords[0],
			1,
			panel.size.width - rightCoords[0],
			height - 1
		);
		ctx.restore();

		// vertical left edge line
		ctx.beginPath();
		ctx.strokeStyle = this.params.style.side.color;
		ctx.lineWidth = this.params.style.side.width;
		ctx.moveTo(leftCoords[0], 0);
		ctx.lineTo(leftCoords[0], height);

		// vertical right edge line
		ctx.moveTo(rightCoords[0], 0);
		ctx.lineTo(rightCoords[0], height);
		ctx.stroke();

		this.handle[0].render();
		this.handle[1].render();

		if (isUpper) {
			// horizontal entry line
			ctx.beginPath();
			ctx.setLineDash([2, 2]);
			ctx.moveTo(0, dataYLeft);
			ctx.lineTo(leftCoords[0], dataYLeft);

			// horizontal exit line
			ctx.moveTo(rightCoords[0], dataYRight);
			ctx.lineTo(panel.size.width - 1, dataYRight);

			ctx.stroke();
			ctx.setLineDash([]);
		}

		if (isUpper) {
			this.renderOverlay(
				ctx,
				dataPointLeft,
				dataPointRight,
				leftCoords,
				rightCoords
			);
		}

		panel.core.onToolRender(this);
	}

	renderLower(panel) {
		return this.render(panel);
	}
}

/** Snap Handle */
class SnapHandle extends Tool {
	constructor(core, panel) {
		super(core);
		this.panel = panel;
		this.params = this._getDefaultParams();
	}

	_getDefaultParams() {
		return {
			id: 'snapHandle',
			name: 'Snap Handle',
			style: this.core.getStyle('tool.snapHandle')
		};
	}

	getCoords(mouse) {
		// get nearest date
		const panel = this.panel;
		const core = panel.core;
		const rulerIdx = Math.round(panel.xAxis.primaryScale.invert(mouse[0]));
		const snapDistance = 20;
		let snapCoords = null;
		const isOHLC = /candlestick|ohlc/.test(
			panel.indicators[0].params.markerType
		);
		let yOpen;
		let yHigh;
		let yLow;
		let distOpen;
		let distHigh;
		let distLow;

		if (core.exchangeDates[rulerIdx]) {
			const dataIdx = core._getIndexByDate(
				core.dataPrimary,
				new Date(core.exchangeDates[rulerIdx])
			);

			if (core.dataPrimary[dataIdx]) {
				const yScale = panel.yAxis.primaryScale;
				const searchData = [];

				// search the index to the left
				if (core.dataPrimary[dataIdx - 1]) {
					searchData.push(core.dataPrimary[dataIdx - 1]);
				}

				// search the index to the right
				if (core.dataPrimary[dataIdx + 1]) {
					searchData.push(core.dataPrimary[dataIdx + 1]);
				}

				// search main index
				searchData.push(core.dataPrimary[dataIdx]);

				// search for the closest point of up to 3 ohlc sets
				for (let x = 0; x < searchData.length; x++) {
					// get OHLC coords
					const yClose = yScale(searchData[x].close);
					const xData = panel.xAxis.primaryScale(
						core.closestExchangeIndex(searchData[x].date)
					);
					const distClose = this.distance(mouse, [xData, yClose]);
					let min = distClose;

					if (isOHLC) {
						yOpen = yScale(searchData[x].open);
						yHigh = yScale(searchData[x].high);
						yLow = yScale(searchData[x].low);

						distOpen = this.distance(mouse, [xData, yOpen]);
						distHigh = this.distance(mouse, [xData, yHigh]);
						distLow = this.distance(mouse, [xData, yLow]);

						min = Math.min(
							min,
							distOpen,
							distHigh,
							distLow,
							distClose
						);
					}

					// when close enough, snap to closest ohlc point
					if (min < snapDistance) {
						if (min === distClose) {
							snapCoords = [xData, yClose];
						}

						if (isOHLC) {
							if (min === distHigh) {
								snapCoords = [xData, yHigh];
							} else if (min === distLow) {
								snapCoords = [xData, yLow];
							} else if (min === distOpen) {
								snapCoords = [xData, yOpen];
							}
						}
					}
				}
			}
		}

		return snapCoords;
	}

	distance(coord1, coord2) {
		let xs = coord2[0] - coord1[0];
		let ys = coord2[1] - coord1[1];

		xs *= xs;
		ys *= ys;

		return Math.sqrt(xs + ys);
	}

	render(event) {
		const mouse = eventUtil.getMouseFromDomEvent(event);
		if (this.renderCustom) {
			return this.renderCustom(mouse);
		}

		this.coords = this.getCoords(mouse);

		if (this.coords) {
			const panel = this.panel;
			const ctx = panel.rootToolContext;
			const size = 2;

			ctx.beginPath();
			ctx.lineWidth = 1;
			ctx.strokeStyle = this.params.style.color;

			ctx.arc(
				this.coords[0] - size / 2 + 1,
				this.coords[1] - size / 2 + 1,
				size * 2,
				0,
				2 * Math.PI
			);
			ctx.stroke();
		}
	}
}

class ModchartsInteractive extends Modcharts {
	constructor(rootSelector, params = {}, parent = null) {
		super(rootSelector, params, parent);
		// Use default params for default crosshair
		this.crosshair = new Crosshair(this);
	}

	initDOM() {
		super.initDOM();

		// root mouse
		this.rootMouse
			.on('mouseover', event => {
				return this.onMouseover(event);
			})
			.on('mouseout', event => {
				return this.onMouseout(event);
			})
			.on('mousemove', event => {
				return this.onMousemove(event);
			})
			.on('click', event => {
				return this.onClick(event);
			})
			.on('dblclick', event => {
				return this.onDoubleClick(event);
			})
			.on('contextmenu', event => {
				return this.onRightClick(event);
			})
			.on('mousedown', event => {
				return this.onMousedown(event);
			})
			.on('mouseup', event => {
				return this.onMouseup(event);
			})
			.on('touchstart', event => {
				return this.onTouchStart(event);
			})
			.on('touchend', event => {
				return this.onTouchEnd(event);
			})
			.on('touchmove', event => {
				return this.onTouchMove(event);
			});

		// Add event listeners for keyboard, use encapsulating functions to perserve scope
		this._onKeyDown = event => { this.onKeyDown(event); };
		document.body.addEventListener('keydown', this._onKeyDown);
		this._onKeyUp = event => { this.onKeyUp(event); };
		document.body.addEventListener('keyup', this._onKeyUp);
	}

	resize() {
		super.resize();

		if (!this.params.size) {
			this.rootMouse.style('width', `${this.size.width}px`);
			this.rootMouse.style('height', `${this.size.height}px`);
		}
	}

	setDateRange(dateStart, dateStop) {
		// unselect tools and exit tool mode
		this.selectTool(null);
		this.setToolMode(false);

		super.setDateRange(dateStart, dateStop);
	}

	set days(days) {
		// Unselect tools and exit tool mode
		this.selectTool(null);
		this.setToolMode(false);

		// Reset the scale extent so that it gets recalculated
		this.zoom.scaleExtent([0, Infinity]);

		// Call super's days setter
		super.days = days;

		// Update the zoom center due to xAxis changing
		this.state.updateZoomCenter = true;
	}

	/**
	 * set new primary symbol and reset price indicators params.symbol
	 * @param {string} symbol
	 */
	setSymbol(symbol) {
		super.setSymbol(symbol);
		// unselect tools and exit tool mode
		this.selectTool(null);
		this.setToolMode(false);
	}
	/** setter for params.toolSnapOHLC which also removes the snapHandle reference */
	setToolSnapOHLC(isEnabled) {
		this.params.toolSnapOHLC =
			typeof isEnabled === 'boolean' ? isEnabled : true;

		if (!this.params.toolSnapOHLC) {
			this.state.snapHandle = null;
		}
	}
	/**
	 * Initializes tool instances from saved tool Object
	 * @method getToolsFromJSON
	 * @params {object} panel
	 * @params {array} tools
	 */
	getToolsFromJSON(panel, savedTools = []) {
		const tools = [];
		for (let t = 0; t < savedTools.length; t++) {
			const savedTool = savedTools[t];
			const ToolClass = Tool.getToolFromType(savedTool.params.id, toolMap);

			if (ToolClass) {
				const tool = new ToolClass(this, savedTool.params);
				tool.init(panel);

				for (const handleJSON of savedTool.handle) {
					tool.handle.push(
						Handle.fromJSON(handleJSON, tool)
					);
				}

				tools.push(tool);
			}
		}
		return tools;
	}

	/** Mouse click fired.  based on current chart state, call various click handlers */
	onClick(event, args = {}) {
		const { isAutomatic, isImmediate } = args;
		let mouse = eventUtil.getMouseFromDomEvent(event);
		const el = event && (event.target || event.srcElement);

		if (el) {
			if (event.cancelable) event.preventDefault();
			event.stopPropagation();
		} else if (
			(
				isAutomatic ||
				isImmediate
			) &&
			this.panels.length
		) {
			const { size } = this.panels[0];
			mouse = [size.padding.left + 1, size.padding.top + size.top + 1];
		} else {
			return;
		}
		const activePanel = this.getActivePanel(mouse);
		this.eventEmitter.emit(types.CLICK_START);

		if (!activePanel) return;

		if (this.state.preventClick) {
			this.state.preventClick = null;
			return;
		}

		this.removeStateTools();
		this.untrackTouches();

		const { tool } = this.state;
		if (tool) {
			// we're in tool mode, so check if we're waiting for the first click or second click.
			if (tool.state && tool.state.inProgressHandle) {
				// currently in the middle of clicking handles for an in-progress tool (such as between two clicks)
				tool.onClickCanvas(mouse); // Pass in mouse coords for extended horizontal lines (CH-1466)
			} else {
				// first click, let's start a new tool
				tool.init(activePanel);
				if (tool.isImmediate) {
					// When a tool isImmediate, it is immediately created on click of the tool button, so it needs
					// to stay invisible until the the cursor enters the panel and produces usable coordinates.
					tool.state.visible = false;
				}
				activePanel.tools.push(tool);
				this.selectTool(tool);
				tool.onClickCanvas(mouse);
			}
			activePanel.clearTools();
			activePanel.renderTools();
		} else {
			// it was a click on the canvas, make sure all tools are deselected
			this.selectTool(null);
		}

		this.eventEmitter.emit(types.CLICK, { mouse });
	}

	/** Return price/date information about click.  return info for both mouse and priceline */
	getClickInfo(mouse) {
		const activePanel = this.getActivePanel(mouse);

		if (activePanel?.index === 0) {
			const y = mouse[1];
			const scaleIdx = Math.round(
				this.panels[0].xAxis.primaryScale.invert(mouse[0])
			);
			const datapointIdx = this._getIndexByDomain(
				scaleIdx,
				this.dataPrimary,
				'dateIndex'
			);
			const date = new Date(this.exchangeDates[scaleIdx]);
			const datapointValue = this.dataPrimary[datapointIdx].close;
			const x = Math.round(this.xAxis.primaryScale(scaleIdx));
			const datapointY = this.panels[0].yAxis.primaryScale(datapointValue);
			const value = this.panels[0].yAxis.primaryScale.invert(mouse[1]);

			return {
				mouse: { x, y, date, value },
				datapoint: {
					x,
					y: datapointY,
					date,
					value: datapointValue
				}
			};
		}
		return null;
	}

	/** Mouse right click fired. */
	onRightClick(event) {
		const mouse = eventUtil.getMouseFromDomEvent(event);
		const activePanel = this.getActivePanel(mouse);

		if (!activePanel) return;

		const valIdx = Math.round(activePanel.xAxis.primaryScale.invert(mouse[0]));
		const date = new Date(this.exchangeDates[valIdx]);
		const value = activePanel.yAxis.primaryScale.invert(mouse[1]);

		this.eventEmitter.emit(types.CLICK_RIGHT, {
			date,
			value,
			mouse,
			event
		});
	}

	/**
	 * Mouse down fired.
	 * if we were hovering over a panel resize handle, enter resizing state
	 * call activepanel's tool's mousedown handlers
	 * else call active panel's mousedown handler
	 * mouseIn can be used to manually override the mouse coordinates
	 * touchId will be passed in if this call came from the touchStart handler.
	 */
	onMousedown(event, mouseIn, touchId) {
		this.eventEmitter.emit(types.MOUSE_DOWN);
		if (event.cancelable) {
			event.preventDefault();
			event.returnValue = false;
		}
		event.stopPropagation();

		if (this.status === 0) return;

		const mouse = mouseIn || eventUtil.getMouseFromDomEvent(event);

		if (this.state.hoverResizePanel) {
			// we were hovering over a panel resizer, and moused down.  begin the panel resizing process.
			this.state.resizePanel = this.state.hoverResizePanel;
			zoomUtil.unregisterZoom(this);
			this.state.resizePanelY = mouse[1];
		} else {
			// determine if we should begin any tool actions (drag handle or drag tool)
			const activePanel = this.getActivePanel(mouse);

			if (activePanel) {
				// call tool's mousedown handler
				for (const tool of activePanel.tools) {
					tool.onMousedown(mouse, touchId);
				}
			}
		}

		return false;
	}

	/**
	 * Main chart mousemove handler
	 * if a tool is in progress, fire its onmousemove handler
	 * a tool is "inProgressHandle" state when it's in the middle of a two- or three-chord click action and we're not yet done.
	 * if no tools are in progress but we are in tool mode and just moused over a handle, fire its move handler
	 * if no tools are in progress and we're not in the middle of zooming (pan/zoom), update the crosshair
	 */
	onMousemove(event, touchId) {
		this.eventEmitter.emit(types.MOUSE_MOVE);
		if (this.state.zooming || this.dataXHR || this.status === 0) return;

		const mouse = eventUtil.getMouseFromDomEvent(event);
		const activePanel = this.getActivePanel(mouse);
		// throttle mousemove events - chromium continues to fire mousemove during resting state
		// see https://code.google.com/p/chromium/issues/detail?id=170631
		if (
			this.state.prevMouse &&
			this.state.prevMouse[0] === mouse[0] &&
			this.state.prevMouse[1] === mouse[1]
		) {
			if (event.cancelable) event.preventDefault();
			event.stopPropagation();

			return false;
		}

		// track last mouse position
		this.state.prevMouse = [...mouse];
		// reset the cursor and exit when no longer on a panel
		if (
			!activePanel &&
			!this.state.resizePanel &&
			// Check if this is a touch event
			!this.state.isTouchDevice
		) {
			this.eventEmitter.emit(types.MOUSE_LEAVE);
			this.setCursor(types$1.DEFAULT);

			// Don't listen for zoom events if not on a panel chart
			zoomUtil.unregisterZoom(this);

			return;
		}
		// Start listening for zoom events
		zoomUtil.registerZoom(this);

		if (this.state.resizePanel) {
			this.handleMousemovePanelResize(mouse);
		} else if (
			this.state.tool &&
			this.state.tool.state &&
			this.state.tool.state.inProgressHandle
		) {
			// in the middle of dragging a tool handle
			this.handleMousemoveActiveToolHandle(event, touchId);
		} else if (this.state.tool && this.state.dragTool) {
			// in the middle of dragging a tool
			this.handleMousemoveActiveTool(mouse);
		} else if (this.handleMousemoveToolDefault(mouse)) {
			// mousing over a tool
			return;
		} else if (this.params.hoverIndicator && this.handleMousemoveIndicator(mouse)) {
			// mousing over an indicator
			return;
		} else {
			// default mousemove handler
			this.handleMousemoveDefault(event, activePanel);
		}

		return false;
	}

	/** Default double-click handler, also detects if the double-click was on a tool */
	onDoubleClick(event) {
		const mouse = eventUtil.getMouseFromDomEvent(event);
		const clickInfo = this.getClickInfo(mouse);
		const tool = this._isWithinTool(mouse);
		if (clickInfo) {
			this.eventEmitter.emit(types.DOUBLE_CLICK, {
				datapoint: clickInfo.datapoint
			});
		}
		if (tool) {
			this.eventEmitter.emit(types.TOOL_DOUBLE_CLICK, {
				...tool.params
			});
		}
		return false;
	}

	/**
	 * User is in the middle of resizing a panel
	 * @param {Number[]} mouse - Mouse position information [x, y]
	 */
	handleMousemovePanelResize(mouse) {
		const deltaPct = (mouse[1] - this.state.resizePanelY) / this.size.height;
		const minHeight = 0.05;
		let panelUp;
		let panelDown;

		for (let p = 1; p < this.panels.length; p++) {
			panelUp = this.panels[p - 1];
			panelDown = this.panels[p];

			if (
				panelDown === this.state.resizePanel &&
				panelUp.size.heightPct + deltaPct > minHeight &&
				panelDown.size.heightPct - deltaPct > minHeight
			) {
				panelUp.size.heightPct += deltaPct;
				panelDown.size.heightPct -= deltaPct;

				panelUp.params.size.heightPct = panelUp.size.heightPct;
				panelDown.params.size.heightPct = panelDown.size.heightPct;

				break;
			}
		}

		this.state.resizePanelY = mouse[1];
		this.resize();
		this.render();
	}

	/**
	 * User is mousing over the chart with no other interaction.  also called when in toolmode
	 * but haven't started drawing yet.
	 * @param {Number[]} mouse - Mouse position information [x, y]
	 * @param {Object} activePanel - Panel that mouse is moving over
	 */
	handleMousemoveDefault(event, activePanel) {
		const mouse = eventUtil.getMouseFromDomEvent(event);
		const prevEventHover = this.state.eventHover;
		let overResizeHandle = false;

		this.eventEmitter.emit(types.MOUSE_MOVE);

		this.state.eventHover = null;

		// User is mousing over an event icon
		for (const panel of this.panels) {
			for (const panelEvent of panel.events) {
				const eventWithin = panelEvent.isWithin(mouse);

				if (eventWithin !== null) {
					this.state.eventHover = eventWithin;
					break;
				}
			}
			if (this.state.eventHover) break;
		}

		// there was an event hover but now there is none.
		if (prevEventHover && !this.state.eventHover) {
			this.eventEmitter.emit(types.EVENT_MOUSE_LEAVE, { previousEventHover: prevEventHover });
		} else if (
			this.state.eventHover !== null &&
			this.state.eventHover !== prevEventHover
		) {
			this.eventEmitter.emit(types.EVENT_MOUSE_ENTER, { event: this.state.eventHover });
		}

		if (this.params.panelResize) {
			// user is mousing over a panel resize handle
			const firstTop = this.panels[0].params.style.padding.top;

			for (const panel of this.panels) {
				// don't activate handle on first panel or fixed-height panels
				if (mouse[1] > firstTop + 5 && !this.params.panelHeightLower) {
					const { top } = panel;

					if (mouse[1] > top - 6 && mouse[1] < top + 5) {
						this.state.hoverResizePanel = panel;
						overResizeHandle = true;
						break;
					}
				}
			}
		}

		if (overResizeHandle) {
			// mousing over resize handle
			this.setCursor(types$1.NS_RESIZE);
			this.eventEmitter.emit(types.RESIZE_HANDLE_HOVER);
		} else if (activePanel?.isWithin(mouse)) {
			const { isUpper } = activePanel;

			// within chart region.  regular mouseover with crosshair active
			this.state.hoverResizePanel = null;

			// set cursor to cross if we are not hovering over an event
			if (!this.state.eventHover) this.setCursor(types$1.CROSSHAIR);

			// create a snap handle
			if (
				isUpper &&
				!this.state.snapHandle &&
				this.params.toolSnapOHLC
			) {
				this.state.snapHandle = new SnapHandle(this, activePanel);
			}

			if (
				isUpper &&
				this.params.toolSnapOHLC &&
				this.state.tool
			) {
				// clear tools canvas
				activePanel.clearTools();

				// re-render
				activePanel.renderTools();

				// snap handle
				this.state.snapHandle.render(event);
			} else {
				this.eventEmitter.emit(types.PANEL_MOUSE_MOVE, { mouse, activePanel });

				for (const tool of activePanel.tools) {
					if (tool.isImmediate && !tool.state.visible) {
						tool.state.visible = true;
						tool.state.inProgressHandle = true;
						tool.state.selected = true;
						tool.state.dragHandle = tool.handle[0];
						tool.render(event);

						this.state.tool = tool;
						this.onMousedown(event, mouse);

						this.eventEmitter.emit(types.TOOL_MOUSE_MOVE, event, mouse);

						return;
					}
				}
			}
		} else {
			// not yet within chart region
			this.state.hoverResizePanel = null;

			this.setCursor(types$1.DEFAULT);
			this.eventEmitter.emit(types.MOUSE_LEAVE);
		}
	}

	/**
	 * user is mousing over the toolmode chart.  set cursor and hover state of tools
	 * @param {Number[]} mouse - Mouse position information [x, y]
	 */
	handleMousemoveToolDefault(mouse) {
		let cursor = types$1.CROSSHAIR;
		const hoverTool = this._isWithinTool(mouse);
		let isRender = false;
		let isEnter = false;
		let isHandleEnter = false;

		// call onmouseout on all non-hovered tools
		this.panels.forEach(panel => {
			panel.tools.forEach(tool => {
				if (
					(!hoverTool || tool !== hoverTool) &&
					tool.state.hover
				) {

					this.onToolMouseout(tool);

					tool.handle.forEach(handle => {
						// fire off the handle mouse leave event.  just to be safe, fire it indiscriminately for all non-hovered handles.
						const mousePanel = [mouse[0], mouse[1] - panel.size.top];
						this.onToolHandleMouseout(handle, mousePanel);
					});

					// this removes the little gray circles from the handles
					if (tool.state.hover) {
						tool.state.hover = false;
						zoomUtil.registerZoom(this);
					}

					isRender = true;
				}
			});
		});

		// call onmouseover of hovered tool
		this.panels.forEach(panel => {
			panel.tools.forEach(tool => {
				if (
					hoverTool &&
					tool === hoverTool &&
					!tool.state.hover
				) {

					// handles
					tool.handle.forEach(handle => {
						// we're not necessarily over a handle just because we're over a tool, so check for that.
						const mousePanel = [mouse[0], mouse[1] - panel.size.top];
						if (handle.isWithin(mousePanel)) {
							isHandleEnter = true;
							this.onToolHandleMouseover(handle, mousePanel);
						}
					});

					// setting the tool state to hover will enables the little gray circles on handles.
					// we want these to show up immediately for both handles when hovering over a tool.
					if (!tool.state.hover) {
						tool.state.hover = true;
						tool.handle.forEach(handle => {
							handle.state.hover = true;
						});
						zoomUtil.unregisterZoom(this); // disable pan/zoom when hovering over handle
					}

					// tool
					cursor = types$1.POINTER;
					if (!isHandleEnter) {
						isEnter = true;
						this.onToolMouseover(tool, mouse);
					}
					isRender = true;
				}
			});
		});

		// call mousemove of hovered tool.  this goes last so mouseover can fire first
		if (hoverTool && !isEnter && !isHandleEnter) {
			cursor = types$1.POINTER;
			this.eventEmitter.emit(types.TOOL_MOUSE_MOVE, hoverTool, mouse);

			isRender = true;
		}

		// re-render tools if hover state changed
		if (isRender) {
			this.setCursor(cursor);

			// render tool canvases
			this.panels.forEach(panel => {
				if (panel.tools.length) {
					panel.clearTools();
					panel.renderTools();
				}
			});
		}

		return hoverTool;
	}

	/**
 * handle mouseover of indicators
 * @param {Number[]} mouse - Mouse position information [x, y]
 */
	handleMousemoveIndicator(mouse) {
		let indicatorHover = null;
		const panel = this.panels[0];

		// Determine the topmost indicator that is hovered
		for (let i = panel.indicators.length - 1; i >= 0; i--) {
			const indicator = panel.indicators[i];
			if (indicator.state) {
				const indicatorWithinData = indicator.isWithin(mouse);
				if (indicatorWithinData) {
					indicatorHover = indicator;
					break; // Stop at the first indicator that is hovered
				}
			}
		}

		// Update hover states and emit events
		panel.indicators.forEach(indicator => {
			if (indicator.state) {
				const origHoverState = indicator.state.hover;
				indicator.state.hover = (indicator === indicatorHover);

				if (indicator.state.hover) {

					if (origHoverState !== indicator.state.hover && indicator.hoverEventOver) {
						// Emit INDICATOR_MOUSE_OVER event
						panel.core.eventEmitter.emit(
							indicator.hoverEventOver, {
								params: { ...indicator.params },
								coords: { x: mouse[0], y: mouse[1] }
							}
						);
					}

					// Emit INDICATOR_MOUSE_MOVE event
					panel.core.eventEmitter.emit(
						'INDICATOR_MOUSE_MOVE', {
							params: { ...indicator.params },
							coords: { x: mouse[0], y: mouse[1] }
						}
					);
				} else {
					if (origHoverState && indicator.hoverEventOut) {
						// Emit INDICATOR_MOUSE_OUT event
						panel.core.eventEmitter.emit(
							indicator.hoverEventOut, {
								params: { ...indicator.params },
								coords: { x: mouse[0], y: mouse[1] }
							}
						);
					}
				}
			}
		});

		return indicatorHover;
	}


	/**
	 * a tool is currently in the middle of drawing
	 * @param {Number[]} mouse - Mouse position information [x, y]
	 */
	handleMousemoveActiveTool(mouse, touchId) {
		this.state.tool.onMousemove(mouse, touchId);

		this.panels.forEach(panel => {
			panel.clearTools();
			panel.renderTools();
		});
	}

	/**
	 * a tool handle is being modified
	 * @param {Number[]} mouse - Mouse position information [x, y]
	 */
	handleMousemoveActiveToolHandle(event, touch) {
		const mouse = eventUtil.getMouseFromDomEvent(event);
		this.state.tool.onMousemove(mouse, touch, event);

		this.panels.forEach(panel => {
			panel.clearTools();
			panel.renderTools();
		});

		// snap handle
		if (
			this.state.snapHandle &&
			this.state.tool &&
			this.state.tool.shouldSnap
		) {
			this.state.snapHandle.render(event);
		}
	}

	/**
	 * onMouseout of entire chart itself
	 * @method onMouseout
	 */
	onMouseout(event) {
		this.eventEmitter.emit(types.MOUSE_LEAVE, event);
		this.setCursor(types$1.DEFAULT);
	}

	/**
	 * onMouseover of entire chart itself
	 * @method onMouseover
	 */
	onMouseover(event) {
		this.eventEmitter.emit(types.MOUSE_ENTER, event);
	}

	/**
	 * onMouseover of a tool object
	 * @method onToolMouseover
	 */
	onToolMouseover(tool, mouse) {
		this.eventEmitter.emit(types.TOOL_MOUSE_ENTER, { tool, mouse });
	}

	/**
	 * onMouseout of a tool object
	 * @method onToolMouseout
	 */
	onToolMouseout(tool) {
		this.eventEmitter.emit(types.TOOL_MOUSE_LEAVE, { tool });
	}

	/**
	 * onMouseover of a handle object
	 * @method onToolHandleMouseover
	 */
	onToolHandleMouseover(handle, mouse) {
		handle.state.hover = true;
		this.eventEmitter.emit('TOOL_HANDLE_MOUSE_ENTER', { handle, mouse });
	}

	/**
	 * onMouseout of a handle object
	 * @method onToolHandleMouseout
	 */
	onToolHandleMouseout(handle, mouse) {
		handle.state.hover = false;
		this.eventEmitter.emit('TOOL_HANDLE_MOUSE_LEAVE', { handle, mouse });
	}

	/**
	 * tool is rendered
	 * @method onToolRender
	 */
	onToolRender(tool) {
		this.eventEmitter.emit(types.TOOL_RENDER, { tool });
	}

	/**
	 * onMousedown of a tool object (or tool handle)
	 * @method onToolMousedown
	 */
	onToolMousedown(tool) {
		this.eventEmitter.emit(types.TOOL_MOUSE_DOWN, { tool });

		// stop tracking finger1/finger2 for longpresses
		this.untrackTouches();
	}

	/**
	 * onMouseup of a tool object (or tool handle)
	 * @method onToolMouseup
	 */
	onToolMouseup(tool) {
		this.eventEmitter.emit(types.TOOL_MOUSE_UP, { tool });
	}

	/**
	 * onMousemove of a tool object (or tool handle)
	 * @method onToolMousemove
	 */
	onToolMousemove(tool, mouse) {
		this.eventEmitter.emit(types.TOOL_MOUSE_MOVE, { tool, mouse });
	}

	/**
	 * handle backspace/delete to remove tools (needs to be onkeydown instead of onkeyup
	 * to prevent the browser's "back button" action)
	 * @method onKeyDown
	 */
	onKeyDown(event) {
		this.eventEmitter.emit(types.KEY_DOWN, event);

		if (this.params.removeToolByKey && event) {
			const keyCode = event.keyCode;

			if (keyCode === 8 || keyCode === 46) {
				// backspace || delete

				const d = event.target || event.srcElement || {};
				const tagName = d.tagName ? d.tagName.toLowerCase() : '';
				const tagType = d.type ? d.type.toLowerCase() : '';
				const allowDefault =
						tagName === 'textarea' ||
						(tagName === 'input' &&
							/text|number|password|file|email|search|date|tel/.test(
								tagType
							));

				if (!allowDefault) {
					const selectedTool = this.getSelectedTool();

					if (selectedTool) {
						// only trap event if we have a selected tool
						event.preventDefault();

						selectedTool.remove();
						this.setCursor(types$1.CROSSHAIR);

						this.eventEmitter.emit(types.TOOL_REMOVE);
					}
				}
			}
		}
	}

	/**
	 * @method onKeyUp
	 */
	onKeyUp(event) {
		this.eventEmitter.emit(types.KEY_UP, event);
	}

	/** Getter to be used in args returned by BOUNDARY_STATUS event */
	get _boundaryStatus() {
		return {
			isAtLeftBoundary: this.isAtLeftBoundary,
			isAtRightBoundary: this.isAtRightBoundary,
			isAtZoomInBoundary: this.isAtZoomInBoundary,
			isAtZoomOutBoundary: this.isAtZoomOutBoundary
		};
	}

	loadingStop(emitBoundaryStatus = true) {
		super.loadingStop();

		// Only emit this event if there's no error
		if (emitBoundaryStatus) this.eventEmitter.emit(types.BOUNDARY_STATUS, this._boundaryStatus);
	}

	/** Determines if chart has been panned/zoomed to left boundary */
	get isAtLeftBoundary() {
		if (this.xAxis) {
			return Math.floor(this.xAxis.primaryScale.domain()[0]) <= 0;
		}
		this.warn('XAxis scale is not available yet');
		return false;
	}

	/** Determines if chart has been panned/zoomed to right boundary */
	get isAtRightBoundary() {
		if (this.xAxis) {
			return Math.ceil(this.xAxis.primaryScale.domain()[1]) >= this._calcLastIndex();
		}
		this.warn('XAxis scale is not available yet');
		return false;
	}

	/** Determines if chart has been zoomed in to the limit */
	get isAtZoomInBoundary() {
		if (this.xAxis) {
			// Is the current scale greater than (zoomed in) or equal to the scale limit (extent)?
			return this.zoomUtil.getZoomTransform(this).k >= this.zoom.scaleExtent()[1];
		}
		this.warn('XAxis scale is not available yet');
		return false;
	}

	/** Determines if chart has been zoomed out to the limit */
	get isAtZoomOutBoundary() {
		if (this.xAxis) {
			// The chart is zoomed completely out if at the left and right boundaries
			return this.isAtLeftBoundary && this.isAtRightBoundary;
		}
		this.warn('XAxis scale is not available yet');
		return false;
	}

	/** Default mouseup event handler */
	onMouseup() {
		this.eventEmitter.emit(types.MOUSE_UP);

		this.state.resizePanel = null;
		this.state.hoverResizePanel = null;

		if (this.state.dragTool) {
			this.state.preventClick = true;
			this.state.dragTool.onMouseup();
		}
	}

	/** Manually fire onMousedown and onMousemove (once) when onTouchStart fires */
	onTouchStart(event) {
		this.eventEmitter.emit(types.TOUCH_START);

		// Store touch device check for use later
		if (!this.state.isTouchDevice) {
			this.state.isTouchDevice = true;

			const bodyTouchCallback = touchEvent => {
				if (touchEvent.target !== this.rootMouse.node()) {
					this.eventEmitter.emit(types.MOUSE_LEAVE);
				}
			};

			// Add body listener (and callback) if one doesn't exist (CH-1601)
			if (!this.body.on('touchstart')) this.body.on('touchstart', bodyTouchCallback);
		}

		const touches = event.changedTouches;
		Object.keys(touches).forEach(idx => {
			const row = touches[idx];
			// begin tracking this unique touch
			const offset = this.panels[0].rootCanvas.node().getBoundingClientRect();
			const x = Math.round(row.pageX - offset.left);
			const y = Math.round(row.pageY - offset.top);

			this.trackTouch(row.identifier, {
				id: row.identifier,
				offset, // so we don't have to recalculate it in the touchmove handler
				x, // xy relative to panel
				y, // xy relative to panel
				newX: x, // xy relative to panel
				newY: y, // xy relative to panel
				date: new Date().getTime()
			});
		});

		// start a timer if either of the long-press tools are enabled and not active
		if (
			!this.state.toolOHLCCrosshair &&
			!this.state.toolReturnsCalculator &&
			(
				this.params.toolOHLCCrosshairEnabled ||
				this.params.toolReturnsCalculatorEnabled
			)
		) {
			const longPressDuration = 600;
			const maxDriftDistance = 25;

			window.clearTimeout(window.pressTimeout);

			// start timer
			window.pressTimeout = window.setTimeout(() => {
				// timer has elapsed, check distance to see if we have a valid longPress or longDoublePress
				let distance = Infinity;
				let distance2 = Infinity;
				const getDistance = LineTool.prototype.distance;
				const mytouches = [];

				// filter to a numeric array of valid touches
				Object.keys(this.state.mytouches).forEach(idx => {
					const row = this.state.mytouches[idx];
					if (typeof row === 'object' && typeof row.x === 'number') {
						mytouches.push({ ...{}, ...row });
					}
				});

				if (mytouches.length === 1) {
					distance = getDistance(
						[mytouches[0].x, mytouches[0].y],
						[mytouches[0].newX, mytouches[0].newY]
					);

					if (Math.abs(distance) < maxDriftDistance) {
						// fire longPress
						this.onLongPress(mytouches[0]);
					}
				} else if (mytouches.length === 2) {
					distance = getDistance(
						[mytouches[0].x, mytouches[0].y],
						[mytouches[0].newX, mytouches[0].newY]
					);

					distance2 = getDistance(
						[mytouches[1].x, mytouches[1].y],
						[mytouches[1].newX, mytouches[1].newY]
					);

					if (
						Math.abs(distance) < maxDriftDistance &&
						Math.abs(distance2) < maxDriftDistance
					) {
						// fire longDoublePress
						this.onLongDoublePress(mytouches[0], mytouches[1]);
					}
				}

				// no need to keep touches around now.
				this.untrackTouches();
			}, longPressDuration);
		}

		if (event.cancelable) event.preventDefault();
		event.stopPropagation();

		this.onMousedown(event, null, touches[0].identifier);

		// needed for AC-265 and AC-290
		this.onMousemove(event);

		this.state.lastTouchStart = new Date();
	}

	/** Manually fire onMousemove when onTouchMove event fires */
	onTouchMove(event) {
		this.eventEmitter.emit(types.TOUCH_MOVE);
		let mytouch = null;

		// find this touch in the running list, and record the new XY.
		// this will be used to measure the overall distance the finger travelled
		// once the longpress timer elapses.
		const changedTouches = event.changedTouches;
		Object.keys(changedTouches).forEach(idx => {
			const row = changedTouches[idx];
			mytouch = this.state.mytouches[row.identifier];

			if (mytouch) {
				mytouch.newX = Math.round(row.pageX - mytouch.offset.left); // xy relative to panel
				mytouch.newY = Math.round(row.pageY - mytouch.offset.top); // xy relative to panel
			}
		});

		try {
			if (event.cancelable) {
				event.preventDefault();
			}
			event.stopPropagation();
		} catch (error) {
			this.warn(error.message);
		}

		if (!mytouch) window.clearTimeout(window.pressTimeout);

		this.onMousemove(event, mytouch);
	}

	/**
	 * Manually fire onMouseup and onClick when onTouchEnd fires
	 * if two onTouchEnd events happen in rapid succession, manually fire onDoubleClick handler
	 */
	onTouchEnd(event) {
		this.eventEmitter.emit(types.TOUCH_END);
		this.untrackTouches();

		if (event.cancelable) event.preventDefault();
		event.stopPropagation();

		this.onMouseup(event);

		// Create single tap
		this.onClick(event);

		// Detect double tap for editing tools (probably not ideal)
		if (new Date() - this.state.lastTouchEnd <= 300) this.onDoubleClick(event);

		this.state.lastTouchEnd = new Date();
	}

	trackTouch(id, touch) {
		// initialize a place to track current touch collection
		if (!this.state.mytouches) this.state.mytouches = {};

		this.state.mytouches[id] = { ...touch };
	}

	untrackTouches() {
		window.clearTimeout(window.pressTimeout);
		this.state.mytouches = {};
	}

	// remove any existing "state" tools (OHLC crosshair, returns calculator)
	removeStateTools() {
		// remove old tool
		if (this.state.toolOHLCCrosshair) {
			this.state.toolOHLCCrosshair.remove();
			this.state.toolOHLCCrosshair = null;
			this.setCursor(types$1.CROSSHAIR);
		}

		// remove old tool
		if (this.state.toolReturnsCalculator) {
			this.state.toolReturnsCalculator.remove();
			this.state.toolReturnsCalculator = null;
			this.setCursor(types$1.CROSSHAIR);
		}
	}

	onLongPress(touch) {
		this.eventEmitter.emit(types.LONG_PRESS);
		// remove existing tools
		this.removeStateTools();

		// create OHLC crosshair tool, if enabled
		if (this.params.toolOHLCCrosshairEnabled) {
			// disable zoom/pan temporarily (re-enabled in onClick handler)
			zoomUtil.unregisterZoom(this);

			// create tool
			const tool = new OHLCCrosshairTool(this, {});
			tool.init(this.panels[0], {});

			// create handle
			tool.initHandles([
				tool.createHandle([touch.newX, touch.newY], EdgeHandle)
			]);

			this.panels[0].tools.push(tool);
			this.state.toolOHLCCrosshair = tool;

			tool.onMousedown([touch.newX, touch.newY], touch.id);
		}
	}

	onLongDoublePress(touch1, touch2) {
		this.eventEmitter.emit(types.LONG_DOUBLE_PRESS);
		// remove existing tools
		this.removeStateTools();

		// create returns calculator tool, if enabled
		if (this.params.toolReturnsCalculatorEnabled) {
			// disable zoom/pan temporarily (re-enabled in onClick handler)
			zoomUtil.unregisterZoom(this);

			// create tool
			const tool = new ReturnsCalculatorTool(this, {});
			tool.init(this.panels[0]);

			// create handles
			tool.handle = [
				tool.createHandle([touch1.newX, touch1.newY], EdgeHandle),
				tool.createHandle([touch2.newX, touch2.newY], EdgeHandle)
			];

			tool.handle[0].state.visible = true;
			tool.handle[1].state.visible = true;

			this.panels[0].tools.push(tool);
			this.state.toolReturnsCalculator = tool;

			this.render();
		}
	}

	// AC-194 Crosshairs Gestural Interactions
	isZoomedSuppressed() {
		return this.state.toolOHLCCrosshair || this.state.toolReturnsCalculator;
	}

	/**
	 * Retrieves currently selected tool
	 * @returns {Tool|null}
	 */
	getSelectedTool() {
		for (const panel of this.panels) {
			for (const tool of panel.tools) {
				if (tool.state.selected) return tool;
			}
		}

		return null;
	}

	/**
	 * Checks to see if mouse coordinates are within a tool
	 * @param {number[]} mouse - Array of mouse coordinates
	 * @returns {Tool|null} Tool over which mouse is hovering
	 */
	_isWithinTool(mouse) {
		for (const panel of this.panels) {
			const mousePanel = [mouse[0], mouse[1] - panel.size.top];

			for (const tool of [...panel.tools].reverse()) {
				// Gets newest tool at this location on panel
				if (tool.isWithin(mousePanel) || tool.isWithinHandle(mousePanel)) return tool;
			}
		}

		return null;
	}

	/** clear out any existing tools and reset the chart state */
	removeTools() {
		this.panels.forEach(panel => {
			[...panel.tools].reverse().forEach(tool => tool.remove());
		});

		this.state.tool = null;
	}

	// remove active tool (currently in progress of drawing) from state
	// One example is this might be called when a tool is in progress and the user cllcks ESC.
	removeActiveTool() {
		if (this.state.tool) {
			this.state.tool.remove();
			this.state.tool = null;
		}
	}

	/** Set or exit tool mode (note: does not affect selected tool state, e.g. if we're exiting tool mode the last tool will still remain in a selected state) */
	setToolMode(tool) {
		if (tool) {
			this.state.tool = tool;
			zoomUtil.unregisterZoom(this);

			if (tool.params && tool.params.isAutomatic) this.onClick(null, { isAutomatic: tool.params.isAutomatic });
			else if (tool.isImmediate) this.onClick(null, { isImmediate: tool.isImmediate });

			if (tool.params) {
				this.eventEmitter.emit(types.TOOL_ADD, {
					toolType: tool.params && tool.params.id,
					tool
				});
			}
		} else {
			// exit tool mode
			this.state.tool = null;

			zoomUtil.registerZoom(this);

			// render tools
			this.panels.forEach(panel => {
				panel.clearTools();
				panel.renderTools();
			});
		}

		this.eventEmitter.emit(types.TOOL_MODE_CHANGE, {
			tool,
			isToolMode: this.state.tool !== null
		});
	}

	/**
	 * Selects the specified tool and unselects all others.
	 * If null is passed, it unselects the current tool.
	 * @param {Object|null} newTool - The tool to be selected, or null to unselect.
	 */
	selectTool(newTool = null) {
		// Update the current state with the new tool selection
		this.state.tool = newTool;

		// Emit an event to notify about the tool selection change
		this.eventEmitter.emit(types.TOOL_SELECT, { tool: newTool });

		// Iterate over each panel to update the tool selection state
		this.panels.forEach(panel => {
			let shouldRerenderTools = false;

			// Check each tool in the panel
			panel.tools.forEach(toolInPanel => {
				// Store the previous selection state of the tool
				const wasPreviouslySelected = toolInPanel.state.selected;

				// Update the selection state: select if it's the new tool, unselect otherwise
				toolInPanel.state.selected = (toolInPanel === newTool);

				// If a tool was previously selected and now is unselected,
				// and no new tool is being selected, mark for re-rendering
				if (!newTool && wasPreviouslySelected && !toolInPanel.state.selected) {
					shouldRerenderTools = true;
				}
			});

			// Re-render tools if needed (e.g., when a tool was unselected without a new selection)
			if (shouldRerenderTools) {
				panel.clearTools();
				panel.renderTools();
			}
		});
	}


	destroy() {
		super.destroy();

		// Remove keyboard event listeners
		document.body.removeEventListener('keydown', this._onKeyDown);
		document.body.removeEventListener('keyup', this._onKeyUp);
	}
}

const DEFAULT_STYLES = generateDefaultStyles();

export { Crosshair, DEFAULT_STYLES, Modcharts, ModchartsInteractive, annotation, enums, _export as exportTools, locales, toolMap as tools };
